import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { flushSync, createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { api, AuthError } from "./api.js";
import { sumChartSeries } from "./sumChartSeries.js";
import { normalizeInstantlyCampaignList, resolveInstantlyCampaignId } from "./instantly-utils.js";

// --- Debounced auto-save hook ---
function useAutoSave(saveFn, delay = 800) {
  const timerRef = useRef(null);
  const latestFn = useRef(saveFn);
  latestFn.current = saveFn;
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const trigger = useCallback((...args) => {
    setSaveStatus('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await latestFn.current(...args);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(prev => prev === 'saved' ? null : prev), 2000);
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(prev => prev === 'error' ? null : prev), 4000);
      }
    }, delay);
  }, [delay]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return { trigger, cancel, saveStatus };
}

function AutoSaveIndicator({ status }) {
  if (!status) return null;
  const config = {
    saving: { text: 'Saving...', color: COLORS?.textDim || '#888' },
    saved: { text: 'Saved', color: '#22c55e' },
    error: { text: 'Save failed', color: '#dc2626' },
  };
  const c = config[status];
  if (!c) return null;
  return React.createElement('span', {
    style: { fontSize: 10, fontWeight: 600, color: c.color, letterSpacing: '0.04em', transition: 'opacity 0.3s', opacity: status === 'saved' ? 0.7 : 1 }
  }, c.text);
}

// --- Saved Prompts (loaded from API, with fallback defaults) ---
const FALLBACK_DEFAULT_PROMPT = `You are an assistant that generates short, personalised outreach email intros.

Objective: Return ONLY the following structure without adding anything else:

Was on your website and noticed you [use 5-8 words to describe the company] - thought I'd reach out.

Instructions:
- Replace [use 5-8 words to describe the company] with an accurate 5–8 word description of what the company does.
- Use only the following sources for this description:
   • {{ $('Scrape Website-ScrapingBee').item.json }}
- Keep the description concise, neutral, and fact-based.
- Do not add extra sentences, comments, or formatting. Return only the two lines in the format above.`;

let SAVED_PROMPTS = {
  default: { label: "Cold Intro — Pain Point Focused", text: FALLBACK_DEFAULT_PROMPT },
};

(async () => {
  try {
    const defaults = await api.prompts.getDefaults();
    if (defaults && typeof defaults === 'object') SAVED_PROMPTS = { ...SAVED_PROMPTS, ...defaults };
  } catch {}
  try {
    const saved = await api.prompts.list();
    if (Array.isArray(saved)) saved.forEach(p => { if (p.key || p.id) SAVED_PROMPTS[p.key || p.id] = { label: p.label || p.name, text: p.text || p.content }; });
  } catch {}
})();

// --- Utility Components ---

/** POST /lead-generation/personalize: prefers subject + body when present; else parses legacy combined message. */
function subjectBodyFromPersonalizeApiResponse(res, contact) {
  const company = contact?.company || 'your team';
  const fallbackSubject = `Connecting with ${company}`;
  const structured = res != null && typeof res.body === 'string';
  const apiBody = typeof res?.body === 'string' ? res.body.trim() : '';
  const apiMsg = typeof res?.message === 'string' ? res.message.trim() : '';
  const text = structured ? apiBody : apiMsg;

  if (structured) {
    const sub = typeof res.subject === 'string' ? res.subject.trim() : '';
    return { subject: sub || fallbackSubject, body: text };
  }

  const subjectMatch = apiMsg.match(/Subject:\s*(.+)/i);
  const bodyMatch = apiMsg.match(/Body:\s*([\s\S]+)/i);
  return {
    subject: subjectMatch ? subjectMatch[1].trim() : fallbackSubject,
    body: bodyMatch ? bodyMatch[1].trim() : apiMsg,
  };
}

const ProgressDots = ({ active }) => {
  const [dots, setDots] = useState(0);
  useEffect(() => {
    if (!active) return;
    const i = setInterval(() => setDots(d => (d + 1) % 4), 400);
    return () => clearInterval(i);
  }, [active]);
  return active ? <span style={{ fontFamily: "monospace", letterSpacing: 2 }}>{".".repeat(dots).padEnd(3, "\u00A0")}</span> : null;
};

const TypeWriter = ({ text, speed = 12, onDone }) => {
  const [displayed, setDisplayed] = useState("");
  const idx = useRef(0);
  useEffect(() => {
    idx.current = 0;
    setDisplayed("");
    const i = setInterval(() => {
      idx.current++;
      setDisplayed(text.slice(0, idx.current));
      if (idx.current >= text.length) {
        clearInterval(i);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(i);
  }, [text]);
  return <>{displayed}</>;
};

// --- Styles ---
const COLORS = {
  bg: "#0a0a0f",
  surface: "#12121a",
  surfaceHover: "#1a1a26",
  border: "#1e1e2e",
  borderActive: "#3b3b5c",
  text: "#e2e2e8",
  textMuted: "#7a7a8e",
  textDim: "#4a4a5e",
  accent: "#c4f04d",
  accentDim: "#8ab025",
  accentBg: "rgba(196, 240, 77, 0.08)",
  danger: "#f04d4d",
  dangerBg: "rgba(240, 77, 77, 0.08)",
  warn: "#f0a84d",
  warnBg: "rgba(240, 168, 77, 0.08)",
  blue: "#4d9ef0",
  blueBg: "rgba(77, 158, 240, 0.08)",
  green: "#22c55e",
  greenBg: "rgba(34, 197, 94, 0.08)",
};

const FONT = "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace";
const FONT_BODY = "'DM Sans', 'Segoe UI', system-ui, sans-serif";
const LOGO_SRC = "/Consultix-Logo.png";

// --- Auth Gate & Screen ---
function AuthScreen({ onSuccess, mode: initialMode = "signin", resetToken: urlToken = null }) {
  const [mode, setMode] = useState(urlToken ? "reset" : initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [resetToken] = useState(urlToken);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [tokenValidated, setTokenValidated] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const validateToken = async () => {
    if (!accessToken.trim()) {
      setError("Enter your access token");
      setErrorCode("MISSING_ACCESS_TOKEN");
      return;
    }
    setError("");
    setErrorCode("");
    setTokenValidating(true);
    try {
      const result = await api.validateSignupToken(accessToken);
      if (result.valid) {
        setTokenValidated(true);
        setError("");
      } else {
        setError(result.error || "Invalid access token");
        setErrorCode("INVALID_ACCESS_TOKEN");
        setTokenValidated(false);
      }
    } catch (err) {
      setError(err.message || "Could not validate token");
      setErrorCode("VALIDATION_ERROR");
      setTokenValidated(false);
    } finally {
      setTokenValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorCode("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.forgotPassword(email);
        setForgotSent(true);
      } else if (mode === "reset") {
        if (password !== passwordConfirm) {
          setError("Passwords do not match");
          setErrorCode("PASSWORD_MISMATCH");
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          setErrorCode("WEAK_PASSWORD");
          setLoading(false);
          return;
        }
        await api.resetPassword(resetToken, password);
        if (typeof window !== "undefined") {
          const url = new URL(window.location.href);
          url.searchParams.delete("reset");
          url.searchParams.delete("token");
          window.history.replaceState({}, "", url.pathname + url.search);
        }
        setMode("signin");
        setPassword("");
        setPasswordConfirm("");
        setError("");
        setErrorCode("");
        setResetSuccess(true);
        setTimeout(() => setResetSuccess(false), 5000);
      } else if (mode === "signin") {
        await api.login(email, password);
        onSuccess();
      } else {
        if (!accessToken.trim()) {
          setError("Access token is required to create an account");
          setErrorCode("MISSING_ACCESS_TOKEN");
          setLoading(false);
          return;
        }
        if (!tokenValidated) {
          setError("Please validate your access token first");
          setErrorCode("TOKEN_NOT_VALIDATED");
          setLoading(false);
          return;
        }
        if (!firstName.trim() || !lastName.trim()) {
          setError("First name and last name are required");
          setErrorCode("MISSING_NAME_PARTS");
          setLoading(false);
          return;
        }
        await api.signup(email, password, firstName, lastName, accessToken);
        onSuccess();
      }
    } catch (err) {
      const msg = err instanceof AuthError ? err.message : (err.message || "Something went wrong");
      setError(msg);
      setErrorCode(err instanceof AuthError ? err.code : "UNKNOWN");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14, outline: "none",
    boxSizing: "border-box", transition: "border-color 0.2s",
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 400, padding: "32px 36px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <img src={LOGO_SRC} alt="ConsultiX" style={{ height: 48, objectFit: "contain" }} />
        </div>
        <h1 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: "0 0 8px", color: COLORS.text }}>
          {mode === "forgot" ? "Forgot password" : mode === "reset" ? "Reset password" : mode === "signin" ? "Sign in" : "Create account"}
        </h1>
        <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 24 }}>
          {mode === "forgot" ? "Enter your email and we'll send a reset link" : mode === "reset" ? "Enter your new password" : mode === "signin" ? "Enter your email and password" : "Get started with your account"}
        </p>
        {resetSuccess && (
          <div style={{
            padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: COLORS.greenBg, border: `1px solid ${COLORS.green}44`, color: COLORS.green,
          }}>Password reset successfully. Sign in with your new password.</div>
        )}
        {forgotSent ? (
          <div style={{ padding: "24px 0", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
            <p style={{ color: COLORS.text, fontSize: 14, marginBottom: 8 }}>Check your email</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 24 }}>If an account exists, we've sent a reset link to {email}</p>
            <button type="button" onClick={() => { setForgotSent(false); setMode("signin"); }} style={{
              background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, cursor: "pointer", padding: 0,
            }}>Back to sign in</button>
          </div>
        ) : (
        <>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {mode === "signup" && (
            <>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>Access Token <span style={{ color: COLORS.danger }}>*</span></label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="password" value={accessToken} onChange={e => { setAccessToken(e.target.value); setTokenValidated(false); setError(""); }} placeholder="Enter your signup access token" style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={validateToken} disabled={tokenValidating || !accessToken.trim()} style={{
                    padding: "12px 16px", background: tokenValidated ? COLORS.green : COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8,
                    fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: tokenValidating || !accessToken.trim() ? "not-allowed" : "pointer", opacity: tokenValidating || !accessToken.trim() ? 0.6 : 1, whiteSpace: "nowrap",
                  }}>{tokenValidating ? "Validating..." : tokenValidated ? "✓ Valid" : "Validate"}</button>
                </div>
                {tokenValidated && <span style={{ fontSize: 11, color: COLORS.green, marginTop: 4, display: "block" }}>Token validated. You can proceed with signup.</span>}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>First name <span style={{ color: COLORS.danger }}>*</span></label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" required autoComplete="given-name" style={inputStyle} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>Last name <span style={{ color: COLORS.danger }}>*</span></label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" required autoComplete="family-name" style={inputStyle} />
                </div>
              </div>
            </>
          )}
          {(mode === "signin" || mode === "signup" || mode === "forgot") && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required={mode !== "reset"} autoComplete="email" style={inputStyle} />
            </div>
          )}
          {(mode === "signin" || mode === "signup") && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={mode === "signup" ? 6 : 1} autoComplete={mode === "signin" ? "current-password" : "new-password"} style={inputStyle} />
              {mode === "signup" && <span style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, display: "block" }}>At least 6 characters</span>}
              {mode === "signin" && (
                <button type="button" onClick={() => { setMode("forgot"); setError(""); setErrorCode(""); }} style={{
                  background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 12, cursor: "pointer", padding: "6px 0 0", textAlign: "left",
                }}>Forgot password?</button>
              )}
            </div>
          )}
          {mode === "reset" && (
            <>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>New password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} autoComplete="new-password" style={inputStyle} />
                <span style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, display: "block" }}>At least 6 characters</span>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, fontFamily: FONT }}>Confirm password</label>
                <input type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} placeholder="••••••••" required minLength={6} autoComplete="new-password" style={inputStyle} />
              </div>
            </>
          )}
          {error && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, fontSize: 13,
              background: errorCode === "EMAIL_EXISTS" ? COLORS.warnBg : (["MISSING_FIELDS", "MISSING_ACCESS_TOKEN", "WEAK_PASSWORD", "PASSWORD_MISMATCH", "TOKEN_NOT_VALIDATED"].includes(errorCode) ? COLORS.blueBg : COLORS.dangerBg),
              border: `1px solid ${errorCode === "EMAIL_EXISTS" ? COLORS.warn + "44" : (["MISSING_FIELDS", "MISSING_ACCESS_TOKEN", "WEAK_PASSWORD", "PASSWORD_MISMATCH", "TOKEN_NOT_VALIDATED"].includes(errorCode) ? COLORS.blue + "44" : COLORS.danger + "44")}`,
              color: errorCode === "EMAIL_EXISTS" ? COLORS.warn : (["MISSING_FIELDS", "MISSING_ACCESS_TOKEN", "WEAK_PASSWORD", "PASSWORD_MISMATCH", "TOKEN_NOT_VALIDATED"].includes(errorCode) ? COLORS.blue : COLORS.danger),
            }}>{error}</div>
          )}
          <button type="submit" disabled={loading} style={{
            padding: "12px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8,
            fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1,
          }}>{loading ? "Please wait..." : mode === "forgot" ? "Send reset link" : mode === "reset" ? "Reset password" : mode === "signin" ? "Sign in" : "Sign up"}</button>
        </form>
        <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${COLORS.border}` }}>
          {mode === "forgot" && (
            <button type="button" onClick={() => { setMode("signin"); setError(""); setErrorCode(""); }} style={{
              background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, cursor: "pointer", padding: 0,
            }}>Back to sign in</button>
          )}
          {mode === "reset" && (
            <button type="button" onClick={() => { setMode("signin"); setError(""); setErrorCode(""); }} style={{
              background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, cursor: "pointer", padding: 0,
            }}>Back to sign in</button>
          )}
          {mode === "signin" && (
            <button type="button" onClick={() => { setMode("signup"); setError(""); setErrorCode(""); setTokenValidated(false); setAccessToken(""); }} style={{
              background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, cursor: "pointer", padding: 0,
            }}>Don't have an account? Sign up</button>
          )}
          {mode === "signup" && (
            <button type="button" onClick={() => { setMode("signin"); setError(""); setErrorCode(""); setTokenValidated(false); setAccessToken(""); }} style={{
              background: "none", border: "none", color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, cursor: "pointer", padding: 0,
            }}>Already have an account? Sign in</button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function MaintenanceScreen({ info, onStaffSignIn }) {
  // Auto-recover: poll status; when maintenance is lifted, reload into the app.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const s = await api.maintenanceStatus();
        if (!s?.enabled) window.location.reload();
      } catch { /* keep waiting */ }
    }, 30000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, color: COLORS.text, padding: 24, textAlign: "center" }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <img src={LOGO_SRC} alt="ConsultiX" style={{ height: 48, objectFit: "contain" }} />
      <div style={{ fontSize: 40 }}>🛠️</div>
      <h1 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, margin: 0 }}>Down for maintenance</h1>
      <p style={{ fontFamily: FONT, fontSize: 14, color: COLORS.textMuted, maxWidth: 460, lineHeight: 1.6, margin: 0 }}>
        {info?.message || "ConsultiX is temporarily offline while we ship an update. We'll be back shortly — this page will refresh automatically."}
      </p>
      {info?.eta && (
        <div style={{ fontFamily: FONT, fontSize: 12, color: COLORS.textDim }}>Expected back: {info.eta}</div>
      )}
      <button
        onClick={onStaffSignIn}
        style={{ marginTop: 8, padding: "7px 16px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textDim, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
      >
        Staff sign in →
      </button>
    </div>
  );
}

function AuthGate({ children }) {
  const [phase, setPhase] = useState("checking"); // checking | maintenance | auth | app
  const [maintInfo, setMaintInfo] = useState(null);
  const [forceAuth, setForceAuth] = useState(false); // staff chose to sign in during maintenance

  useEffect(() => {
    let cancelled = false;
    async function check() {
      const [maint, me] = await Promise.all([
        api.maintenanceStatus().catch(() => ({ enabled: false })),
        api.me().catch(() => null),
      ]);
      if (cancelled) return;
      const isAdmin = me?.user?.role === "platform_admin";
      if (maint?.enabled && !isAdmin) {
        setMaintInfo(maint);
        setPhase("maintenance");
        return;
      }
      if (me?.valid) { setPhase("app"); return; }
      try {
        await api.getToken();
        if (!cancelled) setPhase("app");
        return;
      } catch { /* no apiKey / exchange failed */ }
      if (!cancelled) setPhase("auth");
    }
    check();
    // Mid-session: a 503 MAINTENANCE from any call flips us to the screen.
    const onMaint = (e) => { setMaintInfo(e.detail || {}); setForceAuth(false); setPhase("maintenance"); };
    if (typeof window !== "undefined") window.addEventListener("cx:maintenance", onMaint);
    return () => { cancelled = true; if (typeof window !== "undefined") window.removeEventListener("cx:maintenance", onMaint); };
  }, []);

  if (phase === "checking") {
    return (
      <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, color: COLORS.textMuted }}>
        <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <img src={LOGO_SRC} alt="ConsultiX" style={{ height: 48, objectFit: "contain" }} />
        <span style={{ fontFamily: FONT, fontSize: 14 }}>Loading...</span>
      </div>
    );
  }
  if (phase === "maintenance" && !forceAuth) {
    return <MaintenanceScreen info={maintInfo} onStaffSignIn={() => setForceAuth(true)} />;
  }
  if (phase === "auth" || (phase === "maintenance" && forceAuth)) {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const resetToken = params.get("reset") === "true" ? params.get("token") || null : null;
    // Reload after login so maintenance/admin status is re-evaluated cleanly.
    return <AuthScreen onSuccess={() => window.location.reload()} resetToken={resetToken} />;
  }
  return children;
}

// --- Keep-Alive wrapper: lazy-mount on first visit, then use display:none to preserve state ---
function KeepAlive({ active, children }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { if (active && !mounted) setMounted(true); }, [active]);
  if (!mounted) return null;
  return React.createElement("div", { style: { display: active ? "flex" : "none", flexDirection: "column", flex: 1, height: "100%", overflow: "hidden" } }, children);
}

// --- Section maintenance: Get Leads (leads_mgmt) shows a maintenance panel to
// non-admins. Platform admins keep full access. Flip to false and redeploy to reopen.
const LEAD_GEN_MAINTENANCE = true;

function SectionMaintenancePanel({ title }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 40, textAlign: "center", color: COLORS.text }}>
      <span className="material-symbols-outlined" style={{ fontSize: 44, color: COLORS.warn }}>construction</span>
      <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, margin: 0 }}>{title} is under maintenance</h2>
      <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: COLORS.textMuted, maxWidth: 460, lineHeight: 1.6, margin: 0 }}>
        We're making improvements to this section. It will be back shortly — the rest of ConsultiX is available as normal.
      </p>
    </div>
  );
}

// --- Main App ---
export { AuthGate };
export default function App({ userRole } = {}) {
  const [step, setStep] = useState(0); // 0=ICP, 1=discovery, 2=enrichment, 3=personalization, 4=outreach
  const [showLeadsImport, setShowLeadsImport] = useState(false);
  const [icpForm, setIcpForm] = useState({ listName: "", industry: "", keywords: "", employeeSizes: [], regions: [], roles: [], lookalike: "", maxLeads: "" });
  const [savedPrompts, setSavedPrompts] = useState(SAVED_PROMPTS);
  const [discoveredLeads, setDiscoveredLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [enrichedContacts, setEnrichedContacts] = useState([]);
  const [enrichmentCompanyResults, setEnrichmentCompanyResults] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [personalizedEmails, setPersonalizedEmails] = useState({});
  const [outreachQueue, setOutreachQueue] = useState([]);
  const [outreachResults, setOutreachResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processLog, setProcessLog] = useState([]);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [expandedContact, setExpandedContact] = useState(null);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [activePage, setActivePage] = useState("dashboard");
  const leadGenLocked = LEAD_GEN_MAINTENANCE && userRole !== "platform_admin";
  const [globalCredits, setGlobalCredits] = useState(null);
  // Load credit balance on mount and refresh every 60s
  useEffect(() => {
    const loadCredits = () => api.billing.credits().then(d => { if (d?.balance !== undefined) setGlobalCredits(d.balance); }).catch(() => {});
    loadCredits();
    const interval = setInterval(loadCredits, 15000);
    return () => clearInterval(interval);
  }, []);
  // Handle #account-billing hash navigation (from credit counter click)
  useEffect(() => {
    const handleHash = () => {
      if (window.location.hash === '#account-billing') {
        setActivePage('account');
        window.location.hash = '';
      }
    };
    handleHash(); // check on mount
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  /** Lead Lists tab: open a specific list (and optional enrichment job for per-row job outcome) from View List */
  const [leadlistsFocusListId, setLeadlistsFocusListId] = useState(null);
  const [leadlistsFocusJobId, setLeadlistsFocusJobId] = useState(null);
  /** After QUEUE FOR OUTREACH on Leads Management tab, show QueuedPanel instead of CampaignSetupPanel */
  const [leadsMgmtShowQueued, setLeadsMgmtShowQueued] = useState(false);
  const activePageRef = useRef(activePage);
  const leadsMgmtCampaignSectionRef = useRef(null);
  activePageRef.current = activePage;
  useEffect(() => {
    if (activePage !== "leadlists") {
      setLeadlistsFocusListId(null);
      setLeadlistsFocusJobId(null);
    }
  }, [activePage]);
  const handleViewLeadListFromEnrichment = useCallback((listId, jobId) => {
    if (!listId) return;
    setActivePage("leadlists");
    setLeadlistsFocusListId(String(listId));
    setLeadlistsFocusJobId(jobId != null ? String(jobId) : null);
  }, []);
  const clearLeadlistFocus = useCallback(() => {
    setLeadlistsFocusListId(null);
    setLeadlistsFocusJobId(null);
  }, []);
  const enrichmentRunningJobs = useEnrichmentV2RunningJobs();
  const [pendingConversationId, setPendingConversationId] = useState(null);
  const [auditProjects, setAuditProjects] = useState([]);
  const [selectedAuditProject, setSelectedAuditProject] = useState(null);
  const [promptText, setPromptText] = useState(FALLBACK_DEFAULT_PROMPT);
  const [selectedPromptKey, setSelectedPromptKey] = useState("default");
  const [previewEmails, setPreviewEmails] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [channelAssignments, setChannelAssignments] = useState({});
  const [emailPlatform, setEmailPlatform] = useState("instantly");
  const [emailCampaign, setEmailCampaign] = useState("");
  /** Human label for Instantly campaign when option value is UUID */
  const [emailCampaignLabel, setEmailCampaignLabel] = useState("");
  /** When set (e.g. from enrichment handoff), queue reuses this list instead of creating a new one */
  const [campaignLeadListId, setCampaignLeadListId] = useState(null);
  /** Enrichment v2 job id — server pushes to Instantly after job completes; client polls sync-log */
  const [enrichmentOutreachJobId, setEnrichmentOutreachJobId] = useState(null);
  /** Leads Management: template + Instantly campaign required before Find & Enrich */
  const [lmPreflightPromptText, setLmPreflightPromptText] = useState(FALLBACK_DEFAULT_PROMPT);
  const [lmPreflightPromptKey, setLmPreflightPromptKey] = useState("default");
  const [lmPreflightCampaignId, setLmPreflightCampaignId] = useState("");
  const [lmPreflightCampaignLabel, setLmPreflightCampaignLabel] = useState("");
  const lmPreflightReady = lmPreflightPromptText.trim().length > 0;
  const [lmJobActive, setLmJobActive] = useState(false);
  const instantlyPushedEmailsRef = useRef(new Set());
  const lastEnrichmentSyncLogIdRef = useRef(0);
  const [linkedinPlatform, setLinkedinPlatform] = useState("heyreach");
  const [linkedinCampaign, setLinkedinCampaign] = useState("");
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [discoverCanRun, setDiscoverCanRun] = useState(true); // true until we know otherwise
  const logRef = useRef(null);
  const logRefLeadsMgmt = useRef(null);
  const [enabledSolutions, setEnabledSolutions] = useState(["sol_assistant", "website_builder"]);

  useEffect(() => {
    if (activePage === "leads") {
      api.leadGeneration.getDiscoverStatus()
        .then(r => setDiscoverCanRun(r.canRun ?? true))
        .catch(() => setDiscoverCanRun(false));
    }
  }, [activePage]);

  const scrollAgentLogToBottom = () => {
    const el = activePageRef.current === "leads_mgmt" ? logRefLeadsMgmt.current : logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const addLog = (msg, type = "info") => {
    flushSync(() => {
      setProcessLog(prev => [...prev, { msg, type, ts: Date.now() }]);
    });
    requestAnimationFrame(() => {
      scrollAgentLogToBottom();
    });
  };
  const addLogRef = useRef(addLog);
  addLogRef.current = addLog;
  /** Stable callback for EnrichmentJobDashboard so continuous-personalize effect does not reset every render */
  const agentLogForEnrichment = useCallback((msg, type) => {
    addLogRef.current(msg, type);
  }, []);

  useEffect(() => {
    scrollAgentLogToBottom();
  }, [processLog]);

  useEffect(() => {
    api.prompts.getDefaults().then(defs => {
      api.prompts.list().then(rows => {
        const merged = { ...defs };
        (rows || []).forEach(r => { merged[r.id] = { label: r.label, text: r.text }; });
        setSavedPrompts(merged);
        api.prompts.getActive().then(({ key, label, text }) => {
          if (key && merged[key]) {
            setSelectedPromptKey(key);
            setPromptText(merged[key].text);
            setLmPreflightPromptKey(key);
            setLmPreflightPromptText(merged[key].text);
          } else if (key && text) {
            merged[key] = { label: label || 'Active Prompt', text };
            setSavedPrompts({ ...merged });
            setSelectedPromptKey(key);
            setPromptText(text);
            setLmPreflightPromptKey(key);
            setLmPreflightPromptText(text);
          }
        }).catch(() => {});
      }).catch(() => setSavedPrompts(defs || SAVED_PROMPTS));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.audit.projects.list()
      .then(projects => {
        const list = Array.isArray(projects) ? projects : [];
        setAuditProjects(list);
        setSelectedAuditProject(prev => {
          const prevId = prev == null ? null : String(prev);
          if (prevId && list.some(project => String(project.id) === prevId)) return prevId;
          return list[0]?.id ? String(list[0].id) : null;
        });
      })
      .catch(() => setAuditProjects([]));
  }, []);

  const handleCreateAuditProject = useCallback(async (name) => {
    const project = await api.audit.projects.create({ name });
    setAuditProjects(prev => {
      const next = [...prev, project];
      next.sort((a, b) => String(a.created || '').localeCompare(String(b.created || '')) || String(a.id).localeCompare(String(b.id)));
      return next;
    });
    setSelectedAuditProject(String(project.id));
    return project;
  }, []);

  /** Poll server Instantly sync log into Agent Log while an enrichment handoff job is active */
  useEffect(() => {
    if (!enrichmentOutreachJobId) return undefined;
    lastEnrichmentSyncLogIdRef.current = 0;
    let cancelled = false;
    const SYNC_TERMINAL = new Set(["completed", "failed", "skipped"]);
    const mapSyncLevel = (level) => {
      const l = String(level || "").toLowerCase();
      if (l === "error") return "error";
      if (l === "data") return "data";
      if (l === "system") return "system";
      if (l === "success") return "success";
      if (l === "warn" || l === "warning") return "warn";
      return "info";
    };
    const JOB_TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    const poll = async () => {
      try {
        // Check if the enrichment job itself is done — stop sync-log polling
        const jobStatus = await api.enrichmentV2.status(enrichmentOutreachJobId).catch(() => null);
        if (jobStatus && JOB_TERMINAL.has(jobStatus.status)) {
          // Do one final sync-log fetch, then stop
          const data = await api.enrichmentV2.syncLog(enrichmentOutreachJobId, lastEnrichmentSyncLogIdRef.current).catch(() => null);
          if (!cancelled && data) {
            const lines = Array.isArray(data.lines) ? data.lines : [];
            for (const line of lines) {
              if (line.id != null) lastEnrichmentSyncLogIdRef.current = Math.max(lastEnrichmentSyncLogIdRef.current, Number(line.id));
              addLogRef.current(line.message, mapSyncLevel(line.level));
            }
          }
          clearInterval(iv);
          return;
        }

        const data = await api.enrichmentV2.syncLog(enrichmentOutreachJobId, lastEnrichmentSyncLogIdRef.current);
        if (cancelled || !data) return;
        const lines = Array.isArray(data.lines) ? data.lines : [];
        for (const line of lines) {
          if (line.id != null) lastEnrichmentSyncLogIdRef.current = Math.max(lastEnrichmentSyncLogIdRef.current, Number(line.id));
          addLogRef.current(line.message, mapSyncLevel(line.level));
        }
        // Stop polling when sync is done
        if (data.instantly_sync_status && SYNC_TERMINAL.has(data.instantly_sync_status)) {
          clearInterval(iv);
        }
      } catch (e) {
        if (typeof console !== "undefined") console.error("[enrichment sync-log poll]", e);
      }
    };
    const iv = setInterval(poll, 3000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [enrichmentOutreachJobId]);

  const runDiscovery = async (payload) => {
    const form = payload ?? icpForm;
    console.log('[Discovery] runDiscovery called', { discoverCanRun, form: { industry: form?.industry, maxLeads: form?.maxLeads } });
    if (!discoverCanRun) {
      console.warn('[Discovery] Aborted: discoverCanRun=false');
      addLog("→ Run Discovery cannot run: no lead search integrations configured. Configure at least one in Settings → Integrations.", "info");
      return;
    }
    if (form?.importMode && form?.importedFile?.parsed) {
      setIsProcessing(true);
      setProcessLog([]);
      addLog("→ Import mode: parsing and saving leads...", "system");
      try {
        const parsed = form.importedFile.parsed;
        const columnMap = autoDetectMapping(parsed.headers);
        const importRows = normalizeRows(parsed.rows || [], columnMap);
        if (importRows.length === 0) {
          addLog("→ No valid rows to import after mapping", "error");
          setIsProcessing(false);
          return;
        }
        addLog(`→ Importing ${importRows.length} rows...`, "info");
        const listName = (parsed.name || "").replace(/\.[^.]+$/, "") || form?.listName || "LinkedIn Import";
        const importResult = await api.leadLists.import({ name: listName, rows: importRows });
        const listId = importResult.listId || importResult.id;
        if (!listId) throw new Error("Import did not return list ID");
        if (importResult.status === "importing") {
          addLog("→ Processing import in background...", "info");
          let lastSaved = -1;
          let lastCompanyMsg = "";
          for (let attempt = 0; attempt < 300; attempt++) {
            await new Promise(r => setTimeout(r, 2000));
            try {
              const s = await api.leadLists.importStatus(listId);
              const prog = s.import_progress;
              if (prog?.phase === "companies" && prog.message && prog.message !== lastCompanyMsg) {
                lastCompanyMsg = prog.message;
                addLog(`  → ${prog.message}`, "info");
              }
              if (prog?.phase === "leads" && prog.leadsSaved !== lastSaved) {
                lastSaved = prog.leadsSaved;
                addLog(`  → Saved ${prog.leadsSaved}/${prog.leadsExpected} contacts`, "info");
                if (prog.log?.length) {
                  const tail = prog.log.slice(-5);
                  for (const line of tail) addLog(`      ${line}`, "info");
                }
              }
              if (s.status === "draft") break;
              if (s.status === "error") throw new Error((prog && prog.message) || "Import failed on server");
            } catch (e) {
              if (e?.message === "Import failed on server" || (e?.message && String(e.message).includes("Import failed"))) throw e;
            }
          }
        }
        addLog(`✓ Saved to list "${listName}"`, "success");
        addLog("→ Starting enrichment (verify email, find email, find phone, ICP score, personalisation)...", "system");
        const startRes = await api.leadGeneration.enrichImportStart({
          listId,
          options: { verifyEmail: true, findPhone: true, findEmail: true, companyData: false, icpScore: true, personalisation: true },
        });
        const jobId = startRes.jobId;
        let processedEventCount = 0;
        const poll = async () => {
          const status = await api.leadGeneration.enrichImportStatus(jobId);
          const events = status.events || [];
          for (let i = processedEventCount; i < events.length; i++) {
            const evt = events[i];
            if (evt.event === "progress" && evt.data?.type) {
              const d = evt.data;
              if (d.type === "loaded") addLog(`  Loading ${d.total || 0} leads...`, "info");
              else if (d.type === "verifying") addLog(`  Verifying email ${d.index || 0}/${d.total || 0}`, "info");
              else if (d.type === "finding_email") addLog(`  Finding email ${d.index || 0}/${d.total || 0}: ${d.lead || ""}`, "info");
              else if (d.type === "finding_phone") addLog(`  Finding phone ${d.index || 0}/${d.total || 0}`, "info");
              else if (d.type === "personalising") addLog(`  Personalising ${d.index || 0}/${d.total || 0}`, "info");
            }
          }
          processedEventCount = events.length;
          if (status.status === "complete") {
            addLog(`\n✓ Enrichment complete`, "success");
            if (status.stats) addLog(`  Verified: ${status.stats.verified ?? 0}, Phones: ${status.stats.phonesFound ?? 0}, Emails found: ${status.stats.emailsFound ?? 0}, Personalized: ${status.stats.personalized ?? 0}`, "info");
            const lists = await api.leadLists.list();
            const targetList = Array.isArray(lists) ? lists.find((l) => String(l.id) === String(listId)) : null;
            const contacts = targetList?.contacts || [];
            const mapped = contacts.map((c) => ({
              ...c,
              id: c.id,
              name: c.name || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email,
              bounceRisk: c.bounceRisk || "medium",
            }));
            setEnrichedContacts(mapped);
            setSelectedContacts(new Set(mapped.map((c) => c.id)));
            setStep(2);
            setIsProcessing(false);
            return;
          }
          if (status.status === "error") {
            addLog(`✗ Enrichment failed: ${status.error || "Unknown error"}`, "error");
            setIsProcessing(false);
            return;
          }
          setTimeout(poll, 1500);
        };
        poll();
      } catch (err) {
        addLog(`✗ Import failed: ${err?.message || err}`, "error");
        setIsProcessing(false);
      }
      return;
    }

    setIsProcessing(true);
    setProcessLog([]);
    setCampaignLeadListId(null);
    setEnrichmentOutreachJobId(null);
    instantlyPushedEmailsRef.current = new Set();

    addLog("→ Running discovery cascade (DB → Lead Search providers)...", "system");
    addLog(`→ Target: ${form?.maxLeads || 100} leads — DB first, then cascade for remaining`, "info");
    addLog(`→ ICP: ${form?.industry || "—"}, ${(form?.employeeSizes || ["51-200"]).join(", ")} employees`, "info");
    if (form?.keywords) addLog(`→ Keywords: ${form.keywords}`, "info");
    addLog(`→ Target roles: ${(form?.roles || []).join(", ")}`, "info");
    addLog(`→ Regions: ${(form?.regions || []).join(", ")}`, "info");
    if (form?.lookalikeOnly && form?.lookalike) addLog(`→ Lookalike mode: seed domains`, "info");
    addLog("→ Polling mode: real-time progress (no H12 timeout)", "info");

    const discoverParams = {
      listName: form?.listName,
      industry: form?.industry,
      keywords: form?.keywords,
      employeeSizes: form?.employeeSizes || ["51-200"],
      regions: form?.regions,
      roles: form?.roles,
      maxLeads: form?.maxLeads ? parseInt(form.maxLeads, 10) : 100,
      lookalikeOnly: form?.lookalikeOnly || false,
      lookalike: form?.lookalike,
    };

    const processEvent = (evt) => {
          console.log('[Discovery] SSE event:', evt.event, evt.data?.type ?? evt.data?.message ?? '');
          if (evt.event === "progress") {
            const d = evt.data;
            if (d.type === "postgres_precheck") {
              addLog(`\n→ Postgres pre-check: ${d.dbStrictCount ?? 0} strict matches`, "dim");
            } else if (d.type === "status_message") {
              const templates = [
                "Good progress! {{companies_so_far}} companies found so far",
                "Looking into {{current_step}}...",
                "{{companies_so_far}} of {{target}} companies matched",
                "Querying {{current_step}} for more leads",
                "Building list: {{companies_so_far}} companies ready",
              ];
              const idx = (d.index ?? 0) % templates.length;
              let msg = templates[idx];
              msg = msg.replace(/\{\{companies_so_far\}\}/g, String(d.companiesSoFar ?? 0));
              msg = msg.replace(/\{\{current_step\}\}/g, d.currentStep ?? "database");
              msg = msg.replace(/\{\{target\}\}/g, String(d.targetCount ?? "?"));
              addLog(`  💬 ${msg}`, "info");
            } else if (d.type === "postgres_done") {
              const sqlQueries = d.sqlQueries || [];
              if (sqlQueries.length > 0) {
                addLog(`\n→ Postgres SQL queries run (${sqlQueries.length}):`, "system");
                for (const q of sqlQueries) {
                  const paramsStr = (q.params || []).map((p, i) => `$${i + 1}=${JSON.stringify(p)}`).join(", ");
                  addLog(`  [${q.label}] → ${q.rowCount ?? "?"} rows`, "info");
                  const sqlOneLine = (q.sql || "").replace(/\s+/g, " ").trim();
                  addLog(`  SQL: ${sqlOneLine}`, "dim");
                  if (paramsStr) addLog(`  Params: ${paramsStr}`, "dim");
                }
              } else if (d.count === 0) {
                addLog(`\n→ Postgres ran but returned no matching companies`, "info");
              }
            } else if (d.type === "lookalike_done") {
              addLog(`\n→ Lookalike search: ${d.count ?? 0} companies`, "info");
            } else if (d.type === "cascade_start") {
              addLog(`\n→ Cascade (Lead Search order):`, "system");
            } else if (d.type === "cascade_step") {
              let status = d.tried
                ? (d.count > 0 ? `+${d.count} companies added` : (d.error ? `failed: ${d.error}` : (d.reason || "0 new companies")))
                : (d.reason || "skipped");
              if (d.tried && d.count === 0 && !d.reason && (d.rawCount != null || d.sampleDomains?.length)) {
                const debug = [];
                if (d.rawCount != null) debug.push(`API raw: ${d.rawCount}`);
                if (d.sampleDomains?.length) debug.push(`sample: ${d.sampleDomains.slice(0, 5).join(', ') || '—'}`);
                if (debug.length) status += ` (${debug.join(', ')})`;
              }
              addLog(`  ${d.key}: ${status}`, d.tried && d.count > 0 ? "data" : "dim");
              if (d.tried && d.count === 0 && d.key === "icypeas" && (d.criteria || d.responseShape)) {
                const diag = [];
                if (d.criteria) diag.push(`criteria: industry=${d.criteria.industry ?? '—'}, locations=${(d.criteria.locations || []).join(',') || '—'}, headcount=${d.criteria.headcountMin ?? '—'}-${d.criteria.headcountMax ?? '—'}`);
                if (d.responseShape) diag.push(`response: ${d.responseShape.hasAsyncJob ? 'async job' : 'sync'}, keys=${(d.responseShape.responseKeys || []).join(',') || '—'}`);
                if (diag.length) addLog(`    → ${diag.join('; ')}`, "dim");
              }
            } else if (d.type === "postfilters_done") {
              const postFilterLog = d.postFilterLog || [];
              const anyExcluded = postFilterLog.some(p => (p.excluded || 0) > 0);
              if (anyExcluded) {
                addLog(`\n→ Post-filters applied:`, "system");
                for (const p of postFilterLog) {
                  if ((p.excluded || 0) > 0) {
                    addLog(`  ${p.filter}: ${p.before} → ${p.after} (${p.excluded} excluded)`, "info");
                  }
                }
              }
            } else if (d.type === "companies_ready") {
              const companies = d.companies || [];
              const source = d.source || "unknown";
              addLog(`→ Total: ${d.count ?? companies.length} companies${d.totalPersisted != null ? ` (${d.totalPersisted} saved to DB)` : ''} (source: ${source})`, "info");
              if ((d.count ?? companies.length) === 0) {
                addLog(`⚠ No companies found with current criteria`, "info");
                addLog("→ DB: try broader Industry (e.g. B2B SaaS), fewer employee filters, or North America only", "info");
                addLog("→ Settings > Integrations: connect IcyPeas, AI Ark, or other providers for cascade fallback", "info");
              } else {
                for (const c of companies.slice(0, 10)) {
                  addLog(`  + ${c.name} — ${c.industry || "—"} — ICP: ${c.icpScore || 90}%`, "data");
                }
                if ((d.count ?? 0) > 10) addLog(`  ... and ${(d.count ?? 0) - 10} more`, "dim");
                addLog(`\n✓ Discovery complete: ${d.count ?? 0} companies matched`, "success");
              }
            }
          } else if (evt.event === "done") {
            const result = evt.data;
            const companies = result?.companies || [];
            console.log('[Discovery] Stream done, companies:', companies.length);
            if (companies.length === 0) {
              addLog(`\n→ Run these cURL commands manually (replace YOUR_*_API_KEY):`, "system");
              const industry = form?.industry || "HVAC";
              const keywords = (form?.keywords || "").split(",").map(k => k.trim()).filter(Boolean) || [industry];
              const regions = form?.regions || ["North America"];
              const locIcyPeas = regions.flatMap(r => {
                const s = String(r || "").toLowerCase();
                if (s.includes("north america")) return ["US", "CA", "MX"];
                if (s.includes("latin america")) return ["BR", "MX", "AR", "CO"];
                if (s.includes("europe")) return ["GB", "DE", "FR", "NL", "ES", "IT"];
                if (s.includes("asia") && s.includes("pacific")) return ["JP", "AU", "SG", "IN", "KR"];
                if (s.includes("mena")) return ["AE", "SA"];
                return [r];
              });
              const locAiArk = regions.flatMap(r => {
                const s = String(r || "").toLowerCase();
                if (s.includes("north america")) return ["United States", "Canada", "Mexico"];
                if (s.includes("latin america")) return ["Brazil", "Mexico", "Argentina", "Colombia"];
                if (s.includes("europe")) return ["United Kingdom", "Germany", "France", "Netherlands", "Spain", "Italy"];
                if (s.includes("asia") && s.includes("pacific")) return ["Japan", "Australia", "Singapore", "India"];
                if (s.includes("mena")) return ["United Arab Emirates", "Saudi Arabia"];
                return [r];
              });
              const kwList = [...new Set([industry, ...keywords])].filter(Boolean);
              addLog(`\nIcyPeas (find people):`, "dim");
              const icypeasBody = JSON.stringify({ query: { currentJobTitle: { include: form?.roles || ["CEO", "CTO", "VP Sales"] }, location: { include: [...new Set(locIcyPeas)] }, keyword: { include: kwList }, headcount: { ">=": 1 } }, pagination: { size: 100 } });
              addLog(`curl -X POST "https://app.icypeas.com/api/find-people" -H "Content-Type: application/json" -H "Authorization: YOUR_ICYPEAS_API_KEY" -d '${icypeasBody.replace(/'/g, "'\\''")}'`, "dim");
              addLog(`\nAI Ark (company search):`, "dim");
              const aiArkBody = JSON.stringify({ page: 0, size: 100, account: { industry: { any: { include: [industry] } }, keyword: { any: { include: { mode: "SMART", content: kwList } } }, location: { any: { include: [...new Set(locAiArk)] } } } });
              addLog(`curl -X POST "https://api.ai-ark.com/api/developer-portal/v1/companies" -H "Content-Type: application/json" -H "X-TOKEN: YOUR_AI_ARK_API_KEY" -d '${aiArkBody.replace(/'/g, "'\\''")}'`, "dim");
              setDiscoveredLeads([]);
              setSelectedLeads(new Set());
            } else {
              setDiscoveredLeads(companies);
              setSelectedLeads(new Set(companies.map(c => c.id)));
              setStep(1);
            }
            setIsProcessing(false);
          } else if (evt.event === "error") {
            console.error('[Discovery] Stream error event:', evt.data);
            addLog(`✗ Discovery failed: ${evt.data?.message || "Unknown error"}`, "error");
            addLog("→ Check Settings > Integrations for configured lead search credentials", "info");
            setDiscoveredLeads([]);
            setSelectedLeads(new Set());
            setIsProcessing(false);
          }
    };

    try {
      const startFn = api?.leadGeneration?.discoverStart;
      const statusFn = api?.leadGeneration?.discoverStatus;
      if (typeof startFn === "function" && typeof statusFn === "function") {
        const { jobId } = await startFn(discoverParams);
        let processedEventCount = 0;
        while (true) {
          await new Promise(r => setTimeout(r, 2500));
          const resp = await statusFn(jobId);
          if (!resp?.success) break;
          const events = resp.events || [];
          for (let i = processedEventCount; i < events.length; i++) {
            processEvent(events[i]);
          }
          processedEventCount = events.length;
          if (resp.status === "complete") {
            const companies = resp.companies || [];
            const lastEvt = events[events.length - 1];
            if (companies.length > 0 && (!lastEvt || lastEvt.event !== "done")) {
              setDiscoveredLeads(companies);
              setSelectedLeads(new Set(companies.map(c => c.id)));
              setStep(1);
            }
            break;
          }
          if (resp.status === "error") {
            addLog(`✗ Discovery failed: ${resp.error || "Job failed"}`, "error");
            addLog("→ Check Settings > Integrations for configured lead search credentials", "info");
            setDiscoveredLeads([]);
            setSelectedLeads(new Set());
            break;
          }
        }
      } else {
        addLog("✗ Discovery polling not available, using stream (may timeout)…", "warning");
        await api.leadGeneration.discoverStream(discoverParams, processEvent);
      }
      setIsProcessing(false);
    } catch (err) {
      console.error('[Discovery] runDiscovery failed:', err?.message, err);
      addLog(`✗ Discovery failed: ${err.message}`, "error");
      addLog("→ Check Settings > Integrations for configured lead search credentials", "info");
      setDiscoveredLeads([]);
      setSelectedLeads(new Set());
      setIsProcessing(false);
    }
  };

  const runEnrichment = async () => {
    setIsProcessing(true);
    setProcessLog([]);
    setEnrichmentCompanyResults([]);
    const selected = discoveredLeads.filter(c => selectedLeads.has(c.id));
    console.log('[Enrichment] runEnrichment called, selected=', selected.length, 'companies:', selected.map(c => ({ id: c.id, name: c.name, domain: c.domain })));
    if (selected.length === 0) {
      addLog("✗ No companies selected for enrichment. Select at least one from the discovery results.", "error");
      setIsProcessing(false);
      return;
    }
    addLog("→ Checking DB enrichment cache (skip if enriched < 30 days)...", "system");
    addLog("→ Using Lead Search cascade for person finding, Lead Enrichment cascade for email", "info");
    addLog("→ Polling mode: real-time progress (no H15 timeout)", "info");

    let allContacts = [];
    let doneData = null;
    try {
      const startFn = api?.leadGeneration?.enrichBulkStart;
      const statusFn = api?.leadGeneration?.enrichBulkStatus;
      if (typeof startFn !== "function" || typeof statusFn !== "function") {
        addLog("✗ Enrichment polling not available, falling back to bulk…", "warning");
        const bulkFn = api?.leadGeneration?.enrichBulk;
        if (typeof bulkFn !== "function") {
          addLog("✗ Enrichment failed: no enrichment API available", "error");
          setIsProcessing(false);
          return;
        }
        const result = await bulkFn({
          companies: selected.map(c => ({ id: c.id, name: c.name, domain: c.domain || c.website?.replace?.(/^https?:\/\//, ''), industry: c.industry, website: c.website })),
          roles: icpForm.roles || ["CEO", "CTO", "VP Sales"],
          listName: icpForm.listName || undefined,
        });
        allContacts = (result.contacts || []).map((c, i) => ({ ...c, id: c.id || `gen-${i + 1}` }));
        doneData = { totalContacts: allContacts.length, fromCache: allContacts.filter(c => c.fromCache).length, enriched: allContacts.filter(c => !c.fromCache).length };
      } else {
        const BATCH_SIZE = 2;
        const companyPayload = (list) => list.map(c => ({
          id: c.id,
          name: c.name,
          domain: c.domain || c.website?.replace?.(/^https?:\/\//, ''),
          industry: c.industry,
          website: c.website,
        }));
        addLog(`→ Starting enrichment for ${selected.length} companies (batchSize=${BATCH_SIZE})…`, "info");

        const { jobId } = await startFn({
          companies: companyPayload(selected),
          roles: icpForm.roles || ["CEO", "CTO", "VP Sales"],
          listName: icpForm.listName || undefined,
          batchSize: BATCH_SIZE,
        });

        let processedEventCount = 0;
        const processEvent = (evt) => {
          if (evt.event === "contact") {
            const c = evt.data;
            allContacts.push({ ...c, id: c.id || `gen-${allContacts.length + 1}` });
            setEnrichedContacts([...allContacts]);
          } else if (evt.event === "progress") {
            const d = evt.data;
            if (d.type === "connected") addLog("→ Stream connected", "info");
            else if (d.type === "started") addLog(`→ Started: ${d.totalCompanies ?? 0} companies, roles: [${(d.roles || []).join(", ")}]`, "info");
            else if (d.type === "company_start") addLog(`→ [${d.index}/${d.total}] Processing ${d.company}…`, "dim");
            else if (d.type === "cache_check") addLog(`→ [${d.index}/${d.total}] Checking cache for ${d.company}…`, "dim");
            else if (d.type === "cache_miss") addLog(`→ [${d.index}/${d.total}] ${d.company} — cache miss, running cascade`, "info");
            else if (d.type === "cache_hit") addLog(`→ ${d.company} — cache hit (${d.contactCount} contacts)`, "info");
            else if (d.type === "cache_summary") addLog(`→ Loaded ${d.fromCache} contacts from cache, ${d.remaining} companies need enrichment`, "info");
            else if (d.type === "enriching") {
              const stepLabels = { finding_people: "finding people", finding_email: `finding email for ${d.person || ""}`, verifying_email: `verifying ${d.email || ""}`, upsert_company: "processing" };
              addLog(`→ [${d.index}/${d.total}] ${d.company}: ${stepLabels[d.step] || d.step}${d.provider ? ` (${d.provider})` : ""}`, "dim");
            } else if (d.type === "people_found") addLog(`→ [${d.index}/${d.total}] ${d.company} — ${d.provider} found ${d.count} people`, "info");
            else if (d.type === "email_found") addLog(`→ [${d.index}/${d.total}] ${d.person} — email found via ${d.source}`, "info");
            else if (d.type === "email_verified") addLog(`→ [${d.index}/${d.total}] ${d.person} — ${d.verified ? "✓ verified" : "✗ not verified"}`, d.verified ? "success" : "warning");
            else if (d.type === "no_people") {
              addLog(`→ [${d.index}/${d.total}] ${d.company} — no people found`, "warning");
              setEnrichmentCompanyResults(prev => { const next = [...(prev || [])]; if (!next.find(r => r.name === d.company)) next.push({ name: d.company, contactsFound: 0, reasons: d.reasons || [] }); return next; });
            } else if (d.type === "company_done") addLog(`→ [${d.index}/${d.total}] ${d.company} done (${d.contactsSoFar} contacts so far)`, "dim");
            else if (d.type === "cascade_start" || d.type === "cascade_starting") addLog(`→ Starting enrichment cascade for ${d.total || d.count} companies`, "system");
            else addLog(`→ [${d.index ?? "?"}/${d.total ?? "?"}] ${d.company ?? ""}: ${d.type}`, "dim");
          } else if (evt.event === "log") {
            const t = evt.data?.type === "error" ? "error" : evt.data?.type === "warn" ? "warning" : "info";
            addLog(evt.data?.msg ?? "", t);
          } else if (evt.event === "done") {
            doneData = evt.data;
            if (evt.data?.companyResults?.length) setEnrichmentCompanyResults(evt.data.companyResults);
            if (evt.data?.contacts && !evt.data?.hasMore) allContacts = evt.data.contacts.map((c, i) => ({ ...c, id: c.id || `gen-${i + 1}` }));
          } else if (evt.event === "error") {
            addLog(`✗ ${evt.data?.message || "Unknown error"}`, "error");
          }
        };

        while (true) {
          await new Promise(r => setTimeout(r, 2500));
          const resp = await statusFn(jobId);
          if (!resp?.success) break;
          const events = resp.events || [];
          for (let i = processedEventCount; i < events.length; i++) {
            processEvent(events[i]);
          }
          processedEventCount = events.length;
          if (resp.status === "complete") {
            if (doneData?.companyResults?.length) setEnrichmentCompanyResults(doneData.companyResults);
            break;
          }
          if (resp.status === "error") {
            addLog(`✗ ${resp.error || "Job failed"}`, "error");
            break;
          }
        }
      }

      if (doneData || allContacts.length > 0) {
        const fromCache = allContacts.filter(c => c.fromCache).length;
        const enriched = allContacts.length - fromCache;
        addLog(`\n✓ Enrichment complete: ${allContacts.length} contacts (${fromCache} cached, ${enriched} enriched)`, "success");
        const verified = allContacts.filter(c => c.bounceRisk === "low").length;
        addLog(`  Verified emails: ${verified}/${allContacts.length}`, "info");
      }
    } catch (err) {
      addLog(`✗ Enrichment failed: ${err.message}`, "error");
      if (typeof console !== "undefined") console.error("[Enrichment] Error:", err);
    }

    setEnrichedContacts(allContacts);
    setSelectedContacts(new Set(allContacts.map(c => c.id)));
    setIsProcessing(false);
    setStep(2);
  };

  const skipPersonalization = async () => {
    setIsProcessing(true);
    setProcessLog([]);
    const selected = enrichedContacts.filter(c => selectedContacts.has(c.id));
    addLog("→ Skipping personalization — using placeholder templates", "system");
    await sleep(600);
    let userName = "";
    try {
      const me = await api.me();
      userName = me?.user?.name || "";
    } catch {}
    const emails = {};
    const placeholderBody = `[Personalized email body — to be written]${userName.trim() ? `\n\nBest,\n${userName.trim()}` : ""}`;
    for (const contact of selected) {
      emails[contact.id] = {
        subject: `Quick question for ${contact.company}`,
        body: placeholderBody,
      };
    }
    addLog(`✓ ${selected.length} placeholder emails created`, "success");
    setPersonalizedEmails(emails);
    setIsProcessing(false);
    setStep(3);
  };

  const generatePreview = async () => {
    setIsPreviewLoading(true);
    const selected = enrichedContacts.filter(c => selectedContacts.has(c.id));
    const previewContacts = selected.slice(0, 3);
    const previews = {};
    const promptTemplate = promptText;
    let userName = "";
    let userCompany = "";
    try {
      const me = await api.me();
      userName = me?.user?.name || "";
      userCompany = me?.user?.company || "";
    } catch {}
    const appendSignOff = (body) => {
      const b = (body || "").trimEnd();
      if (!userName.trim()) return b;
      if (b.includes(userName.trim())) return b;
      return `${b}\n\nBest,\n${userName.trim()}`;
    };

    try {
      for (const contact of previewContacts) {
        // Try to generate with OpenAI
        try {
          const companyDomain = contact.company_domain || (contact.company_website && contact.company_website.replace(/^https?:\/\//, '').split('/')[0]) || '';
          const leadData = {
            firstName: contact.name.split(" ")[0],
            lastName: contact.name.split(" ").slice(1).join(" "),
            fullName: contact.name,
            company: contact.company,
            company_domain: companyDomain,
            company_website: companyDomain ? `https://${companyDomain}` : '',
            title: contact.title,
            industry: icpForm.industry,
            linkedinActivity: contact.linkedinData ? `${contact.linkedinData.connections} connections, ${contact.linkedinData.posts} recent posts` : "no LinkedIn data",
            your_name: userName,
            your_company: userCompany,
          };
          
          const res = await api.leadGeneration.personalize({
            prompt: promptTemplate,
            leadData
          });
          const { subject, body: rawBody } = subjectBodyFromPersonalizeApiResponse(res, contact);
          const body = appendSignOff(rawBody);

          previews[contact.id] = { subject, body };
          
        } catch (err) {
          previews[contact.id] = {
            subject: `Quick question for ${contact.company}`,
            body: `I came across ${contact.company} and was impressed by what you're building...`,
          };
        }
        
        await sleep(500);
      }
    } catch (err) {
      for (const contact of previewContacts) {
        previews[contact.id] = {
          subject: `Quick question for ${contact.company}`,
          body: `I came across ${contact.company} and was impressed by what you're building...`,
        };
      }
    }
    
    setPreviewEmails(previews);
    setIsPreviewLoading(false);
  };

  const approveAndPersonalizeAll = async () => {
    setShowPersonalizeModal(false);
    setIsApproved(true);
    setIsProcessing(true);
    setProcessLog([]);
    const selected = enrichedContacts.filter(c => selectedContacts.has(c.id));
    addLog("→ Connecting to OpenAI API (GPT-4o-mini)...", "system");
    await sleep(600);
    let userName = "";
    let userCompany = "";
    try {
      const me = await api.me();
      userName = me?.user?.name || "";
      userCompany = me?.user?.company || "";
    } catch {}
    const appendSignOff = (body) => {
      const b = (body || "").trimEnd();
      if (!userName.trim()) return b;
      if (b.includes(userName.trim())) return b;
      return `${b}\n\nBest,\n${userName.trim()}`;
    };

    addLog("✓ Model ready", "success");
    addLog(`→ Using prompt: "${savedPrompts[selectedPromptKey]?.label || 'Custom'}"`, "info");
    addLog(`→ Generating for ${selected.length} contacts...\n`, "system");

    const emails = {};
    const promptTemplate = promptText;
    
    try {
      for (const contact of selected) {
        addLog(`→ Generating personalized email for ${contact.name} (${contact.company})...`, "system");
        
        try {
          // Prepare lead data for personalization
          const companyDomain = contact.company_domain || (contact.company_website && contact.company_website.replace(/^https?:\/\//, '').split('/')[0]) || '';
          const leadData = {
            firstName: contact.name.split(" ")[0],
            lastName: contact.name.split(" ").slice(1).join(" "),
            fullName: contact.name,
            company: contact.company,
            company_domain: companyDomain,
            company_website: companyDomain ? `https://${companyDomain}` : '',
            title: contact.title,
            industry: icpForm.industry,
            linkedinActivity: contact.linkedinData ? `${contact.linkedinData.connections} connections, ${contact.linkedinData.posts} recent posts` : "no LinkedIn data",
            your_name: userName,
            your_company: userCompany,
          };
          
          // Call OpenAI for personalization
          const res = await api.leadGeneration.personalize({
            prompt: promptTemplate,
            leadData
          });
          const { subject, body: rawBody } = subjectBodyFromPersonalizeApiResponse(res, contact);
          const body = appendSignOff(rawBody);
          
          emails[contact.id] = { subject, body };
          addLog(`  ✓ Subject: "${subject}"`, "data");
          
        } catch (err) {
          // Fallback to template if AI fails
          addLog(`  ✗ AI personalization failed, using template`, "dim");
          emails[contact.id] = { 
            subject: `Quick question for ${contact.company}`, 
            body: `I came across ${contact.company} and was impressed by what you're building...` 
          };
        }
        
        await sleep(300);
      }
      
      await sleep(400);
      addLog(`\n✓ Personalization complete: ${Object.keys(emails).length} emails generated`, "success");
      
    } catch (err) {
      addLog(`✗ Personalization error: ${err.message}`, "error");
      addLog("→ Using template fallback...", "info");
      for (const contact of selected) {
        emails[contact.id] = { 
          subject: `Quick question for ${contact.company}`, 
          body: `I came across ${contact.company} and was impressed by what you're building...` 
        };
      }
    }
    
    setPersonalizedEmails(emails);
    setIsProcessing(false);
    setStep(3);
  };

  const runQueueOutreach = async () => {
    setIsProcessing(true);
    setProcessLog([]);
    setOutreachResults(null);
    const contacts = enrichedContacts.filter(c => selectedContacts.has(c.id));
    const listName = icpForm.listName || "Untitled List";
    let instantlyCampaignIdForSync = null;

    addLog(`→ Saving lead list: "${listName}"...`, "system");

    // Reuse enrichment list when user arrived from Enrichment v2 → Campaigns; otherwise create a new list
    let listId = campaignLeadListId;
    if (!listId) {
      try {
        const listResult = await api.leadLists.create({
          name: listName,
          icp_profile_data: icpForm,
        });
        listId = listResult.id;
        addLog(`✓ Lead list created (ID: ${listId})`, "success");
      } catch (err) {
        addLog(`⚠ Could not save to database: ${err.message}`, "info");
      }
    } else {
      addLog(`✓ Using existing list from enrichment (ID: ${listId})`, "success");
    }

    let savedCount = 0;
    if (listId) {
      if (!campaignLeadListId) {
        try {
          const normDomain = (d) => (d || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "").toLowerCase().trim() || null;
          const companyIdByDomain = {};

          for (const contact of contacts) {
            const companyData = discoveredLeads.find(c => c.name === contact.company);
            const rawDomain = companyData?.website || companyData?.domain || "";
            const domainKey = normDomain(rawDomain) || contact.company?.toLowerCase() || "";

            if (!companyIdByDomain[domainKey]) {
              try {
                const res = await api.companies.create({
                  name: contact.company,
                  domain: rawDomain,
                  industry: companyData?.industry || icpForm.industry,
                  employees: companyData?.employees || null,
                  location: companyData?.location || "",
                  icpScore: companyData?.icpScore || null,
                });
                if (res?.id) companyIdByDomain[domainKey] = res.id;
              } catch (e) {
                // Upsert returns 200 on existing; any error is logged
              }
            }

            try {
              const personalizedEmail = personalizedEmails[contact.id];
              await api.leads.create({
                list_id: listId,
                company_id: companyIdByDomain[domainKey] || null,
                first_name: contact.name.split(" ")[0],
                last_name: contact.name.split(" ").slice(1).join(" "),
                email: contact.email !== "Not found" ? contact.email : null,
                title: contact.title,
                linkedin_url: contact.linkedin || null,
                company_name: contact.company,
                company_domain: companyData?.website || null,
                email_bounce_risk: ["low", "medium", "high"].includes(contact.bounceRisk) ? contact.bounceRisk : null,
                linkedinData: contact.linkedinData || null,
                personalisation_json: personalizedEmail
                  ? {
                      subject: personalizedEmail.subject,
                      body: personalizedEmail.body,
                      channel: channelAssignments[contact.id]?.email ? "email" : channelAssignments[contact.id]?.linkedin ? "linkedin" : "none",
                    }
                  : null,
              });
              savedCount++;
            } catch (e) {
              console.error(`Failed to save lead ${contact.name}:`, e);
            }
          }
          addLog(`✓ ${savedCount}/${contacts.length} contacts saved to database`, "success");
        } catch (err) {
          addLog(`⚠ Some contacts could not be saved: ${err.message}`, "info");
        }
      } else {
        try {
          for (const contact of contacts) {
            try {
              const personalizedEmail = personalizedEmails[contact.id];
              if (contact.id && personalizedEmail) {
                await api.leads.update(contact.id, {
                  personalisation_json: {
                    subject: personalizedEmail.subject,
                    body: personalizedEmail.body,
                    channel: channelAssignments[contact.id]?.email ? "email" : channelAssignments[contact.id]?.linkedin ? "linkedin" : "none",
                  },
                });
              }
            } catch (e) {
              console.error(`Failed to update lead ${contact.name}:`, e);
            }
          }
          savedCount = contacts.length;
          addLog(`✓ Personalization synced for ${savedCount} contact(s) on enrichment list`, "success");
        } catch (err) {
          addLog(`⚠ Could not sync personalization: ${err.message}`, "info");
        }
      }
    }

    await sleep(300);
    addLog(``, "info");

    const emailContacts = contacts.filter(c => channelAssignments[c.id]?.email);
    const linkedinContacts = contacts.filter(c => channelAssignments[c.id]?.linkedin);
    const listOnlyContacts = contacts.filter(c => channelAssignments[c.id]?.listOnly);

    let emailResult = null;
    let linkedinResult = null;

    // Email outreach via Instantly.ai
    if (emailContacts.length > 0 && emailPlatform === "instantly") {
      addLog(`→ Connecting to Instantly.ai...`, "system");
      await sleep(600);

      const campaignDisplay = emailCampaignLabel || emailCampaign || "Default Campaign";
      try {
        let campaignId = null;
        if (emailCampaign) {
          try {
            const campaignsRes = await api.instantly.campaigns.list();
            campaignId = resolveInstantlyCampaignId(emailCampaign, campaignsRes);
            if (campaignId) instantlyCampaignIdForSync = campaignId;
          } catch (_) { /* use default */ }
        }

        const leadsData = emailContacts.map(contact => ({
          email: contact.email,
          first_name: contact.name.split(" ")[0],
          last_name: contact.name.split(" ").slice(1).join(" "),
          company: contact.company,
          personalization: personalizedEmails[contact.id]?.body || "",
        }));

        const result = await api.instantly.leads.bulk(leadsData, campaignId);

        const successful = (result.results || []).filter(r => r.success).length;
        const failedEmails = (result.results || []).filter(r => !r.success).map(r => r.email);
        for (const r of result.results || []) {
          if (r.success && r.email) {
            instantlyPushedEmailsRef.current.add(String(r.email).trim().toLowerCase());
          }
        }
        emailResult = { platform: "instantly", campaign: campaignDisplay, attempted: emailContacts.length, successful, failedEmails: failedEmails.length ? failedEmails : undefined };
        addLog(`✓ ${successful}/${emailContacts.length} leads added to Instantly campaign`, "success");

        for (const r of (result.results || [])) {
          if (r.success) {
            addLog(`  ✓ ${r.email}`, "data");
          } else {
            addLog(`  ✗ ${r.email} - failed${r.error ? `: ${r.error}` : ""}`, "error");
          }
          await sleep(100);
        }
      } catch (err) {
        emailResult = { platform: "instantly", campaign: campaignDisplay, attempted: emailContacts.length, successful: 0 };
        addLog(`✗ Instantly.ai error: ${err.message}`, "error");
        addLog(`→ ${emailContacts.length} leads saved locally`, "info");
        for (const contact of emailContacts) {
          addLog(`  + ${contact.name} → ${contact.email}`, "data");
          await sleep(100);
        }
      }

      await sleep(300);
      addLog(``, "info");
    }

    // Email outreach via SmartLead
    if (emailContacts.length > 0 && emailPlatform === "smartlead") {
      addLog(`→ Connecting to SmartLead...`, "system");
      await sleep(600);

      try {
        let campaignId = null;
        if (emailCampaign) {
          try {
            const campaignsRes = await api.smartlead.campaigns.list();
            const items = Array.isArray(campaignsRes) ? campaignsRes : campaignsRes?.data ?? campaignsRes?.campaigns ?? [];
            const match = items.find(c => (c.name || '').trim() === (emailCampaign || '').trim());
            if (match?.id != null) campaignId = String(match.id);
          } catch (_) { /* fall through */ }
        }
        if (!campaignId) throw new Error('Select a SmartLead campaign first');

        const leadList = emailContacts.map(contact => ({
          email: contact.email,
          first_name: contact.name.split(" ")[0],
          last_name: contact.name.split(" ").slice(1).join(" "),
          company_name: contact.company,
          linkedin_url: contact.linkedin || '',
          custom_fields: personalizedEmails[contact.id]?.body ? { personalization: personalizedEmails[contact.id].body } : {},
        }));

        const result = await api.smartlead.leads.add(campaignId, leadList);
        const successful = result?.added_count ?? 0;
        const skipped = result?.skipped_count ?? 0;
        emailResult = { platform: "smartlead", campaign: emailCampaign || "Default Campaign", attempted: emailContacts.length, successful };
        addLog(`✓ ${successful}/${emailContacts.length} leads added to SmartLead campaign${skipped > 0 ? ` (${skipped} skipped)` : ''}`, "success");

        for (const contact of emailContacts) {
          addLog(`  ✓ ${contact.email}`, "data");
          await sleep(100);
        }
      } catch (err) {
        emailResult = { platform: "smartlead", campaign: emailCampaign || "Default Campaign", attempted: emailContacts.length, successful: 0 };
        addLog(`✗ SmartLead error: ${err.message}`, "error");
        addLog(`→ ${emailContacts.length} leads saved locally`, "info");
        for (const contact of emailContacts) {
          addLog(`  + ${contact.name} → ${contact.email}`, "data");
          await sleep(100);
        }
      }

      await sleep(300);
      addLog(``, "info");
    }

    // LinkedIn outreach via HeyReach
    if (linkedinContacts.length > 0 && linkedinPlatform === "heyreach") {
      addLog(`→ Connecting to HeyReach...`, "system");
      await sleep(600);
      
      try {
        const leadsData = linkedinContacts.map(contact => ({
          profileUrl: contact.linkedin,
          firstName: contact.name.split(" ")[0],
          lastName: contact.name.split(" ").slice(1).join(" "),
          emailAddress: contact.email,
          companyName: contact.company,
          position: contact.title,
        }));
        
        await api.heyreach.campaigns.addLeadsDefault(leadsData);
        linkedinResult = { platform: "heyreach", campaign: linkedinCampaign || "Default Campaign", attempted: linkedinContacts.length, successful: linkedinContacts.length };
        addLog(`✓ ${linkedinContacts.length} leads added to HeyReach campaign`, "success");
        
        for (const contact of linkedinContacts) {
          addLog(`  ✓ ${contact.name}`, "data");
          await sleep(100);
        }
        
      } catch (err) {
        linkedinResult = { platform: "heyreach", campaign: linkedinCampaign || "Default Campaign", attempted: linkedinContacts.length, successful: 0 };
        addLog(`✗ HeyReach error: ${err.message}`, "error");
        addLog(`→ ${linkedinContacts.length} leads saved locally`, "info");
        for (const contact of linkedinContacts) {
          addLog(`  + ${contact.name} → ${contact.linkedin || "profile found"}`, "data");
          await sleep(100);
        }
      }
      
      await sleep(300);
      addLog(``, "info");
    }

    if (listOnlyContacts.length > 0) {
      addLog(`→ ${listOnlyContacts.length} leads saved to list only (no outreach platform)`, "info");
    }

    await sleep(400);
    addLog(`\n✓ All done! Summary:`, "success");
    if (emailContacts.length > 0) addLog(`  📧 ${emailContacts.length} → Email (${emailPlatform === "instantly" ? "Instantly.ai" : "SmartLead"})`, "info");
    if (linkedinContacts.length > 0) addLog(`  💼 ${linkedinContacts.length} → LinkedIn (${linkedinPlatform === "heyreach" ? "HeyReach" : "AimFox"})`, "info");
    if (listOnlyContacts.length > 0) addLog(`  📋 ${listOnlyContacts.length} → List only`, "info");
    
    if (listId && enrichmentOutreachJobId && instantlyCampaignIdForSync) {
      addLog(`→ Instantly: the server will add valid personalized leads to your campaign after enrichment completes (see Agent log for sync progress).`, "info");
    }
    setCampaignLeadListId(null);

    if (listId) {
      await sleep(200);
      addLog(`\n✓ Lead list "${listName}" saved to database!`, "success");
      addLog(`  View in "Outbound → Lead Lists"`, "info");
    }

    if (!enrichmentOutreachJobId) {
      setIcpForm(prev => ({ ...prev, listName: "" }));
    }

    const results = {};
    if (listId && savedCount > 0) results.listSaved = { name: listName, id: listId, count: savedCount };
    if (emailResult) results.email = emailResult;
    if (linkedinResult) results.linkedin = linkedinResult;
    if (listOnlyContacts.length > 0) results.listOnly = listOnlyContacts.length;
    setOutreachResults(results);

    setOutreachQueue(contacts.map(c => ({
      contact: c,
      email: personalizedEmails[c.id],
      channels: channelAssignments[c.id] || {},
      status: "queued",
    })));
    setIsProcessing(false);
    const pageAfter = activePageRef.current;
    if (pageAfter === "leads") setStep(4);
    else if (pageAfter === "leads_mgmt") setLeadsMgmtShowQueued(true);
  };

  const sleep = ms => new Promise(r => setTimeout(r, ms));

  const handleEnrichmentJobStarted = useCallback(async (jobId) => {
    try {
      setProcessLog([]);
      instantlyPushedEmailsRef.current.clear();
      let j = await api.enrichmentV2.status(jobId);
      let listId = j?.list_id;
      if (!listId) {
        await new Promise(r => setTimeout(r, 500));
        j = await api.enrichmentV2.status(jobId);
        listId = j?.list_id;
      }
      setEnrichmentOutreachJobId(jobId);
      if (listId) setCampaignLeadListId(listId);
      setEmailPlatform("instantly");
      setEmailCampaign(lmPreflightCampaignId);
      setEmailCampaignLabel(lmPreflightCampaignLabel);
    } catch (e) {
      if (typeof console !== "undefined") console.error("[handleEnrichmentJobStarted]", e);
    }
  }, [lmPreflightCampaignId, lmPreflightCampaignLabel]);

  const maxStepReached = outreachQueue.length > 0 ? 4
    : Object.keys(personalizedEmails).length > 0 ? 3
    : enrichedContacts.length > 0 ? 2
    : discoveredLeads.length > 0 ? 1
    : 0;

  const STEPS = [
    { label: "01", name: "Define ICP", icon: "◎" },
    { label: "02", name: "Discover", icon: "⟐" },
    { label: "03", name: "Enrich", icon: "◈" },
    { label: "04", name: "Campaigns", icon: "✦" },
    { label: "05", name: "Queued", icon: "➤" },
  ];

  return (
    <div style={{ background: COLORS.bg, height: "100%", color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14, display: "flex", position: "relative" }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" rel="stylesheet" />

      {/* Left Sidebar */}
      <div style={{
        width: 220, flexShrink: 0, background: COLORS.surface,
        borderRight: `1px solid ${COLORS.border}`,
        display: "flex", flexDirection: "column", height: "100%",
      }}>
        {/* Logo + credits pill (centered) */}
        <div style={{ padding: "20px 18px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <img src={LOGO_SRC} alt="ConsultiX" style={{ height: 32, objectFit: "contain" }} />
            {globalCredits !== null && (
              <div
                onClick={() => { setActivePage("account"); window.location.hash = "#account-billing"; }}
                style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 6, background: COLORS.bg, border: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}
                title="Click to manage credits · Refreshes every 15s"
              >
                <span style={{ fontSize: 10 }}>💰</span>
                <span style={{ fontFamily: FONT, fontSize: 10, fontWeight: 600, color: globalCredits > 100 ? COLORS.accent : globalCredits > 0 ? COLORS.warn : COLORS.danger }}>{globalCredits.toLocaleString()} credits</span>
              </div>
            )}
          </div>
        </div>

        {/* Nav Items - All scrollable together */}
        <div style={{ flex: 1, overflow: "auto", paddingBottom: 40 }}>
          <div style={{ padding: "12px 10px", paddingBottom: 40 }}>

          {/* Project Selector — Above everything */}


          {[
            { key: "dashboard", label: "Dashboard", icon: "home", desc: "Overview & metrics" },
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{page.label}</div>
            </button>
          ))}

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, padding: "16px 10px 8px", marginBottom: 4 }}>
            CRM
          </div>
          {[
            { key: "crm", label: "CRM Pipeline", icon: "leaderboard", desc: "Track & manage deals" },
            { key: "appointments", label: "Appointments", icon: "calendar_month", desc: "Calls & calendar" },
            { key: "unibox", label: "Unibox", icon: "inbox", desc: "Unified inbox & AI SDR" },
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{page.label}</div>
            </button>
          ))}

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, padding: "16px 10px 8px", marginBottom: 4 }}>
            SOLUTIONS
          </div>
          {[
            { key: "audit", label: "Strategy", icon: "search_insights", desc: "AI audits & analysis" },
            { key: "workflows", label: "Workflows", icon: "settings", desc: "Automation library" },
            { key: "council", label: "AI Council", icon: "psychology", desc: "Strategic advisor" },
            // AI Assistant (sol_assistant) hidden — re-enable: spread enabledSolutions sol_assistant entry
            // Website Builder hidden — re-enable when ready
            // ...(enabledSolutions.includes("website_builder") ? [{ key: "website_builder", label: "Website Builder", icon: "language", desc: "AI-powered sites" }] : []),
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{page.label}</div>
            </button>
          ))}

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, padding: "16px 10px 8px", marginBottom: 4 }}>
            CAMPAIGNS
          </div>
          {[
            { key: "niche_researcher", label: "Niche Researcher", icon: "track_changes", desc: "Find your ideal niche" },
            { key: "leads_mgmt", label: "Get Leads", icon: "groups", desc: "AI Ark lead pipeline", indicator: enrichmentRunningJobs.length > 0, maintenance: LEAD_GEN_MAINTENANCE },
            { key: "leadlists", label: "Lead Lists", icon: "list_alt", desc: "View scraped data" },
            { key: "campaigns_messaging", label: "Messaging", icon: "edit_note", desc: "AI copywriting workshop" },
            { key: "campaigns_email", label: "Cold Email", icon: "mail", desc: "Instantly / SmartLead" },
            { key: "campaigns_linkedin", label: "LinkedIn", icon: "handshake", desc: "HeyReach / AimFox" },
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{page.label}</div>
              {page.maintenance && <span className="material-symbols-outlined" title="Under maintenance" style={{ fontSize: 16, color: COLORS.warn, flexShrink: 0 }}>construction</span>}
              {page.indicator && <span style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.green, animation: "pulse 1.5s infinite", flexShrink: 0 }} />}
            </button>
          ))}

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, padding: "16px 10px 8px", marginBottom: 4 }}>
            SALES
          </div>
          {[
            { key: "sales_analyser", label: "Call Analyser", icon: "analytics", desc: "AI call feedback" },
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{page.label}</div>
            </button>
          ))}

          {false && (
          <>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, padding: "16px 10px 8px", marginBottom: 4 }}>
            CONTENT
          </div>
          {[
            { key: "content_studio", label: "Content Studio", icon: "edit_note", desc: "Create & schedule across platforms" },
            { key: "content_linkedin", label: "LinkedIn", icon: "history_edu", desc: "Posts, carousels & scheduling" },
            { key: "content_community", label: "Community", icon: "chat", desc: "Engagement & responses" },
          ].map(page => (
            <button key={page.key} onClick={() => setActivePage(page.key)} style={{ width: "100%", padding: "10px 12px", marginBottom: 2, background: activePage === page.key ? COLORS.accentBg : "transparent", border: activePage === page.key ? `1px solid ${COLORS.accent}22` : "1px solid transparent", borderRadius: 8, color: activePage === page.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{page.icon}</span>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{page.label}</div>
            </button>
          ))}
          </>
          )}
          
          {icpForm.listName && step > 0 && activePage === "leads" && (
            <div style={{ padding: "4px 8px", marginTop: 16, marginBottom: 8, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
              <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 2 }}>ACTIVE LIST</div>
              <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, wordBreak: "break-word" }}>{icpForm.listName}</div>
            </div>
          )}

          </div>
        </div>

        {/* Pinned Footer — always visible at bottom of sidebar */}
        <div style={{ flexShrink: 0, padding: "8px 10px", borderTop: `1px solid ${COLORS.border}` }}>
          <button
            onClick={() => setActivePage("settings")}
            style={{
              width: "100%", padding: "10px 12px",
              background: activePage === "settings" ? COLORS.accentBg : "transparent",
              border: activePage === "settings" ? `1px solid ${COLORS.accent}22` : "1px solid transparent",
              borderRadius: 8,
              color: activePage === "settings" ? COLORS.accent : COLORS.textMuted,
              fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500,
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 10,
              transition: "all 0.15s",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>settings</span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Settings</div>
          </button>
          <button
            onClick={() => setActivePage("account")}
            style={{
              width: "100%", padding: "10px 12px",
              background: activePage === "account" ? COLORS.accentBg : "transparent",
              border: activePage === "account" ? `1px solid ${COLORS.accent}22` : "1px solid transparent",
              borderRadius: 8,
              color: activePage === "account" ? COLORS.accent : COLORS.textMuted,
              fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500,
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 10,
              transition: "all 0.15s",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person</span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Account</div>
          </button>
          <button
            onClick={() => { api.clearToken(); window.location.reload(); }}
            style={{
              width: "100%", padding: "10px 12px", marginTop: 4,
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: 8,
              color: COLORS.danger,
              fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500,
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 10,
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = COLORS.dangerBg; e.currentTarget.style.borderColor = COLORS.danger + "22"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>logout</span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Log Out</div>
          </button>
        </div>
      </div>

      {/* Main Content — credits now live in the sidebar header, under the logo */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {activePage === "dashboard" && <DashboardView setActivePage={setActivePage} />}

        {activePage === "leads" && (<>
          {/* Step Nav */}
          <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface, flexShrink: 0 }}>
            {STEPS.map((s, i) => (
              <button
                key={i}
                onClick={() => { if (i <= maxStepReached) setStep(i); }}
                style={{
                  flex: 1, padding: "14px 16px", background: i === step ? COLORS.bg : "transparent",
                  border: "none", borderBottom: i === step ? `2px solid ${COLORS.accent}` : "2px solid transparent",
                  color: i === step ? COLORS.accent : i < maxStepReached ? COLORS.text : COLORS.textDim,
                  cursor: i <= maxStepReached ? "pointer" : "default", fontFamily: FONT, fontSize: 12,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "all 0.2s",
                  opacity: i <= maxStepReached ? 1 : 0.4,
                }}
              >
                <span style={{ fontSize: 14 }}>{i < maxStepReached ? "✓" : s.icon}</span>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Main Panel */}
            <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
              {step === 0 && <ICPForm form={icpForm} setForm={setIcpForm} onSubmit={runDiscovery} isProcessing={isProcessing} canRunDiscovery={discoverCanRun} onBackToDiscover={discoveredLeads.length > 0 ? () => setStep(1) : undefined} />}
              {step === 1 && <DiscoveryPanel leads={discoveredLeads} selected={selectedLeads} setSelected={setSelectedLeads} onNext={runEnrichment} isProcessing={isProcessing} />}
              {step === 2 && <EnrichmentPanel
                contacts={enrichedContacts} enrichmentCompanyResults={enrichmentCompanyResults} selected={selectedContacts} setSelected={setSelectedContacts}
                expanded={expandedContact} setExpanded={setExpandedContact}
                onPersonalize={() => { setPreviewEmails(null); setIsApproved(false); setShowPersonalizeModal(true); }}
                onSkip={skipPersonalization} isProcessing={isProcessing}
              />}
              {step === 3 && <CampaignSetupPanel
                contacts={enrichedContacts.filter(c => selectedContacts.has(c.id))}
                emails={personalizedEmails}
                channelAssignments={channelAssignments} setChannelAssignments={setChannelAssignments}
                emailPlatform={emailPlatform} setEmailPlatform={setEmailPlatform}
                emailCampaign={emailCampaign} setEmailCampaign={setEmailCampaign}
                setEmailCampaignLabel={setEmailCampaignLabel}
                linkedinPlatform={linkedinPlatform} setLinkedinPlatform={setLinkedinPlatform}
                linkedinCampaign={linkedinCampaign} setLinkedinCampaign={setLinkedinCampaign}
                showEmailPreview={showEmailPreview} setShowEmailPreview={setShowEmailPreview}
                onQueue={runQueueOutreach} isProcessing={isProcessing}
                listName={icpForm.listName || "Untitled List"}
              />}
              {step === 4 && <QueuedPanel queue={outreachQueue} listName={icpForm.listName || "Untitled List"} emailPlatform={emailPlatform} linkedinPlatform={linkedinPlatform} emailCampaign={emailCampaign} emailCampaignLabel={emailCampaignLabel} linkedinCampaign={linkedinCampaign} outreachResults={outreachResults} />}
            </div>

            {/* Personalization Modal */}
            {showPersonalizeModal && (
              <PersonalizeModal
                contacts={enrichedContacts.filter(c => selectedContacts.has(c.id))}
                promptText={promptText}
                setPromptText={setPromptText}
                selectedPromptKey={selectedPromptKey}
                setSelectedPromptKey={setSelectedPromptKey}
                savedPrompts={savedPrompts}
                previewEmails={previewEmails}
                isPreviewLoading={isPreviewLoading}
                onPreview={generatePreview}
                onApprove={approveAndPersonalizeAll}
                onClose={() => setShowPersonalizeModal(false)}
                totalContacts={enrichedContacts.filter(c => selectedContacts.has(c.id)).length}
              />
            )}

            {/* Log Panel */}
            <div style={{ width: 360, borderLeft: `1px solid ${COLORS.border}`, background: COLORS.surface, display: "flex", flexDirection: "column", flexShrink: 0 }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontFamily: FONT, fontSize: 11, color: COLORS.textMuted, letterSpacing: "0.08em", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: isProcessing ? COLORS.accent : COLORS.textDim, boxShadow: isProcessing ? `0 0 8px ${COLORS.accent}` : "none", transition: "all 0.3s" }} />
                AGENT LOG
                {isProcessing && <ProgressDots active={true} />}
              </div>
              <div ref={logRef} style={{ flex: 1, overflow: "auto", padding: "12px 16px", fontFamily: FONT, fontSize: 11.5, lineHeight: 1.7 }}>
                {processLog.length === 0 && (
                  <div style={{ color: COLORS.textDim, padding: "40px 0", textAlign: "center" }}>
                    Agent activity will appear here
                  </div>
                )}
                {processLog.map((log, i) => (
                  <div key={i} style={{ color: log.type === "success" ? COLORS.accent : log.type === "data" ? COLORS.blue : log.type === "dim" ? COLORS.textDim : log.type === "system" ? COLORS.textMuted : COLORS.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {log.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>)}

        {activePage === "leadlists" && (
          <LeadListsView
            outreachQueue={outreachQueue}
            enrichedContacts={enrichedContacts}
            personalizedEmails={personalizedEmails}
            channelAssignments={channelAssignments}
            listName={icpForm.listName || "Untitled List"}
            emailPlatform={emailPlatform}
            linkedinPlatform={linkedinPlatform}
            emailCampaign={emailCampaign}
            linkedinCampaign={linkedinCampaign}
            focusListId={leadlistsFocusListId}
            focusJobId={leadlistsFocusJobId}
            onClearLeadlistFocus={clearLeadlistFocus}
            savedPrompts={savedPrompts}
            userRole={userRole}
          />
        )}

        {activePage === "leads_mgmt" && leadGenLocked && <SectionMaintenancePanel title="Get Leads" />}
        <KeepAlive active={activePage === "leads_mgmt" && !leadGenLocked}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            {LEAD_GEN_MAINTENANCE && (
              <div style={{ padding: "8px 28px", background: COLORS.warn + "18", borderBottom: `1px solid ${COLORS.warn}44`, fontFamily: FONT, fontSize: 12, color: COLORS.warn }}>
                Under maintenance — clients currently see a maintenance notice on this page. Visible to platform admins only.
              </div>
            )}
            <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
              <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
                {!lmJobActive && (
                  <LeadsMgmtPreflightPanel
                    savedPrompts={savedPrompts}
                    promptText={lmPreflightPromptText}
                    setPromptText={setLmPreflightPromptText}
                    selectedPromptKey={lmPreflightPromptKey}
                    setSelectedPromptKey={setLmPreflightPromptKey}
                    campaignId={lmPreflightCampaignId}
                    setCampaignId={setLmPreflightCampaignId}
                    setCampaignLabel={setLmPreflightCampaignLabel}
                  />
                )}
                <EnrichmentV2View
                  onAgentLog={agentLogForEnrichment}
                  preflightReady={lmPreflightReady}
                  onJobStarted={handleEnrichmentJobStarted}
                  initialPromptText={lmPreflightPromptText}
                  initialSelectedPromptKey={lmPreflightPromptKey}
                  autoLinearFromPreflight={lmPreflightReady}
                  preflightInstantlyCampaignId={lmPreflightCampaignId}
                  onViewLeadList={handleViewLeadListFromEnrichment}
                  onPhaseChange={(p) => setLmJobActive(p === "dashboard")}
                  globalCredits={globalCredits}
                  userRole={userRole}
                />
                {campaignLeadListId && enrichedContacts.length > 0 && !leadsMgmtShowQueued && (
                  <div ref={leadsMgmtCampaignSectionRef} style={{ marginTop: 28 }}>
                    <CampaignSetupPanel
                      contacts={enrichedContacts.filter(c => selectedContacts.has(c.id))}
                      emails={personalizedEmails}
                      channelAssignments={channelAssignments} setChannelAssignments={setChannelAssignments}
                      emailPlatform={emailPlatform} setEmailPlatform={setEmailPlatform}
                      emailCampaign={emailCampaign} setEmailCampaign={setEmailCampaign}
                      setEmailCampaignLabel={setEmailCampaignLabel}
                      linkedinPlatform={linkedinPlatform} setLinkedinPlatform={setLinkedinPlatform}
                      linkedinCampaign={linkedinCampaign} setLinkedinCampaign={setLinkedinCampaign}
                      showEmailPreview={showEmailPreview} setShowEmailPreview={setShowEmailPreview}
                      onQueue={runQueueOutreach} isProcessing={isProcessing}
                      listName={icpForm.listName || "Untitled List"}
                      queueButtonDisabled
                    />
                  </div>
                )}
                {leadsMgmtShowQueued && outreachQueue.length > 0 && (
                  <div style={{ marginTop: 28 }}>
                    <QueuedPanel queue={outreachQueue} listName={icpForm.listName || "Untitled List"} emailPlatform={emailPlatform} linkedinPlatform={linkedinPlatform} emailCampaign={emailCampaign} emailCampaignLabel={emailCampaignLabel} linkedinCampaign={linkedinCampaign} outreachResults={outreachResults} />
                  </div>
                )}
              </div>
              <div style={{ width: 360, borderLeft: `1px solid ${COLORS.border}`, background: COLORS.surface, display: "flex", flexDirection: "column", flexShrink: 0 }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontFamily: FONT, fontSize: 11, color: COLORS.textMuted, letterSpacing: "0.08em", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: isProcessing ? COLORS.accent : COLORS.textDim, boxShadow: isProcessing ? `0 0 8px ${COLORS.accent}` : "none", transition: "all 0.3s" }} />
                  AGENT LOG
                  {isProcessing && <ProgressDots active={true} />}
                </div>
                <div ref={logRefLeadsMgmt} style={{ flex: 1, overflow: "auto", padding: "12px 16px", fontFamily: FONT, fontSize: 11.5, lineHeight: 1.7 }}>
                  {processLog.length === 0 && (
                    <div style={{ color: COLORS.textDim, padding: "40px 0", textAlign: "center" }}>
                      Agent activity will appear here
                    </div>
                  )}
                  {processLog.map((log, i) => (
                    <div key={i} style={{ color: log.type === "success" ? COLORS.accent : log.type === "data" ? COLORS.blue : log.type === "dim" ? COLORS.textDim : log.type === "system" ? COLORS.textMuted : COLORS.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {log.msg}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </KeepAlive>

        <KeepAlive active={activePage === "crm"}>
          <CRMPipelineView onNavigate={setActivePage} onOpenConversation={(convId) => { setPendingConversationId(convId); setActivePage("unibox"); }} />
        </KeepAlive>
        {activePage === "appointments" && <AppointmentsView />}
        <KeepAlive active={activePage === "unibox"}>
          <UniboxView onNavigate={setActivePage} initialConversationId={pendingConversationId} onConversationLoaded={() => setPendingConversationId(null)} />
        </KeepAlive>

        {/* Keep-alive views: stateful tabs that preserve chat/generated content across tab switches */}
        <KeepAlive active={activePage === "audit"}>
          <AuditView
            key={selectedAuditProject || "no-project"}
            project={auditProjects.find(p => String(p.id) === String(selectedAuditProject))}
            projects={auditProjects}
            selectedProject={selectedAuditProject}
            setSelectedProject={setSelectedAuditProject}
            onCreateProject={handleCreateAuditProject}
          />
        </KeepAlive>
        {activePage === "implementation" && <ImplementationView project={auditProjects.find(p => String(p.id) === String(selectedAuditProject))} />}
        {activePage === "workflows" && <WorkflowsLibraryView />}
        <KeepAlive active={activePage === "council"}>
          <AICouncilView />
        </KeepAlive>

        <KeepAlive active={activePage === "campaigns_messaging"}>
          <MessagingWorkshopView />
        </KeepAlive>
        {activePage === "campaigns_email" && <ColdEmailCampaignsView />}
        {activePage === "campaigns_linkedin" && <LinkedInCampaignsView />}

        <KeepAlive active={activePage === "content_studio"}>
          <ContentStudioView />
        </KeepAlive>
        {activePage === "content_linkedin" && <LinkedInContentView />}
        {activePage === "content_community" && <CommunityView />}
        {activePage === "content_video" && <VideoScriptView />}
        <KeepAlive active={activePage === "niche_researcher"}>
          <NicheResearcherView setActivePage={setActivePage} setIcpForm={setIcpForm} setStep={setStep} />
        </KeepAlive>
        <KeepAlive active={activePage === "sales_scripts"}>
          <SalesScriptGeneratorView />
        </KeepAlive>
        <KeepAlive active={activePage === "sales_analyser"}>
          <SalesCallAnalyserView />
        </KeepAlive>
        <KeepAlive active={activePage === "sol_assistant"}>
          <SolutionAIAssistantView />
        </KeepAlive>
        {activePage === "website_builder" && <WebsiteBuilderView />}
        {activePage === "settings" && <SettingsView enabledSolutions={enabledSolutions} setEnabledSolutions={setEnabledSolutions} />}
        {activePage === "account" && <AccountView onCreditsChange={setGlobalCredits} />}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, background: `${color}11`, border: `1px solid ${color}22`, fontSize: 11, fontFamily: FONT, color: color, fontWeight: 500 }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />
      {label}
    </div>
  );
}

// --- Step Panels ---

function TagSelect({ label, presets, selected, onChange, placeholder, labelStyle, inputStyle }) {
  const [isOpen, setIsOpen] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (item) => {
    onChange(selected.includes(item) ? selected.filter(s => s !== item) : [...selected, item]);
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onChange([...selected, trimmed]);
    }
    setCustomInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addCustom(); }
    if (e.key === "Backspace" && !customInput && selected.length > 0) {
      onChange(selected.slice(0, -1));
    }
  };

  return (
    <div ref={wrapperRef}>
      <label style={labelStyle}>{label}</label>
      {/* Selected Tags + Input */}
      <div
        onClick={() => setIsOpen(true)}
        style={{
          ...inputStyle,
          display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 12px",
          minHeight: 44, cursor: "text", alignItems: "center",
        }}
      >
        {selected.map(item => (
          <span key={item} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", borderRadius: 6,
            background: COLORS.accentBg, border: `1px solid ${COLORS.accent}33`,
            fontFamily: FONT, fontSize: 11, fontWeight: 500, color: COLORS.accent,
            whiteSpace: "nowrap",
          }}>
            {item}
            <span
              onClick={(e) => { e.stopPropagation(); toggle(item); }}
              style={{ cursor: "pointer", fontSize: 13, lineHeight: 1, opacity: 0.6 }}
            >×</span>
          </span>
        ))}
        <input
          value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={selected.length === 0 ? placeholder : ""}
          style={{
            border: "none", outline: "none", background: "transparent",
            color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13,
            flex: 1, minWidth: 120, padding: "2px 0",
          }}
        />
      </div>
      {/* Dropdown */}
      {isOpen && (
        <div style={{
          marginTop: 4, padding: "8px", background: COLORS.surface,
          border: `1px solid ${COLORS.border}`, borderRadius: 8,
          maxHeight: 200, overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {presets.map(item => {
              const isSelected = selected.includes(item);
              return (
                <div
                  key={item}
                  onClick={() => toggle(item)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                    background: isSelected ? COLORS.accentBg : "transparent",
                    border: `1px solid ${isSelected ? COLORS.accent + "44" : COLORS.border}`,
                    transition: "all 0.12s", userSelect: "none",
                  }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 3,
                    border: `2px solid ${isSelected ? COLORS.accent : COLORS.borderActive}`,
                    background: isSelected ? COLORS.accent : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, color: COLORS.bg, fontWeight: 700, flexShrink: 0,
                  }}>
                    {isSelected && "✓"}
                  </div>
                  <span style={{
                    fontFamily: FONT, fontSize: 11, fontWeight: 500,
                    color: isSelected ? COLORS.accent : COLORS.textMuted,
                  }}>{item}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ICPForm({ form, setForm, onSubmit, isProcessing, canRunDiscovery = true, onBackToDiscover }) {
  const [lookalikeOnly, setLookalikeOnly] = useState(false);
  const [importMode, setImportMode] = useState(false);
  const [importedFile, setImportedFile] = useState(null);
  const importFileInputRef = useRef(null);

  const EMPLOYEE_RANGES = [
    "1-10", "11-50", "51-200", "201-500", "501-1,000", "1,001-5,000", "5,000+"
  ];

  const inputStyle = {
    width: "100%", padding: "12px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14,
    outline: "none", transition: "border 0.2s", boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontFamily: FONT, fontSize: 11, color: COLORS.textMuted,
    marginBottom: 6, letterSpacing: "0.05em", fontWeight: 600,
  };

  return (
    <div style={{ maxWidth: 640 }}>
      {!canRunDiscovery && (
        <div style={{
          padding: "14px 18px", marginBottom: 24, background: COLORS.warnBg, border: `1px solid ${COLORS.warn}44`,
          borderRadius: 10, fontSize: 14, color: COLORS.text,
        }}>
          Run Discovery cannot run because no lead search integrations are configured. Configure at least one integration in <strong>Settings → Integrations</strong>.
        </div>
      )}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          Define Your <span style={{ color: COLORS.accent }}>Ideal Customer</span>
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "8px 0 0", lineHeight: 1.6 }}>
          {lookalikeOnly
            ? "Paste company domains and AI Ark will find similar companies."
            : "AI Ark will use this profile to find companies with 95%+ ICP match."}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* List Name */}
        <div>
          <label style={labelStyle}>LIST NAME</label>
          <input
            value={form.listName || ""}
            onChange={e => setForm({ ...form, listName: e.target.value })}
            placeholder="e.g. Q1 SaaS VP Growth — North America"
            style={{
              ...inputStyle,
              fontSize: 15,
              fontWeight: 500,
              padding: "14px 16px",
              borderColor: form.listName ? COLORS.accent + "44" : COLORS.border,
              background: form.listName ? COLORS.accentBg : COLORS.surface,
            }}
            onFocus={e => e.target.style.borderColor = COLORS.borderActive}
            onBlur={e => e.target.style.borderColor = form.listName ? COLORS.accent + "44" : COLORS.border}
          />
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, fontFamily: FONT }}>
            This name follows your leads through the entire pipeline
          </div>
        </div>

        {/* Import Toggle */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", background: importMode ? COLORS.blue + "08" : COLORS.surface,
          border: `1px solid ${importMode ? COLORS.blue + "33" : COLORS.border}`,
          borderRadius: 10, transition: "all 0.25s",
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>I have an existing list</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              Upload a CSV or LinkedIn export instead of searching
            </div>
          </div>
          <div
            onClick={() => { setImportMode(!importMode); if (!importMode) setLookalikeOnly(false); }}
            style={{
              width: 48, height: 26, borderRadius: 13, cursor: "pointer",
              background: importMode ? COLORS.blue : COLORS.borderActive,
              position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: 16,
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: "50%", background: "#fff",
              position: "absolute", top: 3,
              left: importMode ? 25 : 3,
              transition: "left 0.25s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            }} />
          </div>
        </div>

        {/* Import Mode — Drag & Drop */}
        {importMode && (
          <>
            <input
              type="file"
              ref={importFileInputRef}
              accept=".csv,.xlsx,.xls,.txt"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target?.files?.[0];
                if (!file) return;
                try {
                  const parsed = await parseUploadedFile(file, "LinkedIn Export");
                  setImportedFile({ name: parsed.name, rows: parsed.rows?.length || 0, parsed });
                } catch (err) {
                  console.error("[Import] Parse error:", err);
                }
                e.target.value = "";
              }}
            />
            <div
              onClick={() => importFileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = COLORS.accent + "66"; }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!importedFile) e.currentTarget.style.borderColor = COLORS.border; }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.style.borderColor = importedFile ? COLORS.accent + "66" : COLORS.border;
                const file = e.dataTransfer?.files?.[0];
                if (!file) return;
                try {
                  const parsed = await parseUploadedFile(file, "LinkedIn Export");
                  setImportedFile({ name: parsed.name, rows: parsed.rows?.length || 0, parsed });
                } catch (err) {
                  console.error("[Import] Parse error:", err);
                }
              }}
              style={{ padding: "40px", background: COLORS.surface, border: `2px dashed ${importedFile ? COLORS.accent + "66" : COLORS.border}`, borderRadius: 12, textAlign: "center", cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "66"}
              onMouseLeave={e => { if (!importedFile) e.currentTarget.style.borderColor = COLORS.border; }}
            >
              {!importedFile ? (
                <>
                  <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.3 }}>⬆️</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>Drag & drop your file here</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>or click to browse</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim }}>CSV, XLSX, TXT · Up to 10,000 rows</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent }}>{importedFile.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>{importedFile.rows} rows detected</div>
                  <div style={{ fontSize: 11, color: COLORS.blue, marginTop: 6, cursor: "pointer" }} onClick={e => { e.stopPropagation(); setImportedFile(null); }}>Remove & upload different file</div>
                </>
              )}
            </div>
          </>
        )}

        {/* Toggle Switch — Lookalike (hidden) */}
        {false && !importMode && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", background: lookalikeOnly ? COLORS.accentBg : COLORS.surface,
          border: `1px solid ${lookalikeOnly ? COLORS.accentDim + "44" : COLORS.border}`,
          borderRadius: 10, transition: "all 0.25s",
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Lookalike Search Only</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              Skip ICP targeting — just find companies similar to ones you provide
            </div>
          </div>
          <div
            onClick={() => setLookalikeOnly(!lookalikeOnly)}
            style={{
              width: 48, height: 26, borderRadius: 13, cursor: "pointer",
              background: lookalikeOnly ? COLORS.accent : COLORS.borderActive,
              position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: 16,
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: "50%", background: "#fff",
              position: "absolute", top: 3,
              left: lookalikeOnly ? 25 : 3,
              transition: "left 0.25s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            }} />
          </div>
        </div>
        )}

        {/* ICP Targeting Fields — hidden when lookalike only or import mode */}
        {!lookalikeOnly && !importMode && (
          <>
            {/* Target Industry */}
            <div>
              <label style={labelStyle}>TARGET INDUSTRY</label>
              <input
                value={form.industry}
                onChange={e => setForm({ ...form, industry: e.target.value })}
                placeholder="e.g. B2B SaaS, FinTech, Healthcare"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.borderActive}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>

            {/* Keywords */}
            <div>
              <label style={labelStyle}>KEYWORDS</label>
              <input
                value={form.keywords || ""}
                onChange={e => setForm({ ...form, keywords: e.target.value })}
                placeholder="e.g. AI automation, cold outreach, lead generation, SaaS tools"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.borderActive}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, fontFamily: FONT }}>
                Filter results: only companies whose name contains these keywords (applied after industry, size, regions)
              </div>
            </div>

            {/* Employee Range Multi-Select */}
            <div>
              <label style={labelStyle}>COMPANY SIZE</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {EMPLOYEE_RANGES.map(range => {
                  const sizes = form.employeeSizes ?? [];
                  const isSelected = sizes.includes(range);
                  return (
                    <div
                      key={range}
                      onClick={() => {
                        const current = form.employeeSizes ?? [];
                        const next = isSelected
                          ? current.filter(r => r !== range)
                          : [...current, range];
                        setForm({ ...form, employeeSizes: next });
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 7,
                        padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                        background: isSelected ? COLORS.accentBg : COLORS.surface,
                        border: `1px solid ${isSelected ? COLORS.accentDim + "55" : COLORS.border}`,
                        transition: "all 0.15s", userSelect: "none",
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 3,
                        border: `2px solid ${isSelected ? COLORS.accent : COLORS.borderActive}`,
                        background: isSelected ? COLORS.accent : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, color: COLORS.bg, fontWeight: 700, flexShrink: 0,
                        transition: "all 0.15s",
                      }}>
                        {isSelected && "✓"}
                      </div>
                      <span style={{
                        fontFamily: FONT, fontSize: 12, fontWeight: 500,
                        color: isSelected ? COLORS.accent : COLORS.textMuted,
                      }}>
                        {range}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Target Regions */}
            <TagSelect
              label="TARGET REGIONS"
              presets={["North America", "Asia Pacific", "Europe", "MENA", "UK & Ireland", "Latin America", "DACH", "Nordics", "Australia & NZ", "Africa"]}
              selected={form.regions ?? []}
              onChange={regions => setForm({ ...form, regions })}
              placeholder="Type a custom region and press Enter..."
              labelStyle={labelStyle}
              inputStyle={inputStyle}
            />

            {/* Target Roles */}
            <TagSelect
              label="TARGET ROLES / PERSONAS"
              presets={["CEO", "CTO", "COO", "CFO", "CMO", "CRO", "VP Sales", "VP Growth", "VP Marketing", "VP Engineering", "VP Operations", "Head of Product", "Head of Partnerships", "Director of Sales", "Director of Marketing", "Founder"]}
              selected={form.roles ?? []}
              onChange={roles => setForm({ ...form, roles })}
              placeholder="Type a custom role and press Enter..."
              labelStyle={labelStyle}
              inputStyle={inputStyle}
            />

            {/* Max Leads */}
            <div>
              <label style={labelStyle}>MAX LEADS TO DISCOVER</label>
              <input
                value={form.maxLeads || ""}
                onChange={e => setForm({ ...form, maxLeads: e.target.value.replace(/[^0-9]/g, "") })}
                placeholder="e.g. 100 (default 100 if blank)"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.borderActive}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, fontFamily: FONT }}>
                {form.maxLeads ? `Will discover up to ${parseInt(form.maxLeads).toLocaleString()} companies` : "Default 100 leads"}
              </div>
            </div>
          </>
        )}

        {/* Lookalike Companies — only visible in lookalike-only mode */}
        {lookalikeOnly && !importMode && (
          <div>
            <label style={labelStyle}>SEED COMPANIES</label>
            <textarea
              value={form.lookalike}
              onChange={e => setForm({ ...form, lookalike: e.target.value })}
              placeholder={"Paste company domains here, one per line:\nstripe.com\nnotion.so\nfigma.com\nlinear.app"}
              rows={6}
              style={{
                ...inputStyle,
                resize: "vertical",
                minHeight: 140,
                lineHeight: 1.6,
              }}
              onFocus={e => e.target.style.borderColor = COLORS.borderActive}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, fontFamily: FONT }}>
              AI Ark will analyze these companies and find similar ones matching the same profile
            </div>
          </div>
        )}
      </div>

      {isProcessing && onBackToDiscover && (
        <button
          onClick={onBackToDiscover}
          style={{
            marginTop: 28, padding: "12px 24px",
            background: "transparent", color: COLORS.accent,
            border: `1px solid ${COLORS.accent}66`, borderRadius: 8,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
            letterSpacing: "0.02em", transition: "all 0.2s",
          }}
        >
          ← Back to Discover
        </button>
      )}
      <button
        onClick={() => ((importMode && importedFile?.parsed) || (!importMode && canRunDiscovery)) && onSubmit({ ...form, lookalikeOnly, importMode, importedFile })}
        disabled={isProcessing || (importMode ? !importedFile?.parsed : !canRunDiscovery)}
        style={{
          marginTop: 28, padding: "14px 32px",
          background: isProcessing || (importMode ? !importedFile?.parsed : !canRunDiscovery) ? COLORS.border : COLORS.accent,
          color: isProcessing || (importMode ? !importedFile?.parsed : !canRunDiscovery) ? COLORS.textDim : COLORS.bg,
          border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600,
          cursor: isProcessing || (importMode ? !importedFile?.parsed : !canRunDiscovery) ? "default" : "pointer",
          opacity: isProcessing ? 0.6 : 1,
          letterSpacing: "0.02em", transition: "all 0.2s",
        }}
      >
        {isProcessing ? "PROCESSING..." : importMode ? `IMPORT & ENRICH ${importedFile ? importedFile.rows + " LEADS" : ""} →` : lookalikeOnly ? "FIND LOOKALIKES →" : "RUN DISCOVERY →"}
      </button>
    </div>
  );
}

function DiscoveryPanel({ leads, selected, setSelected, onNext, isProcessing }) {
  const toggleLead = id => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            <span style={{ color: COLORS.accent }}>{leads.length}</span> Companies Discovered
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Select companies to enrich with contact data. {selected.size} selected.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <button onClick={onNext} disabled={isProcessing || selected.size === 0} style={{
            padding: "12px 28px", background: selected.size > 0 ? COLORS.accent : COLORS.border, color: selected.size > 0 ? COLORS.bg : COLORS.textDim,
            border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
            cursor: isProcessing || selected.size === 0 ? "default" : "pointer", opacity: isProcessing ? 0.6 : 1,
          }}>
            {isProcessing ? "ENRICHING..." : `ENRICH ${selected.size} COMPANIES →`}
          </button>
          {selected.size > 0 && (
            <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>
              ~{(selected.size * 85).toLocaleString()} credits estimated
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => setSelected(new Set(leads.map(l => l.id)))}
          style={{
            padding: "8px 16px", background: "transparent", color: COLORS.textMuted,
            border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
          }}
        >
          Select All
        </button>
        <button
          onClick={() => setSelected(new Set())}
          style={{
            padding: "8px 16px", background: "transparent", color: COLORS.textMuted,
            border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
          }}
        >
          Deselect All
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {leads.map(lead => (
          <div
            key={lead.id}
            onClick={() => toggleLead(lead.id)}
            style={{
              padding: "16px 20px", background: selected.has(lead.id) ? COLORS.accentBg : COLORS.surface,
              border: `1px solid ${selected.has(lead.id) ? COLORS.accentDim + "44" : COLORS.border}`,
              borderRadius: 10, cursor: "pointer", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 16,
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 4, border: `2px solid ${selected.has(lead.id) ? COLORS.accent : COLORS.borderActive}`,
              background: selected.has(lead.id) ? COLORS.accent : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: COLORS.bg, fontWeight: 700, flexShrink: 0,
            }}>
              {selected.has(lead.id) && "✓"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{lead.name}</span>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>{lead.domain}</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: COLORS.textMuted, flexWrap: "wrap" }}>
                <span>{lead.industry}</span>
                <span>📍 {lead.location}</span>
                <span>👥 {lead.employees}</span>
                {lead.revenue && <span>💰 {lead.revenue}</span>}
              </div>
              {lead.recentNews && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, fontStyle: "italic" }}>
                  📰 {lead.recentNews}
                </div>
              )}
            </div>
            <div style={{
              padding: "6px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              background: lead.icpScore >= 96 ? COLORS.accentBg : COLORS.blueBg,
              color: lead.icpScore >= 96 ? COLORS.accent : COLORS.blue,
              border: `1px solid ${lead.icpScore >= 96 ? COLORS.accent + "33" : COLORS.blue + "33"}`,
              flexShrink: 0,
            }}>
              {lead.icpScore}% ICP
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EnrichmentPanel({ contacts, enrichmentCompanyResults = [], selected, setSelected, expanded, setExpanded, onPersonalize, onSkip, isProcessing }) {
  const toggleContact = id => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            <span style={{ color: COLORS.accent }}>{contacts.length}</span> Contacts Enriched
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>
            {contacts.length === 0 && enrichmentCompanyResults.length > 0
              ? `No contact found for ${enrichmentCompanyResults.map(r => r.name).join(", ")}.`
              : `Click a contact to view LinkedIn insights. ${selected.size} selected.`}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onSkip} disabled={isProcessing || selected.size === 0} style={{
              padding: "12px 22px", background: "transparent",
              color: selected.size > 0 ? COLORS.textMuted : COLORS.textDim,
              border: `1px solid ${COLORS.border}`, borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor: isProcessing || selected.size === 0 ? "default" : "pointer",
              opacity: isProcessing ? 0.6 : 1, transition: "all 0.2s",
            }}>
              SKIP PERSONALIZATION
            </button>
            <button onClick={onPersonalize} disabled={isProcessing || selected.size === 0} style={{
              padding: "12px 22px", background: selected.size > 0 ? COLORS.accent : COLORS.border,
              color: selected.size > 0 ? COLORS.bg : COLORS.textDim,
              border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor: isProcessing || selected.size === 0 ? "default" : "pointer",
              opacity: isProcessing ? 0.6 : 1, transition: "all 0.2s",
            }}>
              {isProcessing ? "GENERATING..." : `PERSONALIZE ${selected.size} EMAILS →`}
            </button>
          </div>
          {selected.size > 0 && (
            <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>
              ~{(selected.size * 12).toLocaleString()} credits estimated
            </div>
          )}
        </div>
      </div>
      {contacts.length === 0 && enrichmentCompanyResults.length > 0 && (
        <div style={{
          padding: 16, background: COLORS.warn + "15", border: `1px solid ${COLORS.warn}33`,
          borderRadius: 10, marginBottom: 16, fontFamily: FONT, fontSize: 13, color: COLORS.textMuted,
        }}>
          <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>No contacts found</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {enrichmentCompanyResults.map(r => (
              <div key={r.id || r.name}>
                <strong>{r.name}</strong> — {r.contactsFound ?? 0} contacts
                {r.reasons?.length ? ` (${r.reasons.join("; ")})` : ""}.
                {r.enrichedAt && ` Enriched ${new Date(r.enrichedAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.`}
                {" "}Company saved for future cache lookups.
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {contacts.map(contact => (
          <div key={contact.id}>
            <div
              style={{
                padding: "14px 20px", background: selected.has(contact.id) ? COLORS.accentBg : COLORS.surface,
                border: `1px solid ${selected.has(contact.id) ? COLORS.accentDim + "44" : COLORS.border}`,
                borderRadius: expanded === contact.id ? "10px 10px 0 0" : 10,
                display: "flex", alignItems: "center", gap: 14, cursor: "pointer", transition: "all 0.15s",
              }}
            >
              <div
                onClick={e => { e.stopPropagation(); toggleContact(contact.id); }}
                style={{
                  width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected.has(contact.id) ? COLORS.accent : COLORS.borderActive}`,
                  background: selected.has(contact.id) ? COLORS.accent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: COLORS.bg, fontWeight: 700, flexShrink: 0,
                }}>
                {selected.has(contact.id) && "✓"}
              </div>
              <div style={{ flex: 1 }} onClick={() => setExpanded(expanded === contact.id ? null : contact.id)}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{contact.name}</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 12 }}>— {contact.title}</span>
                  <span style={{ color: COLORS.textDim, fontSize: 11, fontFamily: FONT }}>@ {contact.company}</span>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 12 }}>
                  <span style={{ color: COLORS.blue, fontFamily: FONT }}>{contact.email}</span>
                  <span style={{ color: contact.bounceRisk === "low" ? COLORS.accent : COLORS.warn, fontFamily: FONT, fontSize: 11 }}>
                    {contact.bounceRisk === "low" ? "✓ verified" : "⚠ medium risk"}
                  </span>
                </div>
              </div>
              <span onClick={() => setExpanded(expanded === contact.id ? null : contact.id)} style={{ color: COLORS.textDim, fontSize: 18, cursor: "pointer", transform: expanded === contact.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▾</span>
            </div>
            {expanded === contact.id && contact.linkedinData && (
              <div style={{
                padding: "16px 20px", background: COLORS.surface, borderRadius: "0 0 10px 10px",
                border: `1px solid ${COLORS.border}`, borderTop: "none",
              }}>
                <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textMuted, letterSpacing: "0.06em", marginBottom: 10, fontWeight: 600 }}>LINKEDIN INSIGHTS</div>
                <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
                  <Stat label="Connections" value={contact.linkedinData.connections.toLocaleString()} />
                  <Stat label="Posts" value={contact.linkedinData.posts} />
                </div>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5, marginBottom: 8 }}>{contact.linkedinData.about}</div>
                <div style={{ fontSize: 12, color: COLORS.blue, fontStyle: "italic" }}>Recent: {contact.linkedinData.recentActivity}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: COLORS.text }}>{value}</div>
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em" }}>{label.toUpperCase()}</div>
    </div>
  );
}

function CampaignSetupPanel({ contacts, emails, channelAssignments, setChannelAssignments, emailPlatform, setEmailPlatform, emailCampaign, setEmailCampaign, setEmailCampaignLabel, linkedinPlatform, setLinkedinPlatform, linkedinCampaign, setLinkedinCampaign, showEmailPreview, setShowEmailPreview, onQueue, isProcessing, listName, queueButtonDisabled = false }) {
  const [listOnly, setListOnly] = useState(false);

  const [instantlyCampaignList, setInstantlyCampaignList] = useState([]);
  const [instantlyCampaignError, setInstantlyCampaignError] = useState(null);
  useEffect(() => {
    if (emailPlatform !== "instantly") return;
    setInstantlyCampaignError(null);
    api.instantly.campaigns.list()
      .then((data) => {
        const rows = normalizeInstantlyCampaignList(data);
        setInstantlyCampaignList(rows);
        if (rows.length === 0) {
          setInstantlyCampaignError("No campaigns found in your Instantly account. Create a campaign in Instantly first.");
        }
      })
      .catch((e) => {
        setInstantlyCampaignList([]);
        const msg = e?.message || "";
        if (/401|403|rejected|Invalid.*key/i.test(msg)) {
          setInstantlyCampaignError("Instantly rejected your API key. Update it in Settings → Integrations.");
        } else if (/not connected|Add your API key/i.test(msg)) {
          setInstantlyCampaignError("Instantly not connected. Add your API key in Settings → Integrations.");
        } else {
          setInstantlyCampaignError("Could not reach Instantly API. Try again later.");
        }
      });
  }, [emailPlatform]);

  useEffect(() => {
    if (emailPlatform !== "instantly" || !emailCampaign || instantlyCampaignList.length === 0) return;
    const row = instantlyCampaignList.find((x) => x.id === emailCampaign);
    if (row) setEmailCampaignLabel?.(row.name);
  }, [emailPlatform, emailCampaign, instantlyCampaignList, setEmailCampaignLabel]);

  const [smartleadCampaignList, setSmartleadCampaignList] = useState([]);
  useEffect(() => {
    if (emailPlatform !== "smartlead") return;
    api.smartlead.campaigns.list().then(data => {
      const items = Array.isArray(data) ? data : data.data || data.campaigns || [];
      setSmartleadCampaignList(items.map(c => ({ id: c.id, name: c.name || `Campaign ${c.id}` })));
    }).catch(() => {});
  }, [emailPlatform]);

  const EMAIL_CAMPAIGNS = {
    instantly: instantlyCampaignList,
    smartlead: smartleadCampaignList.length > 0
      ? smartleadCampaignList.map(c => c.name)
      : ["Enterprise Outbound — Q1", "Mid-Market SaaS Campaign", "CTO Outreach — Tech Stack", "Founder Direct — Warm Style"],
  };
  const [heyreachCampaignList, setHeyreachCampaignList] = useState([]);
  useEffect(() => {
    api.heyreach.campaigns.list().then(data => {
      const items = Array.isArray(data) ? data : data.items || data.campaigns || [];
      setHeyreachCampaignList(items.map(c => ({ id: c.id, name: c.name || c.campaignName || `Campaign ${c.id}` })));
    }).catch(() => {});
  }, []);
  const LINKEDIN_CAMPAIGNS = {
    heyreach: heyreachCampaignList.length > 0
      ? heyreachCampaignList.map(c => c.name)
      : ["Connection Request — Warm Intro", "Content Engagement Sequence", "Decision Maker Outreach — Q1"],
    aimfox: [
      "LinkedIn Drip — VP Level",
      "Founder Connect Campaign",
      "InMail Sequence — Enterprise",
    ],
  };

  const selectStyle = {
    width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13,
    outline: "none", boxSizing: "border-box",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a7a8e' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    cursor: "pointer",
  };

  const labelStyle = {
    display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim,
    letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6,
  };

  // Auto-assign all leads based on campaign selections
  useEffect(() => {
    const newAssignments = {};
    contacts.forEach(c => {
      newAssignments[c.id] = {
        email: !listOnly && !!emailCampaign,
        linkedin: !listOnly && !!linkedinCampaign,
        listOnly: listOnly,
      };
    });
    setChannelAssignments(newAssignments);
  }, [emailCampaign, linkedinCampaign, listOnly, contacts.length]);

  const canQueue = listOnly || emailCampaign || linkedinCampaign;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          Campaign <span style={{ color: COLORS.accent }}>Setup</span>
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>
          Choose where to send {contacts.length} verified lead{contacts.length === 1 ? "" : "s"} from "<span style={{ color: COLORS.accent }}>{listName}</span>"
        </p>
      </div>

      {/* Collapsible Email Preview */}
      <div style={{ marginBottom: 20, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div
          onClick={() => setShowEmailPreview(!showEmailPreview)}
          style={{
            padding: "12px 18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: COLORS.accent }}>✦</span>
            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>
              {Object.keys(emails).length} Personalized Emails Generated
            </span>
            <span style={{ fontSize: 11, color: COLORS.textDim }}>— click to preview</span>
          </div>
          <span style={{ color: COLORS.textDim, fontSize: 16, transform: showEmailPreview ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▾</span>
        </div>
        {showEmailPreview && (
          <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "12px 18px", display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflow: "auto" }}>
            {contacts.map(contact => {
              const email = emails[contact.id];
              if (!email) return null;
              return (
                <div key={contact.id} style={{ padding: "10px 14px", background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                    {contact.name} <span style={{ color: COLORS.textDim, fontWeight: 400 }}>— {contact.company}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.blue, fontFamily: FONT, marginBottom: 6 }}>{email.subject}</div>
                  <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto" }}>{email.body || ""}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Platform & Campaign Selection */}
      {!listOnly && (
        <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
          {/* Email Platform */}
          <div style={{
            flex: 1, padding: 20, background: COLORS.surface,
            border: `1px solid ${emailCampaign ? COLORS.accent + "44" : COLORS.border}`,
            borderRadius: 10, transition: "border 0.2s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 18 }}>📧</span>
              <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>Cold Email</span>
              {emailCampaign && (
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, padding: "2px 8px", background: COLORS.accentBg, borderRadius: 10, marginLeft: "auto" }}>
                  {contacts.length} leads
                </span>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>PLATFORM</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ key: "instantly", label: "Instantly.ai" }, { key: "smartlead", label: "SmartLead" }].map(p => (
                  <button key={p.key} onClick={() => { setEmailPlatform(p.key); setEmailCampaign(""); setEmailCampaignLabel?.(""); }} style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600,
                    border: `1px solid ${emailPlatform === p.key ? COLORS.accent + "55" : COLORS.border}`,
                    background: emailPlatform === p.key ? COLORS.accentBg : "transparent",
                    color: emailPlatform === p.key ? COLORS.accent : COLORS.textMuted,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>CAMPAIGN</label>
              {emailPlatform === "instantly" ? (
                <>
                  <select
                    value={emailCampaign}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmailCampaign(v);
                      const row = instantlyCampaignList.find((x) => x.id === v);
                      setEmailCampaignLabel?.(row?.name || "");
                    }}
                    style={selectStyle}
                  >
                    <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select campaign...</option>
                    {instantlyCampaignList.map((c) => (
                      <option key={c.id} value={c.id} style={{ background: COLORS.surface, color: COLORS.text }}>{c.name}</option>
                    ))}
                  </select>
                  {instantlyCampaignError && (
                    <div style={{ marginTop: 8, fontSize: 11, color: COLORS.danger, fontFamily: FONT_BODY }}>{instantlyCampaignError}</div>
                  )}
                </>
              ) : (
                <select value={emailCampaign} onChange={e => setEmailCampaign(e.target.value)} style={selectStyle}>
                  <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select campaign...</option>
                  {(EMAIL_CAMPAIGNS[emailPlatform] || []).map(c => (
                    <option key={c} value={c} style={{ background: COLORS.surface, color: COLORS.text }}>{c}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* LinkedIn Platform */}
          <div style={{
            flex: 1, padding: 20, background: COLORS.surface,
            border: `1px solid ${linkedinCampaign ? COLORS.blue + "44" : COLORS.border}`,
            borderRadius: 10, transition: "border 0.2s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 18 }}>💼</span>
              <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>LinkedIn</span>
              {linkedinCampaign && (
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.blue, padding: "2px 8px", background: COLORS.blueBg, borderRadius: 10, marginLeft: "auto" }}>
                  {contacts.length} leads
                </span>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>PLATFORM</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ key: "heyreach", label: "HeyReach" }, { key: "aimfox", label: "AimFox" }].map(p => (
                  <button key={p.key} onClick={() => { setLinkedinPlatform(p.key); setLinkedinCampaign(""); }} style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600,
                    border: `1px solid ${linkedinPlatform === p.key ? COLORS.blue + "55" : COLORS.border}`,
                    background: linkedinPlatform === p.key ? COLORS.blueBg : "transparent",
                    color: linkedinPlatform === p.key ? COLORS.blue : COLORS.textMuted,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>CAMPAIGN</label>
              <select value={linkedinCampaign} onChange={e => setLinkedinCampaign(e.target.value)} style={selectStyle}>
                <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select campaign...</option>
                {(LINKEDIN_CAMPAIGNS[linkedinPlatform] || []).map(c => (
                  <option key={c} value={c} style={{ background: COLORS.surface, color: COLORS.text }}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Save to List Only */}
      <div
        onClick={() => { setListOnly(!listOnly); if (!listOnly) { setEmailCampaign(""); setEmailCampaignLabel?.(""); setLinkedinCampaign(""); } }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px",
          background: listOnly ? COLORS.surface : "transparent",
          border: `1px solid ${listOnly ? COLORS.textMuted + "44" : COLORS.border}`,
          borderRadius: 10, cursor: "pointer", transition: "all 0.2s", marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18 }}>📋</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Save to list only</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              Don't push to any platform yet — just save the enriched & personalized leads
            </div>
          </div>
        </div>
        <div style={{
          width: 22, height: 22, borderRadius: 5,
          border: `2px solid ${listOnly ? COLORS.text : COLORS.borderActive}`,
          background: listOnly ? COLORS.text : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, color: COLORS.bg, fontWeight: 700, transition: "all 0.15s",
        }}>
          {listOnly && "✓"}
        </div>
      </div>

      {/* Summary & Queue Button */}
      {canQueue && (
        <div style={{
          padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 10, marginBottom: 20, display: "flex", alignItems: "center", gap: 16,
        }}>
          <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>SUMMARY:</div>
          <div style={{ display: "flex", gap: 12, flex: 1, flexWrap: "wrap" }}>
            {emailCampaign && !listOnly && (
              <span style={{ fontSize: 12, color: COLORS.accent }}>
                📧 {contacts.length} leads → {emailPlatform === "instantly" ? "Instantly.ai" : "SmartLead"} /{" "}
                {emailPlatform === "instantly"
                  ? instantlyCampaignList.find((c) => c.id === emailCampaign)?.name || emailCampaign
                  : emailCampaign}
              </span>
            )}
            {linkedinCampaign && !listOnly && (
              <span style={{ fontSize: 12, color: COLORS.blue }}>
                💼 {contacts.length} leads → {linkedinPlatform === "heyreach" ? "HeyReach" : "AimFox"} / {linkedinCampaign}
              </span>
            )}
            {listOnly && (
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                📋 {contacts.length} leads → Saved to "{listName}" only
              </span>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" onClick={onQueue} disabled={isProcessing || !canQueue || queueButtonDisabled} style={{
          padding: "14px 32px",
          background: canQueue && !queueButtonDisabled ? COLORS.accent : COLORS.border,
          color: canQueue && !queueButtonDisabled ? COLORS.bg : COLORS.textDim,
          border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600,
          cursor: isProcessing || !canQueue || queueButtonDisabled ? "default" : "pointer",
          opacity: isProcessing ? 0.6 : 1, transition: "all 0.2s",
        }}>
          {isProcessing ? "QUEUING..." : listOnly ? "SAVE LIST →" : "QUEUE FOR OUTREACH →"}
        </button>
      </div>
    </div>
  );
}

function PersonalizeModal({ contacts, promptText, setPromptText, selectedPromptKey, setSelectedPromptKey, savedPrompts = SAVED_PROMPTS, previewEmails, isPreviewLoading, onPreview, onApprove, onClose, totalContacts, previewLimit = 3 }) {
  const inputStyle = {
    width: "100%", padding: "10px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13,
    outline: "none", boxSizing: "border-box",
  };

  const previewContacts = contacts.slice(0, Math.max(0, previewLimit));

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "90%", maxWidth: 1100, height: "85vh",
        background: COLORS.bg, border: `1px solid ${COLORS.border}`,
        borderRadius: 16, display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        overflow: "hidden",
      }}>
        {/* Modal Header */}
        <div style={{
          padding: "16px 24px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, letterSpacing: "-0.02em" }}>
              Personalization <span style={{ color: COLORS.accent }}>Studio</span>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              Preview with the active prompt from Platform Admin, then approve for all {totalContacts} contacts
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${COLORS.border}`,
            background: "transparent", color: COLORS.textMuted, fontSize: 18,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>

        {/* Modal Body — Two Columns */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left Panel — Prompt Editor */}
          <div style={{ flex: 1, borderRight: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
              <label style={{ display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>
                ACTIVE PROMPT TEMPLATE
              </label>
              <div style={{ ...inputStyle, display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" fill={COLORS.accent} /><path d="M8 12.5l2.5 2.5 5.5-5.5" stroke="#0a0a0f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>{savedPrompts[selectedPromptKey]?.label || "Default Prompt"}</span>
                <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: "auto" }}>Set in Platform Admin</span>
              </div>
            </div>
            <div style={{ flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <label style={{ display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>
                PERSONALIZATION PROMPT
              </label>
              <textarea
                value={promptText}
                readOnly
                style={{
                  ...inputStyle,
                  flex: 1,
                  resize: "none",
                  fontFamily: FONT,
                  fontSize: 12,
                  lineHeight: 1.7,
                  minHeight: 0,
                }}
                onFocus={e => e.target.style.borderColor = COLORS.borderActive}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={onPreview} disabled={isPreviewLoading || previewContacts.length === 0} style={{
                width: "100%", padding: "12px", background: COLORS.blueBg,
                color: COLORS.blue, border: `1px solid ${COLORS.blue}33`,
                borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
                cursor: isPreviewLoading ? "wait" : "pointer",
                opacity: isPreviewLoading || previewContacts.length === 0 ? 0.7 : 1, transition: "all 0.2s",
              }}>
                {isPreviewLoading ? "GENERATING PREVIEWS..." : `PREVIEW WITH FIRST ${previewContacts.length} LEADS →`}
              </button>
            </div>
          </div>

          {/* Right Panel — Preview */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600 }}>
                EMAIL PREVIEW
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
                {previewEmails ? `Showing ${Object.keys(previewEmails).length} sample emails` : "Click preview to generate sample emails"}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "12px 20px" }}>
              {!previewEmails && !isPreviewLoading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: COLORS.textDim }}>
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>✦</div>
                  <div style={{ fontFamily: FONT, fontSize: 12, textAlign: "center" }}>
                    Review the active prompt on the left,<br />then click Preview to see results
                  </div>
                </div>
              )}
              {isPreviewLoading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: COLORS.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 12, animation: "spin 2s linear infinite" }}>✦</div>
                  <div style={{ fontFamily: FONT, fontSize: 12 }}>We got you. We're personalising the messages<ProgressDots active={true} /></div>
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
              {previewEmails && !isPreviewLoading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {previewContacts.map(contact => {
                    const email = previewEmails[contact.id];
                    if (!email) return null;
                    return (
                      <div key={contact.id} style={{
                        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: 10, overflow: "hidden",
                      }}>
                        <div style={{
                          padding: "10px 16px", borderBottom: `1px solid ${COLORS.border}`,
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: "50%", background: COLORS.accentBg,
                            border: `1px solid ${COLORS.accent}33`, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 11, flexShrink: 0,
                          }}>✦</div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {contact.name} <span style={{ color: COLORS.textDim, fontWeight: 400, fontSize: 11 }}>— {contact.company}</span>
                            </div>
                            <div style={{ fontSize: 11, color: COLORS.blue, fontFamily: FONT }}>{email.subject}</div>
                          </div>
                        </div>
                        <div style={{
                          padding: "12px 16px", whiteSpace: "pre-wrap", lineHeight: 1.6,
                          color: COLORS.text, fontSize: 12, maxHeight: 180, overflow: "auto",
                        }}>
                          {email.body}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: COLORS.surface,
        }}>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {previewEmails
              ? `✓ Preview looks good? Approve to generate for all ${totalContacts} contacts`
              : "Generate a preview first to unlock approval"}
          </div>
          <button
            onClick={onApprove}
            disabled={!previewEmails}
            style={{
              padding: "12px 28px",
              background: previewEmails ? COLORS.accent : COLORS.border,
              color: previewEmails ? COLORS.bg : COLORS.textDim,
              border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor: previewEmails ? "pointer" : "default",
              transition: "all 0.2s",
            }}
          >
            APPROVE & PERSONALIZE ALL {totalContacts} LEADS →
          </button>
        </div>
      </div>
    </div>
  );
}

function QueuedPanel({ queue, listName, emailPlatform, linkedinPlatform, emailCampaign, emailCampaignLabel, linkedinCampaign, outreachResults }) {
  const emailLeads = queue.filter(q => q.channels?.email);
  const linkedinLeads = queue.filter(q => q.channels?.linkedin);
  const listOnlyLeads = queue.filter(q => q.channels?.listOnly);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          Outreach <span style={{ color: COLORS.accent }}>Queued</span> ✓
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>
          List "<span style={{ color: COLORS.accent }}>{listName}</span>" — {queue.length} leads distributed across channels
        </p>
      </div>

      {/* Stats Row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
        <StatCard label="Total Leads" value={queue.length} accent={COLORS.accent} />
        <StatCard label="Email Outreach" value={emailLeads.length} accent={COLORS.accent} />
        <StatCard label="LinkedIn Outreach" value={linkedinLeads.length} accent={COLORS.blue} />
        <StatCard label="List Only" value={listOnlyLeads.length} accent={COLORS.textMuted} />
      </div>

      {/* Channel Summaries */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        {emailLeads.length > 0 && (
          <div style={{ flex: 1, padding: 20, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>📧</span>
              <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>
                {emailPlatform === "instantly" ? "Instantly.ai" : "SmartLead"}
              </span>
              <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, padding: "2px 8px", background: COLORS.accentBg, borderRadius: 10, marginLeft: "auto" }}>
                {emailLeads.length} queued
              </span>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
              Campaign:{" "}
              <span style={{ color: COLORS.text }}>
                {emailPlatform === "instantly" ? emailCampaignLabel || emailCampaign || "Default Campaign" : emailCampaign || "Default Campaign"}
              </span>
            </div>
            {emailLeads.slice(0, 4).map(item => (
              <div key={item.contact.id} style={{ padding: "6px 0", fontSize: 12, borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between" }}>
                <span>{item.contact.name}</span>
                <span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 11 }}>{item.contact.email}</span>
              </div>
            ))}
            {emailLeads.length > 4 && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 6 }}>+ {emailLeads.length - 4} more</div>}
          </div>
        )}
        {linkedinLeads.length > 0 && (
          <div style={{ flex: 1, padding: 20, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>💼</span>
              <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>
                {linkedinPlatform === "heyreach" ? "HeyReach" : "AimFox"}
              </span>
              <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.blue, padding: "2px 8px", background: COLORS.blueBg, borderRadius: 10, marginLeft: "auto" }}>
                {linkedinLeads.length} queued
              </span>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
              Campaign: <span style={{ color: COLORS.text }}>{linkedinCampaign || "Default Campaign"}</span>
            </div>
            {linkedinLeads.slice(0, 4).map(item => (
              <div key={item.contact.id} style={{ padding: "6px 0", fontSize: 12, borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between" }}>
                <span>{item.contact.name}</span>
                <span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 11 }}>{item.contact.title}</span>
              </div>
            ))}
            {linkedinLeads.length > 4 && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 6 }}>+ {linkedinLeads.length - 4} more</div>}
          </div>
        )}
      </div>

      {listOnlyLeads.length > 0 && (
        <div style={{ padding: 16, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 24 }}>
          <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>📋 SAVED TO LIST ONLY ({listOnlyLeads.length})</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {listOnlyLeads.map(item => item.contact.name).join(", ")}
          </div>
        </div>
      )}

      <div style={{ padding: 20, background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
        <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 10, fontWeight: 600 }}>OUTREACH RESULTS</div>
        <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.7 }}>
          {outreachResults ? (
            <>
              {outreachResults.listSaved && (
                <div style={{ marginBottom: 10 }}>
                  Lead list &quot;{outreachResults.listSaved.name}&quot; saved to database ({outreachResults.listSaved.count} contacts). View in Outbound &gt; Lead Lists.
                </div>
              )}
              {outreachResults.email && (
                <div style={{ marginBottom: 10 }}>
                  {outreachResults.email.platform === "instantly" ? "Instantly.ai" : "SmartLead"} ({outreachResults.email.campaign}): {outreachResults.email.successful}/{outreachResults.email.attempted} leads added.
                  {outreachResults.email.failedEmails?.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: COLORS.danger }}>Failed: {outreachResults.email.failedEmails.join(", ")}</div>
                  )}
                </div>
              )}
              {outreachResults.linkedin && (
                <div style={{ marginBottom: 10 }}>
                  LinkedIn ({outreachResults.linkedin.platform === "heyreach" ? "HeyReach" : "AimFox"}): {outreachResults.linkedin.successful}/{outreachResults.linkedin.attempted} leads added.
                </div>
              )}
              {outreachResults.listOnly > 0 && (
                <div>{outreachResults.listOnly} leads saved to list only.</div>
              )}
            </>
          ) : (
            <div>Outreach complete. Check the Agent Log for details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Column mapping for import — target fields and aliases (case-insensitive)
const TARGET_FIELDS = [
  { key: "first_name", label: "First Name", aliases: ["first name", "firstname", "given name"] },
  { key: "last_name", label: "Last Name", aliases: ["last name", "lastname", "surname", "family name"] },
  { key: "full_name", label: "Full Name (split)", aliases: ["full name", "name", "contact name"], optional: true },
  { key: "email", label: "Email", aliases: ["email", "e-mail", "email address", "work email"] },
  { key: "title", label: "Job Title", aliases: ["title", "job title", "position", "role"] },
  { key: "company", label: "Company", aliases: ["company", "company name", "organization"] },
  { key: "company_domain", label: "Company Domain", aliases: ["domain", "website", "company domain", "company website"] },
  { key: "linkedin_url", label: "LinkedIn URL", aliases: ["linkedin", "linkedin url", "profile url", "linkedin profile", "url"] },
  { key: "phone", label: "Phone", aliases: ["phone", "phone number", "mobile", "direct dial"] },
];

function autoDetectMapping(headers) {
  if (!headers?.length) return {};
  const hLower = headers.map((h) => String(h || "").toLowerCase().trim());
  const aliasesFor = (tf) => Array.from(new Set([
    tf.key.toLowerCase(),
    tf.key.toLowerCase().replace(/_/g, " "),
    ...(tf.aliases || []).map((a) => a.toLowerCase()),
  ]));
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Whole-word match: a word boundary for us is any non-alphanumeric (space, _, -, /).
  // Prevents header "first name" from matching alias "name" (which the old h.includes()
  // matcher allowed — first_name and full_name would both claim the same header).
  const wholeWordMatch = (haystack, needle) =>
    new RegExp(`(?:^|[^a-z0-9])${escapeRegex(needle)}(?:[^a-z0-9]|$)`, "i").test(haystack);

  const map = {};
  const usedIdx = new Set();

  // Pass 1: exact header == alias. Deterministic (first field in TARGET_FIELDS wins on ties).
  for (const tf of TARGET_FIELDS) {
    const aliases = aliasesFor(tf);
    for (let i = 0; i < headers.length; i++) {
      if (usedIdx.has(i) || !hLower[i]) continue;
      if (aliases.includes(hLower[i])) {
        map[tf.key] = headers[i];
        usedIdx.add(i);
        break;
      }
    }
  }

  // Pass 2: whole-word substring fallback for fields still unmapped and headers still unused.
  for (const tf of TARGET_FIELDS) {
    if (map[tf.key]) continue;
    const aliases = aliasesFor(tf);
    for (let i = 0; i < headers.length; i++) {
      if (usedIdx.has(i) || !hLower[i]) continue;
      if (aliases.some((a) => wholeWordMatch(hLower[i], a))) {
        map[tf.key] = headers[i];
        usedIdx.add(i);
        break;
      }
    }
  }

  return map;
}

function normalizeDomain(d) {
  if (!d || typeof d !== "string") return d;
  return d.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "").toLowerCase().trim() || null;
}

function parseUploadedFile(file, sourceLabel) {
  const ext = (file.name || "").toLowerCase();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) { reject(new Error("Failed to read file")); return; }
        let rows = [];
        let headers = [];
        if (ext.endsWith(".xlsx") || ext.endsWith(".xls")) {
          const wb = XLSX.read(data, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
        } else {
          const str = typeof data === "string" ? data : new TextDecoder().decode(data);
          const wb = XLSX.read(str, { type: "string", raw: false });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
        }
        if (!rows?.length) { reject(new Error("No rows found")); return; }
        headers = Object.keys(rows[0] || {});
        resolve({ name: file.name, rows, headers, source: sourceLabel || "Upload CSV" });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    if (ext.endsWith(".xlsx") || ext.endsWith(".xls")) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file, "UTF-8");
    }
  });
}

function normalizeRows(rows, columnMap) {
  if (!rows?.length || !columnMap) return [];
  return rows
    .filter((r) => Object.values(r || {}).some((v) => v != null && String(v).trim() !== ""))
    .map((r) => {
      const get = (k) => (columnMap[k] ? String(r[columnMap[k]] ?? "").trim() || null : null);
      let first = get("first_name");
      let last = get("last_name");
      if (!first && !last) {
        const full = get("full_name") || (columnMap.full_name ? String(r[columnMap.full_name] ?? "").trim() || null : null);
        if (full) {
          const parts = full.split(/\s+/);
          first = parts[0] || null;
          last = parts.slice(1).join(" ") || null;
        }
      }
      let domain = get("company_domain");
      if (domain) domain = normalizeDomain(domain);
      return {
        first_name: first,
        last_name: last,
        email: get("email"),
        title: get("title"),
        company: get("company"),
        company_domain: domain,
        linkedin_url: get("linkedin_url"),
        phone: get("phone"),
      };
    });
}

function LeadListsView({ outreachQueue, enrichedContacts, personalizedEmails, channelAssignments, listName, emailPlatform, linkedinPlatform, emailCampaign, linkedinCampaign, focusListId, focusJobId, onClearLeadlistFocus, savedPrompts, userRole }) {
  const [selectedList, setSelectedList] = useState(null);
  const [activeTab, setActiveTab] = useState("lists");
  const [importStep, setImportStep] = useState(0); // 0=upload, 1=map, 2=enrich, 3=processing, 4=done
  const [importFile, setImportFile] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [importRows, setImportRows] = useState([]);
  const [enrichOptions, setEnrichOptions] = useState({ verifyEmail: true, findPhone: true, companyData: true, icpScore: true, personalisation: true });
  const [importPromptText, setImportPromptText] = useState(FALLBACK_DEFAULT_PROMPT);
  const [importSelectedPromptKey, setImportSelectedPromptKey] = useState("default");
  const [importCampaignId, setImportCampaignId] = useState("");
  const [importCampaignLabel, setImportCampaignLabel] = useState("");
  useEffect(() => {
    api.prompts.getActive().then(({ key, label, text }) => {
      if (key && savedPrompts?.[key]) {
        setImportSelectedPromptKey(key);
        setImportPromptText(savedPrompts[key].text);
      } else if (key && text) {
        setSavedPrompts(prev => ({ ...prev, [key]: { label: label || 'Active Prompt', text } }));
        setImportSelectedPromptKey(key);
        setImportPromptText(text);
      }
    }).catch(() => {});
  }, [savedPrompts]);
  const [enrichProgress, setEnrichProgress] = useState(0);
  const [enrichLastEvent, setEnrichLastEvent] = useState(null);
  const [enrichJobStats, setEnrichJobStats] = useState(null);
  const [enrichEvents, setEnrichEvents] = useState([]);
  const enrichLogRef = useRef(null);
  const importProgressLogRef = useRef(null);
  const [importError, setImportError] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importLiveProgress, setImportLiveProgress] = useState(null);
  const [pendingImportSource, setPendingImportSource] = useState("Upload CSV");
  const fileInputRef = useRef(null);
  const [loadedLists, setLoadedLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [refreshListsKey, setRefreshListsKey] = useState(0);
  const [jobLeadsDetail, setJobLeadsDetail] = useState(null);
  const [jobLeadsDetailLoading, setJobLeadsDetailLoading] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [editingLeadData, setEditingLeadData] = useState({});
  const [editingSaving, setEditingSaving] = useState(false);
  const [colWidths, setColWidths] = useState({});
  const [leadSortKey, setLeadSortKey] = useState(null);
  const [leadSortDir, setLeadSortDir] = useState("asc");
  const resizingRef = useRef(null);

  useEffect(() => {
    if (focusListId) setRefreshListsKey(k => k + 1);
  }, [focusListId]);

  useEffect(() => {
    if (importProgressLogRef.current && importLiveProgress?.log?.length) {
      importProgressLogRef.current.scrollTop = importProgressLogRef.current.scrollHeight;
    }
  }, [importLiveProgress]);

  useEffect(() => {
    if (enrichLogRef.current) enrichLogRef.current.scrollTop = enrichLogRef.current.scrollHeight;
  }, [enrichEvents]);

  useEffect(() => {
    if (!focusListId || loadingLists) return;
    const exists = loadedLists.some(l => String(l.id) === String(focusListId));
    if (exists) setSelectedList(focusListId);
  }, [focusListId, loadedLists, loadingLists]);

  useEffect(() => {
    if (!focusJobId || !focusListId || !selectedList || String(selectedList) !== String(focusListId)) {
      setJobLeadsDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setJobLeadsDetailLoading(true);
      try {
        const data = await api.enrichmentV2.jobLeadsDetail(focusJobId);
        if (!cancelled) setJobLeadsDetail(data);
      } catch (e) {
        if (!cancelled) setJobLeadsDetail({ error: e?.message || String(e), rows: [] });
      } finally {
        if (!cancelled) setJobLeadsDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [focusJobId, focusListId, selectedList]);

  // Load lists from DB
  useEffect(() => {
    async function loadLists() {
      try {
        const data = await api.leadLists.list();
        const lists = Array.isArray(data) ? data : [];
        setLoadedLists(lists.map(list => {
          // Extract emails and channels from contacts
          const emails = {};
          const channels = {};
          const contacts = (list.contacts || []).map(c => {
            // Parse personalization data
            if (c.personalisation_json) {
              emails[c.id] = {
                subject: c.personalisation_json.subject || '',
                body: c.personalisation_json.body || '',
              };
              channels[c.id] = {
                email: c.personalisation_json.channel === 'email',
                linkedin: c.personalisation_json.channel === 'linkedin',
                listOnly: c.personalisation_json.channel === 'none',
              };
            }
            return c;
          });
          
          return {
            id: list.id,
            name: list.name,
            createdAt: new Date(list.createdAt),
            contacts: contacts,
            emails: emails,
            channels: channels,
            status: list.status || "draft",
            tags: Array.isArray(list.tags) ? list.tags : [],
            emailPlatform: "instantly",
            linkedinPlatform: "heyreach",
            emailCampaign: "",
            linkedinCampaign: "",
          };
        }));
      } catch (err) {
        console.error("Failed to load lists:", err);
      } finally {
        setLoadingLists(false);
      }
    }
    loadLists();
  }, [refreshListsKey]);

  // Mock saved lists (current + some historical examples) - used as fallback
  const MOCK_LISTS = [
    {
      id: "demo1", name: "Q4 FinTech CROs — EMEA", createdAt: new Date(Date.now() - 86400000 * 5),
      contacts: [
        { id: "d1", name: "Alex Turner", title: "CRO", company: "PayFlow", email: "alex@payflow.io", bounceRisk: "low", linkedin: "linkedin.com/in/alexturner", linkedinData: { connections: 3200, posts: 14, about: "Revenue leader in payments", recentActivity: "Hiring 3 AEs" } },
        { id: "d2", name: "Maria Santos", title: "VP Revenue", company: "NeoBank", email: "maria@neobank.eu", bounceRisk: "low", linkedin: "linkedin.com/in/mariasantos", linkedinData: { connections: 2800, posts: 22, about: "Scaling B2B fintech", recentActivity: "Posted about PLG metrics" } },
        { id: "d3", name: "Henrik Larsson", title: "CRO", company: "KlarPay", email: "henrik@klarpay.se", bounceRisk: "medium", linkedin: "linkedin.com/in/henriklarsson", linkedinData: { connections: 1900, posts: 7, about: "Nordic fintech veteran", recentActivity: "Shared Series C announcement" } },
      ],
      emails: { d1: { subject: "Scaling PayFlow's rev org" }, d2: { subject: "PLG + outbound — best of both" }, d3: { subject: "Congrats on the Series C" } },
      channels: { d1: { email: true }, d2: { email: true, linkedin: true }, d3: { email: true } },
      status: "sent",
      emailPlatform: "instantly", linkedinPlatform: "heyreach", emailCampaign: "FinTech CROs — Q4", linkedinCampaign: "Decision Maker Outreach — Q1",
    },
    {
      id: "demo2", name: "Series A SaaS — US West Coast", createdAt: new Date(Date.now() - 86400000 * 12),
      contacts: [
        { id: "d4", name: "Jordan Lee", title: "CEO", company: "Stackwise", email: "jordan@stackwise.com", bounceRisk: "low", linkedin: "linkedin.com/in/jordanlee", linkedinData: { connections: 8400, posts: 55, about: "Building dev tools", recentActivity: "Launched new API product" } },
        { id: "d5", name: "Priya Mehta", title: "Head of Growth", company: "DataPulse", email: "priya@datapulse.ai", bounceRisk: "low", linkedin: "linkedin.com/in/priyamehta", linkedinData: { connections: 4100, posts: 30, about: "Growth @ DataPulse", recentActivity: "Spoke at SaaStr" } },
      ],
      emails: { d4: { subject: "Stackwise API launch — congrats" }, d5: { subject: "Post-SaaStr follow-up" } },
      channels: { d4: { email: true, linkedin: true }, d5: { email: true } },
      status: "sent",
      emailPlatform: "smartlead", linkedinPlatform: "aimfox", emailCampaign: "Series A Founders — Jan", linkedinCampaign: "Founder Connect Campaign",
    },
  ];

  // Combine current session list + DB lists + mock lists (if DB is empty)
  const currentSessionList = enrichedContacts.length > 0 ? [{
    id: "current",
    name: listName,
    createdAt: new Date(),
    contacts: enrichedContacts,
    emails: personalizedEmails,
    channels: channelAssignments,
    status: outreachQueue.length > 0 ? "queued" : "draft",
    emailPlatform, linkedinPlatform, emailCampaign, linkedinCampaign,
  }] : [];

  const allLists = [
    ...currentSessionList,
    ...loadedLists,
    // No mock fallback — new users see empty state
  ];

  const activeList = selectedList ? allLists.find(l => String(l.id) === String(selectedList)) : null;

  const jobDetailReady = jobLeadsDetail && !jobLeadsDetail.error && focusJobId && selectedList && String(selectedList) === String(focusListId);
  const jobMode = Boolean(jobDetailReady && Array.isArray(jobLeadsDetail.rows));

  const displayContacts = useMemo(() => {
    if (!jobMode || !jobLeadsDetail?.rows) return activeList?.contacts || [];
    return jobLeadsDetail.rows.map((row) => {
      const ld = row.company_data_json?.linkedinData || row.company_data_json?.linkedin_data || {};
      const name = [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email || row.ejp_linkedin_url || "—";
      return {
        id: row.lead_id || row.ejp_id,
        name,
        title: row.title || "",
        email: row.email || "",
        company: row.company || "",
        bounceRisk: row.email_bounce_risk === "low" ? "low" : row.email_bounce_risk === "high" ? "high" : "medium",
        linkedin: row.lead_linkedin_url || row.ejp_linkedin_url,
        linkedinData: (ld.connections != null || ld.recentActivity != null || ld.recent_activity != null)
          ? { connections: ld.connections, recentActivity: ld.recent_activity || ld.recentActivity }
          : null,
        email_validation_status: row.email_validation_status,
        email_verified: row.email_verified,
        icp_score: row.icp_score,
        personalisation_json: row.personalisation_json,
        job_profile_status: row.job_profile_status,
        skip_reason: row.skip_reason,
        instantly_pushed_at: row.instantly_pushed_at,
      };
    });
  }, [jobMode, jobLeadsDetail, activeList?.contacts]);

  const formatEmailValidationStatus = (v) => {
    if (v == null || v === "") return "—";
    const s = String(v).toLowerCase();
    if (s === "valid") return "valid";
    if (s === "catch_all" || s === "catch-all") return "catch-all";
    if (s === "invalid") return "invalid";
    if (s === "pending") return "pending";
    return String(v);
  };

  const hasPersonalizationBody = (contact, list) => {
    const fromList = list?.emails?.[contact.id];
    if (fromList?.body?.trim() || fromList?.subject?.trim()) return "Yes";
    let pj = contact.personalisation_json;
    if (pj && typeof pj === "string") {
      try { pj = JSON.parse(pj); } catch { pj = null; }
    }
    const body = pj && (pj.body || pj.personalizedMessage || pj.message);
    return typeof body === "string" && body.trim() ? "Yes" : "No";
  };

  const LIST_COLUMNS = [
    { key: "name", label: "Name", width: 150 },
    { key: "title", label: "Title", width: 140 },
    { key: "company", label: "Company", width: 130 },
    { key: "email", label: "Email", width: 200 },
    { key: "email_validation", label: "Email status", width: 100 },
    { key: "linkedin", label: "LinkedIn", width: 160 },
    { key: "icp", label: "ICP", width: 52 },
    { key: "personalized", label: "Personalized", width: 300 },
    { key: "connections", label: "Connections", width: 100 },
    { key: "recentActivity", label: "Recent Activity", width: 220 },
    { key: "emailCampaign", label: "Email Campaign", width: 200 },
    { key: "linkedinCampaign", label: "LinkedIn Campaign", width: 220 },
  ];

  const JOB_COLUMNS = [
    { key: "job_profile_status", label: "Job outcome", width: 100 },
    { key: "skip_reason", label: "Skip reason", width: 160 },
    { key: "name", label: "Name", width: 140 },
    { key: "title", label: "Title", width: 120 },
    { key: "company", label: "Company", width: 120 },
    { key: "email", label: "Email", width: 180 },
    { key: "email_validation", label: "Email status", width: 100 },
    { key: "linkedin", label: "LinkedIn", width: 160 },
    { key: "icp", label: "ICP", width: 48 },
    { key: "personalized", label: "Personalized", width: 300 },
    { key: "instantly", label: "Instantly", width: 100 },
    { key: "connections", label: "Connections", width: 90 },
    { key: "recentActivity", label: "Recent Activity", width: 180 },
    { key: "emailCampaign", label: "Email Campaign", width: 160 },
    { key: "linkedinCampaign", label: "LinkedIn Campaign", width: 200 },
  ];

  const COLUMNS = jobMode ? JOB_COLUMNS : LIST_COLUMNS;
  const EDITABLE_KEYS = new Set(["name", "title", "company", "email", "linkedin", "personalized"]);

  const startEditingLead = (contact) => {
    const pj = parsePersonalisationJsonFromRow(contact);
    const nameParts = (contact.name || "").split(" ");
    setEditingLeadId(contact.id);
    setEditingLeadData({
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
      title: contact.title || "",
      company: contact.company || "",
      email: contact.email || "",
      linkedin_url: contact.linkedin_url || contact.linkedin || "",
      p_subject: pj?.subject || "",
      p_body: pj?.body || "",
    });
  };

  const cancelEditingLead = () => { setEditingLeadId(null); setEditingLeadData({}); };

  const saveEditingLead = async () => {
    if (!editingLeadId) return;
    setEditingSaving(true);
    try {
      const payload = {
        first_name: editingLeadData.first_name,
        last_name: editingLeadData.last_name,
        email: editingLeadData.email,
        title: editingLeadData.title,
        company: editingLeadData.company,
        linkedin_url: editingLeadData.linkedin_url,
      };
      if (editingLeadData.p_subject || editingLeadData.p_body) {
        payload.personalisation_json = { subject: editingLeadData.p_subject, body: editingLeadData.p_body };
      }
      await api.leads.update(editingLeadId, payload);
      setEditingLeadId(null);
      setEditingLeadData({});
      setRefreshListsKey(k => k + 1);
    } catch (e) {
      alert("Save failed: " + (e?.message || "Unknown error"));
    }
    setEditingSaving(false);
  };

  const getCellValue = (contact, col, list) => {
    const email = list?.emails?.[contact.id];
    const ch = list?.channels?.[contact.id] || {};
    switch (col.key) {
      case "job_profile_status": return contact.job_profile_status || "—";
      case "skip_reason": return contact.skip_reason || "—";
      case "instantly": {
        if (contact.instantly_pushed_at) return "Yes";
        if (!contact.email) return "—";
        if (formatEmailValidationStatus(contact.email_validation_status) === "valid") return "No";
        return "—";
      }
      case "name": return contact.name;
      case "title": return contact.title;
      case "company": return contact.company;
      case "email": return contact.email;
      case "email_validation": return formatEmailValidationStatus(contact.email_validation_status);
      case "linkedin": return contact.linkedin_url || contact.linkedin || "—";
      case "icp": return contact.icp_score != null && contact.icp_score !== "" ? String(contact.icp_score) : "—";
      case "personalized": return hasPersonalizationBody(contact, list);
      case "connections": return contact.linkedinData?.connections?.toLocaleString() || "—";
      case "recentActivity": return contact.linkedinData?.recentActivity || "—";
      case "subject": {
        const sub = email?.subject?.trim();
        if (sub) return sub;
        let pj = contact.personalisation_json;
        if (pj && typeof pj === "string") {
          try { pj = JSON.parse(pj); } catch { pj = null; }
        }
        const body = pj && (pj.body || pj.message || pj.personalizedMessage);
        if (body && typeof body === "string") {
          const firstLine = body.split("\n")[0]?.trim();
          return firstLine ? (firstLine.length > 60 ? firstLine.slice(0, 57) + "..." : firstLine) : "—";
        }
        const legacyBody = email?.body;
        if (legacyBody && typeof legacyBody === "string") {
          const firstLine = legacyBody.split("\n")[0]?.trim();
          return firstLine ? (firstLine.length > 60 ? firstLine.slice(0, 57) + "..." : firstLine) : "—";
        }
        return "—";
      }
      case "emailCampaign": return ch.email ? (list?.emailCampaign || "—") : "—";
      case "linkedinCampaign": return ch.linkedin ? (list?.linkedinCampaign || "—") : "—";
      default: return "—";
    }
  };

  const getColWidth = (col) => colWidths[col.key] || col.width;

  const handleResizeStart = (colKey, startWidth) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const onMove = (me) => {
      const newW = Math.max(60, startWidth + (me.clientX - startX));
      setColWidths(prev => ({ ...prev, [colKey]: newW }));
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleSort = (key) => {
    if (leadSortKey === key) setLeadSortDir(prev => prev === "asc" ? "desc" : "asc");
    else { setLeadSortKey(key); setLeadSortDir("asc"); }
  };

  const sortedContacts = useMemo(() => {
    if (!leadSortKey) return displayContacts;
    const col = COLUMNS.find(c => c.key === leadSortKey);
    if (!col) return displayContacts;
    return [...displayContacts].sort((a, b) => {
      const va = (getCellValue(a, col, activeList) || "").toString().toLowerCase();
      const vb = (getCellValue(b, col, activeList) || "").toString().toLowerCase();
      const na = parseFloat(va), nb = parseFloat(vb);
      if (!isNaN(na) && !isNaN(nb)) return leadSortDir === "asc" ? na - nb : nb - na;
      if (va < vb) return leadSortDir === "asc" ? -1 : 1;
      if (va > vb) return leadSortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [displayContacts, leadSortKey, leadSortDir, COLUMNS, activeList]);

  if (!activeList) {
    return (
      <div style={{ padding: 32, flex: 1, overflow: "auto" }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Lead <span style={{ color: COLORS.accent }}>Lists</span>
          </h2>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
          {[{ key: "lists", label: "📋 Lists" }, { key: "import", label: "⬆️ Import & Enrich" }].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); if (tab.key === "import") setImportStep(0); }} style={{
              padding: "10px 20px", background: "transparent", border: "none",
              borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted,
              fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Import & Enrich Tab */}
        {activeTab === "import" && (
          <div>
            {importStep === 0 && (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv,.xlsx,.xls,.txt"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target?.files?.[0];
                    if (!file) return;
                    setImportError(null);
                    try {
                      const parsed = await parseUploadedFile(file, pendingImportSource);
                      if (pendingImportSource === "LinkedIn Export") {
                        const hdrs = parsed.headers.map(h => String(h || "").toLowerCase().trim());
                        const needed = ["first name", "last name", "url", "company", "position"];
                        const missing = needed.filter(rh => !hdrs.some(h => h === rh));
                        if (missing.length > 0) {
                          setImportError("This doesn't look like a LinkedIn Connections CSV export. Expected columns: First Name, Last Name, URL, Company, Position. Please export your connections from LinkedIn and upload that file.");
                          e.target.value = "";
                          return;
                        }
                      }
                      setImportFile(parsed);
                      setColumnMap(autoDetectMapping(parsed.headers));
                      setImportStep(1);
                    } catch (err) {
                      setImportError(err.message || "Failed to parse file");
                    }
                    e.target.value = "";
                  }}
                />
                <p style={{ color: COLORS.textMuted, marginBottom: 20, fontSize: 13 }}>Upload an existing list to enrich with verified emails, phone numbers, company data, ICP scoring, and AI personalisation.</p>
                {importError && <div style={{ padding: 12, marginBottom: 16, background: COLORS.warnBg || "#fef3c7", border: `1px solid ${COLORS.warn || "#f59e0b"}33`, borderRadius: 8, fontSize: 12, color: COLORS.warn || "#b45309" }}>{importError}</div>}
                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                  {[
                    { label: "Upload CSV", icon: "📄", desc: ".csv, .xlsx, .txt", active: true, source: "Upload CSV" },
                    { label: "LinkedIn Export", icon: "💼", desc: "Connections CSV", active: true, source: "LinkedIn Export" },
                  ].map((opt, i) => (
                    <div key={i} onClick={() => { if (opt.active && fileInputRef.current) { setPendingImportSource(opt.source || "Upload CSV"); fileInputRef.current.click(); } }}
                      style={{ flex: 1, padding: "20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "center", cursor: opt.active ? "pointer" : "default", opacity: opt.active ? 1 : 0.5, transition: "border-color 0.15s" }}
                      onMouseEnter={e => { if (opt.active) e.currentTarget.style.borderColor = COLORS.accent + "66"; }}
                      onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{opt.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{opt.label}</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
                <div
                  style={{ padding: "40px", background: COLORS.surface, border: `2px dashed ${COLORS.border}`, borderRadius: 12, textAlign: "center", cursor: "pointer" }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = COLORS.accent + "66"; }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = COLORS.border; }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = COLORS.border;
                    const file = e.dataTransfer?.files?.[0];
                    if (!file) return;
                    setImportError(null);
                    try {
                      const parsed = await parseUploadedFile(file, "Upload CSV");
                      setImportFile(parsed);
                      setColumnMap(autoDetectMapping(parsed.headers));
                      setImportStep(1);
                    } catch (err) {
                      setImportError(err.message || "Failed to parse file");
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>⬆️</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 4 }}>Drag & drop your file here</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim }}>Supports CSV, XLSX, TXT up to 10,000 rows</div>
                </div>
              </div>
            )}

            {importStep === 1 && importFile && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <button onClick={() => { setImportStep(0); setImportFile(null); setColumnMap({}); }} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
                  <div><div style={{ fontWeight: 600, fontSize: 15 }}>Map Columns</div><div style={{ fontSize: 11, color: COLORS.textDim }}>{importFile.name} · {importFile.rows?.length || 0} rows detected · Source: {importFile.source}</div></div>
                </div>
                <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 20 }}>
                  <div style={{ padding: "8px 12px", background: COLORS.accentBg, border: `1px solid ${COLORS.accent}22`, borderRadius: 8, marginBottom: 16, fontSize: 12, color: COLORS.accent }}>✨ Auto-detected column mapping. Review and adjust if needed.</div>
                  {TARGET_FIELDS.map((tf, i) => {
                    const mappedHeader = columnMap[tf.key];
                    const sampleValues = importFile.rows && mappedHeader
                      ? [...new Set(importFile.rows.map((r) => String(r[mappedHeader] ?? "").trim()).filter(Boolean))].slice(0, 3).join(", ") + (importFile.rows.length > 3 ? "..." : "")
                      : "";
                    return (
                      <div key={tf.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < TARGET_FIELDS.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                        <div style={{ width: 140, fontSize: 12, fontWeight: 600, color: COLORS.text }}>{tf.label}</div>
                        <div style={{ fontSize: 14, color: COLORS.accent }}>→</div>
                        <select
                          value={mappedHeader || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setColumnMap((prev) => (v ? { ...prev, [tf.key]: v } : (() => { const p = { ...prev }; delete p[tf.key]; return p; })()));
                          }}
                          style={{ padding: "6px 10px", minWidth: 160, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12, outline: "none" }}
                        >
                          <option value="">Skip</option>
                          {(importFile.headers || []).map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <div style={{ flex: 1, fontSize: 11, color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sampleValues || "—"}</div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => {
                    const normalized = normalizeRows(importFile.rows, columnMap);
                    setImportRows(normalized);
                    setImportStep(2);
                  }}
                  style={{ padding: "12px 28px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >
                  Confirm Mapping →
                </button>
              </div>
            )}

            {importStep === 2 && (() => {
              const isLinkedIn = importFile?.source === "LinkedIn Export";
              const qualifiedRows = importRows.filter(r =>
                (r.first_name?.trim() || r.last_name?.trim()) &&
                (isLinkedIn ? r.company?.trim() : (r.email?.trim() && r.company_domain?.trim()))
              );
              const skippedCount = importRows.length - qualifiedRows.length;

              if (isLinkedIn) {
                const canSave = qualifiedRows.length > 0;
                return (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <button onClick={() => setImportStep(1)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
                    <div><div style={{ fontWeight: 600, fontSize: 15 }}>Review LinkedIn Contacts</div><div style={{ fontSize: 11, color: COLORS.textDim }}>{importRows.length} rows uploaded</div></div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                    <div style={{ flex: 1, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderRadius: 10 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.accent, fontFamily: FONT }}>{qualifiedRows.length}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Ready to save</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>Have name + company</div>
                    </div>
                    {skippedCount > 0 && (
                    <div style={{ flex: 1, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.textDim, fontFamily: FONT }}>{skippedCount}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Skipped</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>Missing required fields</div>
                    </div>
                    )}
                  </div>
                  <div style={{ padding: "14px 18px", background: "#0077B510", border: "1px solid #0077B533", borderRadius: 10, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 20 }}>💼</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>LinkedIn Contacts — Rolodex</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>Contacts will be saved as a lead list tagged <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 4, background: "#0077B520", color: "#0077B5", fontFamily: FONT, fontWeight: 600, fontSize: 9 }}>Rolodex</span></div>
                    </div>
                  </div>
                  {qualifiedRows.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                      {[
                        { icon: "👤", label: "Name", desc: `${qualifiedRows.filter(r => r.first_name || r.last_name).length} contacts with names` },
                        { icon: "🏢", label: "Company", desc: `${qualifiedRows.filter(r => r.company?.trim()).length} with company info` },
                        { icon: "💼", label: "Title", desc: `${qualifiedRows.filter(r => r.title?.trim()).length} with job titles` },
                        { icon: "🔗", label: "LinkedIn URL", desc: `${qualifiedRows.filter(r => r.linkedin_url?.trim()).length} with profile links` },
                      ].map(s => (
                        <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                          <span style={{ fontSize: 14 }}>{s.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>{s.label}</div>
                            <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {importLoading && (() => {
                    const exp = importLiveProgress?.leadsExpected || qualifiedRows.length || 0;
                    const saved = importLiveProgress?.leadsSaved ?? 0;
                    const pct = exp > 0 && importLiveProgress?.phase === "leads" ? Math.min(100, Math.round((saved / exp) * 100)) : 0;
                    return (
                      <div style={{ marginBottom: 16, padding: 16, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 12, color: COLORS.text }}>Import progress</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <span style={{
                              width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 700, flexShrink: 0,
                              background: importLiveProgress?.phase === "companies" ? COLORS.accentBg : (importLiveProgress?.phase === "leads" || importLiveProgress?.phase === "error" ? COLORS.accent + "44" : COLORS.border),
                              color: importLiveProgress?.phase === "companies" ? COLORS.accent : COLORS.textMuted,
                            }}>1</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>Resolve companies</div>
                              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                                {importLiveProgress?.phase === "companies" ? (importLiveProgress.message || "Working…") : (importLiveProgress?.phase === "leads" || importLiveProgress?.phase === "error") ? "Complete" : "Waiting…"}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <span style={{
                              width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 700, flexShrink: 0,
                              background: importLiveProgress?.phase === "leads" ? COLORS.accentBg : COLORS.border,
                              color: importLiveProgress?.phase === "leads" ? COLORS.accent : COLORS.textMuted,
                            }}>2</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>Save contacts</div>
                              {importLiveProgress?.phase === "leads" && exp > 0 && (
                                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>{saved} / {exp}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        {importLiveProgress?.phase === "leads" && exp > 0 && (
                          <div style={{ height: 8, background: COLORS.bg, borderRadius: 4, marginBottom: 12, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: COLORS.accent, borderRadius: 4, transition: "width 0.2s ease" }} />
                          </div>
                        )}
                        <div
                          ref={importProgressLogRef}
                          style={{
                            maxHeight: 200,
                            overflowY: "auto",
                            padding: "10px 12px",
                            background: COLORS.bg,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 8,
                            fontFamily: FONT,
                            fontSize: 10,
                            color: COLORS.textDim,
                            lineHeight: 1.5,
                          }}
                        >
                          {(!importLiveProgress?.log || importLiveProgress.log.length === 0) ? (
                            <div style={{ color: COLORS.textMuted }}>Saved contacts appear here as each batch is written…</div>
                          ) : (
                            importLiveProgress.log.map((line, i) => <div key={`${i}-${String(line).slice(0, 24)}`}>{line}</div>)
                          )}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 8 }}>Rolling log (latest {importLiveProgress?.log?.length || 0} lines shown)</div>
                      </div>
                    );
                  })()}
                  <button
                    disabled={importLoading || !canSave}
                    onClick={async () => {
                      setImportLoading(true);
                      setImportError(null);
                      setImportLiveProgress(null);
                      try {
                        const listName = importFile?.name?.replace(/\.[^.]+$/, "") || `LinkedIn Import ${new Date().toISOString().slice(0, 10)}`;
                        const importResult = await api.leadLists.import({ name: listName, rows: qualifiedRows, source: "linkedin_contacts", tags: ["Rolodex"] });
                        const listId = importResult.listId || importResult.id;
                        if (!listId) throw new Error("Import did not return list ID");
                        if (importResult.status === "importing") {
                          for (let attempt = 0; attempt < 300; attempt++) {
                            await new Promise(r => setTimeout(r, 2000));
                            try {
                              const s = await api.leadLists.importStatus(listId);
                              if (s.import_progress) setImportLiveProgress(s.import_progress);
                              if (s.status === "draft") break;
                              if (s.status === "error") throw new Error((s.import_progress && s.import_progress.message) || "Import failed on server");
                            } catch (e) {
                              if (e?.message === "Import failed on server" || (e?.message && String(e.message).includes("Import failed"))) throw e;
                            }
                          }
                        }
                        setRefreshListsKey((k) => k + 1);
                        setImportStep(0);
                        setImportFile(null);
                        setColumnMap({});
                        setImportRows([]);
                        setActiveTab("lists");
                        setTimeout(() => setSelectedList(listId), 300);
                      } catch (err) {
                        setImportError(err.message || "Import failed");
                      } finally {
                        setImportLoading(false);
                        setImportLiveProgress(null);
                      }
                    }}
                    style={{ padding: "12px 28px", background: canSave ? "#0077B5" : COLORS.border, color: canSave ? "#fff" : COLORS.textMuted, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: importLoading || !canSave ? "not-allowed" : "pointer", opacity: importLoading ? 0.8 : 1 }}
                  >
                    {importLoading ? "Importing… this may take a moment" : `Save ${qualifiedRows.length} Contacts as Lead List`}
                  </button>
                  {importError && <div style={{ marginTop: 12, padding: 12, background: COLORS.warnBg || "#fef3c7", border: `1px solid ${COLORS.warn || "#f59e0b"}33`, borderRadius: 8, fontSize: 12, color: COLORS.warn || "#b45309" }}>{importError}</div>}
                </div>
                );
              }

              const canStart = qualifiedRows.length > 0 && importCampaignId;
              return (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <button onClick={() => setImportStep(1)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
                  <div><div style={{ fontWeight: 600, fontSize: 15 }}>Review & Process Leads</div><div style={{ fontSize: 11, color: COLORS.textDim }}>{importRows.length} rows uploaded</div></div>
                </div>
                <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                  <div style={{ flex: 1, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderRadius: 10 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.accent, fontFamily: FONT }}>{qualifiedRows.length}</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Ready to process</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>Have email + name + company domain</div>
                  </div>
                  {skippedCount > 0 && (
                  <div style={{ flex: 1, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.textDim, fontFamily: FONT }}>{skippedCount}</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Skipped</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>Missing required fields</div>
                  </div>
                  )}
                </div>
                {qualifiedRows.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                    {[
                      { icon: "✉️", label: "Email Verification", desc: "Verify deliverability and bounce risk for each email" },
                      { icon: "✨", label: "AI Personalisation", desc: "Generate personalised email subject and body" },
                      { icon: "🚀", label: "Send to Instantly", desc: "Push verified and personalised leads to your selected campaign" },
                    ].map(s => (
                      <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.accent}22`, borderRadius: 8 }}>
                        <span style={{ fontSize: 16 }}>{s.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>{s.label}</div>
                          <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <LeadsMgmtPreflightPanel
                  savedPrompts={savedPrompts || SAVED_PROMPTS}
                  promptText={importPromptText}
                  setPromptText={setImportPromptText}
                  selectedPromptKey={importSelectedPromptKey}
                  setSelectedPromptKey={setImportSelectedPromptKey}
                  campaignId={importCampaignId}
                  setCampaignId={setImportCampaignId}
                  setCampaignLabel={setImportCampaignLabel}
                />
                {importLoading && (() => {
                  const exp = importLiveProgress?.leadsExpected || qualifiedRows.length || 0;
                  const saved = importLiveProgress?.leadsSaved ?? 0;
                  const pct = exp > 0 && importLiveProgress?.phase === "leads" ? Math.min(100, Math.round((saved / exp) * 100)) : 0;
                  return (
                    <div style={{ marginBottom: 16, padding: 16, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 12, color: COLORS.text }}>Import progress</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <span style={{
                            width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                            background: importLiveProgress?.phase === "companies" ? COLORS.accentBg : (importLiveProgress?.phase === "leads" || importLiveProgress?.phase === "error" ? COLORS.accent + "44" : COLORS.border),
                            color: importLiveProgress?.phase === "companies" ? COLORS.accent : COLORS.textMuted,
                          }}>1</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>Resolve companies</div>
                            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                              {importLiveProgress?.phase === "companies" ? (importLiveProgress.message || "Working…") : (importLiveProgress?.phase === "leads" || importLiveProgress?.phase === "error") ? "Complete" : "Waiting…"}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <span style={{
                            width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                            background: importLiveProgress?.phase === "leads" ? COLORS.accentBg : COLORS.border,
                            color: importLiveProgress?.phase === "leads" ? COLORS.accent : COLORS.textMuted,
                          }}>2</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: COLORS.text }}>Save contacts</div>
                            {importLiveProgress?.phase === "leads" && exp > 0 && (
                              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>{saved} / {exp}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      {importLiveProgress?.phase === "leads" && exp > 0 && (
                        <div style={{ height: 8, background: COLORS.bg, borderRadius: 4, marginBottom: 12, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: COLORS.accent, borderRadius: 4, transition: "width 0.2s ease" }} />
                        </div>
                      )}
                      <div
                        ref={importProgressLogRef}
                        style={{
                          maxHeight: 200,
                          overflowY: "auto",
                          padding: "10px 12px",
                          background: COLORS.bg,
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: 8,
                          fontFamily: FONT,
                          fontSize: 10,
                          color: COLORS.textDim,
                          lineHeight: 1.5,
                        }}
                      >
                        {(!importLiveProgress?.log || importLiveProgress.log.length === 0) ? (
                          <div style={{ color: COLORS.textMuted }}>Saved contacts appear here as each batch is written…</div>
                        ) : (
                          importLiveProgress.log.map((line, i) => <div key={`${i}-${String(line).slice(0, 24)}`}>{line}</div>)
                        )}
                      </div>
                      <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 8 }}>Rolling log (latest {importLiveProgress?.log?.length || 0} lines shown)</div>
                    </div>
                  );
                })()}
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <button
                    disabled={importLoading || !canStart}
                    onClick={async () => {
                      setImportLoading(true);
                      setImportError(null);
                      setImportLiveProgress(null);
                      try {
                        const listName = importFile?.name?.replace(/\.[^.]+$/, "") || `Import ${new Date().toISOString().slice(0, 10)}`;
                        const importResult = await api.leadLists.import({ name: listName, rows: qualifiedRows });
                        const listId = importResult.listId || importResult.id;
                        if (!listId) throw new Error("Import did not return list ID");
                        if (importResult.status === "importing") {
                          for (let attempt = 0; attempt < 300; attempt++) {
                            await new Promise(r => setTimeout(r, 2000));
                            try {
                              const s = await api.leadLists.importStatus(listId);
                              if (s.import_progress) setImportLiveProgress(s.import_progress);
                              if (s.status === "draft") break;
                              if (s.status === "error") throw new Error((s.import_progress && s.import_progress.message) || "Import failed on server");
                            } catch (e) {
                              if (e?.message === "Import failed on server" || (e?.message && String(e.message).includes("Import failed"))) throw e;
                            }
                          }
                        }
                        const startRes = await api.leadGeneration.enrichImportStart({
                          listId,
                          options: {
                            verifyEmail: true,
                            findPhone: false,
                            findEmail: false,
                            companyData: false,
                            icpScore: false,
                            personalisation: true,
                            promptText: importPromptText,
                            instantlyCampaignId: importCampaignId,
                          },
                        });
                        const jobId = startRes.jobId;
                        setEnrichOptions({ findEmail: false, verifyEmail: true, findPhone: false, companyData: false, icpScore: false, personalisation: true, instantlyCampaign: !!importCampaignId });
                        setImportStep(3);
                        setEnrichProgress(0);
                        setEnrichEvents([]);
                        const poll = async () => {
                          const status = await api.leadGeneration.enrichImportStatus(jobId);
                          const events = status.events || [];
                          const progressEvents = events.filter((e) => e.event === "progress");
                          setEnrichProgress(progressEvents.length);
                          setEnrichEvents(progressEvents.map(e => e.data));
                          const last = progressEvents[progressEvents.length - 1];
                          if (last?.data) setEnrichLastEvent(last.data);
                          if (status.stats) setEnrichJobStats(status.stats);
                          if (status.status === "complete" || status.status === "error") {
                            setImportStep(4);
                            if (status.stats) setEnrichJobStats(status.stats);
                            setRefreshListsKey((k) => k + 1);
                            return;
                          }
                          setTimeout(poll, 1500);
                        };
                        poll();
                      } catch (err) {
                        setImportError(err.message || "Import failed");
                      } finally {
                        setImportLoading(false);
                        setImportLiveProgress(null);
                      }
                    }}
                    style={{ padding: "12px 28px", background: canStart ? COLORS.accent : COLORS.border, color: canStart ? COLORS.bg : COLORS.textMuted, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: importLoading || !canStart ? "not-allowed" : "pointer", opacity: importLoading ? 0.8 : 1 }}
                  >
                    {importLoading ? "Importing… this may take a moment" : `Start Processing ${qualifiedRows.length} Leads`}
                  </button>
                  {!importCampaignId && <div style={{ fontSize: 11, color: COLORS.warn || "#f59e0b" }}>Select an Instantly campaign to continue</div>}
                </div>
                {importError && <div style={{ marginTop: 12, padding: 12, background: COLORS.warnBg || "#fef3c7", border: `1px solid ${COLORS.warn || "#f59e0b"}33`, borderRadius: 8, fontSize: 12, color: COLORS.warn || "#b45309" }}>{importError}</div>}
              </div>
              );
            })()}

            {importStep === 3 && (() => {
              const STEP_DEFS = [
                { key: "findEmail", label: "Email Finding", icon: "🔍", eventType: "finding_email" },
                { key: "verifyEmail", label: "Email Verification", icon: "✉️", eventType: "verifying" },
                { key: "findPhone", label: "Phone Number Lookup", icon: "📞", eventType: "finding_phone" },
                { key: "companyData", label: "Company Enrichment", icon: "🏢", eventType: "company_data" },
                { key: "icpScore", label: "ICP Scoring", icon: "🎯", eventType: "icp_score" },
                { key: "personalisation", label: "AI Personalisation", icon: "✨", eventType: "personalising" },
                { key: "instantlyCampaign", label: "Campaign Queueing", icon: "🚀", eventType: "instantly_lead_sent", altEventType: "sending_to_instantly" },
              ];
              const activeSteps = STEP_DEFS.filter((s) => enrichOptions[s.key]);
              const eventTypeCounts = {};
              const eventTypeMaxIndex = {};
              enrichEvents.forEach((ev) => {
                if (ev?.type) {
                  eventTypeCounts[ev.type] = (eventTypeCounts[ev.type] || 0) + 1;
                  if (typeof ev.index === "number") eventTypeMaxIndex[ev.type] = Math.max(eventTypeMaxIndex[ev.type] || 0, ev.index);
                }
              });
              const activeStepEventTypes = activeSteps.map(s => s.eventType);
              const latestType = enrichLastEvent?.type;
              const latestIdx = latestType ? activeStepEventTypes.indexOf(latestType) : -1;

              const getStepStatus = (step, stepIdx) => {
                const myIdx = activeStepEventTypes.indexOf(step.eventType);
                const hasEvents = (eventTypeCounts[step.eventType] || 0) > 0 || (step.altEventType && (eventTypeCounts[step.altEventType] || 0) > 0);
                if (latestIdx > myIdx && hasEvents) return "complete";
                if (latestType === step.eventType || (step.altEventType && latestType === step.altEventType)) return "active";
                if (hasEvents && latestIdx >= myIdx) return "complete";
                return "pending";
              };

              const getStepCount = (step) => {
                const total = enrichLastEvent?.total || enrichJobStats?.total || importRows.length;
                const maxIdx = eventTypeMaxIndex[step.eventType];
                if (typeof maxIdx === "number") return `${maxIdx}/${total}`;
                return null;
              };

              const EVENT_LABELS = { loaded: "📥 Loading", finding_email: "🔍 Finding email", verifying: "✉️ Verifying", finding_phone: "📞 Phone lookup", personalising: "✨ Personalising", sending_to_instantly: "🚀 Queuing campaign", instantly_lead_sent: "🚀 Sent to campaign" };

              return (
              <div style={{ padding: "40px 0" }}>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Enriching {enrichJobStats?.total || importRows.length} leads...</div>
                  <div style={{ fontSize: 12, color: COLORS.textDim }}>This may take a few minutes</div>
                  {enrichJobStats && (
                    <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 12, flexWrap: "wrap" }}>
                      {enrichOptions.findEmail && <span style={{ fontSize: 11, color: COLORS.accent }}>{enrichJobStats.emailsFound ?? 0} emails found</span>}
                      {enrichOptions.verifyEmail && <span style={{ fontSize: 11, color: COLORS.accent }}>{enrichJobStats.verified ?? 0} verified</span>}
                      {enrichOptions.personalisation && <span style={{ fontSize: 11, color: "#7B61FF" }}>{enrichJobStats.personalized ?? 0} personalised</span>}
                      {enrichOptions.instantlyCampaign && <span style={{ fontSize: 11, color: COLORS.accent }}>{enrichJobStats.sentToInstantly ?? 0} sent</span>}
                    </div>
                  )}
                </div>
                {activeSteps.map((s, si) => {
                  const status = getStepStatus(s, si);
                  const countStr = getStepCount(s);
                  return (
                    <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 16 }}>{s.icon}</span>
                      <div style={{ flex: 1, fontWeight: 600, fontSize: 13, color: status === "complete" ? COLORS.accent : status === "active" ? COLORS.text : COLORS.textDim }}>
                        {s.label}{countStr ? ` (${countStr})` : ""}
                      </div>
                      {status === "complete" && <span style={{ color: COLORS.accent, fontSize: 12, fontWeight: 600 }}>✓ Complete</span>}
                      {status === "active" && <ProgressDots active={true} />}
                      {status === "pending" && <span style={{ fontSize: 10, color: COLORS.textDim }}>Pending</span>}
                    </div>
                  );
                })}
                <div style={{ marginTop: 16, background: "#0d0d14", border: `1px solid ${COLORS.border}`, borderRadius: 8, maxHeight: 220, overflowY: "auto", fontFamily: "'JetBrains Mono','Fira Code','SF Mono',monospace", fontSize: 11, lineHeight: "20px" }} ref={enrichLogRef}>
                  {enrichEvents.length === 0 && <div style={{ padding: "16px 14px", color: COLORS.textDim }}>Waiting for events...</div>}
                  {enrichEvents.map((ev, idx) => (
                    <div key={idx} style={{ padding: "3px 14px", color: ev.type === "instantly_lead_sent" ? COLORS.accent : ev.type === "personalising" ? "#7B61FF" : ev.type === "verifying" ? COLORS.blue : COLORS.textMuted, borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                      <span style={{ opacity: 0.4, marginRight: 8 }}>{String(idx + 1).padStart(3, " ")}</span>
                      {EVENT_LABELS[ev.type] || ev.type}
                      {ev.lead ? ` — ${ev.lead}` : ev.email ? ` — ${ev.email}` : ""}
                      {ev.index && ev.total ? ` (${ev.index}/${ev.total})` : ""}
                    </div>
                  ))}
                </div>
              </div>
              );
            })()}

            {importStep === 4 && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Enrichment Complete</div>
                <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 8 }}>{enrichJobStats?.total ?? importRows.length} leads processed</div>
                <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 24, flexWrap: "wrap" }}>
                  {enrichOptions.findEmail && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.blue }}>{enrichJobStats?.emailsFound ?? "—"}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Emails Found</div></div>}
                  {enrichOptions.verifyEmail && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.accent }}>{enrichJobStats?.verified ?? "—"}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Emails Verified</div></div>}
                  {enrichOptions.findPhone && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.blue }}>{enrichJobStats?.phonesFound ?? "—"}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Phones Found</div></div>}
                  {enrichOptions.icpScore && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.accent }}>{enrichJobStats?.icpScored ?? "—"}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>ICP Scored</div></div>}
                  {enrichOptions.personalisation && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: "#7B61FF" }}>{enrichJobStats?.personalized ?? importRows.length}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Personalised</div></div>}
                  {enrichOptions.instantlyCampaign && <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.accent }}>{enrichJobStats?.sentToInstantly ?? "—"}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Sent to Instantly</div></div>}
                </div>
                <button onClick={() => { setActiveTab("lists"); setImportStep(0); setImportFile(null); setImportRows([]); setColumnMap({}); setEnrichJobStats(null); setRefreshListsKey((k) => k + 1); }} style={{ padding: "12px 28px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>View in Lead Lists →</button>
              </div>
            )}
          </div>
        )}

        {/* Lists Tab */}
        {activeTab === "lists" && (<>
          <p style={{ color: COLORS.textMuted, margin: "-8px 0 20px", fontSize: 13 }}>
            {loadingLists ? "Loading lists..." : allLists.length > 0 ? "View and manage your scraped lead lists." : "No lead lists yet. Run a pipeline to create one."}
          </p>

        {!loadingLists && allLists.length === 0 && (
          <div style={{
            padding: "60px 40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📋</div>
            <div style={{ fontFamily: FONT, fontSize: 13, color: COLORS.textDim }}>
              Lead lists will appear here after you run a pipeline
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {allLists.map(list => (
            <div
              key={list.id}
              onClick={() => setSelectedList(list.id)}
              style={{
                padding: "18px 24px", background: COLORS.surface,
                border: `1px solid ${COLORS.border}`, borderRadius: 10,
                cursor: "pointer", transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.borderActive}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{list.name}</span>
                  {(list.tags || []).map(tag => (
                    <span key={tag} style={{
                      padding: "2px 8px", borderRadius: 4, fontFamily: FONT, fontSize: 9, fontWeight: 600,
                      background: tag === "Rolodex" ? "#0077B518" : COLORS.accentBg,
                      color: tag === "Rolodex" ? "#0077B5" : COLORS.accent,
                      border: `1px solid ${tag === "Rolodex" ? "#0077B533" : COLORS.accent + "33"}`,
                    }}>{tag}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 12, color: COLORS.textMuted }}>
                  <span>{list.contacts.length} contacts</span>
                  <span>{list.createdAt.toLocaleDateString()}</span>
                  <span>{Object.keys(list.emails || {}).length} personalized</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {list.status && list.status !== "draft" && (
                  <span style={{
                    padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500,
                    background: list.status === "importing" ? "#f59e0b18" : list.status === "sent" ? COLORS.accentBg : list.status === "queued" ? COLORS.warnBg : list.status === "error" ? "#ef444418" : COLORS.blueBg,
                    color: list.status === "importing" ? "#f59e0b" : list.status === "sent" ? COLORS.accent : list.status === "queued" ? COLORS.warn : list.status === "error" ? "#ef4444" : COLORS.blue,
                    border: `1px solid ${list.status === "importing" ? "#f59e0b33" : list.status === "sent" ? COLORS.accent + "33" : list.status === "queued" ? COLORS.warn + "33" : list.status === "error" ? "#ef444433" : COLORS.blue + "33"}`,
                    ...(list.status === "importing" ? { animation: "pulse 1.5s ease-in-out infinite" } : {}),
                  }}>
                    {list.status === "importing" ? "Importing…" : list.status}
                  </span>
                )}
                <span style={{ color: COLORS.textDim, fontSize: 18 }}>→</span>
              </div>
            </div>
          ))}
        </div>
        </>)}
      </div>
    );
  }

  // Table view for selected list
  return (
    <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
      {/* List Header */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => {
              setSelectedList(null);
              setJobLeadsDetail(null);
              if (typeof onClearLeadlistFocus === "function") onClearLeadlistFocus();
            }}
            style={{
              padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`,
              borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            ← Back
          </button>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{activeList.name}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              {displayContacts.length} contacts · Created {activeList.createdAt.toLocaleDateString()}
              {jobMode && jobLeadsDetail?.instantly_sync_status != null && (
                <span> · Instantly sync: {jobLeadsDetail.instantly_sync_status}</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {activeList.emailCampaign && (
            <span style={{ fontSize: 12, color: COLORS.accent, fontFamily: FONT }}>
              📧 {activeList.emailCampaign}
            </span>
          )}
          {activeList.linkedinCampaign && (
            <span style={{ fontSize: 12, color: COLORS.blue, fontFamily: FONT, marginLeft: 8 }}>
              💼 {activeList.linkedinCampaign}
            </span>
          )}
          {activeList.status && activeList.status !== "draft" && (
            <span style={{
              padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, marginLeft: 8,
              background: activeList.status === "sent" ? COLORS.accentBg : activeList.status === "queued" ? COLORS.warnBg : COLORS.blueBg,
              color: activeList.status === "sent" ? COLORS.accent : activeList.status === "queued" ? COLORS.warn : COLORS.blue,
            }}>
              {activeList.status}
            </span>
          )}
          <button
            onClick={() => {
              const headers = COLUMNS.map(c => c.label).join(",");
              const rows = displayContacts.map(contact =>
                COLUMNS.map(col => `"${(getCellValue(contact, col, activeList) || "").replace(/"/g, '""')}"`).join(",")
              );
              const csv = [headers, ...rows].join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${activeList.name.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              padding: "8px 16px", marginLeft: 8,
              background: "transparent", border: `1px solid ${COLORS.border}`,
              borderRadius: 8, color: COLORS.text, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.text; }}
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      {jobLeadsDetailLoading && focusJobId && selectedList && String(selectedList) === String(focusListId) && (
        <div style={{ padding: "10px 24px", fontSize: 12, color: COLORS.textMuted, fontFamily: FONT_BODY, borderBottom: `1px solid ${COLORS.border}` }}>
          Loading enrichment run details…
        </div>
      )}

      {/* Table */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <div style={{ minWidth: COLUMNS.reduce((sum, c) => sum + getColWidth(c), 0) + 100 }}>
          {/* Table Header */}
          <div style={{
            display: "flex", position: "sticky", top: 0, zIndex: 10,
            background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
            padding: "0 20px",
          }}>
            {COLUMNS.map(col => {
              const w = getColWidth(col);
              const isSorted = leadSortKey === col.key;
              return (
              <div key={col.key} style={{
                width: w, flexShrink: 0, padding: "10px 8px",
                fontFamily: FONT, fontSize: 10, color: isSorted ? COLORS.accent : COLORS.textDim,
                letterSpacing: "0.08em", fontWeight: 600,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                cursor: "pointer", userSelect: "none", position: "relative",
              }} onClick={() => handleSort(col.key)}>
                {col.label.toUpperCase()}{isSorted ? (leadSortDir === "asc" ? " ▲" : " ▼") : ""}
                <div onMouseDown={handleResizeStart(col.key, w)} onClick={e => e.stopPropagation()} style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 5, cursor: "col-resize", background: "transparent" }} onMouseEnter={e => e.currentTarget.style.background = COLORS.accent + "33"} onMouseLeave={e => e.currentTarget.style.background = "transparent"} />
              </div>
              );
            })}
            <div style={{ width: 80, flexShrink: 0, padding: "10px 8px", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600 }} />
          </div>

          {/* Table Rows */}
          {sortedContacts.map((contact, i) => {
            const isEditing = editingLeadId === contact.id;
            return (
            <div
              key={`${contact.id}-${i}`}
              style={{
                display: "flex", padding: "0 20px",
                borderBottom: i < sortedContacts.length - 1 ? `1px solid ${COLORS.border}` : "none",
                transition: "background 0.1s",
                background: isEditing ? COLORS.accent + "08" : undefined,
              }}
              onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = COLORS.surfaceHover; }}
              onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = "transparent"; }}
            >
              {COLUMNS.map(col => {
                const val = getCellValue(contact, col, activeList);
                const isEmail = col.key === "email";
                const isEmailStatus = col.key === "email_validation";
                const isPersonalized = col.key === "personalized";
                const editable = isEditing && EDITABLE_KEYS.has(col.key);
                const inputStyle = { width: "100%", padding: "4px 6px", background: COLORS.surface, border: `1px solid ${COLORS.accent}44`, borderRadius: 4, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12, boxSizing: "border-box", outline: "none" };

                if (editable && isPersonalized) {
                  return (
                    <div key={col.key} style={{ width: getColWidth(col), flexShrink: 0, padding: "6px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
                      <input value={editingLeadData.p_subject} onChange={e => setEditingLeadData(d => ({ ...d, p_subject: e.target.value }))} placeholder="Subject" style={inputStyle} />
                      <textarea value={editingLeadData.p_body} onChange={e => setEditingLeadData(d => ({ ...d, p_body: e.target.value }))} placeholder="Body" rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.4 }} />
                    </div>
                  );
                }

                if (editable) {
                  const fieldMap = { name: "first_name", title: "title", company: "company", email: "email", linkedin: "linkedin_url" };
                  const field = fieldMap[col.key];
                  const editVal = col.key === "name" ? `${editingLeadData.first_name || ""}${editingLeadData.last_name ? " " + editingLeadData.last_name : ""}` : (editingLeadData[field] || "");
                  return (
                    <div key={col.key} style={{ width: getColWidth(col), flexShrink: 0, padding: "6px 8px" }}>
                      {col.key === "name" ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <input value={editingLeadData.first_name} onChange={e => setEditingLeadData(d => ({ ...d, first_name: e.target.value }))} placeholder="First" style={{ ...inputStyle, width: "48%" }} />
                          <input value={editingLeadData.last_name} onChange={e => setEditingLeadData(d => ({ ...d, last_name: e.target.value }))} placeholder="Last" style={{ ...inputStyle, width: "52%" }} />
                        </div>
                      ) : (
                        <input value={editVal} onChange={e => setEditingLeadData(d => ({ ...d, [field]: e.target.value }))} style={inputStyle} />
                      )}
                    </div>
                  );
                }

                return (
                  <div key={col.key} style={{
                    width: getColWidth(col), flexShrink: 0, padding: "10px 8px",
                    fontSize: 12.5,
                    overflow: isPersonalized ? "auto" : "hidden",
                    textOverflow: isPersonalized ? undefined : "ellipsis",
                    whiteSpace: isPersonalized ? "pre-wrap" : "nowrap",
                    maxHeight: isPersonalized ? 80 : undefined,
                    resize: isPersonalized ? "horizontal" : undefined,
                    color: isEmail ? COLORS.blue : isEmailStatus ? (val === "valid" ? COLORS.accent : val === "catch-all" ? COLORS.warn : COLORS.text) : COLORS.text,
                    fontFamily: isEmail ? FONT : FONT_BODY,
                  }}>
                    {col.key === "linkedin" && val && val !== "—"
                      ? <a href={val} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.blue, textDecoration: "underline" }} onClick={e => e.stopPropagation()}>{val}</a>
                      : isPersonalized
                        ? (() => { const pj = parsePersonalisationJsonFromRow(contact); const body = pj?.body?.trim() || ""; return body || "—"; })()
                        : val}
                  </div>
                );
              })}
              {/* Actions column */}
              <div style={{ width: 80, flexShrink: 0, padding: "6px 8px", display: "flex", alignItems: "center", gap: 4 }}>
                {isEditing ? (<>
                  <button disabled={editingSaving} onClick={saveEditingLead} style={{ padding: "4px 8px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 4, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: editingSaving ? "wait" : "pointer", opacity: editingSaving ? 0.5 : 1 }}>{editingSaving ? "..." : "Save"}</button>
                  <button onClick={cancelEditingLead} style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", color: COLORS.textDim }}>X</button>
                </>) : (
                  <button onClick={() => startEditingLead(contact)} style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", color: COLORS.textMuted }}>Edit</button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}








// ============================================================================
// LEADS MANAGEMENT — AI Ark two-phase pipeline
// Phase 1: Discover companies (AI Ark) → show with checkboxes
// Phase 2: Enrich selected → find people → verify emails → personalize → send
// ============================================================================
// ═══════════════════════════════════════════════════════════════════════════
// Enrichment v2 — Smart incremental enrichment with AI Ark
// ═══════════════════════════════════════════════════════════════════════════

function useEnrichmentV2RunningJobs() {
  const [running, setRunning] = useState([]);
  const hasRunningRef = useRef(false);
  useEffect(() => {
    let mounted = true;
    const check = () => api.enrichmentV2.jobs("running").then(j => {
      if (!mounted) return;
      const arr = Array.isArray(j) ? j : [];
      setRunning(arr);
      hasRunningRef.current = arr.length > 0;
    }).catch(() => {});
    check();
    // Poll at 5s when jobs are running, 60s when idle
    let iv;
    const schedule = () => {
      iv = setTimeout(() => {
        check().finally(() => { if (mounted) schedule(); });
      }, hasRunningRef.current ? 5000 : 60000);
    };
    schedule();
    return () => { mounted = false; clearTimeout(iv); };
  }, []);
  return running;
}

const MAX_ENRICHMENT_LEADS = 50000;
const DEFAULT_GET_LEADS_COST = 2;
const LEAD_SLIDER_SEGMENTS = 5;

// Map a numeric value to a continuous slider position (0-1000) interpolating between snaps
function clampLeadCount(value, maxValue) {
  const cap = Math.max(0, Math.min(MAX_ENRICHMENT_LEADS, Math.round(Number(maxValue) || 0)));
  if (cap <= 0) return 0;
  const parsed = Math.round(Number(value) || 0);
  if (parsed <= 0) return Math.min(100, cap);
  return Math.max(1, Math.min(cap, parsed));
}

// Map a slider position (0-1000) back to a value
function sliderPosToValue(pos) {
  const segSize = 1000 / (SLIDER_SNAPS.length - 1);
  const segIdx = Math.floor(pos / segSize);
  if (segIdx >= SLIDER_SNAPS.length - 1) return null; // ALL
  const snap = SLIDER_SNAPS[segIdx];
  const nextSnap = SLIDER_SNAPS[segIdx + 1];
  if (nextSnap === null) {
    // In the segment before ALL — just use the last numeric snap
    return snap;
  }
  const frac = (pos - segIdx * segSize) / segSize;
  const raw = snap + frac * (nextSnap - snap);
  return Math.max(1, Math.round(raw));
}

function formatCompactLeadCount(value) {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1).replace(/\.0$/, "")}K`;
  }
  return value.toLocaleString();
}

function buildLeadCountSnapPoints(maxValue) {
  const cap = Math.max(0, Math.min(MAX_ENRICHMENT_LEADS, Math.round(Number(maxValue) || 0)));
  if (cap <= 0) return [0];
  const minValue = Math.min(10, cap);
  if (cap <= minValue) return [cap];

  const points = [];
  for (let i = 0; i <= LEAD_SLIDER_SEGMENTS; i++) {
    const raw = minValue + ((cap - minValue) * i) / LEAD_SLIDER_SEGMENTS;
    const rounded = Math.max(1, Math.round(raw));
    if (!points.includes(rounded)) points.push(rounded);
  }
  if (points[0] !== minValue) points.unshift(minValue);
  if (points[points.length - 1] !== cap) points.push(cap);
  return points;
}

function LeadCountSlider({ value, onChange, maxValue, leadCreditCost }) {
  const snapPoints = buildLeadCountSnapPoints(maxValue);
  const clampedValue = clampLeadCount(value, maxValue);
  const [inputText, setInputText] = useState(String(clampedValue));

  useEffect(() => { setInputText(String(clampedValue)); }, [clampedValue]);

  const nearestSnapIdx = (() => {
    let best = 0;
    for (let i = 0; i < snapPoints.length; i++) {
      if (Math.abs(snapPoints[i] - clampedValue) < Math.abs(snapPoints[best] - clampedValue)) best = i;
    }
    return best;
  })();

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: COLORS.text }}>How many new leads?</div>
        <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textDim }}>Max {Math.max(0, maxValue || 0).toLocaleString()} from current credits</div>
      </div>
      <input type="range" min={0} max={Math.max(0, snapPoints.length - 1)} step={1}
        value={nearestSnapIdx}
        onChange={e => {
          const idx = Math.max(0, Math.min(snapPoints.length - 1, parseInt(e.target.value, 10) || 0));
          onChange(snapPoints[idx]);
        }}
        disabled={snapPoints.length <= 1}
        style={{ width: "100%", accentColor: COLORS.accent }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: COLORS.textDim, fontFamily: FONT, marginTop: 2 }}>
        {snapPoints.map((point, i) => <span key={i} style={{ color: i === nearestSnapIdx ? COLORS.accent : undefined, fontWeight: i === nearestSnapIdx ? 700 : 400 }}>{formatCompactLeadCount(point)}</span>)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
        <input value={inputText}
          onChange={e => setInputText(e.target.value)}
          onBlur={() => {
            const n = parseInt(inputText.trim(), 10);
            if (!isNaN(n)) { onChange(clampLeadCount(n, maxValue)); } else { setInputText(String(clampedValue)); }
          }}
          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
          disabled={maxValue <= 0}
          style={{ width: 80, padding: "6px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 14, textAlign: "center" }}
        />
        <span style={{ fontSize: 12, color: COLORS.textDim, fontFamily: FONT_BODY }}>
          new leads
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: COLORS.textDim, fontFamily: FONT_BODY }}>
        Only profiles not already in your database will be enriched ({leadCreditCost} credit{leadCreditCost === 1 ? "" : "s"} each). Scanning is free.
      </div>
    </div>
  );
}

function industryFromJobFilters(job) {
  try {
    const f = typeof job.filters_json === "string" ? JSON.parse(job.filters_json) : job.filters_json;
    return f?.industry || f?.companyIndustry || "";
  } catch { return ""; }
}

function mapLeadRowToPersonalizeContact(row) {
  const email = row.email || "";
  const domainFromEmail = email.includes("@") ? email.split("@")[1].trim() : "";
  return {
    id: row.id,
    name: row.name || [row.first_name, row.last_name].filter(Boolean).join(" ") || email || "Unknown",
    title: row.title || "",
    company: row.company || "",
    company_domain: row.company_domain || domainFromEmail || "",
    linkedinData: row.linkedinData || null,
  };
}

const ENRICHMENT_PREVIEW_MAX_CAP = 20;

function previewSampleSizeForEnrichment(job, contacts) {
  const len = contacts.length;
  if (len === 0) return 0;
  const ev = job?.emails_valid || 0;
  const capped = Math.min(len, ENRICHMENT_PREVIEW_MAX_CAP);
  if (ev >= 1) return Math.min(ev, capped);
  return Math.min(len, capped);
}

/** Parse personalisation_json from a lead row (object or JSON string). Returns { subject, body } or null. */
function parsePersonalisationJsonFromRow(row) {
  const pj = row?.personalisation_json;
  if (!pj) return null;
  const o = typeof pj === "string" ? (() => { try { return JSON.parse(pj); } catch { return null; } })() : pj;
  if (!o || typeof o !== "object") return null;
  const subject = typeof o.subject === "string" ? o.subject.trim() : (o.subject != null ? String(o.subject).trim() : "");
  const body = typeof o.body === "string" ? o.body.trim() : (o.body != null ? String(o.body).trim() : "");
  if (!subject && !body) return null;
  return { subject, body };
}

function leadRowHasPersonalisation(row) {
  return parsePersonalisationJsonFromRow(row) != null;
}

/** Cold-email / Instantly paths: AI Ark VALID only (`email_validation_status === 'valid'`). */
function leadRowOutreachEligible(row) {
  if (!row || !(String(row.email || "").trim())) return false;
  return row.email_validation_status === "valid";
}

/** Scope to leads produced by this enrichment job (`enrichment_job_profiles` enriched). */
function leadRowInEnrichmentJob(row, jobLeadIdSet) {
  if (!row || jobLeadIdSet == null) return false;
  return jobLeadIdSet.has(String(row.id));
}

function LeadsMgmtPreflightPanel({ savedPrompts, promptText, setPromptText, selectedPromptKey, setSelectedPromptKey, campaignId, setCampaignId, setCampaignLabel }) {
  const [instantlyCampaignList, setInstantlyCampaignList] = useState([]);
  const [instantlyCampaignError, setInstantlyCampaignError] = useState(null);
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);
  const [campaignLoading, setCampaignLoading] = useState(false);
  useEffect(() => {
    setInstantlyCampaignError(null);
    setCampaignLoading(true);
    const load = (attempt = 1) => {
      api.instantly.campaigns.list()
        .then((data) => {
          const rows = normalizeInstantlyCampaignList(data);
          setInstantlyCampaignList(rows);
          setCampaignLoading(false);
          if (rows.length === 0) {
            setInstantlyCampaignError("No campaigns found in your Instantly account. Create a campaign in Instantly first.");
          }
        })
        .catch((err) => {
          if (attempt < 3) {
            setTimeout(() => load(attempt + 1), 3000);
          } else {
            setInstantlyCampaignList([]);
            setCampaignLoading(false);
            const msg = err?.message || "";
            if (/401|403|rejected|Invalid.*key/i.test(msg)) {
              setInstantlyCampaignError("Instantly rejected your API key. Update it in Settings → Integrations.");
            } else if (/not connected|Add your API key/i.test(msg)) {
              setInstantlyCampaignError("Instantly not connected. Add your API key in Settings → Integrations.");
            } else {
              setInstantlyCampaignError("Could not reach Instantly API. Try again later.");
            }
          }
        });
    };
    load();
  }, [campaignRefreshKey]);

  useEffect(() => {
    if (!campaignId || instantlyCampaignList.length === 0) return;
    const row = instantlyCampaignList.find((x) => x.id === campaignId);
    if (row) setCampaignLabel?.(row.name);
  }, [campaignId, instantlyCampaignList, setCampaignLabel]);

  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const selectStyle = {
    width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a7a8e' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", cursor: "pointer",
  };

  const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);

  return (
    <div style={{ marginBottom: 28, padding: 22, background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderRadius: 12 }}>
      <h3 style={{ fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700, color: COLORS.text, margin: "0 0 4px" }}>Personalization & Instantly campaign</h3>
      <p style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textDim, marginBottom: 18, lineHeight: 1.5 }}>
        The active personalisation prompt is set in Platform Admin. Choose an Instantly.ai campaign; verified leads will be personalized and synced to the selected campaign.
      </p>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>PERSONALISATION PROMPT</label>
        <div style={{ ...inputStyle, display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "default" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" fill={COLORS.accent} /><path d="M8 12.5l2.5 2.5 5.5-5.5" stroke="#0a0a0f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span style={{ fontWeight: 600 }}>{savedPrompts?.[selectedPromptKey]?.label || "Default Prompt"}</span>
          <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: "auto" }}>Set in Platform Admin</span>
        </div>
        <div
          onClick={() => setPromptPreviewOpen(!promptPreviewOpen)}
          style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none", fontSize: 10, color: COLORS.textMuted, fontFamily: FONT, marginTop: 4 }}
        >
          <span style={{ fontSize: 8, transition: "transform 0.2s", transform: promptPreviewOpen ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>&#9654;</span>
          {promptPreviewOpen ? "Hide prompt preview" : "Show prompt preview"}
        </div>
        {promptPreviewOpen && (
          <div style={{ marginTop: 6, padding: "10px 14px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontFamily: FONT, fontSize: 11, color: COLORS.textDim, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}>
            {promptText || "(No prompt text)"}
          </div>
        )}
      </div>
      <div>
        <label style={labelStyle}>INSTANTLY.AI CAMPAIGN</label>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <select style={{ ...selectStyle, flex: 1 }}
          value={campaignId}
          onChange={(e) => {
            const v = e.target.value;
            setCampaignId(v);
            const row = instantlyCampaignList.find((x) => x.id === v);
            setCampaignLabel?.(row?.name || "");
          }}
        >
          <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select campaign...</option>
          {instantlyCampaignList.map((c) => (
            <option key={c.id} value={c.id} style={{ background: COLORS.surface, color: COLORS.text }}>{c.name}</option>
          ))}
        </select>
        <button onClick={() => setCampaignRefreshKey(k => k + 1)} disabled={campaignLoading} title="Refresh campaigns" style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "8px 10px", cursor: campaignLoading ? "not-allowed" : "pointer", color: COLORS.accent, fontSize: 14, lineHeight: 1, flexShrink: 0 }}>↻</button>
        </div>
        {instantlyCampaignError && (
          <div style={{ marginTop: 8, fontSize: 11, color: COLORS.danger, fontFamily: FONT_BODY }}>{instantlyCampaignError}</div>
        )}
      </div>
    </div>
  );
}

/** When false, hides the ⟡ Personalize button and background-personalization banner on the enrichment job card (server-side job prompt still applies). */
const SHOW_ENRICHMENT_PERSONALIZE_UI = false;

function EnrichmentJobDashboard({ jobId, onBack, onAgentLog, initialPromptText, initialSelectedPromptKey, autoLinearFromPreflight, onViewLeadList, userRole }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [personalizeContacts, setPersonalizeContacts] = useState([]);
  const [loadingPersonalizeLeads, setLoadingPersonalizeLeads] = useState(false);
  const [personalizeLeadsError, setPersonalizeLeadsError] = useState(null);
  const [promptText, setPromptText] = useState(() =>
    initialPromptText != null && String(initialPromptText).trim() ? initialPromptText : FALLBACK_DEFAULT_PROMPT
  );
  const [selectedPromptKey, setSelectedPromptKey] = useState(() => initialSelectedPromptKey ?? "default");
  const [savedPrompts] = useState(SAVED_PROMPTS);
  const [previewEmails, setPreviewEmails] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApproveProcessing, setIsApproveProcessing] = useState(false);
  const [personalizeBanner, setPersonalizeBanner] = useState(null);
  /** True when GET /api/leads has at least one row with saved subject/body (survives refresh). */
  const [hasListPersonalization, setHasListPersonalization] = useState(false);
  const [continuousPersonalizeActive, setContinuousPersonalizeActive] = useState(false);
  const [stoppingInFlight, setStoppingInFlight] = useState(false);
  const continuousConfigRef = useRef(null);
  const jobRef = useRef(null);
  const continuousPersonalizeActiveRef = useRef(false);
  /** Prevents overlapping setInterval ticks while runTick is still awaiting personalize APIs. */
  const isPersonalizingRef = useRef(false);
  /** When job hits terminal status, keep personalizing until one full pass finds no remaining unpersonalized leads. */
  const personalizeFinalPassRef = useRef(false);
  /** Avoid duplicate agent log when job status polls while terminal. */
  const loggedTerminalFinalPassRef = useRef(false);
  const autoLinearArmedRef = useRef(false);
  const promptTextRefForAuto = useRef("");
  const onAgentLogRef = useRef(onAgentLog);
  /** Lead IDs already personalized in this session (avoid duplicate agent log lines). */
  const personalizedIdsRef = useRef(new Set());
  /** UUIDs from enrichment_job_profiles for this job — scope personalize to this run only. */
  const [jobLeadIdSet, setJobLeadIdSet] = useState(null);
  /** Aggregate counts on the list (any source), vs job row counters. */
  const [listEmailStats, setListEmailStats] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const activityLogAfterRef = useRef(0);
  const activityLogEndRef = useRef(null);
  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    let stopped = false;
    const poll = () => {
      if (!mounted || stopped) return;
      api.enrichmentV2.syncLog(jobId, activityLogAfterRef.current).then(data => {
        if (!mounted) return;
        const logs = Array.isArray(data?.logs) ? data.logs : [];
        if (logs.length > 0) {
          setActivityLog(prev => [...prev, ...logs]);
          activityLogAfterRef.current = Math.max(...logs.map(l => l.id || 0));
          setTimeout(() => activityLogEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        }
        if (TERMINAL.has(jobRef.current?.status)) stopped = true;
      }).catch(() => {});
    };
    setActivityLog([]); activityLogAfterRef.current = 0;
    poll();
    const iv = setInterval(poll, 3000);
    return () => { mounted = false; clearInterval(iv); };
  }, [jobId]);

  useEffect(() => {
    personalizedIdsRef.current = new Set();
    personalizeFinalPassRef.current = false;
    loggedTerminalFinalPassRef.current = false;
  }, [jobId]);
  useEffect(() => { onAgentLogRef.current = onAgentLog; }, [onAgentLog]);
  useEffect(() => { jobRef.current = job; }, [job]);
  useEffect(() => { continuousPersonalizeActiveRef.current = continuousPersonalizeActive; }, [continuousPersonalizeActive]);
  useEffect(() => { promptTextRefForAuto.current = promptText; }, [promptText]);
  useEffect(() => {
    autoLinearArmedRef.current = false;
  }, [jobId]);

  /** Leads Management preflight: server-side personalization handles this now.
   *  The continuous personalize frontend loop is kept as a manual fallback only.
   *  Auto-arming is disabled to prevent double ScrapingBee+OpenAI calls. */
  useEffect(() => {
    if (!autoLinearFromPreflight || !jobId) return;
    autoLinearArmedRef.current = true; // Mark armed so it doesn't re-trigger
  }, [autoLinearFromPreflight, jobId]);

  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    let iv = null;
    const load = () => {
      if (TERMINAL.has(jobRef.current?.status)) {
        if (iv) {
          clearInterval(iv);
          iv = null;
        }
        return;
      }
      api.enrichmentV2.jobLeadIds(jobId).then((ids) => {
        if (mounted) setJobLeadIdSet(new Set((ids || []).map(String)));
      }).catch(() => {
        if (mounted) setJobLeadIdSet(new Set());
      });
    };
    load();
    iv = setInterval(load, 12000);
    return () => { mounted = false; clearInterval(iv); };
  }, [jobId]);

  useEffect(() => {
    if (!job?.list_id) {
      setListEmailStats(null);
      return;
    }
    let mounted = true;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    let iv = null;
    const load = () => {
      if (TERMINAL.has(jobRef.current?.status)) {
        if (iv) {
          clearInterval(iv);
          iv = null;
        }
        return;
      }
      api.leads.stats(job.list_id).then((s) => {
        if (mounted) setListEmailStats(s);
      }).catch(() => {});
    };
    load();
    iv = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(iv); };
  }, [job?.list_id]);

  const sleep = ms => new Promise(r => setTimeout(r, ms));

  const previewSampleSize = useMemo(() => previewSampleSizeForEnrichment(job, personalizeContacts), [job, personalizeContacts]);

  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    const poll = () => api.enrichmentV2.status(jobId).then(j => { if (mounted) setJob(j); }).catch(e => { if (mounted) setError(e.message); });
    poll();
    const iv = setInterval(() => {
      const cur = jobRef.current;
      if (!cur || TERMINAL.has(cur.status)) { clearInterval(iv); return; }
      if (cur.status === "running" || cur.status === "pending") poll();
    }, 3000);
    return () => { mounted = false; clearInterval(iv); };
  }, [jobId]);

  /** Refresh leads while Personalize modal is open so counts stay aligned with enrichment */
  useEffect(() => {
    if (!showPersonalizeModal || !job?.list_id) return;
    let mounted = true;
    const refresh = async () => {
      try {
        const rows = await api.leads.list({ list_id: job.list_id });
        const arr = Array.isArray(rows) ? rows : [];
        const eligible = arr.filter((r) => leadRowOutreachEligible(r) && leadRowInEnrichmentJob(r, jobLeadIdSet));
        const contacts = eligible.map(mapLeadRowToPersonalizeContact);
        if (mounted) setPersonalizeContacts(contacts);
      } catch {}
    };
    refresh();
    const iv = setInterval(refresh, 3000);
    return () => { mounted = false; clearInterval(iv); };
  }, [showPersonalizeModal, job?.list_id, jobLeadIdSet]);

  /** Any lead on this list has saved personalization (for outreach button after refresh). */
  useEffect(() => {
    if (!job?.list_id) {
      setHasListPersonalization(false);
      return;
    }
    let cancelled = false;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "exhausted"]);
    const check = async () => {
      try {
        const rows = await api.leads.list({ list_id: job.list_id });
        const arr = Array.isArray(rows) ? rows : [];
        const ok = arr.some(leadRowHasPersonalisation);
        if (!cancelled) setHasListPersonalization(ok);
        // Stop polling once job is terminal and we've done one final check
        if (TERMINAL.has(jobRef.current?.status)) clearInterval(iv);
      } catch {
        if (!cancelled) setHasListPersonalization(false);
      }
    };
    check();
    const iv = setInterval(check, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [job?.list_id, job?.status, job?.profiles_enriched, personalizeBanner?.type, isApproveProcessing]);

  /**
   * Continuous personalize (secondary to server-side enrichment worker personalization).
   * Primary path: enrichment worker writes personalisation_json after VALID email (ScrapingBee + OpenAI).
   * This loop: fallback for leads the worker skipped or failed; also drives Agent Log entries when the browser path saves.
   * Rows with existing personalisation_json are skipped via leadRowHasPersonalisation()
   * so server- and client-written JSON are not double-processed.
   */
  useEffect(() => {
    if (!continuousPersonalizeActive || !job?.list_id || !continuousConfigRef.current) return;
    let cancelled = false;
    const runTick = async () => {
      if (cancelled) return;
      if (isPersonalizingRef.current) return;
      isPersonalizingRef.current = true;
      try {
        const conf = continuousConfigRef.current;
        if (!conf) return;
        try {
          const rows = await api.leads.list({ list_id: job.list_id });
          const arr = Array.isArray(rows) ? rows : [];
          for (const row of arr) {
            if (cancelled) return;
            if (jobLeadIdSet == null) return;
            if (!leadRowOutreachEligible(row)) continue;
            if (!leadRowInEnrichmentJob(row, jobLeadIdSet)) continue;
            if (personalizedIdsRef.current.has(String(row.id))) continue;
            if (leadRowHasPersonalisation(row)) continue;
            const contact = mapLeadRowToPersonalizeContact(row);
            let subject = `Quick question for ${contact.company}`;
            let body = `I came across ${contact.company} and was impressed by what you're building...`;
            try {
              const companyDomain = contact.company_domain || (contact.company_website && contact.company_website.replace(/^https?:\/\//, "").split("/")[0]) || "";
              const leadData = {
                firstName: contact.name.split(" ")[0],
                lastName: contact.name.split(" ").slice(1).join(" "),
                fullName: contact.name,
                company: contact.company,
                company_domain: companyDomain,
                company_website: companyDomain ? `https://${companyDomain}` : "",
                title: contact.title,
                industry: conf.industry,
                linkedinActivity: contact.linkedinData ? `${contact.linkedinData.connections} connections, ${contact.linkedinData.posts} recent posts` : "no LinkedIn data",
                your_name: conf.userName || "",
                your_company: conf.userCompany || "",
              };
              const res = await api.leadGeneration.personalize({
                prompt: conf.promptText,
                leadData
              });
              const parsed = subjectBodyFromPersonalizeApiResponse(res, contact);
              subject = parsed.subject;
              let rawBody = parsed.body;
              const un = (conf.userName || "").trim();
              if (un && !rawBody.includes(un)) rawBody = `${rawBody.trimEnd()}\n\nBest,\n${un}`;
              body = rawBody;
            } catch (err) {
              // fallback subject/body
            }
            await api.leads.update(contact.id, { personalisation_json: { subject, body } });
            personalizedIdsRef.current.add(String(contact.id));
            if (typeof onAgentLog === "function") {
              onAgentLog(`✓ Personalized & saved: ${contact.name} (${contact.email || contact.id})`, "data");
            }
            await sleep(300);
          }
          if (personalizeFinalPassRef.current && jobLeadIdSet != null) {
            let remaining = 0;
            for (const row of arr) {
              if (!leadRowOutreachEligible(row)) continue;
              if (!leadRowInEnrichmentJob(row, jobLeadIdSet)) continue;
              if (personalizedIdsRef.current.has(String(row.id))) continue;
              if (leadRowHasPersonalisation(row)) continue;
              remaining++;
            }
            if (remaining === 0) {
              personalizeFinalPassRef.current = false;
              setContinuousPersonalizeActive(false);
            }
          }
        } catch (e) {
          if (typeof console !== "undefined") console.error("[Enrichment continuous personalize]", e);
        }
      } finally {
        isPersonalizingRef.current = false;
      }
    };
    const iv = setInterval(runTick, 12000);
    runTick();
    return () => { cancelled = true; clearInterval(iv); };
  }, [continuousPersonalizeActive, job?.list_id, job?.status, onAgentLog, jobLeadIdSet]);

  useEffect(() => {
    if (!continuousPersonalizeActive) return;
    const s = job?.status;
    if (["completed", "failed", "cancelled", "exhausted"].includes(s)) {
      personalizeFinalPassRef.current = true;
      if (!loggedTerminalFinalPassRef.current && typeof onAgentLog === "function") {
        loggedTerminalFinalPassRef.current = true;
        onAgentLog("→ Job finished — finishing personalization for any remaining leads in this run.", "info");
      }
    }
  }, [job?.status, continuousPersonalizeActive, onAgentLog]);

  useEffect(() => {
    if (!continuousPersonalizeActive) {
      personalizeFinalPassRef.current = false;
      loggedTerminalFinalPassRef.current = false;
    }
  }, [continuousPersonalizeActive]);

  const handleStop = async () => {
    if (stoppingInFlight) return;
    setStoppingInFlight(true);
    setError(null);
    try {
      await api.enrichmentV2.stop(jobId);
      const j = await api.enrichmentV2.status(jobId).catch(() => null);
      if (j) setJob(j);
    } catch (e) {
      setError(e.message);
      setStoppingInFlight(false);
    }
  };

  useEffect(() => {
    if (job?.cancelled_at || ["completed", "failed", "cancelled", "exhausted"].includes(job?.status)) {
      setStoppingInFlight(false);
    }
  }, [job?.cancelled_at, job?.status]);

  // Safety: if stop was requested but job is still "running" after 15s, force-refresh status
  useEffect(() => {
    if (!stoppingInFlight || !jobId) return;
    const timeout = setTimeout(async () => {
      try {
        const j = await api.enrichmentV2.status(jobId);
        if (j) setJob(j);
        // If still running after 15s, the worker may be stuck — force UI reset
        if (j?.cancelled_at && j?.status === 'running') {
          console.warn('[EnrichmentDashboard] Job still running 15s after stop — forcing UI reset');
          setJob({ ...j, status: 'cancelled' });
        }
      } catch {}
      setStoppingInFlight(false);
    }, 15000);
    return () => clearTimeout(timeout);
  }, [stoppingInFlight, jobId]);

  const openPersonalize = async () => {
    setPersonalizeBanner(null);
    setPreviewEmails(null);
    setPersonalizeLeadsError(null);
    if (!job?.list_id) {
      setPersonalizeLeadsError("No list is linked to this job yet.");
      return;
    }
    setLoadingPersonalizeLeads(true);
    try {
      const ids = await api.enrichmentV2.jobLeadIds(jobId).catch(() => []);
      const scope = new Set((ids || []).map(String));
      const rows = await api.leads.list({ list_id: job.list_id });
      const arr = Array.isArray(rows) ? rows : [];
      const eligible = arr.filter((r) => leadRowOutreachEligible(r) && scope.has(String(r.id)));
      const contacts = eligible.map(mapLeadRowToPersonalizeContact);
      if (contacts.length === 0) {
        setPersonalizeLeadsError("No verified (VALID) emails for leads from this enrichment run yet. Wait for new profiles to enrich, or check job counters.");
        return;
      }
      setPersonalizeContacts(contacts);
      setShowPersonalizeModal(true);
    } catch (e) {
      setPersonalizeLeadsError(e.message || "Failed to load leads");
    } finally {
      setLoadingPersonalizeLeads(false);
    }
  };

  const generatePreviewEnrichmentV2 = async () => {
    if (!job) return;
    setIsPreviewLoading(true);
    const industry = industryFromJobFilters(job);
    const take = Math.max(0, previewSampleSize);
    const previewContacts = personalizeContacts.slice(0, take);
    const previews = {};
    const promptTemplate = promptText;
    let userName = "";
    let userCompany = "";
    try {
      const me = await api.me();
      userName = me?.user?.name || "";
      userCompany = me?.user?.company || "";
    } catch {}
    const appendSignOffEv2 = (body) => {
      const b = (body || "").trimEnd();
      const un = userName.trim();
      if (!un || b.includes(un)) return b;
      return `${b}\n\nBest,\n${un}`;
    };
    try {
      for (const contact of previewContacts) {
        try {
          const companyDomain = contact.company_domain || (contact.company_website && contact.company_website.replace(/^https?:\/\//, "").split("/")[0]) || "";
          const leadData = {
            firstName: contact.name.split(" ")[0],
            lastName: contact.name.split(" ").slice(1).join(" "),
            fullName: contact.name,
            company: contact.company,
            company_domain: companyDomain,
            company_website: companyDomain ? `https://${companyDomain}` : "",
            title: contact.title,
            industry,
            linkedinActivity: contact.linkedinData ? `${contact.linkedinData.connections} connections, ${contact.linkedinData.posts} recent posts` : "no LinkedIn data",
            your_name: userName,
            your_company: userCompany,
          };
          const res = await api.leadGeneration.personalize({
            prompt: promptTemplate,
            leadData
          });
          const { subject, body: rawBody } = subjectBodyFromPersonalizeApiResponse(res, contact);
          const body = appendSignOffEv2(rawBody);
          previews[contact.id] = { subject, body };
        } catch (err) {
          previews[contact.id] = {
            subject: `Quick question for ${contact.company}`,
            body: `I came across ${contact.company} and was impressed by what you're building...`,
          };
        }
        await sleep(500);
      }
    } catch (err) {
      for (const contact of previewContacts) {
        previews[contact.id] = {
          subject: `Quick question for ${contact.company}`,
          body: `I came across ${contact.company} and was impressed by what you're building...`,
        };
      }
    }
    setPreviewEmails(previews);
    setIsPreviewLoading(false);
  };

  const approveEnrichmentV2 = async () => {
    if (!job) return;
    setShowPersonalizeModal(false);
    setPreviewEmails(null);
    setIsApproveProcessing(true);
    setPersonalizeBanner(null);
    const contacts = personalizeContacts;
    const industry = industryFromJobFilters(job);
    const promptTemplate = promptText;
    let userName = "";
    let userCompany = "";
    try {
      const me = await api.me();
      userName = me?.user?.name || "";
      userCompany = me?.user?.company || "";
    } catch {}
    continuousConfigRef.current = { promptText, industry, userName, userCompany };
    const appendSignOffApprove = (body) => {
      const b = (body || "").trimEnd();
      const un = userName.trim();
      if (!un || b.includes(un)) return b;
      return `${b}\n\nBest,\n${un}`;
    };
    try {
      for (const contact of contacts) {
        let subject = `Quick question for ${contact.company}`;
        let body = `I came across ${contact.company} and was impressed by what you're building...`;
        try {
          const companyDomain = contact.company_domain || (contact.company_website && contact.company_website.replace(/^https?:\/\//, "").split("/")[0]) || "";
          const leadData = {
            firstName: contact.name.split(" ")[0],
            lastName: contact.name.split(" ").slice(1).join(" "),
            fullName: contact.name,
            company: contact.company,
            company_domain: companyDomain,
            company_website: companyDomain ? `https://${companyDomain}` : "",
            title: contact.title,
            industry,
            linkedinActivity: contact.linkedinData ? `${contact.linkedinData.connections} connections, ${contact.linkedinData.posts} recent posts` : "no LinkedIn data",
            your_name: userName,
            your_company: userCompany,
          };
          const res = await api.leadGeneration.personalize({
            prompt: promptTemplate,
            leadData
          });
          const parsed = subjectBodyFromPersonalizeApiResponse(res, contact);
          subject = parsed.subject;
          body = appendSignOffApprove(parsed.body);
        } catch (err) {
          // keep template fallback
        }
        await api.leads.update(contact.id, { personalisation_json: { subject, body } });
        personalizedIdsRef.current.add(String(contact.id));
        await sleep(300);
      }
      setPersonalizeBanner({ type: "ok", text: `Personalized and saved ${contacts.length} verified lead(s). Background updates run for new verified leads until the job ends.` });
      setContinuousPersonalizeActive(true);
    } catch (err) {
      setPersonalizeBanner({ type: "err", text: err.message || "Failed to save personalization" });
    } finally {
      setIsApproveProcessing(false);
    }
  };

  if (error && !job) return <div style={{ padding: 24, color: COLORS.danger, fontFamily: FONT_BODY }}>{error}</div>;
  if (!job) return <div style={{ padding: 24, color: COLORS.textDim, fontFamily: FONT_BODY }}>Loading...</div>;

  const isStopping = Boolean(job.cancelled_at) && job.status === "running";
  const isRunning = (job.status === "running" || job.status === "pending") && !isStopping;
  const jobTerminal = ["completed", "failed", "cancelled", "exhausted"].includes(job.status);
  const progress = job.requested_size ? Math.min(100, Math.round((job.profiles_enriched / job.requested_size) * 100)) : null;
  const listName = (() => { try { const f = typeof job.filters_json === "string" ? JSON.parse(job.filters_json) : job.filters_json; return [f.title, f.industry || f.companyIndustry, f.location?.split(" ").pop()].filter(Boolean).join(" · ") || "Enrichment"; } catch { return "Enrichment"; } })();

  const statusColor = isStopping
    ? COLORS.warn
    : ({ running: COLORS.green, pending: COLORS.blue, completed: COLORS.accent, exhausted: COLORS.warn, cancelled: COLORS.textDim, failed: COLORS.danger }[job.status] || COLORS.textDim);
  const statusLabel = isStopping ? "stopping" : job.status;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 18, padding: 4 }}>←</button>
        <div style={{ fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{listName}</div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor, display: "inline-block", animation: (job.status === "running" || job.status === "pending") ? "pulse 1.5s infinite" : "none" }} />
          <span style={{ fontFamily: FONT, fontSize: 12, color: statusColor, textTransform: "capitalize" }}>{statusLabel}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: COLORS.border, borderRadius: 6, height: 8, marginBottom: 16, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 6, background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.green})`, width: progress != null ? `${progress}%` : `${Math.min(95, (job.profiles_enriched / Math.max(1, job.total_available || 1)) * 100)}%`, transition: "width 0.5s" }} />
      </div>
      <div style={{ fontFamily: FONT, fontSize: 14, color: COLORS.text, marginBottom: 20, textAlign: "center" }}>
        {job.profiles_enriched}{job.requested_size ? ` / ${job.requested_size}` : ""} new leads
        {job.requested_size === null && <span style={{ color: COLORS.textDim }}> (ALL mode)</span>}
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Scanned", value: job.profiles_scanned, sub: "profiles" },
          { label: "Skipped", value: job.profiles_skipped, sub: "(in DB)" },
          { label: "New", value: job.profiles_enriched, sub: "enriched" },
        ].map(s => (
          <div key={s.label} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
            <div style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: COLORS.text }}>{s.value || 0}</div>
            <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textDim }}>{s.label}</div>
            <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Email quality + meta — job row = this run only; list stats = all leads on shared list */}
      <div style={{ fontSize: 12, color: COLORS.textMuted, fontFamily: FONT_BODY, marginBottom: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span>This run: <b style={{ color: COLORS.green }}>{job.emails_valid || 0}</b> valid · <b style={{ color: COLORS.warn }}>{job.emails_catchall || 0}</b> catch-all · <b style={{ color: COLORS.danger }}>{job.emails_invalid || 0}</b> invalid</span>
      </div>
      {/* list-wide email stats row removed — too noisy during enrichment */}
      {SHOW_ENRICHMENT_PERSONALIZE_UI && isRunning && (job.emails_valid || 0) >= 1 && job.list_id && (
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            onClick={openPersonalize}
            disabled={loadingPersonalizeLeads || isApproveProcessing}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "14px 16px", width: "100%",
              background: COLORS.surface, border: `1px solid ${COLORS.accent}44`, borderBottom: `2px solid ${COLORS.accent}`,
              borderRadius: 8, color: COLORS.accent, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor: loadingPersonalizeLeads || isApproveProcessing ? "wait" : "pointer",
              opacity: loadingPersonalizeLeads || isApproveProcessing ? 0.75 : 1,
            }}
          >
            <span style={{ fontSize: 14 }}>⟡</span>
            <span>{loadingPersonalizeLeads ? "Loading…" : "Personalize"}</span>
          </button>
          {personalizeLeadsError && (
            <div style={{ marginTop: 8, fontSize: 12, color: COLORS.danger, fontFamily: FONT_BODY }}>{personalizeLeadsError}</div>
          )}
        </div>
      )}
      {SHOW_ENRICHMENT_PERSONALIZE_UI && isApproveProcessing && (
        <div style={{ marginBottom: 12, padding: "10px 12px", background: COLORS.accentBg, borderRadius: 8, fontSize: 12, color: COLORS.accent, fontFamily: FONT_BODY }}>
          Generating and saving personalized emails…
        </div>
      )}
      {SHOW_ENRICHMENT_PERSONALIZE_UI && personalizeBanner && !isApproveProcessing && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, fontSize: 12, fontFamily: FONT_BODY, background: personalizeBanner.type === "ok" ? COLORS.green + "18" : COLORS.dangerBg, color: personalizeBanner.type === "ok" ? COLORS.green : COLORS.danger }}>
          {personalizeBanner.text}
        </div>
      )}
      {SHOW_ENRICHMENT_PERSONALIZE_UI && continuousPersonalizeActive && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, fontSize: 12, fontFamily: FONT_BODY, background: COLORS.blueBg, color: COLORS.blue, border: `1px solid ${COLORS.blue}33` }}>
          Background personalization is on: new validated leads get the same prompt until the job ends (one final pass after the job completes).
          <button type="button" onClick={() => { personalizeFinalPassRef.current = false; setContinuousPersonalizeActive(false); }} style={{ marginLeft: 10, padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.blue}44`, borderRadius: 6, color: COLORS.blue, fontFamily: FONT, fontSize: 11, cursor: "pointer" }}>Stop</button>
        </div>
      )}
      <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, marginBottom: 20, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span>Credits: {job.credits_used || 0}</span>
        <span>Page {job.current_page || 0}{job.total_pages ? ` / ${job.total_pages}` : ""}</span>
        {job.total_available && <span>Pool: {job.total_available.toLocaleString()}</span>}
        <span>Phase: {job.worker_phase || "head"}</span>
      </div>

      {/* Activity Log */}
      {activityLog.length > 0 && (
        <div style={{ marginBottom: 16, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10 }}>📋</span>
            <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>ACTIVITY LOG</span>
            <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT }}>{activityLog.length}</span>
          </div>
          <div style={{ maxHeight: 200, overflow: "auto", padding: "6px 10px" }}>
            {activityLog.map((log, i) => (
              <div key={log.id || i} style={{ fontSize: 11, fontFamily: FONT_BODY, color: log.level === 'error' ? COLORS.danger : log.level === 'data' ? COLORS.accent : log.level === 'system' ? COLORS.blue : COLORS.textMuted, padding: "2px 0", lineHeight: 1.5 }}>
                {log.message}
              </div>
            ))}
            <div ref={activityLogEndRef} />
          </div>
        </div>
      )}

      {/* Stop / View List */}
      {isStopping && (
        <div style={{ width: "100%", padding: "14px", marginBottom: 12, background: COLORS.warnBg, border: `1px solid ${COLORS.warn}44`, borderRadius: 8, color: COLORS.warn, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, textAlign: "center" }}>
          Stopping enrichment… finishing current batch of emails in queue. This may take a moment.
        </div>
      )}
      {isRunning && (
        <button type="button" onClick={handleStop} disabled={stoppingInFlight} style={{ width: "100%", padding: "14px", background: stoppingInFlight ? COLORS.surfaceHover : COLORS.dangerBg, border: `1px solid ${stoppingInFlight ? COLORS.border : COLORS.danger + "44"}`, borderRadius: 8, color: stoppingInFlight ? COLORS.textDim : COLORS.danger, fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: stoppingInFlight ? "default" : "pointer", marginBottom: 12, opacity: stoppingInFlight ? 0.7 : 1 }}>
          {stoppingInFlight ? "Stopping..." : "■ STOP ENRICHMENT"}
        </button>
      )}
      {error && !stoppingInFlight && (
        <div style={{ width: "100%", padding: "10px 14px", marginBottom: 12, background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}44`, borderRadius: 8, color: COLORS.danger, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Failed to stop: {error}</span>
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", color: COLORS.danger, cursor: "pointer", fontWeight: 700, fontSize: 16, padding: "0 4px" }}>×</button>
        </div>
      )}
      {jobTerminal && job.list_id && (
        <button
          type="button"
          onClick={() => { if (typeof onViewLeadList === "function") onViewLeadList(job.list_id, jobId); }}
          style={{ width: "100%", padding: "12px", background: COLORS.accentBg, border: `1px solid ${COLORS.accent}33`, borderRadius: 8, color: COLORS.accent, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          ↗ View List
        </button>
      )}
      {job.error_message && (
        <div style={{ marginTop: 12, padding: "8px 12px", background: COLORS.dangerBg, borderRadius: 6, fontSize: 12, color: COLORS.danger, fontFamily: FONT_BODY }}>{job.error_message}</div>
      )}

      {SHOW_ENRICHMENT_PERSONALIZE_UI && showPersonalizeModal && (
        <PersonalizeModal
          contacts={personalizeContacts}
          promptText={promptText}
          setPromptText={setPromptText}
          selectedPromptKey={selectedPromptKey}
          setSelectedPromptKey={setSelectedPromptKey}
          savedPrompts={savedPrompts}
          previewEmails={previewEmails}
          isPreviewLoading={isPreviewLoading}
          onPreview={generatePreviewEnrichmentV2}
          onApprove={approveEnrichmentV2}
          onClose={() => { setShowPersonalizeModal(false); setPreviewEmails(null); }}
          totalContacts={personalizeContacts.length}
          previewLimit={previewSampleSize}
        />
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

function EnrichmentV2View({ onAgentLog, preflightReady, onJobStarted, initialPromptText, initialSelectedPromptKey, autoLinearFromPreflight, preflightInstantlyCampaignId, onViewLeadList, onPhaseChange, globalCredits, userRole }) {
  const [phase, setPhaseRaw] = useState("filters"); // "filters" | "dashboard" | "history"
  const setPhase = (p) => { setPhaseRaw(p); if (typeof onPhaseChange === "function") onPhaseChange(p); };
  const [activeJobId, setActiveJobId] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [title, setTitle] = useState("");
  const [titleInput, setTitleInput] = useState("");
  const [titles, setTitles] = useState([]); // tag input: multiple titles
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState([]);
  const [companySize, setCompanySize] = useState([]); // selected size ranges
  const [seniority, setSeniority] = useState([]);
  const [department, setDepartment] = useState([]);
  const [skill, setSkill] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [requestedSize, setRequestedSize] = useState(100);
  const [leadCreditCost, setLeadCreditCost] = useState(DEFAULT_GET_LEADS_COST);
  const [previewCount, setPreviewCount] = useState(null); // { totalElements, sample }
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimerRef = useRef(null);

  // On mount: check for running jobs
  useEffect(() => {
    api.enrichmentV2.jobs("running").then(jobs => {
      if (jobs?.length > 0) { setActiveJobId(jobs[0].id); setPhase("dashboard"); }
    }).catch(() => {});
    api.enrichmentV2.jobs().then(jobs => { if (Array.isArray(jobs)) setRecentJobs(jobs); }).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    api.billing.creditCosts()
      .then(costs => {
        if (!mounted || !Array.isArray(costs)) return;
        const leadDiscovery = Math.round(Number(costs.find(c => c?.action_key === "lead_discovery")?.credits));
        const emailVerification = Math.round(Number(costs.find(c => c?.action_key === "email_verification")?.credits));
        const parsedCost = (leadDiscovery > 0 ? leadDiscovery : 1) + (emailVerification > 0 ? emailVerification : 1);
        if (parsedCost > 0) setLeadCreditCost(parsedCost);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Pill option configs
  const SIZE_OPTIONS = [
    { label: "1-10", min: 1, max: 10 },
    { label: "11-50", min: 11, max: 50 },
    { label: "51-200", min: 51, max: 200 },
    { label: "201-500", min: 201, max: 500 },
    { label: "501-1K", min: 501, max: 1000 },
    { label: "1K-5K", min: 1001, max: 5000 },
    { label: "5K+", min: 5001, max: null },
  ];
  const SENIORITY_OPTIONS = [
    { label: "C-Suite", value: "c_suite" },
    { label: "VP", value: "vp" },
    { label: "Director", value: "director" },
    { label: "Head", value: "head" },
    { label: "Manager", value: "manager" },
    { label: "Senior", value: "senior" },
    { label: "Founder", value: "founder" },
    { label: "Partner", value: "partner" },
  ];
  const DEPARTMENT_OPTIONS = [
    { label: "Engineering", value: "engineering" },
    { label: "Sales", value: "sales" },
    { label: "Marketing", value: "marketing" },
    { label: "Finance", value: "finance" },
    { label: "Operations", value: "operations" },
    { label: "HR", value: "human_resources" },
    { label: "Product", value: "product_management" },
    { label: "Legal", value: "legal" },
    { label: "Business Dev", value: "business_development" },
    { label: "Customer Success", value: "customer_success_and_support" },
  ];
  const LOCATION_OPTIONS = [
    { label: "United States", value: "United States" },
    { label: "Canada", value: "Canada" },
    { label: "United Kingdom", value: "United Kingdom" },
    { label: "Germany", value: "Germany" },
    { label: "France", value: "France" },
    { label: "Netherlands", value: "Netherlands" },
    { label: "Spain", value: "Spain" },
    { label: "Italy", value: "Italy" },
    { label: "Sweden", value: "Sweden" },
    { label: "Switzerland", value: "Switzerland" },
    { label: "Ireland", value: "Ireland" },
    { label: "Australia", value: "Australia" },
    { label: "India", value: "India" },
    { label: "Japan", value: "Japan" },
    { label: "Singapore", value: "Singapore" },
    { label: "Israel", value: "Israel" },
    { label: "Brazil", value: "Brazil" },
    { label: "UAE", value: "United Arab Emirates" },
  ];
  const TITLE_PRESETS = [
    "CEO", "CTO", "CFO", "COO", "CMO",
    "Founder", "Co-Founder",
    "VP Engineering", "VP Sales", "VP Marketing",
    "Head of Engineering", "Head of Sales", "Head of Growth",
    "Director of Engineering", "Sales Director",
  ];

  // Debounced preview count — fires 800ms after any filter change
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    const filters = {};
    if (titles.length > 0) filters.title = titles.join(", ");
    if (industry.trim()) filters.companyIndustry = industry.trim().toLowerCase();
    if (location.length > 0) filters.location = location.join(", ");
    if (companySize.length > 0) {
      const allMins = companySize.map(s => s.min);
      const allMaxs = companySize.map(s => s.max).filter(Boolean);
      filters.minEmployees = Math.min(...allMins);
      if (allMaxs.length > 0 && !companySize.some(s => s.max === null)) filters.maxEmployees = Math.max(...allMaxs);
    }
    if (skill.trim()) filters.skill = skill.trim();
    if (companyName.trim()) filters.companyName = companyName.trim();

    if (!filters.title && !filters.companyIndustry && !filters.skill && !filters.companyName && !filters.location) {
      setPreviewCount(null);
      return;
    }
    previewTimerRef.current = setTimeout(() => {
      setPreviewLoading(true);
      api.enrichmentV2.preview(filters)
        .then(r => setPreviewCount(r))
        .catch(() => setPreviewCount(null))
        .finally(() => setPreviewLoading(false));
    }, 800);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [titles, industry, location, companySize, skill, companyName]);

  const toggleArray = (arr, setArr, val) => setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  const addTitle = (t) => { const trimmed = t.trim(); if (trimmed && !titles.includes(trimmed)) setTitles(prev => [...prev, trimmed]); setTitleInput(""); };
  const safeLeadCreditCost = Math.max(1, Math.round(Number(leadCreditCost) || DEFAULT_GET_LEADS_COST));
  const creditLimitedMax = globalCredits == null
    ? MAX_ENRICHMENT_LEADS
    : Math.max(0, Math.floor(Math.max(0, Number(globalCredits) || 0) / safeLeadCreditCost));
  const effectiveRequestedSizeMax = Math.min(MAX_ENRICHMENT_LEADS, creditLimitedMax);
  const formatRecentJobFilters = (job) => {
    try {
      const filters = typeof job?.filters_json === "string" ? JSON.parse(job.filters_json) : (job?.filters_json || {});
      return [
        filters.title,
        filters.companyIndustry || filters.industry,
        filters.location,
        filters.companyName,
        filters.skill,
      ].filter(Boolean);
    } catch {
      return [];
    }
  };
  const formatRecentJobAge = (createdAt) => {
    const delta = Date.now() - new Date(createdAt).getTime();
    if (!Number.isFinite(delta) || delta < 0) return "just now";
    if (delta < 3600000) return `${Math.max(1, Math.round(delta / 60000))}m ago`;
    if (delta < 86400000) return `${Math.round(delta / 3600000)}h ago`;
    return `${Math.round(delta / 86400000)}d ago`;
  };

  useEffect(() => {
    setRequestedSize(prev => clampLeadCount(prev, effectiveRequestedSizeMax));
  }, [effectiveRequestedSizeMax]);

  const handleStart = async () => {
    const filters = {};
    if (titles.length > 0) filters.title = titles.join(", ");
    if (industry.trim()) filters.companyIndustry = industry.trim().toLowerCase();
    if (location.length > 0) filters.location = location.join(", ");
    if (companySize.length > 0) {
      const allMins = companySize.map(s => s.min);
      const allMaxs = companySize.map(s => s.max).filter(Boolean);
      filters.minEmployees = Math.min(...allMins);
      if (allMaxs.length > 0 && !companySize.some(s => s.max === null)) filters.maxEmployees = Math.max(...allMaxs);
    }
    if (skill.trim()) filters.skill = skill.trim();
    if (companyName.trim()) filters.companyName = companyName.trim();

    if (!filters.title && !filters.companyIndustry && !filters.skill && !filters.companyName && !filters.location) {
      setError("At least one filter is required");
      return;
    }
    if (effectiveRequestedSizeMax <= 0) {
      const bal = Number(globalCredits) || 0;
      const needed = safeLeadCreditCost;
      setError(`You have ${bal.toLocaleString()} credit${bal === 1 ? "" : "s"} but each lead costs ${needed}. Add credits or reduce your lead count to continue.`);
      api.logClientError(`Insufficient credits: balance=${bal}, costPerLead=${needed}`, 'INSUFFICIENT_CREDITS', 'enrichment', { page: 'enrichment-v2', globalCredits: bal, costPerLead: needed });
      return;
    }

    setStarting(true); setError(null);
    try {
      const campaignArg = preflightInstantlyCampaignId != null && String(preflightInstantlyCampaignId).trim() !== ""
        ? String(preflightInstantlyCampaignId).trim()
        : undefined;
      const promptForJob =
        initialPromptText != null && String(initialPromptText).trim()
          ? initialPromptText
          : FALLBACK_DEFAULT_PROMPT;
      const result = await api.enrichmentV2.start(filters, clampLeadCount(requestedSize, effectiveRequestedSizeMax), campaignArg, promptForJob);
      if (result.error) {
        if (result.error === 'insufficient_credits' && result.balance != null && result.required != null) {
          setError(`Insufficient credits: you have ${Number(result.balance).toLocaleString()} but need at least ${Number(result.required).toLocaleString()} to enrich ${clampLeadCount(requestedSize, effectiveRequestedSizeMax).toLocaleString()} lead${clampLeadCount(requestedSize, effectiveRequestedSizeMax) === 1 ? "" : "s"}.`);
          api.logClientError(`Backend insufficient_credits: balance=${result.balance}, required=${result.required}`, 'INSUFFICIENT_CREDITS_BACKEND', 'enrichment', { page: 'enrichment-v2', balance: result.balance, required: result.required });
        } else {
          setError(result.error);
        }
        setStarting(false); return;
      }
      setActiveJobId(result.jobId);
      setPhase("dashboard");
      if (typeof onJobStarted === "function") onJobStarted(result.jobId);
    } catch (e) { setError(e.message); }
    setStarting(false);
  };

  if (phase === "dashboard" && activeJobId) {
    return (
      <EnrichmentJobDashboard
        jobId={activeJobId}
        onAgentLog={onAgentLog}
        initialPromptText={initialPromptText}
        initialSelectedPromptKey={initialSelectedPromptKey}
        autoLinearFromPreflight={Boolean(autoLinearFromPreflight)}
        onViewLeadList={onViewLeadList}
        userRole={userRole}
        onBack={() => {
          setPhase("filters"); setActiveJobId(null);
          api.enrichmentV2.jobs().then(j => { if (Array.isArray(j)) setRecentJobs(j); }).catch(() => {});
        }}
      />
    );
  }

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const tagStyle = (active) => ({ padding: "6px 12px", borderRadius: 6, border: `1px solid ${active ? COLORS.accent + "44" : COLORS.border}`, background: active ? COLORS.accentBg : "transparent", color: active ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 500, cursor: "pointer", transition: "all 0.12s" });
  const sectionStyle = { marginBottom: 20 };
  const hasFilters = titles.length > 0 || industry.trim() || skill.trim() || companyName.trim() || location.length > 0;

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <h2 style={{ fontFamily: FONT_BODY, fontSize: 22, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Find & Enrich Leads</h2>
      <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textDim, marginBottom: 28 }}>Search 500M+ profiles. Only new profiles cost credits — scanning is free.</p>

      {/* ── PERSON FILTERS ── */}
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 6 }}>PERSON</div>

      {/* Job Title — tag input with presets */}
      <div style={sectionStyle}>
        <label style={labelStyle}>JOB TITLE</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {TITLE_PRESETS.map(t => (
            <button key={t} onClick={() => titles.includes(t) ? setTitles(prev => prev.filter(x => x !== t)) : addTitle(t)} style={tagStyle(titles.includes(t))}>{t}</button>
          ))}
        </div>
        {titles.filter(t => !TITLE_PRESETS.includes(t)).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {titles.filter(t => !TITLE_PRESETS.includes(t)).map(t => (
              <span key={t} style={{ ...tagStyle(true), display: "inline-flex", alignItems: "center", gap: 6 }}>{t} <span onClick={() => setTitles(prev => prev.filter(x => x !== t))} style={{ cursor: "pointer", opacity: 0.6, fontSize: 14 }}>×</span></span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <input value={titleInput} onChange={e => setTitleInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { addTitle(titleInput); e.preventDefault(); } }} placeholder="Add custom title..." style={{ ...inputStyle, flex: 1 }} />
          <button onClick={() => addTitle(titleInput)} style={{ padding: "10px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT, fontSize: 12, cursor: "pointer" }}>Add</button>
        </div>
      </div>

      {/* Skills — text input */}
      <div style={sectionStyle}>
        <label style={labelStyle}>SKILLS</label>
        <input value={skill} onChange={e => setSkill(e.target.value)} onBlur={() => setSkill(v => v.trim())} placeholder="Python, Machine Learning, React..." style={inputStyle} />
      </div>

      {/* ── COMPANY FILTERS ── */}
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12, marginTop: 28, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 6 }}>COMPANY</div>

      {/* Industry — pills + text input */}
      <div style={sectionStyle}>
        <label style={labelStyle}>INDUSTRY</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {[
            { label: "SaaS", value: "saas" },
            { label: "Software", value: "software" },
            { label: "Fintech", value: "fintech" },
            { label: "AI", value: "artificial intelligence (ai)" },
            { label: "Cyber Security", value: "cyber security" },
            { label: "Cloud", value: "cloud computing" },
            { label: "E-Commerce", value: "e-commerce" },
            { label: "Health Care", value: "health care" },
            { label: "Biotech", value: "biotechnology" },
            { label: "Financial Services", value: "financial services" },
            { label: "Consulting", value: "consulting" },
            { label: "Marketing", value: "marketing" },
            { label: "Manufacturing", value: "manufacturing" },
            { label: "Education", value: "education" },
            { label: "Insurance", value: "insurance" },
            { label: "Real Estate", value: "real estate" },
            { label: "Logistics", value: "logistics" },
            { label: "Automotive", value: "automotive" },
            { label: "Telecom", value: "telecommunications" },
          ].map(ind => (
            <button key={ind.value} onClick={() => setIndustry(prev => prev === ind.value ? "" : ind.value)} style={tagStyle(industry === ind.value)}>{ind.label}</button>
          ))}
        </div>
        <input value={industry} onChange={e => setIndustry(e.target.value)} onBlur={() => setIndustry(v => v.trim())} placeholder="Or type a custom industry..." style={inputStyle} />
      </div>

      {/* Company Size — multi-select pills */}
      <div style={sectionStyle}>
        <label style={labelStyle}>COMPANY SIZE (EMPLOYEES)</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SIZE_OPTIONS.map(s => (
            <button key={s.label} onClick={() => {
              setCompanySize(prev => prev.some(p => p.label === s.label) ? prev.filter(p => p.label !== s.label) : [...prev, s]);
            }} style={tagStyle(companySize.some(p => p.label === s.label))}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* Location — multi-select pills */}
      <div style={sectionStyle}>
        <label style={labelStyle}>LOCATION</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {LOCATION_OPTIONS.map(l => (
            <button key={l.value} onClick={() => toggleArray(location, setLocation, l.value)} style={tagStyle(location.includes(l.value))}>{l.label}</button>
          ))}
        </div>
      </div>

      {/* Company Name — text input */}
      <div style={sectionStyle}>
        <label style={labelStyle}>COMPANY NAME (OPTIONAL)</label>
        <input value={companyName} onChange={e => setCompanyName(e.target.value)} onBlur={() => setCompanyName(v => v.trim())} placeholder="Search within a specific company..." style={inputStyle} />
      </div>

      {/* ── ENRICHMENT SIZE ── */}
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12, marginTop: 28, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 6 }}>ENRICHMENT</div>

      <LeadCountSlider
        value={requestedSize}
        onChange={setRequestedSize}
        maxValue={effectiveRequestedSizeMax}
        leadCreditCost={safeLeadCreditCost}
      />

      {/* Preview count */}
      {(previewCount || previewLoading) && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: COLORS.accent, minWidth: 70, textAlign: "right" }}>
            {previewLoading ? "..." : (previewCount?.totalElements || 0).toLocaleString()}
          </div>
          <div>
            <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text }}>matching profiles found</div>
            {previewCount?.sample?.[0] && !previewLoading && (
              <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                e.g. {previewCount.sample[0].name} · {previewCount.sample[0].title} @ {previewCount.sample[0].company}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div style={{ marginTop: 16, padding: "10px 14px", background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}33`, borderRadius: 8, fontSize: 12, color: COLORS.danger, fontFamily: FONT_BODY }}>{error}</div>}

      {/* Start button */}
      <button type="button" onClick={handleStart} disabled={starting || !hasFilters || preflightReady === false || effectiveRequestedSizeMax <= 0}
        style={{ width: "100%", marginTop: 24, padding: "16px", background: (starting || !hasFilters || preflightReady === false || effectiveRequestedSizeMax <= 0) ? COLORS.border : COLORS.accent, color: (starting || !hasFilters || preflightReady === false || effectiveRequestedSizeMax <= 0) ? COLORS.textDim : "#0a0a0f", border: "none", borderRadius: 10, fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: (starting || !hasFilters || preflightReady === false || effectiveRequestedSizeMax <= 0) ? "default" : "pointer", transition: "all 0.15s" }}>
        {starting ? "Starting..." : "Find & Enrich Leads"}
      </button>
      {!hasFilters && <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, color: COLORS.textDim, fontFamily: FONT_BODY }}>Select at least one filter to start</div>}
      {hasFilters && effectiveRequestedSizeMax <= 0 && (
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, color: COLORS.warn, fontFamily: FONT_BODY }}>
          {globalCredits != null
            ? `You have ${Number(globalCredits).toLocaleString()} credit${Number(globalCredits) === 1 ? "" : "s"} — each lead costs ${safeLeadCreditCost}. Add credits to continue.`
            : "Add credits to enrich new leads."}
        </div>
      )}
      {hasFilters && preflightReady === false && (
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, color: COLORS.warn, fontFamily: FONT_BODY }}>Complete template and Instantly campaign in Step 1 above.</div>
      )}

      {/* Recent jobs */}
      {recentJobs.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>Last 10 Jobs</div>
          {recentJobs.slice(0, 10).map(j => {
            const tileStatusColor = { completed: COLORS.green, running: COLORS.accent, failed: COLORS.danger, cancelled: COLORS.textDim, exhausted: COLORS.warn, pending: COLORS.blue }[j.status] || COLORS.textDim;
            const tileFilterItems = formatRecentJobFilters(j);
            const tileAgo = formatRecentJobAge(j.created_at);
            const tileStats = [
              { label: "Requested", value: j.requested_size == null ? "ALL" : j.requested_size },
              { label: "New", value: j.profiles_enriched || 0 },
              { label: "Scanned", value: j.profiles_scanned || 0 },
              { label: "Skipped", value: j.profiles_skipped || 0 },
              { label: "Failed", value: j.profiles_failed || 0 },
              { label: "Valid", value: j.emails_valid || 0 },
              { label: "Catch-all", value: j.emails_catchall || 0 },
              { label: "Invalid", value: j.emails_invalid || 0 },
            ];
            return (
              <div key={j.id}
                style={{ padding: "14px 16px", marginBottom: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: tileStatusColor, display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: COLORS.text, textTransform: "capitalize" }}>{j.status || "unknown"}</span>
                  </div>
                  <span style={{ marginLeft: "auto", fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>{tileAgo}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {(tileFilterItems.length > 0 ? tileFilterItems : ["No saved filters"]).map((item, idx) => (
                    <span key={`${j.id}-filter-${idx}`} style={{ padding: "4px 8px", borderRadius: 999, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontFamily: FONT_BODY, fontSize: 11 }}>
                      {item}
                    </span>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                  {tileStats.map((stat) => (
                    <div key={`${j.id}-${stat.label}`} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 8px", textAlign: "center" }}>
                      <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{stat.value}</div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: 10, color: COLORS.textDim }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
            const statusIcon = { completed: "✓", exhausted: "○", cancelled: "■", failed: "✗", running: "●", pending: "◌" }[j.status] || "?";
            const statusColor = { completed: COLORS.green, running: COLORS.accent, failed: COLORS.danger, cancelled: COLORS.textDim, exhausted: COLORS.warn, pending: COLORS.blue }[j.status] || COLORS.textDim;
            const filtersSummary = (() => { try { const f = typeof j.filters_json === "string" ? JSON.parse(j.filters_json) : j.filters_json; return [f.title, f.industry, f.location?.split(" ").pop()].filter(Boolean).join(" · ") || "—"; } catch { return "—"; } })();
            const ago = (() => { const d = Date.now() - new Date(j.created_at).getTime(); if (d < 3600000) return `${Math.round(d / 60000)}m ago`; if (d < 86400000) return `${Math.round(d / 3600000)}h ago`; return `${Math.round(d / 86400000)}d ago`; })();
            return (
              <button key={j.id} onClick={() => { setActiveJobId(j.id); setPhase("dashboard"); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted }}>
                <span style={{ color: statusColor, fontWeight: 700 }}>{statusIcon}</span>
                <span style={{ flex: 1, color: COLORS.text }}>{filtersSummary}</span>
                <span style={{ fontFamily: FONT, fontSize: 11 }}>{j.profiles_enriched}/{j.requested_size ?? "ALL"}</span>
                <span style={{ fontSize: 10, color: COLORS.textDim }}>{ago}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LeadsManagementView() {
  // Phase: "criteria" → "select" → "enriching" → "done"
  const [phase, setPhase] = useState("criteria");
  const [industry, setIndustry] = useState("");
  const [keywords, setKeywords] = useState("");
  const [employeeSizes, setEmployeeSizes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleInput, setRoleInput] = useState("");
  const [maxLeads, setMaxLeads] = useState(25);
  const [discovering, setDiscovering] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [companies, setCompanies] = useState([]); // { ...data, selected: true, status: "found"|"enriching"|"done"|"no_people", people: [] }
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  // Personalization
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [personalizePrompt, setPersonalizePrompt] = useState("Write a short, personalized cold email to {{firstName}} at {{company}}. Mention their role as {{title}}. Keep it under 100 words, conversational, and end with a clear CTA.");
  const [personalizedMap, setPersonalizedMap] = useState({});
  const [personalizing, setPersonalizing] = useState(false);
  const [personalizeProgress, setPersonalizeProgress] = useState({ done: 0, total: 0 });
  const [firstVerifiedTriggered, setFirstVerifiedTriggered] = useState(false);
  const [savedPrompt, setSavedPrompt] = useState(null); // Once approved, auto-apply to all future contacts
  const [firstVerifiedContact, setFirstVerifiedContact] = useState(null);
  const [applyingFirst, setApplyingFirst] = useState(false);
  const savedPromptRef = React.useRef(null); // Ref so SSE handler sees latest value
  const personalizedMapRef = React.useRef({});
  React.useEffect(() => { savedPromptRef.current = savedPrompt; }, [savedPrompt]);
  React.useEffect(() => { personalizedMapRef.current = personalizedMap; }, [personalizedMap]);
  // Campaign send
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Sorting
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const addLog = (msg) => setLogs(prev => [...prev, { msg, ts: Date.now() }]);
  const logEndRef = React.useRef(null);
  React.useEffect(() => { if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [logs]);

  React.useEffect(() => {
    api.instantly.campaigns.list().then(data => {
      const list = Array.isArray(data) ? data : data?.campaigns || data?.data || [];
      setCampaigns(list);
      if (list.length > 0) setSelectedCampaign(list[0].id || list[0].campaign_id || "");
    }).catch(() => {});
  }, []);

  const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "501-1,000", "1,001-5,000", "5,000+"];
  const REGION_OPTIONS = ["North America", "Europe", "Asia Pacific", "United Kingdom", "Germany", "France"];
  const ROLE_OPTIONS = [
    "CEO", "Founder", "Co-Founder", "Managing Director",
    "CTO", "CIO", "VP of Engineering", "Head of Technology",
    "CFO", "VP of Finance", "Head of Finance",
    "COO", "VP of Operations", "Head of Operations",
    "CMO", "VP of Marketing", "Head of Marketing", "Head of Growth",
    "VP of Sales", "Head of Sales", "Sales Director", "Business Development Director",
    "Head of Product", "VP of Product",
    "HR Director", "Head of People",
  ];

  const toggleSize = (s) => setEmployeeSizes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleRegion = (r) => setRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const addRole = () => { if (roleInput.trim() && !roles.includes(roleInput.trim())) { setRoles(prev => [...prev, roleInput.trim()]); setRoleInput(""); } };
  const removeRole = (r) => setRoles(prev => prev.filter(x => x !== r));
  const toggleCompany = (id) => setCompanies(prev => prev.map(c => c.id === id ? { ...c, selected: !c.selected } : c));
  const selectAll = () => setCompanies(prev => prev.map(c => ({ ...c, selected: true })));
  const deselectAll = () => setCompanies(prev => prev.map(c => ({ ...c, selected: false })));

  // Phase 1: Discover companies only (no enrichment)
  const handleDiscover = async () => {
    if (!industry.trim()) return;
    setDiscovering(true); setError(null); setCompanies([]); setContacts([]); setLogs([]); setPersonalizedMap({}); setSent(false); setFirstVerifiedTriggered(false);
    addLog("Searching for companies via AI Ark...");
    try {
      await api.leadGeneration.leadsManagementStream(
        { industry, keywords, employeeSizes, regions, roles, maxLeads, discoverOnly: true },
        ({ event, data }) => {
          if (event === "company") {
            setCompanies(prev => [...prev, { ...data, selected: true, status: "found", people: [] }]);
          } else if (event === "progress") {
            if (data.type === "companies_found") addLog(`Found ${data.count} companies`);
          } else if (event === "done") {
            setDiscovering(false); setPhase("select");
            addLog(`Discovery complete — ${data.totalCompanies || 0} companies found. Select companies and click Enrich.`);
          } else if (event === "error") {
            setError(data.message); setDiscovering(false);
            addLog(`Error: ${data.message}`);
          }
        }
      );
    } catch (e) {
      setError(e.message); setDiscovering(false); addLog(`Error: ${e.message}`);
    }
  };

  // Auto-personalize a single contact using the saved prompt (fire-and-forget)
  const autoPersonalize = (contact) => {
    const prompt = savedPromptRef.current;
    if (!prompt || !contact.email || !contact.verified) return;
    if (personalizedMapRef.current[contact.id]) return; // Already done
    api.leadGeneration.personalize({
      prompt, leadData: { firstName: contact.firstName, lastName: contact.lastName, company: contact.company, title: contact.title, company_domain: contact.company_domain },
    }).then(result => {
      setPersonalizedMap(prev => ({ ...prev, [contact.id]: result.message }));
      addLog(`✎ ${contact.name}: personalized`);
    }).catch(() => {
      setPersonalizedMap(prev => ({ ...prev, [contact.id]: "[Personalization failed]" }));
    });
  };

  // Phase 2: Enrich selected companies
  const handleEnrich = async () => {
    const selected = companies.filter(c => c.selected);
    if (!selected.length) return;
    setEnriching(true); setPhase("enriching"); setContacts([]); setFirstVerifiedTriggered(false); setFirstVerifiedContact(null);
    addLog(`Enriching ${selected.length} companies...`);
    try {
      await api.leadGeneration.leadsManagementStream(
        { enrichCompanies: selected.map(c => ({ id: c.id, name: c.name, domain: c.domain })), roles },
        ({ event, data }) => {
          if (event === "contact") {
            setContacts(prev => [...prev, data]);
            // Update company status and people list
            setCompanies(prev => prev.map(c => {
              if (c.name === data.company || c.id === data.companyId) {
                return { ...c, status: "done", people: [...(c.people || []), data] };
              }
              return c;
            }));
            addLog(`${data.verified ? "✓" : "○"} ${data.name} (${data.company}) — ${data.email || "no email"}${data.verified ? " ✓ verified" : ""}`);

            // Auto-personalize if prompt saved and contact has verified email
            if (data.verified && data.email && savedPromptRef.current) {
              autoPersonalize(data);
            }
            // Show personalization modal on first contact found (so user can start writing)
            if (!savedPromptRef.current) {
              setFirstVerifiedTriggered(was => {
                if (!was) {
                  setFirstVerifiedContact(data);
                  setShowPersonalizeModal(true);
                  return true;
                }
                return was;
              });
            }
          } else if (event === "progress") {
            if (data.type === "enriching_company") {
              setCompanies(prev => prev.map(c => c.name === data.company ? { ...c, status: "enriching" } : c));
              addLog(`Enriching ${data.company} (${data.index}/${data.total})...`);
            } else if (data.type === "people_found") {
              addLog(`${data.company}: ${data.count} ${data.count === 1 ? "person" : "people"} found`);
            } else if (data.type === "no_people") {
              setCompanies(prev => prev.map(c => c.name === data.company ? { ...c, status: "no_people" } : c));
              addLog(`${data.company}: no people found`);
            } else if (data.type === "email_found") {
              addLog(`${data.person}: email found (${data.source})`);
            }
          } else if (event === "done") {
            setEnriching(false); setPhase("done");
            addLog(`Enrichment complete — ${data.totalContacts || 0} contacts found`);
          } else if (event === "error") {
            setError(data.message); setEnriching(false); setPhase("done");
            addLog(`Error: ${data.message}`);
          }
        }
      );
    } catch (e) {
      setError(e.message); setEnriching(false); setPhase("done"); addLog(`Error: ${e.message}`);
    }
  };

  // Apply prompt: personalize the first contact, save prompt, close modal.
  // All future verified contacts will be auto-personalized.
  const handleApplyPrompt = async () => {
    setApplyingFirst(true);
    const target = firstVerifiedContact;
    try {
      if (target?.email && target?.verified) {
        const result = await api.leadGeneration.personalize({
          prompt: personalizePrompt,
          leadData: { firstName: target.firstName, lastName: target.lastName, company: target.company, title: target.title, company_domain: target.company_domain },
        });
        setPersonalizedMap(prev => ({ ...prev, [target.id]: result.message }));
        addLog(`✎ ${target.name}: personalized (first contact)`);
      }
    } catch (_) {
      if (target) setPersonalizedMap(prev => ({ ...prev, [target.id]: "[Personalization failed]" }));
    }
    // Save prompt — all future contacts auto-personalized via autoPersonalize()
    setSavedPrompt(personalizePrompt);
    setShowPersonalizeModal(false);
    setApplyingFirst(false);
    addLog("Prompt saved — all new verified contacts will be auto-personalized");
    // Catch up: personalize any verified contacts that arrived while modal was open
    setContacts(prev => {
      for (const c of prev) {
        if (c.verified && c.email && c.id !== target?.id && !personalizedMapRef.current[c.id]) {
          autoPersonalize(c);
        }
      }
      return prev;
    });
  };

  const handleSend = async () => {
    const eligible = contacts.filter(c => c.email && c.verified && personalizedMap[c.id]);
    if (!eligible.length || !selectedCampaign) return;
    setSending(true);
    try {
      await api.instantly.leads.bulk(eligible.map(c => ({ email: c.email, first_name: c.firstName, last_name: c.lastName, company_name: c.company, personalization: personalizedMap[c.id] || "" })), selectedCampaign);
      setSent(true); addLog(`Sent ${eligible.length} leads to campaign`);
    } catch (e) { addLog(`Send failed: ${e.message}`); }
    setSending(false);
  };

  const resetAll = () => { setPhase("criteria"); setDiscovering(false); setEnriching(false); setCompanies([]); setContacts([]); setLogs([]); setPersonalizedMap({}); setSent(false); setFirstVerifiedTriggered(false); setFirstVerifiedContact(null); setSavedPrompt(null); setError(null); };

  const inputStyle = { padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const tagStyle = (active) => ({ padding: "6px 12px", borderRadius: 6, border: `1px solid ${active ? COLORS.accent + "44" : COLORS.border}`, background: active ? COLORS.accentBg : "transparent", color: active ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 500, cursor: "pointer" });
  const thStyle = { padding: "10px 14px", textAlign: "left", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 };

  const selectedCount = companies.filter(c => c.selected).length;
  const verifiedCount = contacts.filter(c => c.verified).length;
  const personalizedCount = Object.keys(personalizedMap).length;
  const readyToSend = contacts.filter(c => c.email && c.verified && personalizedMap[c.id]).length;

  const statusIcon = (s) => s === "enriching" ? "⏳" : s === "done" ? "✓" : s === "no_people" ? "—" : "";

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(prev => prev === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const sortedCompanies = React.useMemo(() => {
    if (!sortKey) return companies;
    const getValue = (c) => {
      switch (sortKey) {
        case "name": return (c.name || "").toLowerCase();
        case "domain": return (c.domain || "").toLowerCase();
        case "industry": return (c.industry || "").toLowerCase();
        case "employees": return typeof c.employees === "number" ? c.employees : 0;
        case "location": return (c.location || "").toLowerCase();
        case "status": return c.status || "";
        case "people": return (c.people || []).length;
        default: return "";
      }
    };
    return [...companies].sort((a, b) => {
      const va = getValue(a), vb = getValue(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [companies, sortKey, sortDir]);
  const thSortStyle = (key) => ({ ...thStyle, cursor: "pointer", userSelect: "none", color: sortKey === key ? COLORS.accent : COLORS.textDim });

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>Leads Management</div>
        <div style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 24 }}>AI Ark discovery → Select companies → Enrich people → Verify emails → Personalize → Send</div>

        {/* Phase: Criteria */}
        {phase === "criteria" && !discovering && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 700, marginBottom: 24 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>INDUSTRY *</label>
              <input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="e.g. SaaS, Insurance, Healthcare" style={{ ...inputStyle, width: "100%" }} />
            </div>
            <div>
              <label style={labelStyle}>KEYWORDS</label>
              <input value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="e.g. AI, automation" style={{ ...inputStyle, width: "100%" }} />
            </div>
            <div>
              <label style={labelStyle}>MAX COMPANIES</label>
              <input type="number" value={maxLeads} onChange={e => setMaxLeads(e.target.value)} min={1} max={100} style={{ ...inputStyle, width: 100 }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>EMPLOYEE SIZE</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{SIZE_OPTIONS.map(s => <button key={s} onClick={() => toggleSize(s)} style={tagStyle(employeeSizes.includes(s))}>{s}</button>)}</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>REGIONS</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{REGION_OPTIONS.map(r => <button key={r} onClick={() => toggleRegion(r)} style={tagStyle(regions.includes(r))}>{r}</button>)}</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>TARGET ROLES</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>{ROLE_OPTIONS.map(r => <button key={r} onClick={() => roles.includes(r) ? removeRole(r) : setRoles(prev => [...prev, r])} style={tagStyle(roles.includes(r))}>{r}</button>)}</div>
              {roles.filter(r => !ROLE_OPTIONS.includes(r)).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>{roles.filter(r => !ROLE_OPTIONS.includes(r)).map(r => <span key={r} style={{ ...tagStyle(true), display: "flex", alignItems: "center", gap: 6 }}>{r} <span onClick={() => removeRole(r)} style={{ cursor: "pointer", opacity: 0.6 }}>×</span></span>)}</div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <input value={roleInput} onChange={e => setRoleInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addRole()} placeholder="Add custom role..." style={{ ...inputStyle, flex: 1 }} />
                <button onClick={addRole} style={{ padding: "10px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT, fontSize: 12, cursor: "pointer" }}>Add</button>
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <button onClick={handleDiscover} disabled={!industry.trim()} style={{ padding: "14px 32px", background: industry.trim() ? COLORS.accent : COLORS.border, color: industry.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: industry.trim() ? "pointer" : "default" }}>Find Companies</button>
            </div>
          </div>
        )}
        {discovering && <div style={{ padding: 20, color: COLORS.accent, fontFamily: FONT, fontSize: 13 }}>Searching for companies...</div>}

        {error && <div style={{ padding: 12, background: COLORS.danger + "15", border: `1px solid ${COLORS.danger}33`, borderRadius: 8, color: COLORS.danger, fontSize: 12, marginBottom: 16 }}>{error}</div>}

        {/* Phase: Select companies */}
        {(phase === "select" || phase === "enriching" || phase === "done") && companies.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>{companies.length} Companies</span>
              {phase === "select" && (
                <>
                  <button onClick={selectAll} style={{ padding: "4px 10px", borderRadius: 4, background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>Select All</button>
                  <button onClick={deselectAll} style={{ padding: "4px 10px", borderRadius: 4, background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>Deselect All</button>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>{selectedCount} selected</span>
                  <button onClick={handleEnrich} disabled={!selectedCount} style={{ padding: "10px 24px", background: selectedCount ? COLORS.accent : COLORS.border, color: selectedCount ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: selectedCount ? "pointer" : "default" }}>
                    Enrich {selectedCount} Companies
                  </button>
                </>
              )}
              {(phase === "enriching" || phase === "done") && (
                <>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>{contacts.length} contacts</span>
                  <span style={{ fontSize: 11, color: COLORS.accent }}>{verifiedCount} verified</span>
                  {personalizedCount > 0 && <span style={{ fontSize: 11, color: COLORS.blue }}>{personalizedCount} personalized</span>}
                  {enriching && <span style={{ fontSize: 11, color: COLORS.accent }}>● Enriching...</span>}
                  {savedPrompt && <span style={{ fontSize: 10, color: COLORS.blue, fontFamily: FONT }}>✎ Auto-personalizing</span>}
                  {phase === "done" && !sent && verifiedCount > 0 && personalizedCount === 0 && !savedPrompt && (
                    <button onClick={() => { setFirstVerifiedContact(contacts.find(c => c.verified && c.email) || null); setShowPersonalizeModal(true); }} style={{ padding: "8px 16px", background: COLORS.blue, color: "#fff", border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Personalize ({verifiedCount})</button>
                  )}
                  {readyToSend > 0 && !sent && (
                    <>
                      <select value={selectedCampaign} onChange={e => setSelectedCampaign(e.target.value)} style={{ ...inputStyle, fontSize: 11, padding: "6px 8px" }}>
                        {campaigns.length === 0 && <option value="">No campaigns</option>}
                        {campaigns.map(c => <option key={c.id || c.campaign_id} value={c.id || c.campaign_id}>{c.name || c.campaign_name || c.id}</option>)}
                      </select>
                      <button onClick={handleSend} disabled={sending} style={{ padding: "8px 16px", background: sending ? COLORS.border : COLORS.accent, color: sending ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: sending ? "default" : "pointer" }}>{sending ? "Sending..." : `Send ${readyToSend}`}</button>
                    </>
                  )}
                  {sent && <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>✓ Sent</span>}
                  <button onClick={resetAll} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>New Search</button>
                </>
              )}
            </div>

            {/* Companies table with expand for people */}
            <div style={{ borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: "hidden", marginBottom: 16 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_BODY, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
                    {phase === "select" && <th style={{ ...thStyle, width: 40 }}></th>}
                    <th style={thSortStyle("name")} onClick={() => handleSort("name")}>COMPANY{sortArrow("name")}</th>
                    <th style={thSortStyle("domain")} onClick={() => handleSort("domain")}>DOMAIN{sortArrow("domain")}</th>
                    <th style={thSortStyle("industry")} onClick={() => handleSort("industry")}>INDUSTRY{sortArrow("industry")}</th>
                    <th style={thSortStyle("employees")} onClick={() => handleSort("employees")}>EMPLOYEES{sortArrow("employees")}</th>
                    <th style={thSortStyle("location")} onClick={() => handleSort("location")}>LOCATION{sortArrow("location")}</th>
                    {phase !== "select" && <th style={thSortStyle("status")} onClick={() => handleSort("status")}>STATUS{sortArrow("status")}</th>}
                    {phase !== "select" && <th style={thSortStyle("people")} onClick={() => handleSort("people")}>PEOPLE{sortArrow("people")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedCompanies.map(c => (
                    <React.Fragment key={c.id}>
                      <tr onClick={() => { if (phase === "select") toggleCompany(c.id); }} style={{ borderBottom: `1px solid ${COLORS.border}`, background: c.status === "enriching" ? COLORS.accent + "06" : "transparent", cursor: phase === "select" ? "pointer" : "default" }}>
                        {phase === "select" && (
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <input type="checkbox" checked={c.selected} onChange={() => toggleCompany(c.id)} style={{ cursor: "pointer", accentColor: COLORS.accent }} />
                          </td>
                        )}
                        <td style={{ padding: "10px 14px", fontWeight: 500 }}>{c.name}</td>
                        <td style={{ padding: "10px 14px", fontFamily: FONT, fontSize: 11, color: COLORS.textMuted }}>{c.domain || "—"}</td>
                        <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{c.industry || "—"}</td>
                        <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{c.employees || "—"}</td>
                        <td style={{ padding: "10px 14px", color: COLORS.textMuted, fontSize: 11 }}>{c.location || "—"}</td>
                        {phase !== "select" && (
                          <td style={{ padding: "10px 14px", fontFamily: FONT, fontSize: 10, color: c.status === "enriching" ? COLORS.accent : c.status === "done" ? COLORS.accent : c.status === "no_people" ? COLORS.warn : COLORS.textDim }}>
                            {c.status === "enriching" ? "⏳ Enriching" : c.status === "done" ? `✓ ${(c.people || []).length} ${(c.people || []).length === 1 ? "person" : "people"} found` : c.status === "no_people" ? "— No people" : c.status === "found" ? "Pending" : c.status}
                          </td>
                        )}
                        {phase !== "select" && <td style={{ padding: "10px 14px", fontSize: 11 }}>{(c.people || []).length || "—"}</td>}
                      </tr>
                      {/* Inline people rows */}
                      {(c.people || []).map(p => (
                        <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.border}08`, background: COLORS.surface + "40" }}>
                          {phase === "select" && <td />}
                          <td style={{ padding: "6px 14px 6px 36px", fontSize: 11, color: COLORS.text }}>{p.name}</td>
                          <td style={{ padding: "6px 14px", fontFamily: FONT, fontSize: 10, color: p.email ? COLORS.text : COLORS.textDim }}>{p.email || "—"}</td>
                          <td style={{ padding: "6px 14px", fontSize: 11, color: COLORS.textMuted }}>{p.title}</td>
                          <td style={{ padding: "6px 14px" }}>
                            {p.verified ? <span style={{ color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600 }}>✓ VERIFIED</span>
                              : p.email ? <span style={{ color: COLORS.warn, fontFamily: FONT, fontSize: 9 }}>UNVERIFIED</span>
                              : <span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 9 }}>NO EMAIL</span>}
                          </td>
                          <td style={{ padding: "6px 14px" }}>
                            {personalizedMap[p.id] ? <span style={{ color: COLORS.blue, fontFamily: FONT, fontSize: 9, fontWeight: 600 }}>✓ READY</span> : null}
                          </td>
                          {phase !== "select" && <td />}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Agent Log */}
      <div style={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", background: COLORS.surface }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`, fontFamily: FONT, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: COLORS.textDim }}>AGENT LOG</div>
        <div style={{ flex: 1, overflow: "auto", padding: 12, fontSize: 11, fontFamily: FONT, color: COLORS.textMuted }}>
          {logs.map((l, i) => <div key={i} style={{ padding: "4px 0", borderBottom: `1px solid ${COLORS.border}11` }}><span style={{ color: COLORS.textDim, fontSize: 9, marginRight: 6 }}>{new Date(l.ts).toLocaleTimeString()}</span>{l.msg}</div>)}
          <div ref={logEndRef} />
        </div>
      </div>

      {/* Personalization modal — appears on first verified email */}
      {showPersonalizeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 28, width: 580, maxHeight: "85vh", overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>Personalization <span style={{ color: COLORS.accent }}>Studio</span></div>
                <div style={{ fontSize: 12, color: COLORS.textDim }}>Configure your prompt — it will be applied to all verified contacts as they are found</div>
              </div>
              <button onClick={() => setShowPersonalizeModal(false)} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textMuted, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>

            {/* First contact preview */}
            {firstVerifiedContact && (
              <div style={{ padding: 12, background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
                <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 }}>FIRST CONTACT FOUND</div>
                <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                  <div><span style={{ color: COLORS.textDim }}>Name:</span> <span style={{ fontWeight: 500 }}>{firstVerifiedContact.name}</span></div>
                  <div><span style={{ color: COLORS.textDim }}>Title:</span> {firstVerifiedContact.title}</div>
                  <div><span style={{ color: COLORS.textDim }}>Company:</span> {firstVerifiedContact.company}</div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.accent, marginTop: 4, fontFamily: FONT }}>{firstVerifiedContact.email}</div>
              </div>
            )}

            <label style={labelStyle}>PROMPT TEMPLATE</label>
            <textarea value={personalizePrompt} onChange={e => setPersonalizePrompt(e.target.value)} rows={6} style={{ ...inputStyle, width: "100%", resize: "vertical", marginBottom: 12, fontFamily: FONT_BODY }} />
            <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 8 }}>
              Variables: {"{{firstName}}"}, {"{{lastName}}"}, {"{{company}}"}, {"{{title}}"}, {"{{company_domain}}"}
            </div>
            <div style={{ fontSize: 10, color: COLORS.blue, marginBottom: 20, padding: "8px 12px", background: COLORS.blue + "10", borderRadius: 6, border: `1px solid ${COLORS.blue}22` }}>
              Enrichment is still running in the background. Once you apply, this prompt will automatically personalize every new verified contact as it arrives.
            </div>

            {applyingFirst && (
              <div style={{ fontSize: 12, color: COLORS.accent, marginBottom: 12 }}>Personalizing first contact...</div>
            )}

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setShowPersonalizeModal(false)} disabled={applyingFirst} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: applyingFirst ? "default" : "pointer" }}>Skip</button>
              <button onClick={handleApplyPrompt} disabled={applyingFirst} style={{ padding: "10px 24px", background: applyingFirst ? COLORS.border : COLORS.accent, color: applyingFirst ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: applyingFirst ? "default" : "pointer" }}>
                {applyingFirst ? "Applying..." : "Apply & Auto-Personalize All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Smooth cubic path through points (not straight segments). */
function orgWideSmoothLinePath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? i : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

/** Org-wide pipeline chart: all projects — curved lines + equal index spacing (today = last bucket, right edge). */
function OrgWidePipelineVisualization({ bundle, busy, windowKey, setWindowKey }) {
  const [mask, setMask] = useState({ outreach: true, responses: true, meetings: true, deals: true, revenue: true });
  /** HTML tooltip — native SVG &lt;title&gt; is unreliable; paths also steal pointer events without pointer-events:none */
  const [orgHoverTip, setOrgHoverTip] = useState(null);

  const ORG_WIDE_KEYS = [
    { key: "outreach", caption: "Outreach Sent", hoverShort: "Outreach", swatch: "#0D9488" },
    { key: "responses", caption: "Responses", hoverShort: "Responses", swatch: "#F97316" },
    { key: "meetings", caption: "Meetings Booked", hoverShort: "Meetings", swatch: "#E11D48" },
    { key: "deals", caption: "Deals Closed", hoverShort: "Deals won", swatch: "#0EA5E9" },
    { key: "revenue", caption: "Revenue (£K)", hoverShort: "Revenue", swatch: "#A855F7" },
  ];

  const extractSeries = (k) => {
    if (!bundle) return [];
    if (k === "revenue") return (bundle.revenue || []).map((v) => Number(v) / 1000);
    return bundle[k] || [];
  };

  const axisDates = bundle?.dates || [];
  const toggledKeys = ORG_WIDE_KEYS.filter((x) => mask[x.key]);
  const flat = toggledKeys.flatMap((x) => extractSeries(x.key));
  const ceiling = Math.max(...flat, 1);
  const ORG_PLOT_W = 900;
  const ORG_PLOT_H = 208;
  const padTop = 10;
  const xAtIndex = (i, n) => (n <= 1 ? ORG_PLOT_W : (i / (n - 1)) * ORG_PLOT_W);
  const yAtValue = (v) => ORG_PLOT_H - padTop - (ceiling > 0 ? (Number(v) / ceiling) * (ORG_PLOT_H - padTop - 8) : 0);

  const captionForTick = (raw) => {
    if (!raw) return "";
    const part = String(raw).slice(0, 10);
    const [y, mo, d] = part.split("-").map(Number);
    if (isNaN(y)) return "";
    const dt = new Date(y, mo - 1, d);
    const now = new Date();
    if (dt.toDateString() === now.toDateString()) return "Today";
    if (windowKey === "7D" || windowKey === "30D") return windowKey === "7D" ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getDay()] : `${mo}/${d}`;
    if (windowKey === "90D") return `${d} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][mo - 1]}`;
    return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][mo - 1];
  };

  /** One series block (label + value) for tooltip — bucket shown once in combined tooltip. */
  const orgWideSeriesBlock = (row, i) => {
    const label = row.hoverShort || row.caption;
    if (row.key === "revenue") {
      const gbp = Number((bundle?.revenue || [])[i] ?? 0);
      const line2 = `£${gbp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      return `${label}\n${line2}`;
    }
    const v = Math.round(Number((bundle?.[row.key] || [])[i] ?? 0));
    const line2 =
      row.key === "outreach"
        ? `${v} ${v === 1 ? "lead" : "leads"}`
        : row.key === "responses"
          ? `${v} ${v === 1 ? "reply" : "replies"}`
          : row.key === "meetings"
            ? `${v} ${v === 1 ? "meeting" : "meetings"}`
            : `${v} ${v === 1 ? "deal" : "deals"}`;
    return `${label}\n${line2}`;
  };

  /** All toggled series at bucket i — avoids overlapping SVG hit circles (same cx/cy when values match). */
  const orgWideCombinedTooltipForIndex = (i) => {
    const bucket = captionForTick(axisDates[i]);
    const blocks = toggledKeys.map((row) => orgWideSeriesBlock(row, i));
    return [bucket, ...blocks].join("\n\n");
  };

  const orgWideBucketStripBounds = (i, n) => {
    if (n <= 0) return { x0: 0, w: ORG_PLOT_W };
    const xi = xAtIndex(i, n);
    const x0 = i === 0 ? 0 : (xAtIndex(i - 1, n) + xi) / 2;
    const x1 = i === n - 1 ? ORG_PLOT_W : (xi + xAtIndex(i + 1, n)) / 2;
    return { x0, w: Math.max(0.01, x1 - x0) };
  };

  return (
    <section style={{ marginBottom: 24, padding: "22px 26px", background: COLORS.bg, border: `2px dashed ${COLORS.border}`, borderRadius: 12 }}>
      <header style={{ marginBottom: 18 }}>
        <h3 style={{ margin: 0, fontFamily: FONT, fontSize: 16, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>
          Organization pipeline — all projects
        </h3>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: COLORS.textMuted, maxWidth: 720, lineHeight: 1.45 }}>
          Totals across your organisation (every project). Independent of the project selector. Metrics follow the same definitions as CRM: outreach and responses from leads; meetings, wins, and revenue from pipeline timestamps.
        </p>
      </header>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 16, justifyContent: "space-between" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
          {ORG_WIDE_KEYS.map((row) => (
            <label key={row.key} style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={mask[row.key]}
                onChange={() => setMask((m) => ({ ...m, [row.key]: !m[row.key] }))}
                style={{ width: 16, height: 16, accentColor: row.swatch }}
              />
              <span style={{ width: 12, height: 12, borderRadius: 3, background: row.swatch, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontFamily: FONT_BODY, fontWeight: 600, color: COLORS.text }}>{row.caption}</span>
            </label>
          ))}
        </div>
        <div role="group" aria-label="Time window" style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
          {["7D", "30D", "90D", "12M"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setWindowKey(k)}
              style={{
                padding: "8px 14px",
                border: "none",
                borderRight: k !== "12M" ? `1px solid ${COLORS.border}` : undefined,
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                background: windowKey === k ? COLORS.text : COLORS.surface,
                color: windowKey === k ? COLORS.bg : COLORS.textDim,
              }}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {busy ? (
        <div style={{ padding: 40, textAlign: "center", fontSize: 13, color: COLORS.textMuted, fontFamily: FONT_BODY }}>Loading organization metrics…</div>
      ) : !bundle || axisDates.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: COLORS.textMuted }}>No data for this range.</div>
      ) : toggledKeys.length === 0 ? (
        <div style={{ padding: 28, textAlign: "center", fontSize: 13, color: COLORS.textMuted }}>Select at least one metric above.</div>
      ) : (
        <div style={{ paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "stretch", gap: 10, minHeight: ORG_PLOT_H + 36 }}>
            <div
              style={{
                width: 44,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingBottom: 2,
                paddingTop: 2,
              }}
            >
              {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 9,
                    color: COLORS.textDim,
                    fontFamily: FONT,
                    lineHeight: 1,
                    textAlign: "right",
                  }}
                >
                  {Math.round(ceiling * (1 - t)).toLocaleString()}
                </span>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <svg
                viewBox={`0 0 ${ORG_PLOT_W} ${ORG_PLOT_H}`}
                preserveAspectRatio="xMidYMid meet"
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: `${ORG_PLOT_W} / ${ORG_PLOT_H}`,
                  display: "block",
                  overflow: "visible",
                }}
                onMouseLeave={() => setOrgHoverTip(null)}
              >
                {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                  <line
                    key={`grid-${t}`}
                    x1={0}
                    y1={t * ORG_PLOT_H}
                    x2={ORG_PLOT_W}
                    y2={t * ORG_PLOT_H}
                    stroke={COLORS.border}
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    opacity={0.7}
                    pointerEvents="none"
                  />
                ))}
                {toggledKeys.map((row) => {
                  const arr = extractSeries(row.key);
                  const n = axisDates.length;
                  const pts = arr.map((raw, i) => ({
                    x: xAtIndex(i, n),
                    y: yAtValue(raw),
                  }));
                  const d = orgWideSmoothLinePath(pts);
                  return (
                    <path
                      key={row.key}
                      d={d}
                      fill="none"
                      stroke={row.swatch}
                      strokeWidth={2.25}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={0.92}
                      pointerEvents="none"
                    />
                  );
                })}
                {toggledKeys.flatMap((row) => {
                  const arr = extractSeries(row.key);
                  const nb = axisDates.length;
                  return arr.map((raw, i) => (
                    <circle
                      key={`${row.key}-${i}-dot`}
                      cx={xAtIndex(i, nb)}
                      cy={yAtValue(raw)}
                      r={3.2}
                      fill={COLORS.bg}
                      stroke={row.swatch}
                      strokeWidth={1.8}
                      pointerEvents="none"
                    />
                  ));
                })}
                {axisDates.map((_, i) => {
                  const nb = axisDates.length;
                  const { x0, w } = orgWideBucketStripBounds(i, nb);
                  return (
                    <rect
                      key={`org-strip-${i}`}
                      x={x0}
                      y={0}
                      width={w}
                      height={ORG_PLOT_H}
                      fill="#000000"
                      fillOpacity={0.001}
                      stroke="none"
                      pointerEvents="all"
                      style={{ cursor: "default" }}
                      onMouseEnter={(e) => setOrgHoverTip({ x: e.clientX, y: e.clientY, text: orgWideCombinedTooltipForIndex(i) })}
                      onMouseMove={(e) => setOrgHoverTip({ x: e.clientX, y: e.clientY, text: orgWideCombinedTooltipForIndex(i) })}
                    />
                  );
                })}
              </svg>
              {orgHoverTip && (
                <div
                  style={{
                    position: "fixed",
                    left: typeof window !== "undefined" ? Math.min(Math.max(8, orgHoverTip.x + 14), window.innerWidth - 308) : orgHoverTip.x + 14,
                    top: orgHoverTip.y + 12,
                    zIndex: 10050,
                    maxWidth: 300,
                    padding: "8px 10px",
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                    fontSize: 11,
                    fontFamily: FONT_BODY,
                    color: COLORS.text,
                    lineHeight: 1.45,
                    whiteSpace: "pre-line",
                    pointerEvents: "none",
                  }}
                >
                  {orgHoverTip.text}
                </div>
              )}
              <div style={{ position: "relative", height: 28, marginTop: 6 }}>
                {axisDates.map((dt, i) => {
                  const n = axisDates.length;
                  const pct = n <= 1 ? 100 : (i / (n - 1)) * 100;
                  const isLast = i === n - 1;
                  return (
                    <span
                      key={i}
                      style={{
                        position: "absolute",
                        ...(isLast
                          ? { right: 0, left: "auto", transform: "none", textAlign: "right" }
                          : { left: `${pct}%`, transform: "translateX(-50%)", textAlign: "center" }),
                        fontSize: 9,
                        color: COLORS.textDim,
                        fontFamily: FONT,
                        whiteSpace: "nowrap",
                        maxWidth: 72,
                        lineHeight: 1.2,
                      }}
                    >
                      {captionForTick(dt)}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`, fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
            Horizontal spacing is even by bucket; the right edge is today. Curves share one vertical scale (max{" "}
            {ceiling.toLocaleString(undefined, { maximumFractionDigits: 2 })}). Revenue is in £K.
          </div>
        </div>
      )}
    </section>
  );
}

/** Per-project pipeline chart (Outreach Sent / Responses / …) on Dashboard — hidden by default */
const SHOW_DASHBOARD_PROJECT_CHART = false;

/** auditProjectId: only used when SHOW_DASHBOARD_PROJECT_CHART is true (dormant per-project chart). Stat cards + org chart ignore it. */
function DashboardView({ setActivePage, auditProjectId }) {
  const [chartRange, setChartRange] = useState("30D");
  const [chartMetrics, setChartMetrics] = useState({ outreach: true, responses: true, meetings: true, deals: false, revenue: false });
  const [dbStats, setDbStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  /** Shared with org-wide chart: same fetch drives stat cards (pipeline totals) and graph */
  const [orgWideWindowKey, setOrgWideWindowKey] = useState("30D");
  const [orgWideBundle, setOrgWideBundle] = useState(null);
  const [orgWideBusy, setOrgWideBusy] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const stats = await api.stats.dashboard();
        setDbStats(stats);
      } catch (err) {
        console.error("Failed to load stats:", err);
        setDbStats(null);
      } finally {
        setLoadingStats(false);
      }
    }
    loadStats();
  }, []);

  useEffect(() => {
    let alive = true;
    setOrgWideBusy(true);
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const raw = await api.stats.chart({ range: orgWideWindowKey, today, orgWide: true });
        if (alive) setOrgWideBundle(raw);
      } catch (e) {
        console.error("Org-wide chart load failed:", e);
        if (alive) setOrgWideBundle(null);
      } finally {
        if (alive) setOrgWideBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [orgWideWindowKey]);

  useEffect(() => {
    if (!SHOW_DASHBOARD_PROJECT_CHART) {
      setLoadingChart(false);
      return;
    }
    async function loadChart() {
      setLoadingChart(true);
      try {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD for bucket alignment
        const data = await api.stats.chart({ range: chartRange, today, projectId: auditProjectId || undefined });
        setChartData(data);
      } catch (err) {
        console.error("Failed to load chart:", err);
        setChartData(null);
      } finally {
        setLoadingChart(false);
      }
    }
    loadChart();
  }, [chartRange, auditProjectId]);

  // Chart series from DB (outreach, responses, meetings, deals); revenue in £K (divide by 1000)
  const CHART_SERIES = {
    outreach: { label: "Outreach Sent", color: "#3B82F6", data: chartData?.outreach || [] },
    responses: { label: "Responses", color: "#8B5CF6", data: chartData?.responses || [] },
    meetings: { label: "Meetings Booked", color: "#C9A84C", data: chartData?.meetings || [] },
    deals: { label: "Deals Closed", color: "#22C55E", data: chartData?.deals || [] },
    revenue: { label: "Revenue (£K)", color: "#EC4899", data: (chartData?.revenue || []).map((v) => Number(v) / 1000) },
  };

  const RANGE_LABELS = { "7D": "7 Days", "30D": "30 Days", "90D": "90 Days", "12M": "12 Months" };

  // Inventory: org-wide all-time from dashboard. Pipeline cards: sums of org chart series (same window as graph below).
  const s = dbStats?.stats || {};
  const pip = sumChartSeries(orgWideBundle);
  const n = (v) => typeof v === "number" ? v.toLocaleString() : String(v ?? 0);
  const dash = "—";
  const outreachWindow = orgWideBusy ? null : pip.outreach;
  const responsesWindow = orgWideBusy ? null : pip.responses;
  const meetingsWindow = orgWideBusy ? null : pip.meetings;
  const STATS = [
    { label: "Total Leads", value: loadingStats ? dash : n(s.totalLeads), icon: "search", sub: loadingStats ? null : `${n(s.verifiedEmails)} verified` },
    { label: "Companies", value: loadingStats ? dash : n(s.totalCompanies), icon: "apartment", sub: null },
    { label: "Lead Lists", value: loadingStats ? dash : n(s.totalLists), icon: "list_alt", sub: null },
    { label: "Outreach Sent", value: outreachWindow === null ? dash : n(outreachWindow), icon: "send", sub: `in ${orgWideWindowKey}` },
    {
      label: "Responses",
      value: responsesWindow === null ? dash : n(responsesWindow),
      icon: "chat_bubble",
      sub:
        outreachWindow != null && outreachWindow > 0 && responsesWindow != null
          ? `${((responsesWindow / outreachWindow) * 100).toFixed(1)}% reply rate`
          : null,
    },
    { label: "Meetings Booked", value: meetingsWindow === null ? dash : n(meetingsWindow), icon: "calendar_month", sub: null },
  ];

  const recentActivity = Array.isArray(dbStats?.recentActivity) ? dbStats.recentActivity : [];
  const ACTIVITY = recentActivity.map(a => {
    const timeAgo = Math.round((Date.now() - new Date(a.time).getTime()) / (1000 * 60 * 60));
    return {
      action: a.action,
      detail: a.detail,
      time: timeAgo < 1 ? "Just now" : timeAgo < 24 ? `${timeAgo}h ago` : `${Math.round(timeAgo / 24)}d ago`,
      icon: a.action.includes('list') ? "list_alt" : a.action.includes('Company') ? "apartment" : "auto_awesome",
    };
  });

  const QUICK_ACTIONS = [
    { label: "Run Discovery", icon: "bolt", page: "leads_mgmt" },
    { label: "Import List", icon: "upload", page: "leadlists" },
    { label: "Start AI Audit", icon: "search", page: "audit" },
    { label: "Research Niche", icon: "gps_fixed", page: "niche_researcher" },
    { label: "View CRM", icon: "bar_chart", page: "crm" },
  ];

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          <span style={{ color: COLORS.accent }}>Dashboard</span>
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Your pipeline at a glance</p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 28 }}>
        {STATS.map((stat, i) => (
          <div key={i} style={{ padding: "18px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.04em" }}>{stat.label.toUpperCase()}</span>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: COLORS.accent }}>{stat.icon}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: FONT, color: COLORS.text, marginBottom: 4 }}>{stat.value}</div>
            {stat.sub && <div style={{ fontSize: 11, color: COLORS.accent, fontWeight: 500 }}>{stat.sub}</div>}
          </div>
        ))}
      </div>

      {/* Chart (per-project) — gated by SHOW_DASHBOARD_PROJECT_CHART */}
      {SHOW_DASHBOARD_PROJECT_CHART && (() => {
        const chartW = 820; const chartH = 200;
        const activeMetrics = Object.entries(chartMetrics).filter(([_, v]) => v).map(([k]) => k);
        const allValues = activeMetrics.flatMap(k => CHART_SERIES[k].data || []);
        const maxVal = Math.max(...allValues, 1);

        const dates = chartData?.dates || [];
        const startDate = chartData?.startDate;
        const endDate = chartData?.endDate;
        const today = new Date();

        const dateToX = (dateStr, sDate, eDate) => {
          if (!dateStr || !sDate || !eDate) return 0;
          const start = new Date(String(sDate).slice(0, 10)).getTime();
          const end = new Date(String(eDate).slice(0, 10)).getTime();
          const d = new Date(String(dateStr).slice(0, 10)).getTime();
          if (end <= start) return 1;
          return Math.max(0, Math.min(1, (d - start) / (end - start)));
        };

        const formatDateLabel = (dateStr, i) => {
          if (!dateStr) return '';
          const datePart = String(dateStr).slice(0, 10);
          const [y, m, d] = datePart.split('-').map(Number);
          if (isNaN(y) || isNaN(m) || isNaN(d)) return '';
          const dte = new Date(y, m - 1, d);
          const isToday = dte.getDate() === today.getDate() && dte.getMonth() === today.getMonth() && dte.getFullYear() === today.getFullYear();
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          if (isToday) return 'Today';
          if (chartRange === '7D' || chartRange === '30D') {
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const short = days[dte.getDay()];
            return chartRange === '7D' ? short : `${m}/${d}`;
          }
          if (chartRange === '90D') return `${d} ${monthNames[m - 1]}`;
          if (chartRange === '12M') {
            const isCurrentMonth = dte.getMonth() === today.getMonth() && dte.getFullYear() === today.getFullYear();
            return isCurrentMonth ? `${monthNames[m - 1]} (now)` : monthNames[m - 1];
          }
          return '';
        };

        return (
          <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
              {Object.entries(CHART_SERIES).map(([key, series]) => (
                <label key={key} onClick={() => setChartMetrics(prev => ({ ...prev, [key]: !prev[key] }))} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", padding: "4px 10px", borderRadius: 6, background: chartMetrics[key] ? series.color + "12" : "transparent", border: `1px solid ${chartMetrics[key] ? series.color + "44" : COLORS.border}`, transition: "all 0.15s" }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: chartMetrics[key] ? series.color : COLORS.border }} />
                  <span style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: chartMetrics[key] ? series.color : COLORS.textDim }}>{series.label}</span>
                </label>
              ))}
            </div>
            <div style={{ position: "relative", height: chartH + 30, width: "100%", minWidth: 400, overflow: "hidden" }}>
              {/* Timeline selector - top-right of chart area */}
              <div style={{ position: "absolute", top: 0, right: 0, zIndex: 2, display: "flex", gap: 2, background: COLORS.bg, borderRadius: 6, padding: 2, border: `1px solid ${COLORS.border}` }}>
                {["7D", "30D", "90D", "12M"].map(r => (
                  <button key={r} onClick={() => setChartRange(r)} style={{ padding: "4px 10px", borderRadius: 4, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: chartRange === r ? COLORS.accent : "transparent", color: chartRange === r ? COLORS.bg : COLORS.textDim }}>{r}</button>
                ))}
              </div>
              {/* Y-axis labels - full width */}
              {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                <div key={i} style={{ position: "absolute", left: 0, right: 0, top: (1 - pct) * chartH, display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, width: 40, flexShrink: 0, textAlign: "right", paddingRight: 8 }}>{Math.round(maxVal * pct).toLocaleString()}</span>
                  <div style={{ flex: 1, minWidth: 0, height: 1, background: COLORS.border, opacity: 0.5 }} />
                </div>
              ))}
              {/* SVG chart - stretches to fill horizontal space (left: 48 for y-axis, right: 0) */}
              <svg style={{ position: "absolute", left: 48, right: 0, top: 0, height: chartH, overflow: "visible" }} viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none">
                {!loadingChart && activeMetrics.map(key => {
                  const d = CHART_SERIES[key].data || [];
                  if (d.length === 0) return null;
                  const max = Math.max(...allValues, 1);
                  const useDatePos = startDate && endDate && dates.length === d.length;
                  const pathPoints = d.map((v, i) => {
                    const x = useDatePos ? dateToX(dates[i], startDate, endDate) * chartW : (d.length > 1 ? (i / (d.length - 1)) * chartW : chartW / 2);
                    const y = chartH - (v / max) * (chartH - 10);
                    return `${x},${y}`;
                  }).join(" L ");
                  return <path key={key} d={`M ${pathPoints}`} fill="none" stroke={CHART_SERIES[key].color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />;
                })}
                {!loadingChart && activeMetrics.map(key => {
                  const d = CHART_SERIES[key].data || [];
                  if (d.length === 0) return null;
                  const max = Math.max(...allValues, 1);
                  const useDatePos = startDate && endDate && dates.length === d.length;
                  const x = useDatePos ? dateToX(dates[d.length - 1], startDate, endDate) * chartW : (d.length > 1 ? chartW : chartW / 2);
                  const y = chartH - (d[d.length - 1] / max) * (chartH - 10);
                  return <circle key={key + "_dot"} cx={x} cy={y} r="4" fill={CHART_SERIES[key].color} vectorEffect="non-scaling-stroke" />;
                })}
              </svg>
              {/* X-axis labels - sampled to avoid overlap; last label pinned right when Today */}
              <div style={{ position: "absolute", left: 48, right: 0, bottom: 0, height: 30 }}>
                {(() => {
                  const n = dates.length;
                  const labelIndices = n <= 5 ? [...Array(n).keys()] : [...new Set([0, Math.floor(n / 4), Math.floor(n / 2), Math.floor((3 * n) / 4), n - 1])].sort((a, b) => a - b);
                  const useDatePos = startDate && endDate;
                  return labelIndices.map(i => {
                    const dateStr = dates[i];
                    const pct = useDatePos ? dateToX(dateStr, startDate, endDate) * 100 : (n > 1 ? (i / (n - 1)) * 100 : 50);
                    const isLast = i === n - 1;
                    const labelText = formatDateLabel(dateStr, i);
                    const pinRight = isLast;
                    return (
                      <span key={i} style={{
                        position: "absolute",
                        ...(pinRight ? { right: 0, left: "auto", transform: "none", textAlign: "right" } : { left: `${pct}%`, transform: "translateX(-50%)" }),
                        fontSize: 9, color: COLORS.textDim, fontFamily: FONT, whiteSpace: "nowrap"
                      }}>{labelText}</span>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      <OrgWidePipelineVisualization
        bundle={orgWideBundle}
        busy={orgWideBusy}
        windowKey={orgWideWindowKey}
        setWindowKey={setOrgWideWindowKey}
      />

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        {/* Recent Activity */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 11, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.04em" }}>RECENT ACTIVITY</span>
          </div>
          {ACTIVITY.map((a, i) => (
            <div key={i} style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 12, borderBottom: i < ACTIVITY.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, width: 28, textAlign: "center", color: COLORS.accent }}>{a.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{a.action}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{a.detail}</div>
              </div>
              <span style={{ fontSize: 10, color: COLORS.textDim, whiteSpace: "nowrap" }}>{a.time}</span>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 11, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.04em" }}>QUICK ACTIONS</span>
          </div>
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            {QUICK_ACTIONS.map((a, i) => (
              <button key={i} onClick={() => setActivePage(a.page)} style={{ padding: "12px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: COLORS.textMuted, transition: "all 0.15s", textAlign: "left" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent + "44"; e.currentTarget.style.color = COLORS.accent; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
              ><span className="material-symbols-outlined" style={{ fontSize: 16 }}>{a.icon}</span>{a.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CRMPipelineView({ projectId, onNavigate, onOpenConversation }) {
  const STAGES = ["New", "Contacted", "Replied", "Meeting Booked", "Proposal Sent", "Won", "Lost"];
  const STAGE_COLORS = { New: COLORS.blue, Contacted: "#8B5CF6", Replied: COLORS.accent, "Meeting Booked": "#F59E0B", "Proposal Sent": "#EC4899", Won: COLORS.green, Lost: COLORS.danger };

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [viewMode, setViewMode] = useState("kanban");
  const [note, setNote] = useState("");
  const [dealActivity, setDealActivity] = useState([]);
  const [totalDeals, setTotalDeals] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterMode, setFilterMode] = useState("campaign"); // "campaign" = campaign replies only, "all" = all leads
  const [pageSize, setPageSize] = useState(500);
  const [addLeadModalStage, setAddLeadModalStage] = useState(null);
  const [addLeadForm, setAddLeadForm] = useState({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" });
  const [addLeadSubmitting, setAddLeadSubmitting] = useState(false);
  const loadedCountRef = useRef(0);
  const hasMoreRef = useRef(false);
  const pipelineValue = useMemo(() =>
    deals.filter(d => d.stage !== "Lost").reduce((sum, d) => sum + (d.valueNum || 0), 0),
    [deals]
  );

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const fetchPipeline = useCallback(async (offset, append, campaignOverride) => {
    const useCampaign = campaignOverride === undefined ? filterMode === "campaign" : campaignOverride;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const data = await api.crm.pipeline({
        limit: pageSize,
        offset,
        campaignReplies: useCampaign,
      });
      const newDeals = data.deals || [];
      if (append) {
        loadedCountRef.current += newDeals.length;
        setDeals((prev) => [...prev, ...newDeals]);
      } else {
        loadedCountRef.current = newDeals.length;
        setDeals(newDeals);
      }
      setTotalDeals(data.totalDeals ?? 0);
      setHasMore(!!data.hasMore);
    } catch (err) {
      console.error("CRM pipeline load error:", err);
      if (!append) setDeals([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterMode, pageSize]);

  useEffect(() => {
    loadedCountRef.current = 0;
    fetchPipeline(0, false);
  }, [fetchPipeline]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMoreRef.current) return;
    fetchPipeline(loadedCountRef.current, true);
  }, [fetchPipeline, loading, loadingMore]);

  const onCrmListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (loading || loadingMore || !hasMoreRef.current) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) loadMore();
    },
    [loadMore, loading, loadingMore]
  );

  const updateDealStage = async (dealId, newStage) => {
    try {
      await api.leads.update(dealId, { stage: newStage });
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStage } : d));
      if (selectedDeal?.id === dealId) setSelectedDeal(prev => prev ? { ...prev, stage: newStage } : null);
    } catch (err) {
      console.error("Failed to update deal stage:", err);
    }
  };

  const saveNotes = async (dealId, notes) => {
    try {
      await api.leads.update(dealId, { crm_notes: notes });
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, crm_notes: notes } : d));
    } catch (err) {
      console.error("Failed to save notes:", err);
    }
  };

  useEffect(() => {
    if (selectedDeal) {
      setNote(selectedDeal.crm_notes || "");
      api.activity.list({ resource_type: "lead", resource_id: selectedDeal.id }).then(data => {
        setDealActivity(Array.isArray(data) ? data : []);
      }).catch(() => setDealActivity([]));
    } else {
      setDealActivity([]);
    }
  }, [selectedDeal?.id]);

  const handleNoteBlur = () => {
    if (selectedDeal && note !== (selectedDeal.crm_notes || "")) saveNotes(selectedDeal.id, note);
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "18px 28px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: 0 }}>CRM <span style={{ color: COLORS.accent }}>Pipeline</span></h2>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{loading ? "..." : <>Org-wide · {deals.length}{totalDeals > deals.length ? ` of ${totalDeals.toLocaleString()}` : ""} {filterMode === "campaign" ? "campaign replies" : "leads"} · £{Math.round(pipelineValue).toLocaleString()} pipeline value{totalDeals > deals.length ? " (partial)" : ""}</>}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: COLORS.bg, borderRadius: 8, padding: 3, border: `1px solid ${COLORS.border}` }}>
            <button onClick={() => setFilterMode("campaign")} style={{ padding: "6px 12px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: filterMode === "campaign" ? COLORS.accent : "transparent", color: filterMode === "campaign" ? COLORS.bg : COLORS.textMuted }}>Campaign Replies</button>
            <button onClick={() => setFilterMode("all")} style={{ padding: "6px 12px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: filterMode === "all" ? COLORS.accent : "transparent", color: filterMode === "all" ? COLORS.bg : COLORS.textMuted }}>All Leads</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600 }}>Page size</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
            >
              {[100, 250, 500, 1000].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 4, background: COLORS.bg, borderRadius: 8, padding: 3, border: `1px solid ${COLORS.border}` }}>
            <button onClick={() => setViewMode("kanban")} style={{ padding: "6px 14px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: viewMode === "kanban" ? COLORS.accent : "transparent", color: viewMode === "kanban" ? COLORS.bg : COLORS.textMuted }}>Kanban</button>
            <button onClick={() => setViewMode("table")} style={{ padding: "6px 14px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: viewMode === "table" ? COLORS.accent : "transparent", color: viewMode === "table" ? COLORS.bg : COLORS.textMuted }}>Table</button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div onScroll={viewMode === "table" ? onCrmListScroll : undefined} style={{ flex: 1, overflow: "auto", padding: viewMode === "kanban" ? "16px" : "0" }}>
          {viewMode === "kanban" ? (
            <div style={{ display: "flex", gap: 10, minWidth: "max-content", height: "100%" }}>
              {STAGES.map(stage => {
                const stageDeals = deals.filter(d => d.stage === stage);
                const col = STAGE_COLORS[stage];
                return (
                  <div key={stage} style={{ width: 220, flexShrink: 0, display: "flex", flexDirection: "column", maxHeight: "100%" }}>
                    <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexShrink: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: col }} />
                        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.text }}>{stage}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, background: COLORS.surface, padding: "2px 8px", borderRadius: 8 }}>{stageDeals.length}</span>
                        <button onClick={() => { setAddLeadModalStage(stage); setAddLeadForm({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" }); }} style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", background: COLORS.accent, border: "none", borderRadius: 6, color: COLORS.bg, fontSize: 14, fontWeight: 600, cursor: "pointer", lineHeight: 1 }} title="Add lead">+</button>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
                      {stageDeals.map(deal => {
                        const companyMeta = [deal.industry, deal.employee_range && `👥 ${deal.employee_range}`, deal.location && `📍 ${deal.location}`].filter(Boolean);
                        const subtitle = [deal.title, deal.company || deal.company_domain].filter(Boolean).join(' · ');
                        return (
                        <div key={deal.id} onClick={() => setSelectedDeal(deal)} style={{ padding: "12px 14px", background: COLORS.surface, border: `1px solid ${selectedDeal?.id === deal.id ? COLORS.accent + "55" : COLORS.border}`, borderRadius: 8, cursor: "pointer", borderLeft: `3px solid ${col}`, transition: "border-color 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "44"} onMouseLeave={e => { if (selectedDeal?.id !== deal.id) e.currentTarget.style.borderColor = COLORS.border; }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}>
                              <span style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{deal.name}</span>
                              {deal.conversation_id && <span style={{ fontSize: 9, flexShrink: 0, color: deal.conv_channel === "email" ? "#FF6B35" : "#0A66C2" }}>{deal.conv_channel === "email" ? "📧" : "💼"}</span>}
                              {deal.conv_unread && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent, flexShrink: 0 }} title="Unread message" />}
                            </div>
                            {deal.conv_source && deal.conv_source !== "local" && <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 3, flexShrink: 0, background: deal.conv_source === "instantly" ? "#FF6B3520" : deal.conv_source === "aimfox" ? "#F5820D20" : "#0A66C220", color: deal.conv_source === "instantly" ? "#FF6B35" : deal.conv_source === "aimfox" ? "#F5820D" : "#0A66C2", fontWeight: 600 }}>{{ instantly: "Instantly", heyreach: "HeyReach", aimfox: "Aimfox" }[deal.conv_source] || deal.conv_source}</span>}
                          </div>
                          {subtitle && <div style={{ fontSize: 10, color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
                          {companyMeta.length > 0 && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{companyMeta.join(' · ')}</div>}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, fontFamily: FONT }}>{deal.value}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              {deal.email_status && <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, fontWeight: 600, background: deal.email_status === "low" ? COLORS.green + "15" : deal.email_status === "high" ? "#FF4D4D15" : "#FF8C0015", color: deal.email_status === "low" ? COLORS.green : deal.email_status === "high" ? "#FF4D4D" : "#FF8C00" }} title={`Bounce risk: ${deal.email_status}`}>{deal.email_status === "low" ? "✓" : deal.email_status === "high" ? "✗" : "?"}</span>}
                              <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600, background: deal.score != null ? (deal.score >= 90 ? COLORS.green + "15" : COLORS.blue + "15") : "transparent", color: deal.score != null ? (deal.score >= 90 ? COLORS.green : COLORS.blue) : COLORS.textDim }}>{deal.score != null ? `${deal.score}%` : "—"}</span>
                            </div>
                          </div>
                          {deal.conv_subject && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: "italic" }}>{deal.conv_subject}</div>}
                          <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 4 }}>{deal.lastActivity}</div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
                  {["Name", "Company", "Industry", "Location", "Stage", "Value", "ICP", "Source", "Last Activity"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, fontWeight: 600, letterSpacing: "0.04em" }}>{h.toUpperCase()}</th>
                  ))}
                </tr></thead>
                <tbody>{deals.map(deal => (
                  <tr key={deal.id} onClick={() => setSelectedDeal(deal)} style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: selectedDeal?.id === deal.id ? COLORS.accentBg : "transparent" }}
                    onMouseEnter={e => e.currentTarget.style.background = COLORS.surface} onMouseLeave={e => e.currentTarget.style.background = selectedDeal?.id === deal.id ? COLORS.accentBg : "transparent"}>
                    <td style={{ padding: "10px 14px" }}><div style={{ fontWeight: 600, fontSize: 12 }}>{deal.name}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>{deal.title}</div></td>
                    <td style={{ padding: "10px 14px" }}><div style={{ fontSize: 12 }}>{deal.company || deal.company_domain || '—'}</div>{deal.employee_range && <div style={{ fontSize: 9, color: COLORS.textDim }}>👥 {deal.employee_range}</div>}</td>
                    <td style={{ padding: "10px 14px", fontSize: 11, color: COLORS.textDim }}>{deal.industry || '—'}</td>
                    <td style={{ padding: "10px 14px", fontSize: 11, color: COLORS.textDim }}>{deal.location || '—'}</td>
                    <td style={{ padding: "10px 14px" }}><span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 9, fontWeight: 600, background: STAGE_COLORS[deal.stage] + "15", color: STAGE_COLORS[deal.stage] }}>{deal.stage}</span></td>
                    <td style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, fontFamily: FONT, color: COLORS.accent }}>{deal.value}</td>
                    <td style={{ padding: "10px 14px" }}><span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: deal.score != null ? (deal.score >= 90 ? COLORS.green + "15" : COLORS.blue + "15") : "transparent", color: deal.score != null ? (deal.score >= 90 ? COLORS.green : COLORS.blue) : COLORS.textDim }}>{deal.score != null ? `${deal.score}%` : "—"}</span></td>
                    <td style={{ padding: "10px 14px", fontSize: 11, color: COLORS.textDim }}>{deal.source}</td>
                    <td style={{ padding: "10px 14px", fontSize: 10, color: COLORS.textDim }}>{deal.lastActivity}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
        {(hasMore || totalDeals > deals.length) && (
          <div style={{ padding: "8px 16px", textAlign: "center", flexShrink: 0, borderTop: `1px solid ${COLORS.border}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>{deals.length.toLocaleString()} of {totalDeals.toLocaleString()} loaded</span>
            <button type="button" onClick={loadMore} disabled={loadingMore || !hasMore} style={{ padding: "6px 18px", background: hasMore ? COLORS.accent : COLORS.border, border: "none", borderRadius: 6, color: hasMore ? COLORS.bg : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 700, cursor: hasMore ? "pointer" : "default", opacity: loadingMore ? 0.5 : 1 }}>
              {loadingMore ? "Loading..." : hasMore ? "Load More" : "All loaded"}
            </button>
          </div>
        )}

        {/* Detail Panel */}
        {selectedDeal && (
          <div style={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${COLORS.border}`, overflow: "auto", background: COLORS.surface }}>
            <div style={{ padding: "16px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{selectedDeal.name}</span>
              <button onClick={() => setSelectedDeal(null)} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 16 }}>×</button>
            </div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{[selectedDeal.title, selectedDeal.company || selectedDeal.company_domain].filter(Boolean).join(' · ') || '—'}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.accent, fontFamily: FONT, marginTop: 4 }}>{selectedDeal.value}</div>
                {(selectedDeal.industry || selectedDeal.employee_range || selectedDeal.location) && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>{[selectedDeal.industry, selectedDeal.employee_range && `👥 ${selectedDeal.employee_range}`, selectedDeal.location && `📍 ${selectedDeal.location}`].filter(Boolean).join(' · ')}</div>}
                {selectedDeal.email_status && <div style={{ marginTop: 4 }}><span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 600, background: selectedDeal.email_status === "low" ? COLORS.green + "15" : selectedDeal.email_status === "high" ? "#FF4D4D15" : "#FF8C0015", color: selectedDeal.email_status === "low" ? COLORS.green : selectedDeal.email_status === "high" ? "#FF4D4D" : "#FF8C00" }}>{selectedDeal.email_status === "low" ? "✓ Verified" : selectedDeal.email_status === "high" ? "✗ High bounce risk" : "? Medium bounce risk"}</span></div>}
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>STAGE</div>
                <select value={selectedDeal.stage} onChange={e => { const ns = e.target.value; updateDealStage(selectedDeal.id, ns); }} style={{ width: "100%", padding: "8px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12, cursor: "pointer" }}>
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <div style={{ padding: "8px 10px", background: COLORS.bg, borderRadius: 6 }}><div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600 }}>ICP SCORE</div><div style={{ fontSize: 16, fontWeight: 700, fontFamily: FONT, color: selectedDeal.score != null ? (selectedDeal.score >= 90 ? COLORS.green : COLORS.blue) : COLORS.textDim }}>{selectedDeal.score ?? "—"}</div></div>
                <div style={{ padding: "8px 10px", background: COLORS.bg, borderRadius: 6 }}><div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600 }}>SOURCE</div><div style={{ fontSize: 11, color: COLORS.text, marginTop: 2 }}>{selectedDeal.source}</div></div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>CONTACT</div>
                {selectedDeal.email && <div style={{ fontSize: 11, marginBottom: 2 }}><a href={`mailto:${selectedDeal.email}`} style={{ color: COLORS.text, textDecoration: "none" }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>✉️ {selectedDeal.email}</a></div>}
                {selectedDeal.linkedin_url ? (
                  <div style={{ fontSize: 11 }}><a href={selectedDeal.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.text, textDecoration: "none" }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>💼 {selectedDeal.linkedin_url.replace(/^https?:\/\/(www\.)?/, "")}</a></div>
                ) : selectedDeal.conv_channel === "linkedin" ? (
                  <div style={{ fontSize: 11, color: COLORS.textDim }}>💼 LinkedIn (no profile URL)</div>
                ) : null}
                {!selectedDeal.email && !selectedDeal.linkedin_url && <div style={{ fontSize: 11, color: COLORS.textDim }}>No contact info</div>}
              </div>
              {selectedDeal.conversation_id && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>CONVERSATION</div>
                  <div style={{ padding: "8px 10px", background: COLORS.bg, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>{selectedDeal.conv_channel === "email" ? "📧" : "💼"}</span>
                      <span style={{ fontSize: 11, color: COLORS.text }}>{selectedDeal.conv_channel === "email" ? "Email" : "LinkedIn"}</span>
                      {selectedDeal.conv_source && selectedDeal.conv_source !== "local" && <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, background: selectedDeal.conv_source === "instantly" ? "#FF6B3520" : selectedDeal.conv_source === "aimfox" ? "#F5820D20" : "#0A66C220", color: selectedDeal.conv_source === "instantly" ? "#FF6B35" : selectedDeal.conv_source === "aimfox" ? "#F5820D" : "#0A66C2", fontWeight: 600 }}>{{ instantly: "Instantly", heyreach: "HeyReach", aimfox: "Aimfox" }[selectedDeal.conv_source]}</span>}
                    </div>
                    {selectedDeal.conv_unread && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent }} title="Unread" />}
                  </div>
                  <button onClick={() => { if (typeof onOpenConversation === "function") onOpenConversation(selectedDeal.conversation_id); }} style={{ width: "100%", padding: "8px", background: COLORS.accent + "10", border: `1px solid ${COLORS.accent}33`, borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", color: COLORS.accent }}>📥 Open in Inbox</button>
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>NOTES</div>
                <textarea value={note} onChange={e => setNote(e.target.value)} onBlur={handleNoteBlur} placeholder="Add notes about this deal..." rows={3} style={{ width: "100%", padding: "8px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em" }}>ACTIVITY</div>
                {(dealActivity.length > 0 ? dealActivity.slice(0, 10) : [{ action: selectedDeal.lastActivity, created_at: null }]).map((a, i, arr) => {
                  const label = a.action === "stage_changed" ? `Stage → ${a.metadata_json?.to || ""}` : a.action === "note_added" ? "Note added" : a.action || "—";
                  const ago = a.created_at ? (() => { const ms = Date.now() - new Date(a.created_at).getTime(); const m = Math.floor(ms/60000); const h = Math.floor(ms/3600000); const d = Math.floor(ms/86400000); return m < 60 ? (m <= 1 ? "just now" : `${m}m ago`) : h < 24 ? `${h}h ago` : d < 7 ? `${d}d ago` : new Date(a.created_at).toLocaleDateString(); })() : "Latest";
                  return (
                    <div key={a.id || i} style={{ padding: "6px 0", borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none", display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 11, color: COLORS.text }}>{label}</span>
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>{ago}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Add Lead Modal */}
        {addLeadModalStage && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => !addLeadSubmitting && (setAddLeadModalStage(null), setAddLeadForm({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" }))}>
            <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 24, maxWidth: 420, width: "90%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>Add Lead</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Stage: <span style={{ color: STAGE_COLORS[addLeadModalStage], fontWeight: 600 }}>{addLeadModalStage}</span></div>
                  {filterMode === "campaign" && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4, padding: "6px 8px", background: COLORS.surface, borderRadius: 6 }}>Tip: CRM lists org-wide leads. Manually added leads appear in &quot;All Leads&quot;.</div>}
                </div>
                <button onClick={() => !addLeadSubmitting && (setAddLeadModalStage(null), setAddLeadForm({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" }))} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 18, cursor: "pointer" }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>First name *</label>
                  <input value={addLeadForm.first_name} onChange={e => setAddLeadForm(f => ({ ...f, first_name: e.target.value }))} placeholder="John" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Last name *</label>
                  <input value={addLeadForm.last_name} onChange={e => setAddLeadForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Doe" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Company *</label>
                  <input value={addLeadForm.company} onChange={e => setAddLeadForm(f => ({ ...f, company: e.target.value }))} placeholder="Acme Inc" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Email</label>
                  <input value={addLeadForm.email} onChange={e => setAddLeadForm(f => ({ ...f, email: e.target.value }))} placeholder="Optional" type="email" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Job title</label>
                  <input value={addLeadForm.title} onChange={e => setAddLeadForm(f => ({ ...f, title: e.target.value }))} placeholder="Optional" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Company domain</label>
                  <input value={addLeadForm.company_domain} onChange={e => setAddLeadForm(f => ({ ...f, company_domain: e.target.value }))} placeholder="Optional (e.g. acme.com)" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>LinkedIn URL</label>
                  <input value={addLeadForm.linkedin_url} onChange={e => setAddLeadForm(f => ({ ...f, linkedin_url: e.target.value }))} placeholder="Optional (https://linkedin.com/in/...)" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Phone</label>
                  <input value={addLeadForm.phone} onChange={e => setAddLeadForm(f => ({ ...f, phone: e.target.value }))} placeholder="Optional" type="tel" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>Deal value (£)</label>
                  <input value={addLeadForm.deal_value} onChange={e => setAddLeadForm(f => ({ ...f, deal_value: e.target.value }))} placeholder="Optional" type="number" min="0" step="1" style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => !addLeadSubmitting && (setAddLeadModalStage(null), setAddLeadForm({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" }))} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button onClick={async () => {
                  const fn = (addLeadForm.first_name || "").trim();
                  const ln = (addLeadForm.last_name || "").trim();
                  const comp = (addLeadForm.company || "").trim();
                  if (!fn || !ln || !comp) return;
                  setAddLeadSubmitting(true);
                  try {
                    await api.crm.create({ ...addLeadForm, stage: addLeadModalStage, project_id: projectId || null });
                    setAddLeadModalStage(null);
                    setAddLeadForm({ first_name: "", last_name: "", email: "", company: "", title: "", company_domain: "", linkedin_url: "", phone: "", deal_value: "" });
                    if (filterMode === "campaign") {
                      setFilterMode("all");
                      loadedCountRef.current = 0;
                      fetchPipeline(0, false, false);
                    } else {
                      loadedCountRef.current = 0;
                      fetchPipeline(0, false);
                    }
                  } catch (err) {
                    console.error("Failed to add lead:", err);
                  } finally {
                    setAddLeadSubmitting(false);
                  }
                }} disabled={addLeadSubmitting || !(addLeadForm.first_name || "").trim() || !(addLeadForm.last_name || "").trim() || !(addLeadForm.company || "").trim()} style={{ padding: "10px 20px", background: COLORS.accent, border: "none", borderRadius: 8, color: COLORS.bg, fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: (addLeadSubmitting || !(addLeadForm.first_name || "").trim() || !(addLeadForm.last_name || "").trim() || !(addLeadForm.company || "").trim()) ? 0.5 : 1 }}>{addLeadSubmitting ? "Adding..." : "Add Lead"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CALENDAR_INTEGRATION_KEYS = ["calcom", "google_calendar"];
const FULL_24_HOURS = [
  "12 AM","1 AM","2 AM","3 AM","4 AM","5 AM","6 AM","7 AM",
  "8 AM","9 AM","10 AM","11 AM","12 PM","1 PM","2 PM","3 PM",
  "4 PM","5 PM","6 PM","7 PM","8 PM","9 PM","10 PM","11 PM",
];
const TYPE_COLORS = { Discovery: COLORS.blue, "AI Audit": "#7B61FF", "Follow-up": COLORS.accent, Close: COLORS.green, Proposal: "#EC4899" };
const SOURCE_COLORS = { calcom: "#7B61FF", google_calendar: "#4285F4" };
const BOOKING_EVENT_COLORS = [COLORS.blue, "#7B61FF", COLORS.accent, COLORS.green || "#22c55e", COLORS.warn, COLORS.danger];
const BOOKING_SYNC_PLATFORM_META = {
  calcom: { label: "Cal.com", color: SOURCE_COLORS.calcom },
};
const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const HOUR_HEIGHT = 56;

function mapBookingEventTypeRow(et, i) {
  const cfg = et.config_json || {};
  return {
    id: et.id, name: et.name, duration: et.duration || 30,
    location: et.location || "zoom", slug: et.slug || "",
    active: et.active !== false, bookings: et.bookings_count || 0,
    color: et.color || BOOKING_EVENT_COLORS[i % BOOKING_EVENT_COLORS.length],
    description: et.description || cfg.description || "",
    before_buffer: et.before_buffer ?? cfg.before_buffer ?? 0,
    after_buffer: et.after_buffer ?? cfg.after_buffer ?? 0,
    min_notice: et.min_notice ?? cfg.min_notice ?? 60,
    booking_window: et.booking_window ?? cfg.booking_window ?? 60,
    seats: et.seats ?? cfg.seats ?? 0,
    requires_confirmation: et.requires_confirmation ?? cfg.requires_confirmation ?? false,
    redirect_url: et.redirect_url || cfg.redirect_url || "",
    custom_questions: et.custom_questions || cfg.custom_questions || [],
    timezone: et.timezone || cfg.timezone || "Europe/London",
    calcom_id: et.calcom_id || null,
    calcom_booking_url: et.calcom_booking_url || "",
  };
}

function getVisibleBookingSyncPlatforms(report) {
  const summary = report?.summary || {};
  return ["calcom"].map((platform) => {
    const info = summary?.[platform] || {};
    const meta = BOOKING_SYNC_PLATFORM_META[platform];
    const fetched = Number(info.fetched ?? info.total ?? 0) || 0;
    const created = Number(info.created || 0) || 0;
    const updated = Number(info.updated || 0) || 0;
    const failed = Number(info.failed || 0) || 0;
    return {
      platform,
      label: meta?.label || platform,
      color: meta?.color || COLORS.textDim,
      connected: info.connected === true,
      fetched,
      total: fetched,
      created,
      updated,
      failed,
      status: info.status || "unknown",
      message: info.message || null,
    };
  }).filter((info) => (
    info.connected ||
    info.status === "error" ||
    info.status === "partial" ||
    info.status === "empty" ||
    info.fetched > 0 ||
    info.created > 0 ||
    info.updated > 0 ||
    info.failed > 0
  ));
}

function getBookingSyncSummaryText(info) {
  if (info.status === "error") return "Sync failed";
  if (info.status === "partial") return `${info.fetched} fetched - ${info.created} created - ${info.updated} updated - ${info.failed} failed`;
  if (info.status === "empty" || info.fetched === 0) return "No event types found";
  return `${info.fetched} fetched - ${info.created} created - ${info.updated} updated - ${info.failed} failed`;
}

function parseSlotHour(h) {
  const parts = h.trim().split(/\s+/);
  let n = parseInt(parts[0], 10) || 0;
  const period = (parts[1] || "AM").toUpperCase();
  if (period === "PM" && n !== 12) n += 12;
  if (period === "AM" && n === 12) n = 0;
  return n;
}

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function getWeekStart(d) {
  const r = new Date(d);
  r.setDate(r.getDate() - r.getDay() + 1); // Monday start
  return r;
}

function getMonthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function getMonthCalendarDays(d) {
  const first = getMonthStart(d);
  const startDay = first.getDay(); // 0=Sun
  const gridStart = addDays(first, -(startDay === 0 ? 6 : startDay - 1)); // Mon start
  const days = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  return days;
}

function AppointmentsView() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [calView, setCalView] = useState("week"); // "day" | "week" | "month"
  const [currentDate, setCurrentDate] = useState(new Date());
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [allEvents, setAllEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sourceFilter, setSourceFilter] = useState({ calcom: true, google_calendar: true });
  const [bookingView, setBookingView] = useState("events");
  const [eventForm, setEventForm] = useState({ name: "", duration: 30, location: "zoom", slug: "", description: "", before_buffer: 0, after_buffer: 0, min_notice: 60, booking_window: 60, seats: 0, requires_confirmation: false, redirect_url: "", custom_questions: [], timezone: "Europe/London" });
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [syncingEventTypes, setSyncingEventTypes] = useState(false);
  const [eventTypeSyncReport, setEventTypeSyncReport] = useState(null);
  const [copiedBookingLinkKey, setCopiedBookingLinkKey] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const calGridRef = useRef(null);
  const [createEventModal, setCreateEventModal] = useState(null); // { date, hour } or null
  const [newEvent, setNewEvent] = useState({ title: "", description: "", location: "", duration: 30, timezone: "Europe/London", attendees: "", addMeet: false, reminder: 15 });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const mapEventTypeRows = useCallback((rows) => rows.map((et, i) => mapBookingEventTypeRow(et, i)), []);
  const visibleSyncPlatforms = useMemo(() => getVisibleBookingSyncPlatforms(eventTypeSyncReport), [eventTypeSyncReport]);

  // ── Date helpers ──
  const todayDate = new Date();
  const todayStr = fmtDate(todayDate);

  // Compute fetch range based on view
  const fetchRange = useMemo(() => {
    if (calView === "day") return { start: fmtDate(currentDate), end: fmtDate(currentDate) };
    if (calView === "week") {
      const ws = getWeekStart(currentDate);
      return { start: fmtDate(ws), end: fmtDate(addDays(ws, 6)) };
    }
    // month
    const days = getMonthCalendarDays(currentDate);
    return { start: fmtDate(days[0]), end: fmtDate(days[days.length - 1]) };
  }, [calView, currentDate]);

  // ── Data fetching ──
  useEffect(() => {
    api.integrations.list().then((r) => setIntegrationStatus(r.integrations || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === "booking") {
      setEventTypesLoading(true);
      api.booking.eventTypes.list().then(data => {
        const rows = Array.isArray(data) ? data : (data.eventTypes || []);
        setEventTypes(mapEventTypeRows(rows));
      }).catch(() => {}).finally(() => setEventTypesLoading(false));

      api.booking.availability.get().then(data => {
        const rows = Array.isArray(data) ? data : (data.availability || []);
        if (rows.length > 0) {
          setAvailability(rows.map(r => ({
            day: r.day_of_week, enabled: r.enabled !== false,
            start: r.start_time || "09:00", end: r.end_time || "17:00",
          })));
        } else {
          setAvailability(["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => ({
            day: d, enabled: i < 5, start: "09:00", end: "17:00",
          })));
        }
      }).catch(() => {
        setAvailability(["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => ({
          day: d, enabled: i < 5, start: "09:00", end: "17:00",
        })));
      });
    }
  }, [activeTab, mapEventTypeRows]);

  useEffect(() => {
    if (!copiedBookingLinkKey) return undefined;
    const timer = setTimeout(() => setCopiedBookingLinkKey(null), 1600);
    return () => clearTimeout(timer);
  }, [copiedBookingLinkKey]);

  const fetchCalendar = useCallback((showLoading = true) => {
    if (activeTab !== "calendar" && activeTab !== "calls") return Promise.resolve();
    if (showLoading) setCalendarLoading(true);
    const fetchFn = (activeTab === "calls")
      ? api.calendar.getEvents(todayStr)
      : api.calendar.getRange(fetchRange.start, fetchRange.end);
    return fetchFn
      .then((r) => setAllEvents(r.events || []))
      .catch(() => setAllEvents([]))
      .finally(() => showLoading && setCalendarLoading(false));
  }, [activeTab, fetchRange.start, fetchRange.end, todayStr]);

  useEffect(() => {
    if (activeTab === "calendar" || activeTab === "calls") {
      fetchCalendar(true);
    }
  }, [activeTab, fetchRange.start, fetchRange.end, todayStr, fetchCalendar]);

  // Resync calendar from Google Calendar and Cal.com every 1 minute
  useEffect(() => {
    if (activeTab !== "calendar" && activeTab !== "calls") return;
    const id = setInterval(() => fetchCalendar(false), 60000);
    return () => clearInterval(id);
  }, [activeTab, fetchCalendar]);

  // Scroll to current hour on mount
  useEffect(() => {
    if (activeTab === "calendar" && calGridRef.current && (calView === "day" || calView === "week")) {
      const nowH = new Date().getHours();
      const scrollTo = Math.max(0, (nowH - 1)) * HOUR_HEIGHT;
      setTimeout(() => { if (calGridRef.current) calGridRef.current.scrollTop = scrollTo; }, 100);
    }
  }, [activeTab, calView]);

  // Filter events by source
  const events = allEvents.filter(e => !e.source || sourceFilter[e.source] !== false);

  // ── Navigation ──
  function navPrev() {
    if (calView === "day") setCurrentDate(d => addDays(d, -1));
    else if (calView === "week") setCurrentDate(d => addDays(d, -7));
    else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }
  function navNext() {
    if (calView === "day") setCurrentDate(d => addDays(d, 1));
    else if (calView === "week") setCurrentDate(d => addDays(d, 7));
    else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }
  function goToday() { setCurrentDate(new Date()); }

  // ── Header label ──
  function headerLabel() {
    if (calView === "day") return currentDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    if (calView === "week") {
      const ws = getWeekStart(currentDate);
      const we = addDays(ws, 6);
      const sameMonth = ws.getMonth() === we.getMonth();
      if (sameMonth) return `${MONTH_NAMES[ws.getMonth()]} ${ws.getDate()}–${we.getDate()}, ${ws.getFullYear()}`;
      return `${MONTH_NAMES[ws.getMonth()].slice(0, 3)} ${ws.getDate()} – ${MONTH_NAMES[we.getMonth()].slice(0, 3)} ${we.getDate()}, ${we.getFullYear()}`;
    }
    return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }

  // ── Event helpers ──
  function eventsForDay(date) {
    return events.filter(e => isSameDay(new Date(e.start), date));
  }
  function eventsForDayHour(date, hour) {
    return events.filter(e => {
      const d = new Date(e.start);
      return isSameDay(d, date) && d.getHours() === hour;
    });
  }

  // ── Booking handlers ──
  async function handleSaveEventType() {
    if (!eventForm.name.trim()) return;
    setSavingEvent(true);
    try {
      const payload = {
        name: eventForm.name, duration: eventForm.duration,
        location: eventForm.location, slug: eventForm.slug || eventForm.name.toLowerCase().replace(/\s+/g, "-"),
        description: eventForm.description, active: true,
        before_buffer: eventForm.before_buffer, after_buffer: eventForm.after_buffer,
        min_notice: eventForm.min_notice, booking_window: eventForm.booking_window,
        seats: eventForm.seats, requires_confirmation: eventForm.requires_confirmation,
        redirect_url: eventForm.redirect_url, custom_questions: eventForm.custom_questions,
        timezone: eventForm.timezone,
        config_json: { description: eventForm.description, before_buffer: eventForm.before_buffer, after_buffer: eventForm.after_buffer, min_notice: eventForm.min_notice, booking_window: eventForm.booking_window, seats: eventForm.seats, requires_confirmation: eventForm.requires_confirmation, redirect_url: eventForm.redirect_url, custom_questions: eventForm.custom_questions, timezone: eventForm.timezone },
      };
      if (editingEventId) {
        await api.booking.eventTypes.update(editingEventId, payload);
      } else {
        await api.booking.eventTypes.create(payload);
      }
      const data = await api.booking.eventTypes.list();
      const rows = Array.isArray(data) ? data : (data.eventTypes || []);
      setEventTypes(mapEventTypeRows(rows));
      setEventForm({ name: "", duration: 30, location: "zoom", slug: "", description: "", before_buffer: 0, after_buffer: 0, min_notice: 60, booking_window: 60, seats: 0, requires_confirmation: false, redirect_url: "", custom_questions: [], timezone: "Europe/London" });
      setEditingEventId(null);
      setBookingView("events");
    } catch (err) { console.error("Failed to save event type:", err); }
    finally { setSavingEvent(false); }
  }

  async function handleSaveAvailability() {
    setSavingAvailability(true);
    try {
      const payload = availability.map(a => {
        const slots = a.slots && a.slots.length > 0 ? a.slots : [{ start: a.start || "09:00", end: a.end || "17:00" }];
        return { day_of_week: a.day, enabled: a.enabled, start_time: slots[0].start, end_time: slots[0].end, slots };
      });
      await api.booking.availability.update(payload);
    } catch (err) { console.error("Failed to save availability:", err); }
    finally { setSavingAvailability(false); }
  }

  async function handleDeleteEventType(id) {
    if (!confirm("Delete this event type? This cannot be undone.")) return;
    try {
      await api.booking.eventTypes.del(id);
      setEventTypes(prev => prev.filter(et => et.id !== id));
    } catch (err) { console.error("Failed to delete event type:", err); }
  }

  function handleEditEventType(evt) {
    setEventForm({ name: evt.name, duration: evt.duration, location: evt.location, slug: evt.slug, description: evt.description || "", before_buffer: evt.before_buffer || 0, after_buffer: evt.after_buffer || 0, min_notice: evt.min_notice ?? 60, booking_window: evt.booking_window ?? 60, seats: evt.seats || 0, requires_confirmation: evt.requires_confirmation || false, redirect_url: evt.redirect_url || "", custom_questions: evt.custom_questions || [], timezone: evt.timezone || "Europe/London" });
    setEditingEventId(evt.id);
    setBookingView("create");
  }

  async function handleSyncEventTypes() {
    setSyncingEventTypes(true);
    try {
      const data = await api.booking.eventTypes.sync();
      const rows = Array.isArray(data?.event_types) ? data.event_types : [];
      setEventTypes(mapEventTypeRows(rows));
      setEventTypeSyncReport({
        summary: data?.summary || {},
      });
    } catch (err) {
      console.error("Failed to sync event types:", err);
      setEventTypeSyncReport({
        summary: {},
        message: err?.message || "Sync failed",
      });
    } finally {
      setSyncingEventTypes(false);
    }
  }

  async function copyBookingLink(copyKey, url) {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedBookingLinkKey(copyKey);
    } catch (err) {
      console.error("Failed to copy booking link:", err);
    }
  }

  const TIME_SLOTS_30 = Array.from({ length: 48 }, (_, i) => { const h = String(Math.floor(i / 2)).padStart(2, "0"); const m = i % 2 === 0 ? "00" : "30"; return `${h}:${m}`; });

  const TIMEZONE_OPTIONS = ["Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Asia/Tokyo", "Asia/Shanghai", "Australia/Sydney", "UTC"];

  const MIN_NOTICE_OPTIONS = [[0, "None"], [15, "15 min"], [30, "30 min"], [60, "1 hour"], [120, "2 hours"], [180, "3 hours"], [360, "6 hours"], [720, "12 hours"], [1440, "1 day"], [2880, "2 days"], [4320, "3 days"], [10080, "1 week"]];

  const BOOKING_WINDOW_OPTIONS = [[7, "1 week"], [14, "2 weeks"], [30, "1 month"], [60, "2 months"], [90, "3 months"], [180, "6 months"], [365, "1 year"]];

  // ── Call list: uses today's events ──
  const todayEvents = events.filter(e => isSameDay(new Date(e.start), todayDate));
  const now = Date.now();
  const upcomingEvents = todayEvents.filter((e) => new Date(e.start).getTime() >= now);
  const pastEvents = todayEvents.filter((e) => new Date(e.start).getTime() < now);

  // ── Integration badges ──
  const calendarBadges = CALENDAR_INTEGRATION_KEYS.map((k) => {
    const s = integrationStatus[k];
    const connected = typeof s === "object" ? !!s?.connected : !!s;
    const signedIn = typeof s === "object" ? s?.signedIn : connected;
    const ok = k === "google_calendar" ? connected && signedIn : connected;
    const label = k === "calcom" ? "Cal.com" : "Google Calendar";
    return { key: k, label, ok };
  }).filter((b) => b.ok);

  const sourceLabel = (src) => src === "calcom" ? "Cal.com" : src === "google_calendar" ? "Google" : "";

  // ── Current time position ──
  const nowDate = new Date();
  const nowMinuteOfDay = nowDate.getHours() * 60 + nowDate.getMinutes();
  const nowPx = (nowMinuteOfDay / 60) * HOUR_HEIGHT;

  // ── Event popover ──
  function EventPopover({ ev, onClose }) {
    if (!ev) return null;
    const color = SOURCE_COLORS[ev.source] || TYPE_COLORS[ev.type] || COLORS.accent;
    const start = new Date(ev.start);
    const end = ev.end ? new Date(ev.end) : null;
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 24, minWidth: 320, maxWidth: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{ borderLeft: `4px solid ${color}`, paddingLeft: 12 }}>
              <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{ev.name || ev.title}</div>
              <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 2 }}>{ev.title}</div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 18, cursor: "pointer", padding: "0 4px" }}>×</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12, color: COLORS.textMuted }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14 }}>🗓</span>
              <span>{start.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14 }}>🕐</span>
              <span>{ev.time}{end ? ` – ${end.getHours() % 12 || 12}:${String(end.getMinutes()).padStart(2, "0")} ${end.getHours() >= 12 ? "PM" : "AM"}` : ""} ({ev.duration})</span>
            </div>
            {ev.source && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>📌</span>
                <span style={{ padding: "2px 8px", borderRadius: 4, background: color + "18", color, fontFamily: FONT, fontWeight: 600, fontSize: 10 }}>{sourceLabel(ev.source)}</span>
              </div>
            )}
            {ev.company && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>🏢</span>
                <span>{ev.company}</span>
              </div>
            )}
          </div>
          {ev.link && (
            <a href={ev.link} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 16, padding: "8px 18px", background: COLORS.accent, color: COLORS.bg, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>Join Meeting</a>
          )}
        </div>
      </div>
    );
  }

  // ── Shared event chip ──
  function EventChip({ ev, compact, style: extraStyle }) {
    const color = SOURCE_COLORS[ev.source] || TYPE_COLORS[ev.type] || COLORS.accent;
    return (
      <div onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }} title={`${ev.name || ev.title} · ${ev.time} · ${ev.duration}`} style={{ padding: compact ? "2px 6px" : "4px 8px", background: color + "18", border: `1px solid ${color}40`, borderRadius: 4, borderLeft: `3px solid ${color}`, cursor: "pointer", overflow: "hidden", ...extraStyle }}>
        <div style={{ fontWeight: 600, fontSize: compact ? 10 : 11, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.name || ev.title}</div>
        {!compact && <div style={{ fontSize: 9, color: COLORS.textDim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.time} · {ev.duration}</div>}
      </div>
    );
  }

  // ── Create Event handler ──
  function openCreateEvent(date, hourNum) {
    const d = new Date(date);
    d.setHours(hourNum, 0, 0, 0);
    setCreateEventModal({ date: d, hour: hourNum });
    setNewEvent({ title: "", description: "", location: "", duration: 30, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London", attendees: "", addMeet: false, reminder: 15 });
  }

  async function handleCreateCalendarEvent() {
    if (!newEvent.title.trim()) return;
    setCreatingEvent(true);
    try {
      const startDate = new Date(createEventModal.date);
      const endDate = new Date(startDate.getTime() + newEvent.duration * 60000);
      const toISO = (d) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };
      const attendeesList = newEvent.attendees.split(",").map(e => e.trim()).filter(Boolean);
      await api.calendar.createEvent({
        title: newEvent.title.trim(),
        description: newEvent.description,
        location: newEvent.location,
        start: toISO(startDate),
        end: toISO(endDate),
        timezone: newEvent.timezone,
        attendees: attendeesList,
        addMeet: newEvent.addMeet,
        reminder: newEvent.reminder,
      });
      setCreateEventModal(null);
      // Refresh calendar events
      setCalendarLoading(true);
      const fetchFn = calView === "day"
        ? api.calendar.getEvents(fmtDate(currentDate))
        : api.calendar.getRange(fetchRange.start, fetchRange.end);
      fetchFn.then(r => setAllEvents(r.events || [])).catch(() => {}).finally(() => setCalendarLoading(false));
    } catch (err) {
      alert("Failed to create event: " + (err.message || "Unknown error"));
    } finally {
      setCreatingEvent(false);
    }
  }

  // ── Render: Day view 24h grid ──
  function renderDayView(date) {
    const isToday = isSameDay(date, todayDate);
    return (
      <div ref={calGridRef} style={{ position: "relative", overflowY: "auto", maxHeight: "calc(100vh - 280px)", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
        {/* Current time line */}
        {isToday && <div style={{ position: "absolute", top: nowPx, left: 0, right: 0, zIndex: 5, pointerEvents: "none" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", marginLeft: 52 }} />
            <div style={{ flex: 1, height: 2, background: "#EF4444" }} />
          </div>
        </div>}
        {FULL_24_HOURS.map((hour, hi) => {
          const hourNum = parseSlotHour(hour);
          const hourEvents = eventsForDayHour(date, hourNum);
          return (
            <div key={hi} style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}20`, height: HOUR_HEIGHT, position: "relative" }}>
              <div style={{ width: 60, padding: "4px 8px 0 0", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, flexShrink: 0, textAlign: "right", lineHeight: "1" }}>{hour}</div>
              <div onClick={() => openCreateEvent(date, hourNum)} style={{ flex: 1, borderLeft: `1px solid ${COLORS.border}`, position: "relative", padding: "2px 4px", cursor: "crosshair" }} title="Click to create event">
                {/* Half-hour line */}
                <div style={{ position: "absolute", top: HOUR_HEIGHT / 2, left: 0, right: 0, borderTop: `1px dashed ${COLORS.border}20`, pointerEvents: "none" }} />
                {hourEvents.map((ev, ei) => {
                  const evStart = new Date(ev.start);
                  const topOffset = (evStart.getMinutes() / 60) * HOUR_HEIGHT;
                  const durMin = ev.durationMin || 30;
                  const rawHeight = (durMin / 60) * HOUR_HEIGHT - 2;
                  const maxHeight = (24 - hourNum) * HOUR_HEIGHT - topOffset - 4;
                  const evHeight = ev.isAllDay ? 24 : Math.max(20, Math.min(rawHeight, maxHeight));
                  const width = hourEvents.length > 1 ? `${Math.floor(100 / hourEvents.length) - 1}%` : "calc(100% - 8px)";
                  const left = hourEvents.length > 1 ? `${ei * Math.floor(100 / hourEvents.length)}%` : "4px";
                  const color = SOURCE_COLORS[ev.source] || TYPE_COLORS[ev.type] || COLORS.accent;
                  return (
                    <div key={ev.id} onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }} title={`${ev.name || ev.title}\n${ev.time} · ${ev.duration}\n${sourceLabel(ev.source)}`} style={{ position: "absolute", top: topOffset, left, width, height: evHeight, background: color + "20", border: `1px solid ${color}50`, borderLeft: `3px solid ${color}`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", overflow: "hidden", zIndex: 2, boxSizing: "border-box" }}>
                      <div style={{ fontWeight: 600, fontSize: 11, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.name || ev.title}</div>
                      {(evHeight > 28 || ev.isAllDay) && <div style={{ fontSize: 9, color: COLORS.textDim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.time}{ev.isAllDay ? "" : ` · ${ev.duration}`} {ev.source ? `· ${sourceLabel(ev.source)}` : ""}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Render: Week view ──
  function renderWeekView() {
    const weekStart = getWeekStart(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
        {/* Day headers */}
        <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, zIndex: 10, background: COLORS.surface }}>
          <div style={{ width: 60, flexShrink: 0 }} />
          {weekDays.map((day, di) => {
            const isToday = isSameDay(day, todayDate);
            return (
              <div key={di} onClick={() => { setCurrentDate(day); setCalView("day"); }} style={{ flex: 1, textAlign: "center", padding: "10px 0 8px", borderLeft: `1px solid ${COLORS.border}`, cursor: "pointer" }}>
                <div style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{DAY_NAMES_SHORT[day.getDay()]}</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: FONT, color: isToday ? COLORS.accent : COLORS.text, width: 32, height: 32, lineHeight: "32px", borderRadius: "50%", background: isToday ? COLORS.accent + "18" : "transparent", margin: "2px auto 0" }}>{day.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* Hour grid */}
        <div ref={calGridRef} style={{ position: "relative", overflowY: "auto", maxHeight: "calc(100vh - 340px)" }}>
          {/* Current time line */}
          {weekDays.some(d => isSameDay(d, todayDate)) && (() => {
            const todayIdx = weekDays.findIndex(d => isSameDay(d, todayDate));
            const colWidth = `calc((100% - 60px) / 7)`;
            return (
              <div style={{ position: "absolute", top: nowPx, left: `calc(60px + ${todayIdx} * ${colWidth})`, width: colWidth, zIndex: 5, pointerEvents: "none" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", marginLeft: -4 }} />
                  <div style={{ flex: 1, height: 2, background: "#EF4444" }} />
                </div>
              </div>
            );
          })()}
          {FULL_24_HOURS.map((hour, hi) => {
            const hourNum = parseSlotHour(hour);
            return (
              <div key={hi} style={{ display: "flex", height: HOUR_HEIGHT, borderBottom: `1px solid ${COLORS.border}15` }}>
                <div style={{ width: 60, padding: "4px 8px 0 0", fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, flexShrink: 0, textAlign: "right", lineHeight: "1" }}>{hour}</div>
                {weekDays.map((day, di) => {
                  const dayHourEvents = eventsForDayHour(day, hourNum);
                  return (
                    <div key={di} onClick={() => openCreateEvent(day, hourNum)} style={{ flex: 1, position: "relative", borderLeft: `1px solid ${COLORS.border}`, padding: "1px 2px", cursor: "crosshair" }} title="Click to create event">
                      {/* Half-hour line */}
                      <div style={{ position: "absolute", top: HOUR_HEIGHT / 2, left: 0, right: 0, borderTop: `1px dashed ${COLORS.border}15`, pointerEvents: "none" }} />
                      {dayHourEvents.map((ev, ei) => {
                        const evStart = new Date(ev.start);
                        const topOffset = (evStart.getMinutes() / 60) * HOUR_HEIGHT;
                        const durMin = ev.durationMin || 30;
                        const rawHeight = (durMin / 60) * HOUR_HEIGHT - 2;
                        const maxHeight = (24 - hourNum) * HOUR_HEIGHT - topOffset - 4;
                        const evHeight = ev.isAllDay ? 24 : Math.max(18, Math.min(rawHeight, maxHeight));
                        const color = SOURCE_COLORS[ev.source] || TYPE_COLORS[ev.type] || COLORS.accent;
                        const w = dayHourEvents.length > 1 ? `${Math.floor(100 / dayHourEvents.length) - 1}%` : "calc(100% - 4px)";
                        const l = dayHourEvents.length > 1 ? `${ei * Math.floor(100 / dayHourEvents.length)}%` : "2px";
                        return (
                          <div key={ev.id} onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }} title={`${ev.name || ev.title} · ${ev.time}`} style={{ position: "absolute", top: topOffset, left: l, width: w, height: evHeight, background: color + "22", border: `1px solid ${color}50`, borderLeft: `2px solid ${color}`, borderRadius: 3, padding: "1px 4px", cursor: "pointer", overflow: "hidden", zIndex: 2, fontSize: 10, boxSizing: "border-box" }}>
                            <div style={{ fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.name || ev.title}</div>
                            {(evHeight > 24 || ev.isAllDay) && <div style={{ fontSize: 9, color: COLORS.textDim }}>{ev.time}</div>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Render: Month view ──
  function renderMonthView() {
    const days = getMonthCalendarDays(currentDate);
    const currentMonth = currentDate.getMonth();
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${COLORS.border}` }}>
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} style={{ textAlign: "center", padding: "8px 0", fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {days.map((day, i) => {
            const isToday = isSameDay(day, todayDate);
            const isCurrentMonth = day.getMonth() === currentMonth;
            const dayEvents = eventsForDay(day);
            return (
              <div key={i} onClick={() => { setCurrentDate(day); setCalView("day"); }} style={{ minHeight: 90, padding: "4px 6px", borderRight: (i + 1) % 7 !== 0 ? `1px solid ${COLORS.border}20` : "none", borderBottom: i < 35 ? `1px solid ${COLORS.border}20` : "none", cursor: "pointer", opacity: isCurrentMonth ? 1 : 0.35, background: isToday ? COLORS.accent + "08" : "transparent" }}>
                <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, fontFamily: FONT, color: isToday ? COLORS.accent : COLORS.text, width: 24, height: 24, lineHeight: "24px", borderRadius: "50%", textAlign: "center", background: isToday ? COLORS.accent + "20" : "transparent", marginBottom: 2 }}>{day.getDate()}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {dayEvents.slice(0, 3).map(ev => (
                    <EventChip key={ev.id} ev={ev} compact style={{ marginBottom: 0 }} />
                  ))}
                  {dayEvents.length > 3 && (
                    <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, paddingLeft: 4 }}>+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      {/* Event detail popover */}
      {selectedEvent && <EventPopover ev={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      {createEventModal && (() => {
        const startDate = createEventModal.date;
        const startStr = `${startDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at ${startDate.getHours() % 12 || 12}:${String(startDate.getMinutes()).padStart(2, "0")} ${startDate.getHours() >= 12 ? "PM" : "AM"}`;
        const inputStyle = { width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" };
        const labelStyle = { fontSize: 11, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, marginBottom: 4, display: "block" };
        return (
          <div onClick={() => setCreateEventModal(null)} style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }}>
            <div onClick={e => e.stopPropagation()} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 24, width: 420, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: COLORS.text }}>New Event</div>
                <button onClick={() => setCreateEventModal(null)} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 18, cursor: "pointer" }}>x</button>
              </div>
              <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 16, padding: "6px 10px", background: COLORS.accent + "10", borderRadius: 6, border: `1px solid ${COLORS.accent}30` }}>{startStr}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Title *</label>
                  <input value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} placeholder="Meeting with..." style={inputStyle} autoFocus />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Duration</label>
                    <select value={newEvent.duration} onChange={e => setNewEvent(p => ({ ...p, duration: parseInt(e.target.value) }))} style={inputStyle}>
                      {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Reminder</label>
                    <select value={newEvent.reminder} onChange={e => setNewEvent(p => ({ ...p, reminder: parseInt(e.target.value) }))} style={inputStyle}>
                      {[0, 5, 10, 15, 30, 60].map(m => <option key={m} value={m}>{m === 0 ? "None" : `${m} min before`}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Notes or agenda..." style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                <div>
                  <label style={labelStyle}>Location</label>
                  <input value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} placeholder="Office, Zoom link, etc." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Attendees (comma-separated emails)</label>
                  <input value={newEvent.attendees} onChange={e => setNewEvent(p => ({ ...p, attendees: e.target.value }))} placeholder="john@example.com, jane@example.com" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Timezone</label>
                  <select value={newEvent.timezone} onChange={e => setNewEvent(p => ({ ...p, timezone: e.target.value }))} style={inputStyle}>
                    {["Europe/London", "Europe/Paris", "Europe/Berlin", "US/Eastern", "US/Central", "US/Pacific", "Asia/Tokyo", "Asia/Singapore", "Australia/Sydney", "UTC"].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={newEvent.addMeet} onChange={e => setNewEvent(p => ({ ...p, addMeet: e.target.checked }))} />
                  <span style={{ fontSize: 12, fontFamily: FONT, color: COLORS.text }}>Add Google Meet link</span>
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
                <button onClick={() => setCreateEventModal(null)} style={{ padding: "8px 18px", borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button onClick={handleCreateCalendarEvent} disabled={creatingEvent || !newEvent.title.trim()} style={{ padding: "8px 18px", borderRadius: 6, background: COLORS.accent, border: "none", color: COLORS.bg, fontFamily: FONT, fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: creatingEvent || !newEvent.title.trim() ? 0.5 : 1 }}>{creatingEvent ? "Creating..." : "Create Event"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>
            <span style={{ color: COLORS.accent }}>Appointments</span>
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>{todayDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · {events.length} event{events.length !== 1 ? "s" : ""}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {calendarBadges.length > 0 ? (
            calendarBadges.map((b) => (
              <div key={b.key} style={{ padding: "6px 12px", borderRadius: 6, background: COLORS.green + "15", border: `1px solid ${COLORS.green}33`, display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.green }} />
                <span style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.green }}>{b.label} Connected</span>
              </div>
            ))
          ) : (
            <div style={{ padding: "6px 12px", borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim }}>No calendar connected</span>
            </div>
          )}
        </div>
      </div>

      {/* Main tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[{ key: "calendar", label: "📅 Calendar" }, { key: "booking", label: "📎 Booking" }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: "10px 20px", background: "transparent", border: "none", borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent", color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{tab.label}</button>
        ))}
      </div>

      {/* ── Call List ── */}
      {activeTab === "calls" && (
        <div>
          {calendarLoading ? (
            <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading calendar events…</div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                {upcomingEvents.length === 0 && !calendarLoading ? (
                  <div style={{ padding: 24, background: COLORS.surface, border: `1px dashed ${COLORS.border}`, borderRadius: 12, color: COLORS.textDim, fontSize: 13, textAlign: "center" }}>No upcoming events from connected calendars. Connect Cal.com or Google Calendar in Settings &gt; Integrations.</div>
                ) : (
                  upcomingEvents.map((ev) => {
                    const color = SOURCE_COLORS[ev.source] || TYPE_COLORS[ev.type] || COLORS.accent;
                    const src = sourceLabel(ev.source);
                    return (
                      <div key={ev.id} style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderLeft: `4px solid ${color}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div>
                              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: FONT, color: COLORS.text }}>{ev.time}</div>
                              <div style={{ fontSize: 10, color: COLORS.textDim }}>{ev.duration}</div>
                            </div>
                            <div style={{ width: 1, height: 36, background: COLORS.border }} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 15 }}>{ev.name || ev.title}</div>
                              <div style={{ fontSize: 12, color: COLORS.textDim }}>{ev.title} {src && `· ${src}`}</div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 600, background: color + "15", color }}>{ev.type || ev.title}</span>
                            {ev.link && <a href={ev.link} target="_blank" rel="noreferrer" style={{ padding: "6px 14px", background: COLORS.accent, color: COLORS.bg, borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, textDecoration: "none", cursor: "pointer" }}>Join Call</a>}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, cursor: "pointer" }}>View in CRM</button>
                          <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, cursor: "pointer" }}>Prep Script</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {pastEvents.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.04em", marginBottom: 10 }}>COMPLETED</div>
                  {pastEvents.map((ev) => (
                    <div key={ev.id} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: 0.7 }}>
                      <div><span style={{ fontWeight: 600, fontSize: 12 }}>{ev.name || ev.title}</span><span style={{ fontSize: 11, color: COLORS.textDim }}> · {ev.title} · {ev.time}</span></div>
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>{ev.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Calendar ── */}
      {activeTab === "calendar" && (
        <div>
          {/* Toolbar: navigation + view switcher + source filters */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            {/* Left: nav */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={goToday} style={{ padding: "6px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.text, cursor: "pointer" }}>Today</button>
              <button onClick={navPrev} style={{ padding: "6px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 13, color: COLORS.text, cursor: "pointer", lineHeight: 1 }}>‹</button>
              <button onClick={navNext} style={{ padding: "6px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 13, color: COLORS.text, cursor: "pointer", lineHeight: 1 }}>›</button>
              <span style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: COLORS.text, marginLeft: 4 }}>{headerLabel()}</span>
            </div>

            {/* Right: view switcher + source filters */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Source filters */}
              {calendarBadges.length > 0 && (
                <div style={{ display: "flex", gap: 4 }}>
                  {CALENDAR_INTEGRATION_KEYS.map(k => {
                    const s = integrationStatus[k];
                    const connected = typeof s === "object" ? !!s?.connected : !!s;
                    if (!connected) return null;
                    const on = sourceFilter[k] !== false;
                    const color = SOURCE_COLORS[k] || COLORS.accent;
                    const label = k === "calcom" ? "Cal.com" : "Google";
                    return (
                      <button key={k} onClick={() => setSourceFilter(p => ({ ...p, [k]: !p[k] }))} style={{ padding: "4px 10px", borderRadius: 4, fontSize: 10, fontFamily: FONT, fontWeight: 600, cursor: "pointer", background: on ? color + "18" : COLORS.surface, border: `1px solid ${on ? color + "55" : COLORS.border}`, color: on ? color : COLORS.textDim, opacity: on ? 1 : 0.5 }}>{label}</button>
                    );
                  })}
                </div>
              )}
              {/* View switcher */}
              <div style={{ display: "flex", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, overflow: "hidden" }}>
                {[{ key: "day", label: "Day" }, { key: "week", label: "Week" }, { key: "month", label: "Month" }].map(v => (
                  <button key={v.key} onClick={() => setCalView(v.key)} style={{ padding: "6px 14px", background: calView === v.key ? COLORS.accent + "18" : "transparent", border: "none", borderRight: v.key !== "month" ? `1px solid ${COLORS.border}` : "none", fontFamily: FONT, fontSize: 11, fontWeight: 600, color: calView === v.key ? COLORS.accent : COLORS.textMuted, cursor: "pointer" }}>{v.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar content */}
          {calendarLoading ? (
            <div style={{ padding: 60, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading calendar…</div>
          ) : (
            <>
              {calView === "day" && renderDayView(currentDate)}
              {calView === "week" && renderWeekView()}
              {calView === "month" && renderMonthView()}
            </>
          )}
        </div>
      )}

      {/* ── Booking ── */}
      {activeTab === "booking" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
            {["events", "availability", "create"].map(v => (
              <button key={v} onClick={() => { if (v === "create") { setEditingEventId(null); setEventForm({ name: "", duration: 30, location: "zoom", slug: "", description: "", before_buffer: 0, after_buffer: 0, min_notice: 60, booking_window: 60, seats: 0, requires_confirmation: false, redirect_url: "", custom_questions: [], timezone: "Europe/London" }); } setBookingView(v); }} style={{ padding: "7px 16px", borderRadius: 6, fontSize: 11, fontFamily: FONT, fontWeight: 600, cursor: "pointer", background: bookingView === v ? COLORS.accent + "18" : COLORS.surface, border: `1px solid ${bookingView === v ? COLORS.accent + "55" : COLORS.border}`, color: bookingView === v ? COLORS.accent : COLORS.textMuted }}>
                {v === "events" ? "Events" : v === "availability" ? "Availability" : "Create Event"}
              </button>
            ))}
            <button onClick={handleSyncEventTypes} disabled={syncingEventTypes} style={{ marginLeft: "auto", padding: "7px 16px", borderRadius: 6, fontSize: 11, fontFamily: FONT, fontWeight: 600, cursor: syncingEventTypes ? "default" : "pointer", background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, opacity: syncingEventTypes ? 0.7 : 1 }}>{syncingEventTypes ? "Syncing..." : "Sync from platforms"}</button>
          </div>

          {bookingView === "events" && eventTypeSyncReport && (
            <div style={{ marginBottom: 16, padding: "14px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
              <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>Last sync</div>
              {visibleSyncPlatforms.length > 0 ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                  {visibleSyncPlatforms.map((info) => {
                    const isError = info.status === "error" || info.status === "partial";
                    const color = isError ? (COLORS.warn || "#f59e0b") : (info.status === "empty" ? COLORS.textDim : (COLORS.green || "#22c55e"));
                    return (
                    <div key={info.platform} style={{ minWidth: 220, padding: "10px 12px", borderRadius: 8, background: color + "12", border: `1px solid ${color}33` }}>
                      <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color, marginBottom: 4 }}>{info.label}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted }}>{getBookingSyncSummaryText(info)}</div>
                      {info.message && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>{info.message}</div>}
                    </div>
                  );
                })}
              </div>
              ) : (
                <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 10 }}>{eventTypeSyncReport.message || "No connected booking platforms to sync."}</div>
              )}
              <div style={{ fontSize: 10, color: COLORS.textDim }}>Detailed sync diagnostics are written to the backend logs.</div>{/*
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                  {syncReportLogs.map((entry, index) => {
                    const label = entry.platform === "calcom" ? "Cal.com" : "Sync";
                    const color = entry.status === "error" ? (COLORS.danger || "#ef4444") : (COLORS.green || "#22c55e");
                    return (
                      <div key={`${entry.platform}-${entry.action}-${entry.name || entry.message || index}`} style={{ fontSize: 11, color: COLORS.textMuted, borderTop: index === 0 ? "none" : `1px solid ${COLORS.border}`, paddingTop: index === 0 ? 0 : 6 }}>
                        <span style={{ color, fontWeight: 700 }}>{label}</span>
                        {" · "}
                        <span style={{ color: COLORS.text }}>{entry.name || entry.action || "sync"}</span>
                        {" · "}
                        <span>{entry.status === "error" ? (entry.message || "Error") : `${entry.action}${entry.booking_url ? " · link saved" : ""}`}</span>
                      </div>
                    );
                  })}
                </div>
              */}
            </div>
          )}

          {bookingView === "events" && eventTypesLoading && (
            <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading event types...</div>
          )}

          {bookingView === "events" && !eventTypesLoading && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {eventTypes.map(evt => {
                const platformLinks = [
                  evt.calcom_id ? { key: "calcom", label: "Cal.com", color: SOURCE_COLORS.calcom, url: evt.calcom_booking_url } : null,
                  null,
                ].filter(Boolean);
                return (
                <div key={evt.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, borderLeft: `4px solid ${evt.color}`, padding: "16px 18px", opacity: evt.active ? 1 : 0.55 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: COLORS.text }}>{evt.name}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, padding: "3px 8px", borderRadius: 4, background: evt.active ? COLORS.green + "18" : COLORS.border, color: evt.active ? COLORS.green : COLORS.textDim }}>{evt.active ? "Active" : "Inactive"}</span>
                      <button onClick={() => handleEditEventType(evt)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: COLORS.textMuted, fontSize: 13 }} title="Edit">&#9998;</button>
                      <button onClick={() => handleDeleteEventType(evt.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: COLORS.danger || "#ef4444", fontSize: 13 }} title="Delete">&#128465;</button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontFamily: FONT, padding: "2px 8px", borderRadius: 4, background: evt.color + "15", color: evt.color, fontWeight: 600 }}>{evt.duration} min</span>
                    <span style={{ fontSize: 10, fontFamily: FONT, padding: "2px 8px", borderRadius: 4, background: COLORS.border, color: COLORS.textMuted }}>{evt.location.replace("_", " ")}</span>
                    <span style={{ fontSize: 10, fontFamily: FONT, padding: "2px 8px", borderRadius: 4, background: COLORS.blue + "15", color: COLORS.blue }}>{evt.bookings} bookings</span>
                    {evt.requires_confirmation && <span style={{ fontSize: 10, fontFamily: FONT, padding: "2px 8px", borderRadius: 4, background: COLORS.warn + "18", color: COLORS.warn }}>Confirmation required</span>}
                    {evt.seats > 0 && <span style={{ fontSize: 10, fontFamily: FONT, padding: "2px 8px", borderRadius: 4, background: COLORS.blue + "15", color: COLORS.blue }}>{evt.seats} seats</span>}
                  </div>
                  {platformLinks.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                      {platformLinks.map((platform) => {
                        const copyKey = `${evt.id}-${platform.key}`;
                        return (
                          <div key={platform.key} style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: platform.color + "15", color: platform.color, fontSize: 10, fontFamily: FONT, fontWeight: 600 }}>
                            <span>{platform.label}</span>
                            {platform.url ? (
                              <button onClick={() => copyBookingLink(copyKey, platform.url)} title={`Copy ${platform.label} booking link`} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: platform.color, fontSize: 11, lineHeight: 1 }}>
                                {copiedBookingLinkKey === copyKey ? "✓" : "⧉"}
                              </button>
                            ) : (
                              <span style={{ color: COLORS.textDim, fontWeight: 500 }}>no link</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(evt.before_buffer > 0 || evt.after_buffer > 0) && (
                    <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, marginBottom: 6 }}>{evt.before_buffer > 0 ? `${evt.before_buffer} min buffer before` : ""}{evt.before_buffer > 0 && evt.after_buffer > 0 ? " · " : ""}{evt.after_buffer > 0 ? `${evt.after_buffer} min after` : ""}</div>
                  )}
                  {platformLinks.length > 0 && platformLinks[0].url && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{platformLinks[0].url}</span>
                      <button onClick={() => copyBookingLink(`${evt.id}-url`, platformLinks[0].url)} title="Copy booking link" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: COLORS.accent, fontSize: 12, lineHeight: 1, flexShrink: 0 }}>
                        {copiedBookingLinkKey === `${evt.id}-url` ? "✓" : "📋"}
                      </button>
                    </div>
                  )}
                </div>
              )})}
              <div onClick={() => { setEditingEventId(null); setEventForm({ name: "", duration: 30, location: "zoom", slug: "", description: "", before_buffer: 0, after_buffer: 0, min_notice: 60, booking_window: 60, seats: 0, requires_confirmation: false, redirect_url: "", custom_questions: [], timezone: "Europe/London" }); setBookingView("create"); }} style={{ background: COLORS.surface, border: `2px dashed ${COLORS.border}`, borderRadius: 10, padding: "28px 18px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>+ Create Event Type</div>
            </div>
          )}

          {bookingView === "create" && (
            <div style={{ maxWidth: 520, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 24 }}>
              <button onClick={() => { setBookingView("events"); setEditingEventId(null); }} style={{ background: "none", border: "none", color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer", marginBottom: 16, padding: 0 }}>{"\u2190"} Back</button>
              <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 18 }}>{editingEventId ? "Update Event Type" : "Create Event Type"}</div>
              {[{ label: "Event Name", key: "name", type: "text" }, { label: "URL Slug", key: "slug", type: "text" }].map(f => (
                <div key={f.key} style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>{f.label}</label>
                  <input value={eventForm[f.key]} onChange={e => setEventForm({ ...eventForm, [f.key]: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Duration</label>
                  <select value={eventForm.duration} onChange={e => setEventForm({ ...eventForm, duration: +e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13 }}>
                    {[15, 30, 45, 60].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Location</label>
                  <select value={eventForm.location} onChange={e => setEventForm({ ...eventForm, location: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13 }}>
                    {[["zoom","Zoom"],["google_meet","Google Meet"],["phone","Phone"],["in_person","In Person"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Description</label>
                <textarea value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} rows={3} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Buffer Times */}
              <div style={{ fontSize: 11, fontFamily: FONT, fontWeight: 700, color: COLORS.text, marginBottom: 8, marginTop: 8 }}>Buffer Times</div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Before event (min)</label>
                  <input type="number" min={0} step={5} value={eventForm.before_buffer} onChange={e => setEventForm({ ...eventForm, before_buffer: +e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>After event (min)</label>
                  <input type="number" min={0} step={5} value={eventForm.after_buffer} onChange={e => setEventForm({ ...eventForm, after_buffer: +e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>

              {/* Booking Rules */}
              <div style={{ fontSize: 11, fontFamily: FONT, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Booking Rules</div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Minimum notice</label>
                  <select value={eventForm.min_notice} onChange={e => setEventForm({ ...eventForm, min_notice: +e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13 }}>
                    {MIN_NOTICE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Booking window</label>
                  <select value={eventForm.booking_window} onChange={e => setEventForm({ ...eventForm, booking_window: +e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13 }}>
                    {BOOKING_WINDOW_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>

              {/* Seats */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Seats (0 = one-on-one)</label>
                <input type="number" min={0} value={eventForm.seats} onChange={e => setEventForm({ ...eventForm, seats: +e.target.value })} style={{ width: 120, padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Confirmation toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div onClick={() => setEventForm({ ...eventForm, requires_confirmation: !eventForm.requires_confirmation })} style={{ width: 36, height: 20, borderRadius: 10, background: eventForm.requires_confirmation ? COLORS.accent : COLORS.border, position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: eventForm.requires_confirmation ? 18 : 2, transition: "left .2s" }} /></div>
                <label style={{ fontSize: 11, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, cursor: "pointer" }} onClick={() => setEventForm({ ...eventForm, requires_confirmation: !eventForm.requires_confirmation })}>Require manual confirmation</label>
              </div>

              {/* Redirect URL */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Redirect URL after booking</label>
                <input value={eventForm.redirect_url} onChange={e => setEventForm({ ...eventForm, redirect_url: e.target.value })} placeholder="https://..." style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Timezone */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Timezone</label>
                <select value={eventForm.timezone} onChange={e => setEventForm({ ...eventForm, timezone: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13 }}>
                  {TIMEZONE_OPTIONS.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
                </select>
              </div>

              {/* Custom Questions */}
              <div style={{ fontSize: 11, fontFamily: FONT, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Custom Questions</div>
              {(eventForm.custom_questions || []).map((q, qi) => (
                <div key={qi} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "8px 10px" }}>
                  <select value={q.type} onChange={e => { const qs = [...eventForm.custom_questions]; qs[qi] = { ...qs[qi], type: e.target.value }; setEventForm({ ...eventForm, custom_questions: qs }); }} style={{ width: 90, padding: "5px 6px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontFamily: FONT, fontSize: 10 }}>
                    {["text", "textarea", "phone", "select", "checkbox"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input value={q.label} onChange={e => { const qs = [...eventForm.custom_questions]; qs[qi] = { ...qs[qi], label: e.target.value }; setEventForm({ ...eventForm, custom_questions: qs }); }} placeholder="Question label" style={{ flex: 1, padding: "5px 8px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 11, outline: "none" }} />
                  <div onClick={() => { const qs = [...eventForm.custom_questions]; qs[qi] = { ...qs[qi], required: !qs[qi].required }; setEventForm({ ...eventForm, custom_questions: qs }); }} style={{ width: 28, height: 16, borderRadius: 8, background: q.required ? COLORS.accent : COLORS.border, position: "relative", cursor: "pointer", flexShrink: 0 }} title={q.required ? "Required" : "Optional"}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: q.required ? 14 : 2, transition: "left .2s" }} /></div>
                  <button onClick={() => { const qs = eventForm.custom_questions.filter((_, j) => j !== qi); setEventForm({ ...eventForm, custom_questions: qs }); }} style={{ background: "none", border: "none", color: COLORS.danger || "#ef4444", cursor: "pointer", fontSize: 14, padding: 2 }}>{"\u00d7"}</button>
                </div>
              ))}
              <button onClick={() => setEventForm({ ...eventForm, custom_questions: [...(eventForm.custom_questions || []), { type: "text", label: "", required: false, options: "" }] })} style={{ padding: "6px 14px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", marginBottom: 18 }}>+ Add Question</button>

              <div style={{ marginTop: 8 }}>
                <button onClick={handleSaveEventType} disabled={savingEvent || !eventForm.name.trim()} style={{ padding: "10px 24px", background: savingEvent ? COLORS.textMuted : COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: savingEvent ? "not-allowed" : "pointer", opacity: (!eventForm.name.trim() || savingEvent) ? 0.5 : 1 }}>{savingEvent ? "Saving..." : editingEventId ? "Update Event Type" : "Save Event Type"}</button>
              </div>
            </div>
          )}

          {bookingView === "availability" && (() => {
            const avail = availability.length > 0 ? availability : ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => ({ day: d, enabled: i < 5, start: "09:00", end: "17:00", slots: [{ start: "09:00", end: "17:00" }] }));
            // Normalize: ensure each day has a slots array
            const days = avail.map(a => ({ ...a, slots: a.slots && a.slots.length > 0 ? a.slots : [{ start: a.start || "09:00", end: a.end || "17:00" }] }));
            return (
            <div>
              {/* Timezone selector */}
              <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textMuted }}>Timezone</label>
                <select value={eventForm.timezone} onChange={e => setEventForm({ ...eventForm, timezone: e.target.value })} style={{ padding: "6px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12 }}>
                  {TIMEZONE_OPTIONS.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
                {days.map((dayData, i) => (
                  <div key={dayData.day} style={{ padding: "12px 18px", borderBottom: i < 6 ? `1px solid ${COLORS.border}` : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <span style={{ width: 90, fontSize: 12, fontFamily: FONT, fontWeight: 600, color: dayData.enabled ? COLORS.text : COLORS.textDim }}>{dayData.day}</span>
                      <div onClick={() => setAvailability(prev => { const arr = prev.length > 0 ? prev : days; return arr.map((s, j) => j === i ? { ...s, enabled: !s.enabled } : s); })} style={{ width: 32, height: 18, borderRadius: 9, background: dayData.enabled ? COLORS.accent : COLORS.border, position: "relative", cursor: "pointer" }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: dayData.enabled ? 16 : 2, transition: "left .2s" }} /></div>
                      {!dayData.enabled && <span style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT_BODY }}>Unavailable</span>}
                    </div>
                    {dayData.enabled && (
                      <div style={{ marginLeft: 104, marginTop: 6 }}>
                        {dayData.slots.map((slot, si) => (
                          <div key={si} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                            <select value={slot.start} onChange={e => setAvailability(prev => { const arr = prev.length > 0 ? prev : days; return arr.map((s, j) => j === i ? { ...s, start: si === 0 ? e.target.value : s.start, slots: s.slots ? s.slots.map((sl, k) => k === si ? { ...sl, start: e.target.value } : sl) : [{ start: e.target.value, end: s.end }] } : s); })} style={{ width: 80, padding: "5px 6px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontFamily: FONT, fontSize: 11 }}>
                              {TIME_SLOTS_30.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <span style={{ color: COLORS.textDim, fontSize: 10 }}>to</span>
                            <select value={slot.end} onChange={e => setAvailability(prev => { const arr = prev.length > 0 ? prev : days; return arr.map((s, j) => j === i ? { ...s, end: si === 0 ? e.target.value : s.end, slots: s.slots ? s.slots.map((sl, k) => k === si ? { ...sl, end: e.target.value } : sl) : [{ start: s.start, end: e.target.value }] } : s); })} style={{ width: 80, padding: "5px 6px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontFamily: FONT, fontSize: 11 }}>
                              {TIME_SLOTS_30.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            {dayData.slots.length > 1 && (
                              <button onClick={() => setAvailability(prev => { const arr = prev.length > 0 ? prev : days; return arr.map((s, j) => j === i ? { ...s, slots: s.slots.filter((_, k) => k !== si) } : s); })} style={{ background: "none", border: "none", color: COLORS.danger || "#ef4444", cursor: "pointer", fontSize: 14, padding: 2 }}>{"\u00d7"}</button>
                            )}
                          </div>
                        ))}
                        <button onClick={() => setAvailability(prev => { const arr = prev.length > 0 ? prev : days; return arr.map((s, j) => j === i ? { ...s, slots: [...(s.slots || [{ start: s.start, end: s.end }]), { start: "13:00", end: "17:00" }] } : s); })} style={{ background: "none", border: "none", color: COLORS.accent, cursor: "pointer", fontFamily: FONT, fontSize: 10, fontWeight: 600, padding: "2px 0" }}>+ Add slot</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button onClick={handleSaveAvailability} disabled={savingAvailability} style={{ padding: "10px 24px", background: savingAvailability ? COLORS.textMuted : COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: savingAvailability ? "not-allowed" : "pointer" }}>{savingAvailability ? "Saving..." : "Save Availability"}</button>
              </div>
            </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

const sampleItem = (text, feedback) => (typeof text === "string" ? { text, feedback: feedback ?? null } : { text: text?.text || "", feedback: text?.feedback ?? null });

const DEFAULT_AI_SDR = {
  active: false,
  training: { sentMessages: true, scripts: true, brandVoice: true, starredThreads: false },
  permissions: { positiveReply: true, answerQuestions: true, bookMeetings: true, followUp: true, handleObjections: false, negotiatePricing: false },
  responseWindow: "5 min",
  workingHours: "9am–6pm Mon–Fri",
  approvalMode: true,
  customGuidelines: "",
  sampleResponses: [
    sampleItem("Hey Sarah, great to hear the team is interested! I've got availability Thursday at 2pm or Friday at 10am — either work for a quick 15-min intro?"),
    sampleItem("Thanks for the kind words! We just wrapped up a similar project with a Series B SaaS company. Happy to walk you through it — do you have 15 min this week?"),
  ],
};

function UniboxView({ projectId, onNavigate, initialConversationId, onConversationLoaded }) {
  const UNIBOX_STAGES = [
    { key: "new", label: "New", color: COLORS.blue },
    { key: "contacted", label: "Contacted", color: "#8B5CF6" },
    { key: "replied", label: "Replied", color: COLORS.accent },
    { key: "meeting_booked", label: "Meeting Booked", color: "#F59E0B" },
    { key: "proposal_sent", label: "Proposal Sent", color: "#EC4899" },
    { key: "won", label: "Won", color: COLORS.green },
    { key: "lost", label: "Lost", color: COLORS.danger },
  ];
  const stageColor = (s) => UNIBOX_STAGES.find(st => st.key === s)?.color || COLORS.textDim;
  const stageLabel = (s) => UNIBOX_STAGES.find(st => st.key === s)?.label || s || "New";

  const [uniboxTab, setUniboxTab] = useState("inbox");
  const [sdrActive, setSdrActive] = useState(DEFAULT_AI_SDR.active);
  const [sdrPerms, setSdrPerms] = useState(DEFAULT_AI_SDR.permissions);
  const [sdrTraining, setSdrTraining] = useState(DEFAULT_AI_SDR.training);
  const [sdrResponseWindow, setSdrResponseWindow] = useState(DEFAULT_AI_SDR.responseWindow);
  const [sdrApprovalMode, setSdrApprovalMode] = useState(DEFAULT_AI_SDR.approvalMode);
  const [sdrCustomGuidelines, setSdrCustomGuidelines] = useState(DEFAULT_AI_SDR.customGuidelines);
  const [sdrSampleResponses, setSdrSampleResponses] = useState([...DEFAULT_AI_SDR.sampleResponses]);
  const [sdrGenerating, setSdrGenerating] = useState(false);
  const [sdrWorkingHours, setSdrWorkingHours] = useState(DEFAULT_AI_SDR.workingHours);
  const [sdrSettingsLoaded, setSdrSettingsLoaded] = useState(false);

  const sdrParams = { projectId: projectId || "" };

  useEffect(() => {
    async function load() {
      try {
        const res = await api.settings.get("ai_sdr", sdrParams);
        if (res?.settings) {
          const s = res.settings;
          setSdrActive(s.active ?? DEFAULT_AI_SDR.active);
          setSdrTraining({ ...DEFAULT_AI_SDR.training, ...s.training });
          setSdrPerms({ ...DEFAULT_AI_SDR.permissions, ...s.permissions });
          setSdrResponseWindow(s.responseWindow ?? DEFAULT_AI_SDR.responseWindow);
          setSdrApprovalMode(s.approvalMode ?? DEFAULT_AI_SDR.approvalMode);
          setSdrCustomGuidelines(s.customGuidelines ?? "");
          setSdrSampleResponses(Array.isArray(s.sampleResponses)
            ? s.sampleResponses.map(x => sampleItem(x))
            : [...DEFAULT_AI_SDR.sampleResponses]);
          setSdrWorkingHours(s.workingHours ?? DEFAULT_AI_SDR.workingHours);
        } else {
          await api.settings.save("ai_sdr", DEFAULT_AI_SDR, sdrParams);
        }
      } catch (err) {
        console.error("AI SDR settings load error:", err);
      } finally {
        setSdrSettingsLoaded(true);
      }
    }
    load();
  }, [projectId]);

  const saveSdrSettings = async (updates) => {
    const payload = {
      active: sdrActive,
      training: sdrTraining,
      permissions: sdrPerms,
      responseWindow: sdrResponseWindow,
      approvalMode: sdrApprovalMode,
      customGuidelines: sdrCustomGuidelines,
      sampleResponses: sdrSampleResponses,
      workingHours: sdrWorkingHours,
      ...updates,
    };
    try {
      await api.settings.save("ai_sdr", payload, sdrParams);
    } catch (err) {
      console.error("AI SDR settings save error:", err);
    }
  };

  const [sdrActivity, setSdrActivity] = useState([]);
  useEffect(() => {
    api.aiSdr.activity.list({ limit: 10 }).then(data => {
      setSdrActivity(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'syncing' | { total_synced, platforms }
  const [platformStatus, setPlatformStatus] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(0); // triggers message refresh after sync

  // Load connected platform status
  useEffect(() => {
    api.conversations.status().then(s => setPlatformStatus(s)).catch(() => {});
  }, []);

  // Sync helper — fetches from external platforms, refreshes local list
  const syncingRef = useRef(false);
  const runSync = useCallback((isInitial) => {
    if (syncingRef.current) return; // prevent overlapping syncs
    syncingRef.current = true;
    if (isInitial) setSyncStatus('syncing');
    api.conversations.sync().then(result => {
      setSyncStatus(result);
      api.conversations.fixLinks().catch(() => {});
      // Always refresh list after sync (catches new inbound messages)
      api.conversations.list().then(data => {
        const convos = Array.isArray(data) ? data : data.conversations || [];
        setConversations(convos);
        if (convos.length > 0 && !selectedConvo) setSelectedConvo(prev => prev || convos[0]);
        setLastSyncAt(Date.now()); // trigger message refresh for open thread
      }).catch(() => {});
    }).catch(() => { if (isInitial) setSyncStatus(null); })
      .finally(() => { syncingRef.current = false; });
  }, []);

  // Load conversations on mount + auto-sync every 30s while Unibox is open
  useEffect(() => {
    // 1. Load local conversations immediately
    api.conversations.list().then(data => {
      const convos = Array.isArray(data) ? data : data.conversations || [];
      setConversations(convos);
      // Auto-select conversation from CRM navigation, or first in list
      if (initialConversationId) {
        const target = convos.find(c => c.id === initialConversationId);
        if (target) { setSelectedConvo(target); if (onConversationLoaded) onConversationLoaded(); }
        else if (convos.length > 0 && !selectedConvo) setSelectedConvo(convos[0]);
      } else if (convos.length > 0 && !selectedConvo) setSelectedConvo(convos[0]);
    }).catch(() => {});

    // 2. Initial sync from external platforms
    runSync(true);

    // 3. Auto-sync every 30 seconds while this tab is open
    const intervalId = setInterval(() => runSync(false), 30000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (selectedConvo?.id) {
      api.messages.list(selectedConvo.id).then(data => {
        const msgs = Array.isArray(data) ? data : data.messages || [];
        if (msgs.length > 0) {
          setThreadMessages(msgs);
        } else {
          // No messages in DB — fetch full thread from platform API on demand
          api.conversations.thread(selectedConvo.id).then(threadData => {
            const threadMsgs = Array.isArray(threadData) ? threadData : threadData.messages || [];
            setThreadMessages(threadMsgs);
          }).catch(() => setThreadMessages([]));
        }
      }).catch(() => setThreadMessages([]));
    }
  }, [selectedConvo?.id, lastSyncAt]);

  const threadScrollRef = useRef(null);
  const prevConvoIdRef = useRef(null);
  // When a conversation is opened OR new messages arrive, scroll to the
  // bottom so the newest message is visible. On conversation switch we jump
  // instantly; on new-message-in-same-thread we scroll smoothly (and only if
  // the user was already near the bottom, so they don't get yanked while
  // reading older messages up above).
  useEffect(() => {
    const el = threadScrollRef.current;
    if (!el || !selectedConvo?.id) return;
    const isConvoSwitch = prevConvoIdRef.current !== selectedConvo.id;
    prevConvoIdRef.current = selectedConvo.id;
    const wasNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (!isConvoSwitch && !wasNearBottom) return; // user scrolled up — don't interrupt
    const id = requestAnimationFrame(() => {
      const node = threadScrollRef.current;
      if (!node) return;
      if (isConvoSwitch) {
        node.scrollTop = node.scrollHeight; // instant on open
      } else {
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [threadMessages, selectedConvo?.id]);

  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [aiAssist, setAiAssist] = useState(false);
  const [aiGoal, setAiGoal] = useState("Book a meeting");
  const [aiDrafts, setAiDrafts] = useState(null);
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [filter, setFilter] = useState("replies");

  const THREAD = threadMessages.length > 0
    // Sort chronologically (oldest top → newest bottom) so the reply box sits
    // next to the newest message and the scroll-to-bottom effect below lands
    // the viewport on the most recent activity. Fall back to sent_at string
    // compare when the timestamps are ISO strings from the API.
    ? [...threadMessages]
        .sort((a, b) => String(a.sent_at || a.created_at || '').localeCompare(String(b.sent_at || b.created_at || '')))
        .map(m => ({ from: m.direction === 'outbound' ? 'You' : (selectedConvo?.name || 'Contact'), time: m.sent_at || m.created_at || '', content: m.body || m.content || '', direction: m.direction }))
    : (selectedConvo?.preview || selectedConvo?.last_inbound_preview)
      ? [{ from: selectedConvo?.name || 'Contact', time: selectedConvo?.last_inbound_at || selectedConvo?.time || '', content: selectedConvo?.last_inbound_preview || selectedConvo?.preview || '', direction: 'inbound' }]
      : [{ from: 'System', time: '', content: 'No messages synced yet for this conversation.', direction: 'inbound' }];

  const generateAIDrafts = async () => {
    setGeneratingDrafts(true);
    try {
      const result = await api.aiSdr.generateSample({ 
        conversation_context: selectedConvo?.preview || '',
        contact_name: selectedConvo?.name || '',
        company: selectedConvo?.company || '',
        goal: aiGoal,
      });
      if (result?.samples && Array.isArray(result.samples)) {
        setAiDrafts(result.samples.map((s, i) => ({ label: s.label || `Option ${i+1}`, text: s.text || s })));
      } else {
        setAiDrafts([{ label: "AI Draft", text: result?.text || result?.sample || "Could not generate draft. Check your Anthropic API key in Settings." }]);
      }
    } catch (err) {
      setAiDrafts([{ label: "Error", text: `Failed to generate: ${err.message}. Ensure Anthropic API key is configured in Settings > Integrations.` }]);
    }
    setGeneratingDrafts(false);
  };

  const filteredConvos = (() => {
    let list = filter === "all" ? conversations
      : filter === "unread" ? conversations.filter(c => c.unread)
      : filter === "replies" ? conversations.filter(c => c.has_inbound)
      : conversations.filter(c => c.channel === filter);
    if (filter === "replies") list = [...list].sort((a, b) => (b.last_inbound_at || '').localeCompare(a.last_inbound_at || ''));
    return list;
  })();

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0, padding: "0 20px", background: COLORS.surface }}>
        {[{ key: "inbox", label: "📥 Inbox" }].map(tab => (
          <button key={tab.key} onClick={() => setUniboxTab(tab.key)} style={{ padding: "12px 20px", background: "transparent", border: "none", borderBottom: uniboxTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent", color: uniboxTab === tab.key ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* AI SDR Tab */}
      {uniboxTab === "aisdr" && (
        <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: 0 }}>AI <span style={{ color: COLORS.accent }}>SDR</span></h2>
              <p style={{ color: COLORS.textMuted, margin: "4px 0 0", fontSize: 12 }}>Train and activate your AI sales development rep</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: sdrActive ? COLORS.green : COLORS.textDim }}>{sdrActive ? "Active" : "Inactive"}</span>
              <div onClick={() => { const v = !sdrActive; setSdrActive(v); saveSdrSettings({ active: v }); }} style={{ width: 52, height: 28, borderRadius: 14, cursor: "pointer", background: sdrActive ? COLORS.green : COLORS.borderActive, position: "relative", transition: "background 0.25s" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: sdrActive ? 27 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>🎓 Training Sources</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { key: "sentMessages", label: "Learn from my sent messages", desc: "Analyses your writing style, tone, and patterns" },
                  { key: "scripts", label: "Learn from my scripts", desc: "Uses saved sales scripts for objection handling" },
                  { key: "brandVoice", label: "Learn from brand voice", desc: "Uses brand voice profile from Settings" },
                  { key: "starredThreads", label: "Learn from starred threads", desc: "Star successful conversations to teach patterns" },
                ].map(src => (
                  <div key={src.key} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div><div style={{ fontSize: 12, fontWeight: 600 }}>{src.label}</div><div style={{ fontSize: 10, color: COLORS.textDim }}>{src.desc}</div></div>
                    <div onClick={() => { const next = { ...sdrTraining, [src.key]: !sdrTraining[src.key] }; setSdrTraining(next); saveSdrSettings({ training: next }); }} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: sdrTraining[src.key] ? COLORS.accent : COLORS.borderActive, position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: 10 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: sdrTraining[src.key] ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>CUSTOM GUIDELINES</div>
                <textarea value={sdrCustomGuidelines} onChange={e => setSdrCustomGuidelines(e.target.value)} onBlur={e => saveSdrSettings({ customGuidelines: e.target.value })} placeholder="e.g. 'Always mention case studies', 'Book meetings Tue/Thu only', 'Never discount on first call'" rows={3} style={{ width: "100%", padding: "10px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12, resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }} />
              </div>
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 8 }}>SAMPLE RESPONSES</div>
                {sdrSampleResponses.map((sample, si) => (
                  <div key={si} style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5, marginBottom: 6 }}>{typeof sample === "string" ? sample : sample.text}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={async () => { const next = sdrSampleResponses.map((s, i) => i === si ? sampleItem(typeof s === "string" ? s : s.text, "good") : sampleItem(s)); setSdrSampleResponses(next); saveSdrSettings({ sampleResponses: next }); }} style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${COLORS.green}33`, background: COLORS.green + "10", color: COLORS.green, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>👍 Good</button>
                      <button onClick={async () => {
                        setSdrGenerating(true);
                        try {
                          const t = typeof sample === "string" ? sample : sample.text;
                          const { text } = await api.aiSdr.generateSample({ guidelines: sdrCustomGuidelines, refineText: t });
                          if (text) { const next = sdrSampleResponses.map((s, i) => i === si ? sampleItem(text, "refine") : sampleItem(s)); setSdrSampleResponses(next); saveSdrSettings({ sampleResponses: next }); }
                        } catch (err) { alert(err.message || "Failed to refine. Connect Anthropic in Settings > Integrations."); }
                        setSdrGenerating(false);
                      }} disabled={sdrGenerating} style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${COLORS.danger}33`, background: COLORS.danger + "10", color: COLORS.danger, fontSize: 10, fontWeight: 600, cursor: sdrGenerating ? "not-allowed" : "pointer", fontFamily: FONT }}>{sdrGenerating ? "…" : "👎 Refine"}</button>
                    </div>
                  </div>
                ))}
                <button onClick={async () => {
                  setSdrGenerating(true);
                  try {
                    const { text } = await api.aiSdr.generateSample({ guidelines: sdrCustomGuidelines });
                    if (text) { const next = [...sdrSampleResponses, sampleItem(text)]; setSdrSampleResponses(next); saveSdrSettings({ sampleResponses: next }); }
                  } catch (err) { alert(err.message || "Failed to generate. Connect Anthropic in Settings > Integrations."); }
                  setSdrGenerating(false);
                }} disabled={sdrGenerating} style={{ marginTop: 6, padding: "8px 14px", borderRadius: 6, border: `1px dashed ${COLORS.border}`, background: "transparent", color: COLORS.accent, fontSize: 11, fontWeight: 600, cursor: sdrGenerating ? "not-allowed" : "pointer", fontFamily: FONT }}>{sdrGenerating ? "Generating…" : "+ Generate sample"}</button>
              </div>
            </div>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>⚡ Permissions</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                {[
                  { key: "positiveReply", label: "Reply to positive responses" },
                  { key: "answerQuestions", label: "Answer product/service questions" },
                  { key: "bookMeetings", label: "Book meetings (with calendar link)" },
                  { key: "followUp", label: "Send follow-ups after no response (3 days)" },
                  { key: "handleObjections", label: "Handle objections" },
                  { key: "negotiatePricing", label: "Negotiate pricing" },
                ].map(perm => (
                  <div key={perm.key} style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12, color: COLORS.text }}>{perm.label}</span>
                    <div onClick={() => { const next = { ...sdrPerms, [perm.key]: !sdrPerms[perm.key] }; setSdrPerms(next); saveSdrSettings({ permissions: next }); }} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: sdrPerms[perm.key] ? COLORS.accent : COLORS.borderActive, position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: sdrPerms[perm.key] ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                <div><div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 6 }}>RESPONSE WINDOW</div><select value={sdrResponseWindow} onChange={e => { const v = e.target.value; setSdrResponseWindow(v); saveSdrSettings({ responseWindow: v }); }} style={{ width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, fontFamily: FONT_BODY, color: COLORS.text, cursor: "pointer" }}><option>5 min</option><option>30 min</option><option>1 hour</option></select></div>
                <div><div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 6 }}>WORKING HOURS</div><div style={{ padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, color: COLORS.text }}>{sdrWorkingHours}</div></div>
              </div>
              <div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div><div style={{ fontSize: 12, fontWeight: 600 }}>Review before sending</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Drafts go to approval queue</div></div>
                <div onClick={() => { const v = !sdrApprovalMode; setSdrApprovalMode(v); saveSdrSettings({ approvalMode: v }); }} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: sdrApprovalMode ? COLORS.accent : COLORS.borderActive, position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: 10 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: sdrApprovalMode ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </div>
              </div>
              <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>📋 Recent AI SDR Activity</div>
              {(sdrActivity.length > 0 ? sdrActivity : [
                { action: "Replied to Sarah Chen", detail: "Booked meeting — Thu 2pm", activity_type: "reply", created_at: new Date(Date.now() - 2*3600000).toISOString() },
                { action: "Follow-up sent to Tom Bradley", detail: "3 days no response", activity_type: "follow_up", created_at: new Date(Date.now() - 4*3600000).toISOString() },
                { action: "Flagged for review", detail: "Marcus Webb asked about pricing", activity_type: "flagged", created_at: new Date(Date.now() - 6*3600000).toISOString() },
                { action: "Replied to David Kim", detail: "Shared case study", activity_type: "reply", created_at: new Date(Date.now() - 24*3600000).toISOString() },
              ]).map((a, i) => {
                const actColor = { reply: COLORS.green, follow_up: COLORS.blue, flagged: COLORS.warn, meeting: COLORS.green }[a.activity_type] || COLORS.textDim;
                const mins = Math.floor((Date.now() - new Date(a.created_at).getTime()) / 60000);
                const timeAgo = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins/60)}h ago` : `${Math.floor(mins/1440)}d ago`;
                return (
                  <div key={a.id || i} style={{ padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, borderLeft: `3px solid ${actColor}`, marginBottom: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 11, fontWeight: 600 }}>{a.action}</span><span style={{ fontSize: 9, color: COLORS.textDim }}>{timeAgo}</span></div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{a.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Inbox Tab */}
      {uniboxTab === "inbox" && (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Left — Conversation List */}
      <div style={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>📥 Unibox</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {syncStatus === 'syncing' && <span style={{ fontSize: 9, color: COLORS.accent, fontWeight: 600 }}>Syncing...</span>}
              {syncStatus && syncStatus !== 'syncing' && syncStatus.total_synced > 0 && <span style={{ fontSize: 9, color: COLORS.green, fontWeight: 600 }}>{syncStatus.total_synced} synced</span>}
              {syncStatus && syncStatus !== 'syncing' && syncStatus.total_synced === 0 && <span style={{ fontSize: 9, color: COLORS.textDim }}>Up to date</span>}
              {syncStatus !== 'syncing' && <span style={{ fontSize: 8, color: COLORS.textDim, fontFamily: FONT }}>auto-sync 30s</span>}
              {syncStatus !== 'syncing' && <button onClick={() => runSync(true)} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>↻ Sync Now</button>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {[{ key: "replies", label: "📩 Replies" }, { key: "unread", label: "Unread" }, { key: "email", label: "📧 Email" }, { key: "linkedin", label: "💼 LinkedIn" }].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${filter === f.key ? COLORS.accent + "44" : COLORS.border}`, background: filter === f.key ? COLORS.accentBg : "transparent", color: filter === f.key ? COLORS.accent : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{f.label}</button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {filteredConvos.map(c => (
            <div key={c.id} onClick={() => { setSelectedConvo(c); setAiDrafts(null); setReplyText(""); setConversations(prev => prev.map(x => x.id === c.id ? { ...x, unread: false } : x)); if (c.unread) api.conversations.update(c.id, { unread: false }).catch(() => {}); }} style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: selectedConvo?.id === c.id ? COLORS.accentBg : "transparent" }}
              onMouseEnter={e => { if (selectedConvo?.id !== c.id) e.currentTarget.style.background = COLORS.surface; }} onMouseLeave={e => { if (selectedConvo?.id !== c.id) e.currentTarget.style.background = "transparent"; }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {c.unread && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent }} />}
                  <span style={{ fontWeight: c.unread ? 700 : 500, fontSize: 13 }}>{c.name}</span>
                  <span style={{ fontSize: 10 }}>{c.channel === "email" ? "📧" : "💼"}</span>
                  {c.source_platform && c.source_platform !== 'local' && <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, background: c.source_platform === 'instantly' ? '#FF6B3520' : c.source_platform === 'aimfox' ? '#F5820D20' : '#0A66C220', color: c.source_platform === 'instantly' ? '#FF6B35' : c.source_platform === 'aimfox' ? '#F5820D' : '#0A66C2', fontWeight: 600 }}>{{ instantly: 'Instantly', heyreach: 'HeyReach', aimfox: 'Aimfox' }[c.source_platform] || c.source_platform}</span>}
                </div>
                <span style={{ fontSize: 9, color: COLORS.textDim }}>{(filter === "replies" && c.last_inbound_at) ? new Date(c.last_inbound_at).toLocaleDateString() : c.time}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <span style={{ fontSize: 11, color: COLORS.textDim }}>{c.company}</span>
                <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 600, fontFamily: FONT, background: stageColor(c.stage) + "15", color: stageColor(c.stage) }}>{stageLabel(c.stage)}</span>
              </div>
              <div style={{ fontSize: 11, color: COLORS.text, fontWeight: c.unread ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(filter === "replies" && c.last_inbound_preview) ? c.last_inbound_preview : c.preview}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Middle — Thread */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedConvo?.subject}</div>
            <div style={{ fontSize: 11, color: COLORS.textDim }}>{selectedConvo?.name} · {selectedConvo?.company} · {selectedConvo?.channel === "email" ? "📧 Email" : "💼 LinkedIn"}</div>
          </div>
          <select value={selectedConvo?.stage || "new"} onChange={async (e) => {
            const newStage = e.target.value;
            try {
              await api.conversations.update(selectedConvo.id, { stage: newStage });
              setSelectedConvo(prev => prev ? { ...prev, stage: newStage } : null);
              setConversations(prev => prev.map(c => c.id === selectedConvo.id ? { ...c, stage: newStage } : c));
            } catch (err) { console.error("Stage update failed:", err); }
          }} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 600, background: stageColor(selectedConvo?.stage) + "15", color: stageColor(selectedConvo?.stage), border: `1px solid ${stageColor(selectedConvo?.stage)}33`, cursor: "pointer", outline: "none" }}>
            {UNIBOX_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        {/* Thread metadata header */}
        {selectedConvo && (
          <div style={{ padding: "10px 20px", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 11, color: COLORS.textDim, display: "flex", gap: 16, flexWrap: "wrap" }}>
            {selectedConvo.email && <span>📧 {selectedConvo.email}</span>}
            {selectedConvo.linkedin_url && <span>💼 {selectedConvo.linkedin_url}</span>}
            {selectedConvo.source_platform && selectedConvo.source_platform !== 'local' && <span style={{ fontWeight: 600, color: selectedConvo.source_platform === 'instantly' ? '#FF6B35' : '#0A66C2' }}>via {{ instantly: 'Instantly', heyreach: 'HeyReach', aimfox: 'Aimfox' }[selectedConvo.source_platform]}</span>}
            <span>{THREAD.length} message{THREAD.length !== 1 ? 's' : ''}</span>
          </div>
        )}
        <div ref={threadScrollRef} style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {THREAD.map((msg, i) => {
            const isInbound = msg.direction === 'inbound';
            const isFirst = i === 0 && isInbound;
            return (
            <div key={i} style={{ padding: isFirst ? "18px 20px" : "14px 18px", background: isInbound ? COLORS.surface : COLORS.accent + "08", border: `1px solid ${isInbound ? COLORS.border : COLORS.accent + "22"}`, borderRadius: 10, borderLeft: isFirst ? `3px solid ${COLORS.green}` : undefined }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 12, color: isInbound ? COLORS.text : COLORS.accent }}>{msg.from}</span>
                  {isFirst && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: COLORS.green + "18", color: COLORS.green, fontWeight: 600 }}>Reply</span>}
                  {!isInbound && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: COLORS.accent + "15", color: COLORS.accent, fontWeight: 600 }}>Sent</span>}
                </div>
                <span style={{ fontSize: 10, color: COLORS.textDim }}>{msg.time ? new Date(msg.time).toLocaleString() : ''}</span>
              </div>
              <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.content}</div>
            </div>
            );
          })}
        </div>
        {/* Reply Box */}
        <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "12px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textDim }}>Reply</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {aiAssist && (
                <select value={aiGoal} onChange={e => setAiGoal(e.target.value)} style={{ padding: "4px 8px", borderRadius: 4, border: `1px solid ${COLORS.border}`, fontSize: 10, fontFamily: FONT, color: COLORS.text, background: COLORS.surface, cursor: "pointer" }}>
                  <option>Book a meeting</option><option>Qualify interest</option><option>Handle objection</option><option>Share case study</option>
                </select>
              )}
              <div onClick={() => { setAiAssist(!aiAssist); setAiDrafts(null); }} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "4px 10px", borderRadius: 6, background: aiAssist ? COLORS.accent + "15" : "transparent", border: `1px solid ${aiAssist ? COLORS.accent + "44" : COLORS.border}` }}>
                <span style={{ fontSize: 11 }}>✨</span>
                <span style={{ fontSize: 10, fontWeight: 600, fontFamily: FONT, color: aiAssist ? COLORS.accent : COLORS.textDim }}>AI Assist</span>
              </div>
            </div>
          </div>
          {aiAssist && !aiDrafts && (
            <button onClick={generateAIDrafts} disabled={generatingDrafts} style={{ width: "100%", padding: "10px", marginBottom: 8, background: COLORS.accent + "10", border: `1px dashed ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: generatingDrafts ? "default" : "pointer" }}>{generatingDrafts ? "Generating drafts..." : `✨ Generate AI responses — Goal: ${aiGoal}`}</button>
          )}
          {aiDrafts && (
            <div style={{ marginBottom: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {aiDrafts.map((draft, i) => (
                <div key={i} onClick={() => { setReplyText(draft.text); setAiDrafts(null); }} style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "44"} onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
                  <div style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.accent, marginBottom: 4 }}>{draft.label.toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5, whiteSpace: "pre-line" }}>{draft.text}</div>
                </div>
              ))}
            </div>
          )}
          <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." rows={3} style={{ width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.6 }} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button
              disabled={sendingReply || !replyText.trim() || !selectedConvo?.id}
              onClick={async () => {
                if (sendingReply || !replyText.trim() || !selectedConvo?.id) return;
                const bodyToSend = replyText.trim();
                setSendingReply(true);
                try {
                  const msg = await api.messages.create({
                    conversation_id: selectedConvo.id,
                    direction: 'outbound',
                    sender_name: 'You',
                    body: bodyToSend,
                    ai_drafted: false,
                  });
                  setThreadMessages(prev => [...prev, msg]);
                  setReplyText("");
                  // Show platform delivery status
                  if (msg._sendWarning) {
                    alert(`⚠️ ${msg._sendWarning}`);
                  } else if (msg._sentVia) {
                    console.log(`Reply sent via ${msg._sentVia}`);
                  }
                  // Backend auto-advances stage; update UI to reflect "contacted" if still "new"
                  const newStage = selectedConvo.stage === "new" ? "contacted" : selectedConvo.stage;
                  setConversations(prev => prev.map(c =>
                    c.id === selectedConvo.id ? { ...c, preview: bodyToSend, time: new Date().toISOString(), stage: newStage } : c
                  ));
                  if (newStage !== selectedConvo.stage) setSelectedConvo(prev => prev ? { ...prev, stage: newStage } : null);
                } catch (err) {
                  console.error('Send reply failed:', err);
                  alert(`Failed to send reply: ${err.message || 'Unknown error'}`);
                } finally {
                  setSendingReply(false);
                }
              }}
              style={{
                padding: "8px 20px",
                background: sendingReply
                  ? COLORS.border
                  : (replyText.trim() ? COLORS.accent : COLORS.border),
                color: sendingReply
                  ? COLORS.textDim
                  : (replyText.trim() ? COLORS.bg : COLORS.textDim),
                border: "none",
                borderRadius: 6,
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                cursor: sendingReply
                  ? "wait"
                  : (replyText.trim() ? "pointer" : "default"),
                opacity: sendingReply ? 0.8 : 1,
                minWidth: 92,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "background 0.15s, opacity 0.15s",
              }}
            >
              {sendingReply && (
                <span
                  aria-hidden="true"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    border: `2px solid ${COLORS.textDim}`,
                    borderTopColor: "transparent",
                    display: "inline-block",
                    animation: "ux-spin 0.9s linear infinite",
                  }}
                />
              )}
              {sendingReply ? "Sending…" : "Send Reply"}
            </button>
          </div>
        </div>
      </div>

      {/* Right — Lead Context */}
      <div style={{ width: 260, flexShrink: 0, borderLeft: `1px solid ${COLORS.border}`, overflow: "auto", background: COLORS.surface }}>
        <div style={{ padding: "16px 14px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: COLORS.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: FONT, color: COLORS.accent }}>{selectedConvo?.name?.split(" ").map(n => n[0]).join("")}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{selectedConvo?.name}</div>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>{selectedConvo?.company}</div>
            </div>
          </div>
          <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: stageColor(selectedConvo?.stage) + "15", color: stageColor(selectedConvo?.stage) }}>{stageLabel(selectedConvo?.stage)}</span>
        </div>
        <div style={{ padding: "12px 14px" }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>PIPELINE STAGE</div>
            <select value={selectedConvo?.stage || "new"} onChange={async (e) => {
              const newStage = e.target.value;
              try {
                await api.conversations.update(selectedConvo.id, { stage: newStage });
                setSelectedConvo(prev => prev ? { ...prev, stage: newStage } : null);
                setConversations(prev => prev.map(c => c.id === selectedConvo.id ? { ...c, stage: newStage } : c));
              } catch (err) { console.error("Stage update failed:", err); }
            }} style={{ width: "100%", padding: "6px 10px", borderRadius: 6, fontSize: 11, fontFamily: FONT, fontWeight: 600, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, cursor: "pointer", outline: "none" }}>
              {UNIBOX_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>CONTACT</div>
            {selectedConvo?.email ? (
              <div style={{ fontSize: 11, marginBottom: 2 }}><a href={`mailto:${selectedConvo.email}`} style={{ color: COLORS.text, textDecoration: "none" }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>✉️ {selectedConvo.email}</a></div>
            ) : (
              <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 2 }}>✉️ No email</div>
            )}
            {selectedConvo?.linkedin_url ? (
              <div style={{ fontSize: 11 }}><a href={selectedConvo.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.text, textDecoration: "none" }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>💼 {selectedConvo.linkedin_url.replace(/^https?:\/\/(www\.)?/, "")}</a></div>
            ) : selectedConvo?.channel === "linkedin" ? (
              <div style={{ fontSize: 11, color: COLORS.textDim }}>💼 LinkedIn (no profile URL)</div>
            ) : null}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>CHANNEL</div>
            <div style={{ fontSize: 11, color: COLORS.text }}>{selectedConvo?.channel === "email" ? "📧 Email thread" : "💼 LinkedIn message"}</div>
          </div>
          <button onClick={() => { if (typeof onNavigate === "function") onNavigate("crm"); }} style={{ width: "100%", padding: "10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", color: COLORS.textMuted }}>Open in CRM</button>
        </div>
      </div>
    </div>
      )}
    </div>
  );
}

function ImplementationView({ project }) {
  const [expandedPhase, setExpandedPhase] = useState(0);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const DEFAULT_PHASES = [
    {
      title: "Phase 1: Foundation", timeline: "Month 1-2", status: "not_started", color: "#eab308", cost: "£35-45K", sort_order: 0,
      tasks: [
        { id: "t1", name: "Lease tracking system — vendor selection", status: "not_started", assignee: "Andrew", dueDate: "Feb 14", priority: "high" },
        { id: "t2", name: "Lease tracking system — implementation", status: "not_started", assignee: "Andrew", dueDate: "Feb 28", priority: "high" },
        { id: "t3", name: "Data consolidation — map existing sources", status: "not_started", assignee: "Sarah Mitchell", dueDate: "Feb 21", priority: "high" },
        { id: "t4", name: "Data consolidation — migration plan", status: "not_started", assignee: "Andrew", dueDate: "Mar 7", priority: "medium" },
        { id: "t5", name: "Core integrations — CRM + accounting sync", status: "not_started", assignee: "TBD", dueDate: "Mar 14", priority: "medium" },
        { id: "t6", name: "Staff training — lease tracking module", status: "not_started", assignee: "Mike Thompson", dueDate: "Mar 21", priority: "low" },
      ],
    },
    {
      title: "Phase 2: Unlock", timeline: "Month 3-4", status: "not_started", color: "#3b82f6", cost: "£40-55K", sort_order: 1,
      tasks: [
        { id: "t7", name: "Director dashboard — requirements gathering", status: "not_started", assignee: "James Richardson", dueDate: "Apr 1", priority: "high" },
        { id: "t8", name: "Director dashboard — build & deploy", status: "not_started", assignee: "Andrew", dueDate: "Apr 21", priority: "high" },
        { id: "t9", name: "Knowledge base — content structure", status: "not_started", assignee: "Mike Thompson", dueDate: "Apr 7", priority: "medium" },
        { id: "t10", name: "Knowledge base — populate initial content", status: "not_started", assignee: "All", dueDate: "Apr 28", priority: "medium" },
        { id: "t11", name: "Process automation — identify top 5 workflows", status: "not_started", assignee: "Andrew", dueDate: "May 1", priority: "medium" },
      ],
    },
    {
      title: "Phase 3: Scale", timeline: "Month 5-6", status: "not_started", color: "#8b5cf6", cost: "£50-70K", sort_order: 2,
      tasks: [
        { id: "t12", name: "AI contract analysis — model training", status: "not_started", assignee: "Andrew", dueDate: "May 15", priority: "high" },
        { id: "t13", name: "AI contract analysis — integration with lease system", status: "not_started", assignee: "Andrew", dueDate: "Jun 1", priority: "high" },
        { id: "t14", name: "Predictive insights — dashboard module", status: "not_started", assignee: "Andrew", dueDate: "Jun 15", priority: "medium" },
        { id: "t15", name: "Portfolio scaling tools — automation suite", status: "not_started", assignee: "TBD", dueDate: "Jun 28", priority: "medium" },
      ],
    },
  ];

  const projectId = project ? String(project.id) : null;

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    api.implementation.list({ project_id: projectId }).then(async (data) => {
      const rows = Array.isArray(data) ? data : [];
      if (rows.length > 0) {
        setPhases(rows.map(r => ({
          ...r,
          tasks: Array.isArray(r.tasks) ? r.tasks : (typeof r.tasks === 'string' ? JSON.parse(r.tasks) : []),
        })));
      } else {
        try {
          const created = await api.implementation.bulkCreate({ project_id: projectId, phases: DEFAULT_PHASES });
          const arr = Array.isArray(created) ? created : [];
          setPhases(arr.map(r => ({
            ...r,
            tasks: Array.isArray(r.tasks) ? r.tasks : (typeof r.tasks === 'string' ? JSON.parse(r.tasks) : []),
          })));
        } catch (seedErr) {
          console.error('Failed to seed default phases:', seedErr);
          setPhases(DEFAULT_PHASES.map((p, i) => ({ ...p, id: `local_${i}`, name: p.title })));
        }
      }
    }).catch(() => {
      setPhases(DEFAULT_PHASES.map((p, i) => ({ ...p, id: `local_${i}`, name: p.title })));
    }).finally(() => setLoading(false));
  }, [projectId]);

  const STATUS_CYCLE = ["not_started", "in_progress", "complete"];

  const cycleTaskStatus = async (phaseIndex, taskIndex) => {
    const phase = phases[phaseIndex];
    if (!phase) return;
    const tasks = [...phase.tasks];
    const current = tasks[taskIndex].status;
    const nextIdx = (STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length;
    tasks[taskIndex] = { ...tasks[taskIndex], status: STATUS_CYCLE[nextIdx] };

    const completedCount = tasks.filter(t => t.status === "complete").length;
    const inProgressCount = tasks.filter(t => t.status === "in_progress").length;
    let phaseStatus = "not_started";
    if (completedCount === tasks.length) phaseStatus = "complete";
    else if (completedCount > 0 || inProgressCount > 0) phaseStatus = "in_progress";

    const updated = phases.map((p, i) => i === phaseIndex ? { ...p, tasks, status: phaseStatus } : p);
    setPhases(updated);

    if (phase.id && !String(phase.id).startsWith('local_')) {
      setSaving(true);
      try {
        await api.implementation.update(phase.id, { tasks, status: phaseStatus });
      } catch (err) {
        console.error('Failed to save task status:', err);
      }
      setSaving(false);
    }
  };

  const getProgress = (phase) => {
    const tasks = phase.tasks || [];
    if (tasks.length === 0) return 0;
    return Math.round((tasks.filter(t => t.status === "complete").length / tasks.length) * 100);
  };

  if (!project) return <div style={{ padding: 28, color: COLORS.textDim, fontFamily: FONT_BODY }}>Select a Strategy project first.</div>;

  const getStatusBadge = (status, onClick) => {
    const styles = {
      complete: { bg: COLORS.accent + "15", color: COLORS.accent, label: "Complete" },
      in_progress: { bg: COLORS.blue + "15", color: COLORS.blue, label: "In Progress" },
      not_started: { bg: COLORS.surface, color: COLORS.textDim, label: "Not Started" },
    };
    const s = styles[status] || styles.not_started;
    return (
      <span onClick={onClick} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.color}22`, cursor: onClick ? "pointer" : "default", userSelect: "none" }}>{s.label}</span>
    );
  };

  const getPriorityDot = (priority) => {
    const colors = { high: COLORS.danger, medium: COLORS.warn, low: COLORS.textDim };
    return <div style={{ width: 6, height: 6, borderRadius: "50%", background: colors[priority] || COLORS.textDim, flexShrink: 0 }} />;
  };

  const totalTasks = phases.reduce((s, p) => s + (p.tasks || []).length, 0);
  const completeTasks = phases.reduce((s, p) => s + (p.tasks || []).filter(t => t.status === "complete").length, 0);
  const inProgressTasks = phases.reduce((s, p) => s + (p.tasks || []).filter(t => t.status === "in_progress").length, 0);
  const totalBudget = phases.map(p => p.cost || '').filter(Boolean).join(' + ') || '—';

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Implementation <span style={{ color: COLORS.accent }}>Roadmap</span>
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>{project.client} — Generated from AI analysis</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {saving && <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>Saving...</span>}
          <span style={{ padding: "4px 10px", background: COLORS.accentBg, color: COLORS.accent, fontSize: 11, borderRadius: 6, fontFamily: FONT, fontWeight: 500, border: `1px solid ${COLORS.accent}22` }}>Auto-generated</span>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}><ProgressDots active={true} /><div style={{ marginTop: 12, fontSize: 12, color: COLORS.textDim }}>Loading roadmap...</div></div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
            <StatCard label="Total Tasks" value={totalTasks} accent={COLORS.accent} />
            <StatCard label="Complete" value={completeTasks} accent={COLORS.accent} />
            <StatCard label="In Progress" value={inProgressTasks} accent={COLORS.blue} />
            <StatCard label="Total Budget" value={totalBudget} accent={COLORS.warn} />
          </div>

          <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
            {phases.map((p, i) => (
              <div key={p.id || i} style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: p.color || COLORS.textDim, fontFamily: FONT, fontWeight: 600 }}>{p.title || p.name}</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{p.timeline}</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: COLORS.surface }}>
                  <div style={{ width: `${getProgress(p)}%`, height: "100%", borderRadius: 3, background: p.color || COLORS.accent, transition: "width 0.3s" }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {phases.map((phase, pi) => {
              const tasks = phase.tasks || [];
              const phaseColor = phase.color || COLORS.textDim;
              return (
                <div key={phase.id || pi} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", borderLeft: `4px solid ${phaseColor}` }}>
                  <div onClick={() => setExpandedPhase(expandedPhase === pi ? -1 : pi)} style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{phase.title || phase.name}</div>
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>{phase.timeline} · {phase.cost}</span>
                      {getStatusBadge(phase.status)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11, color: COLORS.textDim }}>{tasks.filter(t => t.status === "complete").length}/{tasks.length} tasks</span>
                      <span style={{ color: COLORS.textDim, fontSize: 12, transform: expandedPhase === pi ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▶</span>
                    </div>
                  </div>
                  {expandedPhase === pi && (
                    <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "8px 12px" }}>
                      {tasks.map((task, ti) => (
                        <div key={task.id || ti} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}` }}>
                          {getPriorityDot(task.priority)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: task.status === "complete" ? COLORS.textDim : COLORS.text, textDecoration: task.status === "complete" ? "line-through" : "none" }}>{task.name}</div>
                          </div>
                          <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, minWidth: 70 }}>{task.assignee}</span>
                          <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, minWidth: 50 }}>{task.dueDate}</span>
                          {getStatusBadge(task.status, (e) => { e.stopPropagation(); cycleTaskStatus(pi, ti); })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function WorkflowsLibraryView() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [WORKFLOWS, setWorkflows] = useState([]);
  const [selectedWf, setSelectedWf] = useState(null);
  const [editingWf, setEditingWf] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadWorkflows = () => {
    api.workflows.list().then(data => {
      const wfs = Array.isArray(data) ? data : data.workflows || [];
      const mapped = wfs.map(w => ({
        ...w,
        name: w.name || w.title || 'Untitled Workflow',
        desc: w.description || w.desc || '',
        tools: Array.isArray(w.tools) ? w.tools : [],
        stepCount: typeof w.steps === 'number' ? w.steps : (Array.isArray(w.steps) ? w.steps.length : 0),
        rating: parseFloat(w.rating) || 0,
        downloads: parseInt(w.downloads) || 0,
      }));
      setWorkflows(mapped);
      if (mapped.length === 0) seedWorkflows();
    }).catch(() => {});
  };

  const seedWorkflows = () => {
    setSeeding(true);
    api.workflows.seedLibrary().then(() => {
      loadWorkflows();
    }).catch(() => {}).finally(() => setSeeding(false));
  };

  useEffect(() => { loadWorkflows(); }, []);

  const handleDownload = async (wf) => {
    try {
      const result = await api.workflows.download(wf.id);
      const content = result.markdown_content || result.description || wf.desc;
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(wf.title || wf.name || 'workflow').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const newDownloads = result.downloads != null ? parseInt(result.downloads) : (wf.downloads || 0) + 1;
      setWorkflows(prev => prev.map(w => w.id === wf.id ? { ...w, downloads: newDownloads } : w));
      if (selectedWf?.id === wf.id) setSelectedWf(prev => ({ ...prev, downloads: newDownloads }));
    } catch (e) { console.error('Download failed', e); }
  };

  const handleRate = async (wf, rating) => {
    try {
      const result = await api.workflows.rate(wf.id, rating);
      const newRating = parseFloat(result.rating) || rating;
      const newUserRating = result.userRating != null ? result.userRating : rating;
      const update = { ...wf, rating: newRating, user_rating: newUserRating };
      setWorkflows(prev => prev.map(w => w.id === wf.id ? { ...w, ...update } : w));
      if (selectedWf && selectedWf.id === wf.id) setSelectedWf(prev => ({ ...prev, ...update }));
    } catch (e) { console.error('Rate failed', e); }
  };

  const handleEdit = (wf) => {
    setEditingWf(wf);
    setEditForm({ title: wf.title || wf.name, description: wf.description || wf.desc, markdown_content: wf.markdown_content || '', category: wf.category, tools: (wf.tools || []).join(', ') });
  };

  const handleSaveEdit = async () => {
    if (!editingWf) return;
    try {
      const toolsArr = editForm.tools ? editForm.tools.split(',').map(t => t.trim()).filter(Boolean) : [];
      await api.workflows.update(editingWf.id, {
        title: editForm.title,
        description: editForm.description,
        markdown_content: editForm.markdown_content,
        category: editForm.category,
        tools: toolsArr,
      });
      setEditingWf(null);
      loadWorkflows();
    } catch (e) { console.error('Save failed', e); }
  };

  const CATEGORIES = [
    { key: "all", label: "All Workflows" },
    { key: "lead_gen", label: "Lead Generation" },
    { key: "outreach", label: "Outreach" },
    { key: "content", label: "Content" },
    { key: "consulting", label: "Consulting" },
    { key: "operations", label: "Operations" },
    { key: "analytics", label: "Analytics" },
  ].map(c => ({ ...c, count: c.key === "all" ? WORKFLOWS.length : WORKFLOWS.filter(w => w.category === c.key).length }));

  const getCategoryColor = (cat) => {
    const colors = { lead_gen: COLORS.accent, outreach: COLORS.blue, content: COLORS.warn, consulting: "#7B61FF", operations: "#22c55e", analytics: "#e879f9" };
    return colors[cat] || COLORS.textDim;
  };

  const getCategoryLabel = (cat) => {
    const labels = { lead_gen: "Lead Gen", outreach: "Outreach", content: "Content", consulting: "Consulting", operations: "Operations", analytics: "Analytics" };
    return labels[cat] || cat;
  };

  let filtered = selectedCategory === "all" ? WORKFLOWS : WORKFLOWS.filter(w => w.category === selectedCategory);
  if (searchTerm) {
    const s = searchTerm.toLowerCase();
    filtered = filtered.filter(w => (w.name || '').toLowerCase().includes(s) || (w.desc || '').toLowerCase().includes(s) || (w.tools || []).some(t => t.toLowerCase().includes(s)));
  }

  const StarRating = ({ rating, userRating, onRate, size = 12 }) => {
    const displayRating = (userRating != null ? userRating : rating) ?? 0;
    return (
      <div style={{ display: "inline-flex", gap: 1 }}>
        {[1, 2, 3, 4, 5].map(star => (
          <span key={star} onClick={e => { if (onRate) { e.stopPropagation(); onRate(star); } }} style={{ cursor: onRate ? "pointer" : "default", fontSize: size, color: star <= displayRating ? "#f59e0b" : COLORS.textDim, transition: "color 0.15s" }}
            onMouseEnter={e => { if (onRate) e.currentTarget.style.color = "#f59e0b"; }}
            onMouseLeave={e => { if (onRate) e.currentTarget.style.color = star <= displayRating ? "#f59e0b" : COLORS.textDim; }}
          >★</span>
        ))}
      </div>
    );
  };

  // Detail modal
  if (selectedWf) {
    const wf = selectedWf;
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        <button onClick={() => setSelectedWf(null)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, padding: "6px 12px", cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>← Back to Library</button>
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ padding: "3px 10px", borderRadius: 4, fontSize: 10, fontFamily: FONT, fontWeight: 600, background: getCategoryColor(wf.category) + "15", color: getCategoryColor(wf.category), border: `1px solid ${getCategoryColor(wf.category)}22` }}>{getCategoryLabel(wf.category)}</span>
              <span style={{ fontSize: 10, color: COLORS.textDim }}>{wf.trigger_type || 'manual'}</span>
            </div>
            <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: "0 0 8px", letterSpacing: "-0.02em" }}>{wf.name}</h2>
            <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "0 0 16px", lineHeight: 1.6 }}>{wf.desc}</p>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
              <StarRating rating={wf.rating} userRating={wf.user_rating} onRate={(r) => handleRate(wf, r)} size={16} />
              {wf.user_rating != null && <span style={{ fontSize: 11, color: COLORS.textDim }}>Your rating: {wf.user_rating}</span>}
              <span style={{ fontSize: 11, color: COLORS.textDim }}>{wf.rating.toFixed(1)} avg</span>
              <span style={{ fontSize: 11, color: COLORS.textDim }}>|</span>
              <span style={{ fontSize: 11, color: COLORS.textDim }}>{(wf.downloads ?? 0)} downloads</span>
              <span style={{ fontSize: 11, color: COLORS.textDim }}>|</span>
              <span style={{ fontSize: 11, color: COLORS.textDim }}>{wf.stepCount} steps</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
              {(wf.tools || []).map(tool => (
                <span key={tool} style={{ padding: "3px 10px", borderRadius: 4, fontSize: 10, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontFamily: FONT }}>{tool}</span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              <button onClick={() => handleDownload(wf)} style={{ padding: "8px 20px", background: COLORS.accent, border: "none", borderRadius: 6, color: COLORS.bg, fontFamily: FONT, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Download .md</button>
              {!wf.is_library && (
                <button onClick={() => handleEdit(wf)} style={{ padding: "8px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Edit</button>
              )}
            </div>
            {/* Markdown content preview */}
            <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20, fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-wrap", maxHeight: 500, overflow: "auto" }}>
              {wf.markdown_content || 'No content available. Click Edit to add workflow content.'}
            </div>
          </div>
          {/* Right sidebar with steps */}
          <div style={{ width: 280, flexShrink: 0 }}>
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Workflow Steps</div>
              {(Array.isArray(wf.steps) ? wf.steps : []).map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: COLORS.accentBg, color: COLORS.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{step.step || i + 1}</div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>{step.action || `Step ${i + 1}`}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>{step.detail || ''}</div>
                  </div>
                </div>
              ))}
              {(!Array.isArray(wf.steps) || wf.steps.length === 0) && (
                <div style={{ fontSize: 11, color: COLORS.textDim }}>No steps defined</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit modal
  if (editingWf) {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        <button onClick={() => setEditingWf(null)} style={{ background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, padding: "6px 12px", cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>← Cancel</button>
        <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: "0 0 20px", letterSpacing: "-0.02em" }}>Edit Workflow</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 800 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Title</label>
            <input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Description</label>
            <textarea value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Category</label>
              <select value={editForm.category || ''} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none" }}>
                <option value="lead_gen">Lead Generation</option>
                <option value="outreach">Outreach</option>
                <option value="content">Content</option>
                <option value="consulting">Consulting</option>
                <option value="operations">Operations</option>
                <option value="analytics">Analytics</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Tools (comma-separated)</label>
              <input value={editForm.tools || ''} onChange={e => setEditForm(f => ({ ...f, tools: e.target.value }))} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", boxSizing: "border-box" }} placeholder="e.g. LinkedIn, AI, CRM" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Markdown Content (Claude Skill)</label>
            <textarea value={editForm.markdown_content || ''} onChange={e => setEditForm(f => ({ ...f, markdown_content: e.target.value }))} rows={20} style={{ width: "100%", padding: "12px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 12, outline: "none", resize: "vertical", lineHeight: 1.6, boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSaveEdit} style={{ padding: "10px 24px", background: COLORS.accent, border: "none", borderRadius: 6, color: COLORS.bg, fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save Changes</button>
            <button onClick={() => setEditingWf(null)} style={{ padding: "10px 24px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Main library view
  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Workflow <span style={{ color: COLORS.accent }}>Library</span>
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Pre-built automation workflows — download, customise, and deploy</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input placeholder="Search workflows..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: "7px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT, fontSize: 11, outline: "none", width: 180 }} />
          {WORKFLOWS.length === 0 && (
            <button onClick={seedWorkflows} disabled={seeding} style={{ padding: "7px 14px", background: COLORS.accent, border: "none", borderRadius: 6, color: COLORS.bg, fontFamily: FONT, fontSize: 10, fontWeight: 700, cursor: "pointer", opacity: seeding ? 0.5 : 1 }}>{seeding ? 'Seeding...' : 'Load Templates'}</button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}`, overflowX: "auto" }}>
        {CATEGORIES.map(cat => (
          <button key={cat.key} onClick={() => setSelectedCategory(cat.key)} style={{
            padding: "10px 16px", background: "transparent", border: "none",
            borderBottom: selectedCategory === cat.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            color: selectedCategory === cat.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
          }}>
            {cat.label}
            <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 8, background: selectedCategory === cat.key ? COLORS.accentBg : COLORS.surface, color: selectedCategory === cat.key ? COLORS.accent : COLORS.textDim }}>{cat.count}</span>
          </button>
        ))}
      </div>

      {seeding && (
        <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>Loading workflow templates...</div>
      )}

      {/* Workflow Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {filtered.map(wf => (
          <div key={wf.id} onClick={() => { setSelectedWf(wf); api.workflows.get(wf.id).then(full => { setSelectedWf(prev => ({ ...prev, ...full, name: full.title || full.name || prev.name, desc: full.description || prev.desc, tools: Array.isArray(full.tools) ? full.tools : prev.tools, stepCount: Array.isArray(full.steps) ? full.steps.length : prev.stepCount, rating: parseFloat(full.rating) || prev.rating, downloads: parseInt(full.downloads) || prev.downloads })); }).catch(() => {}); }} style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, display: "flex", flexDirection: "column", justifyContent: "space-between", cursor: "pointer", transition: "border-color 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.borderActive; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: getCategoryColor(wf.category) + "15", color: getCategoryColor(wf.category), border: `1px solid ${getCategoryColor(wf.category)}22` }}>{getCategoryLabel(wf.category)}</span>
                <StarRating rating={wf.rating} userRating={wf.user_rating} onRate={(r) => handleRate(wf, r)} size={10} />
                <span style={{ fontSize: 10, color: COLORS.textDim }}>{wf.rating.toFixed(1)}</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{wf.name}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{wf.desc}</div>
            </div>
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                {(wf.tools || []).slice(0, 4).map(tool => (
                  <span key={tool} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textDim, fontFamily: FONT }}>{tool}</span>
                ))}
                {(wf.tools || []).length > 4 && <span style={{ fontSize: 9, color: COLORS.textDim }}>+{wf.tools.length - 4}</span>}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 12, fontSize: 10, color: COLORS.textDim }}>
                  <span>{wf.stepCount} steps</span>
                  <span>{(wf.downloads ?? 0)} downloads</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleDownload(wf); }} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
                >Download →</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && !seeding && (
        <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>
          {WORKFLOWS.length === 0 ? 'No workflows yet. Click "Load Templates" to get started.' : 'No workflows match your search.'}
        </div>
      )}
    </div>
  );
}

function AICouncilView() {
  const [messages, setMessages] = useState([{ role: "agent", text: "Welcome to AI Council. Ask a strategic question and I'll convene a council of AI models — each responds independently, they peer-review each other anonymously, then a Chairman synthesizes the final answer.\n\nType a question to start a council session." }]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [councilSession, setCouncilSession] = useState(null);
  const [activeTab, setActiveTab] = useState("stage1");
  const [modelsInfo, setModelsInfo] = useState(null);
  const chatEndRef = React.useRef(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages, isTyping]);

  // Load available models on mount
  React.useEffect(() => {
    api.aiCouncil.models().then(setModelsInfo).catch(() => {});
  }, []);

  // Polling for council session status (with 6-minute client-side timeout)
  const pollStartRef = React.useRef(null);
  React.useEffect(() => {
    if (!councilSession?.id || councilSession.status === "completed" || councilSession.status === "failed") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      // Client-side timeout: stop polling after 6 minutes
      if (Date.now() - pollStartRef.current > 360000) {
        clearInterval(pollRef.current);
        setIsTyping(false);
        setCouncilSession(prev => prev ? { ...prev, status: "failed", error: "Session timed out — no response after 6 minutes." } : prev);
        setMessages(prev => [...prev, { role: "agent", text: "Council session timed out after 6 minutes. The backend may still be processing — try refreshing." }]);
        return;
      }
      try {
        const data = await api.aiCouncil.councilStatus(councilSession.id);
        setCouncilSession(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          setIsTyping(false);
          if (data.status === "completed") {
            setMessages(prev => [...prev, { role: "agent", text: "The council has reached its verdict. Review each stage in the panels on the right." }]);
            setActiveTab("stage3");
          } else {
            setMessages(prev => [...prev, { role: "agent", text: `Council session failed: ${data.error || "Unknown error"}` }]);
          }
        }
      } catch (_) {}
    }, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [councilSession?.id, councilSession?.status]);

  const startCouncil = async () => {
    if (!input.trim() || isTyping) return;
    const q = input.trim();
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setInput("");
    setIsTyping(true);
    setActiveTab("stage1");
    setCouncilSession(null);
    try {
      const session = await api.aiCouncil.council(q);
      setCouncilSession({ ...session, stage1: [], stage2: {}, stage3: {}, current_stage: 0, user_query: q });
      setMessages(prev => [...prev, { role: "agent", text: `Council convened with ${session.models?.length || 4} models. Stages: Collect → Rank → Synthesize.\nChairman: ${session.chairman?.label || "Gemini"}` }]);
    } catch (err) {
      setIsTyping(false);
      setMessages(prev => [...prev, { role: "agent", text: err?.message || err?.data?.error || "Failed to start council session." }]);
    }
  };

  const startNewSession = () => {
    setCouncilSession(null);
    setMessages([{ role: "agent", text: "Welcome to AI Council. Ask a strategic question and I'll convene a council of AI models — each responds independently, they peer-review each other anonymously, then a Chairman synthesizes the final answer.\n\nType a question to start a council session." }]);
    setInput("");
    setActiveTab("stage1");
  };

  const s = councilSession;
  const stage1 = s?.stage1 || [];
  const stage2 = s?.stage2 || {};
  const stage3 = s?.stage3 || {};
  const rankings = stage2.aggregate_rankings || [];
  const labelToModel = stage2.label_to_model || {};
  const modelToLabel = Object.fromEntries(Object.entries(labelToModel).map(([l, m]) => [m, l]));
  const isRunning = s && s.status !== "completed" && s.status !== "failed";

  const STAGE_TABS = [
    { key: "stage1", label: "Responses", stage: 1, icon: "💬" },
    { key: "stage2", label: "Rankings", stage: 2, icon: "📊" },
    { key: "stage3", label: "Synthesis", stage: 3, icon: "🏛️" },
    { key: "summary", label: "Summary", stage: 3, icon: "📋" },
  ];

  const stageReady = (stageNum) => s && s.current_stage >= stageNum;

  const ModelCard = ({ model, children, latency, rank, color }) => (
    <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 10, borderLeft: `3px solid ${color || COLORS.accent}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>{model?.icon || "🤖"}</span>
          <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: COLORS.text }}>{model?.label || model?.model || "Model"}</span>
          {model?.id && <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, background: COLORS.bg, padding: "2px 6px", borderRadius: 4 }}>{model.id}</span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {rank != null && <span style={{ fontFamily: FONT, fontSize: 10, fontWeight: 600, color: rank === 1 ? COLORS.green : rank === 2 ? COLORS.accent : COLORS.textDim, background: COLORS.bg, padding: "2px 8px", borderRadius: 4 }}>#{rank}</span>}
          {latency > 0 && <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, background: COLORS.bg, padding: "2px 8px", borderRadius: 4 }}>{(latency / 1000).toFixed(1)}s</span>}
        </div>
      </div>
      {children}
    </div>
  );

  const MODEL_COLORS = ["#7B61FF", "#4D9EF0", "#2dd4a8", "#F0A84D"];

  const renderStage1 = () => {
    if (!stageReady(1) && isRunning) return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <ProgressDots active={true} />
        <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>Querying {s?.models_used?.length || 4} models in parallel...</div>
      </div>
    );
    if (stage1.length === 0) return <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 40 }}>No responses yet</div>;
    return stage1.map((r, i) => (
      <ModelCard key={i} model={r} latency={r.latency_ms} color={MODEL_COLORS[i % MODEL_COLORS.length]} rank={rankings.find(rk => rk.model === r.model)?.average_rank ? Math.round(rankings.find(rk => rk.model === r.model)?.average_rank) : null}>
        {r.error ? (
          <div style={{ fontSize: 12, color: COLORS.danger, fontStyle: "italic" }}>Error: {r.error}</div>
        ) : (
          <div style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{r.response}</div>
        )}
      </ModelCard>
    ));
  };

  const renderStage2 = () => {
    if (!stageReady(2) && isRunning) return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <ProgressDots active={true} />
        <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>Models are anonymously peer-reviewing each other...</div>
      </div>
    );
    const evals = stage2.evaluations || [];
    if (evals.length === 0) return <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 40 }}>Waiting for Stage 1 to complete...</div>;

    return (
      <div>
        {/* Aggregate ranking table */}
        {rankings.length > 0 && (
          <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 12 }}>AGGREGATE RANKINGS</div>
            {stage2.ranking_warning && <div style={{ padding: "6px 10px", background: COLORS.warn + "15", border: `1px solid ${COLORS.warn}33`, borderRadius: 6, marginBottom: 10, fontSize: 10, color: COLORS.warn }}>{stage2.ranking_warning}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 80px 60px", gap: "6px 12px", fontSize: 12 }}>
              <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600 }}>#</div>
              <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600 }}>MODEL</div>
              <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600 }}>AVG RANK</div>
              <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600 }}>VOTES</div>
              {rankings.map((r, i) => {
                const m = stage1.find(s1 => s1.model === r.model);
                return (
                  <React.Fragment key={i}>
                    <div style={{ fontWeight: 700, color: i === 0 ? COLORS.green : i === 1 ? COLORS.accent : COLORS.text }}>{i + 1}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{m?.icon || "🤖"}</span>
                      <span style={{ color: COLORS.text, fontWeight: 500 }}>{m?.label || r.model}</span>
                      <span style={{ fontSize: 9, color: COLORS.textDim }}>({modelToLabel[r.model] || ""})</span>
                    </div>
                    <div style={{ fontFamily: FONT, color: i === 0 ? COLORS.green : COLORS.text }}>{r.average_rank ?? "—"}</div>
                    <div style={{ color: COLORS.textDim }}>{r.rankings_count}</div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Individual evaluations */}
        <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 10 }}>PEER EVALUATIONS</div>
        {evals.map((ev, i) => (
          <ModelCard key={i} model={ev} latency={ev.latency_ms} color={MODEL_COLORS[i % MODEL_COLORS.length]}>
            {ev.error ? (
              <div style={{ fontSize: 12, color: COLORS.danger, fontStyle: "italic" }}>Error: {ev.error}</div>
            ) : (
              <div>
                <div style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line", maxHeight: 200, overflow: "auto" }}>{ev.evaluation}</div>
                {ev.parsed_ranking && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: COLORS.bg, borderRadius: 6 }}>
                    <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.accent, fontWeight: 600, marginBottom: 4 }}>RANKING</div>
                    {ev.parsed_ranking.map((label, ri) => {
                      const modelName = labelToModel[label];
                      const m = stage1.find(s1 => s1.model === modelName);
                      return <div key={ri} style={{ fontSize: 11, color: ri === 0 ? COLORS.green : COLORS.text, marginBottom: 2 }}>{ri + 1}. {label} — {m?.label || modelName || "Unknown"}</div>;
                    })}
                  </div>
                )}
              </div>
            )}
          </ModelCard>
        ))}
      </div>
    );
  };

  const renderStage3 = () => {
    if (!stageReady(3) && isRunning) return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <ProgressDots active={true} />
        <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>Chairman is synthesizing the council's collective wisdom...</div>
      </div>
    );
    if (!stage3.synthesis && !stage3.error) return <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 40 }}>Waiting for ranking stage to complete...</div>;

    return (
      <div>
        <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderLeft: `3px solid ${COLORS.accent}`, borderRadius: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>🏛️</span>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: COLORS.text }}>Chairman's Synthesis</div>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>{stage3.label || stage3.model || "Chairman"} {stage3.latency_ms ? `· ${(stage3.latency_ms / 1000).toFixed(1)}s` : ""}</div>
            </div>
          </div>
          {stage3.error ? (
            <div style={{ fontSize: 12, color: COLORS.danger }}>Chairman error: {stage3.error}</div>
          ) : (
            <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.8, whiteSpace: "pre-line" }}>{stage3.synthesis}</div>
          )}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (s?.status !== "completed") return <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 40 }}>Summary available after all stages complete.</div>;
    const validS1 = stage1.filter(r => r.response);
    const totalLatency = stage1.reduce((s, r) => Math.max(s, r.latency_ms || 0), 0) + (stage2.evaluations || []).reduce((s, r) => Math.max(s, r.latency_ms || 0), 0) + (stage3.latency_ms || 0);

    return (
      <div>
        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          {[
            { label: "MODELS", value: validS1.length, icon: "🤖" },
            { label: "EVALUATIONS", value: (stage2.evaluations || []).filter(e => e.evaluation).length, icon: "📊" },
            { label: "TOTAL TIME", value: `${(totalLatency / 1000).toFixed(0)}s`, icon: "⏱️" },
            { label: "CONSENSUS", value: rankings.length > 0 && rankings[0].rankings_count >= 2 ? "Strong" : "Weak", icon: "🎯" },
          ].map((st, i) => (
            <div key={i} style={{ padding: "14px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>{st.icon}</div>
              <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{st.value}</div>
              <div style={{ fontFamily: FONT, fontSize: 8, color: COLORS.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>{st.label}</div>
            </div>
          ))}
        </div>

        {/* Leaderboard */}
        <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 }}>
          <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 14 }}>MODEL LEADERBOARD</div>
          {rankings.map((r, i) => {
            const m = stage1.find(s1 => s1.model === r.model);
            const barWidth = r.average_rank ? Math.max(10, 100 - ((r.average_rank - 1) / Math.max(rankings.length - 1, 1)) * 80) : 10;
            const medals = ["🥇", "🥈", "🥉"];
            return (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>{medals[i] || "  "}</span>
                    <span style={{ fontSize: 14 }}>{m?.icon || "🤖"}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{m?.label || r.model}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>avg rank: {r.average_rank}</span>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>{m?.latency_ms ? `${(m.latency_ms / 1000).toFixed(1)}s` : ""}</span>
                  </div>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: COLORS.bg }}>
                  <div style={{ width: `${barWidth}%`, height: "100%", borderRadius: 3, background: MODEL_COLORS[i % MODEL_COLORS.length], transition: "width 0.5s" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Label mapping */}
        {Object.keys(labelToModel).length > 0 && (
          <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 8 }}>ANONYMIZATION KEY</div>
            {Object.entries(labelToModel).map(([label, model]) => {
              const m = stage1.find(s1 => s1.model === model);
              return <div key={label} style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 3 }}><span style={{ color: COLORS.text, fontWeight: 500 }}>{label}</span> → {m?.icon || ""} {m?.label || model}</div>;
            })}
          </div>
        )}
      </div>
    );
  };

  // Stage progress indicator
  const StageProgress = () => {
    const stages = [
      { num: 1, label: "Collect", desc: "Individual responses" },
      { num: 2, label: "Rank", desc: "Peer evaluation" },
      { num: 3, label: "Synthesize", desc: "Chairman verdict" },
    ];
    return (
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {stages.map((st, i) => {
          const done = s && s.current_stage > st.num;
          const active = s && s.current_stage === st.num;
          const pending = !done && !active;
          return (
            <React.Fragment key={st.num}>
              {i > 0 && <div style={{ width: 20, height: 2, background: done ? COLORS.green : COLORS.border, alignSelf: "center" }} />}
              <div style={{ flex: 1, padding: "8px 10px", background: active ? COLORS.accent + "15" : COLORS.surface, border: `1px solid ${active ? COLORS.accent + "44" : done ? COLORS.green + "44" : COLORS.border}`, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontFamily: FONT, fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", color: done ? COLORS.green : active ? COLORS.accent : COLORS.textDim, marginBottom: 2 }}>
                  {done ? "✓ " : active ? "● " : ""}{st.label.toUpperCase()}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textDim }}>{st.desc}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Left panel: chat */}
      <div style={{ width: 380, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🧠</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>AI Council</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>Multi-model strategic advisor · {modelsInfo?.models?.length || 4} models</div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "88%", padding: "10px 14px", borderRadius: 12, background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface, border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`, borderBottomRightRadius: msg.role === "user" ? 4 : 12, borderBottomLeftRadius: msg.role === "agent" ? 4 : 12 }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI COUNCIL</div>}
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {isTyping && <div style={{ display: "flex" }}><div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}><ProgressDots active={true} /></div></div>}
          <div ref={chatEndRef} />
        </div>
        {!councilSession ? (
          <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) startCouncil(); }} placeholder="Ask the AI Council..." style={{ flex: 1, padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }} disabled={isTyping} />
            <button onClick={startCouncil} disabled={isTyping || !input.trim()} style={{ padding: "10px 18px", background: input.trim() && !isTyping ? COLORS.accent : COLORS.border, color: input.trim() && !isTyping ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: input.trim() && !isTyping ? "pointer" : "default" }}>Convene</button>
          </div>
        ) : (
          <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}` }}>
            <button onClick={startNewSession} style={{ width: "100%", padding: "10px 18px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ New question</button>
          </div>
        )}
      </div>

      {/* Right panel: stage tabs */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!councilSession ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 400 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>🧠</div>
              <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: COLORS.textDim, marginBottom: 6 }}>AI Council</div>
              <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.6, marginBottom: 16 }}>Ask a question and multiple AI models will independently respond, anonymously peer-review each other, and a Chairman will synthesize the final answer.</div>
              {modelsInfo?.available && (
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                  {modelsInfo.models.map((m, i) => (
                    <div key={i} style={{ padding: "6px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 20, fontSize: 11, color: COLORS.textDim }}>
                      {m.icon} {m.label}
                    </div>
                  ))}
                </div>
              )}
              {modelsInfo && !modelsInfo.available && (
                <div style={{ padding: "10px 16px", background: COLORS.warn + "15", border: `1px solid ${COLORS.warn}33`, borderRadius: 8, fontSize: 11, color: COLORS.warn }}>
                  {modelsInfo.message || "Connect OpenRouter or at least 2 LLM providers in Settings > Integrations."}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Stage progress bar */}
            <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
              <StageProgress />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, padding: "0 20px", borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
              {STAGE_TABS.map(tab => {
                const active = activeTab === tab.key;
                const ready = tab.key === "summary" ? s?.status === "completed" : stageReady(tab.stage);
                return (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: "10px 16px", background: "none", border: "none", borderBottom: active ? `2px solid ${COLORS.accent}` : "2px solid transparent", color: active ? COLORS.accent : ready ? COLORS.text : COLORS.textDim, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: ready || active ? 1 : 0.5, display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 12 }}>{tab.icon}</span> {tab.label}
                    {isRunning && s?.current_stage === tab.stage && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 3, background: COLORS.accent, animation: "pulse 1.5s infinite" }} />}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
              {s?.user_query && (
                <div style={{ padding: "10px 14px", background: COLORS.accent + "10", border: `1px solid ${COLORS.accent}22`, borderRadius: 8, marginBottom: 14, fontSize: 12, color: COLORS.text }}>
                  <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.accent, fontWeight: 600, letterSpacing: "0.06em" }}>QUERY </span>{s.user_query}
                </div>
              )}
              {activeTab === "stage1" && renderStage1()}
              {activeTab === "stage2" && renderStage2()}
              {activeTab === "stage3" && renderStage3()}
              {activeTab === "summary" && renderSummary()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AuditView({ project, projects, selectedProject, setSelectedProject, onCreateProject }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [transcripts, setTranscripts] = useState([]);
  const [surveyCount, setSurveyCount] = useState(0);
  const [interviewCount, setInterviewCount] = useState(0);
  const [deckGenerating, setDeckGenerating] = useState(false);
  const [deckToast, setDeckToast] = useState(null);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [addProjectCreating, setAddProjectCreating] = useState(false);

  useEffect(() => {
    if (!project?.id) {
      setTranscripts([]);
      setSurveyCount(0);
      setInterviewCount(0);
      return;
    }
    api.audit.transcripts.list({ project_id: project.id }).then(data => {
      const ts = Array.isArray(data) ? data : data.transcripts || [];
      setTranscripts(ts.map(t => ({ ...t, tags: t.tags || [], speaker: t.speaker || t.speaker_name || '', role: t.role || t.speaker_role || '' })));
    }).catch(() => setTranscripts([]));
    api.audit.surveys.list({ project_id: project.id }).then(data => {
      setSurveyCount(Array.isArray(data) ? data.length : 0);
    }).catch(() => setSurveyCount(0));
    api.audit.interviews.list({ project_id: project.id }).then(data => {
      setInterviewCount(Array.isArray(data) ? data.length : 0);
    }).catch(() => setInterviewCount(0));
  }, [project?.id]);

  useEffect(() => {
    setDeckGenerating(Boolean(getStoredDeckJob(project?.id)?.jobId));
  }, [project?.id]);

  useEffect(() => {
    if (!deckToast) return;
    const timer = setTimeout(() => setDeckToast(null), 6000);
    return () => clearTimeout(timer);
  }, [deckToast]);

  const projectCreateModal = showAddProjectModal && (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => !addProjectCreating && setShowAddProjectModal(false)}>
      <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 24, maxWidth: 360, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>New Strategy Project</div>
        <label style={{ display: "block", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Project name</label>
        <input value={newProjectName} onChange={e => setNewProjectName(e.target.value)} placeholder="e.g. Acme Corp AI Audit" style={{ width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 20 }} autoFocus />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button onClick={() => !addProjectCreating && setShowAddProjectModal(false)} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={async () => {
            const name = newProjectName.trim();
            if (!name || typeof onCreateProject !== "function") return;
            setAddProjectCreating(true);
            try {
              await onCreateProject(name);
              setShowAddProjectModal(false);
              setNewProjectName("");
            } catch (err) {
              alert(err.message || "Failed to create project");
            } finally {
              setAddProjectCreating(false);
            }
          }} disabled={addProjectCreating || !newProjectName.trim()} style={{ padding: "10px 24px", background: (addProjectCreating || !newProjectName.trim()) ? COLORS.border : COLORS.accent, color: (addProjectCreating || !newProjectName.trim()) ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: (addProjectCreating || !newProjectName.trim()) ? "not-allowed" : "pointer" }}>{addProjectCreating ? "Creating..." : "Create"}</button>
        </div>
      </div>
    </div>
  );

  const projectSelectorBar = (
    <div style={{ padding: "14px 28px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600 }}>STRATEGY PROJECT</div>
        <select value={selectedProject ?? ""} onChange={e => setSelectedProject(e.target.value || null)} style={{
          minWidth: 280, maxWidth: 360, padding: "9px 12px",
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8,
          color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, outline: "none", cursor: "pointer",
        }}>
          {projects.length === 0
            ? <option value="" disabled>No strategy projects yet</option>
            : projects.map(p => <option key={p.id} value={String(p.id)}>{p.client || p.name}</option>)}
        </select>
        <button onClick={() => { setNewProjectName(""); setShowAddProjectModal(true); }} style={{ padding: "9px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.accent, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          + New Project
        </button>
      </div>
    </div>
  );

  if (!project) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {projectCreateModal}
        {projectSelectorBar}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 28 }}>
          <div style={{ maxWidth: 480, padding: "28px 32px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, textAlign: "center" }}>
            <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Strategy Project Selected</div>
            <div style={{ color: COLORS.textDim, fontFamily: FONT_BODY, fontSize: 13, lineHeight: 1.6 }}>
              Create a project from the bar above. Strategy is now scoped to the selected project instead of the sidebar.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <style>{`@keyframes deckPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      {projectCreateModal}
      {projectSelectorBar}
      {/* Header */}
      <div style={{ padding: "14px 28px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{project.name}</div>
          <div style={{ fontSize: 11, color: COLORS.textDim, display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 14 }}>description</span> {transcripts.length} transcripts</span>
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 14 }}>bar_chart</span> {surveyCount} surveys</span>
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 14 }}>mic</span> {interviewCount} interviews</span>
            <span>Created {project.created}</span>
          </div>
        </div>
        <span style={{ padding: "4px 10px", background: COLORS.accentBg, color: COLORS.accent, fontSize: 11, borderRadius: 6, fontFamily: FONT, fontWeight: 500, border: `1px solid ${COLORS.accent}22` }}>Review Mode</span>
      </div>

      {deckToast && (
        <div style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 9999,
          padding: "10px 18px",
          background: deckToast.type === "error" ? COLORS.danger + "20" : COLORS.accent + "20",
          border: `1px solid ${deckToast.type === "error" ? COLORS.danger : COLORS.accent}50`,
          borderRadius: 8,
          color: deckToast.type === "error" ? COLORS.danger : COLORS.accent,
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          maxWidth: 320,
          boxSizing: "border-box",
        }}>{deckToast.message}</div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0, padding: "0 28px" }}>
        {[
          { key: "overview", icon: "apartment", label: "Company" },
          { key: "surveys", icon: "bar_chart", label: "Surveys", count: surveyCount },
          { key: "interviews", icon: "mic", label: "Interviews", count: interviewCount },
          { key: "transcripts", icon: "description", label: "Transcripts", count: transcripts.length },
          { key: "analysis", icon: "auto_awesome", label: "Analysis", deckGenerating },
          { key: "prototypelab", icon: "science", label: "Prototype Lab" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "12px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.deckGenerating && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 3, background: COLORS.accent, animation: "deckPulse 1.5s infinite" }} title="Deck generating" />}
            {tab.count !== undefined && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: activeTab === tab.key ? COLORS.accentBg : COLORS.surface, color: activeTab === tab.key ? COLORS.accent : COLORS.textDim }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: (activeTab === "analysis" || activeTab === "processmaps") ? 0 : 28, display: "flex", flexDirection: "column" }}>
        {activeTab === "overview" && <AuditCompanyTab project={project} transcripts={transcripts} />}
        {activeTab === "surveys" && <AuditSurveysTab project={project} onCountChange={setSurveyCount} />}
        {activeTab === "interviews" && <AuditInterviewsTab project={project} onCountChange={setInterviewCount} />}
        {activeTab === "transcripts" && <AuditTranscriptsTab project={project} transcripts={transcripts} setTranscripts={setTranscripts} />}
        <KeepAlive active={activeTab === "processmaps"}>
          <AuditProcessMapsTab project={project} />
        </KeepAlive>
        <KeepAlive active={activeTab === "prototypelab"}>
          <PrototypeLabTab project={project} />
        </KeepAlive>
        <KeepAlive active={activeTab === "analysis" || deckGenerating}>
          <AuditAnalysisTab project={project} isActive={activeTab === "analysis"} onDeckGenerating={setDeckGenerating} onDeckJobEvent={setDeckToast} />
        </KeepAlive>
      </div>
    </div>
  );
}

function AuditCompanyTab({ project, transcripts }) {
  const [companyUrl, setCompanyUrl] = useState("");
  const [isResearching, setIsResearching] = useState(false);
  const [researchDone, setResearchDone] = useState(false);
  const [researchError, setResearchError] = useState("");
  const [overview, setOverview] = useState("");
  const [metrics, setMetrics] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [transcriptInsights, setTranscriptInsights] = useState([]);
  const [researchLogSteps, setResearchLogSteps] = useState([]);
  const [researchLogExpanded, setResearchLogExpanded] = useState(false);
  const researchLogRef = useRef(null);

  useEffect(() => {
    if (!project?.id) return;
    api.settings.get('audit_company', { projectId: project.id }).then(data => {
      const s = data?.settings || data || {};
      if (s.overview) setOverview(s.overview);
      if (s.metrics?.length) setMetrics(s.metrics);
      if (s.sections?.length) setCustomSections(s.sections);
      if (s.insights?.length) setTranscriptInsights(s.insights);
      if (s.overview || s.metrics?.length) setResearchDone(true);
    }).catch(() => {});
  }, [project?.id]);

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };

  const doResearch = async () => {
    if (!companyUrl.trim()) return;
    setIsResearching(true);
    setResearchError("");
    const url = companyUrl.trim();
    setResearchLogSteps([{ id: 1, text: "Starting deep research...", time: Date.now() }]);
    setResearchLogExpanded(true);

    const SIMULATED_STEPS = [
      "Searching web for company data...",
      "Analyzing sources and compiling insights...",
      "Extracting metrics and key information...",
    ];
    let stepIdx = 0;
    const stepInterval = setInterval(() => {
      if (stepIdx < SIMULATED_STEPS.length) {
        setResearchLogSteps(prev => [...prev, { id: prev.length + 1, text: SIMULATED_STEPS[stepIdx], time: Date.now() }]);
        stepIdx++;
      }
    }, 1500);

    try {
      const result = await api.audit.research({ project_id: project?.id || "default", company_url: url });
      clearInterval(stepInterval);
      const rd = result.research_data || {};
      const steps = [];
      if (rd.overview) steps.push("Overview compiled");
      if (rd.metrics?.length) steps.push(`Metrics found: ${rd.metrics.length} items`);
      if (rd.sections?.length) steps.push(`Sections found: ${rd.sections.length} items`);
      if (steps.length) {
        setResearchLogSteps(prev => [...prev, ...steps.map((t, i) => ({ id: prev.length + i + 1, text: t, time: Date.now() }))]);
      }
      if (rd.overview) setOverview(rd.overview);
      if (rd.metrics?.length) setMetrics(rd.metrics);
      if (rd.sections?.length) setCustomSections(rd.sections.map(s => ({ title: s.title || "", content: s.content || "" })));
      setResearchDone(true);
      api.settings.save('audit_company', {
        overview: rd.overview || "",
        metrics: rd.metrics || [],
        sections: rd.sections || [],
        company_url: url,
      }, { projectId: project?.id }).catch(() => {});
      setTimeout(() => setResearchLogExpanded(false), 1200);
    } catch (err) {
      clearInterval(stepInterval);
      setResearchError(err.message || "Research failed");
      setResearchLogSteps(prev => [...prev, { id: prev.length + 1, text: `Error: ${err.message}`, time: Date.now() }]);
    } finally {
      setIsResearching(false);
    }
  };

  useEffect(() => {
    if (researchLogSteps.length && researchLogRef.current) {
      const last = researchLogRef.current.querySelector('[data-last-step]') || researchLogRef.current;
      last.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [researchLogSteps]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 18 }}>🏢</span>
        <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Company Research</h2>
      </div>

      {/* URL Input */}
      <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 14 }}>🔗</span>
        <input value={companyUrl} onChange={e => setCompanyUrl(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && companyUrl.trim() && !isResearching) doResearch(); }} placeholder="Paste company website URL to auto-research..." style={{ ...inputStyle, flex: 1, background: COLORS.bg }} />
        <button onClick={doResearch} disabled={isResearching || !companyUrl.trim()} style={{
          padding: "10px 20px", background: (isResearching || !companyUrl.trim()) ? COLORS.border : COLORS.accent, color: (isResearching || !companyUrl.trim()) ? COLORS.textDim : COLORS.bg,
          border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: (isResearching || !companyUrl.trim()) ? "default" : "pointer", whiteSpace: "nowrap",
        }}>{isResearching ? "Researching..." : "🔍 Research"}</button>
      </div>

      {researchLogSteps.length > 0 && (
        <div style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 16 }}>
          <div
            onClick={() => setResearchLogExpanded(prev => !prev)}
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
          >
            <span style={{ fontSize: 12, transform: researchLogExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", color: COLORS.textDim }}>▶</span>
            <span style={{ fontSize: 13, color: COLORS.accent, fontWeight: 600 }}>
              {isResearching ? `Running deep research on ${companyUrl.trim() || "…"}` : "Research log"}
            </span>
            {isResearching && <ProgressDots active={true} />}
          </div>
          {researchLogExpanded && (
            <div
              ref={researchLogRef}
              style={{ maxHeight: 120, overflowY: "auto", marginTop: 10, paddingRight: 4, fontFamily: FONT, fontSize: 11, lineHeight: 1.8, color: COLORS.textDim }}
            >
              {researchLogSteps.map((step, i) => (
                <div key={step.id} data-last-step={i === researchLogSteps.length - 1 ? "1" : undefined} style={{ marginBottom: 2 }}>
                  <span style={{ color: COLORS.accent, marginRight: 6 }}>•</span>
                  {step.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {researchError && (
        <div style={{ padding: "12px 16px", background: "#ff4d4f12", border: "1px solid #ff4d4f33", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#ff4d4f" }}>
          {researchError}
        </div>
      )}

      {/* Overview — Editable */}
      <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>OVERVIEW</div>
          {researchDone && <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT }}>AI-generated</span>}
        </div>
        <textarea value={overview} onChange={e => setOverview(e.target.value)} rows={4} style={{ ...inputStyle, background: COLORS.bg, resize: "vertical", lineHeight: 1.7 }} />
      </div>

      {/* Metrics — Editable */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {metrics.map((m, i) => (
          <div key={m.key} style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>{m.label.toUpperCase()}</div>
            <input value={m.value} onChange={e => { const u = [...metrics]; u[i] = { ...m, value: e.target.value }; setMetrics(u); }}
              style={{ width: "100%", padding: "4px 0", background: "transparent", border: "none", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text, fontFamily: FONT, fontSize: 18, fontWeight: 600, outline: "none" }}
            />
          </div>
        ))}
      </div>

      {/* Transcript Insights */}
      {transcripts.length > 0 && transcriptInsights.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 14 }}>💡</span>
            <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.warn, letterSpacing: "0.06em", fontWeight: 600 }}>INSIGHTS FROM TRANSCRIPTS</span>
            <span style={{ fontSize: 10, color: COLORS.textDim }}>— Review and accept to add to profile</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {transcriptInsights.filter(ti => !ti.accepted).map(insight => (
              <div key={insight.id} style={{ padding: "12px 16px", background: COLORS.warn + "08", border: `1px solid ${COLORS.warn}22`, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4 }}>{insight.text}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>Source: {insight.source}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setTranscriptInsights(prev => prev.map(ti => ti.id === insight.id ? { ...ti, accepted: true } : ti))}
                    style={{ padding: "4px 10px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 4, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Accept</button>
                  <button onClick={() => setTranscriptInsights(prev => prev.filter(ti => ti.id !== insight.id))}
                    style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Sections */}
      {customSections.map((sec, i) => (
        <div key={i} style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <input value={sec.title} onChange={e => { const u = [...customSections]; u[i] = { ...sec, title: e.target.value }; setCustomSections(u); }}
              placeholder="Section title..." style={{ background: "transparent", border: "none", color: COLORS.textDim, fontFamily: FONT, fontSize: 10, letterSpacing: "0.06em", fontWeight: 600, outline: "none", textTransform: "uppercase" }} />
            <button onClick={() => setCustomSections(prev => prev.filter((_, idx) => idx !== i))} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer" }}>×</button>
          </div>
          <textarea value={sec.content} onChange={e => { const u = [...customSections]; u[i] = { ...sec, content: e.target.value }; setCustomSections(u); }}
            rows={3} placeholder="Add your notes here..." style={{ ...inputStyle, background: COLORS.bg, resize: "vertical", lineHeight: 1.7 }} />
        </div>
      ))}

      <button onClick={() => setCustomSections([...customSections, { title: "", content: "" }])}
        style={{ padding: "10px 20px", background: "transparent", border: `1px dashed ${COLORS.border}`, borderRadius: 8, color: COLORS.textDim, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", width: "100%" }}>
        + Add Section
      </button>
    </div>
  );
}

function AuditSurveysTab({ project, onCountChange }) {
  const [view, setView] = useState("list");
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [responseData, setResponseData] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [builderTitle, setBuilderTitle] = useState("");
  const [builderDesc, setBuilderDesc] = useState("");
  const [builderQuestions, setBuilderQuestions] = useState([]);
  const [editingSurveyId, setEditingSurveyId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [distEmails, setDistEmails] = useState("");
  const [distMessage, setDistMessage] = useState("");
  const [distLink, setDistLink] = useState("");
  const [saveError, setSaveError] = useState(null);

  const TEMPLATES = [
    { id: "blank", name: "Blank Survey", desc: "Start from scratch", questions: [] },
    { id: "ai_readiness", name: "AI Readiness Assessment", desc: "Evaluate AI adoption preparedness", questions: [
      { id: "q1", type: "rating", question: "How would you rate your organisation's current use of AI?", options: [], required: true },
      { id: "q2", type: "multiple_choice", question: "Which departments currently use AI or automation tools?", options: ["Sales", "Marketing", "Operations", "Finance", "HR", "IT", "None"], required: true },
      { id: "q3", type: "checkboxes", question: "What are the biggest barriers to AI adoption?", options: ["Budget", "Lack of expertise", "Data quality", "Leadership buy-in", "Security concerns", "Unclear ROI"], required: true },
      { id: "q4", type: "long_text", question: "Describe one process that takes too much time and could benefit from automation.", options: [], required: false },
      { id: "q5", type: "multiple_choice", question: "How would you describe the organisation's attitude toward AI?", options: ["Very enthusiastic", "Cautiously optimistic", "Neutral", "Skeptical", "Resistant"], required: true },
    ]},
    { id: "tech_stack", name: "Technology Stack Review", desc: "Map current tools and systems", questions: [
      { id: "q1", type: "long_text", question: "List all software tools you use daily in your role.", options: [], required: true },
      { id: "q2", type: "multiple_choice", question: "How satisfied are you with your current tech stack?", options: ["Very satisfied", "Somewhat satisfied", "Neutral", "Dissatisfied", "Very dissatisfied"], required: true },
      { id: "q3", type: "checkboxes", question: "Which of these frustrations do you experience?", options: ["Too many tools", "Poor integration", "Slow performance", "Hard to learn", "Missing features", "Data silos"], required: true },
      { id: "q4", type: "text", question: "If you could replace one tool, what would it be and why?", options: [], required: false },
    ]},
    { id: "process_efficiency", name: "Process Efficiency Audit", desc: "Identify bottlenecks and waste", questions: [
      { id: "q1", type: "rating", question: "Rate the overall efficiency of your department's processes.", options: [], required: true },
      { id: "q2", type: "long_text", question: "What is your biggest time waster each week?", options: [], required: true },
      { id: "q3", type: "multiple_choice", question: "How much time per week do you spend on manual data entry?", options: ["<1 hour", "1-3 hours", "3-5 hours", "5-10 hours", "10+ hours"], required: true },
    ]},
  ];

  const FIELD_TYPES = [
    { key: "multiple_choice", label: "Multiple Choice", icon: "○" },
    { key: "checkboxes", label: "Checkboxes", icon: "☐" },
    { key: "text", label: "Short Text", icon: "—" },
    { key: "long_text", label: "Long Text", icon: "≡" },
    { key: "rating", label: "Rating Scale", icon: "★" },
    { key: "dropdown", label: "Dropdown", icon: "▾" },
  ];

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };

  const loadSurveys = () => {
    setLoading(true);
    api.audit.surveys.list(project?.id ? { project_id: project.id } : {}).then(data => {
      const list = Array.isArray(data) ? data : [];
      setSurveys(list);
      if (onCountChange) onCountChange(list.length);
    }).catch(() => { setSurveys([]); if (onCountChange) onCountChange(0); }).finally(() => setLoading(false));
  };

  useEffect(() => { loadSurveys(); }, [project?.id]);

  const saveSurvey = async (andDistribute) => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: builderTitle || "Untitled Survey",
        description: builderDesc || null,
        questions_json: builderQuestions,
        status: "active",
        ...(project?.id ? { project_id: project.id } : {}),
      };
      let saved;
      if (editingSurveyId) {
        saved = await api.audit.surveys.update(editingSurveyId, payload);
      } else {
        saved = await api.audit.surveys.create(payload);
      }
      loadSurveys();
      if (andDistribute && saved) {
        const s = { ...saved, questions: saved.questions || saved.questions_json || builderQuestions, responseData: [] };
        setSelectedSurvey(s);
        setDistLink(`${window.location.origin}/survey/${saved.id}`);
        setDistEmails("");
        setDistMessage(`Hi,\n\nYou've been invited to complete the "${s.title}" survey as part of our AI audit. It should take about 5 minutes.\n\nPlease complete it at your earliest convenience.\n\nThank you`);
        setView("distribute");
      } else {
        setView("list");
      }
    } catch (err) {
      console.error("Save survey error:", err);
      setSaveError(err.message || "Failed to save survey. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSurvey = async (id) => {
    try {
      await api.audit.surveys.delete(id);
      setSurveys(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error("Delete survey error:", err);
    }
  };

  const openResponses = async (survey) => {
    setSelectedSurvey(survey);
    setResponsesLoading(true);
    setView("responses");
    try {
      const data = await api.audit.surveys.listResponses(survey.id);
      setResponseData(Array.isArray(data) ? data : []);
    } catch {
      setResponseData([]);
    } finally {
      setResponsesLoading(false);
    }
  };

  const openEdit = (survey) => {
    setEditingSurveyId(survey.id);
    setBuilderTitle(survey.title);
    setBuilderDesc(survey.description || "");
    const q = survey.questions || survey.questions_json || [];
    setBuilderQuestions(Array.isArray(q) ? q : []);
    setSelectedTemplate("editing");
    setView("builder");
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl.id);
    setEditingSurveyId(null);
    setBuilderTitle(tpl.id === "blank" ? "" : tpl.name);
    setBuilderDesc(tpl.id === "blank" ? "" : tpl.desc);
    setBuilderQuestions(tpl.questions.map((q, i) => ({ ...q, id: `new_${i}` })));
  };

  const addQuestion = (type) => {
    setBuilderQuestions([...builderQuestions, {
      id: `new_${Date.now()}`, type, question: "", options: type === "multiple_choice" || type === "checkboxes" || type === "dropdown" ? ["Option 1"] : [], required: false,
    }]);
  };

  const updateQuestion = (idx, updates) => {
    const u = [...builderQuestions]; u[idx] = { ...u[idx], ...updates }; setBuilderQuestions(u);
  };

  const removeQuestion = (idx) => { setBuilderQuestions(builderQuestions.filter((_, i) => i !== idx)); };

  const openDistribute = (survey) => {
    setSelectedSurvey(survey);
    setDistLink(`${window.location.origin}/survey/${survey.id}`);
    setDistEmails("");
    setDistMessage(`Hi,\n\nYou've been invited to complete the "${survey.title}" survey as part of our AI audit. It should take about 5 minutes.\n\nPlease complete it at your earliest convenience.\n\nThank you`);
    setView("distribute");
  };

  // LIST VIEW
  if (view === "list") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>bar_chart</span>
            <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Surveys</h2>
          </div>
          <button onClick={() => { setView("builder"); setSelectedTemplate(null); setEditingSurveyId(null); setBuilderTitle(""); setBuilderDesc(""); setBuilderQuestions([]); }}
            style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ CREATE SURVEY</button>
        </div>
        {loading && <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>Loading surveys...</div>}
        {!loading && surveys.length === 0 && <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>No surveys yet. Create one to get started.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {surveys.map(survey => {
            const pct = survey.total > 0 ? Math.round((survey.responses / survey.total) * 100) : (survey.responses > 0 ? 100 : 0);
            return (
              <div key={survey.id} style={{ padding: "18px 22px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{survey.title}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{
                      padding: "3px 10px", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 500,
                      background: survey.status === "active" ? COLORS.accentBg : COLORS.surface,
                      color: survey.status === "active" ? COLORS.accent : COLORS.textDim,
                      border: `1px solid ${survey.status === "active" ? COLORS.accent + "33" : COLORS.border}`,
                    }}>{survey.status}</span>
                    <button onClick={() => deleteSurvey(survey.id)} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer", padding: "2px 4px" }}
                      onMouseEnter={e => e.currentTarget.style.color = COLORS.danger} onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}>×</button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>{survey.responses}{survey.total > 0 ? `/${survey.total}` : ""} responses</div>
                {survey.total > 0 && <div style={{ width: "100%", height: 4, borderRadius: 2, background: COLORS.border, marginBottom: 12 }}>
                  <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: COLORS.accent }} />
                </div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => openResponses(survey)}
                    style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>View Responses</button>
                  <button onClick={() => openEdit(survey)}
                    style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                  <button onClick={() => openDistribute(survey)}
                    style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>📨 Distribute</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // RESPONSES VIEW
  if (view === "responses" && selectedSurvey) {
    const questions = selectedSurvey.questions || selectedSurvey.questions_json || [];
    const respCount = responseData.length;
    const compRate = selectedSurvey.total > 0 ? Math.round((respCount / selectedSurvey.total) * 100) : (respCount > 0 ? 100 : 0);
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => { setView("list"); loadSurveys(); }} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>{selectedSurvey.title}</h2>
          <span style={{ padding: "3px 10px", background: COLORS.accentBg, color: COLORS.accent, fontSize: 10, borderRadius: 6, fontFamily: FONT, fontWeight: 500, border: `1px solid ${COLORS.accent}22` }}>{respCount} responses</span>
        </div>

        {responsesLoading && <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>Loading responses...</div>}

        {!responsesLoading && <>
          <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
            <StatCard label="Total Responses" value={respCount} accent={COLORS.accent} />
            <StatCard label="Completion Rate" value={`${compRate}%`} accent={COLORS.blue} />
            <StatCard label="Questions" value={questions.length} accent={COLORS.warn} />
          </div>

          {questions.length > 0 && <>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>QUESTION BREAKDOWN</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
              {questions.map((q, qi) => (
                <div key={q.id || qi} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, fontWeight: 600 }}>Q{qi + 1}</span>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textDim, fontFamily: FONT }}>{(q.type || "").replace("_", " ")}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>{q.question}</div>
                  {(q.type === "multiple_choice" || q.type === "checkboxes") && (q.options || []).length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {q.options.map(opt => {
                        const count = responseData.filter(r => { const ans = (r.answers || {})[q.id] || ""; return typeof ans === "string" ? ans.includes(opt) : false; }).length;
                        const pct = respCount > 0 ? Math.round((count / respCount) * 100) : 0;
                        return (
                          <div key={opt} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                <span style={{ fontSize: 12, color: COLORS.text }}>{opt}</span>
                                <span style={{ fontSize: 11, color: COLORS.textDim }}>{count} ({pct}%)</span>
                              </div>
                              <div style={{ width: "100%", height: 4, borderRadius: 2, background: COLORS.border }}>
                                <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: COLORS.accent, transition: "width 0.3s" }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {responseData.map((r, ri) => { const ans = (r.answers || {})[q.id]; return ans ? (
                        <div key={ri} style={{ padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
                          <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 2 }}>{ans}</div>
                          <div style={{ fontSize: 10, color: COLORS.textDim }}>— {r.respondent_name || "Anonymous"}{r.respondent_role ? `, ${r.respondent_role}` : ""}</div>
                        </div>
                      ) : null; })}
                      {responseData.length === 0 && <div style={{ fontSize: 12, color: COLORS.textDim, padding: 8 }}>No responses yet</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>}

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>INDIVIDUAL RESPONSES</div>
          {responseData.length === 0 && <div style={{ fontSize: 12, color: COLORS.textDim, padding: 8 }}>No responses submitted yet</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {responseData.map((r, i) => (
              <div key={r.id || i} style={{ padding: "14px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.respondent_name || "Anonymous"}</span>
                    {r.respondent_role && <span style={{ fontSize: 11, color: COLORS.textDim, marginLeft: 8 }}>{r.respondent_role}</span>}
                  </div>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : ""}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(r.answers || {}).map(([qId, ans]) => (
                    <div key={qId} style={{ padding: "4px 10px", background: COLORS.bg, borderRadius: 4, border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.textMuted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {typeof ans === "string" ? ans : JSON.stringify(ans)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>}
      </div>
    );
  }

  // DISTRIBUTE VIEW
  if (view === "distribute" && selectedSurvey) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => { setView("list"); loadSurveys(); }} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Distribute: {selectedSurvey.title}</h2>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ flex: 1, padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 16 }}>🔗</span>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Share Link</div>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>Anyone with this link can complete the survey.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={distLink} readOnly style={{ ...inputStyle, flex: 1, background: COLORS.bg, fontSize: 11 }} />
              <button onClick={() => navigator.clipboard?.writeText(distLink)}
                style={{ padding: "10px 16px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Copy Link</button>
            </div>
          </div>
          <div style={{ flex: 1, padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 16 }}>📧</span>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Email Invites</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>RECIPIENTS</label>
              <textarea value={distEmails} onChange={e => setDistEmails(e.target.value)} placeholder="Enter email addresses, one per line or comma-separated..." rows={3} style={{ ...inputStyle, background: COLORS.bg, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>MESSAGE</label>
              <textarea value={distMessage} onChange={e => setDistMessage(e.target.value)} rows={5} style={{ ...inputStyle, background: COLORS.bg, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 11, color: COLORS.textDim }}>Sends from: <span style={{ color: COLORS.accent }}>connected account email</span></div>
              <button style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>📨 Send Invites</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SURVEY BUILDER
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={() => { setView("list"); loadSurveys(); }} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
        <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>{editingSurveyId ? "Edit Survey" : "Create Survey"}</h2>
      </div>

      {!selectedTemplate && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>START FROM A TEMPLATE</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {TEMPLATES.map(tpl => (
              <button key={tpl.id} onClick={() => selectTemplate(tpl)} style={{
                padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10,
                cursor: "pointer", textAlign: "left", width: 200, transition: "border-color 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "66"}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
              >
                <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.text, marginBottom: 4 }}>{tpl.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{tpl.desc}</div>
                {tpl.questions.length > 0 && <div style={{ fontSize: 10, color: COLORS.accent, marginTop: 6 }}>{tpl.questions.length} questions</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedTemplate && (
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>SURVEY TITLE</label>
              <input value={builderTitle} onChange={e => setBuilderTitle(e.target.value)} placeholder="e.g. AI Readiness Assessment" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>DESCRIPTION</label>
              <input value={builderDesc} onChange={e => setBuilderDesc(e.target.value)} placeholder="Brief description of the survey..." style={inputStyle} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {builderQuestions.map((q, i) => {
                const fieldType = FIELD_TYPES.find(f => f.key === q.type);
                return (
                  <div key={q.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, fontWeight: 600 }}>Q{i + 1}</span>
                        <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: COLORS.bg, color: COLORS.textDim, fontFamily: FONT, border: `1px solid ${COLORS.border}` }}>{fieldType?.icon} {fieldType?.label}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: COLORS.textDim, cursor: "pointer" }}>
                          <input type="checkbox" checked={q.required} onChange={e => updateQuestion(i, { required: e.target.checked })} />
                          Required
                        </label>
                        <button onClick={() => removeQuestion(i)} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.color = COLORS.danger} onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}>×</button>
                      </div>
                    </div>
                    <input value={q.question} onChange={e => updateQuestion(i, { question: e.target.value })}
                      placeholder="Type your question..." style={{ ...inputStyle, background: COLORS.bg, marginBottom: q.options?.length > 0 ? 10 : 0 }} />
                    {(q.type === "multiple_choice" || q.type === "checkboxes" || q.type === "dropdown") && (
                      <div>
                        {(q.options || []).map((opt, oi) => (
                          <div key={oi} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ color: COLORS.textDim, fontSize: 12 }}>{q.type === "checkboxes" ? "☐" : q.type === "dropdown" ? "▾" : "○"}</span>
                            <input value={opt} onChange={e => { const newOpts = [...q.options]; newOpts[oi] = e.target.value; updateQuestion(i, { options: newOpts }); }}
                              style={{ ...inputStyle, background: COLORS.bg, flex: 1, padding: "6px 10px", fontSize: 12 }} />
                            <button onClick={() => { const newOpts = q.options.filter((_, idx) => idx !== oi); updateQuestion(i, { options: newOpts }); }}
                              style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 12, cursor: "pointer" }}>×</button>
                          </div>
                        ))}
                        <button onClick={() => updateQuestion(i, { options: [...(q.options || []), `Option ${(q.options || []).length + 1}`] })}
                          style={{ padding: "4px 12px", background: "transparent", border: `1px dashed ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 10, cursor: "pointer", marginTop: 4 }}>+ Add Option</button>
                      </div>
                    )}
                    {q.type === "rating" && (
                      <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                        {[1,2,3,4,5].map(n => (
                          <div key={n} style={{ width: 32, height: 32, borderRadius: 6, background: COLORS.bg, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: COLORS.textDim }}>{n}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>ADD QUESTION</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FIELD_TYPES.map(ft => (
                  <button key={ft.key} onClick={() => addQuestion(ft.key)} style={{
                    padding: "8px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                    color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent + "66"; e.currentTarget.style.color = COLORS.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
                  >{ft.icon} {ft.label}</button>
                ))}
              </div>
            </div>

            {saveError && (
              <div style={{ padding: "10px 14px", background: COLORS.danger + "15", border: `1px solid ${COLORS.danger}33`, borderRadius: 8, marginBottom: 10, fontSize: 12, color: COLORS.danger }}>{saveError}</div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => saveSurvey(false)} disabled={saving} style={{ padding: "12px 24px", background: saving ? COLORS.border : COLORS.accent, color: saving ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>{saving ? "Saving..." : "Save Survey"}</button>
              <button onClick={() => saveSurvey(true)} disabled={saving} style={{ padding: "12px 24px", background: saving ? COLORS.border : COLORS.blue, color: saving ? COLORS.textDim : "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>Save & Distribute →</button>
            </div>
          </div>

          <div style={{ width: 340, flexShrink: 0 }}>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>PREVIEW</div>
            <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, position: "sticky", top: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{builderTitle || "Untitled Survey"}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20 }}>{builderDesc || "No description"}</div>
              {builderQuestions.length === 0 && <div style={{ fontSize: 12, color: COLORS.textDim, textAlign: "center", padding: "20px 0" }}>No questions yet</div>}
              {builderQuestions.map((q, i) => (
                <div key={q.id} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>
                    {q.question || `Question ${i + 1}`}
                    {q.required && <span style={{ color: COLORS.danger, marginLeft: 2 }}>*</span>}
                  </div>
                  {q.type === "text" && <div style={{ padding: "8px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, color: COLORS.textDim }}>Short answer</div>}
                  {q.type === "long_text" && <div style={{ padding: "8px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, color: COLORS.textDim, minHeight: 48 }}>Long answer</div>}
                  {q.type === "rating" && <div style={{ display: "flex", gap: 3 }}>{[1,2,3,4,5].map(n => <div key={n} style={{ width: 24, height: 24, borderRadius: 4, background: COLORS.bg, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: COLORS.textDim }}>{n}</div>)}</div>}
                  {(q.type === "multiple_choice" || q.type === "checkboxes") && (q.options || []).map((opt, oi) => (
                    <div key={oi} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>{q.type === "checkboxes" ? "☐" : "○"}</span>
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>{opt}</span>
                    </div>
                  ))}
                  {q.type === "dropdown" && <div style={{ padding: "6px 10px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, color: COLORS.textDim }}>▾ Select an option</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditInterviewsTab({ project, onCountChange }) {
  const [interviewees, setInterviewees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", role: "", type: "Stakeholder", department: "", context: "" });
  const [generating, setGenerating] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 };

  const DEFAULT_QUESTIONS = [
    { section: "Opening & Context", questions: ["Can you walk me through your typical day-to-day responsibilities?", "How long have you been in this role and what's changed most since you started?", "What does success look like for your department this year?"] },
    { section: "Pain Points & Challenges", questions: ["What are the biggest bottlenecks or frustrations in your current workflow?", "Where do you feel the most time is wasted in your team's processes?", "If you could fix one thing about how your department operates, what would it be?", "What manual or repetitive tasks take up the most time?"] },
    { section: "Technology & Systems", questions: ["What tools and systems do you currently use? Which do you love and which frustrate you?", "How do you currently handle reporting and data analysis?", "Are there any tasks you wish were automated but aren't?"] },
    { section: "Strategic Alignment", questions: ["How do you see AI fitting into your department's operations?", "What would a successful AI implementation look like from your perspective?", "What concerns, if any, do you have about introducing AI tools?", "How would you measure the ROI of any new system or process?"] },
  ];

  useEffect(() => {
    setLoading(true);
    api.audit.interviews.list(project?.id ? { project_id: project.id } : {}).then(data => {
      const interviews = Array.isArray(data) ? data : [];
      const mapped = interviews.map(i => {
        const q = i.questions || i.questions_json || DEFAULT_QUESTIONS;
        const qCount = Array.isArray(q) ? q.reduce((s, sec) => s + (sec.questions?.length || 0), 0) : 0;
        return {
          id: i.id,
          name: i.interviewee_name || "",
          role: i.interviewee_role || "",
          type: i.interviewee_type || "Stakeholder",
          department: i.department || "",
          status: i.status || "generated",
          questionCount: qCount,
          questions: q,
        };
      });
      setInterviewees(mapped);
      if (onCountChange) onCountChange(mapped.length);
    }).catch(() => { setInterviewees([]); if (onCountChange) onCountChange(0); }).finally(() => setLoading(false));
  }, [project?.id]);

  const handleGenerate = async () => {
    if (!formData.name.trim() || !formData.role.trim()) return;
    setGenerating(true);
    try {
      const saved = await api.audit.interviews.generate({
        interviewee_name: formData.name,
        interviewee_role: formData.role,
        interviewee_type: formData.type,
        department: formData.department,
        context: formData.context || null,
        ...(project?.id ? { project_id: project.id } : {}),
      });
      const q = saved.questions || saved.questions_json || DEFAULT_QUESTIONS;
      const qCount = Array.isArray(q) ? q.reduce((s, sec) => s + (sec.questions?.length || 0), 0) : 0;
      const newEntry = {
        id: saved.id,
        name: saved.interviewee_name || formData.name,
        role: saved.interviewee_role || formData.role,
        type: saved.interviewee_type || formData.type,
        department: saved.department || formData.department,
        status: "generated",
        questionCount: qCount,
        questions: q,
      };
      setInterviewees(prev => [newEntry, ...prev]);
      setSelectedInterview(newEntry);
      setShowForm(false);
      setFormData({ name: "", role: "", type: "Stakeholder", department: "", context: "" });
    } catch (err) {
      console.error("Create interview error:", err);
    } finally {
      setGenerating(false);
    }
  };

  const deleteInterview = async (id) => {
    try {
      await api.audit.interviews.delete(id);
      setInterviewees(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error("Delete interview error:", err);
    }
  };

  const getDisplayQuestions = (interview) => {
    const q = interview?.questions || DEFAULT_QUESTIONS;
    return Array.isArray(q) ? q : DEFAULT_QUESTIONS;
  };

  const copyAllQuestions = (interview) => {
    const sections = getDisplayQuestions(interview);
    const text = sections.map(s => `## ${s.section}\n${s.questions.map((q, i) => `${i + 1}. ${q}`).join("\n")}`).join("\n\n");
    navigator.clipboard?.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  if (selectedInterview) {
    const sections = getDisplayQuestions(selectedInterview);
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => setSelectedInterview(null)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{selectedInterview.name}</div>
            <div style={{ fontSize: 11, color: COLORS.textDim }}>{selectedInterview.role} · {selectedInterview.department} · {selectedInterview.type}</div>
          </div>
          <button onClick={() => copyAllQuestions(selectedInterview)} style={{ padding: "8px 16px", background: copySuccess ? COLORS.accentBg : "transparent", border: `1px solid ${copySuccess ? COLORS.accent + "33" : COLORS.border}`, borderRadius: 6, color: copySuccess ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>{copySuccess ? "Copied!" : "Copy All"}</button>
        </div>
        {sections.map((section, si) => (
          <div key={si} style={{ marginBottom: 16, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.accent, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10 }}>{(section.section || "").toUpperCase()}</div>
            {(section.questions || []).map((q, qi) => (
              <div key={qi} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: qi < section.questions.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, fontWeight: 600, minWidth: 20 }}>{qi + 1}.</span>
                <span style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>{q}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>Interview Question Generator</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>AI-generated questions tailored to each interviewee's role and department</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{ padding: "8px 18px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>+ New Interview</button>
      </div>

      {showForm && (
        <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderRadius: 12, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div><label style={labelStyle}>INTERVIEWEE NAME</label><input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Sarah Mitchell" style={inputStyle} /></div>
            <div><label style={labelStyle}>ROLE / TITLE</label><input value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} placeholder="e.g. CFO, Estate Manager" style={inputStyle} /></div>
            <div>
              <label style={labelStyle}>TYPE</label>
              <div style={{ display: "flex", gap: 6 }}>
                {["Stakeholder", "End User"].map(t => (
                  <button key={t} onClick={() => setFormData({ ...formData, type: t })} style={{ flex: 1, padding: "8px", borderRadius: 6, border: `1px solid ${formData.type === t ? COLORS.accent + "55" : COLORS.border}`, background: formData.type === t ? COLORS.accentBg : "transparent", color: formData.type === t ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{t}</button>
                ))}
              </div>
            </div>
            <div><label style={labelStyle}>DEPARTMENT</label><input value={formData.department} onChange={e => setFormData({ ...formData, department: e.target.value })} placeholder="e.g. Finance, Operations" style={inputStyle} /></div>
          </div>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>FOCUS AREAS / CONTEXT (OPTIONAL)</label><textarea value={formData.context} onChange={e => setFormData({ ...formData, context: e.target.value })} placeholder="Any specific areas to focus on, known pain points, or context about this person's role..." rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} /></div>
          <button onClick={handleGenerate} disabled={generating || !formData.name.trim() || !formData.role.trim()} style={{ padding: "10px 24px", background: formData.name.trim() && formData.role.trim() ? COLORS.accent : COLORS.border, color: formData.name.trim() && formData.role.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: formData.name.trim() && formData.role.trim() && !generating ? "pointer" : "default" }}>{generating ? "Generating..." : <><span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>mic</span>Generate Interview Questions</>}</button>
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>Loading interviews...</div>}
      {!loading && interviewees.length === 0 && !showForm && <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim, fontSize: 13 }}>No interviews yet. Click "+ New Interview" to get started.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {interviewees.map(person => (
          <div key={person.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "44"} onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
            <div onClick={() => setSelectedInterview(person)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: COLORS.accent + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><span className="material-symbols-outlined" style={{ fontSize: 18 }}>mic</span></div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{person.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{person.role} · {person.department} · {person.questionCount} questions</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 9, fontFamily: FONT, fontWeight: 500, background: person.type === "Stakeholder" ? COLORS.accent + "10" : COLORS.blue + "10", color: person.type === "Stakeholder" ? COLORS.accent : COLORS.blue, border: `1px solid ${person.type === "Stakeholder" ? COLORS.accent : COLORS.blue}22` }}>{person.type}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteInterview(person.id); }} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer", padding: "2px 4px" }}
                onMouseEnter={e => e.currentTarget.style.color = COLORS.danger} onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}>×</button>
              <span onClick={() => setSelectedInterview(person)} style={{ fontSize: 10, color: COLORS.textDim, cursor: "pointer" }}>→</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditTranscriptsTab({ project, transcripts, setTranscripts }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [addContent, setAddContent] = useState("");
  const [addName, setAddName] = useState("");
  const [addSpeaker, setAddSpeaker] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };

  const handleAddTranscript = async () => {
    const content = addContent.trim();
    if (!content) {
      setSaveError("Please paste transcript content.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const name = addName.trim() || "Transcript " + new Date().toLocaleDateString();
      const saved = await api.audit.transcripts.create({
        name,
        content_text: content,
        speaker_name: addSpeaker.trim() || null,
        source: "manual",
        ...(project?.id ? { project_id: project.id } : {}),
      });
      const newEntry = {
        ...saved,
        tags: saved.tags || [],
        speaker: saved.speaker_name || "",
        role: saved.speaker_role || "",
        duration: saved.duration_minutes ?? null,
      };
      setTranscripts(prev => [newEntry, ...prev]);
      setShowAddModal(false);
      setAddContent("");
      setAddName("");
      setAddSpeaker("");
    } catch (err) {
      console.error("Add transcript error:", err);
      setSaveError(err?.message || "Failed to save transcript");
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setAddContent("");
    setAddName("");
    setAddSpeaker("");
    setSaveError(null);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>description</span>
          <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Transcripts</h2>
        </div>
        <button onClick={() => setShowAddModal(true)} style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ ADD TRANSCRIPT</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {transcripts.map(t => (
          <div key={t.id} style={{ padding: "14px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", flexDirection: "column", gap: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{t.speaker} · {t.role} · {t.duration ?? "—"} min</div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  {(t.tags || []).map(tag => (
                    <span key={tag} style={{ padding: "2px 8px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>{tag}</span>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button onClick={() => setExpandedId(expandedId === t.id ? null : t.id)} style={{ padding: "4px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontSize: 12, cursor: "pointer", fontFamily: FONT }} title={expandedId === t.id ? "Hide content" : "View content"}>{expandedId === t.id ? "▾ Hide" : "▸ View content"}</button>
                <button onClick={() => api.audit.transcripts.delete(t.id).then(() => setTranscripts(prev => prev.filter(tr => tr.id !== t.id))).catch(e => console.error("Delete transcript error:", e))} style={{ padding: "4px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.color = COLORS.danger}
                  onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}
                >🗑</button>
              </div>
            </div>
            {expandedId === t.id && (
              <div style={{ marginTop: 12, padding: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, maxHeight: 300, overflow: "auto", fontSize: 12, lineHeight: 1.6, color: COLORS.text, whiteSpace: "pre-wrap" }}>
                {(t.content_text || t.content || "").trim() || "No transcript content."}
              </div>
            )}
          </div>
        ))}
      </div>
      {showAddModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div onClick={e => e.stopPropagation()} style={{ width: 560, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Add Transcript</div>
              <button onClick={closeModal} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 18, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              {saveError && <div style={{ marginBottom: 12, padding: 10, background: "rgba(255,80,80,0.1)", border: `1px solid ${COLORS.danger}44`, borderRadius: 8, color: COLORS.danger, fontSize: 12 }}>{saveError}</div>}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>NAME (OPTIONAL)</label>
                <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="e.g. CTO Interview" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>SPEAKER (OPTIONAL)</label>
                <input value={addSpeaker} onChange={e => setAddSpeaker(e.target.value)} placeholder="e.g. John Smith, CEO" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>TRANSCRIPT CONTENT</label>
                <textarea value={addContent} onChange={e => setAddContent(e.target.value)} placeholder="Paste transcript content here..." rows={10} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={closeModal} disabled={saving} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>Cancel</button>
              <button onClick={handleAddTranscript} disabled={saving || !addContent.trim()} style={{ padding: "10px 24px", background: (addContent.trim() && !saving) ? COLORS.accent : COLORS.border, color: (addContent.trim() && !saving) ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: (addContent.trim() && !saving) ? "pointer" : "default" }}>{saving ? "Saving..." : "Add Transcript"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function AuditProcessMapsTab({ project }) {
  const [mapView, setMapView] = useState("business");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const PROCESS_MAP_QUESTIONS = [
    "What area do you want to map first — Acquisition (getting leads/clients), Delivery (how you serve them), Support (ongoing service), or all three?",
    "Which industry or customer segment is this map for? (We'll use your ICP data if available; otherwise describe briefly.)",
    "What are the biggest bottlenecks or pain points you've heard in interviews or transcripts?",
    "What tools does the team currently use? (CRM, outreach, discovery, etc.)",
    "Who owns these processes? (Roles like SDR, AE, CSM, Ops — we'll infer from transcripts if available.)",
    "Do you want process-level only (high-level blocks), or should I also suggest detailed step-by-step workflows for key processes?",
  ];
  const [messages, setMessages] = useState([{ role: "agent", text: PROCESS_MAP_QUESTIONS[0] }]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [mapGenerated, setMapGenerated] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAddProcess, setShowAddProcess] = useState(null); // pillar id
  const [newProcessLabel, setNewProcessLabel] = useState("");
  const [newProcessOwner, setNewProcessOwner] = useState("");
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStepLabel, setNewStepLabel] = useState("");
  const [editingProcess, setEditingProcess] = useState(null); // { pillarId, procId }
  const [editingStep, setEditingStep] = useState(null); // { procId, stepIdx }
  const [editForm, setEditForm] = useState({});
  const [pillars, setPillars] = useState([]);

  useEffect(() => {
    const projectId = project?.id ? String(project.id) : null;
    api.audit.processMaps.list(projectId).then(data => {
      const maps = Array.isArray(data) ? data : data.process_maps || [];
      let filtered = maps;
      if (projectId) {
        filtered = maps.filter(m => !m.project_id || String(m.project_id) === projectId);
      }
      if (filtered.length > 0) {
        const grouped = {};
        filtered.forEach(m => {
          let desc = {};
          try { desc = typeof m.description === 'string' ? JSON.parse(m.description) : (m.description || {}); } catch (_) {}
          const pillarId = desc.pillar_id || m.pillar_id || m.category || 'other';
          const node0 = m.nodes && m.nodes[0];
          if (!grouped[pillarId]) grouped[pillarId] = { id: pillarId, label: desc.pillar_label || m.pillar_label || pillarId, icon: desc.pillar_icon || m.pillar_icon || '📋', desc: desc.pillar_desc || m.pillar_desc || '', processes: [] };
          grouped[pillarId].processes.push({
            id: m.id,
            label: m.title || node0?.label || m.name || m.label || '',
            owner: desc.owner || node0?.owner || m.owner || '',
            time: desc.time || node0?.time || m.time_estimate || m.time || '',
            tag: desc.tag || node0?.tag || m.tag || 'neutral',
            tools: desc.tools || node0?.tools || m.tools || '',
            aiRec: desc.aiRec || node0?.aiRec || m.ai_recommendation || m.aiRec || '',
            savings: desc.savings || node0?.savings || m.estimated_savings || m.savings || '',
            source_refs: desc.source_refs || node0?.source_refs || [],
          });
        });
        const next = Object.values(grouped);
        setPillars(next);
        if (next.length > 0) setMapGenerated(true);
      }
    }).catch(() => {});
  }, [project?.id]);
  const [processFlows, setProcessFlows] = useState({
    a1: [
      { step: 1, label: "Open LinkedIn Sales Navigator", owner: "SDR", time: "5 min", tag: "timesink", aiOverlay: null },
      { step: 2, label: "Run saved search with ICP filters", owner: "SDR", time: "10 min", tag: "timesink", aiOverlay: null },
      { step: 3, label: "Manually review profiles (50-100)", owner: "SDR", time: "45 min", tag: "timesink", aiOverlay: "AI Ark auto-discovery replaces this entirely" },
      { step: 4, label: "Export to CSV", owner: "SDR", time: "5 min", tag: "timesink", aiOverlay: null },
      { step: 5, label: "Upload to CRM, check for duplicates", owner: "SDR", time: "20 min", tag: "timesink", aiOverlay: "Pipeline auto-deduplication on import" },
      { step: 6, label: "Manually find email addresses", owner: "SDR", time: "60 min", tag: "timesink", aiOverlay: "BetterContact / Icypeas waterfall verification" },
      { step: 7, label: "Write personalised first lines", owner: "SDR", time: "90 min", tag: "timesink", aiOverlay: "AI personalisation agent (2 credits/lead)" },
      { step: 8, label: "Load into outreach tool, launch sequence", owner: "SDR", time: "15 min", tag: "aiready", aiOverlay: "One-click campaign sync from Lead Lists" },
    ],
    a3: [
      { step: 1, label: "Select leads for outreach", owner: "SDR", time: "15 min", tag: "timesink", aiOverlay: "Auto-selected by ICP score" },
      { step: 2, label: "Draft cold email sequence", owner: "SDR", time: "45 min", tag: "timesink", aiOverlay: "AI Messaging Workshop generates sequences" },
      { step: 3, label: "Send LinkedIn connection requests", owner: "SDR", time: "30 min", tag: "timesink", aiOverlay: "HeyReach / AimFox automation" },
      { step: 4, label: "Monitor replies, respond manually", owner: "SDR", time: "30 min", tag: "timesink", aiOverlay: "AI SDR auto-responds within 5 min" },
    ],
  });

  const addProcess = (pillarId) => {
    if (!newProcessLabel.trim()) return;
    setPillars(prev => prev.map(p => p.id === pillarId ? { ...p, processes: [...p.processes, { id: `${pillarId[0]}${Date.now()}`, label: newProcessLabel, owner: newProcessOwner || "TBD", time: "TBD", tag: "qualityrisk", tools: "TBD", aiRec: "", savings: "" }] } : p));
    setNewProcessLabel(""); setNewProcessOwner(""); setShowAddProcess(null);
  };
  const removeProcess = (pillarId, procId) => { setPillars(prev => prev.map(p => p.id === pillarId ? { ...p, processes: p.processes.filter(pr => pr.id !== procId) } : p)); };
  const moveProcess = (pillarId, procIdx, dir) => {
    setPillars(prev => prev.map(p => { if (p.id !== pillarId) return p; const procs = [...p.processes]; const ni = procIdx + dir; if (ni < 0 || ni >= procs.length) return p; [procs[procIdx], procs[ni]] = [procs[ni], procs[procIdx]]; return { ...p, processes: procs }; }));
  };
  const startEditProcess = (pillarId, proc) => { setEditingProcess({ pillarId, procId: proc.id }); setEditForm({ label: proc.label, owner: proc.owner, time: proc.time, tools: proc.tools, tag: proc.tag, aiRec: proc.aiRec || "", savings: proc.savings || "" }); };
  const saveEditProcess = () => {
    if (!editingProcess) return;
    const procId = editingProcess.procId;
    setPillars(prev => prev.map(p => p.id === editingProcess.pillarId ? { ...p, processes: p.processes.map(pr => pr.id === procId ? { ...pr, ...editForm } : pr) } : p));
    // Debounced auto-save to API
    processAutoSave.trigger(procId, {
      name: editForm.label,
      owner: editForm.owner,
      time_estimate: editForm.time,
      tools: editForm.tools,
      tag: editForm.tag,
      ai_recommendation: editForm.aiRec,
      estimated_savings: editForm.savings,
    });
    setEditingProcess(null); setEditForm({});
  };
  const addStep = (procId) => {
    if (!newStepLabel.trim()) return;
    setProcessFlows(prev => { const steps = prev[procId] || []; return { ...prev, [procId]: [...steps, { step: steps.length + 1, label: newStepLabel, owner: "TBD", time: "TBD", tag: "qualityrisk", aiOverlay: null }] }; });
    setNewStepLabel(""); setShowAddStep(false);
  };
  const removeStep = (procId, stepIdx) => {
    setProcessFlows(prev => { const steps = (prev[procId] || []).filter((_, i) => i !== stepIdx).map((s, i) => ({ ...s, step: i + 1 })); return { ...prev, [procId]: steps }; });
  };
  const moveStep = (procId, stepIdx, dir) => {
    setProcessFlows(prev => { const steps = [...(prev[procId] || [])]; const ni = stepIdx + dir; if (ni < 0 || ni >= steps.length) return prev; [steps[stepIdx], steps[ni]] = [steps[ni], steps[stepIdx]]; return { ...prev, [procId]: steps.map((s, i) => ({ ...s, step: i + 1 })) }; });
  };
  const startEditStep = (procId, stepIdx) => { const step = (processFlows[procId] || [])[stepIdx]; if (!step) return; setEditingStep({ procId, stepIdx }); setEditForm({ label: step.label, owner: step.owner, time: step.time, tag: step.tag, aiOverlay: step.aiOverlay || "" }); };
  const saveEditStep = () => {
    if (!editingStep) return;
    setProcessFlows(prev => { const steps = [...(prev[editingStep.procId] || [])]; steps[editingStep.stepIdx] = { ...steps[editingStep.stepIdx], ...editForm, aiOverlay: editForm.aiOverlay || null }; return { ...prev, [editingStep.procId]: steps }; });
    setEditingStep(null); setEditForm({});
  };

  const chatEndRef = React.useRef(null);
  React.useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages, isTyping]);

  const TAG_CONFIG = { timesink: { label: "Time Sink", color: COLORS.danger, icon: "🔴" }, qualityrisk: { label: "Quality Risk", color: COLORS.warn, icon: "🟡" }, aiready: { label: "AI Ready", color: COLORS.green, icon: "🟢" }, optimised: { label: "Optimised", color: COLORS.blue, icon: "🔵" } };
  const PILLAR_COLORS = { acquisition: COLORS.blue, delivery: "#7B61FF", support: COLORS.warn };

  const [mapError, setMapError] = useState(null);
  const [exportStatus, setExportStatus] = useState(null); // { type, msg } or null
  const processMapContainerRef = useRef(null);

  // ── Export handlers ──
  async function handleExport(type) {
    setShowExportMenu(false);
    if (!pillars.length) return;
    setExportStatus({ type, msg: "Exporting..." });
    try {
      if (type === "pptx") {
        const { blob, filename } = await api.audit.processMaps.exportPptx(pillars);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        setExportStatus({ type, msg: "Downloaded!" });
      } else if (type === "pdf") {
        const html = await api.audit.processMaps.exportPdf(pillars);
        const win = window.open("", "_blank");
        win.document.write(html);
        win.document.close();
        setExportStatus({ type, msg: "Opened in new tab" });
      } else if (type === "png") {
        const el = processMapContainerRef.current;
        if (!el) { setExportStatus({ type, msg: "No map to export" }); return; }
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(el, { backgroundColor: "#0a0a0f", scale: 2, useCORS: true, logging: false });
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = "Process_Map.png"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        }, "image/png");
        setExportStatus({ type, msg: "Downloaded!" });
      } else if (type === "deck") {
        const result = await api.audit.processMaps.addToDeck(pillars);
        setExportStatus({ type, msg: `Added ${result.slidesAdded} slides to deck` });
      }
    } catch (err) {
      setExportStatus({ type, msg: "Error: " + (err.message || "Failed") });
    }
    setTimeout(() => setExportStatus(null), 3000);
  }

  // Auto-save for process map edits
  const processAutoSave = useAutoSave(async (processId, updates) => {
    await api.audit.processMaps.update(processId, updates);
  }, 800);

  const userAnswerCount = messages.filter(m => m.role === "user").length;
  const workshopStep = Math.min(userAnswerCount, PROCESS_MAP_QUESTIONS.length);

  const sendMessage = async () => {
    if (!input.trim() || isTyping) return;
    const userMsg = input; const nm = [...messages, { role: "user", text: userMsg }]; setMessages(nm); setInput(""); setIsTyping(true);
    setMapError(null);
    try {
      const result = await api.audit.processMaps.chat({
        messages: nm,
        project_id: project?.id || null,
        analysis_context: { themes: [], opportunities: [] },
      });
      const newPillars = result.pillars || result.process_map || [];
      if (newPillars && newPillars.length > 0) {
        setPillars(newPillars.map(p => ({
          id: p.id || p.pillar_id,
          label: p.label || p.name,
          icon: p.icon || (p.id === 'acquisition' ? '🎯' : p.id === 'delivery' ? '🚀' : '🛟'),
          desc: p.desc || p.description || '',
          processes: (p.processes || []).map(proc => ({
            id: proc.id || proc.process_id || `p${Date.now()}${Math.random()}`,
            label: proc.label || proc.name,
            owner: proc.owner || 'TBD',
            time: proc.time || proc.time_estimate || '',
            tag: proc.tag || 'neutral',
            tools: proc.tools || '',
            aiRec: proc.aiRec || proc.ai_recommendation || '',
            savings: proc.savings || proc.estimated_savings || '',
            source_refs: proc.source_refs || [],
          })),
        })));
        setMapGenerated(true);
      }
      setMessages([...nm, { role: "agent", text: result.agentText || "Done." }]);
    } catch (err) {
      const msg = err.message || 'Failed to generate process map';
      setMapError(msg);
      setMessages([...nm, { role: "agent", text: `Sorry, I encountered an error: ${msg}` }]);
    }
    setIsTyping(false);
  };

  return (
    <div style={{ display: "flex", gap: 0, flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Left: Chat */}
      <div style={{ width: 360, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🗺️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Process Map Builder</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>{mapGenerated ? "Refine & explore" : `Step ${workshopStep + 1} of ${PROCESS_MAP_QUESTIONS.length}`}</div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "88%", padding: "10px 14px", borderRadius: 12, background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface, border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`, borderBottomRightRadius: msg.role === "user" ? 4 : 12, borderBottomLeftRadius: msg.role === "agent" ? 4 : 12 }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI ANALYST</div>}
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {isTyping && <div style={{ display: "flex" }}><div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}><ProgressDots active={true} /></div></div>}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) sendMessage(); }} placeholder={mapGenerated ? "Refine the map..." : "Type your answer..."} style={{ flex: 1, padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }} disabled={isTyping} />
          <button onClick={sendMessage} disabled={isTyping || !input.trim()} style={{ padding: "10px 18px", background: input.trim() && !isTyping ? COLORS.accent : COLORS.border, color: input.trim() && !isTyping ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: input.trim() && !isTyping ? "pointer" : "default" }}>Send</button>
        </div>
      </div>

      {/* Right: Map View */}
      <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
        {!mapGenerated ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 420 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>🗺️</div>
              <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: COLORS.textDim, marginBottom: 8 }}>Process Maps</div>
              <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.6, marginBottom: 20 }}>Answer the 6 questions in the chat to build a tailored process map. We'll map 15+ processes across Acquisition, Delivery, and Support with AI opportunity tags.</div>
              <button onClick={() => { setInput("All three"); }} style={{ padding: "12px 24px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Quick start: map all three areas</button>
            </div>
          </div>
        ) : (
          <div ref={processMapContainerRef}>
            {/* Export status toast */}
            {exportStatus && (
              <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 18px", background: exportStatus.msg.startsWith("Error") ? COLORS.danger + "20" : COLORS.accent + "20", border: `1px solid ${exportStatus.msg.startsWith("Error") ? COLORS.danger : COLORS.accent}50`, borderRadius: 8, color: exportStatus.msg.startsWith("Error") ? COLORS.danger : COLORS.accent, fontFamily: FONT, fontSize: 11, fontWeight: 600 }}>{exportStatus.msg}</div>
            )}
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {mapView === "process" && <button onClick={() => { setMapView("business"); setSelectedProcess(null); }} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>← Business Flow</button>}
                <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700 }}>{mapView === "business" ? "Business Process Flow" : selectedProcess?.label}</span>
                <AutoSaveIndicator status={processAutoSave.saveStatus} />
                {mapView === "process" && <span style={{ fontSize: 10, color: COLORS.textDim }}>{selectedProcess?.owner} · {selectedProcess?.time}</span>}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {Object.entries(TAG_CONFIG).map(([k, v]) => (
                  <span key={k} style={{ fontSize: 9, display: "flex", alignItems: "center", gap: 3, color: COLORS.textDim }}>{v.icon} {v.label}</span>
                ))}
                <div style={{ width: 1, height: 16, background: COLORS.border, margin: "0 4px" }} />
                <div style={{ position: "relative" }}>
                  <button onClick={() => setShowExportMenu(!showExportMenu)} style={{ padding: "6px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>📤 Export <span style={{ fontSize: 8 }}>▼</span></button>
                  {showExportMenu && (
                    <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 50, minWidth: 200, overflow: "hidden" }}>
                      {[
                        { label: "Add to Audit Deck", desc: "Append slides to project deck", icon: "📊", type: "deck" },
                        { label: "Download as PPTX", desc: "Standalone presentation", icon: "📑", type: "pptx" },
                        { label: "Export as PDF", desc: "Print-ready document", icon: "📄", type: "pdf" },
                        { label: "Export as PNG", desc: "Image for Miro / Figma / Docs", icon: "🖼️", type: "png" },
                      ].map((opt, i) => (
                        <div key={i} onClick={() => handleExport(opt.type)} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: i < 3 ? `1px solid ${COLORS.border}` : "none", display: "flex", alignItems: "center", gap: 10 }}
                          onMouseEnter={e => e.currentTarget.style.background = COLORS.accentBg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={{ fontSize: 14 }}>{opt.icon}</span>
                          <div><div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div><div style={{ fontSize: 9, color: COLORS.textDim }}>{opt.desc}</div></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Business Flow — Horizontal Left to Right */}
            {mapView === "business" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {pillars.map(pillar => {
                  const pc = PILLAR_COLORS[pillar.id];
                  return (
                    <div key={pillar.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderLeft: `4px solid ${pc}`, overflow: "hidden" }}>
                      {/* Pillar Header */}
                      <div style={{ padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 16 }}>{pillar.icon}</span>
                          <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: pc }}>{pillar.label}</span>
                          <span style={{ fontSize: 10, color: COLORS.textDim, fontStyle: "italic" }}>{pillar.desc}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>{pillar.processes.length} processes</span>
                          <button onClick={() => { setShowAddProcess(showAddProcess === pillar.id ? null : pillar.id); setNewProcessLabel(""); setNewProcessOwner(""); }} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${pc}44`, borderRadius: 4, color: pc, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add</button>
                        </div>
                      </div>
                      {/* Add Process Form */}
                      {showAddProcess === pillar.id && (
                        <div style={{ padding: "10px 20px", background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 8, alignItems: "center" }}>
                          <input value={newProcessLabel} onChange={e => setNewProcessLabel(e.target.value)} placeholder="Process name" onKeyDown={e => { if (e.key === "Enter") addProcess(pillar.id); }} style={{ flex: 2, padding: "6px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 11, fontFamily: FONT_BODY, color: COLORS.text, outline: "none" }} autoFocus />
                          <input value={newProcessOwner} onChange={e => setNewProcessOwner(e.target.value)} placeholder="Owner" onKeyDown={e => { if (e.key === "Enter") addProcess(pillar.id); }} style={{ flex: 1, padding: "6px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 11, fontFamily: FONT_BODY, color: COLORS.text, outline: "none" }} />
                          <button onClick={() => addProcess(pillar.id)} disabled={!newProcessLabel.trim()} style={{ padding: "6px 12px", background: newProcessLabel.trim() ? pc : COLORS.border, color: newProcessLabel.trim() ? "#fff" : COLORS.textDim, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: newProcessLabel.trim() ? "pointer" : "default" }}>Add</button>
                          <button onClick={() => setShowAddProcess(null)} style={{ padding: "6px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textDim, fontSize: 10, cursor: "pointer" }}>×</button>
                        </div>
                      )}
                      {/* Horizontal Flow */}
                      <div style={{ padding: "16px 20px", display: "flex", alignItems: "stretch", gap: 0, overflow: "auto" }}>
                        {pillar.processes.map((proc, pi) => (
                          <React.Fragment key={proc.id}>
                            <div style={{ minWidth: 160, maxWidth: 185, padding: "14px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, borderTop: `3px solid ${TAG_CONFIG[proc.tag]?.color || COLORS.border}`, transition: "all 0.15s", display: "flex", flexDirection: "column", flexShrink: 0, position: "relative" }}>
                              {/* Controls: move arrows, edit, remove */}
                              <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 1, opacity: 0.35 }} className="proc-controls"
                                onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.35"}>
                                {pi > 0 && <button onClick={(e) => { e.stopPropagation(); moveProcess(pillar.id, pi, -1); }} style={{ width: 18, height: 18, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }} title="Move left">◀</button>}
                                {pi < pillar.processes.length - 1 && <button onClick={(e) => { e.stopPropagation(); moveProcess(pillar.id, pi, 1); }} style={{ width: 18, height: 18, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }} title="Move right">▶</button>}
                                <button onClick={(e) => { e.stopPropagation(); startEditProcess(pillar.id, proc); }} style={{ width: 18, height: 18, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }} title="Edit">✏️</button>
                                <button onClick={(e) => { e.stopPropagation(); removeProcess(pillar.id, proc.id); }} style={{ width: 18, height: 18, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                                  onMouseEnter={e => e.currentTarget.style.color = COLORS.danger} onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim} title="Remove">×</button>
                              </div>
                              <div onClick={() => { if (processFlows[proc.id]) { setSelectedProcess(proc); setMapView("process"); } }} style={{ cursor: processFlows[proc.id] ? "pointer" : "default", flex: 1, display: "flex", flexDirection: "column" }}
                                onMouseEnter={e => { if (processFlows[proc.id]) e.currentTarget.parentElement.style.borderColor = pc + "66"; }} onMouseLeave={e => { e.currentTarget.parentElement.style.borderColor = COLORS.border; }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, paddingRight: 20 }}>
                                  <span style={{ fontWeight: 700, fontSize: 12, color: COLORS.text, lineHeight: 1.3 }}>{proc.label}</span>
                                  <span style={{ fontSize: 12, flexShrink: 0, marginLeft: 4 }}>{TAG_CONFIG[proc.tag]?.icon}</span>
                                </div>
                                <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 2 }}>{proc.owner}</div>
                                <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 6 }}>{proc.time}</div>
                                <div style={{ fontSize: 9, color: COLORS.textDim, marginBottom: 4 }}>Tools: {proc.tools}</div>
                                {proc.aiRec && <div style={{ fontSize: 9, color: COLORS.accent, padding: "4px 8px", background: COLORS.accent + "08", borderRadius: 4, marginTop: "auto" }}>✨ {proc.aiRec}</div>}
                                {proc.savings && proc.savings !== "—" && <div style={{ fontSize: 9, color: COLORS.green, fontWeight: 600, marginTop: 4 }}>⏱ Saves {proc.savings}</div>}
                                {processFlows[proc.id] && <div style={{ fontSize: 8, color: pc, fontFamily: FONT, fontWeight: 600, marginTop: 6, letterSpacing: "0.04em" }}>CLICK TO EXPAND →</div>}
                              </div>
                            </div>
                            {pi < pillar.processes.length - 1 && (
                              <div style={{ display: "flex", alignItems: "center", flexShrink: 0, padding: "0 2px" }}>
                                <svg width="28" height="20" viewBox="0 0 28 20"><path d="M2 10 L20 10 M16 5 L22 10 L16 15" stroke={pc} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Summary Table */}
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>📊</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: COLORS.accent }}>AI OPPORTUNITY SUMMARY</span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: COLORS.bg }}>
                      {["Process", "Owner", "Current Time", "Status", "AI Recommendation", "Time Saved", "Source"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontFamily: FONT, fontSize: 9, color: COLORS.textDim, fontWeight: 600, letterSpacing: "0.04em", borderBottom: `1px solid ${COLORS.border}` }}>{h.toUpperCase()}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {pillars.flatMap(pil => pil.processes.map((proc, pi) => (
                        <tr key={proc.id} style={{ borderBottom: `1px solid ${COLORS.border}`, background: pi === 0 ? PILLAR_COLORS[pil.id] + "05" : "transparent" }}>
                          <td style={{ padding: "8px 12px" }}><div style={{ display: "flex", alignItems: "center", gap: 6 }}>{pi === 0 && <span style={{ fontSize: 10 }}>{pil.icon}</span>}<span style={{ fontSize: 11, fontWeight: pi === 0 ? 700 : 500 }}>{proc.label}</span></div></td>
                          <td style={{ padding: "8px 12px", fontSize: 10, color: COLORS.textDim }}>{proc.owner}</td>
                          <td style={{ padding: "8px 12px", fontSize: 10, fontFamily: FONT, fontWeight: 600 }}>{proc.time}</td>
                          <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 9, display: "flex", alignItems: "center", gap: 3 }}>{TAG_CONFIG[proc.tag]?.icon} <span style={{ color: TAG_CONFIG[proc.tag]?.color, fontWeight: 600, fontFamily: FONT }}>{TAG_CONFIG[proc.tag]?.label}</span></span></td>
                          <td style={{ padding: "8px 12px", fontSize: 10, color: COLORS.accent }}>{proc.aiRec || "—"}</td>
                          <td style={{ padding: "8px 12px", fontSize: 10, fontWeight: 600, color: proc.savings && proc.savings !== "—" ? COLORS.green : COLORS.textDim }}>{proc.savings || "—"}</td>
                          <td style={{ padding: "8px 12px", fontSize: 10, color: COLORS.textDim }}>{(proc.source_refs?.length && Array.isArray(proc.source_refs)) ? proc.source_refs.map(r => `${r.name || r.title || 'Unnamed'} (${(r.type || 'document').charAt(0).toUpperCase() + (r.type || 'document').slice(1)}`).join(', ') : "—"}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Process Flow — Vertical Top to Bottom */}
            {mapView === "process" && (
              <div>
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {(processFlows[selectedProcess?.id] || processFlows.a1).map((step, si, arr) => {
                    const pid = selectedProcess?.id || "a1";
                    return (
                    <React.Fragment key={si}>
                      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0 }}>
                          {si > 0 && <button onClick={() => moveStep(pid, si, -1)} style={{ width: 20, height: 14, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 8, cursor: "pointer", padding: 0, opacity: 0.4 }} onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"} title="Move up">▲</button>}
                          <div style={{ width: 34, height: 34, borderRadius: "50%", background: TAG_CONFIG[step.tag]?.color + "15", border: `2px solid ${TAG_CONFIG[step.tag]?.color || COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 12, fontWeight: 700, color: TAG_CONFIG[step.tag]?.color }}>{step.step}</div>
                          {si < arr.length - 1 && <button onClick={() => moveStep(pid, si, 1)} style={{ width: 20, height: 14, background: "transparent", border: "none", color: COLORS.textDim, fontSize: 8, cursor: "pointer", padding: 0, opacity: 0.4 }} onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"} title="Move down">▼</button>}
                        </div>
                        <div style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${step.aiOverlay ? COLORS.accent + "55" : COLORS.border}`, borderRadius: 10, borderLeft: step.aiOverlay ? `4px solid ${COLORS.accent}` : `4px solid ${TAG_CONFIG[step.tag]?.color || COLORS.border}`, position: "relative" }}>
                          <div style={{ position: "absolute", top: 6, right: 8, display: "flex", gap: 2, opacity: 0.35 }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.35"}>
                            <button onClick={() => startEditStep(pid, si)} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 10, cursor: "pointer", padding: "0 3px" }} title="Edit">✏️</button>
                            <button onClick={() => removeStep(pid, si)} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer", padding: "0 3px" }}
                              onMouseEnter={e => e.currentTarget.style.color = COLORS.danger} onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim} title="Remove">×</button>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingRight: 40 }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{step.label}</div>
                              <div style={{ fontSize: 10, color: COLORS.textDim }}>{step.owner} · {step.time}</div>
                            </div>
                            <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: TAG_CONFIG[step.tag]?.color + "12", color: TAG_CONFIG[step.tag]?.color }}>{TAG_CONFIG[step.tag]?.label}</span>
                          </div>
                          {step.aiOverlay && (
                            <div style={{ marginTop: 10, padding: "10px 14px", background: COLORS.accent + "08", border: `1px solid ${COLORS.accent}22`, borderRadius: 8 }}>
                              <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.04em", marginBottom: 3 }}>✨ AI REPLACEMENT</div>
                              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>{step.aiOverlay}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {si < arr.length - 1 && (
                        <div style={{ display: "flex", paddingLeft: 26 }}>
                          <svg width="2" height="20"><line x1="1" y1="0" x2="1" y2="20" stroke={COLORS.border} strokeWidth="2" /></svg>
                        </div>
                      )}
                    </React.Fragment>
                  );})}
                </div>

                {/* Add Step */}
                {!showAddStep ? (
                  <button onClick={() => setShowAddStep(true)} style={{ marginTop: 12, padding: "8px 16px", background: "transparent", border: `1px dashed ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", width: "100%", marginLeft: 48 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; }} onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}>+ Add Step</button>
                ) : (
                  <div style={{ marginTop: 12, marginLeft: 48, display: "flex", gap: 8 }}>
                    <input value={newStepLabel} onChange={e => setNewStepLabel(e.target.value)} placeholder="Step description" onKeyDown={e => { if (e.key === "Enter") addStep(selectedProcess?.id || "a1"); }} style={{ flex: 1, padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, fontFamily: FONT_BODY, color: COLORS.text, outline: "none" }} autoFocus />
                    <button onClick={() => addStep(selectedProcess?.id || "a1")} disabled={!newStepLabel.trim()} style={{ padding: "8px 14px", background: newStepLabel.trim() ? COLORS.accent : COLORS.border, color: newStepLabel.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: newStepLabel.trim() ? "pointer" : "default" }}>Add</button>
                    <button onClick={() => { setShowAddStep(false); setNewStepLabel(""); }} style={{ padding: "8px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textDim, fontSize: 10, cursor: "pointer" }}>×</button>
                  </div>
                )}
                <div style={{ marginTop: 20, padding: "16px 20px", background: COLORS.accent + "08", border: `1px solid ${COLORS.accent}22`, borderRadius: 10 }}>
                  <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>PROCESS SUMMARY — {(selectedProcess?.label || "Lead Capture").toUpperCase()}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                    {[
                      { label: "Current Time", value: selectedProcess?.time || "3 hrs/day", color: COLORS.text },
                      { label: "AI Automatable", value: selectedProcess?.id === "a3" ? "1.5 hrs (75%)" : "2.5 hrs (83%)", color: COLORS.accent },
                      { label: "Weekly Savings", value: selectedProcess?.id === "a3" ? "7.5 hrs/week" : "12.5 hrs/week", color: COLORS.green },
                      { label: "Cost Equivalent", value: selectedProcess?.id === "a3" ? "~£750/month" : "~£1,250/month", color: COLORS.green },
                    ].map((s, i) => (
                      <div key={i} style={{ padding: "10px 12px", background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.04em", marginBottom: 4 }}>{s.label.toUpperCase()}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: FONT, color: s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Process Modal */}
      {editingProcess && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setEditingProcess(null); setEditForm({}); }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 460, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "16px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Edit Process</div>
            </div>
            <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { key: "label", label: "PROCESS NAME", ph: "e.g. Lead Capture" },
                { key: "owner", label: "OWNER", ph: "e.g. SDR" },
                { key: "time", label: "TIME ESTIMATE", ph: "e.g. 3 hrs/day" },
                { key: "tools", label: "CURRENT TOOLS", ph: "e.g. LinkedIn, manual search" },
                { key: "aiRec", label: "AI RECOMMENDATION", ph: "e.g. Automate with AI Ark" },
                { key: "savings", label: "ESTIMATED SAVINGS", ph: "e.g. 2.5 hrs/day" },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 4 }}>{f.label}</div>
                  <input value={editForm[f.key] || ""} onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })} placeholder={f.ph} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, fontFamily: FONT_BODY, color: COLORS.text, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
              <div>
                <div style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>STATUS TAG</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {Object.entries(TAG_CONFIG).map(([k, v]) => (
                    <button key={k} onClick={() => setEditForm({ ...editForm, tag: k })} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${editForm.tag === k ? v.color + "55" : COLORS.border}`, background: editForm.tag === k ? v.color + "12" : "transparent", color: editForm.tag === k ? v.color : COLORS.textDim, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{v.icon} {v.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setEditingProcess(null); setEditForm({}); }} style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={saveEditProcess} style={{ padding: "8px 18px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Step Modal */}
      {editingStep && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setEditingStep(null); setEditForm({}); }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 460, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "16px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Edit Step</div>
            </div>
            <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { key: "label", label: "STEP DESCRIPTION", ph: "e.g. Upload to CRM" },
                { key: "owner", label: "OWNER", ph: "e.g. SDR" },
                { key: "time", label: "TIME ESTIMATE", ph: "e.g. 20 min" },
                { key: "aiOverlay", label: "AI REPLACEMENT (optional)", ph: "e.g. Pipeline auto-deduplication on import" },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 4 }}>{f.label}</div>
                  <input value={editForm[f.key] || ""} onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })} placeholder={f.ph} style={{ width: "100%", padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, fontFamily: FONT_BODY, color: COLORS.text, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
              <div>
                <div style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>STATUS TAG</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {Object.entries(TAG_CONFIG).map(([k, v]) => (
                    <button key={k} onClick={() => setEditForm({ ...editForm, tag: k })} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${editForm.tag === k ? v.color + "55" : COLORS.border}`, background: editForm.tag === k ? v.color + "12" : "transparent", color: editForm.tag === k ? v.color : COLORS.textDim, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{v.icon} {v.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setEditingStep(null); setEditForm({}); }} style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={saveEditStep} style={{ padding: "8px 18px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PrototypeLabTab({ project }) {
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [generated, setGenerated] = useState({});
  const [expandedOutput, setExpandedOutput] = useState(null);
  const [copied, setCopied] = useState(null);
  const [lovableSending, setLovableSending] = useState(null);
  const [lovableSent, setLovableSent] = useState({});
  const [boltSending, setBoltSending] = useState(null);
  const [boltSent, setBoltSent] = useState({});
  const [opportunities, setOpportunities] = useState([]);
  const [loadingOpps, setLoadingOpps] = useState(true);
  const outputRef = useRef(null);

  const FALLBACK_OPPORTUNITIES = [
    {
      id: "opp1", title: "Automated Lease Tracking System", category: "Quick Win", phase: "Phase 1",
      impact: "$100K+ revenue protection", complexity: "Medium",
      valueSummary: "Replace manual spreadsheet tracking across 150 properties with an automated system that tracks renewal dates, sends alerts, and prevents missed renewals that currently cause $100K+ annual revenue leakage.",
      painPoints: ["Estate manager maintains all 150 properties in spreadsheets — described as 'getting unmanageable'", "Missed renewal windows multiple times, directly impacting revenue", "No backup system — single point of failure if estate manager is unavailable", "Current process breaks down beyond ~180 properties, blocking growth to 300"],
      users: ["Estate Managers", "Property Directors", "Finance Team"],
      keyFeatures: ["Dashboard showing all lease statuses with renewal countdown timers", "Automated email/SMS alerts 90/60/30 days before renewal deadlines", "Bulk import from existing spreadsheets with data validation", "Audit trail for all lease modifications and status changes", "Revenue-at-risk calculator showing exposure from upcoming renewals", "Integration with accounting system for financial reconciliation"],
      sourceData: "CFO Interview (Sarah Mitchell), Estate Manager Interview (Mike Thompson), AI Readiness Survey"
    },
    {
      id: "opp2", title: "Executive Director Dashboard", category: "Quick Win", phase: "Phase 2",
      impact: "20hr/month time saved", complexity: "Medium",
      valueSummary: "Consolidate data from 6+ disconnected systems into a single real-time dashboard, eliminating the 3-day monthly board report assembly process and giving directors instant portfolio visibility.",
      painPoints: ["CFO spends 3 full days per month assembling board reports from 6+ systems", "Data silos prevent real-time decision making across the portfolio", "No single source of truth — executives get conflicting numbers from different departments", "75% of staff dissatisfied with current technology stack"],
      users: ["CEO / Board", "CFO", "Department Heads"],
      keyFeatures: ["Real-time portfolio overview with KPIs: occupancy, revenue, compliance status", "Automated board report generation with one-click export to PDF/PPTX", "Department-level drill-down views (Finance, Operations, Legal)", "Anomaly detection — auto-flag metrics that deviate from expected ranges", "Comparative period analysis (MoM, QoQ, YoY) with trend visualization", "Role-based access control with different views per stakeholder level"],
      sourceData: "CEO Interview (James Richardson), CFO Interview (Sarah Mitchell), Technology Stack Survey"
    },
    {
      id: "opp3", title: "Knowledge Management System", category: "Big Swing", phase: "Phase 2",
      impact: "De-risk key-person dependency", complexity: "High",
      valueSummary: "Capture and systematize institutional knowledge currently locked in individual employees' heads, particularly the estate manager who is a single point of failure for 150-property portfolio operations.",
      painPoints: ["Estate manager is single point of failure — no documentation or knowledge transfer system", "Critical operational knowledge locked in individuals' heads", "No onboarding system for new staff — takes months to become effective", "Growth to 300 properties impossible without systematized knowledge"],
      users: ["All Staff", "New Hires", "Estate Managers", "Operations Team"],
      keyFeatures: ["Searchable knowledge base with property-specific procedures and contacts", "AI-powered document ingestion — upload existing docs, auto-categorize and index", "Decision trees for common operational scenarios (maintenance, complaints, renewals)", "Guided onboarding workflows for new team members by role", "Version-controlled SOPs with change tracking and approval flows", "Smart search with natural language queries across all documentation"],
      sourceData: "Estate Manager Interview (Mike Thompson), CEO Interview (James Richardson)"
    },
    {
      id: "opp4", title: "AI Contract Analysis Engine", category: "Big Swing", phase: "Phase 3",
      impact: "Unlock operational efficiency", complexity: "High",
      valueSummary: "Deploy AI to automatically analyze lease contracts, extract key terms, flag risks, and identify optimization opportunities across the entire 150-property portfolio — scaling to 300 without additional headcount.",
      painPoints: ["Manual contract review is time-intensive and error-prone", "Inconsistent lease terms across portfolio create hidden financial exposure", "No systematic way to identify favorable renegotiation opportunities", "Legal team overwhelmed with routine contract queries"],
      users: ["Legal Team", "Estate Managers", "CFO", "Property Directors"],
      keyFeatures: ["Drag-and-drop contract upload with AI extraction of key terms and dates", "Risk scoring for each contract based on unfavorable clauses and exposure", "Side-by-side contract comparison tool for benchmarking terms", "Auto-generated summary sheets for each property with key obligations", "Renegotiation opportunity alerts based on market data and lease terms", "Natural language query interface — 'Show me all leases expiring in Q3 with break clauses'"],
      sourceData: "All Interviews, AI Readiness Survey, Technology Stack Survey"
    },
  ];

  const OPPORTUNITIES = opportunities;

  useEffect(() => {
    let cancelled = false;
    async function loadOpportunities() {
      if (!project?.id) {
        setLoadingOpps(false);
        return;
      }
      try {
        const rows = await api.audit.analyses.list({ project_id: project.id });
        const completed = (rows || []).find(r => r.status === "completed" && r.analysis?.opportunities?.length > 0);
        const raw = completed?.analysis?.opportunities || [];
        const mapped = raw.map((o, i) => ({
          id: `opp_${i}`,
          title: o.title || o.name || `Opportunity ${i + 1}`,
          category: (o.category === "quick_win" || o.category === "Quick Win") ? "Quick Win" : (o.category === "big_swing" || o.category === "Big Swing") ? "Big Swing" : o.category ? String(o.category).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Opportunity",
          phase: o.phase || (i < 2 ? "Phase 1" : i < 4 ? "Phase 2" : "Phase 3"),
          impact: o.estimated_value || (typeof o.impact === "string" ? o.impact : "") || "High impact",
          complexity: o.complexity ? String(o.complexity).charAt(0).toUpperCase() + String(o.complexity).slice(1) : "Medium",
          valueSummary: o.description || o.valueSummary || "",
          painPoints: o.painPoints || o.pain_points || [],
          users: o.users || [],
          keyFeatures: o.keyFeatures || o.key_features || [],
          sourceData: o.sourceData || o.source_data || "Audit Analysis",
        })).filter(o => o.title && o.valueSummary);
        if (!cancelled) setOpportunities(mapped);
      } catch (err) {
        console.error("Failed to load Prototype Lab opportunities:", err);
        if (!cancelled) setOpportunities([]);
      } finally {
        if (!cancelled) setLoadingOpps(false);
      }
    }
    loadOpportunities();
    return () => { cancelled = true; };
  }, [project?.id]);

  const [generateError, setGenerateError] = useState(null);

  const generateOutput = async (oppId, outputType) => {
    setGenerating(outputType);
    setExpandedOutput(outputType);
    setGenerateError(null);
    const opp = OPPORTUNITIES.find(o => o.id === oppId);
    try {
      const result = await api.prototypes.generate({
        project_id: project?.id,
        opportunity_id: oppId,
        output_type: outputType,
        opportunity: {
          title: opp.title,
          category: opp.category,
          valueSummary: opp.valueSummary,
          painPoints: opp.painPoints,
          users: opp.users,
          keyFeatures: opp.keyFeatures,
          sourceData: opp.sourceData,
        },
      });
      const content = result.content || '';
      setGenerated(prev => ({ ...prev, [oppId]: { ...(prev[oppId] || {}), [outputType]: content } }));
    } catch (err) {
      setGenerateError(err.message || 'Generation failed');
    }
    setGenerating(null);
  };

  const handleCopy = (text, key) => {
    navigator.clipboard?.writeText?.(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const sendToLovable = (oppId) => {
    const promptText = generated[oppId]?.lovable;
    if (!promptText?.trim()) return;
    setLovableSending(oppId);
    const encodedPrompt = encodeURIComponent(promptText);
    const lovableUrl = `https://lovable.dev/?autosubmit=true#prompt=${encodedPrompt}`;
    window.open(lovableUrl, '_blank');
    setLovableSent(prev => ({ ...prev, [oppId]: true }));
    setLovableSending(null);
  };

  const sendToBolt = (oppId) => {
    const promptText = generated[oppId]?.bolt;
    if (!promptText?.trim()) return;
    setBoltSending(oppId);
    const encodedPrompt = encodeURIComponent(promptText);
    const boltUrl = `https://bolt.new/?prompt=${encodedPrompt}`;
    window.open(boltUrl, '_blank');
    setBoltSent(prev => ({ ...prev, [oppId]: true }));
    setBoltSending(null);
  };

  const opp = selectedOpp ? OPPORTUNITIES.find(o => o.id === selectedOpp) : null;

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
      {!selectedOpp ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, display: "flex", alignItems: "center", gap: 10 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>science</span> Prototype Lab
              </div>
              <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 4, maxWidth: 500, lineHeight: 1.5 }}>
                Turn audit findings into working prototypes. Generate PRDs and builder-ready prompts for Lovable or v0 — go from insight to clickable demo in minutes.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ padding: "6px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 10, fontFamily: FONT, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: loadingOpps ? COLORS.textDim : COLORS.accent }} />
                {loadingOpps ? "Loading..." : `${OPPORTUNITIES.length} opportunities from audit`}
              </div>
            </div>
          </div>
          <div style={{ padding: "14px 18px", marginBottom: 20, borderRadius: 10, background: `linear-gradient(135deg, ${COLORS.accentBg}, rgba(77, 158, 240, 0.06))`, border: `1px solid ${COLORS.accent}22`, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 22 }}>💡</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>How it works</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
                Each opportunity below was identified during the audit analysis. Select one to generate a full PRD or a builder-optimized prompt you can paste directly into Lovable or v0 to scaffold a working prototype.
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {OPPORTUNITIES.map(o => (
              <div key={o.id} onClick={() => setSelectedOpp(o.id)} style={{ padding: "20px 22px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, cursor: "pointer", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent + "55"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: o.category === "Quick Win" ? "rgba(34, 197, 94, 0.1)" : COLORS.blueBg, color: o.category === "Quick Win" ? "#22c55e" : COLORS.blue, border: `1px solid ${o.category === "Quick Win" ? "rgba(34, 197, 94, 0.2)" : "rgba(77, 158, 240, 0.2)"}` }}>{o.category}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 500, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textDim }}>{o.phase}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 500, background: o.complexity === "High" ? COLORS.warnBg : COLORS.bg, border: `1px solid ${o.complexity === "High" ? "rgba(240, 168, 77, 0.2)" : COLORS.border}`, color: o.complexity === "High" ? COLORS.warn : COLORS.textDim }}>{o.complexity} Complexity</span>
                </div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: COLORS.text }}>{o.title}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5, marginBottom: 14, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{o.valueSummary}</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6, background: COLORS.accentBg, border: `1px solid ${COLORS.accent}22` }}>
                  <span style={{ fontSize: 11 }}>⚡</span>
                  <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.accent }}>{o.impact}</span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  {["Lovable"].map(t => (
                    <span key={t} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 8, fontFamily: FONT, background: generated[o.id]?.[t.toLowerCase()] ? COLORS.accentBg : COLORS.bg, color: generated[o.id]?.[t.toLowerCase()] ? COLORS.accent : COLORS.textDim, border: `1px solid ${generated[o.id]?.[t.toLowerCase()] ? COLORS.accent + "33" : COLORS.border}` }}>
                      {generated[o.id]?.[t.toLowerCase()] ? "✓ " : ""}{t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <button onClick={() => { setSelectedOpp(null); setExpandedOutput(null); }} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>← Back</button>
            <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600 }}>{opp.title}</div>
            <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 600, background: opp.category === "Quick Win" ? "rgba(34, 197, 94, 0.1)" : COLORS.blueBg, color: opp.category === "Quick Win" ? "#22c55e" : COLORS.blue, border: `1px solid ${opp.category === "Quick Win" ? "rgba(34, 197, 94, 0.2)" : "rgba(77, 158, 240, 0.2)"}` }}>{opp.category}</span>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            <div style={{ width: 340, flexShrink: 0 }}>
              <div style={{ padding: "16px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>OPPORTUNITY SUMMARY</div>
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6 }}>{opp.valueSummary}</div>
              </div>
              <div style={{ padding: "16px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>VALIDATED PAIN POINTS</div>
                {opp.painPoints.map((p, i) => (<div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}><span style={{ color: COLORS.danger, fontSize: 10, marginTop: 2, flexShrink: 0 }}>●</span><div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>{p}</div></div>))}
              </div>
              <div style={{ padding: "16px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>KEY FEATURES</div>
                {opp.keyFeatures.map((f, i) => (<div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><span style={{ color: COLORS.accent, fontSize: 10, marginTop: 2, flexShrink: 0 }}>→</span><div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>{f}</div></div>))}
              </div>
              <div style={{ padding: "12px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}><span style={{ fontWeight: 600 }}>SOURCE:</span> {opp.sourceData}</div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                {[
                  { key: "lovable", label: "💜 Lovable Prompt", desc: "Optimized for Lovable.dev", color: "#a855f7" },
                ].map(btn => {
                  const isGenerated = !!generated[opp.id]?.[btn.key];
                  const isGenerating = generating === btn.key;
                  return (
                    <button key={btn.key} onClick={() => { if (isGenerated) { setExpandedOutput(expandedOutput === btn.key ? null : btn.key); } else { generateOutput(opp.id, btn.key); } }} disabled={isGenerating}
                      style={{ flex: 1, padding: "14px 16px", background: isGenerating ? btn.color + "15" : isGenerated ? btn.color + "12" : COLORS.surface, border: `1px solid ${isGenerated || isGenerating ? btn.color + "44" : COLORS.border}`, borderRadius: 10, cursor: isGenerating ? "wait" : "pointer", textAlign: "left", transition: "all 0.2s" }}
                      onMouseEnter={e => { if (!isGenerating) e.currentTarget.style.borderColor = btn.color + "66"; }}
                      onMouseLeave={e => { if (!isGenerating) e.currentTarget.style.borderColor = isGenerated ? btn.color + "44" : COLORS.border; }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: isGenerated ? btn.color : COLORS.text }}>{btn.label}</span>
                        {isGenerated && <span style={{ fontSize: 10, color: btn.color, fontFamily: FONT }}>✓</span>}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>{isGenerating ? "Generating..." : isGenerated ? (expandedOutput === btn.key ? "Click to collapse" : "Click to view") : btn.desc}</div>
                      {isGenerating && (<div style={{ marginTop: 8, height: 3, borderRadius: 2, background: COLORS.border, overflow: "hidden" }}><div style={{ height: "100%", background: btn.color, borderRadius: 2, animation: "prototype-progress 2s ease-in-out infinite", width: "60%" }} /></div>)}
                    </button>
                  );
                })}
              </div>
              {generateError && !generating && (
                <div style={{ padding: "12px 16px", marginBottom: 12, background: COLORS.danger + "12", border: `1px solid ${COLORS.danger}33`, borderRadius: 8, fontSize: 12, color: COLORS.danger }}>{generateError}</div>
              )}
              {generated[opp.id]?.lovable && (
                <div style={{ padding: "12px 16px", marginBottom: 16, background: "linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(168, 85, 247, 0.04))", border: "1px solid rgba(168, 85, 247, 0.2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 18 }}>🚀</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Build in Lovable</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>{lovableSent[opp.id] ? "Opened in new tab — building your prototype!" : "Open Lovable.dev with this prompt pre-loaded and auto-submitted"}</div>
                    </div>
                  </div>
                  <button onClick={() => sendToLovable(opp.id)} disabled={lovableSending === opp.id || lovableSent[opp.id]}
                    style={{ padding: "8px 18px", background: lovableSent[opp.id] ? "#22c55e" : lovableSending === opp.id ? "#a855f755" : "#a855f7", color: "#fff", border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: lovableSending === opp.id || lovableSent[opp.id] ? "default" : "pointer" }}>
                    {lovableSent[opp.id] ? "✓ Opened" : lovableSending === opp.id ? "Opening..." : "Build in Lovable →"}
                  </button>
                </div>
              )}
              {expandedOutput && generated[opp.id]?.[expandedOutput] && (
                <div style={{ flex: 1, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.text }}>
                        {expandedOutput === "prd" ? "📄 Product Requirements Document" : "💜 Lovable Prompt"}
                      </span>
                      <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT }}>{generated[opp.id][expandedOutput].split("\n").length} lines</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => handleCopy(generated[opp.id][expandedOutput], `${opp.id}-${expandedOutput}`)}
                        style={{ padding: "5px 12px", background: copied === `${opp.id}-${expandedOutput}` ? COLORS.accentBg : "transparent", border: `1px solid ${copied === `${opp.id}-${expandedOutput}` ? COLORS.accent + "44" : COLORS.border}`, borderRadius: 5, color: copied === `${opp.id}-${expandedOutput}` ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
                        {copied === `${opp.id}-${expandedOutput}` ? "✓ Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                  <div ref={outputRef} style={{ flex: 1, overflow: "auto", padding: "16px 20px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, lineHeight: 1.7, color: COLORS.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {generated[opp.id][expandedOutput].split("\n").map((line, i) => {
                      if (line.startsWith("# ")) return <div key={i} style={{ fontSize: 18, fontWeight: 700, color: COLORS.accent, marginTop: 16, marginBottom: 8, fontFamily: FONT_BODY }}>{line.replace("# ", "")}</div>;
                      if (line.startsWith("## ")) return <div key={i} style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginTop: 14, marginBottom: 6, fontFamily: FONT_BODY }}>{line.replace("## ", "")}</div>;
                      if (line.startsWith("### ")) return <div key={i} style={{ fontSize: 12, fontWeight: 600, color: COLORS.blue, marginTop: 10, marginBottom: 4, fontFamily: FONT_BODY }}>{line.replace("### ", "")}</div>;
                      if (line.startsWith("---")) return <div key={i} style={{ borderBottom: `1px solid ${COLORS.border}`, margin: "12px 0" }} />;
                      if (line.startsWith("- ") || line.startsWith("* ")) return <div key={i} style={{ paddingLeft: 12, display: "flex", gap: 6 }}><span style={{ color: COLORS.accent, flexShrink: 0 }}>→</span><span>{line.replace(/^[-*] /, "")}</span></div>;
                      if (line.startsWith("|")) return <div key={i} style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>{line}</div>;
                      if (line.startsWith("**") && line.endsWith("**")) return <div key={i} style={{ fontWeight: 600, color: COLORS.text }}>{line.replace(/\*\*/g, "")}</div>;
                      if (line.trim() === "") return <div key={i} style={{ height: 8 }} />;
                      return <div key={i}>{line}</div>;
                    })}
                  </div>
                </div>
              )}
              {!expandedOutput && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, minHeight: 300 }}>
                  <div style={{ textAlign: "center", maxWidth: 320 }}>
                    <div style={{ marginBottom: 12, opacity: 0.15 }}><span className="material-symbols-outlined" style={{ fontSize: 42 }}>science</span></div>
                    <div style={{ fontFamily: FONT, fontSize: 14, color: COLORS.textDim, marginBottom: 8 }}>Generate a deliverable</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>Choose a PRD for full product documentation, or a builder prompt optimized for Lovable to scaffold a working prototype instantly.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <style>{`@keyframes prototype-progress { 0% { transform: translateX(-100%); } 50% { transform: translateX(60%); } 100% { transform: translateX(-100%); } }`}</style>
        </>
      )}
    </div>
  );
}

const DECK_JOB_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

function getStoredDeckJob(projectId) {
  if (!projectId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`audit-deck-job:${projectId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.startedAt && Date.now() - new Date(parsed.startedAt).getTime() > DECK_JOB_TTL_MS) {
      window.localStorage.removeItem(`audit-deck-job:${projectId}`);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setStoredDeckJob(projectId, payload) {
  if (!projectId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`audit-deck-job:${projectId}`, JSON.stringify(payload));
  } catch {}
}

function clearStoredDeckJob(projectId) {
  if (!projectId || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(`audit-deck-job:${projectId}`);
  } catch {}
}

// Cross-tab coordination for deck generation status
const deckBroadcast = (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined")
  ? new BroadcastChannel("deck-generation")
  : null;

const DECK_WORKFLOW_STAGES = [
  { id: "collecting-data", label: "Collecting data" },
  { id: "calling-claude", label: "specs_agent" },
  { id: "specs-ready", label: "Specs ready" },
  { id: "submitting-manus", label: "builder_agent" },
  { id: "manus-running", label: "Building" },
  { id: "downloading", label: "Downloading deck" },
  { id: "complete", label: "Deck ready" },
];

function formatDeckGeneratedAt(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const PREVIEW_BASE_WIDTH = 1280;
const PREVIEW_BASE_HEIGHT = 720;
const PREVIEW_IFRAME_SANDBOX = "allow-scripts";

function fingerprintPreviewContent(value) {
  const input = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `fp:${(hash >>> 0).toString(16)}`;
}

function extractSlideSlotNumber(...values) {
  const patterns = [
    /(?:^|[\s_-])slide[\s_-]*(\d+)(?:\b|[_-])/i,
    /^(\d+)[\s._-]/i,
  ];
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (!match) continue;
      const slot = Number(match[1]);
      if (Number.isFinite(slot) && slot > 0) return slot - 1;
    }
  }
  return null;
}

function normalizePreviewSlideEntry(slide, index) {
  const slotNumberRaw = Number(slide?.slot ?? slide?.slideSlot ?? slide?.order_index ?? slide?.itemIndex);
  const slotNumber = Number.isFinite(slotNumberRaw) && slotNumberRaw >= 0
    ? slotNumberRaw
    : extractSlideSlotNumber(slide?.name, slide?.title, slide?.url);
  const fallbackIdentity = String(slide?.slideId || slide?.id || slide?.name || slide?.title || "").trim() || null;
  const slotKey = String(
    slide?.slideKey
    || slide?.slotKey
    || slide?.identityKey
    || (slotNumber != null ? `slot:${slotNumber}` : "")
    || (fallbackIdentity ? `id:${fallbackIdentity}` : `unkeyed:${index}`)
  );
  const versionKey = String(
    slide?.versionKey
    || slide?.artifactVersionKey
    || slide?.contentFingerprint
    || fingerprintPreviewContent(slide?.html || slide?.url || `${slotKey}:${index}`)
  );
  const sortOrderRaw = Number(slide?.order);
  const sortOrder = slotNumber != null
    ? slotNumber
    : (Number.isFinite(sortOrderRaw) ? sortOrderRaw : index);

  return {
    ...slide,
    slot: slotNumber,
    slotKey,
    versionKey,
    sortOrder,
    firstSeenIndex: index,
  };
}

function normalizePreviewSlides(slides) {
  const slots = new Map();

  (Array.isArray(slides) ? slides : []).forEach((slide, index) => {
    const normalizedSlide = normalizePreviewSlideEntry(slide, index);
    if (!normalizedSlide.html && !normalizedSlide.url) return;
    const previous = slots.get(normalizedSlide.slotKey);
    if (!previous || normalizedSlide.sortOrder >= previous.sortOrder) {
      slots.set(normalizedSlide.slotKey, previous ? { ...normalizedSlide, firstSeenIndex: previous.firstSeenIndex } : normalizedSlide);
    }
  });

  const merged = [...slots.values()];
  merged.sort((a, b) => {
    const aHasSlot = a.slot != null;
    const bHasSlot = b.slot != null;
    if (aHasSlot && bHasSlot && a.slot !== b.slot) return a.slot - b.slot;
    if (aHasSlot !== bHasSlot) return aHasSlot ? -1 : 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.firstSeenIndex - b.firstSeenIndex;
  });

  return merged.map((slide, index) => ({
    ...slide,
    displayIndex: index + 1,
  }));
}

function getPreviewScale(width, height) {
  if (!width || !height) return null;
  return Math.min(width / PREVIEW_BASE_WIDTH, height / PREVIEW_BASE_HEIGHT, 1);
}

// White-label backend deck errors shown to customers: the presentation engine
// (Manus) must never be named in the client UI.
function whiteLabelDeckError(msg) {
  return String(msg || '').replace(/manus/gi, 'the presentation service');
}

function AuditAnalysisTab({ project, isActive, onDeckGenerating, onDeckJobEvent }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [analysisRun, setAnalysisRun] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [reasoningLogs, setReasoningLogs] = useState([]);
  const [chatReasoningLogs, setChatReasoningLogs] = useState([]);
  const [isReasoning, setIsReasoning] = useState(false);
  const [deckGenerated, setDeckGenerated] = useState(false);
  const [showGenerateBtn, setShowGenerateBtn] = useState(false);
  const [showFullDeck, setShowFullDeck] = useState(false);
  const [sourceSummary, setSourceSummary] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [visibleReasoningCount, setVisibleReasoningCount] = useState(0);
  const [reasoningCollapsed, setReasoningCollapsed] = useState(false);
  const [deckData, setDeckData] = useState(null);
  const [deckError, setDeckError] = useState(null);
  const [deckJobId, setDeckJobId] = useState(null);
  const [deckJobStatus, setDeckJobStatus] = useState(null);
  const [activeManusTaskUrl, setActiveManusTaskUrl] = useState(null);
  const [isStoppingDeck, setIsStoppingDeck] = useState(false);
  const [deckReasoningLogs, setDeckReasoningLogs] = useState([]);
  const [currentArtifactSlideIdx, setCurrentArtifactSlideIdx] = useState(0);
  const [selectedPreviewSlotKey, setSelectedPreviewSlotKey] = useState(null);
  const [isPreviewFollowingLatest, setIsPreviewFollowingLatest] = useState(true);
  const [styleProfiles, setStyleProfiles] = useState([]);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [showStylePicker, setShowStylePicker] = useState(false);
  const [stylePickerRect, setStylePickerRect] = useState(null);
  const [showDeckGenerateConfirm, setShowDeckGenerateConfirm] = useState(false);
  const chatEndRef = React.useRef(null);
  const reasoningContentRef = React.useRef(null);
  const stylePickerTriggerRef = React.useRef(null);
  const previewViewportRef = React.useRef(null);
  const previewThumbnailRailRef = React.useRef(null);
  const activeDeckPollRef = React.useRef(0);
  const deckJobIdRef = React.useRef(null);
  const deckJobStatusRef = React.useRef(null);
  const deckGeneratedRef = React.useRef(false);
  const deckStatusRefreshRef = React.useRef({ inFlight: null, lastStartedAt: 0 });
  const reasoningSequenceRef = React.useRef(0);
  const [mainPreviewScale, setMainPreviewScale] = useState(1);
  const [thumbnailPreviewScale, setThumbnailPreviewScale] = useState(0.09);

  const getReasoningSourceLabel = (source) => {
    switch (source) {
      case "analysis": return "ANALYSIS";
      case "deck": return "DECK";
      case "chat": return "CHAT";
      default: return "SYSTEM";
    }
  };

  const getReasoningSourceColor = (source) => {
    switch (source) {
      case "analysis": return COLORS.blue;
      case "deck": return COLORS.accent;
      case "chat": return "#7B61FF";
      default: return COLORS.textDim;
    }
  };

  const nextReasoningSequence = () => {
    reasoningSequenceRef.current += 1;
    return reasoningSequenceRef.current;
  };

  const stampReasoningLog = (source, log, index = 0, options = {}) => {
    const safeLog = log && typeof log === "object" ? log : { text: String(log || "") };
    const rawTs = Number(safeLog.ts);
    const loggedAt = Number.isFinite(rawTs)
      ? rawTs
      : (Number.isFinite(options.baseTs) ? options.baseTs + index : Date.now());
    const sourceKey = safeLog.source || source || "system";
    const eventKey = safeLog.eventKey || [
      sourceKey,
      safeLog.stage || safeLog.type || "log",
      safeLog.meta?.artifact_version_key || safeLog.meta?.manus_output_index || safeLog.meta?.artifact_url || safeLog.text || index,
      loggedAt,
    ].join(":");
    return {
      ...safeLog,
      source: sourceKey,
      sourceLabel: safeLog.sourceLabel || getReasoningSourceLabel(sourceKey),
      loggedAt,
      order: Number.isFinite(safeLog.order) ? safeLog.order : nextReasoningSequence(),
      eventKey,
    };
  };

  const stampReasoningLogs = (source, logs, options = {}) => (
    Array.isArray(logs) ? logs.map((log, index) => stampReasoningLog(source, log, index, options)) : []
  );

  const appendChatReasoningLog = (log) => {
    setReasoningCollapsed(false);
    setChatReasoningLogs(prev => [...prev, stampReasoningLog("chat", log)]);
  };

  const notifyDeckJob = (type, message) => {
    onDeckJobEvent?.({ type, message });
    if (typeof document === "undefined" || document.visibilityState !== "hidden") return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try {
      new Notification(type === "error" ? "Deck generation failed" : "Deck ready", { body: message });
    } catch {}
  };

  const clearDeckJobState = ({ clearLogs = true } = {}) => {
    activeDeckPollRef.current += 1;
    clearStoredDeckJob(project?.id);
    setDeckJobId(null);
    setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
    if (clearLogs) setDeckReasoningLogs([]);
    setIsStoppingDeck(false);
    onDeckGenerating?.(false);
  };

  useEffect(() => {
    deckJobIdRef.current = deckJobId;
    deckJobStatusRef.current = deckJobStatus;
    deckGeneratedRef.current = deckGenerated;
  }, [deckGenerated, deckJobId, deckJobStatus]);

  const resetPreviewSelectionState = React.useCallback(({ clearPreviewLogs = false } = {}) => {
    setCurrentArtifactSlideIdx(0);
    setSelectedPreviewSlotKey(null);
    setIsPreviewFollowingLatest(true);
    setMainPreviewScale(1);
    setThumbnailPreviewScale(0.09);
    if (clearPreviewLogs) setDeckReasoningLogs([]);
  }, []);

  const selectPreviewSlide = React.useCallback((slide, index, { followLatest = false } = {}) => {
    if (!slide) return;
    setCurrentArtifactSlideIdx(index);
    setSelectedPreviewSlotKey(slide.slotKey || null);
    setIsPreviewFollowingLatest(followLatest);
  }, []);

  useEffect(() => {
    const target = reasoningLogs.length;
    if (target === 0) {
      setVisibleReasoningCount(0);
      return;
    }
    setVisibleReasoningCount(prev => (prev > target ? target : prev));
    let timer;
    timer = setInterval(() => {
      setVisibleReasoningCount(prev => {
        if (prev >= target) {
          if (timer) clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 120);
    return () => { if (timer) clearInterval(timer); };
  }, [reasoningLogs]);

  React.useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, reasoningLogs, isTyping]);

  useEffect(() => {
    if (visibleReasoningCount > 0 && reasoningContentRef.current) {
      reasoningContentRef.current.scrollTop = reasoningContentRef.current.scrollHeight;
    }
  }, [visibleReasoningCount]);

  useEffect(() => {
    if (chatReasoningLogs.length > 0 && reasoningContentRef.current) {
      reasoningContentRef.current.scrollTop = reasoningContentRef.current.scrollHeight;
    }
  }, [chatReasoningLogs.length]);

  useEffect(() => {
    if (deckJobId && deckReasoningLogs.length > 0 && reasoningContentRef.current) {
      reasoningContentRef.current.scrollTop = reasoningContentRef.current.scrollHeight;
    }
  }, [deckJobId, deckReasoningLogs.length]);

  useEffect(() => {
    api.audit.deckStyles?.list?.().then(profiles => {
      if (Array.isArray(profiles) && profiles.length) {
        setStyleProfiles(profiles);
        const def = profiles.find(p => p.is_default);
        if (def) setSelectedStyleId(def.id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (showStylePicker && stylePickerTriggerRef.current) {
      const rect = stylePickerTriggerRef.current.getBoundingClientRect();
      setStylePickerRect(rect);
    } else {
      setStylePickerRect(null);
    }
  }, [showStylePicker]);

  useEffect(() => {
    // Reset all analysis state when project changes — prevents stale data from previous project
    setChatMessages([]);
    setAnalysisRun(false);
    setAnalysisData(null);
    setReasoningLogs([]);
    setChatReasoningLogs([]);
    setShowGenerateBtn(false);
    setIsReasoning(false);
    setAnalysisError(null);
    setVisibleReasoningCount(0);
    setReasoningCollapsed(false);
    setSourceSummary(null);
    reasoningSequenceRef.current = 0;

    let cancelled = false;
    const listParams = project?.id ? { project_id: project.id } : {};
    Promise.all([
      api.audit.transcripts.list(listParams).catch(() => []),
      api.audit.surveys.list(listParams).catch(() => []),
      api.audit.interviews.list(listParams).catch(() => []),
    ]).then(([transcripts, surveys, interviews]) => {
      if (cancelled) return;
      const tCount = Array.isArray(transcripts) ? transcripts.length : 0;
      const sCount = Array.isArray(surveys) ? surveys.length : 0;
      const iCount = Array.isArray(interviews) ? interviews.length : 0;
      setSourceSummary({ transcripts: tCount, surveys: sCount, interviews: iCount });
      const parts = [];
      if (tCount > 0) parts.push(`${tCount} transcript${tCount > 1 ? "s" : ""}`);
      if (sCount > 0) parts.push(`${sCount} survey${sCount > 1 ? "s" : ""}`);
      if (iCount > 0) parts.push(`${iCount} interview set${iCount > 1 ? "s" : ""}`);
      const dataDesc = parts.length > 0 ? parts.join(", ") : "no data sources";
      setChatMessages([{
        role: "agent",
        text: `Welcome to the Analysis workspace. I've found ${dataDesc} for this project.\n\n${parts.length > 0 ? "You can ask me questions about the data, or when you're ready, tell me to run the full analysis and I'll work through everything — extracting themes, mapping opportunities, and building a strategic roadmap." : "Add some transcripts, surveys, or interviews first, then come back to run the analysis."}\n\nWhat would you like to do?`,
      }]);
    });
    api.audit.analyses.list(listParams).then(data => {
      if (cancelled) return;
      const analyses = Array.isArray(data) ? data : [];
      if (analyses.length > 0 && analyses[0].analysis_json) {
        const latest = analyses[0];
        const analysis = latest.analysis_json || latest.analysis || {};
        setAnalysisData(analysis);
        setAnalysisRun(true);
        setShowGenerateBtn(true);
        if (Array.isArray(analysis.reasoning_steps)) {
          setReasoningLogs(
            stampReasoningLogs("analysis", analysis.reasoning_steps, {
              baseTs: new Date(latest.created_at || Date.now()).getTime(),
            }),
          );
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [project?.id]);

  useEffect(() => {
    return () => {
      activeDeckPollRef.current += 1;
    };
  }, [project?.id]);

  useEffect(() => {
    setDeckGenerated(false);
    setDeckData(null);
    setDeckError(null);
    setDeckJobId(null);
    setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
    setDeckReasoningLogs([]);
    setShowFullDeck(false);
  }, [project?.id]);

  const getTagColor = (tag) => {
    if (!tag) return COLORS.accent;
    const t = tag.toUpperCase();
    if (t === "CRITICAL RISK") return COLORS.danger;
    if (t === "BUDGET" || t === "STRATEGIC") return COLORS.blue;
    if (t === "COMPLIANCE") return COLORS.warn;
    if (t === "SURVEY") return "#7B61FF";
    if (t === "OPPORTUNITY") return "#7B61FF";
    return COLORS.accent;
  };

  const getLogStyle = (log) => {
    const base = { padding: "6px 10px", borderRadius: 6, marginBottom: 3, fontSize: 12, lineHeight: 1.5 };
    switch (log.type) {
      case "thinking": return { ...base, color: COLORS.textMuted, fontStyle: "italic" };
      case "reading": return { ...base, color: COLORS.blue };
      case "insight": return { ...base, color: COLORS.text, background: COLORS.bg, border: `1px solid ${COLORS.border}` };
      case "theme": return { ...base, color: COLORS.accent, background: COLORS.accentBg, border: `1px solid ${COLORS.accent}22` };
      case "matrix": return { ...base, color: "#7B61FF", background: "#7B61FF08" };
      case "roadmap": return { ...base, color: COLORS.warn, background: COLORS.warn + "08" };
      case "value": return { ...base, color: COLORS.accent, fontWeight: 600 };
      case "complete": return { ...base, color: COLORS.accent, fontWeight: 600 };
      case "plan": return { ...base, color: COLORS.blue, fontStyle: "italic" };
      case "build": return { ...base, color: "#7B61FF", background: "#7B61FF08" };
      case "verify": return { ...base, color: COLORS.text, background: COLORS.bg, border: `1px solid ${COLORS.border}` };
      case "refine": return { ...base, color: COLORS.warn, background: COLORS.warn + "08" };
      case "error": return { ...base, color: COLORS.danger || "#ef4444", background: "#ef444408", fontWeight: 600 };
      default: return base;
    }
  };

  const isDeckArtifactLog = (log) => !!(log?.meta?.kind === "artifact" && (log?.meta?.artifact_url || log?.meta?.artifact_html));
  const isPreviewableDeckArtifactLog = (log) => {
    if (!isDeckArtifactLog(log)) return false;
    if (log?.meta?.artifact_subkind === "slide-html" || !!log?.meta?.artifact_html) return true;
    const mime = String(log?.meta?.artifact_mime_type || "").toLowerCase();
    const url = String(log?.meta?.artifact_url || "").toLowerCase();
    return mime.startsWith("image/")
      || /\.(png|jpe?g|webp|gif|bmp|svg)(\?|$)/i.test(url);
  };
  const normalizePersistedPreviewArtifacts = (artifacts) => (
    Array.isArray(artifacts) ? artifacts : []
  ).map((artifact, index) => ({
    id: String(artifact?.id || artifact?.url || `saved-preview:${index}`),
    url: artifact?.url || null,
    name: artifact?.name || artifact?.title || `Slide ${index + 1}`,
    mimeType: artifact?.mimeType || null,
    html: artifact?.html || null,
    title: artifact?.title || null,
    summary: artifact?.summary || null,
    subkind: artifact?.kind || null,
    order: Number(artifact?.order ?? index),
    source: artifact?.source || "saved-deck.preview",
    slot: artifact?.slot ?? artifact?.slideSlot ?? null,
    slideId: artifact?.slideId || null,
    slideKey: artifact?.slideKey || artifact?.identityKey || null,
    versionKey: artifact?.versionKey || artifact?.contentFingerprint || null,
  })).filter((artifact) => artifact.html || artifact.url)
    .sort((a, b) => a.order - b.order);

  const runAnalysis = async () => {
    setIsReasoning(true);
    setReasoningCollapsed(false);
    setAnalysisRun(true);
    setReasoningLogs([]);
    setAnalysisError(null);

    setReasoningLogs(stampReasoningLogs("analysis", [
      { type: "thinking", text: "Starting analysis — fetching all audit data from database..." },
    ], { baseTs: Date.now() }));

    try {
      // Step 1: Kick off the analysis (returns immediately with a pending row)
      const pending = await api.audit.analyse({ project_id: project?.id || null });
      const analysisId = pending.id;

      if (!analysisId) {
        // Fallback: if backend returned completed result directly (shouldn't happen, but handle gracefully)
        const analysis = pending.analysis || pending.analysis_json || {};
        setAnalysisData(analysis);
        setIsReasoning(false);
        setShowGenerateBtn(true);
        return;
      }

      setReasoningLogs(stampReasoningLogs("analysis", [
        { type: "thinking", text: "Starting analysis — fetching all audit data from database..." },
        { type: "reading", text: `Loaded ${pending.source_summary?.transcripts || 0} transcripts, ${pending.source_summary?.surveys || 0} surveys, ${pending.source_summary?.interviews || 0} interview sets` },
        { type: "thinking", text: "Sending to AI for deep analysis — this takes 1-2 minutes..." },
      ], { baseTs: Date.now() }));

      // Step 2: Poll until completed or failed (max ~3 minutes)
      let analysis = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const poll = await api.audit.analyses.get(analysisId);

        if (poll.status === "completed") {
          analysis = poll.analysis || poll.analysis_json || {};
          break;
        }
        if (poll.status === "failed") {
          const errMsg = poll.analysis?.error || "Analysis failed on the server";
          throw new Error(errMsg);
        }

        // Show progress while waiting
        if (i === 5) setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", { type: "reading", text: "Still processing — extracting themes from transcripts..." })]);
        if (i === 15) setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", { type: "insight", text: "Cross-referencing survey data with interview findings..." })]);
        if (i === 25) setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", { type: "matrix", text: "Building opportunity matrix and roadmap..." })]);
      }

      if (!analysis) throw new Error("Analysis timed out — please try again");

      setAnalysisData(analysis);

      // Animate reasoning steps from the result
      const steps = Array.isArray(analysis.reasoning_steps) ? analysis.reasoning_steps : [];
      if (steps.length > 0) {
        const replayBaseTs = Date.now();
        setReasoningLogs([]);
        for (let i = 0; i < steps.length; i++) {
          await new Promise(r => setTimeout(r, 200));
          setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", steps[i], i, { baseTs: replayBaseTs })]);
        }
      } else {
        setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", { type: "complete", text: "Analysis completed." })]);
      }

      setIsReasoning(false);
      setShowGenerateBtn(true);

      const themeCount = Array.isArray(analysis.themes) ? analysis.themes.length : 0;
      const oppCount = Array.isArray(analysis.opportunities) ? analysis.opportunities.length : 0;
      const phaseCount = Array.isArray(analysis.roadmap) ? analysis.roadmap.length : 0;

      let summaryText = `Analysis complete.`;
      if (themeCount > 0) summaryText += ` I've identified ${themeCount} key theme${themeCount > 1 ? "s" : ""}`;
      if (oppCount > 0) summaryText += `, ${oppCount} opportunit${oppCount > 1 ? "ies" : "y"}`;
      if (phaseCount > 0) summaryText += `, and mapped a ${phaseCount}-phase implementation roadmap`;
      summaryText += ".\n\nYou can ask me questions about any of the findings, or hit Generate Deck to create your presentation.";
      if (analysis.executive_summary) {
        summaryText += `\n\nExecutive Summary:\n${analysis.executive_summary}`;
      }

      setChatMessages(prev => [...prev, { role: "agent", text: summaryText }]);
    } catch (err) {
      setIsReasoning(false);
      setAnalysisError(err.message || "Analysis failed");
      setReasoningLogs(prev => [...prev, stampReasoningLog("analysis", { type: "error", text: `Analysis failed: ${err.message || "Unknown error"}` })]);
      setChatMessages(prev => [...prev, { role: "agent", text: `I encountered an error running the analysis: ${err.message || "Unknown error"}\n\nPlease check that your Anthropic API key is configured in Settings > Integrations, and that you have some data (transcripts, surveys, or interviews) available.` }]);
    }
  };

  const mapDeckLogToDisplay = (log) => stampReasoningLog("deck", {
    type: log.type || "complete",
    text: log.text || "",
    detail: log.detail || log.why || null,
    stage: log.stage || null,
    meta: log.meta || null,
    ts: log.ts,
  });

  const isClaudeTranscriptDeckLog = (log) => log?.meta?.kind === "claude-transcript";

  const combineDeckVisibleLogs = (logs) => {
    if (!Array.isArray(logs) || logs.length === 0) return [];
    const combined = [];
    for (const log of logs) {
      if (isClaudeTranscriptDeckLog(log)) {
        const previous = combined[combined.length - 1];
        if (previous && isClaudeTranscriptDeckLog(previous)) {
          previous.text = `${previous.text || ""}${log.text || ""}`;
          previous.meta = { ...(previous.meta || {}), ...(log.meta || {}) };
          previous.stage = log.stage || previous.stage || null;
          continue;
        }
      }
      combined.push({ ...log });
    }
    return combined;
  };

  const getDeckPollIntervalMs = (logs, status) => {
    const latestStage = [...(Array.isArray(logs) ? logs : [])].reverse().find(log => log?.stage)?.stage || null;
    if (status === "pending") return 750;
    if (!latestStage || ["collecting-data", "calling-claude", "specs-ready"].includes(latestStage)) return 750;
    return 1000;
  };

  const pollDeckGenerationJob = async (jobId) => {
    if (!jobId) throw new Error("No deck job id provided");
    const pollRunId = ++activeDeckPollRef.current;
    setDeckJobId(jobId);
    setDeckJobStatus("running");
    onDeckGenerating?.(true);
    setDeckError(null);
    setReasoningCollapsed(false);

    let pollIntervalMs = 750;
    const pollStartedAt = Date.now();
    let consecutiveErrors = 0;
    let consecutiveMalformed = 0;

    for (;;) {
      await new Promise(r => setTimeout(r, pollIntervalMs));
      if (activeDeckPollRef.current !== pollRunId) return { status: "aborted" };

      // 80-minute hard ceiling
      if (Date.now() - pollStartedAt > 80 * 60 * 1000) {
        clearDeckJobState({ clearLogs: false });
        throw new Error("Deck generation timed out. Check the Decks tab for your result.");
      }

      let poll;
      try {
        poll = await api.audit.decks.generatePoll(jobId);
        consecutiveErrors = 0;
      } catch (networkErr) {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          pollIntervalMs = 5000;
          console.warn(`[deck-poll] ${consecutiveErrors} consecutive errors, slowing to 5s:`, networkErr.message);
        }
        continue;
      }
      if (activeDeckPollRef.current !== pollRunId) return { status: "aborted" };

      // Validate response shape
      if (!poll || typeof poll.status === "undefined") {
        consecutiveMalformed++;
        if (consecutiveMalformed >= 5) {
          clearDeckJobState({ clearLogs: false });
          throw new Error("Deck generation status unavailable. Please refresh and check the Decks tab.");
        }
        continue;
      }
      consecutiveMalformed = 0;
      if (consecutiveErrors > 0) {
        pollIntervalMs = getDeckPollIntervalMs([], poll.status || "running");
      }

      const logs = Array.isArray(poll.reasoning_logs) ? poll.reasoning_logs : [];
      const mappedLogs = logs.map(mapDeckLogToDisplay);
      setDeckJobStatus(poll.status || "running");
      setDeckReasoningLogs(prev => (mappedLogs.length > 0 ? mappedLogs : prev));
      if (poll.manus_task_url) setActiveManusTaskUrl(poll.manus_task_url);
      pollIntervalMs = getDeckPollIntervalMs(logs, poll.status || "running");

      if (poll.status === "completed") {
        const deck = await api.audit.decks.get(poll.deck_id);
        if (activeDeckPollRef.current !== pollRunId) return { status: "aborted" };
        setDeckData(deck);
        setDeckGenerated(true);
        setShowGenerateBtn(true);
        clearDeckJobState({ clearLogs: false });
        notifyDeckJob("success", "Your deck is ready.");
        try { deckBroadcast?.postMessage({ type: "completed", jobId, deckId: poll.deck_id }); } catch {}
        return { status: "completed", deck };
      }
      if (poll.status === "failed") {
        const errMsg = poll.error || "Deck generation failed";
        clearDeckJobState({ clearLogs: false });
        try { deckBroadcast?.postMessage({ type: "failed", jobId, error: errMsg }); } catch {}
        throw new Error(errMsg);
      }
      if (poll.status === "cancelled") {
        setDeckReasoningLogs(prev => (mappedLogs.length > 0 ? mappedLogs : prev));
        clearDeckJobState({ clearLogs: false });
        setShowGenerateBtn(true);
        notifyDeckJob("error", poll.error || "Deck generation stopped.");
        try { deckBroadcast?.postMessage({ type: "cancelled", jobId }); } catch {}
        return { status: "cancelled" };
      }
    }
  };

  const runDeckGenerationAsync = async (generateBody, { isRevision = false } = {}) => {
    setDeckGenerated(false);
    setDeckData(null);
    resetPreviewSelectionState({ clearPreviewLogs: true });
    setReasoningCollapsed(false);
    setDeckReasoningLogs(stampReasoningLogs("deck", [
      {
        type: "plan",
        stage: "queued",
        text: isRevision ? "Deck revision requested." : "Deck generation requested.",
        detail: "Preparing the brief and connecting the deck workflow.",
      },
      {
        type: "thinking",
        stage: "queued",
        text: "Waiting for the deck job to start streaming updates...",
      },
    ], { baseTs: Date.now() }));
    const initRes = await api.audit.decks.generate(generateBody);
    const jobId = initRes.job_id;
    if (!jobId) throw new Error("No job_id returned");
    setDeckJobStatus(initRes.status || "pending");
    setStoredDeckJob(project?.id, {
      jobId,
      projectId: project?.id || null,
      title: generateBody?.title || null,
      startedAt: new Date().toISOString(),
      isRevision,
    });
    return pollDeckGenerationJob(jobId);
  };

  const refreshDeckStatusFromServer = React.useCallback(async ({ resumePolling = true, force = false } = {}) => {
    if (!project?.id) return null;

    const refreshState = deckStatusRefreshRef.current;
    const now = Date.now();
    if (!force) {
      if (refreshState.inFlight) return refreshState.inFlight;
      if (now - refreshState.lastStartedAt < 800) return null;
    }

    const refreshPromise = (async () => {
      const storedJob = getStoredDeckJob(project.id);
      let latestJob = null;
      try {
        latestJob = await api.audit.decks.generateLatest({ project_id: project.id });
      } catch {}

      const latestLogs = Array.isArray(latestJob?.reasoning_logs) ? latestJob.reasoning_logs.map(mapDeckLogToDisplay) : [];

      if (latestJob?.job_id && ["pending", "running", "finalizing"].includes(latestJob.status)) {
        setDeckData(null);
        setDeckGenerated(false);
        setShowGenerateBtn(false);
        setDeckError(null);
        setDeckJobId(latestJob.job_id);
        setDeckJobStatus(latestJob.status);
        setDeckReasoningLogs(prev => (latestLogs.length > 0 ? latestLogs : prev));
        setActiveManusTaskUrl(latestJob.manus_task_url || null);
        onDeckGenerating?.(true);
        setStoredDeckJob(project.id, {
          jobId: latestJob.job_id,
          projectId: project.id,
          title: null,
          startedAt: latestJob.created_at || new Date().toISOString(),
          isRevision: false,
        });

        if (resumePolling && (!deckJobIdRef.current || deckJobIdRef.current !== latestJob.job_id || !["pending", "running", "finalizing"].includes(deckJobStatusRef.current))) {
          return pollDeckGenerationJob(latestJob.job_id);
        }
        return { status: latestJob.status, jobId: latestJob.job_id };
      }

      if (storedJob?.jobId && resumePolling && !latestJob?.job_id && !deckGeneratedRef.current) {
        // Don't blindly poll — check job status first to avoid reconciliation on archived Manus tasks
        try {
          const check = await api.audit.decks.generatePoll(storedJob.jobId);
          if (check?.status === "completed" && check.deck_id) {
            clearStoredDeckJob(project.id);
            const deck = await api.audit.decks.get(check.deck_id);
            setDeckData(deck);
            setDeckGenerated(true);
            setShowGenerateBtn(true);
            setDeckJobId(null);
            setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
            onDeckGenerating?.(false);
            notifyDeckJob("success", "Your deck is ready.");
            return { status: "completed", deck };
          }
          if (["failed", "cancelled"].includes(check?.status)) {
            clearStoredDeckJob(project.id);
            setDeckJobId(null);
            setDeckJobStatus(check.status);
            setShowGenerateBtn(true);
            if (check.error) setDeckError(check.error);
            onDeckGenerating?.(false);
            return { status: check.status };
          }
          if (["pending", "running", "finalizing"].includes(check?.status)) {
            return pollDeckGenerationJob(storedJob.jobId);
          }
        } catch {
          clearStoredDeckJob(project.id);
        }
      }

      if (latestJob?.job_id && latestLogs.length > 0) {
        setDeckReasoningLogs(latestLogs);
      }

      if (latestJob?.status === "completed" && latestJob.deck_id) {
        clearStoredDeckJob(project.id);
        setDeckJobId(null);
        setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
        onDeckGenerating?.(false);
        const deck = await api.audit.decks.get(latestJob.deck_id);
        setDeckData(deck);
        setDeckGenerated(true);
        setShowGenerateBtn(true);
        setDeckError(null);
        return { status: "completed", deck };
      }

      if (latestJob?.job_id && ["failed", "cancelled"].includes(latestJob.status)) {
        clearStoredDeckJob(project.id);
        setDeckJobId(null);
        setDeckJobStatus(latestJob.status);
        setShowGenerateBtn(true);
        setDeckError(latestJob.error || null);
        onDeckGenerating?.(false);
      }

      try {
        const latestDeck = await api.audit.decks.latest({ project_id: project.id });
        if (latestDeck?.id) {
          setDeckData(latestDeck);
          setDeckGenerated(true);
          setShowGenerateBtn(true);
          if (!latestJob?.job_id || latestJob.status === "completed") {
            setDeckJobId(null);
            setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
          }
          return { status: latestJob?.status || "deck", deck: latestDeck };
        }
      } catch {}

      if (!latestJob?.job_id) {
        clearStoredDeckJob(project.id);
        setDeckJobId(null);
        setDeckJobStatus(null);
    setActiveManusTaskUrl(null);
        onDeckGenerating?.(false);
      }

      return { status: latestJob?.status || null };
    })();

    refreshState.inFlight = refreshPromise;
    refreshState.lastStartedAt = now;
    try {
      return await refreshPromise;
    } finally {
      if (deckStatusRefreshRef.current.inFlight === refreshPromise) {
        deckStatusRefreshRef.current.inFlight = null;
      }
    }
  }, [onDeckGenerating, project?.id]);

  const stopDeckGeneration = async () => {
    if (!deckJobId || isStoppingDeck) return;
    const jobIdToCancel = deckJobId;
    setIsStoppingDeck(true);
    activeDeckPollRef.current += 1;
    try {
      const result = await api.audit.decks.generateCancel(jobIdToCancel);
      const logs = Array.isArray(result?.reasoning_logs) ? result.reasoning_logs : [];
      setDeckJobStatus(result?.status || "cancelled");
      const mappedLogs = logs.map(mapDeckLogToDisplay);
      setDeckReasoningLogs(prev => (mappedLogs.length > 0 ? mappedLogs : prev));
      setIsPreviewFollowingLatest(false);
      clearDeckJobState({ clearLogs: false });
      setShowGenerateBtn(true);
      setDeckError(null);
      setIsTyping(false);
      setChatMessages(prev => [...prev, { role: "agent", text: "Deck generation stopped. You can start a new run whenever you want." }]);
    } catch (err) {
      setIsStoppingDeck(false);
      const msg = err.message || "Could not stop deck generation";
      setDeckError(msg);
      notifyDeckJob("error", msg);
      setChatMessages(prev => [...prev, { role: "agent", text: `I couldn't stop the deck job cleanly: ${msg}` }]);
    }
  };

  useEffect(() => {
    refreshDeckStatusFromServer({ resumePolling: true }).catch((err) => {
      const msg = err.message || "Deck generation failed";
      setDeckError(msg);
    });
  }, [project?.id, refreshDeckStatusFromServer]);

  useEffect(() => {
    if (!isActive || !project?.id) return;
    refreshDeckStatusFromServer({ resumePolling: true, force: true }).catch(() => {});
  }, [isActive, project?.id, refreshDeckStatusFromServer]);

  useEffect(() => {
    if (!isActive || typeof document === "undefined" || typeof window === "undefined") return undefined;

    const handleVisible = () => {
      if (document.visibilityState !== "visible") return;
      refreshDeckStatusFromServer({ resumePolling: true, force: true }).catch(() => {});
    };
    const handleFocus = () => {
      refreshDeckStatusFromServer({ resumePolling: true, force: true }).catch(() => {});
    };

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isActive, refreshDeckStatusFromServer]);

  const isActiveDeckJob = ["pending", "running", "finalizing"].includes(deckJobStatus);
  const activeDeckLogs = isActiveDeckJob ? deckReasoningLogs : [];
  const visibleDeckLogs = combineDeckVisibleLogs(activeDeckLogs);
  const visibleAnalysisLogs = reasoningLogs.slice(0, visibleReasoningCount);
  const mergedReasoningLogs = [...visibleAnalysisLogs, ...chatReasoningLogs, ...combineDeckVisibleLogs(deckReasoningLogs)]
    .sort((a, b) => {
      const tsA = Number.isFinite(a?.loggedAt) ? a.loggedAt : 0;
      const tsB = Number.isFinite(b?.loggedAt) ? b.loggedAt : 0;
      if (tsA !== tsB) return tsA - tsB;
      const orderA = Number.isFinite(a?.order) ? a.order : 0;
      const orderB = Number.isFinite(b?.order) ? b.order : 0;
      return orderA - orderB;
    });
  const artifactPreviewSlides = normalizePreviewSlides(deckReasoningLogs
    .filter(isPreviewableDeckArtifactLog)
    .map((log, index) => {
      const artifactBaseKey =
        log?.meta?.artifact_slide_key
        || log?.meta?.session_artifact_key
        || log?.meta?.artifact_url
        || "artifact";
      const artifactVersionKey =
        log?.meta?.artifact_version_key
        || (log?.meta?.manus_output_index ?? index);
      return {
        id: `${artifactBaseKey}:${artifactVersionKey}:${index}`,
        url: log?.meta?.artifact_url,
        name: log?.meta?.artifact_name || log?.text || `Slide ${index + 1}`,
        mimeType: log?.meta?.artifact_mime_type || null,
        html: log?.meta?.artifact_html || null,
        title: log?.meta?.artifact_title || null,
        summary: log?.meta?.artifact_summary || null,
        subkind: log?.meta?.artifact_subkind || null,
        order: Number(log?.meta?.artifact_sort_order ?? log?.meta?.manus_output_index ?? index),
        slot: log?.meta?.artifact_slide_slot ?? null,
        slideId: log?.meta?.artifact_slide_id || null,
        slideKey: log?.meta?.artifact_slide_key || null,
        versionKey: log?.meta?.artifact_version_key || null,
      };
    })
  );
  const savedDeckPreviewSlides = normalizePreviewSlides(normalizePersistedPreviewArtifacts(deckData?.preview_artifacts_json));
  const previewSlides = isActiveDeckJob
    ? (artifactPreviewSlides.length > 0 ? artifactPreviewSlides : savedDeckPreviewSlides)
    : (savedDeckPreviewSlides.length > 0 ? savedDeckPreviewSlides : artifactPreviewSlides);
  const previewSlidesSignature = React.useMemo(
    () => previewSlides.map((slide) => `${slide.slotKey}:${slide.versionKey}`).join("|"),
    [previewSlides],
  );
  const hasArtifactPreviewSlides = previewSlides.length > 0;
  const currentArtifactSlide = hasArtifactPreviewSlides
    ? previewSlides[Math.max(0, Math.min(currentArtifactSlideIdx, previewSlides.length - 1))]
    : null;
  const latestDeckStageId = [...activeDeckLogs].reverse().find(log => log?.stage)?.stage || null;
  const latestDeckStageIndexRaw = latestDeckStageId ? DECK_WORKFLOW_STAGES.findIndex(stage => stage.id === latestDeckStageId) : -1;
  const latestDeckStageIndex = latestDeckStageIndexRaw >= 0 ? latestDeckStageIndexRaw : -1;
  const latestClaudeProgressTokens = [...activeDeckLogs].reverse().find(log => log?.meta?.estimated_streamed_output_tokens != null)?.meta?.estimated_streamed_output_tokens ?? null;
  const latestBuilderProgress = [...activeDeckLogs].reverse().find(log => log?.meta?.builder_progress_pct != null)?.meta?.builder_progress_pct ?? null;
  const showBuilderProgress = ["submitting-manus", "manus-running", "downloading"].includes(latestDeckStageId);
  const deckGeneratedAt = formatDeckGeneratedAt(deckData?.updated_at || deckData?.created_at);
  useEffect(() => {
    if (isReasoning || isTyping || isActiveDeckJob) {
      setReasoningCollapsed(false);
    }
  }, [isActiveDeckJob, isReasoning, isTyping]);

  useEffect(() => {
    if (!previewSlides.length) {
      resetPreviewSelectionState();
      return;
    }
    const maxIndex = previewSlides.length - 1;
    const pinnedIndex = selectedPreviewSlotKey
      ? previewSlides.findIndex(slide => slide.slotKey === selectedPreviewSlotKey)
      : -1;
    if (pinnedIndex >= 0) {
      setCurrentArtifactSlideIdx(pinnedIndex);
      return;
    }
    if (isActiveDeckJob && artifactPreviewSlides.length > 0 && isPreviewFollowingLatest) {
      const latestSlide = previewSlides[maxIndex];
      setCurrentArtifactSlideIdx(maxIndex);
      setSelectedPreviewSlotKey(latestSlide?.slotKey || null);
      return;
    }
    const nextIndex = Math.max(0, Math.min(currentArtifactSlideIdx, maxIndex));
    const nextSlide = previewSlides[nextIndex] || previewSlides[0];
    setCurrentArtifactSlideIdx(nextIndex);
    setSelectedPreviewSlotKey(nextSlide?.slotKey || null);
  }, [
    artifactPreviewSlides.length,
    currentArtifactSlideIdx,
    isActiveDeckJob,
    isPreviewFollowingLatest,
    previewSlides,
    previewSlidesSignature,
    resetPreviewSelectionState,
    selectedPreviewSlotKey,
  ]);

  React.useLayoutEffect(() => {
    const viewportEl = previewViewportRef.current;
    const railEl = previewThumbnailRailRef.current;
    if (typeof ResizeObserver === "undefined") {
      setMainPreviewScale(1);
      setThumbnailPreviewScale(0.09);
      return;
    }

    const updateScales = () => {
      if (viewportEl) {
        const nextMainScale = getPreviewScale(viewportEl.clientWidth || 0, viewportEl.clientHeight || 0);
        if (nextMainScale != null) setMainPreviewScale(nextMainScale);
      }
      if (railEl) {
        const thumbnailWidth = Math.max(0, (railEl.clientWidth || 0) - 14);
        const thumbnailHeight = thumbnailWidth * (PREVIEW_BASE_HEIGHT / PREVIEW_BASE_WIDTH);
        const nextThumbnailScale = getPreviewScale(thumbnailWidth, thumbnailHeight);
        if (nextThumbnailScale != null) setThumbnailPreviewScale(nextThumbnailScale);
      }
    };

    const frame = requestAnimationFrame(() => updateScales());
    const observer = new ResizeObserver(() => updateScales());
    if (viewportEl) observer.observe(viewportEl);
    if (railEl) observer.observe(railEl);
    window.addEventListener("resize", updateScales);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", updateScales);
    };
  }, [currentArtifactSlide?.slotKey, currentArtifactSlide?.versionKey, deckGenerated, hasArtifactPreviewSlides, previewSlidesSignature]);

  const startDeckGeneration = async () => {
    setShowGenerateBtn(false);
    setIsTyping(true);
    setReasoningCollapsed(false);
    setDeckError(null);
    setShowDeckGenerateConfirm(false);
    resetPreviewSelectionState({ clearPreviewLogs: true });
    setDeckData(null);
    setChatMessages(prev => [...prev, { role: "agent", text: "Generating your presentation deck - first creating the brief with specs_agent, then sending it to builder_agent to build the PowerPoint..." }]);
    try {
      const result = await runDeckGenerationAsync({
        prompt: `Create a comprehensive consulting assessment deck based on the full analysis. Include title, agenda, current state findings, key themes, opportunity matrix, implementation roadmap, investment case, and next steps.`,
        title: project?.name ? `${project.name} — Assessment Deck` : 'Strategic Assessment Deck',
        project_id: project?.id || null,
        analysis_context: analysisData || null,
        style_profile_id: selectedStyleId || null,
        chat_messages: chatMessages || [],
        source_summary: sourceSummary || null,
      });
      setIsTyping(false);
      if (result?.status !== "completed") return;
      setChatMessages(prev => [...prev, { role: "agent", text: "Your deck is ready.\n\nspecs_agent prepared the brief and builder_agent produced the final PowerPoint.\n\nClick the preview on the right to open it, or download the PowerPoint directly. You can also ask me to revise the deck." }]);
    } catch (err) {
      setIsTyping(false);
      setShowGenerateBtn(true);
      onDeckGenerating?.(false);
      const msg = err.message || 'Failed to generate deck';
      clearStoredDeckJob(project?.id);
      setDeckError(msg);
      notifyDeckJob("error", msg);
      let userMsg = `I encountered an error generating the deck: ${whiteLabelDeckError(msg)}`;
      if (msg.includes('Manus') && msg.includes('not configured')) {
        userMsg += '\n\nThe presentation service isn\'t configured yet. Please contact support.';
      } else if (msg.includes('Anthropic') || msg.includes('API key') || msg.includes('not configured')) {
        userMsg += '\n\nPlease add your Anthropic API key in Settings > Integrations > LLM.';
      } else if (msg.includes('credit') || msg.includes('quota')) {
        userMsg += '\n\nYour Anthropic account may need credits. Check console.anthropic.com.';
      } else if (msg.includes('rate limit')) {
        userMsg += '\n\nPlease wait a moment and try again.';
      }
      setChatMessages(prev => [...prev, { role: "agent", text: userMsg }]);
    }
  };

  const handleGenerateDeckClick = () => {
    if (isTyping || isReasoning || isActiveDeckJob) return;
    setShowDeckGenerateConfirm(true);
  };

  const sendMessage = async () => {
    const trimmedInput = userInput.trim();
    if (!trimmedInput || isTyping || isReasoning) return;
    const msg = trimmedInput.toLowerCase();
    const newMessages = [...chatMessages, { role: "user", text: trimmedInput }];
    setChatMessages(newMessages);
    setUserInput("");
    setIsTyping(true);
    setReasoningCollapsed(false);
    appendChatReasoningLog({ type: "plan", text: "New analyst chat message received.", detail: trimmedInput });

    if (!analysisRun && (msg.includes("run") || msg.includes("start") || msg.includes("analyse") || msg.includes("analyze") || msg.includes("go ahead") || msg.includes("full analysis"))) {
      setChatMessages([...newMessages, { role: "agent", text: "Starting the full analysis now. I'll work through all transcripts and surveys, identify themes, map opportunities, and build a roadmap. Watch the reasoning panel..." }]);
      appendChatReasoningLog({ type: "complete", text: "Handing off from chat to the full analysis workflow." });
      setIsTyping(false);
      await new Promise(r => setTimeout(r, 400));
      runAnalysis();
      return;
    }

    // Deck revision intent: update/revise/change deck when deck exists
    const hasRevisionVerb = /\b(update|revise|change|modify|condense|restructure|make|apply|use)\b/.test(msg);
    const hasDeckContext = /\b(deck|slides?)\b/.test(msg) || /\d+\s*slides?/.test(msg) || msg.includes("proposed");
    const isDeckRevisionIntent = deckGenerated && hasRevisionVerb && hasDeckContext;

    if (isDeckRevisionIntent) {
      appendChatReasoningLog({ type: "build", text: "Deck revision requested from chat.", detail: trimmedInput });
      setChatMessages(prev => [...prev, { role: "agent", text: "Updating your deck..." }]);
      try {
        const lastAgent = chatMessages.filter(m => m.role === "agent").pop();
        const referencesProposal = msg.includes("proposed") || msg.includes("you suggested") || msg.includes("from above");
        let revisionPrompt = `Create a deck based on the analysis. The user wants to revise the deck: "${trimmedInput}"`;
        if (referencesProposal && lastAgent?.text) {
          const context = String(lastAgent.text).slice(0, 2000);
          revisionPrompt += `\n\nUse this structure from my previous response as guidance:\n${context}`;
        }
        const result = await runDeckGenerationAsync({
          prompt: revisionPrompt,
          title: project?.name ? `${project.name} — Assessment Deck` : 'Strategic Assessment Deck',
          project_id: project?.id || null,
          analysis_context: analysisData || null,
          style_profile_id: selectedStyleId || null,
          chat_messages: chatMessages || [],
          source_summary: sourceSummary || null,
        });
        if (result?.status !== "completed") {
          setIsTyping(false);
          return;
        }
        setChatMessages(prev => {
          const withoutPlaceholder = prev.slice(0, -1);
          return [...withoutPlaceholder, { role: "agent", text: "I've updated the deck and sent the revised brief through the same specs_agent to builder_agent workflow." }];
        });
        appendChatReasoningLog({ type: "complete", text: "Deck revision workflow completed." });
        setDeckError(null);
      } catch (err) {
        onDeckGenerating?.(false);
        const msg = err.message || 'Failed to update deck';
        clearStoredDeckJob(project?.id);
        setDeckError(msg);
        notifyDeckJob("error", msg);
        appendChatReasoningLog({ type: "error", text: "Deck revision workflow failed.", detail: msg });
        let userMsg = `I couldn't update the deck: ${whiteLabelDeckError(msg)}`;
        if (msg.includes('Manus') && msg.includes('not configured')) userMsg += '\n\nThe presentation service isn\'t configured yet. Please contact support.';
        else if (msg.includes('Anthropic') || msg.includes('API key') || msg.includes('not configured')) userMsg += '\n\nPlease add your Anthropic API key in Settings > Integrations > LLM.';
        else if (msg.includes('credit') || msg.includes('quota')) userMsg += '\n\nYour Anthropic account may need credits. Check console.anthropic.com.';
        else if (msg.includes('rate limit')) userMsg += '\n\nPlease wait a moment and try again.';
        setChatMessages(prev => [...prev.slice(0, -1), { role: "agent", text: userMsg }]);
      }
      setIsTyping(false);
      return;
    }

    appendChatReasoningLog({ type: "thinking", text: "AI analyst is preparing a response..." });
    try {
      const resp = await api.audit.analyseChat({
        message: trimmedInput,
        analysis_context: analysisData || null,
        history: newMessages.slice(-10),
      });
      appendChatReasoningLog({
        type: "complete",
        text: "AI analyst responded.",
        detail: String(resp.text || "I was unable to generate a response.").slice(0, 320),
      });
      setChatMessages(prev => [...prev, { role: "agent", text: resp.text || "I was unable to generate a response." }]);
    } catch (err) {
      appendChatReasoningLog({
        type: "error",
        text: "AI analyst chat failed.",
        detail: err.message || "Could not connect to the AI analyst. Check your API key configuration.",
      });
      setChatMessages(prev => [...prev, { role: "agent", text: `Error: ${err.message || "Could not connect to the AI analyst. Check your API key configuration."}` }]);
    }
    setIsTyping(false);
  };

  if (showFullDeck) return <AuditFullDeckViewer project={project} deckData={deckData} onClose={() => setShowFullDeck(false)} />;

  const isManusDeck = !!deckData?.pptx_url;
  const SLIDE_LABELS = deckData?.slide_data?.map(s => s.label || s.template_slug) || (isManusDeck ? [] : ["Title", "Agenda", "Current State", "Key Finding", "Survey Results", "Opportunity Matrix", "Roadmap", "Investment Case", "Value Projection", "Next Steps"]);

  return (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ width: 440, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14 }}>🧠</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>AI Analyst</div>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>
                {isReasoning ? "Running analysis..." : deckGenerated ? "Deck ready — ask for revisions" : analysisRun ? "Analysis complete" : "Ask questions or start analysis"}
              </div>
            </div>
          </div>
          {(showGenerateBtn || analysisData) && !isActiveDeckJob && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
              {false && (<div ref={promptVariantPickerTriggerRef} style={{ position: "relative" }}>
                <button onClick={() => { setShowStylePicker(false); setShowPromptVariantPicker(!showPromptVariantPicker); }} style={{ padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 10, color: COLORS.textDim, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, maxWidth: 190 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: COLORS.blue, display: "inline-block", flexShrink: 0 }}></span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedPromptVariant?.name || "Deck brief"}</span>
                  <span style={{ fontSize: 8, flexShrink: 0 }}>â–¼</span>
                </button>
                {showPromptVariantPicker && promptVariantPickerRect && createPortal(
                  <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setShowPromptVariantPicker(false)}>
                    <div onClick={e => e.stopPropagation()} style={{
                      position: "fixed",
                      left: Math.max(16, promptVariantPickerRect.right - 320),
                      top: promptVariantPickerRect.bottom + 4,
                      background: COLORS.surface,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 8,
                      boxShadow: "0 8px 24px rgba(0,0,0,.3)",
                      zIndex: 9999,
                      width: 320,
                      maxHeight: 360,
                      overflow: "auto",
                    }}>
                      {deckPromptVariants.map(variant => (
                        <div key={variant.id} onClick={() => { setSelectedPromptVariantId(variant.id); setShowPromptVariantPicker(false); }} style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          background: variant.id === selectedPromptVariantId ? COLORS.accent + "15" : "transparent",
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}>
                          <span style={{ width: 8, height: 8, borderRadius: 999, background: variant.id === selectedPromptVariantId ? COLORS.accent : COLORS.blue, display: "inline-block", marginTop: 5, flexShrink: 0 }}></span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{variant.name}</div>
                            {variant.description && <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2, lineHeight: 1.45 }}>{variant.description}</div>}
                          </div>
                          {variant.id === selectedPromptVariantId && <span style={{ marginLeft: "auto", color: COLORS.accent, fontSize: 12 }}>âœ“</span>}
                        </div>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>)}
              {false && (<div ref={stylePickerTriggerRef} style={{ position: "relative" }}>
                <button onClick={() => { setShowPromptVariantPicker(false); setShowStylePicker(!showStylePicker); }} style={{ padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 10, color: COLORS.textDim, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: styleProfiles.find(p => p.id === selectedStyleId)?.color_scheme?.accent || COLORS.accent, display: "inline-block" }}></span>
                  {styleProfiles.find(p => p.id === selectedStyleId)?.name || "Style"}
                  <span style={{ fontSize: 8 }}>▼</span>
                </button>
                {showStylePicker && stylePickerRect && createPortal(
                  <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setShowStylePicker(false)}>
                    <div onClick={e => e.stopPropagation()} style={{
                      position: "fixed",
                      left: stylePickerRect.right - 260,
                      top: stylePickerRect.bottom + 4,
                      background: COLORS.surface,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 8,
                      boxShadow: "0 8px 24px rgba(0,0,0,.3)",
                      zIndex: 9999,
                      width: 260,
                      maxHeight: 320,
                      overflow: "auto",
                    }}>
                      {styleProfiles.map(sp => (
                        <div key={sp.id} onClick={() => { setSelectedStyleId(sp.id); setShowStylePicker(false); }} style={{
                          padding: "10px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                          background: sp.id === selectedStyleId ? COLORS.accent + "15" : "transparent",
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}>
                          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                            {[sp.color_scheme?.dark, sp.color_scheme?.accent, sp.color_scheme?.blue, sp.color_scheme?.green].filter(Boolean).map((c, i) => (
                              <span key={i} style={{ width: 12, height: 12, borderRadius: 2, background: c, display: "inline-block", border: "1px solid rgba(255,255,255,.1)" }}></span>
                            ))}
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{sp.name}</div>
                            {sp.firm && <div style={{ fontSize: 9, color: COLORS.textDim }}>{sp.firm}</div>}
                          </div>
                          {sp.id === selectedStyleId && <span style={{ marginLeft: "auto", color: COLORS.accent, fontSize: 12 }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>)}
              <button onClick={handleGenerateDeckClick} style={{ padding: "8px 16px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", animation: "pulse 2s infinite" }}>Generate Deck</button>
            </div>
          )}
          {isActiveDeckJob && !!deckJobId && (
            <button
              onClick={stopDeckGeneration}
              disabled={isStoppingDeck}
              style={{
                padding: "8px 14px",
                background: "transparent",
                color: isStoppingDeck ? COLORS.textDim : "#fca5a5",
                border: `1px solid ${isStoppingDeck ? COLORS.border : "#7f1d1d"}`,
                borderRadius: 6,
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                cursor: isStoppingDeck ? "default" : "pointer",
              }}
            >
              {isStoppingDeck ? "Stopping..." : "Stop Generation"}
            </button>
          )}
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          {/* Reasoning panel — always visible, never scrolled away */}
          <div style={{ flex: "0 0 auto", padding: "14px 16px 0 16px" }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", borderBottomLeftRadius: 4 }}>
              <div onClick={() => setReasoningCollapsed(prev => !prev)} style={{ padding: "8px 12px", borderBottom: reasoningCollapsed ? "none" : `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <span style={{ fontSize: 10 }}>🧠</span>
                <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>REASONING</span>
                <ProgressDots active={isReasoning || isTyping || isActiveDeckJob} />
                {isActiveDeckJob && latestDeckStageId === "calling-claude" && latestClaudeProgressTokens != null && (
                  <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.accent, fontWeight: 600, marginLeft: "auto" }}>
                    Deck size ~{latestClaudeProgressTokens}
                  </span>
                )}
                {isActiveDeckJob && showBuilderProgress && latestBuilderProgress != null && (
                  <span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.accent, fontWeight: 600, marginLeft: "auto" }}>
                    Building ~{latestBuilderProgress}%
                  </span>
                )}
                {!isActiveDeckJob && !(latestDeckStageId === "calling-claude" && latestClaudeProgressTokens != null) && !(showBuilderProgress && latestBuilderProgress != null) && <span style={{ marginLeft: "auto", color: COLORS.textDim, fontSize: 10, transform: reasoningCollapsed ? "rotate(-90deg)" : "rotate(0)", transition: "transform 0.2s" }}>▾</span>}
              </div>
              <div ref={reasoningContentRef} style={{ padding: reasoningCollapsed ? 0 : "8px 10px", maxHeight: reasoningCollapsed ? 0 : "min(280px, 30vh)", overflow: reasoningCollapsed ? "hidden" : "auto", transition: "max-height 0.2s ease" }}>
                {isActiveDeckJob && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6, marginBottom: 8 }}>
                    {DECK_WORKFLOW_STAGES.map((stage, idx) => {
                      const isDone = latestDeckStageIndex >= idx;
                      const isCurrent = latestDeckStageIndex === idx && isActiveDeckJob;
                      return (
                        <div key={stage.id} style={{
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: `1px solid ${isDone ? COLORS.accent + "40" : COLORS.border}`,
                          background: isCurrent ? COLORS.accent + "18" : isDone ? COLORS.accent + "10" : COLORS.bg,
                          color: isDone ? COLORS.accent : COLORS.textDim,
                          fontFamily: FONT,
                          fontSize: 9,
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}>{stage.label}</div>
                      );
                    })}
                  </div>
                )}
                {mergedReasoningLogs.length === 0 && (
                  <div style={{ padding: "6px 2px", fontSize: 11, color: COLORS.textDim, lineHeight: 1.6 }}>
                    Activity from chat, analysis, and AI will appear here as soon as a workflow starts for this project.
                  </div>
                )}
                {mergedReasoningLogs.map((log, i) => (
                  <div key={log.eventKey || i} style={getLogStyle(log)}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                      <div style={{ flex: 1 }}>
                        {isDeckArtifactLog(log) ? (
                          <div
                            style={{
                              display: "block",
                              color: COLORS.text,
                              background: COLORS.bg,
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 8,
                              padding: "8px 10px",
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>
                              {whiteLabelDeckError(log.meta.artifact_name || log.text || "Artifact")}
                            </div>
                            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                              {log.meta.artifact_subkind === "slide-html"
                                ? "Live slide preview"
                                : `${log.meta.artifact_mime_type || "Artifact"}${log.meta.artifact_url ? " · Open artifact" : ""}`}
                            </div>
                            {log.meta.artifact_url && (
                              <a
                                href={log.meta.artifact_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: "inline-block", marginTop: 6, fontSize: 10, color: COLORS.accent, textDecoration: "none" }}
                              >
                                Open original artifact
                              </a>
                            )}
                          </div>
                        ) : (
                          <div style={{ whiteSpace: isClaudeTranscriptDeckLog(log) ? "pre-wrap" : "pre-line" }}>{whiteLabelDeckError(log.text)}</div>
                        )}
                        {log.detail && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 1, whiteSpace: "pre-line" }}>{whiteLabelDeckError(log.detail)}</div>}
                        {!log.stage && log.meta && Object.entries(log.meta).some(([key, value]) => !/manus/i.test(key) && value !== null && value !== undefined && value !== "") && (
                          <div style={{ marginTop: 6, padding: "6px 8px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                            {Object.entries(log.meta).filter(([key, value]) => !/manus/i.test(key) && value !== null && value !== undefined && value !== "").map(([key, value]) => (
                              <div key={key} style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.4, wordBreak: "break-word" }}>
                                <span style={{ fontFamily: FONT, color: COLORS.textMuted }}>{key}:</span>{" "}
                                <span style={{ fontFamily: FONT_BODY }}>{whiteLabelDeckError(String(value))}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 3, background: getReasoningSourceColor(log.source) + "20", color: getReasoningSourceColor(log.source), fontFamily: FONT, fontWeight: 600, whiteSpace: "nowrap" }}>
                          {whiteLabelDeckError(log.sourceLabel || getReasoningSourceLabel(log.source))}
                        </span>
                        {log.tag && (
                          <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 3, background: getTagColor(log.tag) + "20", color: getTagColor(log.tag), fontFamily: FONT, fontWeight: 600, whiteSpace: "nowrap" }}>{log.tag}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Chat area — scrollable independently, reasoning panel stays visible above */}
          <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 14px 16px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0, minHeight: 0 }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "88%", padding: "10px 14px", borderRadius: 12,
                background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface,
                border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`,
                borderBottomRightRadius: msg.role === "user" ? 4 : 12,
                borderBottomLeftRadius: msg.role === "agent" ? 4 : 12,
              }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI ANALYST</div>}
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{(msg.text || "").replace(/\*\*(.*?)\*\*/g, "$1")}</div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}>
                <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI ANALYST</div>
                <ProgressDots active={true} />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          <input value={userInput} onChange={e => setUserInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !isTyping && !isReasoning) sendMessage(); }}
            placeholder={deckGenerated ? "Ask for revisions..." : analysisRun ? "Ask about findings..." : "Ask questions or say 'run the analysis'..."}
            style={{ flex: 1, padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }}
            disabled={isTyping || isReasoning} />
          <button onClick={sendMessage} disabled={isTyping || isReasoning || !userInput.trim()} style={{
            padding: "10px 18px", background: userInput.trim() && !isTyping && !isReasoning ? COLORS.accent : COLORS.border,
            color: userInput.trim() && !isTyping && !isReasoning ? COLORS.bg : COLORS.textDim,
            border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
            cursor: userInput.trim() && !isTyping && !isReasoning ? "pointer" : "default",
          }}>Send</button>
        </div>
      </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column" }}>
        {!deckGenerated ? (
          hasArtifactPreviewSlides && currentArtifactSlide ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 0, flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, margin: 0 }}>Live <span style={{ color: COLORS.accent }}>Preview</span></h3>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                    {previewSlides.length} intermediate slide{previewSlides.length > 1 ? "s" : ""} from builder_agent
                  </div>
                </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      onClick={() => {
                        const nextIndex = Math.max(0, currentArtifactSlideIdx - 1);
                        selectPreviewSlide(previewSlides[nextIndex], nextIndex, { followLatest: false });
                      }}
                      disabled={currentArtifactSlideIdx <= 0}
                    style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: currentArtifactSlideIdx <= 0 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: currentArtifactSlideIdx <= 0 ? "default" : "pointer" }}
                  >
                    ↑ Prev
                    </button>
                    <div style={{ minWidth: 72, textAlign: "center", fontSize: 11, color: COLORS.textDim }}>
                    {(currentArtifactSlide?.slot != null ? currentArtifactSlide.slot + 1 : currentArtifactSlideIdx + 1)} / {previewSlides.length}
                    </div>
                  <button
                    onClick={() => {
                      const nextIndex = Math.min(previewSlides.length - 1, currentArtifactSlideIdx + 1);
                      selectPreviewSlide(previewSlides[nextIndex], nextIndex, { followLatest: false });
                    }}
                    disabled={currentArtifactSlideIdx >= previewSlides.length - 1}
                    style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: currentArtifactSlideIdx >= previewSlides.length - 1 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: currentArtifactSlideIdx >= previewSlides.length - 1 ? "default" : "pointer" }}
                  >
                    Next ↓
                  </button>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: `minmax(0, 1fr)${(deckData?.manus_task_url || activeManusTaskUrl) ? "" : " 132px"}`, gap: 14, minHeight: 0, flex: 1 }}>
                <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, background: COLORS.surface, overflow: "hidden" }}>
                  <div ref={previewViewportRef} style={{ width: "100%", aspectRatio: "16/9", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                    {(deckData?.manus_task_url || activeManusTaskUrl) ? (
                      // LIVE PREVIEW DURING GENERATION ONLY. The builder's
                      // slide.html is a proprietary XML DSL that browsers can't
                      // render, so we embed the builder's own live view while the
                      // deck is being built (activeManusTaskUrl is set from the job
                      // poll once the task starts, ~4-5 min in). Completed decks
                      // never use this — they render from persisted screenshots.
                      <iframe
                        key={`manus-preview-${deckData?.manus_task_url || activeManusTaskUrl}`}
                        title={deckData?.title || "Deck preview"}
                        src={deckData?.manus_task_url || activeManusTaskUrl}
                        style={{ width: "100%", height: "100%", border: "none", background: "#fff" }}
                      />
                    ) : currentArtifactSlide.html ? (
                      <div style={{ position: "absolute", left: "50%", top: "50%", width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, transform: `translate(-50%, -50%) scale(${mainPreviewScale})`, transformOrigin: "center center" }}>
                        <iframe
                          key={`${currentArtifactSlide.slotKey}:${currentArtifactSlide.versionKey}`}
                          title={currentArtifactSlide.name}
                          srcDoc={currentArtifactSlide.html}
                          sandbox={PREVIEW_IFRAME_SANDBOX}
                          scrolling="no"
                          style={{ width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, border: "none", background: "#fff", pointerEvents: "none", overflow: "hidden" }}
                        />
                      </div>
                    ) : (
                      <img src={currentArtifactSlide.url} alt={currentArtifactSlide.name} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#0f172a" }} />
                    )}
                  </div>
                  <div style={{ padding: "10px 12px", borderTop: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{currentArtifactSlide.title || currentArtifactSlide.name}</div>
                    {currentArtifactSlide.summary && (
                      <div style={{ marginTop: 4, fontSize: 10, color: COLORS.textDim, lineHeight: 1.45 }}>{currentArtifactSlide.summary}</div>
                    )}
                    {currentArtifactSlide.url && (
                      <a href={currentArtifactSlide.url} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 4, fontSize: 11, color: COLORS.accent, textDecoration: "none" }}>
                        Open original artifact
                      </a>
                    )}
                  </div>
                </div>

                {!(deckData?.manus_task_url || activeManusTaskUrl) && (
                <div ref={previewThumbnailRailRef} style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto", paddingRight: 2 }}>
                  {previewSlides.map((slide, index) => (
                    <button
                      key={slide.id}
                      onClick={() => selectPreviewSlide(slide, index, { followLatest: false })}
                      style={{
                        border: `1px solid ${index === currentArtifactSlideIdx ? COLORS.accent : COLORS.border}`,
                        borderRadius: 8,
                        background: index === currentArtifactSlideIdx ? COLORS.accent + "10" : COLORS.surface,
                        padding: 6,
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 6, overflow: "hidden", background: COLORS.bg, marginBottom: 6 }}>
                        {slide.html ? (
                          <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
                            <div style={{ position: "absolute", left: "50%", top: "50%", width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, transform: `translate(-50%, -50%) scale(${thumbnailPreviewScale})`, transformOrigin: "center center" }}>
                              <iframe
                                key={`${slide.slotKey}:${slide.versionKey}:thumb`}
                                title={slide.name}
                                srcDoc={slide.html}
                                sandbox={PREVIEW_IFRAME_SANDBOX}
                                scrolling="no"
                                style={{ width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, border: "none", background: "#fff", pointerEvents: "none", overflow: "hidden" }}
                              />
                            </div>
                          </div>
                        ) : (
                          <img src={slide.url} alt={slide.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.text, lineHeight: 1.35 }}>{slide.slot != null ? slide.slot + 1 : index + 1}. {slide.name}</div>
                    </button>
                  ))}
                </div>
                )}
              </div>
            </div>
          ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>📊</div>
              <div style={{ fontFamily: FONT, fontSize: 15, color: COLORS.textDim, marginBottom: 8 }}>Your deck will appear here</div>
              <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>
                {analysisRun
                  ? "Analysis complete — click Generate Deck in the chat panel to create the deck brief and build your PowerPoint."
                  : "Chat with the AI Analyst on the left. Ask questions about the data, then run the full analysis to generate your consulting deck."}
              </div>
              {sourceSummary && (
                <div style={{ marginTop: 16, display: "flex", justifyContent: "center", gap: 12 }}>
                  {sourceSummary.transcripts > 0 && <span style={{ fontSize: 11, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 13 }}>description</span> {sourceSummary.transcripts} transcript{sourceSummary.transcripts > 1 ? "s" : ""}</span>}
                  {sourceSummary.surveys > 0 && <span style={{ fontSize: 11, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 13 }}>bar_chart</span> {sourceSummary.surveys} survey{sourceSummary.surveys > 1 ? "s" : ""}</span>}
                  {sourceSummary.interviews > 0 && <span style={{ fontSize: 11, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 3 }}><span className="material-symbols-outlined" style={{ fontSize: 13 }}>mic</span> {sourceSummary.interviews} interview{sourceSummary.interviews > 1 ? "s" : ""}</span>}
                </div>
              )}
            </div>
          </div>
          )
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, margin: 0 }}>Presentation <span style={{ color: COLORS.accent }}>Deck</span></h3>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                  {isManusDeck
                    ? `Interactive deck · Full-fidelity render${deckGeneratedAt ? ` · Generated ${deckGeneratedAt}` : ""} · .pptx export available for editing (lossy)`
                    : `${SLIDE_LABELS.length} slides${deckGeneratedAt ? ` · Generated ${deckGeneratedAt}` : ""} · Click to preview · Ask for revisions in chat`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {deckData?.id && <button onClick={async () => {
                  try {
                    const { blob, filename } = await api.audit.decks.exportPptx(deckData.id);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                  } catch (err) { console.error("Export failed:", err); }
                }} title={isManusDeck ? "Editable copy — some formatting may differ from the interactive view." : undefined} style={{ padding: "6px 14px", background: "transparent", color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{isManusDeck ? "Download .pptx (lossy)" : "Export PPTX"}</button>}
                {isManusDeck ? <button onClick={() => setShowFullDeck(true)} style={{ padding: "6px 14px", background: COLORS.accent, color: COLORS.bg, border: `1px solid ${COLORS.accent}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Open Fullscreen →</button> : <button onClick={() => setShowFullDeck(true)} style={{ padding: "6px 14px", background: COLORS.accent, color: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Open Editor →</button>}
              </div>
            </div>

            {isManusDeck ? (
              (Array.isArray(deckData?.slide_screenshots_json) && deckData.slide_screenshots_json.length > 0) ? (
                <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, marginBottom: 16 }}>
                  <DeckSlideScreenshotViewer slides={deckData.slide_screenshots_json} />
                </div>
              ) : deckData?.manus_task_url ? (
                // Older decks have no persisted slide images. Never embed the
                // builder's share page — show a clean download-only card.
                <div onClick={() => setShowFullDeck(true)} title="Open the presentation" style={{ cursor: "pointer", marginBottom: 16 }}>
                  <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 8, overflow: "hidden", border: `1px solid ${COLORS.border}`, background: "linear-gradient(135deg, #1e293b, #0f172a)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ textAlign: "center", padding: 24 }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "#fff", marginBottom: 8 }}>Presentation ready</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>Download the .pptx above to view the full deck</div>
                    </div>
                  </div>
                </div>
              ) : savedDeckPreviewSlides.length > 0 && currentArtifactSlide ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 0, flex: 1, marginBottom: 16 }}>
                  {false && (<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                      <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, margin: 0 }}>Saved <span style={{ color: COLORS.accent }}>Preview</span></h3>
                      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                        {savedDeckPreviewSlides.length} captured slide{savedDeckPreviewSlides.length > 1 ? "s" : ""}{deckGeneratedAt ? ` · Generated ${deckGeneratedAt}` : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        onClick={() => {
                          const nextIndex = Math.max(0, currentArtifactSlideIdx - 1);
                          selectPreviewSlide(savedDeckPreviewSlides[nextIndex], nextIndex, { followLatest: false });
                        }}
                        disabled={currentArtifactSlideIdx <= 0}
                        style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: currentArtifactSlideIdx <= 0 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: currentArtifactSlideIdx <= 0 ? "default" : "pointer" }}
                      >
                        ↑ Prev
                      </button>
                      <div style={{ minWidth: 72, textAlign: "center", fontSize: 11, color: COLORS.textDim }}>
                        {(currentArtifactSlide?.slot != null ? currentArtifactSlide.slot + 1 : currentArtifactSlideIdx + 1)} / {savedDeckPreviewSlides.length}
                      </div>
                      <button
                        onClick={() => {
                          const nextIndex = Math.min(savedDeckPreviewSlides.length - 1, currentArtifactSlideIdx + 1);
                          selectPreviewSlide(savedDeckPreviewSlides[nextIndex], nextIndex, { followLatest: false });
                        }}
                        disabled={currentArtifactSlideIdx >= savedDeckPreviewSlides.length - 1}
                        style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: currentArtifactSlideIdx >= savedDeckPreviewSlides.length - 1 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: currentArtifactSlideIdx >= savedDeckPreviewSlides.length - 1 ? "default" : "pointer" }}
                      >
                        Next ↓
                      </button>
                    </div>
                  </div>)}

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 132px", gap: 14, minHeight: 0 }}>
                    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, background: COLORS.surface, overflow: "hidden" }}>
                      <div ref={previewViewportRef} style={{ width: "100%", aspectRatio: "16/9", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                        {currentArtifactSlide.html ? (
                          <div style={{ position: "absolute", left: "50%", top: "50%", width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, transform: `translate(-50%, -50%) scale(${mainPreviewScale})`, transformOrigin: "center center" }}>
                            <iframe
                              key={`${currentArtifactSlide.slotKey}:${currentArtifactSlide.versionKey}:saved`}
                              title={currentArtifactSlide.name}
                              srcDoc={currentArtifactSlide.html}
                              sandbox={PREVIEW_IFRAME_SANDBOX}
                              scrolling="no"
                              style={{ width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, border: "none", background: "#fff", pointerEvents: "none", overflow: "hidden" }}
                            />
                          </div>
                        ) : (
                          <img src={currentArtifactSlide.url} alt={currentArtifactSlide.name} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#0f172a" }} />
                        )}
                      </div>
                      <div style={{ padding: "10px 12px", borderTop: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{currentArtifactSlide.title || currentArtifactSlide.name}</div>
                        {currentArtifactSlide.summary && (
                          <div style={{ marginTop: 4, fontSize: 10, color: COLORS.textDim, lineHeight: 1.45 }}>{currentArtifactSlide.summary}</div>
                        )}
                      </div>
                    </div>

                    <div ref={previewThumbnailRailRef} style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto", paddingRight: 2 }}>
                      {savedDeckPreviewSlides.map((slide, index) => (
                        <button
                          key={slide.id}
                          onClick={() => selectPreviewSlide(slide, index, { followLatest: false })}
                          style={{
                            border: `1px solid ${index === currentArtifactSlideIdx ? COLORS.accent : COLORS.border}`,
                            borderRadius: 8,
                            background: index === currentArtifactSlideIdx ? COLORS.accent + "10" : COLORS.surface,
                            padding: 6,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 6, overflow: "hidden", background: COLORS.bg, marginBottom: 6 }}>
                            {slide.html ? (
                              <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
                                <div style={{ position: "absolute", left: "50%", top: "50%", width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, transform: `translate(-50%, -50%) scale(${thumbnailPreviewScale})`, transformOrigin: "center center" }}>
                                  <iframe
                                    key={`${slide.slotKey}:${slide.versionKey}:saved-thumb`}
                                    title={slide.name}
                                    srcDoc={slide.html}
                                    sandbox={PREVIEW_IFRAME_SANDBOX}
                                    scrolling="no"
                                    style={{ width: PREVIEW_BASE_WIDTH, height: PREVIEW_BASE_HEIGHT, border: "none", background: "#fff", pointerEvents: "none", overflow: "hidden" }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <img src={slide.url} alt={slide.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: COLORS.text, lineHeight: 1.35 }}>{slide.slot != null ? slide.slot + 1 : index + 1}. {slide.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div onClick={() => setShowFullDeck(true)} style={{ cursor: "pointer", marginBottom: 16 }}>
                  <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 8, overflow: "hidden", border: `1px solid ${COLORS.border}`, background: "linear-gradient(135deg, #1e293b, #0f172a)", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "#fff", marginBottom: 8 }}>Deck Ready</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>Click to open fullscreen</div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <>
                <div onClick={() => setShowFullDeck(true)} style={{ cursor: "pointer", marginBottom: 16 }}>
                  <div style={{ width: "100%", aspectRatio: "16/9", borderRadius: 8, overflow: "hidden", border: `1px solid ${COLORS.border}`, background: "linear-gradient(135deg, #1e293b, #0f172a)", position: "relative", display: "flex", alignItems: "center" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: "#eab308" }} />
                    <div style={{ padding: "0 40px" }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Strategic Analysis</div>
                      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 20 }}>Operations Assessment & Strategic Recommendations</div>
                      <div style={{ fontSize: 8, color: "#eab308", fontWeight: 600, marginBottom: 2, letterSpacing: "0.04em" }}>AI-POWERED AUDIT ANALYSIS</div>
                      <div style={{ fontSize: 8, color: "#64748b" }}>{new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                  {SLIDE_LABELS.map((label, i) => (
                    <div key={i} onClick={() => setShowFullDeck(true)} style={{
                      border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 6, cursor: "pointer",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "66"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
                    >
                      <div style={{ aspectRatio: "16/9", background: i === 0 ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#fff", borderRadius: 4, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4, overflow: "hidden" }}>
                        <span style={{ fontSize: 7, color: i === 0 ? "#94a3b8" : "#334155", fontWeight: 600, textAlign: "center", padding: 4 }}>{label}</span>
                      </div>
                      <div style={{ fontSize: 9, color: COLORS.textDim, textAlign: "center", fontFamily: FONT }}>{i + 1}. {label}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showDeckGenerateConfirm && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.62)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setShowDeckGenerateConfirm(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 460, maxWidth: "92vw", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.45)", overflow: "hidden" }}
          >
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div>
                  <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: COLORS.text }}>Start Deck Generation?</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Long-running process with external AI build costs</div>
                </div>
              </div>
            </div>

            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ padding: "12px 14px", background: COLORS.warn + "12", border: `1px solid ${COLORS.warn}44`, borderRadius: 10, fontSize: 12, color: COLORS.text, lineHeight: 1.55 }}>
                Deck generation usually takes <span style={{ color: COLORS.warn, fontWeight: 700 }}>10–30 minutes</span> and uses external model credits.
                You can leave this window and come back later. The app will keep the run going and you can check progress from the same screen.
              </div>

              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6 }}>
                Before continuing, make sure the project has all the transcripts, surveys, interviews, and analysis context you want included.
              </div>

              <div style={{ padding: "12px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontFamily: FONT, fontWeight: 700, marginBottom: 8 }}>READY CHECK</div>
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6 }}>
                  Uses the admin-selected default deck prompt.
                  {sourceSummary && (
                    <>
                      <br />
                      Sources: {sourceSummary.transcripts || 0} transcript{(sourceSummary.transcripts || 0) === 1 ? "" : "s"}, {sourceSummary.surveys || 0} survey{(sourceSummary.surveys || 0) === 1 ? "" : "s"}, {sourceSummary.interviews || 0} interview set{(sourceSummary.interviews || 0) === 1 ? "" : "s"}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: "16px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setShowDeckGenerateConfirm(false)}
                style={{ padding: "10px 18px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={startDeckGeneration}
                style={{ padding: "10px 18px", background: COLORS.accent, border: "none", borderRadius: 8, color: COLORS.bg, fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MOCK_SLIDE_DATA = {
  0: { title: "Hodge Insurance\nAgency", subtitle: "Operations Assessment &\nStrategic Recommendations", preparedFor: "PREPARED FOR PERRY SALVAGNE IV, PRESIDENT/CEO", date: "October 2024" },
  1: { title: "AGENDA", items: ["Current State Assessment", "Key Findings & Pain Points", "Survey Results Analysis", "Strategic Recommendations", "Implementation Roadmap", "Investment Case & ROI", "Supported Impact Analysis", "Next Steps & Timeline"] },
  2: { title: "CURRENT STATE ASSESSMENT", summary: "Hodge has strong fundamentals constrained by manual processes — targeted automation can unlock 200-300+ hours per month and enable £75-100K+ annual growth.", bullets: [
    "Manual operations across 150 properties creating £100K+ annual revenue risk through missed lease renewals and delayed reconciliation",
    "Key-person dependency: estate manager is single point of failure with no documentation or knowledge transfer system in place",
    "Finance team spending 3 days per month on board reporting due to data scattered across 6+ disconnected systems",
    "Growth target of 300 properties by 2028 fundamentally blocked by current operational infrastructure",
  ], stats: [{ label: "200-300+ Hours/Mo", sublabel: "Manual work" }, { label: "67% Staff Readiness", sublabel: "Gap identified" }, { label: "£100K+ Risk/Year", sublabel: "Revenue leakage" }]},
  3: { title: "KEY FINDING: MANUAL LEASE TRACKING CREATES £100K+ REVENUE RISK", body: "Hastingwood Securities lacks centralized lease renewal tracking, resulting in missed opportunities and revenue leakage across 150 properties.", stats: [{ label: "2023-2024 TRACKING", value: "MANUAL" }, { label: "MISSED RENEWALS", value: "£124K" }, { label: "PROPERTIES AT RISK", value: "12 / 150" }] },
  4: { title: "SURVEY RESULTS: AI READINESS & TECHNOLOGY ASSESSMENT", headline: "Five interviews across all organisational levels provided comprehensive understanding of pain points, priorities, and readiness.", topStat: "274", topStatLabel: "combined data points analysed across interviews and surveys", findings: [
    { label: "AI Readiness", value: "67% rated adoption 2/5 or below" },
    { label: "Tech Satisfaction", value: "75% dissatisfied with current stack" },
    { label: "Top Barrier", value: "Budget concerns (58% of respondents)" },
    { label: "Biggest Need", value: "Integrated systems replacing data silos" },
  ]},
  5: { title: "INVESTMENT DECISION MATRIX", quickWins: [{ name: "Automated Lease Tracking", impact: "£100K+ revenue protection" }, { name: "Director Dashboard", impact: "20hr/month time saved" }], bigSwings: [{ name: "Knowledge Management System", impact: "De-risk key-person dependency" }, { name: "AI Contract Analysis", impact: "Unlock operational efficiency" }] },
  6: { title: "IMPLEMENTATION ROADMAP", phases: [
    { name: "Phase 1: Foundation", timeline: "Month 1-2", cost: "£35-45K", items: ["Lease tracking automation", "Data consolidation", "Core integrations"] },
    { name: "Phase 2: Unlock", timeline: "Month 3-4", cost: "£40-55K", items: ["Director dashboard", "Knowledge base", "Process automation"] },
    { name: "Phase 3: Scale", timeline: "Month 5-6", cost: "£50-70K", items: ["AI contract analysis", "Predictive insights", "Portfolio scaling tools"] },
  ]},
  7: { title: "INVESTMENT CASE", totalInvestment: "£125-170K", timeline: "6 months", annualValue: "£280-350K", roi: "2.1x ROI in Year 1" },
  8: { title: "PROJECTED VALUE & IMPACT", year1: "£280-350K", year2: "£400-500K", year3: "£600-750K", description: "Compound value increases as AI systems learn, processes optimise, and the portfolio scales toward 300 properties." },
  9: { title: "NEXT STEPS", steps: ["Schedule technical deep-dive with IT team (Week 1)", "Finalise Phase 1 scope and vendor selection (Week 2)", "Begin lease tracking implementation (Week 3-4)", "Monthly progress reviews with leadership team"] },
};

function parseDeckSlideData(slideDataRaw) {
  const arr = Array.isArray(slideDataRaw)
    ? (typeof slideDataRaw === 'string' ? JSON.parse(slideDataRaw || '[]') : slideDataRaw)
    : [];
  if (!arr.length) return { slideData: MOCK_SLIDE_DATA, templateSlugs: {}, labels: ["Title", "Agenda", "Current State", "Key Finding", "Survey Results", "Opportunity Matrix", "Roadmap", "Investment Case", "Value Projection", "Next Steps"] };
  const slideData = {};
  const templateSlugs = {};
  const labels = [];
  arr.forEach((s, i) => {
    const idx = s.index ?? i;
    slideData[idx] = { ...(s.fields || {}) };
    templateSlugs[idx] = s.template_slug || `slide-${idx}`;
    labels.push(s.label || s.template_slug || `Slide ${idx + 1}`);
  });
  return { slideData, templateSlugs, labels };
}

// Per-slide viewer fed by slide_screenshots_json — Manus's own rendered
// screenshots persisted to Cloudinary at deck completion. Plain <img> tags:
// full fidelity, no third-party scripts, immune to tracking prevention,
// and survives Manus task retention-deletion (unlike the share iframe).
function DeckSlideScreenshotViewer({ slides }) {
  const [idx, setIdx] = useState(0);
  const current = slides[Math.min(idx, slides.length - 1)];
  if (!current) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 132px", gap: 14, minHeight: 0, flex: 1 }}>
      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, background: COLORS.surface, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ width: "100%", aspectRatio: "16/9", background: "#0b1220", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
          <img src={current.image_url} alt={current.title || `Slide ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{current.title || `Slide ${idx + 1}`}</div>
            {current.summary && (
              <div style={{ marginTop: 2, fontSize: 10, color: COLORS.textDim, lineHeight: 1.4, maxHeight: 28, overflow: "hidden" }}>{current.summary}</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx <= 0} style={{ padding: "5px 11px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: idx <= 0 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: idx <= 0 ? "default" : "pointer" }}>↑ Prev</button>
            <span style={{ fontSize: 11, color: COLORS.textDim, minWidth: 44, textAlign: "center" }}>{idx + 1} / {slides.length}</span>
            <button onClick={() => setIdx(Math.min(slides.length - 1, idx + 1))} disabled={idx >= slides.length - 1} style={{ padding: "5px 11px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: idx >= slides.length - 1 ? COLORS.textDim : COLORS.text, fontFamily: FONT, fontSize: 11, cursor: idx >= slides.length - 1 ? "default" : "pointer" }}>Next ↓</button>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto", paddingRight: 2 }}>
        {slides.map((s, i) => (
          <button key={s.id || i} onClick={() => setIdx(i)} style={{ border: `1px solid ${i === idx ? COLORS.accent : COLORS.border}`, borderRadius: 8, background: i === idx ? COLORS.accent + "10" : COLORS.surface, padding: 6, cursor: "pointer", textAlign: "left" }}>
            <img src={s.image_url} alt="" loading="lazy" style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: 4, display: "block", background: "#0b1220" }} />
            <div style={{ marginTop: 4, fontSize: 9, color: COLORS.textDim, lineHeight: 1.3 }}>{i + 1}. {(s.title || "").slice(0, 42)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AuditFullDeckViewer({ project, deckData: deckDataProp, onClose }) {
  const deckId = deckDataProp?.id || null;
  const isManusDeck = !!deckDataProp?.pptx_url;
  const parsed = parseDeckSlideData(deckDataProp?.slide_data);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [viewerStyleProfiles, setViewerStyleProfiles] = useState([]);
  const [viewerSelectedStyle, setViewerSelectedStyle] = useState(deckDataProp?.style_profile_id || null);
  const [showViewerStyleMenu, setShowViewerStyleMenu] = useState(false);
  const [slideData, setSlideData] = useState(parsed.slideData);
  const [templateSlugs] = useState(parsed.templateSlugs);

  if (isManusDeck) {
    // Primary: persisted per-slide screenshots (Manus's own render, stored on
    // Cloudinary — no third-party scripts, works in every browser). Fallback:
    // Manus share iframe. The .pptx export is a lossy shape-flattened
    // conversion; we offer it as a download only.
    const manusTaskUrl = deckDataProp?.manus_task_url || null;
    const screenshots = Array.isArray(deckDataProp?.slide_screenshots_json) ? deckDataProp.slide_screenshots_json : [];
    return (
      <div style={{ position: "fixed", inset: 0, background: COLORS.bg, zIndex: 9999, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{ margin: 0, fontFamily: FONT, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{deckDataProp?.title || "Presentation Deck"}</h3>
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>Full-fidelity slide render</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {deckId && (
              <button onClick={async () => {
                try {
                  const { blob, filename } = await api.audit.decks.exportPptx(deckId);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                } catch (err) { console.error("Export failed:", err); }
              }} title="Editable copy — some formatting may differ from the interactive view above." style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                Download .pptx (lossy)
              </button>
            )}
            <button onClick={onClose} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: FONT, fontSize: 11, cursor: "pointer", color: COLORS.text }}>Close</button>
          </div>
        </div>
        {screenshots.length > 0 ? (
          <div style={{ flex: 1, minHeight: 0, padding: 16, display: "flex", flexDirection: "column", background: COLORS.bg }}>
            <DeckSlideScreenshotViewer slides={screenshots} />
          </div>
        ) : (
          // No persisted slide images (older decks). Never embed the builder's
          // share page — show a clean download-only state instead.
          <div style={{ flex: 1, background: "#000", position: "relative", minHeight: 0 }}>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, gap: 16, color: "#fff", textAlign: "center" }}>
              <div style={{ fontSize: 48, opacity: 0.6 }}>📊</div>
              <div style={{ fontFamily: FONT, fontSize: 15, maxWidth: 480 }}>An interactive preview isn't available for this presentation. Download the .pptx to view it in PowerPoint or Google Slides.</div>
              {deckId && (
                <button onClick={async () => {
                  try {
                    const { blob, filename } = await api.audit.decks.exportPptx(deckId);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                  } catch (err) { console.error("Export failed:", err); }
                }} style={{ padding: "9px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Download .pptx
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    api.audit.deckStyles?.list?.().then(profiles => {
      if (Array.isArray(profiles)) setViewerStyleProfiles(profiles);
    }).catch(() => {});
  }, []);

  // Sync slideData when deckDataProp changes (e.g. after initial fetch)
  useEffect(() => {
    const p = parseDeckSlideData(deckDataProp?.slide_data);
    if (Object.keys(p.slideData).length > 0) setSlideData(p.slideData);
  }, [deckDataProp?.slide_data]);

  const slideIndices = Object.keys(slideData).map(Number).sort((a, b) => a - b);
  const SLIDE_LABELS = parsed.labels.length ? parsed.labels : slideIndices.map(i => slideData[i]?.title || slideData[i]?.label || `Slide ${i + 1}`);

  // Auto-save deck slides — preserve template_slug from each slide
  const deckAutoSave = useAutoSave(async (allSlideData) => {
    if (!deckId) return;
    const indices = Object.keys(allSlideData).map(Number).sort((a, b) => a - b);
    const slideArray = indices.map(idx => ({
      template_slug: templateSlugs[idx] || `slide-${idx}`,
      label: allSlideData[idx]?.title || allSlideData[idx]?.label || templateSlugs[idx] || `Slide ${idx + 1}`,
      index: idx,
      fields: allSlideData[idx] || {},
    }));
    await api.audit.decks.update(deckId, { slide_data: slideArray });
  }, 1200);

  const updateSlideField = (si, field, value) => {
    setSlideData(prev => {
      const next = { ...prev, [si]: { ...prev[si], [field]: value } };
      deckAutoSave.trigger(next);
      return next;
    });
  };

  const EditableText = ({ value, onChange, style, multiline }) => {
    const displayVal = (value != null && typeof value === 'object')
      ? (value.title ?? value.timeline ?? value.name ?? value.milestone ?? value.detail ?? '')
      : (value ?? '');
    const [editing, setEditing] = useState(false);
    const [tempVal, setTempVal] = useState(displayVal);
    if (editing) {
      const El = multiline ? "textarea" : "input";
      return React.createElement(El, {
        value: tempVal, onChange: e => setTempVal(e.target.value),
        onBlur: () => { onChange(tempVal); setEditing(false); },
        onKeyDown: !multiline ? e => { if (e.key === "Enter") { onChange(tempVal); setEditing(false); } } : undefined,
        autoFocus: true,
        style: { ...style, background: "rgba(234,179,8,0.08)", border: "2px solid #eab308", borderRadius: 4, outline: "none", resize: multiline ? "none" : undefined, padding: "4px 8px", width: "100%", boxSizing: "border-box" },
      });
    }
    return (
      <div onClick={() => { setEditing(true); setTempVal(displayVal); }} style={{ ...style, cursor: "text", borderRadius: 4, transition: "background 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(234,179,8,0.05)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >{displayVal}</div>
    );
  };

  const headingStyle = { fontSize: 22, fontWeight: 700, color: "#0f172a", borderBottom: "4px solid #eab308", paddingBottom: 12, marginBottom: 20 };

  const slugToCase = (slug, idx) => {
    if (!slug && idx !== undefined) {
      if (idx === 0) return 0; if (idx === 1) return 1; if (idx === 2) return 2; if (idx === 3) return 3; if (idx === 4) return 4;
      if (idx === 5) return 5; if (idx === 6) return 6; if (idx === 7) return 7; if (idx === 8) return 8; if (idx === 9) return 9;
    }
    if (!slug) return 'generic';
    const s = slug.toLowerCase();
    if (s.startsWith('slide_science_') || s.startsWith('slide-')) return 'generic';
    if ((s === 'title_dark' || s === 'title_light' || s === 'title') && idx === 0) return 0;
    if (s.includes('agenda')) return 1;
    if (s.includes('current_state') || s.includes('currentstate')) return 2;
    if (s.includes('key_finding') || s.includes('keyfinding')) return 3;
    if (s.includes('survey')) return 4;
    if (s.includes('opportunity') || s.includes('matrix')) return 5;
    if (s.includes('roadmap')) return 6;
    if (s.includes('investment')) return 7;
    if (s.includes('value') || s.includes('projection')) return 8;
    if (s.includes('next_steps') || s.includes('nextsteps') || s.includes('thank_you')) return 9;
    return 'generic';
  };

  const renderSlide = (idx) => {
    const d = slideData[idx];
    if (!d) return null;
    const slug = templateSlugs[idx] || '';
    const caseKey = slugToCase(slug, idx);
    if (caseKey === 'generic') {
      return (
        <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60, overflow: "auto" }}>
          <div style={headingStyle}><EditableText value={d.title ?? ''} onChange={v => updateSlideField(idx, "title", v)} style={{ display: "inline", color: (d.title == null || d.title === '') ? '#94a3b8' : undefined, fontStyle: (d.title == null || d.title === '') ? 'italic' : undefined }} /></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {Object.entries(d).filter(([k]) => k !== 'title' && k !== 'label').map(([key, val]) => {
              if (val == null || val === '') return null;
              if (Array.isArray(val)) {
                return (
                  <div key={key} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{key}</div>
                    {val.map((item, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
                        {typeof item === 'object' && item !== null ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {Object.entries(item).filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                              <div key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontSize: 10, color: "#64748b" }}>{k}:</span>
                                <EditableText value={String(v)} onChange={nv => { const u = [...val]; u[i] = { ...item, [k]: nv }; updateSlideField(idx, key, u); }} style={{ fontSize: 12, color: "#334155" }} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <EditableText value={item} onChange={v => { const u = [...val]; u[i] = v; updateSlideField(idx, key, u); }} multiline style={{ fontSize: 12, color: "#334155", flex: 1 }} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              }
              if (typeof val === 'object') return null;
              return (
                <div key={key}>
                  <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{key}</div>
                  <EditableText value={String(val)} onChange={v => updateSlideField(idx, key, v)} multiline style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }} />
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    switch (caseKey) {
      case 0:
        return (
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #1e293b, #0f172a)", display: "flex", alignItems: "center", position: "relative" }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: "#eab308" }} />
            <div style={{ padding: "0 60px" }}>
              <EditableText value={d.title} onChange={v => updateSlideField(0, "title", v)} multiline style={{ fontSize: 40, fontWeight: 700, color: "#fff", marginBottom: 16, whiteSpace: "pre-line", lineHeight: 1.2 }} />
              <EditableText value={d.subtitle} onChange={v => updateSlideField(0, "subtitle", v)} multiline style={{ fontSize: 16, color: "#94a3b8", marginBottom: 36, whiteSpace: "pre-line" }} />
              <EditableText value={d.preparedFor} onChange={v => updateSlideField(0, "preparedFor", v)} style={{ fontSize: 11, color: "#eab308", fontWeight: 600, marginBottom: 4, letterSpacing: "0.04em" }} />
              <EditableText value={d.date} onChange={v => updateSlideField(0, "date", v)} style={{ fontSize: 11, color: "#64748b" }} />
            </div>
          </div>
        );
      case 1:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(1, "title", v)} style={headingStyle} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 40px" }}>
              {(d.items || []).map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#eab308", flexShrink: 0 }} />
                  <EditableText value={item} onChange={v => { const u = [...(d.items || [])]; u[i] = v; updateSlideField(1, "items", u); }} style={{ fontSize: 14, color: "#334155" }} />
                </div>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: "40px 50px", display: "flex", flexDirection: "column" }}>
            <EditableText value={d.title} onChange={v => updateSlideField(2, "title", v)} style={{ ...headingStyle, fontSize: 18 }} />
            <EditableText value={d.summary} onChange={v => updateSlideField(2, "summary", v)} multiline style={{ fontSize: 11, color: "#334155", lineHeight: 1.6, marginBottom: 14, fontWeight: 600, fontStyle: "italic" }} />
            <div style={{ flex: 1 }}>
              {(d.bullets || []).map((b, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#eab308", flexShrink: 0, marginTop: 6 }} />
                  <EditableText value={b} onChange={v => { const u = [...(d.bullets || [])]; u[i] = v; updateSlideField(2, "bullets", u); }} multiline style={{ fontSize: 11, color: "#475569", lineHeight: 1.5 }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              {(d.stats || []).map((s, i) => (
                <div key={i} style={{ flex: 1, padding: 12, background: "#f8fafc", borderRadius: 6, borderLeft: "3px solid #eab308" }}>
                  <EditableText value={s.label} onChange={v => { const u = [...(d.stats || [])]; u[i] = { ...s, label: v }; updateSlideField(2, "stats", u); }} style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }} />
                  <div style={{ fontSize: 10, color: "#64748b" }}>{s.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(3, "title", v)} style={{ ...headingStyle, fontSize: 18 }} />
            <EditableText value={d.body} onChange={v => updateSlideField(3, "body", v)} multiline style={{ fontSize: 13, color: "#475569", lineHeight: 1.7, marginBottom: 28 }} />
            <div style={{ display: "flex", gap: 16 }}>
              {(d.stats || []).map((s, i) => (
                <div key={i} style={{ flex: 1, padding: 16, background: "#f1f5f9", borderRadius: 8 }}>
                  <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>{s.label}</div>
                  <EditableText value={s.value} onChange={v => { const u = [...(d.stats || [])]; u[i] = { ...s, value: v }; updateSlideField(3, "stats", u); }} style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }} />
                </div>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: "40px 50px" }}>
            <EditableText value={d.title} onChange={v => updateSlideField(4, "title", v)} style={{ ...headingStyle, fontSize: 16 }} />
            <EditableText value={d.headline} onChange={v => updateSlideField(4, "headline", v)} multiline style={{ fontSize: 11, color: "#475569", lineHeight: 1.5, marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 20 }}>
              <div style={{ padding: 20, background: "#f0fdf4", borderRadius: 8, textAlign: "center", width: 100 }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: "#166534" }}>{d.topStat}</div>
                <div style={{ fontSize: 9, color: "#166534", lineHeight: 1.3, marginTop: 4 }}>{d.topStatLabel}</div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                {(d.findings || []).map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, minWidth: 100 }}>{f.label}</div>
                    <EditableText value={f.value} onChange={v => { const u = [...(d.findings || [])]; u[i] = { ...f, value: v }; updateSlideField(4, "findings", u); }} style={{ fontSize: 12, color: "#0f172a", fontWeight: 500 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(5, "title", v)} style={headingStyle} />
            <div style={{ display: "flex", gap: 20, flex: 1 }}>
              <div style={{ flex: 1, border: "2px solid #22c55e", borderRadius: 8, padding: 20, background: "#f0fdf4" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#166534", marginBottom: 14 }}>QUICK WINS</div>
                {(d.quickWins || []).map((w, i) => (
                  <div key={i} style={{ background: "#fff", borderRadius: 6, padding: 10, marginBottom: 6 }}>
                    <EditableText value={w.name} onChange={v => { const u = [...(d.quickWins || [])]; u[i] = { ...w, name: v }; updateSlideField(5, "quickWins", u); }} style={{ fontWeight: 600, fontSize: 12 }} />
                    <EditableText value={w.impact} onChange={v => { const u = [...(d.quickWins || [])]; u[i] = { ...w, impact: v }; updateSlideField(5, "quickWins", u); }} style={{ fontSize: 10, color: "#6b7280" }} />
                  </div>
                ))}
              </div>
              <div style={{ flex: 1, border: "2px solid #3b82f6", borderRadius: 8, padding: 20, background: "#eff6ff" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e40af", marginBottom: 14 }}>BIG SWINGS</div>
                {(d.bigSwings || []).map((w, i) => (
                  <div key={i} style={{ background: "#fff", borderRadius: 6, padding: 10, marginBottom: 6 }}>
                    <EditableText value={w.name} onChange={v => { const u = [...(d.bigSwings || [])]; u[i] = { ...w, name: v }; updateSlideField(5, "bigSwings", u); }} style={{ fontWeight: 600, fontSize: 12 }} />
                    <EditableText value={w.impact} onChange={v => { const u = [...(d.bigSwings || [])]; u[i] = { ...w, impact: v }; updateSlideField(5, "bigSwings", u); }} style={{ fontSize: 10, color: "#6b7280" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: "40px 50px" }}>
            <EditableText value={d.title} onChange={v => updateSlideField(6, "title", v)} style={{ ...headingStyle, fontSize: 18 }} />
            <div style={{ display: "flex", gap: 16 }}>
              {(d.phases || []).map((p, i) => (
                <div key={i} style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px", background: ["#eab308", "#3b82f6", "#8b5cf6"][i], color: "#fff" }}>
                    <EditableText value={p.name} onChange={v => { const u = [...(d.phases || [])]; u[i] = { ...p, name: v }; updateSlideField(6, "phases", u); }} style={{ fontWeight: 700, fontSize: 12, color: "#fff" }} />
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{p.timeline} · {p.cost}</div>
                  </div>
                  <div style={{ padding: 12 }}>
                    {(p.items || []).map((item, j) => {
                      const itemStr = typeof item === 'string' ? item : (item && typeof item === 'object' ? [item.title, item.timeline, item.name, item.milestone].filter(Boolean).join(' · ') || '' : String(item ?? ''));
                      return (
                      <div key={j} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                        <span style={{ color: "#eab308", fontSize: 11 }}>→</span>
                        <EditableText value={itemStr} onChange={v => { const u = [...(d.phases || [])]; const items = [...(p.items || [])]; items[j] = v; u[i] = { ...p, items }; updateSlideField(6, "phases", u); }} style={{ fontSize: 11, color: "#475569" }} />
                      </div>
                    );})}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 7:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(7, "title", v)} style={headingStyle} />
            <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
              {[
                { label: "Total Investment", value: d.totalInvestment, field: "totalInvestment", color: "#0f172a" },
                { label: "Timeline", value: d.timeline, field: "timeline", color: "#3b82f6" },
                { label: "Annual Value", value: d.annualValue, field: "annualValue", color: "#22c55e" },
                { label: "Return", value: d.roi, field: "roi", color: "#eab308" },
              ].map((item, i) => (
                <div key={i} style={{ flex: 1, padding: 20, background: "#f8fafc", borderRadius: 8, textAlign: "center", borderTop: `4px solid ${item.color}` }}>
                  <div style={{ fontSize: 10, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{item.label}</div>
                  <EditableText value={item.value} onChange={v => updateSlideField(7, item.field, v)} style={{ fontSize: 22, fontWeight: 700, color: item.color }} />
                </div>
              ))}
            </div>
          </div>
        );
      case 8:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(8, "title", v)} style={headingStyle} />
            <div style={{ display: "flex", gap: 20, marginTop: 20, marginBottom: 20, alignItems: "flex-end" }}>
              {[{ label: "Year 1", value: d.year1, field: "year1", color: "#22c55e", h: 120 }, { label: "Year 2", value: d.year2, field: "year2", color: "#3b82f6", h: 180 }, { label: "Year 3", value: d.year3, field: "year3", color: "#8b5cf6", h: 240 }].map((y, i) => (
                <div key={i} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ height: y.h, background: `${y.color}15`, border: `2px solid ${y.color}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                    <EditableText value={y.value} onChange={v => updateSlideField(8, y.field, v)} style={{ fontSize: 24, fontWeight: 700, color: y.color }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{y.label}</div>
                </div>
              ))}
            </div>
            <EditableText value={d.description} onChange={v => updateSlideField(8, "description", v)} multiline style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, textAlign: "center" }} />
          </div>
        );
      case 9:
        return (
          <div style={{ width: "100%", height: "100%", background: "#fff", padding: 60 }}>
            <EditableText value={d.title} onChange={v => updateSlideField(9, "title", v)} style={headingStyle} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
              {(d.steps || []).map((step, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, background: "#f8fafc", borderRadius: 8, borderLeft: "4px solid #eab308" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eab308", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#fff", flexShrink: 0 }}>{i + 1}</div>
                  <EditableText value={step} onChange={v => { const u = [...(d.steps || [])]; u[i] = v; updateSlideField(9, "steps", u); }} style={{ fontSize: 14, color: "#334155" }} />
                </div>
              ))}
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: COLORS.bg, zIndex: 50, display: "flex" }}>
      {/* Left — Slide Panel */}
      <div style={{ width: 160, borderRight: `1px solid ${COLORS.border}`, overflow: "auto", padding: "12px 8px", flexShrink: 0 }}>
        {slideIndices.map((idx, i) => {
          const slug = templateSlugs[idx] || '';
          const isTitleSlide = idx === 0 || (slug && !slug.startsWith('slide_science_') && (slug.includes('title_dark') || slug.includes('title_light') || slug === 'title'));
          const label = SLIDE_LABELS[i] ?? `Slide ${idx + 1}`;
          return (
            <div key={idx} onClick={() => setCurrentSlide(idx)} style={{
              padding: 6, marginBottom: 8, borderRadius: 6, cursor: "pointer",
              border: currentSlide === idx ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              background: currentSlide === idx ? COLORS.accentBg : "transparent",
            }}>
              <div style={{ aspectRatio: "16/9", background: isTitleSlide ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#fff", borderRadius: 4, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 7, color: isTitleSlide ? "#94a3b8" : "#334155", fontWeight: 600, textAlign: "center", padding: 4 }}>{label}</span>
              </div>
              <div style={{ fontSize: 9, color: currentSlide === idx ? COLORS.accent : COLORS.textDim, textAlign: "center", fontFamily: FONT }}>{i + 1}. {label}</div>
            </div>
          );
        })}
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ height: 50, background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: COLORS.textMuted, fontSize: 18, cursor: "pointer" }}>×</button>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{deckDataProp?.title || 'Assessment Deck'}</span>
            <span style={{ fontSize: 10, color: COLORS.textDim }}>Click any text to edit</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AutoSaveIndicator status={deckAutoSave.saveStatus} />
            <span style={{ fontSize: 11, color: COLORS.textDim }}>{slideIndices.indexOf(currentSlide) + 1 || 1} / {slideIndices.length}</span>
            {viewerStyleProfiles.length > 0 && (
              <div style={{ position: "relative" }}>
                <button onClick={() => setShowViewerStyleMenu(!showViewerStyleMenu)} style={{ padding: "6px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: viewerStyleProfiles.find(p => p.id === viewerSelectedStyle)?.color_scheme?.accent || COLORS.accent, display: "inline-block" }}></span>
                  {viewerStyleProfiles.find(p => p.id === viewerSelectedStyle)?.name || "Style"}
                  <span style={{ fontSize: 7 }}>▼</span>
                </button>
                {showViewerStyleMenu && (
                  <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.3)", zIndex: 100, width: 240, maxHeight: 300, overflow: "auto" }}>
                    {viewerStyleProfiles.map(sp => (
                      <div key={sp.id} onClick={async () => {
                        setViewerSelectedStyle(sp.id);
                        setShowViewerStyleMenu(false);
                        if (deckId) {
                          try { await api.audit.decks.update(deckId, { style_profile_id: sp.id }); } catch (_) {}
                        }
                      }} style={{
                        padding: "8px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                        background: sp.id === viewerSelectedStyle ? COLORS.accent + "15" : "transparent",
                        borderBottom: `1px solid ${COLORS.border}`,
                      }}>
                        <div style={{ display: "flex", gap: 2 }}>
                          {[sp.color_scheme?.dark, sp.color_scheme?.accent].filter(Boolean).map((c, i) => (
                            <span key={i} style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }}></span>
                          ))}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.text }}>{sp.name}</div>
                        {sp.id === viewerSelectedStyle && <span style={{ marginLeft: "auto", color: COLORS.accent, fontSize: 10 }}>✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowExportMenu(!showExportMenu)} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {exporting ? `Exporting ${exporting}…` : "Export ▾"}
              </button>
              {showExportMenu && (
                <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 100, minWidth: 160, overflow: "hidden" }}>
                  {[
                    { label: "Download PPTX", key: "pptx" },
                    { label: "Download HTML", key: "html" },
                    { label: "Print / Save PDF", key: "pdf" },
                  ].map(opt => (
                    <button key={opt.key} disabled={!deckId || !!exporting} onClick={async () => {
                      setShowExportMenu(false);
                      setExporting(opt.key.toUpperCase());
                      try {
                        if (opt.key === "pptx") {
                          const { blob, filename } = await api.audit.decks.exportPptx(deckId);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                        } else if (opt.key === "html") {
                          const a = document.createElement("a"); a.href = `/api/audit/decks/${deckId}/export/html?download=true`; a.download = "deck.html"; document.body.appendChild(a); a.click(); a.remove();
                        } else if (opt.key === "pdf") {
                          window.open(`/api/audit/decks/${deckId}/export/pdf`, "_blank");
                        }
                      } catch (err) { console.error("Export failed:", err); }
                      setExporting(null);
                    }} style={{ display: "block", width: "100%", padding: "10px 14px", background: "transparent", border: "none", color: COLORS.text, fontFamily: FONT, fontSize: 12, cursor: deckId && !exporting ? "pointer" : "not-allowed", opacity: deckId && !exporting ? 1 : 0.4, textAlign: "left" }}
                    onMouseEnter={e => { if (deckId) e.currentTarget.style.background = COLORS.accentBg; }}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ width: "100%", maxWidth: 900, aspectRatio: "16/9", background: "#fff", borderRadius: 6, boxShadow: "0 8px 40px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            {renderSlide(currentSlide)}
          </div>
        </div>
        <div style={{ height: 50, background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexShrink: 0 }}>
          <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} style={{ padding: "6px 14px", background: "transparent", border: "none", color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer", opacity: currentSlide === 0 ? 0.3 : 1 }}>← Previous</button>
          <span style={{ fontSize: 11, color: COLORS.textDim }}>{SLIDE_LABELS[currentSlide]}</span>
          <button onClick={() => setCurrentSlide(Math.min(SLIDE_LABELS.length - 1, currentSlide + 1))} disabled={currentSlide === SLIDE_LABELS.length - 1} style={{ padding: "6px 14px", background: "transparent", border: "none", color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer", opacity: currentSlide === SLIDE_LABELS.length - 1 ? 0.3 : 1 }}>Next →</button>
        </div>
      </div>
    </div>
  );
}
function MessagingWorkshopView() {
  const [view, setView] = useState("list");
  const [listTab, setListTab] = useState("emails"); // "emails", "linkedin", "subjects"
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [workshopStep, setWorkshopStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [messagingSuite, setMessagingSuite] = useState(null);
  const [revisingItem, setRevisingItem] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [playbookName, setPlaybookName] = useState("");
  const [saveSelections, setSaveSelections] = useState({});
  const [typingMessageIdx, setTypingMessageIdx] = useState(0);
  const [copiedKey, setCopiedKey] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  const [editingMessagingItem, setEditingMessagingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [MOCK_PLAYBOOKS, setPlaybooks] = useState([]);
  const chatEndRef = React.useRef(null);

  React.useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [chatMessages, isTyping]);

  const refreshPlaybooks = () => {
    api.messagingCopies.list().then(data => {
      const copies = Array.isArray(data) ? data : data.messaging_copies || [];
      const playbooks = copies
        .filter(c => c.suite_json && (c.suite_json.emails?.length || c.suite_json.linkedin?.length || c.suite_json.subjects?.length))
        .map(c => ({
          id: c.id, name: c.name || c.title || 'Untitled',
          audience: c.audience || '', created: c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
          emails: c.suite_json?.emails ?? c.emails ?? c.content?.emails ?? [],
          linkedin: c.suite_json?.linkedin ?? c.linkedin ?? c.content?.linkedin ?? [],
          subjects: c.suite_json?.subjects ?? c.subjects ?? c.content?.subjects ?? [],
        }));
      setPlaybooks(playbooks);
    }).catch(() => {});
  };

  useEffect(() => { refreshPlaybooks(); }, []);

  const AGENT_QUESTIONS = [
    { message: "Hey! Let's build your outbound messaging. First — who are you targeting with this campaign? You can describe them or pick from one of your existing ICP lists.", field: "audience" },
    { message: "Got it. Now, what's the core offer or outcome you want to lead with? What would make them stop scrolling and actually read?", field: "offer" },
    { message: "Nice. What's the #1 pain point you're solving for them? The thing that keeps them up at night.", field: "pain" },
    { message: "What proof do you have that you can deliver? Think case studies, numbers, client names, testimonials — anything that builds instant credibility.", field: "proof" },
    { message: "What's the CTA? What do you want them to actually do — book a call, reply, click a link, something else?", field: "cta" },
    { message: "Last one — what tone fits this audience best? Formal and corporate? Casual and direct? Provocative? Consultative? Or something else?", field: "tone" },
  ];

  const TYPING_MESSAGES = {
    audience: ["Analyzing your target audience...", "Tailoring messaging to your ICP...", "Understanding who you're reaching..."],
    offer: ["Crafting your core offer...", "Highlighting your value proposition...", "Shaping the hook..."],
    pain: ["Identifying pain points...", "Zeroing in on their biggest challenge...", "Mining for emotional triggers..."],
    proof: ["Gathering proof points...", "Adding credibility layers...", "Weaving in case studies..."],
    cta: ["Defining your call to action...", "Sharpening the next step...", "Making the CTA irresistible..."],
    tone: ["Setting the tone...", "Fine-tuning voice and style...", "Applying your brand voice..."],
    suite: ["Generating your cold email sequence...", "Writing LinkedIn messages...", "Creating subject lines...", "Building your complete suite..."],
    revise: (label) => [`Revising ${label}...`, "Applying your feedback...", "Updating your copy..."],
  };

  const messageSet = React.useMemo(() => {
    if (revisingItem) return TYPING_MESSAGES.revise(revisingItem.label);
    if (!messagingSuite && workshopStep < 5) return TYPING_MESSAGES[AGENT_QUESTIONS[workshopStep]?.field] || TYPING_MESSAGES.audience;
    return TYPING_MESSAGES.suite;
  }, [workshopStep, messagingSuite, revisingItem]);

  React.useEffect(() => {
    if (!isTyping || !messageSet?.length) return;
    setTypingMessageIdx(0);
    const t = setInterval(() => setTypingMessageIdx(i => (i + 1) % messageSet.length), 2500);
    return () => clearInterval(t);
  }, [isTyping, messageSet]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopy = async (text, key) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const MOCK_SUITE = {
    emails: [
      { id: "e1", label: "Initial Email — Variant A (Pain-Led)", body: "Hi {{firstName}},\n\nI noticed {{companyName}} has been scaling the sales team — congrats on the growth.\n\nQuick question: how much time are your reps spending on lead research vs. actually selling?\n\nMost teams I work with find their reps waste 3+ hours a day manually researching prospects. We built an AI-powered system that automates the entire discovery-to-personalisation pipeline — one client went from 12 meetings/month to 47 in 6 weeks.\n\nWorth a 15-min chat to see if it'd work for {{companyName}}?\n\nBest,\n[Your name]" },
      { id: "e2", label: "Initial Email — Variant B (Result-Led)", body: "Hi {{firstName}},\n\nA quick stat: one of our clients went from 12 booked meetings per month to 47 — in 6 weeks.\n\nThe difference? They stopped relying on manual prospecting and switched to an AI-powered pipeline that finds, verifies, and personalises outreach at scale.\n\nI help companies like {{companyName}} implement the same system. No fluff, no bloated tech stack — just more qualified meetings on your calendar.\n\nHappy to show you the exact workflow. 15 minutes — you'll know if it's a fit.\n\n[Your name]" },
      { id: "e3", label: "Follow-up #1 (2 days later)", body: "Hi {{firstName}},\n\nJust bumping this up — I know inboxes get buried.\n\nThe short version: we help sales teams automate lead research so your reps spend time selling, not Googling.\n\nHappy to share a 2-min Loom showing exactly how it works if that's easier?\n\n[Your name]" },
      { id: "e4", label: "Follow-up #2 (4 days later)", body: "Hi {{firstName}},\n\nLast note from me — I get that timing matters.\n\nIf scaling outbound is on the roadmap for {{companyName}} this quarter, I'd love to show you how we've helped similar teams 3-4x their pipeline without adding headcount.\n\nIf not, no worries at all — happy to reconnect when it makes sense.\n\n[Your name]" },
    ],
    linkedin: [
      { id: "l1", label: "Connection Request — Variant A", body: "Hi {{firstName}} — saw {{companyName}} is scaling the sales team. I work with similar companies on automating outbound with AI. Would love to connect and share ideas." },
      { id: "l2", label: "Connection Request — Variant B", body: "{{firstName}}, your work at {{companyName}} caught my eye. I help growth-stage teams 3-4x their pipeline with AI-powered prospecting. Happy to share what's working if useful." },
      { id: "l3", label: "Follow-up Message (after acceptance)", body: "Thanks for connecting {{firstName}}!\n\nQuick question — how are you currently handling lead research and outbound personalisation at {{companyName}}?\n\nI recently helped a similar company go from 12 to 47 booked meetings/month by automating their prospecting pipeline. Happy to share the approach if you're interested.\n\nNo pitch — genuinely curious how you're tackling it." },
    ],
    subjects: [
      "{{firstName}}, quick question about {{companyName}}'s outbound",
      "47 meetings in 6 weeks (here's how)",
      "{{companyName}}'s sales team is leaving money on the table",
      "saw {{companyName}} is hiring SDRs — what if you didn't need to?",
      "the AI outbound playbook (steal this)",
      "re: scaling pipeline at {{companyName}}",
      "{{firstName}}, are your reps still researching leads manually?",
      "we helped [similar company] 4x their pipeline",
      "quick idea for {{companyName}}'s outbound",
      "{{firstName}} — 15 mins to save your team 15 hours/week?",
      "your competitors are automating outbound (here's how)",
      "the cold email mistake 90% of sales teams make",
      "{{firstName}}, this might save {{companyName}} £50K/year",
      "I'd love your take on this, {{firstName}}",
      "what if {{companyName}} never had to manually prospect again?",
      "{{firstName}}, one question about your sales process",
      "3 mins — I'll show you what your pipeline could look like",
      "{{companyName}} + AI outbound = ?",
      "most sales teams hate prospecting. here's the fix.",
      "{{firstName}}, saw something interesting about {{companyName}}",
    ],
  };

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };

  const allEmails = MOCK_PLAYBOOKS.flatMap(pb => pb.emails.map(e => ({ ...e, playbook: pb.name, playbookId: pb.id })));
  const allLinkedin = MOCK_PLAYBOOKS.flatMap(pb => pb.linkedin.map(l => ({ ...l, playbook: pb.name, playbookId: pb.id })));
  const allSubjects = MOCK_PLAYBOOKS.flatMap(pb => pb.subjects.map((s, idx) => ({ text: s, playbook: pb.name, playbookId: pb.id, subjectIndex: idx })));

  const openEditMessagingItem = (type, item, subjectIndex) => {
    setSaveError(null);
    if (type === 'email') setEditForm({ label: item.label ?? '', body: item.body ?? '' });
    else if (type === 'linkedin') setEditForm({ label: item.label ?? '', body: item.body ?? '' });
    else setEditForm({ text: item.text ?? '' });
    setEditingMessagingItem({ type, playbookId: item.playbookId, item, subjectIndex });
  };

  const handleSaveMessagingEdit = async () => {
    if (!editingMessagingItem) return;
    const { type, playbookId, item, subjectIndex } = editingMessagingItem;
    const pb = MOCK_PLAYBOOKS.find(p => p.id === playbookId);
    if (!pb) return;
    const emails = [...(pb.emails || [])];
    const linkedin = [...(pb.linkedin || [])];
    const subjects = [...(pb.subjects || [])];
    if (type === 'email') {
      const idx = emails.findIndex(e => e.id === item.id);
      if (idx >= 0) {
        // Drop legacy subject field if present on the stored row; emails only carry body now.
        const { subject: _legacySubject, ...prev } = emails[idx];
        emails[idx] = { ...prev, label: editForm.label ?? prev.label, body: editForm.body ?? prev.body };
      }
    } else if (type === 'linkedin') {
      const idx = linkedin.findIndex(l => l.id === item.id);
      if (idx >= 0) { linkedin[idx] = { ...linkedin[idx], label: editForm.label ?? linkedin[idx].label, body: editForm.body ?? linkedin[idx].body }; }
    } else if (type === 'subject' && subjectIndex != null) {
      subjects[subjectIndex] = editForm.text ?? subjects[subjectIndex];
    }
    setSavingEdit(true);
    try {
      await api.messagingCopies.update(playbookId, { suite_json: { emails, linkedin, subjects } });
      setEditingMessagingItem(null);
      setEditForm({});
      refreshPlaybooks();
    } catch (e) {
      console.error('Save messaging edit failed', e);
      setSaveError(e?.message || 'Could not save. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const startWorkshop = () => {
    setView("workshop");
    setChatMessages([{ role: "agent", text: AGENT_QUESTIONS[0].message }]);
    setWorkshopStep(0);
    setMessagingSuite(null);
    setRevisingItem(null);
    setPlaybookName("");
    setSaveSelections({});
  };

  const openSaveModal = () => {
    const sel = {};
    messagingSuite.emails.forEach(e => sel[e.id] = true);
    messagingSuite.linkedin.forEach(l => sel[l.id] = true);
    messagingSuite.subjects.forEach((_, i) => sel[`s${i}`] = true);
    setSaveSelections(sel);
    setPlaybookName("");
    setSaveError(null);
    setShowSaveModal(true);
  };

  const handleSavePlaybook = async () => {
    if (!playbookName.trim() || !messagingSuite) return;
    const emails = messagingSuite.emails.filter(e => saveSelections[e.id]);
    const linkedin = messagingSuite.linkedin.filter(l => saveSelections[l.id]);
    const subjects = messagingSuite.subjects.filter((_, i) => saveSelections[`s${i}`]);
    if (!emails.length && !linkedin.length && !subjects.length) return;
    setSaveError(null);
    setSavingPlaybook(true);
    try {
      await api.messagingCopies.create({
        name: playbookName.trim(),
        category: 'value_prop',
        audience: '',
        suite_json: { emails, linkedin, subjects },
      });
      setShowSaveModal(false);
      setView("list");
      setPlaybookName("");
      setSaveSelections({});
      refreshPlaybooks();
    } catch (err) {
      console.error("Save playbook error:", err);
      setSaveError(err?.message || "Could not save. Please try again.");
    } finally {
      setSavingPlaybook(false);
    }
  };

  const toggleSelection = (key) => {
    setSaveSelections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllInGroup = (group) => {
    const newSel = { ...saveSelections };
    if (group === "emails") messagingSuite.emails.forEach(e => newSel[e.id] = true);
    if (group === "linkedin") messagingSuite.linkedin.forEach(l => newSel[l.id] = true);
    if (group === "subjects") messagingSuite.subjects.forEach((_, i) => newSel[`s${i}`] = true);
    setSaveSelections(newSel);
  };

  const deselectAllInGroup = (group) => {
    const newSel = { ...saveSelections };
    if (group === "emails") messagingSuite.emails.forEach(e => newSel[e.id] = false);
    if (group === "linkedin") messagingSuite.linkedin.forEach(l => newSel[l.id] = false);
    if (group === "subjects") messagingSuite.subjects.forEach((_, i) => newSel[`s${i}`] = false);
    setSaveSelections(newSel);
  };

  const getSelectedCount = (group) => {
    if (group === "emails") return messagingSuite.emails.filter(e => saveSelections[e.id]).length;
    if (group === "linkedin") return messagingSuite.linkedin.filter(l => saveSelections[l.id]).length;
    if (group === "subjects") return messagingSuite.subjects.filter((_, i) => saveSelections[`s${i}`]).length;
    return 0;
  };

  const requestRevise = (item) => {
    setRevisingItem(item);
    setChatMessages(prev => [
      ...prev,
      { role: "user", text: `I'd like to revise: ${item.label}` },
      { role: "agent", text: `Sure — what would you like to change about "${item.label}"? Tell me what to adjust and I'll update it.` },
    ]);
  };

  const sendMessage = async () => {
    if (!userInput.trim() || isTyping) return;
    const newMessages = [...chatMessages, { role: "user", text: userInput }];
    setChatMessages(newMessages);
    setUserInput("");
    setIsTyping(true);
    try {
      const payload = { messages: newMessages };
      if (messagingSuite && revisingItem) {
        payload.reviseRequest = { itemLabel: revisingItem.label, userFeedback: userInput.trim() };
        payload.currentSuite = messagingSuite;
      }
      const { agentText, suite: newSuite } = await api.messagingCopies.copywriterChat(payload);
      setChatMessages(prev => [...prev, { role: "agent", text: agentText || "Done." }]);
      if (newSuite) {
        setMessagingSuite(newSuite);
        setRevisingItem(null);
        if (!messagingSuite) setWorkshopStep(AGENT_QUESTIONS.length);
      } else if (!messagingSuite) {
        setWorkshopStep(prev => Math.min(prev + 1, AGENT_QUESTIONS.length));
      }
    } catch (err) {
      console.error("Copywriter chat error:", err);
      setChatMessages(prev => [...prev, { role: "agent", text: "Sorry, I couldn't process that. " + (err?.message || "Please try again.") }]);
    } finally {
      setIsTyping(false);
    }
  };

  const CheckBox = ({ checked, onChange, accentColor }) => (
    <div onClick={onChange} style={{
      width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? (accentColor || COLORS.accent) : COLORS.border}`,
      background: checked ? (accentColor || COLORS.accent) : "transparent",
      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
    }}>
      {checked && <span style={{ color: COLORS.bg, fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✓</span>}
    </div>
  );

  // LIST VIEW
  if (view === "list") {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        {/* Edit Messaging Item Modal */}
        {editingMessagingItem && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { if (!savingEdit) { setEditingMessagingItem(null); setEditForm({}); setSaveError(null); } }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: "80vh", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  Edit {editingMessagingItem.type === 'email' ? 'Email' : editingMessagingItem.type === 'linkedin' ? 'LinkedIn Message' : 'Subject Line'}
                </div>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
                {saveError && <div style={{ padding: "10px 14px", background: "#dc262615", border: "1px solid #dc262633", borderRadius: 8, marginBottom: 16, fontSize: 12, color: "#dc2626" }}>{saveError}</div>}
                {editingMessagingItem.type === 'email' && (
                  <>
                    <div style={{ marginBottom: 12 }}><label style={labelStyle}>LABEL</label><input value={editForm.label ?? ''} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} style={inputStyle} placeholder="e.g. Follow-up #1" /></div>
                    <div style={{ marginBottom: 12 }}><label style={labelStyle}>BODY</label><textarea value={editForm.body ?? ''} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))} rows={8} style={{ ...inputStyle, resize: "vertical" }} placeholder="Email body" /></div>
                  </>
                )}
                {editingMessagingItem.type === 'linkedin' && (
                  <>
                    <div style={{ marginBottom: 12 }}><label style={labelStyle}>LABEL</label><input value={editForm.label ?? ''} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} style={inputStyle} placeholder="e.g. Connection Request — Variant A" /></div>
                    <div style={{ marginBottom: 12 }}><label style={labelStyle}>BODY</label><textarea value={editForm.body ?? ''} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))} rows={8} style={{ ...inputStyle, resize: "vertical" }} placeholder="LinkedIn message" /></div>
                  </>
                )}
                {editingMessagingItem.type === 'subject' && (
                  <div style={{ marginBottom: 12 }}><label style={labelStyle}>SUBJECT LINE</label><input value={editForm.text ?? ''} onChange={e => setEditForm(f => ({ ...f, text: e.target.value }))} style={inputStyle} placeholder="Subject line text" /></div>
                )}
              </div>
              <div style={{ padding: "16px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => { setEditingMessagingItem(null); setEditForm({}); setSaveError(null); }} disabled={savingEdit} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: savingEdit ? "default" : "pointer" }}>Cancel</button>
                <button onClick={handleSaveMessagingEdit} disabled={savingEdit} style={{ padding: "10px 24px", background: savingEdit ? COLORS.border : COLORS.accent, color: savingEdit ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: savingEdit ? "default" : "pointer" }}>{savingEdit ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}
        <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
              Messaging <span style={{ color: COLORS.accent }}>Workshop</span>
            </h2>
            <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Build your outbound messaging with an AI copywriting strategist</p>
          </div>
          <button onClick={startWorkshop} style={{ padding: "12px 24px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ NEW MESSAGING</button>
        </div>

        {/* Category Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
          {[
            { key: "emails", label: "Emails", count: allEmails.length },
            { key: "linkedin", label: "LinkedIn", count: allLinkedin.length },
            { key: "subjects", label: "Subject Lines", count: allSubjects.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setListTab(tab.key)} style={{
              padding: "10px 20px", background: "transparent", border: "none",
              borderBottom: listTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              color: listTab === tab.key ? COLORS.accent : COLORS.textMuted,
              fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}>
              {tab.label}
              <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: listTab === tab.key ? COLORS.accentBg : COLORS.surface, color: listTab === tab.key ? COLORS.accent : COLORS.textDim }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Emails */}
        {listTab === "emails" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {allEmails.map((email, i) => (
              <div key={i} style={{ padding: "14px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.blue }}>{email.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {email.playbookId && <button onClick={() => openEditMessagingItem('email', email)} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Edit</button>}
                    <button onClick={() => handleCopy(email.body ?? '', `email-${i}`)} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>{copiedKey === `email-${i}` ? 'Copied!' : 'Copy'}</button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5, maxHeight: 40, overflow: "hidden" }}>{email.body}</div>
              </div>
            ))}
          </div>
        )}

        {/* LinkedIn */}
        {listTab === "linkedin" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {allLinkedin.map((msg, i) => (
              <div key={i} style={{ padding: "14px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.warn }}>{msg.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {msg.playbookId && <button onClick={() => openEditMessagingItem('linkedin', msg)} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Edit</button>}
                    <button onClick={() => handleCopy(msg.body ?? '', `linkedin-${i}`)} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>{copiedKey === `linkedin-${i}` ? 'Copied!' : 'Copy'}</button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5, maxHeight: 40, overflow: "hidden" }}>{msg.body}</div>
              </div>
            ))}
          </div>
        )}

        {/* Subject Lines */}
        {listTab === "subjects" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {allSubjects.map((s, i) => (
              <div key={i} style={{ padding: "8px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.text}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {s.playbookId && <button onClick={() => openEditMessagingItem('subject', s, s.subjectIndex)} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Edit</button>}
                  <button onClick={() => handleCopy(s.text ?? '', `subject-${i}`)} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>{copiedKey === `subject-${i}` ? 'Copied!' : 'Copy'}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // WORKSHOP VIEW
  return (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Save Playbook Modal */}
      {showSaveModal && messagingSuite && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSaveModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: "80vh", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Save Messaging Playbook</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Select which pieces to include in this playbook</div>
            </div>
            <div style={{ padding: "16px 24px", borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
              <label style={labelStyle}>PLAYBOOK NAME</label>
              <input value={playbookName} onChange={e => setPlaybookName(e.target.value)} placeholder="e.g. Q1 SaaS VP Growth — Cold Intro" style={inputStyle} autoFocus />
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
              {/* Emails */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12 }}>📧</span>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.blue, letterSpacing: "0.06em", fontWeight: 600 }}>EMAILS</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>({getSelectedCount("emails")}/{messagingSuite.emails.length})</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => selectAllInGroup("emails")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: COLORS.blue, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>All</button>
                    <button onClick={() => deselectAllInGroup("emails")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>None</button>
                  </div>
                </div>
                {messagingSuite.emails.map(email => (
                  <div key={email.id} onClick={() => toggleSelection(email.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: saveSelections[email.id] ? COLORS.blue + "08" : "transparent", border: `1px solid ${saveSelections[email.id] ? COLORS.blue + "22" : COLORS.border}`, borderRadius: 6, marginBottom: 4, cursor: "pointer" }}>
                    <CheckBox checked={saveSelections[email.id]} accentColor={COLORS.blue} onChange={() => {}} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: saveSelections[email.id] ? COLORS.text : COLORS.textDim }}>{email.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(email.body || '').replace(/\s+/g, ' ').slice(0, 80)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* LinkedIn */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12 }}>💼</span>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.warn, letterSpacing: "0.06em", fontWeight: 600 }}>LINKEDIN</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>({getSelectedCount("linkedin")}/{messagingSuite.linkedin.length})</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => selectAllInGroup("linkedin")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: COLORS.warn, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>All</button>
                    <button onClick={() => deselectAllInGroup("linkedin")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>None</button>
                  </div>
                </div>
                {messagingSuite.linkedin.map(msg => (
                  <div key={msg.id} onClick={() => toggleSelection(msg.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: saveSelections[msg.id] ? COLORS.warn + "08" : "transparent", border: `1px solid ${saveSelections[msg.id] ? COLORS.warn + "22" : COLORS.border}`, borderRadius: 6, marginBottom: 4, cursor: "pointer" }}>
                    <CheckBox checked={saveSelections[msg.id]} accentColor={COLORS.warn} onChange={() => {}} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: saveSelections[msg.id] ? COLORS.text : COLORS.textDim }}>{msg.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{msg.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Subject Lines */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12 }}>✉️</span>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: "#7B61FF", letterSpacing: "0.06em", fontWeight: 600 }}>SUBJECT LINES</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>({getSelectedCount("subjects")}/{messagingSuite.subjects.length})</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => selectAllInGroup("subjects")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: "#7B61FF", fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>All</button>
                    <button onClick={() => deselectAllInGroup("subjects")} style={{ padding: "2px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>None</button>
                  </div>
                </div>
                {messagingSuite.subjects.map((subj, i) => (
                  <div key={i} onClick={() => toggleSelection(`s${i}`)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", background: saveSelections[`s${i}`] ? "#7B61FF08" : "transparent", border: `1px solid ${saveSelections[`s${i}`] ? "#7B61FF22" : COLORS.border}`, borderRadius: 6, marginBottom: 3, cursor: "pointer" }}>
                    <CheckBox checked={saveSelections[`s${i}`]} accentColor="#7B61FF" onChange={() => {}} />
                    <div style={{ fontSize: 12, color: saveSelections[`s${i}`] ? COLORS.text : COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subj}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
              {saveError && <div style={{ fontSize: 11, color: "#dc2626" }}>{saveError}</div>}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>
                  {getSelectedCount("emails")} emails · {getSelectedCount("linkedin")} LinkedIn · {getSelectedCount("subjects")} subjects selected
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setShowSaveModal(false)} disabled={savingPlaybook} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: savingPlaybook ? "default" : "pointer" }}>Cancel</button>
                  <button onClick={handleSavePlaybook} disabled={!playbookName.trim() || savingPlaybook} style={{
                    padding: "10px 24px", background: playbookName.trim() && !savingPlaybook ? COLORS.accent : COLORS.border,
                    color: playbookName.trim() && !savingPlaybook ? COLORS.bg : COLORS.textDim,
                    border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
                    cursor: playbookName.trim() && !savingPlaybook ? "pointer" : "default",
                  }}>{savingPlaybook ? "Saving..." : "Save Playbook"}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left — Chat */}
      <div style={{ width: 420, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setView("list")} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>←</button>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>AI Copywriter</div>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>{messagingSuite ? "Revise mode — click ✏️ on any piece" : `Step ${Math.min(workshopStep + 1, AGENT_QUESTIONS.length)} of ${AGENT_QUESTIONS.length}`}</div>
            </div>
          </div>
          <div style={{ width: 100, height: 4, borderRadius: 2, background: COLORS.surface }}>
            <div style={{ width: `${messagingSuite ? 100 : Math.min((workshopStep + 1) / AGENT_QUESTIONS.length * 100, 100)}%`, height: "100%", borderRadius: 2, background: COLORS.accent, transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "85%", padding: "10px 14px", borderRadius: 12,
                background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface,
                border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`,
                borderBottomRightRadius: msg.role === "user" ? 4 : 12,
                borderBottomLeftRadius: msg.role === "agent" ? 4 : 12,
              }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI COPYWRITER</div>}
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}>
                <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI COPYWRITER</div>
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>
                  {messageSet?.[typingMessageIdx] ?? "Working on it"}
                  <ProgressDots active={true} />
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) sendMessage(); }} placeholder={revisingItem ? `Describe changes for "${revisingItem.label}"...` : messagingSuite ? "Ask for revisions..." : "Type your answer..."} style={{ ...inputStyle, flex: 1 }} disabled={isTyping} />
          <button onClick={sendMessage} disabled={isTyping || !userInput.trim()} style={{
            padding: "10px 18px", background: userInput.trim() && !isTyping ? COLORS.accent : COLORS.border,
            color: userInput.trim() && !isTyping ? COLORS.bg : COLORS.textDim,
            border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
            cursor: userInput.trim() && !isTyping ? "pointer" : "default",
          }}>Send</button>
        </div>
      </div>

      {/* Right — Messaging Preview */}
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        {!messagingSuite ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>📝</div>
              <div style={{ fontFamily: FONT, fontSize: 15, color: COLORS.textDim, marginBottom: 8 }}>Your messaging suite will appear here</div>
              <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>Answer the questions on the left and the AI will generate your complete cold email sequence, LinkedIn messages, and subject lines.</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Messaging <span style={{ color: COLORS.accent }}>Suite</span></h3>
                <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 2 }}>{messagingSuite.emails.length} emails · {messagingSuite.linkedin.length} LinkedIn · {messagingSuite.subjects.length} subject lines</div>
              </div>
              <button onClick={openSaveModal} style={{ padding: "8px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Save Playbook</button>
            </div>

            {/* Emails */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 14 }}>📧</span>
                <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.blue, letterSpacing: "0.08em", fontWeight: 600 }}>COLD EMAIL SEQUENCE</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {messagingSuite.emails.map(email => (
                  <div key={email.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.blue }}>{email.label}</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => requestRevise(email)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.blue; e.currentTarget.style.color = COLORS.blue; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
                        >✏️ Revise</button>
                        <button onClick={() => handleCopy(email.body ?? '', `email-${email.id}`)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>{copiedKey === `email-${email.id}` ? "Copied!" : "Copy"}</button>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 3 }}>BODY</div>
                      <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line", padding: "10px 14px", background: COLORS.bg, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>{email.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* LinkedIn */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 14 }}>💼</span>
                <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.warn, letterSpacing: "0.08em", fontWeight: 600 }}>LINKEDIN MESSAGES</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {messagingSuite.linkedin.map(msg => (
                  <div key={msg.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.warn }}>{msg.label}</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => requestRevise(msg)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.warn; e.currentTarget.style.color = COLORS.warn; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
                        >✏️ Revise</button>
                        <button onClick={() => handleCopy(msg.body, `linkedin-${msg.id}`)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>{copiedKey === `linkedin-${msg.id}` ? "Copied!" : "Copy"}</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line", padding: "10px 14px", background: COLORS.bg, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>{msg.body}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Subject Lines */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 14 }}>✉️</span>
                <span style={{ fontFamily: FONT, fontSize: 10, color: "#7B61FF", letterSpacing: "0.08em", fontWeight: 600 }}>SUBJECT LINES ({messagingSuite.subjects.length})</span>
              </div>
              <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {messagingSuite.subjects.map((subj, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: COLORS.bg, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                        <span style={{ fontFamily: FONT, fontSize: 10, color: "#7B61FF", fontWeight: 600, flexShrink: 0 }}>{String(i + 1).padStart(2, "0")}</span>
                        <span style={{ fontSize: 12, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subj}</span>
                      </div>
                      <button onClick={() => handleCopy(subj, `subject-${i}`)} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer", flexShrink: 0, marginLeft: 8 }}>{copiedKey === `subject-${i}` ? "Copied!" : "Copy"}</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function AddLeadsModal({ campaign, onClose, accentColor, availableLists = [], onSubmit }) {
  const [selectedList, setSelectedList] = useState(null);
  const [loadedLists, setLoadedLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  useEffect(() => {
    async function loadLists() {
      try {
        const lists = await api.leadLists.list();
        setLoadedLists(lists.map(list => ({
          id: list.id,
          name: list.name,
          contacts: (list.contacts || []).length,
          date: new Date(list.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        })));
      } catch (err) {
        console.error("Failed to load lists:", err);
      } finally {
        setLoading(false);
      }
    }
    loadLists();
  }, []);
  
  const MOCK_LISTS = [
    { id: "l1", name: "Q1 SaaS VP Growth — North America", contacts: 13, date: "Feb 11, 2026" },
    { id: "l2", name: "Q4 FinTech CROs — EMEA", contacts: 3, date: "Feb 6, 2026" },
    { id: "l3", name: "Series A SaaS — US West Coast", contacts: 2, date: "Jan 30, 2026" },
  ];
  
  const lists = loadedLists;
  const handleSubmit = async () => {
    if (!selectedList || submitting) return;
    if (typeof onSubmit !== "function") {
      onClose();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(selectedList);
      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Could not add leads to campaign");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 500, maxHeight: "calc(100vh - 48px)", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Add Leads to Campaign</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{campaign.name}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: COLORS.textDim, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "16px 24px", flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", flexShrink: 1 }}>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10, flexShrink: 0 }}>SELECT LEAD LIST</div>
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center", color: COLORS.textMuted }}>Loading lists...</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minHeight: 0, overflowY: "auto" }}>
              {lists.map(list => (
                <div key={list.id} onClick={() => setSelectedList(list.id)} style={{
                  padding: "12px 16px", borderRadius: 8, cursor: "pointer",
                  background: selectedList === list.id ? (accentColor || COLORS.accent) + "15" : COLORS.surface,
                  border: `1px solid ${selectedList === list.id ? (accentColor || COLORS.accent) + "44" : COLORS.border}`,
                  transition: "all 0.15s",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{list.name}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{list.contacts} contacts · {list.date}</div>
                    </div>
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%",
                      border: `2px solid ${selectedList === list.id ? (accentColor || COLORS.accent) : COLORS.borderActive}`,
                      background: selectedList === list.id ? (accentColor || COLORS.accent) : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: COLORS.bg, fontWeight: 700,
                    }}>{selectedList === list.id && "✓"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {submitError && (
          <div style={{ margin: "0 24px 14px", padding: "10px 12px", background: COLORS.warnBg, border: `1px solid ${COLORS.warn}44`, borderRadius: 8, color: COLORS.warn, fontFamily: FONT_BODY, fontSize: 12 }}>
            {submitError}
          </div>
        )}
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} disabled={submitting} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!selectedList || submitting} style={{
            padding: "10px 24px", background: selectedList && !submitting ? (accentColor || COLORS.accent) : COLORS.border,
            color: selectedList && !submitting ? COLORS.bg : COLORS.textDim,
            border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: selectedList && !submitting ? "pointer" : "default",
            opacity: submitting ? 0.8 : 1,
          }}>Add {selectedList ? lists.find(l => l.id === selectedList)?.contacts : 0} Leads →</button>
        </div>
      </div>
    </div>
  );
}

/** Parse DB jsonb or stringified suite_json for messaging playbooks. */
function normalizeMessagingSuiteJson(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

/** Subject lines for campaign picker: sourced exclusively from each playbook's
 * Subject Lines pool. Email objects no longer carry a subject field — subjects
 * live only in suite.subjects and are picked independently at campaign build time. */
function buildAllSavedSubjectsForPicker(playbooks) {
  const seen = new Set();
  const out = [];
  const add = (text, playbook) => {
    const t = String(text || "").trim();
    if (!t) return;
    const key = `${t}\0${playbook}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ text: t, playbook, sourceLabel: null });
  };
  for (const pb of playbooks) {
    for (const s of pb.subjects || []) {
      const text = typeof s === "string" ? s : s?.text ?? "";
      add(text, pb.name);
    }
  }
  return out;
}

function SubjectVariantInput({ subj, index, campaignForm, setCampaignForm, inputStyle, savedSubjects = [], onSelectSaved, onSwitchToManual }) {
  const inputRef = useRef(null);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const insertVar = (v) => {
    const el = inputRef.current;
    if (!el) { const next = [...campaignForm.subjects]; next[index] = { ...subj, value: subj.value + v }; setCampaignForm({ ...campaignForm, subjects: next }); return; }
    const start = el.selectionStart; const end = el.selectionEnd;
    const newVal = subj.value.substring(0, start) + v + subj.value.substring(end);
    const next = [...campaignForm.subjects]; next[index] = { ...subj, value: newVal }; setCampaignForm({ ...campaignForm, subjects: next });
    setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 0);
  };
  const isSaved = subj.mode === "saved";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT }}>Variant {index + 1}</span>
        {isSaved ? (
          <>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT, fontWeight: 500 }}>From: {subj.sourceLabel || "Playbook"}</span>
            <button onClick={() => onSwitchToManual?.()} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Switch to manual</button>
          </>
        ) : (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT, fontWeight: 500 }}>Manual Input</span>
        )}
        {savedSubjects.length > 0 && (
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button onClick={() => setShowSavedDropdown(v => !v)} style={{ padding: "2px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}40`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Pick from saved</button>
            {showSavedDropdown && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 2, padding: 6, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", zIndex: 50, maxHeight: 200, overflowY: "auto", minWidth: 260 }}>
                {savedSubjects.map((s, i) => (
                  <div key={i} onClick={() => { onSelectSaved?.(s.text, s.playbook, s.sourceLabel); setShowSavedDropdown(false); }} style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, color: COLORS.text, fontFamily: FONT, borderBottom: i < savedSubjects.length - 1 ? `1px solid ${COLORS.border}` : "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = COLORS.surface; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.text}</div>
                    {s.sourceLabel && <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2, fontWeight: 600 }}>{s.sourceLabel}</div>}
                    <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>{s.playbook}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <input ref={inputRef} value={subj.value} onChange={e => { const next = [...campaignForm.subjects]; next[index] = { ...subj, value: e.target.value }; setCampaignForm({ ...campaignForm, subjects: next }); }} placeholder="Enter subject line..." style={inputStyle} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {[["firstName", "First Name"], ["lastName", "Last Name"], ["companyName", "Company"]].map(([k]) => (
          <button key={k} onClick={() => insertVar(`{{${k}}}`)} style={{ padding: "3px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}30`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{`{{${k}}}`}</button>
        ))}
      </div>
    </div>
  );
}

function BodyVariantInput({ body, index, campaignForm, setCampaignForm, inputStyle, savedEmails = [], onSelectSaved, onSwitchToManual }) {
  const textRef = useRef(null);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const insertVar = (v) => {
    const el = textRef.current;
    if (!el) { const next = [...campaignForm.bodies]; next[index] = { ...body, value: body.value + v }; setCampaignForm({ ...campaignForm, bodies: next }); return; }
    const start = el.selectionStart; const end = el.selectionEnd;
    const newVal = body.value.substring(0, start) + v + body.value.substring(end);
    const next = [...campaignForm.bodies]; next[index] = { ...body, value: newVal }; setCampaignForm({ ...campaignForm, bodies: next });
    setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 0);
  };
  const isSaved = body.mode === "saved";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT }}>Variant {index + 1}</span>
        {isSaved ? (
          <>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT, fontWeight: 500 }}>From: {body.sourceLabel || "Playbook"}</span>
            <button onClick={() => onSwitchToManual?.()} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Switch to manual</button>
          </>
        ) : (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT, fontWeight: 500 }}>Manual Input</span>
        )}
        {savedEmails.length > 0 && (
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button onClick={() => setShowSavedDropdown(v => !v)} style={{ padding: "2px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}40`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Pick from saved</button>
            {showSavedDropdown && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 2, padding: 6, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", zIndex: 50, maxHeight: 200, overflowY: "auto", minWidth: 280 }}>
                {savedEmails.map((email, i) => (
                  <div key={email.id || i} onClick={() => { onSelectSaved?.(email.body || email.message || "", email.playbook, email); setShowSavedDropdown(false); }} style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, color: COLORS.text, fontFamily: FONT, borderBottom: i < savedEmails.length - 1 ? `1px solid ${COLORS.border}` : "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = COLORS.surface; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ fontWeight: 600 }}>{email.label || "Email"}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.body?.slice(0, 80) || ""}</div>
                    <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>{email.playbook}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <textarea ref={textRef} value={body.value} onChange={e => { const next = [...campaignForm.bodies]; next[index] = { ...body, value: e.target.value }; setCampaignForm({ ...campaignForm, bodies: next }); }} placeholder="Write your email body..." rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {[["firstName", "First Name"], ["lastName", "Last Name"], ["email", "Email"], ["companyName", "Company"], ["website", "Website"], ["personalization", "Personalization"]].map(([k]) => (
          <button key={k} onClick={() => insertVar(`{{${k}}}`)} style={{ padding: "3px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}30`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{`{{${k}}}`}</button>
        ))}
      </div>
    </div>
  );
}

function FollowupBodyVariantInput({ body, bodyIndex, followupIndex, campaignForm, setCampaignForm, inputStyle, savedEmails = [], onSelectSaved, onSwitchToManual }) {
  const fuRef = useRef(null);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const insertVar = (v) => {
    const el = fuRef.current;
    if (!el) { const next = [...campaignForm.followups]; const nb = [...next[followupIndex].bodies]; nb[bodyIndex] = { ...body, value: body.value + v }; next[followupIndex] = { ...next[followupIndex], bodies: nb }; setCampaignForm({ ...campaignForm, followups: next }); return; }
    const start = el.selectionStart; const end = el.selectionEnd;
    const newVal = body.value.substring(0, start) + v + body.value.substring(end);
    const next = [...campaignForm.followups]; const nb = [...next[followupIndex].bodies]; nb[bodyIndex] = { ...body, value: newVal }; next[followupIndex] = { ...next[followupIndex], bodies: nb }; setCampaignForm({ ...campaignForm, followups: next });
    setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 0);
  };
  const isSaved = body.mode === "saved";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT }}>Variant {bodyIndex + 1}</span>
        {isSaved ? (
          <>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.accentBg, color: COLORS.accent, fontFamily: FONT, fontWeight: 500 }}>From: {body.sourceLabel || "Playbook"}</span>
            <button onClick={() => onSwitchToManual?.()} style={{ padding: "2px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Switch to manual</button>
          </>
        ) : (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT, fontWeight: 500 }}>Manual</span>
        )}
        {savedEmails.length > 0 && (
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button onClick={() => setShowSavedDropdown(v => !v)} style={{ padding: "2px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}40`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Pick from saved</button>
            {showSavedDropdown && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 2, padding: 6, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", zIndex: 50, maxHeight: 200, overflowY: "auto", minWidth: 280 }}>
                {savedEmails.map((email, i) => (
                  <div key={email.id || i} onClick={() => { onSelectSaved?.(email.body || email.message || "", email.playbook, email); setShowSavedDropdown(false); }} style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, color: COLORS.text, fontFamily: FONT, borderBottom: i < savedEmails.length - 1 ? `1px solid ${COLORS.border}` : "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = COLORS.surface; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ fontWeight: 600 }}>{email.label || "Email"}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.body?.slice(0, 80) || ""}</div>
                    <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>{email.playbook}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <textarea ref={fuRef} value={body.value} onChange={e => { const next = [...campaignForm.followups]; const nextBodies = [...next[followupIndex].bodies]; nextBodies[bodyIndex] = { ...body, value: e.target.value }; next[followupIndex] = { ...next[followupIndex], bodies: nextBodies }; setCampaignForm({ ...campaignForm, followups: next }); }} placeholder="Write follow-up body..." rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {[["firstName", "First Name"], ["lastName", "Last Name"], ["email", "Email"], ["companyName", "Company"], ["website", "Website"], ["personalization", "Personalization"]].map(([k]) => (
          <button key={k} onClick={() => insertVar(`{{${k}}}`)} style={{ padding: "3px 8px", background: COLORS.accent + "12", border: `1px solid ${COLORS.accent}30`, borderRadius: 4, color: COLORS.accent, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{`{{${k}}}`}</button>
        ))}
      </div>
    </div>
  );
}

function instantlyCampaignListSortKey(c) {
  const ts = c?.timestamp_updated || c?.timestamp_created;
  const n = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Instantly list API has no sort param; order client-side (newest activity first). */
function sortInstantlyCampaignItemsNewestFirst(items) {
  return [...(items || [])].sort((a, b) => instantlyCampaignListSortKey(b) - instantlyCampaignListSortKey(a));
}

function internalEmailCampaignSortKey(c) {
  const ts = c?.updated_at || c?.updatedAt || c?.created_at || c?.createdAt;
  const n = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function sortInternalEmailCampaignsNewestFirst(camps) {
  return [...(camps || [])].sort((a, b) => {
    const kb = internalEmailCampaignSortKey(b);
    const ka = internalEmailCampaignSortKey(a);
    if (kb !== ka) return kb - ka;
    return String(b.id ?? "").localeCompare(String(a.id ?? ""), undefined, { numeric: true });
  });
}

function ColdEmailCampaignsView() {
  const [view, setView] = useState("list");
  const [addLeadsCampaign, setAddLeadsCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: "", selectedLeadList: "", senderAccounts: [], subjects: [{ id: 1, value: "", mode: "manual" }], bodies: [{ id: 1, value: "", mode: "manual" }],
    followups: [{ id: 1, delay: 1, subjectLine: false, bodies: [{ id: 1, value: "", mode: "manual" }] }, { id: 2, delay: 2, subjectLine: false, bodies: [{ id: 1, value: "", mode: "manual" }] }],
    dailyLimit: 50, startDate: "", endDate: "", openTracking: true, timezone: "Europe/London", sendStart: "09:00", sendEnd: "17:00", sendDays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
  });
  const [builderStep, setBuilderStep] = useState(0);
  const [MOCK_CAMPAIGNS, setEmailCampaigns] = useState([]);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [launchResult, setLaunchResult] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [togglingCampaignId, setTogglingCampaignId] = useState(null);

  useEffect(() => {
    const STATUS_MAP = { 0: 'draft', 1: 'active', 2: 'paused', 3: 'completed' };
    const parseAnalytics = (a) => {
      if (!a) return { leads: 0, sent: 0, opened: 0, replied: 0, bounced: 0 };
      return {
        leads: a.leads_count ?? a.leads ?? 0,
        sent: a.emails_sent_count ?? a.emails_sent ?? a.contacted_count ?? a.contacted ?? 0,
        opened: a.open_count_unique ?? a.opens ?? a.opened_count ?? a.emails_opened ?? 0,
        replied: a.reply_count_unique ?? a.replies ?? a.replied_count ?? 0,
        bounced: a.bounced_count ?? a.bounced ?? 0,
      };
    };
    api.instantly.campaigns.list().then(data => {
      const items = Array.isArray(data) ? data : (data?.data ?? data?.campaigns ?? data?.results ?? data?.items ?? []);
      const sorted = sortInstantlyCampaignItemsNewestFirst(items);
      const campIds = sorted.map(c => c.id).filter(Boolean);
      setEmailCampaigns(sorted.map(c => ({
        id: c.id,
        name: c.name || `Campaign ${c.id?.slice(0, 8)}`,
        status: STATUS_MAP[c.status] ?? (typeof c.status === 'string' ? c.status.toLowerCase() : 'draft'),
        leads: c.leads_count ?? 0,
        sent: 0, opened: 0, replied: 0, bounced: 0,
        dailyLimit: c.daily_limit || 50,
        startDate: c.campaign_schedule?.start_date || '',
      })));
      if (campIds.length) {
        Promise.all(campIds.map(id => api.instantly.analytics({ id }).catch(() => null))).then(results => {
          const arr = results.flatMap(r => {
            if (!r) return [];
            return Array.isArray(r) ? r : (r && typeof r === 'object' && (r.campaign_id ?? r.id) ? [r] : []);
          });
          setEmailCampaigns(prev => prev.map(camp => {
            const a = arr.find(x => (x.campaign_id ?? x.id) === camp.id);
            if (!a) return camp;
            const metrics = parseAnalytics(a);
            return { ...camp, leads: metrics.leads || camp.leads, sent: metrics.sent, opened: metrics.opened, replied: metrics.replied, bounced: metrics.bounced };
          }));
        });
      }
    }).catch(() => {
      api.campaigns.list().then(data => {
        const raw = Array.isArray(data) ? data : (data?.campaigns ?? []);
        const camps = sortInternalEmailCampaignsNewestFirst(
          raw.filter(c => c.platform === 'email' || c.channel === 'email' || !c.platform && !c.channel)
        );
        setEmailCampaigns(camps.map(c => ({ ...c, leads: c.lead_count || c.leads || 0, sent: c.sent_count ?? c.sent ?? 0, opened: c.opened_count ?? c.opened ?? c.emails_opened ?? 0, replied: c.replied_count ?? c.replied ?? c.replies ?? 0, bounced: c.bounced_count ?? c.bounced ?? 0, dailyLimit: c.daily_limit || c.dailyLimit || 50, startDate: c.start_date || c.startDate || '' })));
      }).catch(() => {});
    });

    api.instantly.accounts().then(data => {
      const items = Array.isArray(data) ? data : data.data || data.accounts || [];
      setSenderAccounts(items.map(a => ({ email: a.email, status: a.status === 1 || a.status === 'active' ? 'active' : 'inactive' })));
    }).catch(() => {});
  }, [refreshTrigger]);
  const MOCK_SENDERS = senderAccounts.length > 0 ? senderAccounts : [
    { email: "andrew@dunnco.co.uk", status: "inactive" }, { email: "andrewdunn@gbiw.co.uk", status: "active" },
    { email: "adunn@gbiw.co.uk", status: "active" }, { email: "andrew@gbiw.co.uk", status: "active" }, { email: "a.dunn@outreach.io", status: "active" },
  ];
  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const selectStyle = { ...inputStyle, appearance: "none", cursor: "pointer", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a7a8e' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" };
  const sectionStyle = { padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 };
  const sectionTitle = { fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, marginBottom: 16 };
  const toggleSender = (email) => setCampaignForm(p => ({ ...p, senderAccounts: p.senderAccounts.includes(email) ? p.senderAccounts.filter(e => e !== email) : [...p.senderAccounts, email] }));
  const toggleDay = (day) => setCampaignForm(p => ({ ...p, sendDays: p.sendDays.includes(day) ? p.sendDays.filter(d => d !== day) : [...p.sendDays, day] }));
  const [MOCK_LEAD_LISTS, setLeadListsData] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  useEffect(() => {
    api.leadLists.list().then(data => {
      const lists = Array.isArray(data) ? data : [];
      setLeadListsData(lists.map(l => ({ id: l.id, name: l.name || 'Untitled', count: l.contact_count || l.contacts?.length || 0 })));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    api.messagingCopies.list().then(data => {
      const copies = Array.isArray(data) ? data : data.messaging_copies || [];
      const items = copies
        .map((c) => {
          const suite = normalizeMessagingSuiteJson(c.suite_json);
          if (!suite || (!suite.emails?.length && !suite.subjects?.length)) return null;
          return {
            id: c.id,
            name: c.name || "Untitled",
            emails: suite.emails ?? [],
            subjects: suite.subjects ?? [],
          };
        })
        .filter(Boolean);
      setPlaybooks(items);
    }).catch(() => {});
  }, []);
  const allSavedEmails = playbooks.flatMap(pb => (pb.emails || []).map(e => ({ ...e, playbook: pb.name })));
  const allSavedSubjectsForPicker = buildAllSavedSubjectsForPicker(playbooks);
  const startNewCampaign = () => { setCampaignForm({ name: "", selectedLeadList: "", senderAccounts: [], subjects: [{ id: 1, value: "", mode: "manual" }], bodies: [{ id: 1, value: "", mode: "manual" }], followups: [{ id: 1, delay: 1, subjectLine: false, bodies: [{ id: 1, value: "", mode: "manual" }] }, { id: 2, delay: 2, subjectLine: false, bodies: [{ id: 1, value: "", mode: "manual" }] }], dailyLimit: 50, startDate: "", endDate: "", openTracking: true, timezone: "Europe/London", sendStart: "09:00", sendEnd: "17:00", sendDays: ["Mon", "Tue", "Wed", "Thu", "Fri"] }); setBuilderStep(0); setView("create"); setLaunchError(null); setLaunchResult(null); };
  const builderStepTabs = ["Setup", "Emails", "Settings", "Review"];

  const handleLaunch = async () => {
    setLaunchError(null);
    setLaunchLoading(true);
    try {
      const hasInitialBody = (campaignForm.bodies || []).some((b) => String(b?.value ?? "").trim().length > 0);
      if (!hasInitialBody) {
        setLaunchError("Add body text for at least one initial email variant (or use Pick from saved with a row that includes body content).");
        setLaunchLoading(false);
        return;
      }
      const res = await api.instantly.campaigns.create({
        name: campaignForm.name,
        selectedLeadList: campaignForm.selectedLeadList || undefined,
        sendStart: campaignForm.sendStart,
        sendEnd: campaignForm.sendEnd,
        sendDays: campaignForm.sendDays,
        timezone: campaignForm.timezone,
        startDate: campaignForm.startDate || undefined,
        endDate: campaignForm.endDate || undefined,
        subjects: campaignForm.subjects,
        bodies: campaignForm.bodies,
        followups: campaignForm.followups,
        senderAccounts: campaignForm.senderAccounts,
        dailyLimit: campaignForm.dailyLimit,
        openTracking: campaignForm.openTracking,
      });
      const d = res?.data || res;
      setLaunchResult({
        leads_added: d?.leads_added ?? 0,
        leads_failed: d?.leads_failed ?? 0,
        last_error: d?.last_error,
        hadList: !!campaignForm.selectedLeadList,
      });
      setRefreshTrigger(t => t + 1);
      startNewCampaign();
      setView("list");
    } catch (e) {
      setLaunchError(e?.message || 'Failed to create campaign');
    } finally {
      setLaunchLoading(false);
    }
  };

  const handleToggleInstantlyStatus = async (c) => {
    if (!c.id) return;
    setTogglingCampaignId(c.id);
    try {
      if (c.status === 'active') {
        await api.instantly.campaigns.pause(c.id);
        setEmailCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, status: 'paused' } : x));
      } else if (c.status === 'paused' || c.status === 'draft') {
        await api.instantly.campaigns.activate(c.id);
        setEmailCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, status: 'active' } : x));
      }
      setRefreshTrigger(t => t + 1);
    } catch (e) {
      console.warn('Toggle Instantly campaign status:', e?.message);
    } finally {
      setTogglingCampaignId(null);
    }
  };

  const handleAddLeadsToCampaign = async (campaign, listId) => {
    const rows = await api.leads.list({ list_id: listId });
    const listRows = Array.isArray(rows) ? rows : [];
    const selectedListMeta = MOCK_LEAD_LISTS.find((l) => String(l.id) === String(listId));
    const leads = listRows
      .filter((row) => String(row.email || "").trim() !== "")
      .map((row) => {
        const p = parsePersonalisationJsonFromRow(row);
        const fullName = row.name || [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
        return {
          email: String(row.email || "").trim(),
          first_name: row.first_name || fullName.split(" ")[0] || "",
          last_name: row.last_name || fullName.split(" ").slice(1).join(" ") || "",
          company_name: row.company || "",
          personalization: p?.body || "",
          ...(p?.subject ? { payload: { subject: p.subject } } : {}),
        };
      });

    if (leads.length === 0) {
      setLaunchResult({
        action: "added",
        targetCampaign: campaign.name,
        sourceList: selectedListMeta?.name || "Selected list",
        leads_added: 0,
        leads_failed: 0,
        hadList: true,
      });
      return;
    }

    const result = await api.instantly.leads.bulk(leads, campaign.id);
    const allResults = Array.isArray(result?.results) ? result.results : [];
    const leadsAdded = allResults.filter((r) => r.success).length;
    const failed = allResults.filter((r) => !r.success);
    setLaunchResult({
      action: "added",
      targetCampaign: campaign.name,
      sourceList: selectedListMeta?.name || "Selected list",
      leads_added: leadsAdded,
      leads_failed: failed.length,
      last_error: failed[0]?.error || null,
      hadList: true,
    });
    setRefreshTrigger(t => t + 1);
  };

  const addLeadsBtn = (c) => (
    <button onClick={() => setAddLeadsCampaign(c)} style={{ padding: "7px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
    >+ Add Leads</button>
  );

  if (view === "list") {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        {addLeadsCampaign && <AddLeadsModal campaign={addLeadsCampaign} onClose={() => setAddLeadsCampaign(null)} accentColor={COLORS.accent} onSubmit={(listId) => handleAddLeadsToCampaign(addLeadsCampaign, listId)} />}
        {launchResult && (
          <div style={{ marginBottom: 16, padding: "12px 16px", background: launchResult.leads_added === 0 && launchResult.hadList ? COLORS.warnBg : "rgba(76,175,80,0.15)", border: `1px solid ${launchResult.leads_added === 0 && launchResult.hadList ? COLORS.warn + "55" : COLORS.accent + "55"}`, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontFamily: FONT, fontSize: 13, color: COLORS.text }}>
              {launchResult.action === "added"
                ? (launchResult.leads_added === 0
                    ? `No leads were added from ${launchResult.sourceList || "the selected list"} to ${launchResult.targetCampaign || "the campaign"}.`
                    : `${launchResult.leads_added} lead${launchResult.leads_added === 1 ? "" : "s"} added to ${launchResult.targetCampaign || "the campaign"} from ${launchResult.sourceList || "the selected list"}.`)
                : (launchResult.leads_added === 0 && launchResult.hadList
                    ? "Campaign created but no leads were added. Ensure leads in your list have valid email addresses."
                    : `Campaign created. ${launchResult.leads_added} lead${launchResult.leads_added === 1 ? "" : "s"} added.`)}
              {launchResult.leads_failed > 0 && launchResult.last_error && (
                <span style={{ color: COLORS.textDim, marginLeft: 8 }}>({launchResult.leads_failed} failed: {launchResult.last_error})</span>
              )}
            </span>
            <button onClick={() => setLaunchResult(null)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer" }}>Dismiss</button>
          </div>
        )}
        <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Cold Email <span style={{ color: COLORS.accent }}>Campaigns</span></h2>
            <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Manage your Instantly.ai campaigns</p>
          </div>
          <button onClick={startNewCampaign} style={{ padding: "12px 24px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ CREATE CAMPAIGN</button>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
          <StatCard label="Active Campaigns" value={MOCK_CAMPAIGNS.filter(c => c.status === "active").length} accent={COLORS.accent} />
          <StatCard label="Total Sent" value={MOCK_CAMPAIGNS.reduce((s, c) => s + c.sent, 0)} accent={COLORS.blue} />
          <StatCard label="Avg Open Rate" value={`${Math.round(MOCK_CAMPAIGNS.filter(c=>c.sent>0).reduce((s,c)=>s+(c.opened/c.sent)*100,0)/Math.max(MOCK_CAMPAIGNS.filter(c=>c.sent>0).length,1))}%`} accent={COLORS.accent} />
          <StatCard label="Avg Reply Rate" value={`${Math.round(MOCK_CAMPAIGNS.filter(c=>c.sent>0).reduce((s,c)=>s+(c.replied/c.sent)*100,0)/Math.max(MOCK_CAMPAIGNS.filter(c=>c.sent>0).length,1))}%`} accent={COLORS.warn} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {MOCK_CAMPAIGNS.map(c => (
            <div key={c.id} style={{ padding: "18px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ flex: 2 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>{c.leads} leads · Daily limit: {c.dailyLimit}{c.startDate ? ` · Started ${c.startDate}` : ""}</div>
              </div>
              <div style={{ width: 340, flexShrink: 0, display: "flex", gap: 20, justifyContent: "space-around" }}>
                {[{ label: "Sent", value: c.sent, color: COLORS.text }, { label: "Opened", value: c.opened, color: COLORS.blue }, { label: "Replied", value: c.replied, color: COLORS.accent }, { label: "Bounced", value: c.bounced, color: COLORS.danger }].map(stat => (
                  <div key={stat.label} style={{ width: 60, textAlign: "center" }}>
                    <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                    <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>{stat.label.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              <div style={{ flexShrink: 0, minWidth: 300, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                {addLeadsBtn(c)}
                {['draft', 'active', 'paused'].includes(c.status) ? (
                  <button
                    onClick={() => handleToggleInstantlyStatus(c)}
                    disabled={togglingCampaignId === c.id}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 20,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      cursor: togglingCampaignId === c.id ? "not-allowed" : "pointer",
                      opacity: togglingCampaignId === c.id ? 0.7 : 1, fontFamily: FONT, fontSize: 11, fontWeight: 600,
                    }}
                  >
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 16,
                      background: c.status !== "active" ? COLORS.warnBg : "transparent",
                      color: c.status !== "active" ? COLORS.warn : COLORS.textDim,
                      transition: "all 0.15s",
                    }}>⏸ Paused</span>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 16,
                      background: c.status === "active" ? COLORS.accentBg : "transparent",
                      color: c.status === "active" ? COLORS.accent : COLORS.textDim,
                      transition: "all 0.15s",
                    }}>▶ Running</span>
                  </button>
                ) : (
                  <span style={{ padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, background: COLORS.textDim + "22", color: COLORS.textDim, border: `1px solid ${COLORS.border}` }}>{c.status}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // CREATE CAMPAIGN FORM
  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setView("list")} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Create New <span style={{ color: COLORS.accent }}>Campaign</span></h2>
        </div>
        <div style={{ display: "flex", gap: 6, fontSize: 11, fontFamily: FONT }}>
          <span style={{ color: COLORS.danger }}>●</span><span style={{ color: COLORS.textDim }}>Required</span>
          <span style={{ color: COLORS.textDim, marginLeft: 8 }}>●</span><span style={{ color: COLORS.textDim }}>Optional</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {builderStepTabs.map((tab, i) => (<button key={tab} onClick={() => setBuilderStep(i)} style={{ flex: 1, padding: "10px 0", background: i === builderStep ? COLORS.accentBg : "transparent", border: `1px solid ${i === builderStep ? COLORS.accent+"55" : COLORS.border}`, borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600, color: i === builderStep ? COLORS.accent : COLORS.textDim, cursor: "pointer" }}>{i + 1}. {tab}</button>))}
      </div>
      {builderStep === 0 && <>{/* Campaign Basics */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Campaign Basics</div>
        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}><span style={{ color: COLORS.danger }}>●</span> CAMPAIGN NAME</label>
            <input value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} placeholder="My Cold Email Campaign" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}><span style={{ color: COLORS.danger }}>●</span> SENDER EMAIL ACCOUNTS</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 140, overflow: "auto" }}>
              {MOCK_SENDERS.map(sender => (
                <div key={sender.email} onClick={() => toggleSender(sender.email)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: "pointer", background: campaignForm.senderAccounts.includes(sender.email) ? COLORS.accentBg : "transparent" }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${campaignForm.senderAccounts.includes(sender.email) ? COLORS.accent : COLORS.borderActive}`, background: campaignForm.senderAccounts.includes(sender.email) ? COLORS.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: COLORS.bg, fontWeight: 700, flexShrink: 0 }}>{campaignForm.senderAccounts.includes(sender.email) && "✓"}</div>
                  <span style={{ fontSize: 12, color: COLORS.text, fontFamily: FONT }}>{sender.email}</span>
                  <span style={{ marginLeft: "auto", fontSize: 10, padding: "2px 8px", borderRadius: 10, background: sender.status === "active" ? COLORS.accentBg : COLORS.dangerBg, color: sender.status === "active" ? COLORS.accent : COLORS.danger, fontFamily: FONT, fontWeight: 500 }}>{sender.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={labelStyle}>LEAD LIST</label>
          <select value={campaignForm.selectedLeadList} onChange={e => setCampaignForm({ ...campaignForm, selectedLeadList: e.target.value })} style={selectStyle}>
            <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select a lead list...</option>
            {MOCK_LEAD_LISTS.map(l => (<option key={l.id} value={l.id} style={{ background: COLORS.surface, color: COLORS.text }}>{l.name} ({l.count} leads)</option>))}
          </select>
        </div>
      </div>
      </>}
      {builderStep === 1 && <>{/* Initial Email */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Initial Email</div>
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}><span style={{ color: COLORS.danger }}>●</span> SUBJECT LINES</label>
          {allSavedSubjectsForPicker.length > 0 && <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8 }}>Use &quot;Pick from saved&quot; to import from your Messaging Workshop playbooks (subject pool and per-email subjects).</div>}
          {campaignForm.subjects.map((subj, i) => (
            <SubjectVariantInput key={subj.id} subj={subj} index={i} campaignForm={campaignForm} setCampaignForm={setCampaignForm} inputStyle={inputStyle} savedSubjects={allSavedSubjectsForPicker} onSelectSaved={(text, playbook, variantLabel) => { const next = [...campaignForm.subjects]; const src = variantLabel ? `${variantLabel} · ${playbook}` : playbook; next[i] = { ...subj, value: text, mode: "saved", sourceLabel: src }; setCampaignForm({ ...campaignForm, subjects: next }); }} onSwitchToManual={() => { const next = [...campaignForm.subjects]; next[i] = { ...subj, mode: "manual" }; setCampaignForm({ ...campaignForm, subjects: next }); }} />
          ))}
          <button onClick={() => setCampaignForm(p => ({ ...p, subjects: [...p.subjects, { id: Date.now(), value: "", mode: "manual" }] }))} style={{ padding: "8px 14px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>+ Add Subject Variant</button>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}><span style={{ color: COLORS.danger }}>●</span> BODY</label>
          {allSavedEmails.length > 0 && <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8 }}>Use "Pick from saved" to import from your Messaging Workshop playbooks.</div>}
          {campaignForm.bodies.map((body, i) => (
            <BodyVariantInput key={body.id} body={body} index={i} campaignForm={campaignForm} setCampaignForm={setCampaignForm} inputStyle={inputStyle} savedEmails={allSavedEmails} onSelectSaved={(bodyValue, playbook, emailRow) => {
              const src = emailRow && typeof emailRow === "object" && "playbook" in emailRow ? emailRow : null;
              const badge = src?.label ? `${src.label} · ${playbook}` : playbook;
              const next = [...campaignForm.bodies];
              next[i] = { ...body, value: bodyValue, mode: "saved", sourceLabel: badge };
              const subjects = [...campaignForm.subjects];
              if (src && String(src.subject || "").trim() && subjects[i] && !String(subjects[i].value || "").trim()) {
                subjects[i] = { ...subjects[i], value: String(src.subject).trim(), mode: "saved", sourceLabel: badge };
              }
              setCampaignForm({ ...campaignForm, bodies: next, subjects });
            }} onSwitchToManual={() => { const next = [...campaignForm.bodies]; next[i] = { ...body, mode: "manual" }; setCampaignForm({ ...campaignForm, bodies: next }); }} />
          ))}
          <button onClick={() => setCampaignForm(p => ({ ...p, bodies: [...p.bodies, { id: Date.now(), value: "", mode: "manual" }] }))} style={{ padding: "8px 14px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>+ Add Body Variant</button>
        </div>
      </div>
      {/* Follow-ups */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Follow-up Emails (Optional)</div>
        {campaignForm.followups.map((followup, fi) => (
          <div key={followup.id} style={{ padding: "16px 20px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Follow-up #{fi + 1}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>Delay (days):</span>
                  <input value={followup.delay} onChange={e => { const next = [...campaignForm.followups]; next[fi] = { ...followup, delay: parseInt(e.target.value) || 0 }; setCampaignForm({ ...campaignForm, followups: next }); }} type="number" min="1" style={{ ...inputStyle, width: 60, textAlign: "center", padding: "6px 8px" }} />
                </div>
              </div>
            </div>
            <label style={labelStyle}>BODY</label>
            {followup.bodies.map((body, bi) => (
              <FollowupBodyVariantInput key={body.id} body={body} bodyIndex={bi} followupIndex={fi} campaignForm={campaignForm} setCampaignForm={setCampaignForm} inputStyle={inputStyle} savedEmails={allSavedEmails} onSelectSaved={(bodyValue, playbook, emailRow) => {
                const src = emailRow && typeof emailRow === "object" && "playbook" in emailRow ? emailRow : null;
                const badge = src?.label ? `${src.label} · ${playbook}` : playbook;
                const next = [...campaignForm.followups];
                const nb = [...next[fi].bodies];
                nb[bi] = { ...body, value: bodyValue, mode: "saved", sourceLabel: badge };
                next[fi] = { ...next[fi], bodies: nb };
                setCampaignForm({ ...campaignForm, followups: next });
              }} onSwitchToManual={() => { const next = [...campaignForm.followups]; const nb = [...next[fi].bodies]; nb[bi] = { ...body, mode: "manual" }; next[fi] = { ...next[fi], bodies: nb }; setCampaignForm({ ...campaignForm, followups: next }); }} />
            ))}
            <button onClick={() => { const next = [...campaignForm.followups]; next[fi] = { ...next[fi], bodies: [...next[fi].bodies, { id: Date.now(), value: "", mode: "manual" }] }; setCampaignForm({ ...campaignForm, followups: next }); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>+ Add Body Variant</button>
          </div>
        ))}
        <button onClick={() => setCampaignForm(p => ({ ...p, followups: [...p.followups, { id: Date.now(), delay: p.followups.length + 1, subjectLine: false, bodies: [{ id: 1, value: "", mode: "manual" }] }] }))} style={{ width: "100%", padding: "12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Follow-up Email</button>
      </div>
      </>}
      {builderStep === 2 && <>{/* Settings */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Campaign Settings</div>
        <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
          <div style={{ flex: 1 }}><label style={labelStyle}>DAILY LIMIT</label><input value={campaignForm.dailyLimit} onChange={e => setCampaignForm({ ...campaignForm, dailyLimit: e.target.value })} type="number" style={inputStyle} /></div>
          <div style={{ flex: 1 }}><label style={labelStyle}>START DATE</label><input value={campaignForm.startDate} onChange={e => setCampaignForm({ ...campaignForm, startDate: e.target.value })} type="date" style={inputStyle} /></div>
          <div style={{ flex: 1 }}><label style={labelStyle}>END DATE</label><input value={campaignForm.endDate} onChange={e => setCampaignForm({ ...campaignForm, endDate: e.target.value })} type="date" style={inputStyle} /></div>
        </div>
        <div onClick={() => setCampaignForm({ ...campaignForm, openTracking: !campaignForm.openTracking })} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", cursor: "pointer", marginBottom: 16 }}>
          <div style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${campaignForm.openTracking ? COLORS.accent : COLORS.borderActive}`, background: campaignForm.openTracking ? COLORS.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: COLORS.bg, fontWeight: 700 }}>{campaignForm.openTracking && "✓"}</div>
          <span style={{ fontSize: 13 }}>Enable Open Tracking</span>
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Sending Schedule</div>
        <div style={{ marginBottom: 12 }}><label style={labelStyle}>TIMEZONE</label>
          <select value={campaignForm.timezone} onChange={e => setCampaignForm({ ...campaignForm, timezone: e.target.value })} style={selectStyle}>
            {["Europe/London", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney"].map(tz => (<option key={tz} value={tz} style={{ background: COLORS.surface, color: COLORS.text }}>{tz}</option>))}
          </select>
        </div>
        <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
          <div style={{ flex: 1 }}><label style={labelStyle}>START TIME</label><input value={campaignForm.sendStart} onChange={e => setCampaignForm({ ...campaignForm, sendStart: e.target.value })} type="time" style={inputStyle} /></div>
          <div style={{ flex: 1 }}><label style={labelStyle}>END TIME</label><input value={campaignForm.sendEnd} onChange={e => setCampaignForm({ ...campaignForm, sendEnd: e.target.value })} type="time" style={inputStyle} /></div>
        </div>
        <div><label style={labelStyle}>DAYS TO SEND</label>
          <div style={{ display: "flex", gap: 6 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => { const active = campaignForm.sendDays.includes(day); return (<button key={day} onClick={() => toggleDay(day)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, border: `1px solid ${active ? COLORS.accent+"55" : COLORS.border}`, background: active ? COLORS.accentBg : "transparent", color: active ? COLORS.accent : COLORS.textDim, cursor: "pointer", transition: "all 0.15s" }}>{day}</button>); })}
          </div>
        </div>
      </div>
      </>}
      {builderStep === 3 && <div style={sectionStyle}>
        <div style={sectionTitle}>Review Campaign</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Campaign Name</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.name || "—"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Lead List</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{MOCK_LEAD_LISTS.find(l => l.id === campaignForm.selectedLeadList)?.name || "—"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Sender Accounts</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.senderAccounts.length} selected</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Subject Lines</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.subjects.filter(s => s.value).length} variant(s)</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Follow-ups</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.followups.length}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Daily Limit</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.dailyLimit}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Schedule</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.sendDays.join(", ")} · {campaignForm.sendStart}–{campaignForm.sendEnd}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Timezone</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignForm.timezone}</span></div>
        </div>
      </div>}
      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, paddingBottom: 32 }}>
        <button onClick={() => { if (builderStep === 0) setView("list"); else setBuilderStep(builderStep - 1); }} style={{ padding: "12px 24px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{builderStep === 0 ? "Cancel" : "← Back"}</button>
        {builderStep < 3 ? (
          <button onClick={() => setBuilderStep(builderStep + 1)} style={{ padding: "12px 28px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Next →</button>
        ) : (
          <>
            {launchError && <div style={{ flex: 1, color: COLORS.danger, fontSize: 12, marginRight: 12 }}>{launchError}</div>}
            <button onClick={handleLaunch} disabled={launchLoading} style={{ padding: "12px 28px", background: launchLoading ? COLORS.border : COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: launchLoading ? "not-allowed" : "pointer", opacity: launchLoading ? 0.8 : 1 }}>{launchLoading ? "Creating…" : "Create Campaign"}</button>
          </>
        )}
      </div>
    </div>
  );
}

function LinkedInStepMessageInput({ step, onUpdate, inputStyle, placeholder }) {
  const textRef = useRef(null);
  const insertVar = (v) => {
    const el = textRef.current;
    const curr = step.message || '';
    if (!el) {
      onUpdate(curr + v);
      return;
    }
    const start = el.selectionStart ?? curr.length;
    const end = el.selectionEnd ?? curr.length;
    const newVal = curr.substring(0, start) + v + curr.substring(end);
    onUpdate(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 0);
  };
  const LI_VARS = [['firstName', 'First Name'], ['lastName', 'Last Name'], ['companyName', 'Company'], ['personalization', 'Personalization']];
  return (
    <>
      <textarea ref={textRef} value={step.message || ''} onChange={e => onUpdate(e.target.value)} placeholder={placeholder} rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
        {LI_VARS.map(([k]) => (
          <button key={k} onClick={() => insertVar(`{{${k}}}`)} style={{ padding: '3px 8px', background: COLORS.blue + '12', border: `1px solid ${COLORS.blue}30`, borderRadius: 4, color: COLORS.blue, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: 'pointer' }}>{`{{${k}}}`}</button>
        ))}
      </div>
    </>
  );
}

function LinkedInCampaignsView() {
  const [view, setView] = useState("list");
  const [addLeadsCampaign, setAddLeadsCampaign] = useState(null);
  const [MOCK_CAMPAIGNS, setLiCampaigns] = useState([]);
  const [heyreachStats, setHeyreachStats] = useState(null);
  const [builderStep, setBuilderStep] = useState(0);
  const [campaignName, setCampaignName] = useState("");
  const [connectionDegree, setConnectionDegree] = useState("2nd_3rd");
  const [selectedLeadList, setSelectedLeadList] = useState("");
  const [sequenceSteps, setSequenceSteps] = useState([]);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [launchResult, setLaunchResult] = useState(null);
  const [liRefreshTrigger, setLiRefreshTrigger] = useState(0);
  const [togglingCampaignId, setTogglingCampaignId] = useState(null);
  const [liCampaignsReady, setLiCampaignsReady] = useState(false);

  useEffect(() => {
    setLiCampaignsReady(false);
    Promise.allSettled([
      api.heyreach.campaigns.list(),
      api.aimfox.campaigns.list(),
    ]).then(([heyRes, aimRes]) => {
      const heyItems = heyRes.status === 'fulfilled'
        ? (Array.isArray(heyRes.value) ? heyRes.value : heyRes.value?.items || heyRes.value?.campaigns || [])
        : [];
      const aimItems = aimRes.status === 'fulfilled'
        ? (Array.isArray(aimRes.value) ? aimRes.value : aimRes.value?.campaigns || [])
        : [];
      const heyNorm = heyItems.map(c => ({
        id: `heyreach-${c.id}`,
        name: c.name || c.campaignName || `Campaign ${c.id}`,
        status: (c.status || c.campaignStatus || '').toLowerCase() === 'active' ? 'active' : 'paused',
        leads: c.leadCount ?? c.leads ?? 0,
        sent: c.sentCount ?? c.sent ?? 0,
        accepted: c.acceptedCount ?? c.accepted ?? 0,
        replied: c.repliedCount ?? c.replied ?? 0,
        platform: 'HeyReach',
        _rawId: c.id,
      }));
      const aimNorm = aimItems.map(c => ({
        id: `aimfox-${c.id}`,
        name: c.name || `Campaign ${c.id}`,
        status: (c.state || '').toUpperCase() === 'ACTIVE' ? 'active' : 'paused',
        leads: c.audience_size ?? c.audienceSize ?? 0,
        sent: c.sent_count ?? c.sent ?? 0,
        accepted: c.accepted_count ?? c.accepted ?? 0,
        replied: c.replied_count ?? c.replied ?? 0,
        platform: 'AimFox',
        _rawId: c.id,
      }));
      const merged = [...heyNorm, ...aimNorm];
      setLiCampaigns(merged);
      const heyIds = heyNorm.map(c => c._rawId).filter(Boolean);
      if (heyIds.length) {
        api.heyreach.stats({ campaignIds: heyIds }).then(s => setHeyreachStats(s)).catch(() => {});
      }
    }).catch(() => {
      api.campaigns.list().then(data => {
        const camps = (Array.isArray(data) ? data : data.campaigns || []).filter(c => c.channel === 'linkedin');
        setLiCampaigns(camps.map(c => ({ ...c, id: c.id, leads: c.lead_count || c.leads || 0, sent: c.sent_count || c.sent || 0, accepted: c.accepted_count || c.accepted || 0, replied: c.replied_count || c.replied || 0, platform: c.platform || 'HeyReach', _rawId: c.id })));
      }).catch(() => {});
    }).finally(() => setLiCampaignsReady(true));
  }, [liRefreshTrigger]);

  const handleLaunch = async () => {
    setLaunchError(null);
    setLaunchLoading(true);
    try {
      const res = await api.aimfox.campaigns.create({
        name: campaignName.trim(),
        selectedLeadList: selectedLeadList || undefined,
      });
      const d = res?.data || res;
      setLaunchResult({
        leads_added: d?.leads_added ?? 0,
        leads_failed: d?.leads_failed ?? 0,
        last_error: d?.last_error,
        hadList: !!selectedLeadList,
      });
      setLiRefreshTrigger(t => t + 1);
      startNewCampaign();
      setView("list");
    } catch (e) {
      setLaunchError(e?.message || 'Failed to launch campaign');
    } finally {
      setLaunchLoading(false);
    }
  };

  const handleToggleStatus = async (c) => {
    if (!c._rawId || !['active', 'paused'].includes(c.status)) return;
    setTogglingCampaignId(c.id);
    try {
      if (c.platform === 'AimFox') {
        await api.aimfox.campaigns.update(c._rawId, { state: c.status === 'active' ? 'PAUSED' : 'ACTIVE' });
      } else if (c.platform === 'HeyReach') {
        if (c.status === 'active') {
          await api.heyreach.campaigns.pause(c._rawId);
        } else {
          await api.heyreach.campaigns.resume(c._rawId);
        }
      }
      setLiRefreshTrigger(t => t + 1);
    } catch (e) {
      console.warn('Toggle campaign status:', e?.message);
    } finally {
      setTogglingCampaignId(null);
    }
  };

  const addLeadsBtn = (c) => (
    <button onClick={() => setAddLeadsCampaign(c)} style={{ padding: "7px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.blue; e.currentTarget.style.color = COLORS.blue; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}
    >+ Add Leads</button>
  );

  const [LI_MOCK_LISTS, setLiLeadLists] = useState([]);
  useEffect(() => {
    api.leadLists.list().then(data => {
      const lists = Array.isArray(data) ? data : [];
      setLiLeadLists(lists.map(l => ({ id: l.id, name: l.name || 'Untitled', count: l.contact_count || l.contacts?.length || 0 })));
    }).catch(() => {});
  }, []);
  const liBuilderTabs = ["Setup", "Sequence", "Schedule", "Review"];
  const [liSendDays, setLiSendDays] = useState(["Mon","Tue","Wed","Thu","Fri"]);
  const [liTimezone, setLiTimezone] = useState("Europe/London");
  const [liSendStart, setLiSendStart] = useState("09:00");
  const [liSendEnd, setLiSendEnd] = useState("17:00");
  const startNewCampaign = () => { setCampaignName(""); setConnectionDegree("2nd_3rd"); setSelectedLeadList(""); setSequenceSteps([]); setLiSendDays(["Mon","Tue","Wed","Thu","Fri"]); setLiTimezone("Europe/London"); setLiSendStart("09:00"); setLiSendEnd("17:00"); setBuilderStep(0); setView("builder"); setLaunchError(null); setLaunchResult(null); };
  const addSeqStep = (type) => setSequenceSteps(p => [...p, { id: Date.now(), type, message: "", delay: 1 }]);
  const removeSeqStep = (id) => setSequenceSteps(p => p.filter(s => s.id !== id));
  const updateSeqStep = (id, field, val) => setSequenceSteps(p => p.map(s => s.id === id ? { ...s, [field]: val } : s));
  const toggleLiDay = (day) => setLiSendDays(p => p.includes(day) ? p.filter(d => d !== day) : [...p, day]);
  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const selectStyle = { ...inputStyle, appearance: "none", cursor: "pointer", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a7a8e' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" };
  const sectionStyle = { padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 };

  if (view === "list") return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      {addLeadsCampaign && <AddLeadsModal campaign={addLeadsCampaign} onClose={() => setAddLeadsCampaign(null)} accentColor={COLORS.blue} />}
      {launchResult && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: launchResult.leads_added === 0 && launchResult.hadList ? COLORS.warnBg : "rgba(76,175,80,0.15)", border: `1px solid ${launchResult.leads_added === 0 && launchResult.hadList ? COLORS.warn + "55" : COLORS.blue + "55"}`, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontFamily: FONT, fontSize: 13, color: COLORS.text }}>
            {launchResult.leads_added === 0 && launchResult.hadList
              ? "Campaign created in AimFox but no leads were added. Ensure leads in your list have LinkedIn URLs."
              : `Campaign created in AimFox. ${launchResult.leads_added} lead${launchResult.leads_added === 1 ? "" : "s"} added.`}
            {launchResult.leads_failed > 0 && launchResult.last_error && (
              <span style={{ color: COLORS.textDim, marginLeft: 8 }}>({launchResult.leads_failed} failed: {launchResult.last_error})</span>
            )}
          </span>
          <button onClick={() => setLaunchResult(null)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer" }}>Dismiss</button>
        </div>
      )}
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>LinkedIn <span style={{ color: COLORS.blue }}>Campaigns</span></h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Manage your HeyReach & AimFox campaigns</p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
        <StatCard label="Active Campaigns" value={MOCK_CAMPAIGNS.filter(c => c.status === "active").length} accent={COLORS.blue} />
        <StatCard label="Total Sent" value={MOCK_CAMPAIGNS.reduce((s, c) => s + c.sent, 0)} accent={COLORS.blue} />
        <StatCard label="Acceptance Rate" value={`${Math.round(MOCK_CAMPAIGNS.reduce((s,c)=>s+c.accepted,0)/Math.max(MOCK_CAMPAIGNS.reduce((s,c)=>s+c.sent,0),1)*100)}%`} accent={COLORS.accent} />
        <StatCard label="Reply Rate" value={`${Math.round(MOCK_CAMPAIGNS.reduce((s,c)=>s+c.replied,0)/Math.max(MOCK_CAMPAIGNS.reduce((s,c)=>s+c.sent,0),1)*100)}%`} accent={COLORS.warn} />
      </div>
      {!liCampaignsReady && (
        <div style={{ padding: "20px 24px", color: COLORS.textDim, fontFamily: FONT_BODY, fontSize: 13 }}>Loading campaigns…</div>
      )}
      {liCampaignsReady && MOCK_CAMPAIGNS.length === 0 && (
        <div style={{ padding: "28px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "center" }}>
          <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>No LinkedIn campaigns yet</div>
          <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textMuted, margin: 0, lineHeight: 1.5 }}>
            Connect HeyReach or AimFox under Settings → Integrations with your own API keys. Campaigns from your connected accounts will appear here.
          </p>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {MOCK_CAMPAIGNS.map(c => (
          <div key={c.id} style={{ padding: "18px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 2 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{c.name}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{c.leads} leads · {c.platform}</div>
            </div>
            <div style={{ width: 260, flexShrink: 0, display: "flex", gap: 20, justifyContent: "space-around" }}>
              {[{ label: "Sent", value: c.sent, color: COLORS.text }, { label: "Accepted", value: c.accepted, color: COLORS.blue }, { label: "Replied", value: c.replied, color: COLORS.accent }].map(stat => (
                <div key={stat.label} style={{ width: 60, textAlign: "center" }}>
                  <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>{stat.label.toUpperCase()}</div>
                </div>
              ))}
            </div>
            <div style={{ flexShrink: 0, minWidth: 300, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
              {addLeadsBtn(c)}
              {c._rawId && ['active', 'paused'].includes(c.status) ? (
                <button onClick={() => handleToggleStatus(c)} disabled={togglingCampaignId === c.id} style={{
                  display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 20,
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  cursor: togglingCampaignId === c.id ? "not-allowed" : "pointer",
                  opacity: togglingCampaignId === c.id ? 0.7 : 1, fontFamily: FONT, fontSize: 11, fontWeight: 600,
                }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 16,
                    background: c.status !== "active" ? COLORS.warnBg : "transparent",
                    color: c.status !== "active" ? COLORS.warn : COLORS.textDim,
                    transition: "all 0.15s",
                  }}>⏸ Paused</span>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 16,
                    background: c.status === "active" ? COLORS.blueBg : "transparent",
                    color: c.status === "active" ? COLORS.blue : COLORS.textDim,
                    transition: "all 0.15s",
                  }}>▶ Running</span>
                </button>
              ) : (
                <span style={{ padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, background: c.status === "active" ? COLORS.blueBg : COLORS.warnBg, color: c.status === "active" ? COLORS.blue : COLORS.warn, border: `1px solid ${c.status === "active" ? COLORS.blue+"33" : COLORS.warn+"33"}` }}>{c.status}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setView("list")} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Create LinkedIn <span style={{ color: COLORS.blue }}>Campaign</span></h2>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {liBuilderTabs.map((tab, i) => (<button key={tab} onClick={() => setBuilderStep(i)} style={{ flex: 1, padding: "10px 0", background: i === builderStep ? COLORS.blueBg : "transparent", border: `1px solid ${i === builderStep ? COLORS.blue+"55" : COLORS.border}`, borderRadius: 8, fontFamily: FONT, fontSize: 11, fontWeight: 600, color: i === builderStep ? COLORS.blue : COLORS.textDim, cursor: "pointer" }}>{i + 1}. {tab}</button>))}
      </div>
      {builderStep === 0 && <div style={sectionStyle}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Campaign Setup</div>
        <div style={{ marginBottom: 14 }}><label style={labelStyle}>CAMPAIGN NAME</label><input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="My LinkedIn Campaign" style={inputStyle} /></div>
        <div style={{ marginBottom: 14 }}><label style={labelStyle}>CONNECTION DEGREE</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{ val: "1st", label: "1st Degree" }, { val: "2nd_3rd", label: "2nd + 3rd Degree" }].map(opt => (<button key={opt.val} onClick={() => setConnectionDegree(opt.val)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, border: `1px solid ${connectionDegree === opt.val ? COLORS.blue+"55" : COLORS.border}`, background: connectionDegree === opt.val ? COLORS.blueBg : "transparent", color: connectionDegree === opt.val ? COLORS.blue : COLORS.textDim, cursor: "pointer" }}>{opt.label}</button>))}
          </div>
        </div>
        <div><label style={labelStyle}>LEAD LIST</label>
          <select value={selectedLeadList} onChange={e => setSelectedLeadList(e.target.value)} style={selectStyle}>
            <option value="" style={{ background: COLORS.surface, color: COLORS.textDim }}>Select a lead list...</option>
            {LI_MOCK_LISTS.map(l => (<option key={l.id} value={l.id} style={{ background: COLORS.surface, color: COLORS.text }}>{l.name} ({l.count} leads)</option>))}
          </select>
        </div>
      </div>}
      {builderStep === 1 && <div style={sectionStyle}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Sequence Builder</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {[{ type: "connection_request", label: "🤝 Connection Request" }, { type: "message", label: "💬 Message" }, { type: "delay", label: "⏳ Delay" }, { type: "engage", label: "👍 Engage" }].map(st => (
            <button key={st.type} onClick={() => addSeqStep(st.type)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, border: `1px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textMuted, cursor: "pointer" }}>{st.label}</button>
          ))}
        </div>
        {sequenceSteps.length === 0 && <div style={{ textAlign: "center", padding: "24px 0", color: COLORS.textDim, fontFamily: FONT_BODY, fontSize: 13 }}>Add steps above to build your sequence</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sequenceSteps.map((step, idx) => (
            <div key={step.id} style={{ padding: "12px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.blue, fontWeight: 700, marginTop: 2 }}>{idx + 1}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, marginBottom: step.type === "message" || step.type === "connection_request" || step.type === "delay" ? 8 : 0, textTransform: "capitalize" }}>{step.type.replace("_", " ")}</div>
                {step.type === "message" && <LinkedInStepMessageInput step={step} onUpdate={v => updateSeqStep(step.id, "message", v)} inputStyle={inputStyle} placeholder="Write your message... Use {{firstName}}, {{companyName}}" />}
                {step.type === "connection_request" && <LinkedInStepMessageInput step={step} onUpdate={v => updateSeqStep(step.id, "message", v)} inputStyle={inputStyle} placeholder="Write your connection request note... Use {{firstName}}, {{companyName}}" />}
                {step.type === "delay" && <div style={{ display: "flex", alignItems: "center", gap: 8 }}><input value={step.delay} onChange={e => updateSeqStep(step.id, "delay", parseInt(e.target.value) || 0)} type="number" min="1" style={{ ...inputStyle, width: 70, textAlign: "center" }} /><span style={{ fontSize: 12, color: COLORS.textDim }}>days</span></div>}
              </div>
              <button onClick={() => removeSeqStep(step.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>✕</button>
            </div>
          ))}
        </div>
      </div>}
      {builderStep === 2 && <div style={sectionStyle}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Schedule</div>
        <div style={{ marginBottom: 14 }}><label style={labelStyle}>DAYS TO SEND</label>
          <div style={{ display: "flex", gap: 6 }}>
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(day => { const active = liSendDays.includes(day); return (<button key={day} onClick={() => toggleLiDay(day)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, border: `1px solid ${active ? COLORS.blue+"55" : COLORS.border}`, background: active ? COLORS.blueBg : "transparent", color: active ? COLORS.blue : COLORS.textDim, cursor: "pointer" }}>{day}</button>); })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 20, marginBottom: 14 }}>
          <div style={{ flex: 1 }}><label style={labelStyle}>START TIME</label><input value={liSendStart} onChange={e => setLiSendStart(e.target.value)} type="time" style={inputStyle} /></div>
          <div style={{ flex: 1 }}><label style={labelStyle}>END TIME</label><input value={liSendEnd} onChange={e => setLiSendEnd(e.target.value)} type="time" style={inputStyle} /></div>
        </div>
        <div><label style={labelStyle}>TIMEZONE</label>
          <select value={liTimezone} onChange={e => setLiTimezone(e.target.value)} style={selectStyle}>
            {["Europe/London","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","Asia/Dubai","Asia/Singapore","Australia/Sydney"].map(tz => (<option key={tz} value={tz} style={{ background: COLORS.surface, color: COLORS.text }}>{tz}</option>))}
          </select>
        </div>
      </div>}
      {builderStep === 3 && <div style={sectionStyle}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Review Campaign</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Campaign Name</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{campaignName || "—"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Connection Degree</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{connectionDegree === "1st" ? "1st Degree" : "2nd + 3rd Degree"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Lead List</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{LI_MOCK_LISTS.find(l => l.id === selectedLeadList)?.name || "—"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Sequence Steps</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{sequenceSteps.length} step(s)</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Schedule</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{liSendDays.join(", ")} · {liSendStart}–{liSendEnd}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}><span style={{ color: COLORS.textDim, fontFamily: FONT, fontSize: 12 }}>Timezone</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{liTimezone}</span></div>
        </div>
      </div>}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, paddingBottom: 32 }}>
        <button onClick={() => { if (builderStep === 0) setView("list"); else setBuilderStep(builderStep - 1); }} style={{ padding: "12px 24px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{builderStep === 0 ? "Cancel" : "← Back"}</button>
        {builderStep < 3 ? (
          <button onClick={() => setBuilderStep(builderStep + 1)} style={{ padding: "12px 28px", background: COLORS.blue, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Next →</button>
        ) : (
          <>
            {launchError && <div style={{ flex: 1, color: COLORS.danger, fontSize: 12, marginRight: 12 }}>{launchError}</div>}
            <button onClick={handleLaunch} disabled={launchLoading || !campaignName?.trim()} style={{ padding: "12px 28px", background: launchLoading || !campaignName?.trim() ? COLORS.border : COLORS.blue, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: launchLoading || !campaignName?.trim() ? "not-allowed" : "pointer", opacity: launchLoading ? 0.8 : 1 }}>{launchLoading ? "Launching…" : "Launch Campaign 🚀"}</button>
          </>
        )}
      </div>
    </div>
  );
}




// Integration metadata: key, label, icon, desc, category, credential fields for the config modal

function IntegrationConfigModal({ intg, existingCredentials, onSave, onClose }) {
  const [form, setForm] = useState(existingCredentials);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(existingCredentials), [intg?.key, existingCredentials]);
  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text, fontWeight: 500, marginBottom: 6 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 24, maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 28 }}>{intg.icon}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Configure {intg.label}</div>
            <div style={{ fontSize: 12, color: COLORS.textDim }}>{intg.desc}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
          {(intg.credentialFields || []).map(f => (
            <div key={f.name}>
              <label style={labelStyle}>{f.label}</label>
              <input type={f.type || "text"} value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder || f.label} style={inputStyle} autoComplete="off" />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={async () => { setSaving(true); await onSave(form); setSaving(false); }} disabled={saving} style={{ padding: "10px 24px", background: saving ? COLORS.border : COLORS.accent, color: saving ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "Saving..." : "Save & Connect"}</button>
        </div>
      </div>
    </div>
  );
}

// orderType: lead_search = find leads, lead_enrichment = verify/enrich. costTier 1=cheapest, 5=most expensive
const INTEGRATIONS_META = [
  { key: "fathom", label: "Fathom", icon: "🎙️", desc: "AI meeting assistant — import call transcripts", category: "call_recording", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/fathom/connect" },
  { key: "unipile", label: "Unipile", icon: "💼", desc: "LinkedIn campaigns & actions — execute outreach on LinkedIn (not used in lead enrichment waterfall)", category: "enrichment", orderTypes: [], credentialFields: [{ name: "account_id", label: "Account ID", type: "text" }, { name: "access_token", label: "Access Token", type: "password" }, { name: "dsn", label: "DSN", type: "text", placeholder: "e.g. [REDACTED]" }], connectEndpoint: "/unipile/connect" },
  { key: "instantly", label: "Instantly", icon: "📧", desc: "Cold email campaigns", category: "outreach", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "campaign_id", label: "Campaign ID (optional)", type: "text" }], connectEndpoint: "/instantly/connect" },
  { key: "smartlead", label: "SmartLead", icon: "📬", desc: "Cold email campaigns", category: "outreach", hidden: true, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "workspace_id", label: "Workspace ID", type: "text" }], connectEndpoint: "/smartlead/connect" },
  { key: "heyreach", label: "HeyReach", icon: "🤝", desc: "LinkedIn outreach automation", category: "outreach", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "campaign_id", label: "Campaign ID", type: "text" }], connectEndpoint: "/heyreach/connect" },
  { key: "aimfox", label: "AimFox", icon: "🦊", desc: "LinkedIn outreach automation", category: "outreach", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "campaign_id", label: "Campaign ID", type: "text" }], connectEndpoint: "/aimfox/connect" },
  { key: "millionverifier", label: "MillionVerifier", icon: "✅", desc: "Email verification & validation — fast real-time API", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.0005/verify", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/millionverifier/connect" },
  { key: "icypeas", label: "IcyPeas", icon: "🧊", desc: "Find people & email search", category: "enrichment", orderTypes: ["lead_search", "lead_enrichment"], costLabel: "~$0.02/lead", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/icypeas/connect" },
  { key: "bettercontact", label: "BetterContact", icon: "✉️", desc: "Email verification & list cleaning", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.01/verify", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/bettercontact/connect" },
  { key: "zerobounce", label: "ZeroBounce", icon: "🛡️", desc: "Email verification & validation", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.008/verify", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/zerobounce/connect" },
  { key: "neverbounce", label: "NeverBounce", icon: "✉️", desc: "Email verification & deliverability", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.008/verify", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/neverbounce/connect" },
  { key: "ai_ark", label: "AI Ark", icon: "🦅", desc: "B2B data enrichment — people & company lookup", category: "enrichment", orderTypes: ["lead_search", "lead_enrichment"], costLabel: "~$0.02/lead", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/ai-ark/connect" },
  { key: "findy", label: "Findy", icon: "🔍", desc: "Lead discovery & enrichment — FindyMail IntelliMatch + employee search", category: "enrichment", orderTypes: ["lead_search", "lead_enrichment"], costLabel: "~$0.03/lead", costTier: 2, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/findy/connect" },
  { key: "findymail", label: "FindyMail", icon: "✉️", desc: "Email finder & verification — find by name+domain, verify address (same key as Findy)", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.01/lead", costTier: 1, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/findy/connect" },
  { key: "cleanlist", label: "Cleanlist", icon: "🧹", desc: "List cleaning & verification", category: "enrichment", orderTypes: ["lead_enrichment"], costLabel: "~$0.012/verify", costTier: 2, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/cleanlist/connect" },
  { key: "wiza", label: "Wiza", icon: "📊", desc: "Sales intelligence & lead data", category: "enrichment", orderTypes: ["lead_search"], costLabel: "~$0.04/lead", costTier: 4, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/wiza/connect" },
  { key: "leadsmagix", label: "Leads Magix", icon: "✨", desc: "B2B lead generation platform", category: "enrichment", orderTypes: ["lead_search"], costLabel: "~$0.025/lead", costTier: 3, credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "workspace_id", label: "Workspace ID", type: "text" }], connectEndpoint: "/leadsmagix/connect" },
  { key: "anthropic", label: "Anthropic", icon: "🧠", desc: "Claude — AI SDR & AI Council", category: "llm", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/anthropic/connect" },
  { key: "openai", label: "OpenAI", icon: "🤖", desc: "GPT — Chat completions & embeddings", category: "llm", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/openai/connect" },
  { key: "openrouter", label: "OpenRouter", icon: "🔀", desc: "Unified LLM gateway — access multiple models", category: "llm", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/openrouter/connect" },
  { key: "gemini", label: "Gemini", icon: "✨", desc: "Google Gemini — Multimodal AI", category: "llm", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/gemini/connect" },
  { key: "grok", label: "Grok", icon: "🪐", desc: "xAI Grok — Reasoning & chat", category: "llm", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/grok/connect" },
  { key: "calcom", label: "Cal.com", icon: "📆", desc: "Open-source scheduling", category: "calendar", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }], connectEndpoint: "/calcom/connect" },
  { key: "google_calendar", label: "Google Calendar", icon: "📆", desc: "Google Calendar sync (via Nylas)", category: "calendar", credentialFields: [], connectEndpoint: "/nylas/auth", nylasAuth: true },
];

function SettingsView({ enabledSolutions = [], setEnabledSolutions = () => {} }) {
  const [activeTab, setActiveTab] = useState("buyer_persona");
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [buyerPersonaAnswers, setBuyerPersonaAnswers] = useState({});
  const [buyerPersonaSubmitted, setBuyerPersonaSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [configModal, setConfigModal] = useState(null);
  const [showSkillForm, setShowSkillForm] = useState(false);
  const [customSkills, setCustomSkills] = useState([]);
  const [skillForm, setSkillForm] = useState({ name: "", icon: "⚡", prompt: "", data: [], output: "chat" });
  const [brandVoiceSchema, setBrandVoiceSchema] = useState([]); // from Postgres form_schemas

  useEffect(() => {
    async function loadSettings() {
      try {
        const [brandRes, buyerRes, schemaRes] = await Promise.all([
          api.settings.get('brand_voice'),
          api.settings.get('buyer_persona').catch(() => ({ settings: null })),
          api.settings.getFormSchema('brand_voice').catch(() => ({ schema: [] })),
        ]);
        if (brandRes?.settings) {
          setAnswers(brandRes.settings);
          setSubmitted(true);
        }
        if (buyerRes?.settings) {
          setBuyerPersonaAnswers(buyerRes.settings);
          setBuyerPersonaSubmitted(true);
        }
        if (schemaRes?.schema?.length) {
          setBrandVoiceSchema(schemaRes.schema);
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      } finally {
        setLoading(false);
      }
    }
    loadSettings();
  }, []);
  
  async function loadIntegrationStatus() {
    try {
      const res = await api.integrations.list();
      const status = res.integrations || {};
      setIntegrationStatus(status);
      // Live-verify connected integrations that support it
      const verifiable = ['instantly'];
      for (const key of verifiable) {
        if (status[key]) {
          api.integrations.check(key).then(r => {
            if (r && !r.ok) {
              setIntegrationStatus(prev => ({ ...prev, [key]: false, [`${key}_error`]: r.message }));
            }
          }).catch(() => {});
        }
      }
    } catch (err) {
      console.error("Failed to load integrations:", err);
    }
  }
  
  useEffect(() => {
    if (activeTab === "integrations") loadIntegrationStatus();
  }, [activeTab]);

  useEffect(() => {
    if (["brand_voice", "sending_accounts", "solutions"].includes(activeTab)) {
      setActiveTab("buyer_persona");
    }
  }, [activeTab]);
  
  async function saveBrandVoice() {
    setSaving(true);
    try {
      await api.settings.save('brand_voice', answers);
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to save brand voice:", err);
      alert("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const DEFAULT_BRAND_VOICE = [{ section: "About You", items: [{ key: "name", label: "What's your full name?", placeholder: "Andrew Dunn", type: "input" }] }];
  const brandVoiceQuestions = brandVoiceSchema?.length ? brandVoiceSchema : DEFAULT_BRAND_VOICE;

  const BUYER_PERSONA_SECTIONS = [
    { section: "Professional Background", items: [
      { key: "bp_title", label: "What is their typical job title?", placeholder: "e.g. VP of Sales, Head of Growth, CTO, Founder/CEO", type: "input" },
      { key: "bp_seniority", label: "What seniority level are they?", placeholder: "e.g. C-suite, VP/Director, Manager, Individual contributor", type: "input" },
      { key: "bp_industry", label: "What industry/industries do they work in?", placeholder: "e.g. B2B SaaS, FinTech, Professional Services, Healthcare Tech", type: "input" },
      { key: "bp_company_size", label: "What size company do they work at?", placeholder: "e.g. 50-500 employees, Series A-C, £5-50M revenue", type: "input" },
      { key: "bp_experience", label: "How many years of experience do they typically have?", placeholder: "e.g. 8-15 years in their field, 3-5 years in current role", type: "input" },
    ]},
    { section: "Day-to-Day Reality", items: [
      { key: "bp_responsibilities", label: "What are their core responsibilities?", placeholder: "e.g. Revenue targets, team management, pipeline generation, board reporting", type: "textarea" },
      { key: "bp_tools", label: "What tools do they use daily?", placeholder: "e.g. Salesforce, HubSpot, LinkedIn, Slack, spreadsheets for reporting", type: "textarea" },
      { key: "bp_reports_to", label: "Who do they report to and who reports to them?", placeholder: "e.g. Reports to CEO/CRO, manages team of 5-15 SDRs/AEs", type: "input" },
      { key: "bp_kpis", label: "What KPIs are they measured on?", placeholder: "e.g. Pipeline generated, meetings booked, revenue closed, CAC, conversion rates", type: "textarea" },
    ]},
    { section: "Pain Points & Frustrations", items: [
      { key: "bp_pain_primary", label: "What is their #1 pain point right now?", placeholder: "e.g. Outbound pipeline is drying up, reply rates below 2%, team spending too much time on manual prospecting", type: "textarea" },
      { key: "bp_pain_secondary", label: "What other frustrations do they deal with?", placeholder: "e.g. Too many tools, poor data quality, can't personalise at scale, expensive tech stack", type: "textarea" },
      { key: "bp_tried", label: "What have they already tried that didn't work?", placeholder: "e.g. Hired more SDRs, bought Clay/Apollo, outsourced to agencies, used generic email templates", type: "textarea" },
    ]},
    { section: "Decision Making", items: [
      { key: "bp_buying_triggers", label: "What triggers them to look for a solution?", placeholder: "e.g. Missed quarterly target, board pressure, competitor doing better, team burnout", type: "textarea" },
      { key: "bp_objections", label: "What are their common objections?", placeholder: "e.g. 'We've tried AI before', 'Budget is tight', 'Need to check with team', 'Timing isn't right'", type: "textarea" },
      { key: "bp_budget", label: "What's their typical budget authority?", placeholder: "e.g. Can approve up to £10K without sign-off, needs board approval above £50K", type: "input" },
      { key: "bp_timeline", label: "How quickly do they typically make decisions?", placeholder: "e.g. 2-4 weeks for <£10K, 2-3 months for larger investments", type: "input" },
    ]},
    { section: "Information & Trust", items: [
      { key: "bp_content", label: "Where do they consume content?", placeholder: "e.g. LinkedIn, industry podcasts, peer communities (Pavilion, SaaStr), newsletters", type: "textarea" },
      { key: "bp_trust", label: "What builds trust with them?", placeholder: "e.g. Case studies with specific numbers, peer recommendations, seeing the tool in action, ROI projections", type: "textarea" },
      { key: "bp_communication", label: "How do they prefer to be communicated with?", placeholder: "e.g. Direct and concise, data-driven, no fluff, respect their time, visual proof over written claims", type: "textarea" },
      { key: "bp_turnoff", label: "What turns them off immediately?", placeholder: "e.g. Generic outreach, pushy sales tactics, no social proof, overpromising, too much jargon", type: "textarea" },
    ]},
  ];

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text, fontWeight: 500, marginBottom: 6 };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          <span style={{ color: COLORS.accent }}>Settings</span>
        </h2>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[
          { key: "buyer_persona", label: "👤 Buyer Persona" },
          { key: "integrations", label: "🔌 Integrations" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Buyer Persona Tab - Edit mode */}
      {activeTab === "buyer_persona" && !buyerPersonaSubmitted && (
        <div>
          <p style={{ color: COLORS.textMuted, marginBottom: 24, fontSize: 13 }}>Define your ideal buyer's background, pain points, and decision-making process. This feeds into messaging personalisation, content targeting, and sales scripts.</p>
          {BUYER_PERSONA_SECTIONS.map(section => (
            <div key={section.section} style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12, padding: "8px 16px", background: COLORS.accentBg, borderRadius: 6, display: "inline-block" }}>{section.section.toUpperCase()}</div>
              <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
                {section.items.map((q, i) => (
                  <div key={q.key} style={{ marginBottom: i < section.items.length - 1 ? 18 : 0 }}>
                    <label style={labelStyle}>{q.label}</label>
                    {q.type === "textarea" ? (
                      <textarea value={buyerPersonaAnswers[q.key] || ""} onChange={e => setBuyerPersonaAnswers({ ...buyerPersonaAnswers, [q.key]: e.target.value })} placeholder={q.placeholder} rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
                    ) : (
                      <input value={buyerPersonaAnswers[q.key] || ""} onChange={e => setBuyerPersonaAnswers({ ...buyerPersonaAnswers, [q.key]: e.target.value })} placeholder={q.placeholder} style={inputStyle} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingBottom: 32 }}>
            <button onClick={async () => {
              setSaving(true);
              try {
                await api.settings.save('buyer_persona', buyerPersonaAnswers);
                setBuyerPersonaSubmitted(true);
              } catch (err) {
                console.error("Failed to save buyer persona:", err);
                alert("Failed to save. Please try again.");
              } finally {
                setSaving(false);
              }
            }} disabled={saving} style={{ padding: "14px 32px", background: saving ? COLORS.border : COLORS.accent, color: saving ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : "Save Buyer Persona →"}
            </button>
          </div>
        </div>
      )}

      {/* Buyer Persona Tab - Saved mode */}
      {activeTab === "buyer_persona" && buyerPersonaSubmitted && (
        <div>
          <div style={{ padding: "20px 24px", background: COLORS.accent + "08", border: `1px solid ${COLORS.accent}22`, borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 28 }}>✅</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Buyer Persona Active</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5 }}>Your ideal buyer profile is being used for messaging personalisation and ICP scoring.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
            <StatCard label="Questions Answered" value={`${Object.values(buyerPersonaAnswers).filter(v => v && v.trim()).length}/${BUYER_PERSONA_SECTIONS.reduce((s, sec) => s + sec.items.length, 0)}`} accent={COLORS.accent} />
            <StatCard label="Profile Strength" value={Object.values(buyerPersonaAnswers).filter(v => v && v.trim()).length >= 15 ? "Strong" : "Good"} accent={COLORS.accent} />
            <StatCard label="Active Modules" value="2" accent={COLORS.blue} />
          </div>
          {BUYER_PERSONA_SECTIONS.map(section => (
            <div key={section.section} style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>{section.section.toUpperCase()}</div>
              <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                {section.items.map((q, qi) => (
                  <div key={q.key} style={{ marginBottom: qi < section.items.length - 1 ? 12 : 0 }}>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 2 }}>{q.label}</div>
                    <div style={{ fontSize: 13, color: buyerPersonaAnswers[q.key] ? COLORS.text : COLORS.textDim }}>{buyerPersonaAnswers[q.key] || "\u2014"}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={() => setBuyerPersonaSubmitted(false)} style={{ padding: "12px 24px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 32 }}>Edit Buyer Persona</button>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === "integrations" && (
        <div>
          {configModal && (
            <IntegrationConfigModal
              intg={configModal}
              existingCredentials={configModal.existingCredentials || {}}
              onSave={async (credentials) => {
                try {
                  if (configModal.connectEndpoint) {
                    await api.integrations.connect(configModal.connectEndpoint, credentials);
                  } else {
                    await api.integrations.save(configModal.key, credentials);
                  }
                  await loadIntegrationStatus();
                  setConfigModal(null);
                } catch (err) {
                  console.error("Failed to save integration:", err);
                  alert(err.message || "Failed to save credentials. Please try again.");
                }
              }}
              onClose={() => setConfigModal(null)}
            />
          )}
          <p style={{ color: COLORS.textMuted, marginBottom: 20, fontSize: 13 }}>Connect your tools to power the platform. Call recording tools feed into content generation, outreach tools sync with campaigns. LLM and enrichment integrations are configured in Platform Admin.</p>

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>CALL RECORDING</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {INTEGRATIONS_META.filter(i => i.category === "call_recording" && !i.hidden).map(intg => {
              const connected = !!integrationStatus[intg.key];
              return (
                <div key={intg.key} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{intg.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{intg.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>{intg.desc}</div>
                    </div>
                  </div>
                  <button onClick={async () => { const data = await api.integrations.get(intg.key).catch(() => ({})); setConfigModal({ ...intg, existingCredentials: data.credentials_json || {} }); }} style={{
                    padding: "8px 18px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    background: connected ? "transparent" : COLORS.accent,
                    color: connected ? COLORS.accent : COLORS.bg,
                    border: connected ? `1px solid ${COLORS.accent}44` : "none",
                  }}>{connected ? "Connected ✓" : "Connect"}</button>
                </div>
              );
            })}
          </div>

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>OUTREACH & CAMPAIGNS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {INTEGRATIONS_META.filter(i => i.category === "outreach" && !i.hidden).map(intg => {
              const connected = !!integrationStatus[intg.key];
              const checkError = integrationStatus[`${intg.key}_error`];
              return (
                <div key={intg.key} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${checkError ? COLORS.danger + "44" : COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{intg.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{intg.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>{intg.desc}</div>
                      {checkError && <div style={{ fontSize: 10, color: COLORS.danger, marginTop: 2 }}>{checkError}</div>}
                    </div>
                  </div>
                  <button onClick={async () => { const data = await api.integrations.get(intg.key).catch(() => ({})); setConfigModal({ ...intg, existingCredentials: data.credentials_json || {} }); }} style={{
                    padding: "8px 18px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    background: checkError ? COLORS.danger + "15" : connected ? "transparent" : COLORS.accent,
                    color: checkError ? COLORS.danger : connected ? COLORS.accent : COLORS.bg,
                    border: checkError ? `1px solid ${COLORS.danger}44` : connected ? `1px solid ${COLORS.accent}44` : "none",
                  }}>{checkError ? "Re-connect" : connected ? "Connected ✓" : "Connect"}</button>
                </div>
              );
            })}
          </div>

          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>CALENDAR</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {INTEGRATIONS_META.filter(i => i.category === "calendar" && !i.hidden).map(intg => {
              const status = integrationStatus[intg.key];
              const connected = typeof status === "object" ? !!status?.connected : !!status;
              const signedIn = typeof status === "object" ? status?.signedIn : connected;
              const fullyConnected = connected && signedIn;
              return (
                <div key={intg.key} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{intg.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{intg.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>{intg.desc}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {intg.nylasAuth ? (
                      <button onClick={async () => {
                        if (fullyConnected) return;
                        try { const { redirectUrl } = await api.integrations.getNylasAuthUrl(); if (redirectUrl) window.location.href = redirectUrl; } catch (e) { alert(e.message || "Failed to start Google Calendar connection"); }
                      }} style={{
                        padding: "8px 18px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: fullyConnected ? "default" : "pointer",
                        background: fullyConnected ? "transparent" : "#4285F4",
                        color: fullyConnected ? COLORS.accent : "#fff",
                        border: fullyConnected ? `1px solid ${COLORS.accent}44` : "none",
                      }}>{fullyConnected ? "Connected ✓" : "Connect Google Calendar"}</button>
                    ) : (
                      <button onClick={async () => { const data = await api.integrations.get(intg.key).catch(() => ({})); setConfigModal({ ...intg, existingCredentials: data.credentials_json || {} }); }} style={{
                        padding: "8px 18px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        background: fullyConnected ? "transparent" : COLORS.accent,
                        color: fullyConnected ? COLORS.accent : COLORS.bg,
                        border: fullyConnected ? `1px solid ${COLORS.accent}44` : "none",
                      }}>{fullyConnected ? "Connected ✓" : "Connect"}</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "sending_accounts" && <SendingAccountsView />}

      {/* Skills & Plugins Tab */}
      {activeTab === "solutions" && (() => {
        const BUILTIN = [
          { key: "sol_assistant", icon: "🤖", name: "AI Assistant", desc: "Personal AI with full org context" },
          { key: "website_builder", icon: "🌐", name: "Website Builder", desc: "AI-powered website creation" },
          { key: "seo_analyzer", icon: "📈", name: "SEO Analyzer", desc: "Content optimization", soon: true },
          { key: "proposal_gen", icon: "📝", name: "Proposal Generator", desc: "Auto-generate client proposals", soon: true },
        ];
        const EMOJI_PRESETS = ["⚡", "🎯", "🔍", "📊", "🚀"];
        const DATA_OPTIONS = ["leads", "deals", "content", "analytics"];
        return (
          <div>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>BUILT-IN SOLUTIONS</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 28 }}>
              {BUILTIN.map(s => {
                const on = enabledSolutions.includes(s.key);
                return (
                  <div key={s.key} style={{ padding: "16px 18px", background: COLORS.surface, border: `1px solid ${on ? COLORS.accent + "44" : COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{s.icon}</span>
                      <div>
                        <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13, color: COLORS.text }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: COLORS.textDim }}>{s.desc}</div>
                      </div>
                    </div>
                    {s.soon ? (
                      <span style={{ fontSize: 10, fontFamily: FONT, color: COLORS.warn, background: COLORS.warnBg, padding: "3px 8px", borderRadius: 6, fontWeight: 600 }}>SOON</span>
                    ) : (
                      <button onClick={() => setEnabledSolutions(prev => on ? prev.filter(k => k !== s.key) : [...prev, s.key])} style={{ width: 38, height: 20, borderRadius: 10, border: "none", cursor: "pointer", background: on ? COLORS.accent : COLORS.border, position: "relative", transition: "background .2s" }}>
                        <span style={{ position: "absolute", top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: 8, background: on ? COLORS.bg : COLORS.textDim, transition: "left .2s" }} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.08em", fontWeight: 600 }}>CUSTOM SKILLS</div>
              <button onClick={() => setShowSkillForm(!showSkillForm)} style={{ padding: "6px 14px", background: showSkillForm ? COLORS.border : COLORS.accent, color: showSkillForm ? COLORS.textMuted : COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {showSkillForm ? "Cancel" : "+ Create Custom Skill"}
              </button>
            </div>
            {showSkillForm && (
              <div style={{ padding: 18, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Name</label>
                  <input value={skillForm.name} onChange={e => setSkillForm({ ...skillForm, name: e.target.value })} placeholder="My Custom Skill" style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Icon</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {EMOJI_PRESETS.map(em => (
                      <button key={em} onClick={() => setSkillForm({ ...skillForm, icon: em })} style={{ width: 32, height: 32, borderRadius: 6, border: skillForm.icon === em ? `2px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`, background: COLORS.bg, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{em}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>System Prompt</label>
                  <textarea value={skillForm.prompt} onChange={e => setSkillForm({ ...skillForm, prompt: e.target.value })} rows={3} placeholder="You are a helpful assistant that..." style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Data Access</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {DATA_OPTIONS.map(d => (
                      <label key={d} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: COLORS.text, fontFamily: FONT_BODY, cursor: "pointer" }}>
                        <input type="checkbox" checked={skillForm.data.includes(d)} onChange={() => setSkillForm({ ...skillForm, data: skillForm.data.includes(d) ? skillForm.data.filter(x => x !== d) : [...skillForm.data, d] })} /> {d}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Output Type</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["chat", "document"].map(t => (
                      <button key={t} onClick={() => setSkillForm({ ...skillForm, output: t })} style={{ padding: "6px 16px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none", background: skillForm.output === t ? COLORS.accent : COLORS.border, color: skillForm.output === t ? COLORS.bg : COLORS.textMuted }}>{t}</button>
                    ))}
                  </div>
                </div>
                <button onClick={() => { if (!skillForm.name.trim()) return; setCustomSkills(prev => [...prev, { ...skillForm, runs: 0 }]); setSkillForm({ name: "", icon: "⚡", prompt: "", data: [], output: "chat" }); setShowSkillForm(false); }} style={{ alignSelf: "flex-end", padding: "8px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Save Skill</button>
              </div>
            )}
            {customSkills.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {customSkills.map((sk, i) => (
                  <div key={i} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{sk.icon}</span>
                      <div>
                        <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13, color: COLORS.text }}>{sk.name}</div>
                        <div style={{ fontSize: 11, color: COLORS.textDim }}>{sk.runs} runs</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Brand Voice Tab */}
      {activeTab === "brand_voice" && !submitted && (
        <div>
          <p style={{ color: COLORS.textMuted, marginBottom: 24, fontSize: 13 }}>Answer these questions once to train the AI on your persona, tone, and style. This feeds into all content generation across the platform.</p>
          {brandVoiceQuestions.map(section => (
            <div key={section.section} style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12, padding: "8px 16px", background: COLORS.accentBg, borderRadius: 6, display: "inline-block" }}>{section.section.toUpperCase()}</div>
              <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
                {section.items.map((q, i) => (
                  <div key={q.key} style={{ marginBottom: i < section.items.length - 1 ? 18 : 0 }}>
                    <label style={labelStyle}>{q.label}</label>
                    {q.type === "input" ? (
                      <input value={answers[q.key] || ""} onChange={e => setAnswers({ ...answers, [q.key]: e.target.value })} placeholder={q.placeholder} style={inputStyle} />
                    ) : (
                      <textarea value={answers[q.key] || ""} onChange={e => setAnswers({ ...answers, [q.key]: e.target.value })} placeholder={q.placeholder} rows={q.type === "textarea_lg" ? 6 : 3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingBottom: 32 }}>
            <button onClick={saveBrandVoice} disabled={saving} style={{ padding: "14px 32px", background: saving ? COLORS.border : COLORS.accent, color: saving ? COLORS.textDim : COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : "Save Brand Voice →"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "brand_voice" && submitted && (
        <div>
          <div style={{ padding: "20px 24px", background: COLORS.accent + "08", border: `1px solid ${COLORS.accent}22`, borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 28 }}>✅</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Brand Voice Active</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5 }}>Your persona is being used across LinkedIn Content, Community Monitor, and Video Scripts.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
            <StatCard label="Questions Answered" value={`${Object.values(answers).filter(v => v && v.trim()).length}/${brandVoiceQuestions.reduce((s, sec) => s + sec.items.length, 0)}`} accent={COLORS.accent} />
            <StatCard label="Profile Strength" value={Object.values(answers).filter(v => v && v.trim()).length >= 15 ? "Strong" : "Good"} accent={COLORS.accent} />
            <StatCard label="Active Modules" value="3" accent={COLORS.blue} />
          </div>
          {brandVoiceQuestions.map(section => (
            <div key={section.section} style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>{section.section.toUpperCase()}</div>
              <div style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                {section.items.map((q, qi) => (
                  <div key={q.key} style={{ marginBottom: qi < section.items.length - 1 ? 12 : 0 }}>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 2 }}>{q.label}</div>
                    <div style={{ fontSize: 13, color: answers[q.key] ? COLORS.text : COLORS.textDim }}>{answers[q.key] || "\u2014"}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={() => setSubmitted(false)} style={{ padding: "12px 24px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 32 }}>Edit Brand Voice</button>
        </div>
      )}
    </div>
  );
}




function NicheResearcherView({ setActivePage, setIcpForm, setStep }) {
  const [view, setView] = useState("library"); // library, chat, detail
  const [selectedNiche, setSelectedNiche] = useState(null);
  const [savedNiches, setSavedNiches] = useState([]);

  useEffect(() => {
    api.niches.list().then(data => {
      const niches = Array.isArray(data) ? data : data.niches || [];
      setSavedNiches(niches.map(n => ({
        ...n,
        avgDeal: n.avg_deal ?? n.avgDeal ?? '',
        size: n.market_size ?? n.size ?? '',
        why: n.why_niche ?? n.why ?? '',
        savedDate: n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
        score: n.score ?? null,
        competition: n.competition ?? '',
      })));
    }).catch(() => {});
  }, []);
  const parseNicheToIcpForm = (niche) => {
    const aud = niche.audience || '';
    const regionText = `${niche.name || ''} ${aud} ${niche.channels || ''}`.toLowerCase();

    const INDUSTRY_PATTERNS = [
      { pattern: /\bb2b\s*saas\b/i, value: "B2B SaaS" },
      { pattern: /\bsaas\b/i, value: "SaaS" },
      { pattern: /\bfintech\b/i, value: "FinTech" },
      { pattern: /\binsurance\b/i, value: "Insurance" },
      { pattern: /\bproperty\s*management\b/i, value: "Property Management" },
      { pattern: /\breal\s*estate\b/i, value: "Real Estate" },
      { pattern: /\bhealthcare\b/i, value: "Healthcare" },
      { pattern: /\be-?commerce\b/i, value: "E-Commerce" },
      { pattern: /\bagenc(?:y|ies)\b/i, value: "Agencies" },
      { pattern: /\bconsultan(?:t|ts|cy|cies)\b/i, value: "Consulting" },
      { pattern: /\bmanufacturing\b/i, value: "Manufacturing" },
      { pattern: /\blogistics\b/i, value: "Logistics" },
      { pattern: /\beducation\b/i, value: "Education" },
      { pattern: /\blegal\b/i, value: "Legal" },
    ];
    let industry = "";
    for (const { pattern, value } of INDUSTRY_PATTERNS) {
      if (pattern.test(niche.name || '') || pattern.test(aud)) { industry = value; break; }
    }

    const ROLE_PRESETS = ["CEO", "CTO", "COO", "CFO", "CMO", "CRO", "VP Sales", "VP Growth", "VP Marketing", "VP Engineering", "VP Operations", "Head of Product", "Head of Partnerships", "Director of Sales", "Director of Marketing", "Founder"];
    const ROLE_ALIASES = [
      { pattern: /\bvp\s*(?:of\s+)?sales\b/i, value: "VP Sales" },
      { pattern: /\bvp\s*(?:of\s+)?growth\b/i, value: "VP Growth" },
      { pattern: /\bvp\s*(?:of\s+)?marketing\b/i, value: "VP Marketing" },
      { pattern: /\bvp\s*(?:of\s+)?(?:engineering|eng)\b/i, value: "VP Engineering" },
      { pattern: /\bvp\s*(?:of\s+)?(?:operations|ops)\b/i, value: "VP Operations" },
      { pattern: /\b(?:ops|operations)\s*managers?\b/i, value: "VP Operations" },
      { pattern: /\bhead\s+of\s+product\b/i, value: "Head of Product" },
      { pattern: /\bhead\s+of\s+partnerships\b/i, value: "Head of Partnerships" },
      { pattern: /\bdirector\s+of\s+sales\b/i, value: "Director of Sales" },
      { pattern: /\bdirector\s+of\s+marketing\b/i, value: "Director of Marketing" },
      { pattern: /\bfounders?\b/i, value: "Founder" },
      { pattern: /\bceos?\b/i, value: "CEO" },
      { pattern: /\bctos?\b/i, value: "CTO" },
      { pattern: /\bcoos?\b/i, value: "COO" },
      { pattern: /\bcfos?\b/i, value: "CFO" },
      { pattern: /\bcmos?\b/i, value: "CMO" },
      { pattern: /\bcros?\b/i, value: "CRO" },
    ];
    const rawRoles = [];
    const ROLE_PATTERNS = [
      /\bVPs?\s+of\s+(\w[\w\s&]*)/gi,
      /\bCROs?\b/gi, /\bCEOs?\b/gi, /\bCTOs?\b/gi, /\bCOOs?\b/gi, /\bCFOs?\b/gi, /\bCMOs?\b/gi,
      /\bManaging\s+Directors?\b/gi, /\bDirectors?\s+of\s+(\w[\w\s&]*)/gi,
      /\bHead\s+of\s+(\w[\w\s&]*)/gi,
      /\bAgency\s+(?:founders?|principals?|owners?)\b/gi,
      /\bFounders?\b/gi, /\bOwners?\b/gi,
      /\b(?:ops|operations)\s*managers?\b/gi,
    ];
    for (const rx of ROLE_PATTERNS) {
      const m = aud.matchAll(rx);
      for (const match of m) {
        let role = match[0].replace(/\s+/g, ' ').trim();
        if (role.length > 30) role = role.slice(0, 30).trim();
        if (!rawRoles.some(r => r.toLowerCase() === role.toLowerCase())) rawRoles.push(role);
      }
    }
    const roles = [];
    for (const raw of rawRoles) {
      for (const { pattern, value } of ROLE_ALIASES) {
        if (pattern.test(raw) && !roles.includes(value)) { roles.push(value); break; }
      }
    }
    if (roles.length === 0) roles.push("CEO", "CTO", "Founder");

    const EMPLOYEE_RANGES = ["1-10", "11-50", "51-200", "201-500", "501-1,000", "1,001-5,000", "5,000+"];
    const SIZE_BUCKETS = [
      { min: 1, max: 10, label: "1-10" },
      { min: 11, max: 50, label: "11-50" },
      { min: 51, max: 200, label: "51-200" },
      { min: 201, max: 500, label: "201-500" },
      { min: 501, max: 1000, label: "501-1,000" },
      { min: 1001, max: 5000, label: "1,001-5,000" },
      { min: 5001, max: Infinity, label: "5,000+" },
    ];
    let employeeSizes = [];
    const sizeMatch = aud.match(/(\d[\d,]*)\s*[-–to]+\s*(\d[\d,]*)\s*(?:employees?|staff|people)/i);
    if (sizeMatch) {
      const lo = parseInt(sizeMatch[1].replace(/,/g, ''), 10);
      const hi = parseInt(sizeMatch[2].replace(/,/g, ''), 10);
      for (const b of SIZE_BUCKETS) {
        if (b.max >= lo && b.min <= hi) employeeSizes.push(b.label);
      }
    }
    if (employeeSizes.length === 0) employeeSizes.push("51-200");
    employeeSizes = employeeSizes.filter(s => EMPLOYEE_RANGES.includes(s));
    if (employeeSizes.length === 0) employeeSizes = ["51-200"];

    const REGION_PRESETS = ["North America", "Asia Pacific", "Europe", "MENA", "UK & Ireland", "Latin America", "DACH", "Nordics", "Australia & NZ", "Africa"];
    const REGION_PATTERNS = [
      { pattern: /\b(?:uk|united kingdom|britain|gb|ireland)\b/i, value: "UK & Ireland" },
      { pattern: /\b(?:us|usa|america|north america)\b/i, value: "North America" },
      { pattern: /\b(?:europe|emea|eu)\b/i, value: "Europe" },
      { pattern: /\b(?:dach|germany|austria|switzerland)\b/i, value: "DACH" },
      { pattern: /\b(?:mena|middle east|uae|gulf)\b/i, value: "MENA" },
      { pattern: /\b(?:australia|new zealand|anz)\b/i, value: "Australia & NZ" },
      { pattern: /\b(?:apac|asia)\b/i, value: "Asia Pacific" },
      { pattern: /\b(?:latin america|latam|brazil|mexico)\b/i, value: "Latin America" },
      { pattern: /\b(?:nordics|scandinavia|sweden|norway|denmark|finland)\b/i, value: "Nordics" },
      { pattern: /\bafrica\b/i, value: "Africa" },
    ];
    const regionSet = new Set();
    for (const { pattern, value } of REGION_PATTERNS) {
      if (pattern.test(regionText)) regionSet.add(value);
    }
    const regions = regionSet.size > 0 ? [...regionSet].filter(r => REGION_PRESETS.includes(r)) : ["North America", "Europe"];

    const STOP = new Set(["the", "for", "and", "a", "an", "of", "in", "at", "to", "is", "by", "on", "or", "with", "your", "that", "this", "from"]);
    const keywords = (niche.name || '').split(/[\s,—–-]+/)
      .map(w => w.replace(/[^a-zA-Z0-9]/g, '').trim())
      .filter(w => w.length > 2 && !STOP.has(w.toLowerCase()))
      .slice(0, 6)
      .join(', ');

    return {
      listName: niche.name || '',
      industry,
      keywords,
      employeeSizes,
      regions,
      roles,
      maxLeads: '',
    };
  };

  const [chatMessages, setChatMessages] = useState([{ role: "agent", text: "Let's research a new niche. What are your core skills and expertise?" }]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [nichesGenerated, setNichesGenerated] = useState(false);
  const [discoveredNiches, setDiscoveredNiches] = useState([]);
  const [chatError, setChatError] = useState(null);
  const chatEndRef = React.useRef(null);

  React.useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [chatMessages, isTyping]);

  const sendMessage = async () => {
    if (!userInput.trim() || isTyping) return;
    const msg = userInput.trim();
    const nm = [...chatMessages, { role: "user", text: msg }];
    setChatMessages(nm); setUserInput(""); setIsTyping(true); setChatError(null);
    try {
      const res = await api.niches.chat({ message: msg, messages: chatMessages });
      const agentText = res.agentText || "I couldn't generate a response. Please try again.";
      setChatMessages([...nm, { role: "agent", text: agentText }]);
      if (res.niches && Array.isArray(res.niches) && res.niches.length > 0) {
        setDiscoveredNiches(res.niches.map((n, i) => ({ ...n, id: `gen_${Date.now()}_${i}` })));
        setNichesGenerated(true);
      }
    } catch (err) {
      const errMsg = err?.message || err?.error || "Failed to reach AI. Check your Anthropic API key in Settings → Integrations.";
      setChatError(errMsg);
      setChatMessages([...nm, { role: "agent", text: `⚠️ ${errMsg}` }]);
    }
    setIsTyping(false);
  };

  const saveNiche = async (niche) => {
    if (savedNiches.find(sn => sn.name === niche.name)) return;
    const optimistic = { ...niche, id: `sn_${Date.now()}`, savedDate: "Just now" };
    setSavedNiches(prev => [optimistic, ...prev]);
    try {
      const saved = await api.niches.create({
        name: niche.name,
        audience: niche.audience || null,
        market_size: niche.size || niche.market_size || null,
        competition: niche.competition || null,
        demand: niche.demand || null,
        avg_deal: niche.avgDeal || niche.avg_deal || null,
        positioning: niche.positioning || null,
        score: typeof niche.score === 'number' ? niche.score : null,
        monetisation: niche.monetisation || null,
        advantage: niche.advantage || null,
        channels: niche.channels || null,
        why_niche: niche.why || niche.why_niche || null,
      });
      setSavedNiches(prev => prev.map(sn => sn.id === optimistic.id ? { ...saved, avgDeal: saved.avg_deal ?? '', size: saved.market_size ?? '', why: saved.why_niche ?? saved.why ?? '', savedDate: "Just now", competition: saved.competition ?? '' } : sn));
    } catch (err) {
      console.error('Failed to save niche:', err);
    }
  };

  const getScoreColor = (s) => s >= 90 ? COLORS.accent : s >= 80 ? COLORS.blue : COLORS.warn;
  const getCompColor = (c) => c === "Very Low" || c === "Low" ? COLORS.accent : c === "Medium" ? COLORS.warn : COLORS.danger;

  // DETAIL VIEW
  if (view === "detail" && selectedNiche) {
    const isSaved = savedNiches.some(sn => sn.name === selectedNiche.name);
    const size = selectedNiche.size ?? selectedNiche.market_size ?? '';
    const why = selectedNiche.why ?? selectedNiche.why_niche ?? '';
    const isIncomplete = !size || !selectedNiche.competition || !why;
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        {isIncomplete && (
          <div style={{ padding: "12px 16px", background: COLORS.warnBg, border: `1px solid ${COLORS.warn}33`, borderRadius: 8, marginBottom: 16, fontSize: 12, color: COLORS.warn }}>
            Some details are missing. Consider re-running niche research to get a full analysis.
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => setView(nichesGenerated ? "chat" : "library")} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{selectedNiche.name}</h2>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: getScoreColor(selectedNiche.score) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 18, fontWeight: 700, color: getScoreColor(selectedNiche.score) }}>{selectedNiche.score}</div>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <StatCard label="Market Size" value={selectedNiche.size ?? selectedNiche.market_size ?? ''} accent={COLORS.accent} />
          <StatCard label="Competition" value={selectedNiche.competition} accent={getCompColor(selectedNiche.competition)} />
          <StatCard label="Demand" value={selectedNiche.demand} accent={COLORS.blue} />
          <StatCard label="Avg Deal" value={selectedNiche.avgDeal ?? selectedNiche.avg_deal ?? ''} accent={COLORS.accent} />
        </div>
        <div style={{ padding: "16px 20px", background: COLORS.accent + "08", border: `1px solid ${COLORS.accent}22`, borderRadius: 10, marginBottom: 20 }}>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>WHY THIS NICHE</div>
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>{selectedNiche.why ?? selectedNiche.why_niche ?? ''}</div>
        </div>
        {[{ label: "TARGET AUDIENCE", value: selectedNiche.audience, color: COLORS.blue }, { label: "POSITIONING", value: selectedNiche.positioning, color: COLORS.accent }, { label: "MONETISATION", value: selectedNiche.monetisation, color: "#7B61FF" }, { label: "UNFAIR ADVANTAGE", value: selectedNiche.advantage, color: COLORS.accent }, { label: "CHANNELS", value: selectedNiche.channels, color: COLORS.blue }].map((f, i) => (
          <div key={i} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 8, borderLeft: `3px solid ${f.color}` }}>
            <div style={{ fontFamily: FONT, fontSize: 9, color: f.color, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>{f.label}</div>
            <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>{f.value}</div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          {!isSaved && <button onClick={() => saveNiche(selectedNiche)} style={{ padding: "12px 24px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>💾 Save Niche</button>}
          {isSaved && <span style={{ padding: "12px 24px", background: COLORS.accentBg, color: COLORS.accent, borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, border: `1px solid ${COLORS.accent}22` }}>✓ Saved</span>}
          <button onClick={async () => {
            if (!isSaved) await saveNiche(selectedNiche);
            const parsed = parseNicheToIcpForm(selectedNiche);
            setIcpForm(prev => ({ ...prev, ...parsed }));
            setStep(0);
            setActivePage("leads_mgmt");
          }} style={{ padding: "12px 24px", background: COLORS.blue, color: "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>⚡ Generate Lead List →</button>
        </div>
      </div>
    );
  }

  // LIBRARY VIEW
  if (view === "library") {
    const nichesWithScore = savedNiches.filter(n => n.score != null && typeof n.score === 'number');
    const topByScore = [...savedNiches].filter(n => n.score != null).sort((a, b) => b.score - a.score)[0];
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div><h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>Saved <span style={{ color: COLORS.accent }}>Niches</span></h2><p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Your researched and saved niche profiles</p></div>
          <button onClick={() => { setView("chat"); setNichesGenerated(false); setDiscoveredNiches([]); setChatError(null); setChatMessages([{ role: "agent", text: "Let's research a new niche. What are your core skills and expertise?" }]); }}
            style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Research New Niche</button>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <StatCard label="Saved Niches" value={savedNiches.length} accent={COLORS.accent} />
          <StatCard label="Avg Score" value={nichesWithScore.length ? Math.round(nichesWithScore.reduce((s, n) => s + n.score, 0) / nichesWithScore.length) : 0} accent={COLORS.accent} />
          <StatCard label="Top Niche" value={topByScore ? `${topByScore.score}/100` : "—"} accent={COLORS.accent} />
        </div>
        {savedNiches.length === 0 ? (
          <div style={{ padding: "60px 40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.2 }}>🎯</div>
            <div style={{ fontSize: 13, color: COLORS.textDim }}>No saved niches yet. Click "+ Research New Niche" to get started.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {savedNiches.map(niche => (
              <div key={niche.id} onClick={() => { setSelectedNiche(niche); setView("detail"); }} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "44"} onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: (niche.score != null ? getScoreColor(niche.score) : COLORS.textDim) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 16, fontWeight: 700, color: niche.score != null ? getScoreColor(niche.score) : COLORS.textDim, flexShrink: 0 }}>{niche.score != null ? niche.score : '—'}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{niche.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim }}>{[niche.audience, niche.size || niche.market_size, niche.avgDeal ? `Avg deal: ${niche.avgDeal}` : ''].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 500, background: (niche.competition ? getCompColor(niche.competition) : COLORS.textDim) + "10", color: niche.competition ? getCompColor(niche.competition) : COLORS.textDim, border: `1px solid ${(niche.competition ? getCompColor(niche.competition) : COLORS.textDim)}22` }}>{niche.competition || '—'}</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{niche.savedDate}</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>→</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // CHAT + DASHBOARD VIEW
  return (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ width: 420, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => setView("library")} style={{ padding: "2px 6px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontSize: 10, cursor: "pointer" }}>←</button>
            <span style={{ fontSize: 14 }}>🎯</span>
            <div><div style={{ fontWeight: 600, fontSize: 13 }}>Niche Researcher</div><div style={{ fontSize: 10, color: COLORS.textDim }}>{nichesGenerated ? `${discoveredNiches.length} niches found` : "AI-powered discovery"}</div></div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "88%", padding: "10px 14px", borderRadius: 12, background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface, border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`, borderBottomRightRadius: msg.role === "user" ? 4 : 12, borderBottomLeftRadius: msg.role === "agent" ? 4 : 12 }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>NICHE RESEARCHER</div>}
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {isTyping && <div style={{ display: "flex" }}><div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}><ProgressDots active={true} /></div></div>}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) sendMessage(); }} placeholder={nichesGenerated ? "Refine niches..." : "Type your answer..."} style={{ flex: 1, padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }} disabled={isTyping} />
          <button onClick={sendMessage} disabled={isTyping || !userInput.trim()} style={{ padding: "10px 18px", background: userInput.trim() && !isTyping ? COLORS.accent : COLORS.border, color: userInput.trim() && !isTyping ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: userInput.trim() && !isTyping ? "pointer" : "default" }}>Send</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        {!nichesGenerated ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}><div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>🎯</div><div style={{ fontFamily: FONT, fontSize: 15, color: COLORS.textDim }}>Niches will appear here</div></div>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>Discovered <span style={{ color: COLORS.accent }}>Niches</span></h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {discoveredNiches.map(niche => {
                const isSaved = savedNiches.some(sn => sn.name === niche.name);
                return (
                  <div key={niche.id} style={{ padding: "20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, flex: 1, marginRight: 8 }}>{niche.name}</div>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: getScoreColor(niche.score) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 18, fontWeight: 700, color: getScoreColor(niche.score), flexShrink: 0 }}>{niche.score}</div>
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10 }}>{niche.audience}</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 9, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textDim }}>{niche.size}</span>
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 9, background: getCompColor(niche.competition) + "10", color: getCompColor(niche.competition) }}>{niche.competition}</span>
                      <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 9, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textDim }}>{niche.avgDeal}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => { setSelectedNiche(niche); setView("detail"); }} style={{ flex: 1, padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>View Details</button>
                      {!isSaved ? (
                        <button onClick={() => saveNiche(niche)} style={{ padding: "6px 12px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>💾 Save</button>
                      ) : (
                        <span style={{ padding: "6px 12px", background: COLORS.accentBg, color: COLORS.accent, borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, border: `1px solid ${COLORS.accent}22` }}>✓ Saved</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SalesScriptGeneratorView() {
  const [view, setView] = useState("library");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [chatMessages, setChatMessages] = useState([{ role: "agent", text: "Let's build your sales script. What type do you need?" }]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [scriptType, setScriptType] = useState(null);
  const [step, setStep] = useState(0);
  const [generatedScript, setGeneratedScript] = useState(null);
  const chatEndRef = React.useRef(null);
  const [savedScripts, setSavedScripts] = useState([]);

  useEffect(() => {
    api.salesScripts.list().then(data => {
      const scripts = Array.isArray(data) ? data : data.sales_scripts || [];
      setSavedScripts(scripts.map(s => ({ ...s, type: s.script_type || s.type || '', created: s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '', sections: Object.values(s.sections_json || {}).filter(Boolean).length, uses: s.use_count ?? s.uses ?? 0 })));
    }).catch(() => {});
  }, []);

  React.useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [chatMessages, isTyping]);

  const SCRIPT_TYPES = ["Cold Call", "Discovery Call", "Sales Call", "Follow-up Call", "Objection Handling"];
  const QUESTIONS = ["Who are you calling? Role, company type, what they care about.", "What are you selling? One-liner outcome.", "Top 2-3 objections?", "Desired outcome? Book meeting, close, referral?", "Tone — assertive, consultative, casual?"];

  const [scriptError, setScriptError] = useState(null);
  const [conversationAnswers, setConversationAnswers] = useState({});

  const selectType = (type) => { setScriptType(type); setChatMessages(prev => [...prev, { role: "user", text: type }, { role: "agent", text: `${type} — got it. ${QUESTIONS[0]}` }]); setStep(1); };

  const sendMessage = async () => {
    if (!userInput.trim() || isTyping) return;
    const nm = [...chatMessages, { role: "user", text: userInput }]; setChatMessages(nm); setUserInput(""); setIsTyping(true);
    setScriptError(null);

    // Collect answers as user progresses through questions
    const answerKeys = ['audience', 'product', 'objections', 'outcome', 'tone'];
    if (step > 0 && step <= QUESTIONS.length) {
      setConversationAnswers(prev => ({ ...prev, [answerKeys[step - 1]]: userInput }));
    }

    if (step < QUESTIONS.length) {
      await new Promise(r => setTimeout(r, 400));
      setChatMessages([...nm, { role: "agent", text: QUESTIONS[step] }]); setStep(step + 1);
      setIsTyping(false);
    } else if (!generatedScript) {
      // All questions answered — generate via AI
      const answers = { ...conversationAnswers, [answerKeys[step - 1]]: userInput };
      setChatMessages([...nm, { role: "agent", text: "Generating your script with AI — this takes a few seconds..." }]);
      try {
        const result = await api.salesScripts.generate({
          type: scriptType,
          audience: answers.audience || '',
          product: answers.product || '',
          objections: answers.objections || '',
          outcome: answers.outcome || '',
          tone: answers.tone || 'consultative',
        });
        const sections = result.sections || result;
        setGeneratedScript(sections);
        setChatMessages(prev => [...prev, { role: "agent", text: "Script ready! Preview on the right. Revise via chat or hit Save to name it and add to your library." }]);
      } catch (err) {
        const msg = err.message || 'Failed to generate script';
        setScriptError(msg);
        let userMsg = `Error generating script: ${msg}`;
        if (msg.includes('API key') || msg.includes('not configured')) userMsg += '\n\nAdd your Anthropic API key in Settings > Integrations > LLM.';
        else if (msg.includes('credit') || msg.includes('quota')) userMsg += '\n\nYour Anthropic account may need credits.';
        setChatMessages(prev => [...prev, { role: "agent", text: userMsg }]);
      }
      setIsTyping(false);
    } else {
      // Script already generated — revision mode
      await new Promise(r => setTimeout(r, 600));
      setChatMessages([...nm, { role: "agent", text: "Updated! Check the revised section." }]);
      setIsTyping(false);
    }
  };

  const [savedScriptId, setSavedScriptId] = useState(null);

  // Auto-save for script section edits
  const scriptAutoSave = useAutoSave(async (scriptId, sections) => {
    await api.salesScripts.update(scriptId, { sections_json: sections });
  }, 800);

  const updateScriptSection = (key, value) => {
    setGeneratedScript(prev => {
      const next = { ...prev, [key]: value };
      if (savedScriptId) scriptAutoSave.trigger(savedScriptId, next);
      return next;
    });
  };

  const handleSave = async () => {
    if (!saveName.trim() || !generatedScript) return;
    try {
      const result = await api.salesScripts.create({
        name: saveName,
        type: scriptType || 'cold_call',
        sections_json: generatedScript,
        audience: conversationAnswers.audience || null,
      });
      setSavedScriptId(result.id);
      setSavedScripts(prev => [{ id: result.id, name: saveName, type: scriptType, created: "Just now", sections: 5, uses: 0, audience: conversationAnswers.audience || "—" }, ...prev]);
    } catch (_) {
      setSavedScripts(prev => [{ id: `ss_${Date.now()}`, name: saveName, type: scriptType, created: "Just now", sections: 5, uses: 0, audience: "—" }, ...prev]);
    }
    setShowSaveModal(false); setSaveName(""); setView("library");
  };

  // LIBRARY
  if (view === "library") {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div><h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>Sales <span style={{ color: COLORS.accent }}>Scripts</span></h2><p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Your saved scripts library</p></div>
          <button onClick={() => { setView("generator"); setScriptType(null); setStep(0); setGeneratedScript(null); setChatMessages([{ role: "agent", text: "Let's build your sales script. What type do you need?" }]); }} style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ New Script</button>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Scripts" value={savedScripts.length} accent={COLORS.accent} />
          <StatCard label="Total Uses" value={savedScripts.reduce((s, sc) => s + sc.uses, 0)} accent={COLORS.blue} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {savedScripts.map(sc => (
            <div key={sc.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{sc.name}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: COLORS.blue + "15", color: COLORS.blue }}>{sc.type}</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{sc.audience} · {sc.sections} sections · {sc.uses} uses · {sc.created}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}><button onClick={() => { setGeneratedScript(sc.sections_json || {}); setScriptType(sc.type || 'Cold Call'); setSavedScriptId(sc.id); setView('generator'); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>View</button><button onClick={() => { const text = Object.values(sc.sections_json || {}).filter(Boolean).join('\n\n---\n\n'); navigator.clipboard?.writeText(text); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>Copy</button></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // GENERATOR
  return (
    <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ width: 420, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setView("library")} style={{ padding: "2px 6px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontSize: 10, cursor: "pointer" }}>←</button>
          <span style={{ fontSize: 14 }}>📞</span>
          <div><div style={{ fontWeight: 600, fontSize: 13 }}>Script Generator</div><div style={{ fontSize: 10, color: COLORS.textDim }}>{generatedScript ? "Revise mode" : scriptType || "Select type"}</div></div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "88%", padding: "10px 14px", borderRadius: 12, background: msg.role === "user" ? COLORS.accent + "20" : COLORS.surface, border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`, borderBottomRightRadius: msg.role === "user" ? 4 : 12, borderBottomLeftRadius: msg.role === "agent" ? 4 : 12 }}>
                {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>SCRIPT GENERATOR</div>}
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-line" }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {!scriptType && <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{SCRIPT_TYPES.map(t => <button key={t} onClick={() => selectType(t)} style={{ padding: "8px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer" }} onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; }} onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}>{t}</button>)}</div>}
          {isTyping && <div style={{ display: "flex" }}><div style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, borderBottomLeftRadius: 4 }}><ProgressDots active={true} /></div></div>}
          <div ref={chatEndRef} />
        </div>
        {scriptType && <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) sendMessage(); }} placeholder={generatedScript ? "Ask for revisions..." : "Answer..."} style={{ flex: 1, padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }} disabled={isTyping} />
          <button onClick={sendMessage} disabled={isTyping || !userInput.trim()} style={{ padding: "10px 18px", background: userInput.trim() && !isTyping ? COLORS.accent : COLORS.border, color: userInput.trim() && !isTyping ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: userInput.trim() && !isTyping ? "pointer" : "default" }}>Send</button>
        </div>}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        {!generatedScript ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ textAlign: "center" }}><div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>📞</div><div style={{ fontFamily: FONT, fontSize: 15, color: COLORS.textDim }}>Script preview here</div></div></div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div><h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: 0 }}>{scriptType} <span style={{ color: COLORS.accent }}>Script</span></h3></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { const f = Object.values(generatedScript).join("\n\n---\n\n"); navigator.clipboard?.writeText(f); }} style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Copy All</button>
                <button onClick={() => { setShowSaveModal(true); setSaveName(""); }} style={{ padding: "8px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>💾 Save Script</button>
              </div>
            </div>
            {[{ key: "opener", label: "OPENER", icon: "👋", color: COLORS.accent }, { key: "qualifying", label: "QUALIFYING", icon: "❓", color: COLORS.blue }, { key: "valueProps", label: "VALUE PROP", icon: "💎", color: COLORS.accent }, { key: "objections", label: "OBJECTIONS", icon: "🛡️", color: COLORS.warn }, { key: "close", label: "CLOSE", icon: "🎯", color: "#7B61FF" }].map(sec => (
              <div key={sec.key} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 12 }}>{sec.icon}</span><span style={{ fontFamily: FONT, fontSize: 10, color: sec.color, letterSpacing: "0.06em", fontWeight: 600 }}>{sec.label}</span></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AutoSaveIndicator status={scriptAutoSave.saveStatus} />
                    <button onClick={() => navigator.clipboard?.writeText(generatedScript[sec.key])} style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textDim, fontFamily: FONT, fontSize: 9, cursor: "pointer" }}>Copy</button>
                  </div>
                </div>
                <textarea value={generatedScript[sec.key] || ''} onChange={e => updateScriptSection(sec.key, e.target.value)} style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line", padding: "10px 14px", background: COLORS.bg, borderRadius: 6, border: `1px solid ${COLORS.border}`, width: "100%", boxSizing: "border-box", fontFamily: FONT_BODY, outline: "none", resize: "vertical", minHeight: 80 }} />
              </div>
            ))}
          </div>
        )}
        {/* Save Modal */}
        {showSaveModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSaveModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: 420, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
              <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Save Script</div>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Give your script a name to save it to your library</div>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>SCRIPT NAME</div>
                <input value={saveName} onChange={e => setSaveName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleSave(); }} placeholder={`e.g. ${scriptType} — B2B SaaS VPs`} autoFocus style={{ width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, padding: "10px 14px", background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: COLORS.blue + "15", color: COLORS.blue }}>{scriptType}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>5 sections</span>
                </div>
              </div>
              <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={() => setShowSaveModal(false)} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button onClick={handleSave} disabled={!saveName.trim()} style={{ padding: "10px 24px", background: saveName.trim() ? COLORS.accent : COLORS.border, color: saveName.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saveName.trim() ? "pointer" : "default" }}>Save to Library</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VideoScriptView() {
  const [activeTab, setActiveTab] = useState("create");
  const [topic, setTopic] = useState("");
  const [style, setStyle] = useState("educational");
  const [duration, setDuration] = useState("30");
  const [sourceContent, setSourceContent] = useState("");
  const [scripts, setScripts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveScriptName, setSaveScriptName] = useState("");
  const [selectedScripts, setSelectedScripts] = useState({});
  const [savedVideoScripts, setSavedVideoScripts] = useState([]);

  useEffect(() => {
    api.contentPosts.list({ format: 'video_script' }).then(data => {
      const scripts = Array.isArray(data) ? data : data.content_posts || [];
      setSavedVideoScripts(scripts.map(s => ({ id: s.id, name: s.title || s.name || 'Untitled', scripts: s.script_count || 1, created: s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '', style: s.style || '', topic: s.topic || '' })));
    }).catch(() => {});
  }, []);
  const [useHookTemplate, setUseHookTemplate] = useState(false);
  const [selectedHookStyle, setSelectedHookStyle] = useState(null);
  const [selectedHook, setSelectedHook] = useState(null);
  const [hookFilter, setHookFilter] = useState("all");
  const [compFilter, setCompFilter] = useState("all");

  const HOOK_STYLES = ["Pattern Interrupt", "Question", "Bold Claim", "Story", "Controversy", "Tutorial", "Before/After", "POV"];

  const HOOK_TEMPLATES = [
    { id: "h1", text: "Stop scrolling if you [pain point]...", style: "Pattern Interrupt", source: "Custom", platform: "TikTok", example: "Stop scrolling if you're still doing outreach manually..." },
    { id: "h2", text: "I [impressive result] in [timeframe]. Here's how.", style: "Bold Claim", source: "Alex Hormozi", platform: "YouTube Shorts", example: "I replaced a £200K employee with AI in 6 weeks. Here's how." },
    { id: "h3", text: "What if I told you [counterintuitive truth]?", style: "Question", source: "Custom", platform: "TikTok", example: "What if I told you your cold emails are failing because they're too short?" },
    { id: "h4", text: "6 months ago I [starting point]. Today I [result].", style: "Before/After", source: "Saved from @codyschneiderxx", platform: "TikTok", example: "6 months ago I had 0 clients. Today I run a £300K consultancy." },
    { id: "h5", text: "Nobody talks about this, but [insight]...", style: "Controversy", source: "Custom", platform: "LinkedIn", example: "Nobody talks about this, but AI consultants are charging too little." },
    { id: "h6", text: "Here's the exact [system/process] I use to [outcome]", style: "Tutorial", source: "Saved from @thesalestechnologies", platform: "YouTube Shorts", example: "Here's the exact 5-step system I use to book 20 meetings per week." },
    { id: "h7", text: "POV: You're a [role] who just discovered [thing]", style: "POV", source: "Custom", platform: "TikTok", example: "POV: You're a sales manager who just discovered your entire SDR process can be automated." },
    { id: "h8", text: "[Famous person/company] does this one thing differently", style: "Story", source: "Saved from @garyvee", platform: "TikTok", example: "Clay.com does this one thing that makes them 10x better than every other enrichment tool." },
    { id: "h9", text: "The [industry] is about to change forever. Here's why.", style: "Bold Claim", source: "Custom", platform: "YouTube Shorts", example: "The consulting industry is about to change forever. Here's why." },
    { id: "h10", text: "If you're still [old way], you're already behind.", style: "Pattern Interrupt", source: "Saved from @alexbanayan", platform: "TikTok", example: "If you're still cold calling without AI research, you're already behind." },
    { id: "h11", text: "I asked 100 [people] what [question]. The answer shocked me.", style: "Story", source: "Custom", platform: "YouTube Shorts", example: "I asked 100 B2B buyers what makes them reply to cold emails. The answer shocked me." },
    { id: "h12", text: "Most [role]s get this wrong about [topic]", style: "Controversy", source: "Saved from @justinwelsh", platform: "LinkedIn", example: "Most consultants get this wrong about pricing." },
  ];

  const TRACKED_ACCOUNTS = [
    { id: "a1", name: "Alex Hormozi", handle: "@hormozi", platform: "YouTube Shorts", followers: "2.8M", avgEng: "4.2%", frequency: "Daily" },
    { id: "a2", name: "Justin Welsh", handle: "@justinwelsh", platform: "LinkedIn", followers: "850K", avgEng: "3.8%", frequency: "2x daily" },
    { id: "a3", name: "Cody Schneider", handle: "@codyschneiderxx", platform: "TikTok", followers: "245K", avgEng: "5.1%", frequency: "3x week" },
    { id: "a4", name: "The Sales Technologies", handle: "@thesalestechnologies", platform: "TikTok", followers: "180K", avgEng: "6.3%", frequency: "Daily" },
  ];

  const COMPETITOR_POSTS = [
    { id: "cp1", account: "Alex Hormozi", platform: "YouTube Shorts", date: "Feb 11", hook: "I fired my $300K sales team. Revenue went UP.", content: "I fired my $300K sales team and replaced them with 2 AI agents and 1 human. Revenue went up 40% in 3 months. Here's the framework...", views: "2.4M", likes: "89K", comments: "3.2K", topPerformer: true },
    { id: "cp2", account: "Justin Welsh", platform: "LinkedIn", date: "Feb 10", hook: "The biggest lie in consulting: you need a team to scale.", content: "The biggest lie in consulting: you need a team to scale. I hit $5M solo. No employees. No office. Here's my operating system...", views: "450K", likes: "12K", comments: "890", topPerformer: true },
    { id: "cp3", account: "Cody Schneider", platform: "TikTok", date: "Feb 10", hook: "This AI tool finds you 1,000 leads in 5 minutes", content: "This AI tool finds you 1,000 leads in 5 minutes and costs $0. Open LinkedIn Sales Nav, export your search, run it through this free enrichment pipeline...", views: "890K", likes: "42K", comments: "1.8K", topPerformer: true },
    { id: "cp4", account: "The Sales Technologies", platform: "TikTok", date: "Feb 9", hook: "3 cold email mistakes killing your reply rate", content: "3 cold email mistakes killing your reply rate: 1. Your subject line is too long 2. You're talking about yourself 3. No clear CTA...", views: "120K", likes: "5.4K", comments: "340", topPerformer: false },
    { id: "cp5", account: "Alex Hormozi", platform: "YouTube Shorts", date: "Feb 8", hook: "Why nobody replies to your emails (fix this today)", content: "Why nobody replies to your emails. It's not your offer. It's not your list. It's your first line. The first 8 words decide everything...", views: "1.1M", likes: "52K", comments: "2.1K", topPerformer: false },
    { id: "cp6", account: "Justin Welsh", platform: "LinkedIn", date: "Feb 8", hook: "I've tested 47 different hooks. These 5 outperform everything.", content: "I've tested 47 different hooks. These 5 outperform everything else by 3x: Pattern Interrupt, Before/After, Bold Claim, Direct Question, POV...", views: "320K", likes: "9.8K", comments: "720", topPerformer: true },
  ];

  const STYLES = [
    { key: "educational", label: "Educational", desc: "Teach something valuable" },
    { key: "storytelling", label: "Storytelling", desc: "Hook with a narrative" },
    { key: "controversial", label: "Hot Take", desc: "Bold opinion" },
    { key: "tutorial", label: "Tutorial", desc: "Step-by-step" },
    { key: "listicle", label: "Listicle", desc: "\"3 things...\"" },
  ];

  const MOCK_SCRIPTS = [
    { id: 1, hook: "Stop using AI like it's Google. Here's what the top 1% do instead.", body: "Most people type a question into ChatGPT and accept the first answer. But the people getting 10x results? They're doing something completely different.\n\nThey start with context. They tell the AI who they are, what they're working on, and what good looks like.\n\nThen they iterate. First draft is never the final draft.\n\nFinally — they use AI as a thinking partner, not an answer machine.", cta: "Follow for more AI strategies that actually work.", duration: "45s", style: "educational" },
    { id: 2, hook: "I replaced a £200K employee with an AI agent. Here's what happened.", body: "Six months ago I built an AI agent that does the work of a full-time data analyst.\n\nThe first month? Rough. Lots of errors.\n\nBy month three, it was outperforming benchmarks. Not because it was smarter — because it never got tired.\n\nBut here's what nobody talks about: I didn't fire anyone. I redeployed that person to strategic work AI can't do.", cta: "Save this for when your boss asks about AI.", duration: "60s", style: "storytelling" },
  ];

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };

  const [scriptError, setScriptError] = useState(null);

  const generateScripts = async () => {
    if (!topic.trim()) return;
    setIsGenerating(true);
    setScriptError(null);
    try {
      const result = await api.contentPosts.generateVideo({
        topic,
        style,
        duration,
        hookTemplate: selectedHook?.text || undefined,
        sourceContent: sourceContent || undefined,
      });
      setScripts(result.scripts || []);
    } catch (err) {
      setScriptError(err.message || 'Failed to generate scripts');
      setScripts([]);
    }
    setIsGenerating(false);
  };

  const openSaveModal = () => { setSaveScriptName(""); setSelectedScripts(scripts.reduce((a, s) => ({ ...a, [s.id]: true }), {})); setShowSaveModal(true); };

  const handleSave = async () => {
    const selectedList = scripts.filter(s => selectedScripts[s.id]);
    try {
      const result = await api.contentPosts.create({
        platform: 'tiktok',
        title: saveScriptName || 'Untitled Video Script',
        body_text: selectedList.map(s => `${s.hook}\n\n${s.body}\n\n${s.cta}`).join('\n\n---\n\n'),
        format: 'video_script',
        status: 'draft',
        video_script: { scripts: selectedList, style, topic, duration },
      });
      setSavedVideoScripts(prev => [{ id: result.id, name: saveScriptName || 'Untitled', scripts: selectedList.length, created: "Just now", style, topic }, ...prev]);
    } catch (err) {
      setSavedVideoScripts(prev => [{ id: `vs_${Date.now()}`, name: saveScriptName, scripts: selectedList.length, created: "Just now", style, topic }, ...prev]);
    }
    setShowSaveModal(false);
  };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>Short Form <span style={{ color: COLORS.warn }}>Scripts</span></h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Generate & save scripts for TikTok, Reels & YouTube Shorts</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[{ key: "create", label: "🎬 Create" }, { key: "saved", label: `📚 Library (${savedVideoScripts.length})` }, { key: "hooks", label: "🪝 Hook Templates" }, { key: "competitors", label: "🔎 Top Performers" }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: "10px 20px", background: "transparent", border: "none", borderBottom: activeTab === tab.key ? `2px solid ${COLORS.warn}` : "2px solid transparent", color: activeTab === tab.key ? COLORS.warn : COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "saved" && (
        <div>
          {savedVideoScripts.length === 0 ? (
            <div style={{ padding: "60px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}><div style={{ fontSize: 13, color: COLORS.textDim }}>No saved scripts yet</div></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {savedVideoScripts.map(vs => (
                <div key={vs.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{vs.name}</span>
                      <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: COLORS.warn + "15", color: COLORS.warn }}>{vs.style}</span>
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textDim }}>{vs.topic} · {vs.scripts} scripts · {vs.created}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}><button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>View</button><button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>Copy</button></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "create" && (
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
              {/* Hook Template Toggle */}
              <div style={{ padding: "12px 16px", background: useHookTemplate ? COLORS.warn + "08" : COLORS.bg, border: `1px solid ${useHookTemplate ? COLORS.warn + "33" : COLORS.border}`, borderRadius: 8, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div><div style={{ fontSize: 12, fontWeight: 600 }}>🪝 Use Hook Template</div><div style={{ fontSize: 10, color: COLORS.textDim }}>Start with a proven hook structure</div></div>
                <div onClick={() => { setUseHookTemplate(!useHookTemplate); setSelectedHookStyle(null); setSelectedHook(null); }} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: useHookTemplate ? COLORS.warn : COLORS.borderActive, position: "relative", transition: "background 0.25s" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: useHookTemplate ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </div>
              </div>
              {useHookTemplate && (
                <div style={{ marginBottom: 20, padding: "14px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>CHOOSE HOOK STYLE</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: selectedHookStyle ? 12 : 0 }}>
                    {HOOK_STYLES.map(hs => (
                      <button key={hs} onClick={() => setSelectedHookStyle(hs === selectedHookStyle ? null : hs)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${selectedHookStyle === hs ? COLORS.warn + "55" : COLORS.border}`, background: selectedHookStyle === hs ? COLORS.warn + "12" : "transparent", color: selectedHookStyle === hs ? COLORS.warn : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{hs}</button>
                    ))}
                  </div>
                  {selectedHookStyle && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {HOOK_TEMPLATES.filter(h => h.style === selectedHookStyle).map(h => (
                        <div key={h.id} onClick={() => setSelectedHook(h)} style={{ padding: "8px 12px", borderRadius: 6, border: `1px solid ${selectedHook?.id === h.id ? COLORS.warn + "55" : COLORS.border}`, background: selectedHook?.id === h.id ? COLORS.warn + "10" : "transparent", cursor: "pointer" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: selectedHook?.id === h.id ? COLORS.warn : COLORS.text }}>{h.text}</div>
                          <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>{h.source} · {h.platform}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedHook && <div style={{ marginTop: 8, padding: "8px 12px", background: COLORS.warn + "08", border: `1px solid ${COLORS.warn}22`, borderRadius: 6 }}><div style={{ fontSize: 9, color: COLORS.warn, fontFamily: FONT, fontWeight: 600, marginBottom: 2 }}>EXAMPLE</div><div style={{ fontSize: 11, color: COLORS.text, fontStyle: "italic" }}>{selectedHook.example}</div></div>}
                </div>
              )}
              <div style={{ marginBottom: 20 }}><label style={labelStyle}>TOPIC / IDEA</label><input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Why most people use AI wrong..." style={inputStyle} /></div>
              <div style={{ marginBottom: 20 }}><label style={labelStyle}>STYLE</label><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{STYLES.map(s => (<div key={s.key} onClick={() => setStyle(s.key)} style={{ padding: "8px 14px", borderRadius: 8, cursor: "pointer", background: style === s.key ? COLORS.warn + "15" : "transparent", border: `1px solid ${style === s.key ? COLORS.warn + "44" : COLORS.border}`, color: style === s.key ? COLORS.warn : COLORS.textMuted }}><div style={{ fontWeight: 600, fontSize: 12 }}>{s.label}</div><div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{s.desc}</div></div>))}</div></div>
              <div style={{ marginBottom: 20 }}><label style={labelStyle}>DURATION</label><div style={{ display: "flex", gap: 8 }}>{["15", "30", "45", "60", "90"].map(d => (<button key={d} onClick={() => setDuration(d)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, border: `1px solid ${duration === d ? COLORS.warn + "55" : COLORS.border}`, background: duration === d ? COLORS.warn + "15" : "transparent", color: duration === d ? COLORS.warn : COLORS.textDim, cursor: "pointer" }}>{d}s</button>))}</div></div>
              <div style={{ marginBottom: 20 }}><label style={labelStyle}>SOURCE CONTENT (OPTIONAL)</label><textarea value={sourceContent} onChange={e => setSourceContent(e.target.value)} placeholder="Paste content to repurpose..." rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} /></div>
              <button onClick={generateScripts} disabled={isGenerating || !topic.trim()} style={{ width: "100%", padding: "14px", background: topic.trim() ? COLORS.warn : COLORS.border, color: topic.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: topic.trim() && !isGenerating ? "pointer" : "default" }}>{isGenerating ? "GENERATING..." : "🎬 GENERATE SCRIPTS"}</button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {scripts.length === 0 && !isGenerating && <div style={{ padding: "60px 40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}><div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>🎬</div><div style={{ fontSize: 13, color: COLORS.textDim }}>Scripts will appear here</div></div>}
            {isGenerating && <div style={{ padding: "60px 40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}><div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div><ProgressDots active={true} /></div>}
            {scriptError && <div style={{ padding: "12px 16px", marginBottom: 12, background: COLORS.danger + "12", border: `1px solid ${COLORS.danger}33`, borderRadius: 8, fontSize: 12, color: COLORS.danger }}>{scriptError}</div>}
            {scripts.length > 0 && !isGenerating && (
              <div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}><button onClick={openSaveModal} style={{ padding: "8px 16px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>💾 Save Scripts</button></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {scripts.map((sc, i) => (
                    <div key={sc.id} style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.warn, fontWeight: 600 }}>SCRIPT {i + 1}</span>
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.warn + "15", color: COLORS.warn, fontFamily: FONT }}>{sc.duration}</span>
                        </div>
                        <button onClick={() => navigator.clipboard?.writeText(`${sc.hook}\n\n${sc.body}\n\n${sc.cta}`)} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Copy</button>
                      </div>
                      <div style={{ marginBottom: 12 }}><div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>HOOK</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.warn, lineHeight: 1.4 }}>{sc.hook}</div></div>
                      <div style={{ marginBottom: 12 }}><div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>BODY</div><div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{sc.body}</div></div>
                      <div><div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>CTA</div><div style={{ fontSize: 13, color: COLORS.accent, fontWeight: 500 }}>{sc.cta}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hook Templates Tab */}
      {activeTab === "hooks" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <button onClick={() => setHookFilter("all")} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${hookFilter === "all" ? COLORS.warn + "55" : COLORS.border}`, background: hookFilter === "all" ? COLORS.warn + "12" : "transparent", color: hookFilter === "all" ? COLORS.warn : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>All</button>
              {HOOK_STYLES.map(hs => (
                <button key={hs} onClick={() => setHookFilter(hs)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${hookFilter === hs ? COLORS.warn + "55" : COLORS.border}`, background: hookFilter === hs ? COLORS.warn + "12" : "transparent", color: hookFilter === hs ? COLORS.warn : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{hs}</button>
              ))}
            </div>
            <button style={{ padding: "8px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>+ Add Custom Hook</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {HOOK_TEMPLATES.filter(h => hookFilter === "all" || h.style === hookFilter).map(h => (
              <div key={h.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: COLORS.warn + "15", color: COLORS.warn }}>{h.style}</span>
                  <span style={{ fontSize: 9, color: COLORS.textDim }}>{h.platform}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6, lineHeight: 1.4 }}>{h.text}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim, fontStyle: "italic", marginBottom: 8, lineHeight: 1.4 }}>"{h.example}"</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{h.source}</span>
                  <button onClick={() => { setActiveTab("create"); setUseHookTemplate(true); setSelectedHookStyle(h.style); setSelectedHook(h); }} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.warn}44`, borderRadius: 4, color: COLORS.warn, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Use This →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Performers Tab */}
      {activeTab === "competitors" && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>Tracked Accounts</span>
              <button style={{ padding: "6px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>+ Track Account</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {TRACKED_ACCOUNTS.map(acc => (
                <div key={acc.id} style={{ padding: "14px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: COLORS.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: FONT, color: COLORS.accent }}>{acc.name.split(" ").map(n => n[0]).join("")}</div>
                    <div><div style={{ fontWeight: 600, fontSize: 12 }}>{acc.name}</div><div style={{ fontSize: 9, color: COLORS.textDim }}>{acc.handle}</div></div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim }}>
                    <span>{acc.followers}</span><span>{acc.avgEng} eng</span>
                  </div>
                  <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 4 }}>{acc.platform} · {acc.frequency}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
            {[{ key: "all", label: "All" }, { key: "top", label: "🔥 Top Performers" }, ...TRACKED_ACCOUNTS.map(a => ({ key: a.name, label: a.name.split(" ")[0] }))].map(f => (
              <button key={f.key} onClick={() => setCompFilter(f.key)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${compFilter === f.key ? COLORS.warn + "55" : COLORS.border}`, background: compFilter === f.key ? COLORS.warn + "12" : "transparent", color: compFilter === f.key ? COLORS.warn : COLORS.textDim, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{f.label}</button>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {COMPETITOR_POSTS.filter(p => compFilter === "all" ? true : compFilter === "top" ? p.topPerformer : p.account === compFilter).map(post => (
              <div key={post.id} style={{ padding: "18px 22px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, borderLeft: post.topPerformer ? `4px solid ${COLORS.warn}` : "4px solid transparent" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>{post.account}</span>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, background: COLORS.blue + "12", color: COLORS.blue }}>{post.platform}</span>
                    {post.topPerformer && <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: FONT, fontWeight: 600, background: COLORS.warn + "15", color: COLORS.warn }}>🔥 Top Performer</span>}
                  </div>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{post.date}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.warn, marginBottom: 6, lineHeight: 1.3 }}>{post.hook}</div>
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5, marginBottom: 12, maxHeight: 60, overflow: "hidden" }}>{post.content}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 14, fontSize: 10, color: COLORS.textDim }}>
                    <span>👁 {post.views}</span><span>❤️ {post.likes}</span><span>💬 {post.comments}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.textMuted, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Save to Library</button>
                    <button style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.warn}44`, borderRadius: 4, color: COLORS.warn, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Save Hook</button>
                    <button onClick={() => { setActiveTab("create"); setTopic(post.hook); }} style={{ padding: "4px 10px", background: COLORS.warn + "12", border: `1px solid ${COLORS.warn}44`, borderRadius: 4, color: COLORS.warn, fontFamily: FONT, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Remix →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Script Modal */}
      {showSaveModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSaveModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 440, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Save Video Scripts</div>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>COLLECTION NAME</div>
              <input value={saveScriptName} onChange={e => setSaveScriptName(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && saveScriptName.trim()) handleSave(); }} placeholder="e.g. AI Usage Hot Takes" autoFocus style={{ width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 16 }} />
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>SELECT SCRIPTS TO SAVE</div>
              {scripts.map((sc, i) => (
                <label key={sc.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!selectedScripts[sc.id]} onChange={e => setSelectedScripts({ ...selectedScripts, [sc.id]: e.target.checked })} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.warn }}>{sc.hook}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{sc.duration} · {sc.style}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setShowSaveModal(false)} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={!saveScriptName.trim()} style={{ padding: "10px 24px", background: saveScriptName.trim() ? COLORS.accent : COLORS.border, color: saveScriptName.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: saveScriptName.trim() ? "pointer" : "default" }}>Save to Library</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SalesCallAnalyserView() {
  const [view, setView] = useState("dashboard"); // dashboard, upload, analysis, fathom
  const [transcript, setTranscript] = useState("");
  const [callName, setCallName] = useState("");
  const [analysing, setAnalysing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [uploadMode, setUploadMode] = useState("paste"); // paste, fathom

  // Fathom state
  const [fathomConnected, setFathomConnected] = useState(false);
  const [fathomMeetings, setFathomMeetings] = useState([]);
  const [fathomLoading, setFathomLoading] = useState(false);
  const [fathomError, setFathomError] = useState(null);
  const [importingId, setImportingId] = useState(null);

  const [CALL_HISTORY, setCallHistory] = useState([]);
  const [COMMON_OBJECTIONS, setCommonObjections] = useState([]);

  const loadHistory = () => {
    api.callAnalyses.list().then(data => {
      const calls = Array.isArray(data) ? data : data.call_analyses || [];
      setCallHistory(calls.map(c => {
        let outcome = '';
        try {
          const recs = typeof c.recommendations_json === 'string' ? JSON.parse(c.recommendations_json) : c.recommendations_json;
          const metrics = typeof c.metrics_json === 'string' ? JSON.parse(c.metrics_json) : c.metrics_json;
          // Outcome is not stored as a column — derive from score
          const sc = c.overall_score != null ? c.overall_score / 10 : 0;
          outcome = sc >= 8 ? 'Closed won' : sc >= 6 ? 'Follow-up needed' : 'No interest';
        } catch(_) {}
        return {
          ...c,
          name: c.name || '',
          date: c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : '',
          duration: c.duration_seconds ? `${Math.round(c.duration_seconds / 60)} min` : '',
          score: c.overall_score != null ? (c.overall_score / 10) : 0,
          outcome,
          source: c.source || 'manual',
        };
      }));
    }).catch(() => {});
    api.callAnalyses.objections().then(data => {
      const objs = Array.isArray(data) ? data : [];
      setCommonObjections(objs.map(o => ({
        objection: o.objection_text,
        count: o.occurrence_count || 0,
        winRate: o.win_rate || 0,
        bestResponse: o.best_response || '—',
      })));
    }).catch(() => {});
  };

  useEffect(() => {
    loadHistory();
    api.fathom.status().then(s => setFathomConnected(!!s.connected)).catch(() => {});
  }, []);

  const loadFathomMeetings = async () => {
    setFathomLoading(true);
    setFathomError(null);
    try {
      const data = await api.fathom.meetings({ limit: 20 });
      setFathomMeetings(data.meetings || []);
    } catch (err) {
      setFathomError(err?.message || err?.error || 'Failed to load Fathom meetings');
    } finally {
      setFathomLoading(false);
    }
  };

  const importFathomMeeting = async (meeting) => {
    setImportingId(meeting.id);
    try {
      // Convert Fathom transcript array to readable text on the frontend
      let transcriptText = null;
      if (Array.isArray(meeting.transcript)) {
        transcriptText = meeting.transcript.map(seg => {
          const speaker = seg.speaker?.display_name || seg.speaker_name || 'Speaker';
          const text = seg.text || '';
          const time = seg.timestamp || '';
          return `[${time}] ${speaker}: ${text}`;
        }).join('\n');
      } else if (typeof meeting.transcript === 'string') {
        transcriptText = meeting.transcript;
      }

      const imported = await api.fathom.import(meeting.id, { title: meeting.title, transcript_text: transcriptText });
      // Pre-fill the paste view with the transcript so user can run AI analysis
      if (transcriptText && transcriptText.length >= 20) {
        setTranscript(transcriptText);
        setCallName(meeting.title);
        setView("upload");
        setUploadMode("paste");
      } else if (imported.transcript_text && imported.transcript_text.length >= 20) {
        setTranscript(imported.transcript_text);
        setCallName(meeting.title);
        setView("upload");
        setUploadMode("paste");
      } else {
        loadHistory();
        setView("dashboard");
      }
    } catch (err) {
      setFathomError(err?.message || err?.error || 'Failed to import meeting');
    } finally {
      setImportingId(null);
    }
  };

  const totalCalls = CALL_HISTORY.length;
  const avgScore = totalCalls ? (CALL_HISTORY.reduce((s, c) => s + c.score, 0) / totalCalls).toFixed(1) : '0';
  const closedWon = CALL_HISTORY.filter(c => c.outcome === "Closed won").length;
  const meetingsBooked = CALL_HISTORY.filter(c => ["Meeting booked", "Demo scheduled"].includes(c.outcome)).length;

  const runAnalysis = async () => {
    setAnalysing(true);
    setAnalysisError(null);
    try {
      const result = await api.callAnalyses.analyse({ transcript, call_name: callName || undefined });
      const a = result.analysis || {};
      const rawMetrics = a.metrics || [];
      const rawHighlights = a.highlights || [];
      const metrics = Array.isArray(rawMetrics) ? rawMetrics.map(m => ({
        score: m.score,
        label: m.label ?? m.name,
        detail: m.detail ?? m.feedback ?? '',
      })) : [];
      const highlights = Array.isArray(rawHighlights) ? rawHighlights.map(h => typeof h === 'string'
        ? { time: '', text: h, type: 'positive' }
        : { time: h.time ?? h.timestamp ?? '', text: h.text ?? h.content ?? h.description ?? '', type: h.type ?? 'positive' }
      ) : [];
      setAnalysis({
        overallScore: a.overallScore,
        talkListenRatio: a.talkListenRatio || { talk: 50, listen: 50 },
        fillerWords: a.fillerWords ?? 0,
        callDuration: a.callDuration ?? '—',
        metrics, highlights,
        recommendations: a.recommendations || [],
        objections: a.objections || [],
      });
      setView("analysis");
      loadHistory();
    } catch (err) {
      const msg = err?.message || err?.error || 'Analysis failed. Please try again.';
      const code = err?.code || '';
      if (code === 'ANTHROPIC_NOT_CONFIGURED') {
        setAnalysisError('Anthropic API key not configured. Go to Settings > Integrations > LLM to add your key.');
      } else if (code === 'ANTHROPIC_INVALID_KEY') {
        setAnalysisError('Your Anthropic API key is invalid. Please update it in Settings > Integrations > LLM.');
      } else if (code === 'ANTHROPIC_NO_CREDITS') {
        setAnalysisError('No Anthropic credits remaining. Please add credits at console.anthropic.com.');
      } else if (code === 'ANTHROPIC_RATE_LIMIT') {
        setAnalysisError('Rate limit hit. Please wait a moment and try again.');
      } else {
        setAnalysisError(msg);
      }
    } finally {
      setAnalysing(false);
    }
  };

  const viewSavedAnalysis = (call) => {
    const rawMetrics = typeof call.metrics_json === 'string' ? JSON.parse(call.metrics_json) : (call.metrics_json || []);
    const rawHighlights = typeof call.highlights_json === 'string' ? JSON.parse(call.highlights_json) : (call.highlights_json || []);
    const recs = typeof call.recommendations_json === 'string' ? JSON.parse(call.recommendations_json) : (call.recommendations_json || []);
    const objs = typeof call.objections_detected_json === 'string' ? JSON.parse(call.objections_detected_json) : (call.objections_detected_json || []);
    const metrics = Array.isArray(rawMetrics) ? rawMetrics.map(m => ({
      score: m.score,
      label: m.label ?? m.name,
      detail: m.detail ?? m.feedback ?? '',
    })) : [];
    const highlights = Array.isArray(rawHighlights) ? rawHighlights.map(h => typeof h === 'string'
      ? { time: '', text: h, type: 'positive' }
      : { time: h.time ?? h.timestamp ?? '', text: h.text ?? h.content ?? h.description ?? '', type: h.type ?? 'positive' }
    ) : [];
    setAnalysis({
      overallScore: call.overall_score != null ? call.overall_score / 10 : 0,
      talkListenRatio: { talk: 50, listen: 50 },
      fillerWords: 0,
      callDuration: call.duration_seconds ? `${Math.round(call.duration_seconds / 60)} min` : '—',
      metrics, highlights, recommendations: recs, objections: objs,
    });
    setView("analysis");
  };
  const getScoreColor = (s) => s >= 8 ? COLORS.accent : s >= 6 ? COLORS.warn : COLORS.danger;

  // ANALYSIS VIEW
  if (view === "analysis" && analysis) {
    return (
      <div style={{ flex: 1, display: "flex", height: "100%", overflow: "hidden" }}>
        <div style={{ width: 380, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.border}` }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>📊</span><div><div style={{ fontWeight: 600, fontSize: 13 }}>Key Moments</div><div style={{ fontSize: 10, color: COLORS.textDim }}>{analysis.highlights.length} flagged</div></div></div>
            <button onClick={() => setView("dashboard")} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "14px 16px" }}>
            {analysis.highlights.map((h, i) => (
              <div key={i} style={{ padding: "10px 14px", marginBottom: 6, borderRadius: 8, background: h.type === "positive" ? COLORS.accent + "08" : COLORS.danger + "08", border: `1px solid ${h.type === "positive" ? COLORS.accent + "22" : COLORS.danger + "22"}`, borderLeft: `3px solid ${h.type === "positive" ? COLORS.accent : COLORS.danger}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontFamily: FONT, fontSize: 10, color: h.type === "positive" ? COLORS.accent : COLORS.danger, fontWeight: 600 }}>{h.time}</span>
                  <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: h.type === "positive" ? COLORS.accent + "20" : COLORS.danger + "20", color: h.type === "positive" ? COLORS.accent : COLORS.danger, fontFamily: FONT, fontWeight: 600 }}>{h.type === "positive" ? "STRENGTH" : "IMPROVE"}</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>{h.text}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
          <h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, margin: "0 0 20px" }}>Call <span style={{ color: COLORS.accent }}>Analysis</span></h3>
          <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
            <div style={{ width: 100, padding: "20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: getScoreColor(analysis.overallScore) }}>{analysis.overallScore}</div>
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>OVERALL</div>
            </div>
            <div style={{ flex: 1, display: "flex", gap: 10 }}>
              <StatCard label="Duration" value={analysis.callDuration} accent={COLORS.blue} />
              <StatCard label="Talk/Listen" value={`${analysis.talkListenRatio.talk}/${analysis.talkListenRatio.listen}`} accent={analysis.talkListenRatio.talk > 60 ? COLORS.warn : COLORS.accent} />
              <StatCard label="Filler Words" value={analysis.fillerWords} accent={analysis.fillerWords > 10 ? COLORS.warn : COLORS.accent} />
            </div>
          </div>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10 }}>SKILL BREAKDOWN</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            {analysis.metrics.map((m, i) => (
              <div key={i} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: getScoreColor(m.score) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 14, fontWeight: 700, color: getScoreColor(m.score), flexShrink: 0 }}>{m.score}</div>
                <div><div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{m.label}</div><div style={{ fontSize: 11, color: COLORS.textDim }}>{m.detail}</div></div>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10 }}>RECOMMENDATIONS</div>
          <div style={{ padding: "16px 20px", background: COLORS.accentBg, border: `1px solid ${COLORS.accent}22`, borderRadius: 12 }}>
            {analysis.recommendations.map((rec, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < analysis.recommendations.length - 1 ? 10 : 0, fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                <span style={{ color: COLORS.accent, fontWeight: 600, flexShrink: 0 }}>{i + 1}.</span><div>{rec}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // UPLOAD VIEW
  if (view === "upload") {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <button onClick={() => setView("dashboard")} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, cursor: "pointer" }}>← Back</button>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>Analyse <span style={{ color: COLORS.accent }}>Call</span></h2>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          {[
            { key: "paste", label: "Paste Transcript", icon: "📋", desc: "Paste text directly" },
            { key: "fathom", label: "Import from Fathom", icon: "🎙️", desc: fathomConnected ? "Connected ✓" : "Not connected" },
            { key: "file", label: "Upload File", icon: "📄", desc: ".txt, .docx, .pdf" },
          ].map((opt) => (
            <div key={opt.key} onClick={() => {
              setUploadMode(opt.key);
              if (opt.key === "fathom" && fathomConnected && fathomMeetings.length === 0) loadFathomMeetings();
            }} style={{ flex: 1, padding: "16px", background: uploadMode === opt.key ? COLORS.accentBg : COLORS.surface, border: `1px solid ${uploadMode === opt.key ? COLORS.accent + "33" : COLORS.border}`, borderRadius: 10, textAlign: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{opt.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: uploadMode === opt.key ? COLORS.accent : COLORS.text }}>{opt.label}</div>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>{opt.desc}</div>
            </div>
          ))}
        </div>

        {/* Fathom Import Mode */}
        {uploadMode === "fathom" && (
          <div style={{ marginBottom: 16 }}>
            {!fathomConnected ? (
              <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 8 }}>Fathom is not connected.</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>Go to Settings &gt; Integrations to add your Fathom API key.</div>
              </div>
            ) : fathomLoading ? (
              <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
                <ProgressDots active={true} />
                <div style={{ fontSize: 13, color: COLORS.accent, marginTop: 8 }}>Loading Fathom meetings...</div>
              </div>
            ) : fathomError ? (
              <div style={{ padding: "16px", background: COLORS.danger + "10", border: `1px solid ${COLORS.danger}22`, borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: COLORS.danger }}>{fathomError}</div>
                <button onClick={loadFathomMeetings} style={{ marginTop: 8, padding: "6px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontSize: 11, cursor: "pointer" }}>Retry</button>
              </div>
            ) : fathomMeetings.length === 0 ? (
              <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted }}>No meetings found in Fathom.</div>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Record a meeting with Fathom first, then import it here.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>FATHOM MEETINGS ({fathomMeetings.length})</div>
                {fathomMeetings.map(m => (
                  <div key={m.id} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.title}</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                        {m.date ? new Date(m.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        {m.invitees?.length ? ` · ${m.invitees.map(i => i.name).join(', ')}` : ''}
                        {m.transcript ? '' : ' · No transcript'}
                      </div>
                    </div>
                    <button
                      onClick={() => importFathomMeeting(m)}
                      disabled={importingId === m.id || !m.transcript}
                      style={{
                        padding: "6px 16px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: (importingId === m.id || !m.transcript) ? "default" : "pointer",
                        background: !m.transcript ? COLORS.border : importingId === m.id ? COLORS.border : COLORS.accent,
                        color: !m.transcript ? COLORS.textDim : importingId === m.id ? COLORS.textDim : COLORS.bg,
                      }}
                    >
                      {importingId === m.id ? 'Importing...' : !m.transcript ? 'No transcript' : 'Import & Analyse'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Paste Transcript Mode */}
        {uploadMode === "paste" && (<>
        <input value={callName} onChange={e => setCallName(e.target.value)} placeholder="Call name (e.g. Sarah Chen — Acme SaaS)" style={{ width: "100%", padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 12 }} />
        <textarea value={transcript} onChange={e => setTranscript(e.target.value)} placeholder="Paste your sales call transcript here..." rows={12} style={{ width: "100%", padding: "16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", resize: "vertical", lineHeight: 1.7, boxSizing: "border-box", marginBottom: 16 }} />
        {analysisError && (
          <div style={{ padding: "12px 16px", background: COLORS.danger + "10", border: `1px solid ${COLORS.danger}22`, borderRadius: 8, marginBottom: 12, fontSize: 12, color: COLORS.danger }}>{analysisError}</div>
        )}
        {analysing ? (
          <div style={{ padding: "40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
            <ProgressDots active={true} />
            <div style={{ fontSize: 13, color: COLORS.accent, marginTop: 8 }}>Analysing call transcript with AI...</div>
          </div>
        ) : (
          <button onClick={runAnalysis} disabled={!transcript.trim()} style={{ width: "100%", padding: "14px", background: transcript.trim() ? COLORS.accent : COLORS.border, color: transcript.trim() ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: transcript.trim() ? "pointer" : "default" }}>🧠 Analyse Call</button>
        )}
        </>)}
      </div>
    );
  }

  // DASHBOARD VIEW
  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Sales Call <span style={{ color: COLORS.accent }}>Analyser</span></h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0", fontSize: 13 }}>Track call performance, close rates, and common objections</p>
        </div>
        <button onClick={() => setView("upload")} style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Analyse Call</button>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Calls" value={totalCalls} accent={COLORS.accent} />
        <StatCard label="Avg Score" value={`${avgScore}/10`} accent={parseFloat(avgScore) >= 7 ? COLORS.accent : COLORS.warn} />
        <StatCard label="Closed Won" value={closedWon} accent={COLORS.accent} />
        <StatCard label="Meetings Booked" value={meetingsBooked} accent={COLORS.blue} />
        <StatCard label="Close Rate" value={`${totalCalls ? Math.round((closedWon / totalCalls) * 100) : 0}%`} accent={COLORS.accent} />
      </div>

      {/* Call History */}
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10 }}>RECENT CALLS</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
        {CALL_HISTORY.map(call => (
          <div key={call.id} onClick={() => viewSavedAnalysis(call)} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent + "44"}
            onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: getScoreColor(call.score) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 14, fontWeight: 700, color: getScoreColor(call.score), flexShrink: 0 }}>{call.score}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{call.name}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim }}>{call.date} · {call.duration} · {call.source}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 10, fontFamily: FONT, fontWeight: 500,
                background: call.outcome === "Closed won" ? COLORS.accent + "15" : call.outcome === "No interest" ? COLORS.danger + "10" : COLORS.blue + "10",
                color: call.outcome === "Closed won" ? COLORS.accent : call.outcome === "No interest" ? COLORS.danger : COLORS.blue,
                border: `1px solid ${call.outcome === "Closed won" ? COLORS.accent + "22" : call.outcome === "No interest" ? COLORS.danger + "22" : COLORS.blue + "22"}`,
              }}>{call.outcome}</span>
              <span style={{ fontSize: 10, color: COLORS.textDim }}>→</span>
            </div>
          </div>
        ))}
      </div>

      {/* Common Objections */}
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10 }}>COMMON OBJECTIONS TRACKER</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {COMMON_OBJECTIONS.map((obj, i) => (
          <div key={i} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>"{obj.objection}"</div>
              <div style={{ display: "flex", gap: 10, fontSize: 11 }}>
                <span style={{ color: COLORS.textDim }}>{obj.count} times</span>
                <span style={{ color: obj.winRate >= 60 ? COLORS.accent : obj.winRate >= 40 ? COLORS.warn : COLORS.danger, fontWeight: 600 }}>{obj.winRate}% win rate</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 10, color: COLORS.accent, fontFamily: FONT, fontWeight: 600 }}>BEST RESPONSE:</span>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{obj.bestResponse}</span>
            </div>
            <div style={{ width: "100%", height: 3, borderRadius: 2, background: COLORS.border, marginTop: 8 }}>
              <div style={{ width: `${obj.winRate}%`, height: "100%", borderRadius: 2, background: obj.winRate >= 60 ? COLORS.accent : obj.winRate >= 40 ? COLORS.warn : COLORS.danger }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SitePreview() {
  return (
    <div style={{ background: "#fff", borderRadius: 8, overflow: "hidden", fontFamily: FONT_BODY, color: "#111" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f3f3f3", borderBottom: "1px solid #ddd", fontSize: 12 }}>
        <span style={{ display: "flex", gap: 4 }}>{["#ff5f57","#ffbd2e","#28c840"].map(c => <span key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />)}</span>
        <div style={{ flex: 1, background: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: 11, color: "#666", border: "1px solid #ddd" }}>https://vibe-consulting.ai</div>
      </div>
      <div style={{ padding: 32, textAlign: "center", background: "linear-gradient(135deg, #0a0a1a 0%, #1a1a3a 100%)", color: "#fff" }}>
        <div style={{ fontSize: 11, letterSpacing: 3, color: "#c4f04d", marginBottom: 12, textTransform: "uppercase" }}>Vibe Consulting</div>
        <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>AI-Powered Growth</div>
        <div style={{ fontSize: 14, color: "#aaa", marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>We help B2B companies build predictable revenue pipelines with intelligent automation.</div>
        <div style={{ display: "inline-block", padding: "10px 28px", background: "#c4f04d", color: "#000", borderRadius: 6, fontWeight: 600, fontSize: 13 }}>Book a Strategy Call</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, padding: 24, background: "#fafafa" }}>
        {["Lead Generation", "Email Outreach", "Pipeline Analytics"].map(s => (
          <div key={s} style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #eee", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{s}</div>
            <div style={{ fontSize: 11, color: "#888" }}>Automated & intelligent</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WebsiteBuilderView() {
  const [view, setView] = useState("sites");
  const [prompt, setPrompt] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatedLovablePrompt, setGeneratedLovablePrompt] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [refinement, setRefinement] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [siteGenerated, setSiteGenerated] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [refinements, setRefinements] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [currentSiteId, setCurrentSiteId] = useState(null);

  const templates = [
    { id: "landing", label: "Landing Page", icon: "🎯" },
    { id: "business", label: "Business Site", icon: "🏢" },
    { id: "portfolio", label: "Portfolio", icon: "🎨" },
    { id: "coming_soon", label: "Coming Soon", icon: "⏳" },
    { id: "funnel", label: "Sales Funnel", icon: "🔻" },
  ];

  useEffect(() => {
    async function loadSites() {
      setSitesLoading(true);
      try {
        const data = await api.websites.list();
        const rows = Array.isArray(data) ? data : (data.websites || []);
        setSites(rows.map(s => ({
          id: s.id, name: s.name || "Untitled", domain: s.url || "",
          status: s.status || "draft",
          updated: s.updated_at ? new Date(s.updated_at).toLocaleDateString("en-GB", { month: "short", day: "numeric" }) : "",
          prompt: s.prompt || "", template: s.template || "",
          refinements: s.refinements || [],
        })));
      } catch (err) { console.error("Failed to load websites:", err); }
      finally { setSitesLoading(false); }
    }
    loadSites();
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const templateLabel = templates.find(t => t.id === selectedTemplate)?.label || "";
      const opportunity = {
        title: "Website",
        category: templateLabel || "Custom",
        sourceData: prompt.trim(),
        keyFeatures: templateLabel ? [templateLabel] : [],
      };
      const result = await api.prototypes.generate({
        opportunity_id: "website_builder",
        output_type: "lovable",
        opportunity,
      });
      setGeneratedLovablePrompt(result.content || "");
    } catch (err) {
      setGenerateError(err.message || "Failed to generate prompt. Connect Anthropic in Settings > Integrations.");
    } finally {
      setIsGenerating(false);
    }
  };

  const sendToLovable = () => {
    if (!generatedLovablePrompt) return;
    const encodedPrompt = encodeURIComponent(generatedLovablePrompt);
    const lovableUrl = `https://lovable.dev/?autosubmit=true#prompt=${encodedPrompt}`;
    window.open(lovableUrl, "_blank");
  };

  const handleRefine = async () => {
    if (!refinement.trim()) return;
    const newRefinements = [...refinements, { role: "user", text: refinement }];
    setRefinements(newRefinements);
    setRefinement("");
    const siteId = currentSiteId || editingSite?.id;
    if (siteId) {
      try {
        await api.websites.update(siteId, { refinements: JSON.stringify(newRefinements) });
        setRefinements(prev => [...prev, { role: "ai", text: "Changes noted and saved." }]);
      } catch (err) {
        setRefinements(prev => [...prev, { role: "ai", text: "Failed to save refinement." }]);
      }
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    const siteId = currentSiteId || editingSite?.id;
    try {
      if (siteId) await api.websites.publish(siteId);
      setIsPublished(true);
    } catch (err) { console.error("Failed to publish:", err); }
    finally { setIsPublishing(false); }
  };

  const handleDeleteSite = async (id) => {
    try {
      await api.websites.del(id);
      setSites(prev => prev.filter(s => s.id !== id));
    } catch (err) { console.error("Failed to delete website:", err); }
  };

  const reloadSites = async () => {
    try {
      const data = await api.websites.list();
      const rows = Array.isArray(data) ? data : (data.websites || []);
      setSites(rows.map(s => ({
        id: s.id, name: s.name || "Untitled", domain: s.url || "",
        status: s.status || "draft",
        updated: s.updated_at ? new Date(s.updated_at).toLocaleDateString("en-GB", { month: "short", day: "numeric" }) : "",
        prompt: s.prompt || "", template: s.template || "",
        refinements: s.refinements || [],
      })));
    } catch (err) { console.error("Failed to reload websites:", err); }
  };

  const sectionTitle = { fontFamily: FONT, fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 };
  const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16 };
  const btn = (primary) => ({ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, background: primary ? COLORS.accent : COLORS.surfaceHover, color: primary ? "#000" : COLORS.text, opacity: (primary && !prompt.trim() && !editingSite) ? 0.4 : 1 });

  if (view === "builder" || editingSite) {
    return (
      <div style={{ padding: 28, fontFamily: FONT_BODY, color: COLORS.text, maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => { setView("sites"); setEditingSite(null); setSiteGenerated(false); setGeneratedLovablePrompt(null); setSelectedTemplate(null); setGenerateError(null); setIsPublished(false); setRefinements([]); setPrompt(""); setCurrentSiteId(null); reloadSites(); }} style={{ background: "none", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 13 }}>← Back</button>
          <span style={{ ...sectionTitle, margin: 0 }}>{editingSite ? `Editing — ${editingSite.name}` : "New Site"}</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: editingSite && siteGenerated ? "1fr 1fr" : "1fr", gap: 20 }}>
          <div>
            {!editingSite && generatedLovablePrompt && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ ...card }}>
                  <div style={sectionTitle}>Generated Lovable Prompt</div>
                  <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>{generatedLovablePrompt}</div>
                  <button onClick={sendToLovable} style={{ padding: "10px 20px", background: "#a855f7", color: "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Build in Lovable →</button>
                </div>
                <button onClick={() => { setGeneratedLovablePrompt(null); setGenerateError(null); }} style={{ ...btn(false) }}>Generate again</button>
              </div>
            )}

            {!editingSite && !generatedLovablePrompt && (
              <>
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={sectionTitle}>Describe your site</div>
                  <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g. A sleek landing page for my AI consulting agency with a hero, services grid, and booking CTA..." rows={4} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
                </div>
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={sectionTitle}>Quick-start templates</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {templates.map(t => (
                      <button key={t.id} onClick={() => setSelectedTemplate(prev => prev === t.id ? null : t.id)} style={{ ...btn(false), display: "flex", alignItems: "center", gap: 6, fontSize: 12, background: selectedTemplate === t.id ? COLORS.accentBg : COLORS.surfaceHover, border: selectedTemplate === t.id ? `1px solid ${COLORS.accent}44` : "1px solid transparent" }}>{t.icon} {t.label}</button>
                    ))}
                  </div>
                </div>
                {generateError && <div style={{ padding: "10px 14px", marginBottom: 12, background: "#ff4d4f12", border: "1px solid #ff4d4f33", borderRadius: 8, fontSize: 12, color: "#ff4d4f" }}>{generateError}</div>}
                <button onClick={handleGenerate} disabled={!prompt.trim() || isGenerating} style={btn(true)}>{isGenerating ? "Generating…" : "Generate Site"}</button>
              </>
            )}

            {editingSite && siteGenerated && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ ...card, flex: 1, display: "flex", flexDirection: "column" }}>
                  <div style={sectionTitle}>Refinement chat</div>
                  <div style={{ flex: 1, maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                    {refinements.map((r, i) => (
                      <div key={i} style={{ marginBottom: 8, textAlign: r.role === "user" ? "right" : "left" }}>
                        <span style={{ display: "inline-block", padding: "6px 12px", borderRadius: 8, fontSize: 12, background: r.role === "user" ? COLORS.accentBg : COLORS.surfaceHover, color: COLORS.text }}>{r.text}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={refinement} onChange={e => setRefinement(e.target.value)} onKeyDown={e => e.key === "Enter" && handleRefine()} placeholder="Ask for changes…" style={{ flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", color: COLORS.text, fontFamily: FONT_BODY, fontSize: 12 }} />
                    <button onClick={handleRefine} style={btn(true)}>Send</button>
                  </div>
                </div>
                <button onClick={handlePublish} disabled={isPublishing} style={btn(true)}>{isPublished ? "✓ Published" : isPublishing ? "Publishing…" : "Publish Site"}</button>
              </div>
            )}
          </div>

          {siteGenerated && (
            <div style={card}>
              <div style={sectionTitle}>Preview</div>
              <SitePreview />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 28, fontFamily: FONT_BODY, color: COLORS.text, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Website Builder</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Create and manage AI-generated sites</div>
        </div>
        <button onClick={() => setView("builder")} style={btn(true)}>+ New Site</button>
      </div>
      {sitesLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading websites...</div>
      ) : sites.length === 0 ? (
        <div style={{ padding: 24, background: COLORS.surface, border: `1px dashed ${COLORS.border}`, borderRadius: 12, color: COLORS.textDim, fontSize: 13, textAlign: "center" }}>No websites yet. Click "+ New Site" to create one.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sites.map(s => (
            <div key={s.id} onClick={() => { setEditingSite(s); setCurrentSiteId(s.id); setRefinements(s.refinements || []); if (s.prompt) { setPrompt(s.prompt); setSiteGenerated(true); } }} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", transition: "border-color .15s", borderColor: COLORS.border }} onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.borderActive} onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>{s.domain ? `${s.domain} · ` : ""}Updated {s.updated}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 600, background: s.status === "live" ? (COLORS.greenBg || COLORS.accentBg) : COLORS.warnBg, color: s.status === "live" ? (COLORS.green || COLORS.accent) : COLORS.warn }}>{s.status}</span>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteSite(s.id); }} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 14, padding: "2px 6px" }} title="Delete site">x</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentStudioView() {
  const [activePlatform, setActivePlatform] = useState("overview");
  const [overviewMode, setOverviewMode] = useState("kanban");
  const [platformSection, setPlatformSection] = useState("scheduled");
  const [inspirationTab, setInspirationTab] = useState("ideas");
  const [topic, setTopic] = useState("");
  const [format, setFormat] = useState("text");
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [schedulePost, setSchedulePost] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [expandedCard, setExpandedCard] = useState(null);
  const [hookMode, setHookMode] = useState("auto");
  const [selectedHookStyle, setSelectedHookStyle] = useState(null);
  const [selectedSavedHook, setSelectedSavedHook] = useState(null);

  const HOOK_STYLES = ["Pattern Interrupt", "Question", "Bold Claim", "Story", "Controversy", "Tutorial", "Before/After", "POV"];
  const SAVED_HOOKS = [
    { id: "h1", template: "Stop scrolling if you [pain point]...", style: "Pattern Interrupt", source: "Custom", example: "Stop scrolling if you're still doing outreach manually..." },
    { id: "h2", template: "I [impressive result] in [timeframe]. Here's how.", style: "Bold Claim", source: "Alex Hormozi", example: "I replaced a $200K employee with AI in 6 weeks. Here's how." },
    { id: "h3", template: "What if I told you [counterintuitive truth]?", style: "Question", source: "Custom", example: "What if I told you your cold emails are failing because they're too short?" },
    { id: "h4", template: "6 months ago I [starting point]. Today I [result].", style: "Before/After", source: "Cody Schneider", example: "6 months ago I had 0 clients. Today I run a $300K consultancy." },
    { id: "h5", template: "Nobody talks about this, but [insight]...", style: "Controversy", source: "Custom", example: "Nobody talks about this, but AI consultants are charging too little." },
    { id: "h6", template: "Here's the exact [system] I use to [outcome]", style: "Tutorial", source: "The Sales Technologies", example: "Here's the exact 5-step system I use to book 20 meetings per week." },
  ];

  const isVideoPlatform = ["tiktok", "youtube", "instagram"].includes(activePlatform);
  const PLATFORMS = [
    { key: "linkedin", label: "LinkedIn", icon: "💼", color: "#0077B5", connected: true },
    { key: "x", label: "X / Twitter", icon: "𝕏", color: "#1DA1F2", connected: true },
    { key: "instagram", label: "Instagram", icon: "📸", color: "#E1306C", connected: false },
    { key: "tiktok", label: "TikTok", icon: "🎵", color: "#010101", connected: false },
    { key: "youtube", label: "YouTube", icon: "▶️", color: "#FF0000", connected: false },
    { key: "facebook", label: "Facebook", icon: "📘", color: "#1877F2", connected: false },
  ];
  const STATUSES = ["Idea", "Drafting", "Review", "Scheduled", "Published"];
  const STATUS_COLORS_CS = { Idea: COLORS.blue, Drafting: "#8B5CF6", Review: COLORS.warn, Scheduled: COLORS.accent, Published: COLORS.green };
  const [contentItems] = useState([
    { id: 1, title: "Why AI won't replace consultants", platform: "linkedin", status: "Published", format: "text", date: "Feb 10", engagement: { impressions: 12400, likes: 234, comments: 47 }, snippet: "Most people think AI automation is about replacing humans. They're wrong..." },
    { id: 2, title: "5 AI Tools That Changed My Business", platform: "linkedin", status: "Published", format: "carousel", date: "Feb 8", engagement: { impressions: 15200, likes: 312, comments: 58 }, snippet: "A carousel breaking down the 5 tools I use daily..." },
    { id: 3, title: "Cold emails are failing because...", platform: "linkedin", status: "Scheduled", format: "text", date: "Feb 14", engagement: null, snippet: "Your cold emails are failing because of one thing..." },
    { id: 4, title: "3 tools that replaced my lead gen team", platform: "linkedin", status: "Scheduled", format: "carousel", date: "Feb 15", engagement: null, snippet: "3 tools that replaced my entire lead gen team →" },
    { id: 5, title: "The ROI of AI consulting", platform: "linkedin", status: "Review", format: "text", date: "Feb 16", engagement: null, snippet: "Draft ready for final review before scheduling..." },
    { id: 6, title: "Stop using AI like it's Google", platform: "tiktok", status: "Drafting", format: "video", date: "", engagement: null, snippet: "Script: Here's what the top 1% do instead..." },
    { id: 7, title: "I replaced a $200K employee with AI", platform: "youtube", status: "Drafting", format: "video", date: "", engagement: null, snippet: "Six months ago I built an AI agent..." },
    { id: 8, title: "The one-person agency model", platform: "x", status: "Scheduled", format: "thread", date: "Feb 13", engagement: null, snippet: "Thread: Why one-person businesses will outperform agencies" },
    { id: 9, title: "Client result: 40% revenue increase", platform: "instagram", status: "Idea", format: "reel", date: "", engagement: null, snippet: "Before/after showing client transformation" },
    { id: 10, title: "3 cold email mistakes", platform: "tiktok", status: "Idea", format: "video", date: "", engagement: null, snippet: "Quick-hit video on the 3 mistakes killing reply rates" },
  ]);

  const IDEAS = {
    ideas: [
      { id: "i1", title: "Why AI won't replace consultants (but will replace their methods)", tag: "Trending", tagColor: COLORS.warn },
      { id: "i2", title: "The real cost of bad lead data — and how to fix it", tag: "Competitor signal", tagColor: COLORS.blue },
      { id: "i3", title: "How I went from 0 to 100 client testimonials in 12 months", tag: "Follow-up", tagColor: COLORS.accent },
    ],
    calls: [
      { id: "c1", title: "\"We're spending $500/month on Apollo and getting nothing\"", quote: "Prospect paying for lead gen tools for 6 months with zero ROI.", source: "Lisa Park — Feb 8" },
      { id: "c2", title: "\"Our reps spend 3 hours a day just researching leads\"", quote: "Team manually researches every prospect.", source: "Marcus Johnson — Feb 5" },
    ],
    trending: {
      linkedin: [{ topic: "AI agents replacing SaaS tools", growth: "↑ 340%", level: "High", format: "Text post" }, { topic: "One-person businesses scaling past $1M", growth: "↑ 220%", level: "Very High", format: "Carousel" }],
      x: [{ topic: "AI replacing junior devs debate", growth: "↑ 500%", level: "Very High", format: "Thread" }],
      tiktok: [{ topic: "Day in the life of AI consultant", growth: "↑ 420%", level: "Very High", format: "Story" }],
      instagram: [{ topic: "Behind the scenes workflow", growth: "↑ 280%", level: "High", format: "Reel" }],
      youtube: [{ topic: "Full automation walkthrough", growth: "↑ 350%", level: "Very High", format: "Tutorial" }],
      facebook: [{ topic: "Small business AI adoption", growth: "↑ 180%", level: "Medium", format: "Text post" }],
    },
    competitors: [
      { id: "comp1", name: "Chris Walker", handle: "@chris_walker", followers: "142K", platform: "linkedin", post: "The demand gen playbook is broken...", engagement: "2.4K likes" },
      { id: "comp2", name: "Justin Welsh", handle: "@justinwelsh", followers: "580K", platform: "linkedin", post: "I've made $5M as a solopreneur...", engagement: "5.1K likes" },
    ],
  };

  const [generateError, setGenerateError] = useState(null);

  // Auto-save for content post inline edits
  const contentAutoSave = useAutoSave(async (postId, updates) => {
    if (postId) await api.contentPosts.update(postId, updates);
  }, 800);

  const updateGeneratedPost = (postIndex, field, value) => {
    setGeneratedPosts(prev => {
      const next = [...prev];
      next[postIndex] = { ...next[postIndex], [field]: value };
      if (next[postIndex].dbId) {
        contentAutoSave.trigger(next[postIndex].dbId, { [field === 'content' ? 'body_text' : field]: value });
      }
      return next;
    });
  };

  const getPlatform = (key) => PLATFORMS.find(p => p.key === key);
  const platColor = getPlatform(activePlatform)?.color || COLORS.accent;
  const csInputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const csLabelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const TIMELINE_DAYS = Array.from({ length: 21 }, (_, i) => { const d = new Date(2026, 1, i + 1); return { date: d, label: `Feb ${i + 1}`, day: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()] }; });
  const formatOptions = activePlatform === "linkedin" ? [{ key: "text", label: "✍️ Text" }, { key: "carousel", label: "📑 Carousel" }] : activePlatform === "x" ? [{ key: "tweet", label: "💬 Tweet" }, { key: "thread", label: "🧵 Thread" }] : activePlatform === "instagram" ? [{ key: "reel", label: "🎬 Reel" }, { key: "carousel", label: "📑 Carousel" }] : activePlatform === "tiktok" ? [{ key: "script", label: "🎬 Script" }] : activePlatform === "youtube" ? [{ key: "script", label: "🎬 Short" }] : [{ key: "text", label: "✍️ Text" }];
  const currentTrending = IDEAS.trending[activePlatform] || IDEAS.trending.linkedin;
  const currentCompetitors = IDEAS.competitors.filter(c => activePlatform === "overview" || c.platform === activePlatform || IDEAS.competitors.filter(cc => cc.platform === activePlatform).length === 0);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "18px 28px 0", flexShrink: 0 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, margin: 0 }}>Content <span style={{ color: COLORS.accent }}>Studio</span></h2>
          <p style={{ color: COLORS.textMuted, margin: "4px 0 0", fontSize: 12 }}>Create, schedule, and manage content across all platforms</p>
        </div>
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.border}`, overflow: "auto" }}>
          <button onClick={() => setActivePlatform("overview")} style={{ padding: "10px 18px", background: "transparent", border: "none", borderBottom: activePlatform === "overview" ? `2px solid ${COLORS.accent}` : "2px solid transparent", color: activePlatform === "overview" ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>📊 Overview</button>
          {PLATFORMS.map(p => (
            <button key={p.key} onClick={() => { setActivePlatform(p.key); setGeneratedPosts([]); setInspirationTab("ideas"); setPlatformSection("scheduled"); }} style={{ padding: "10px 14px", background: "transparent", border: "none", borderBottom: activePlatform === p.key ? `2px solid ${p.color}` : "2px solid transparent", color: activePlatform === p.key ? p.color : COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6, opacity: p.connected ? 1 : 0.5 }}>
              <span style={{ fontSize: 14 }}>{p.icon}</span>{p.label}
              {!p.connected && <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 3, background: COLORS.border, color: COLORS.textDim }}>setup</span>}
            </button>
          ))}
        </div>
      </div>

      {activePlatform === "overview" && (
        <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
            {STATUSES.map(s => (<div key={s} style={{ padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><div style={{ width: 7, height: 7, borderRadius: "50%", background: STATUS_COLORS_CS[s] }} /><span style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>{s.toUpperCase()}</span></div><div style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: STATUS_COLORS_CS[s] }}>{contentItems.filter(c => c.status === s).length}</div></div>))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 4, background: COLORS.bg, borderRadius: 8, padding: 3, border: `1px solid ${COLORS.border}` }}>
              <button onClick={() => setOverviewMode("kanban")} style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: overviewMode === "kanban" ? COLORS.accent : "transparent", color: overviewMode === "kanban" ? COLORS.bg : COLORS.textMuted }}>Kanban</button>
              <button onClick={() => setOverviewMode("timeline")} style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer", background: overviewMode === "timeline" ? COLORS.accent : "transparent", color: overviewMode === "timeline" ? COLORS.bg : COLORS.textMuted }}>Timeline</button>
            </div>
          </div>
          {overviewMode === "kanban" && (
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 16 }}>
              {STATUSES.map(status => { const items = contentItems.filter(c => c.status === status); const col = STATUS_COLORS_CS[status]; return (
                <div key={status} style={{ width: 230, flexShrink: 0 }}>
                  <div style={{ padding: "6px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}><div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: col }} /><span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600 }}>{status}</span></div><span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, background: COLORS.surface, padding: "2px 7px", borderRadius: 8 }}>{items.length}</span></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {items.map(item => { const plat = getPlatform(item.platform); return (
                      <div key={item.id} onClick={() => setExpandedCard(expandedCard === item.id ? null : item.id)} style={{ padding: "10px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", borderLeft: `3px solid ${plat?.color || COLORS.accent}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}><span style={{ fontSize: 10 }}>{plat?.icon}</span><span style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: plat?.color }}>{plat?.label}</span><span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, background: COLORS.bg, color: COLORS.textDim, fontFamily: FONT }}>{item.format}</span></div>
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 3, lineHeight: 1.4 }}>{item.title}</div>
                        {item.date && <div style={{ fontSize: 9, color: COLORS.textDim }}>{item.date}</div>}
                        {expandedCard === item.id && (<div style={{ marginTop: 6, padding: "6px 8px", background: COLORS.bg, borderRadius: 6, fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.snippet}{item.engagement && (<div style={{ display: "flex", gap: 8, marginTop: 4 }}><span style={{ fontSize: 9, color: COLORS.blue }}>{item.engagement.impressions?.toLocaleString()} views</span><span style={{ fontSize: 9, color: COLORS.accent }}>{item.engagement.likes} likes</span></div>)}</div>)}
                      </div>); })}
                  </div>
                </div>); })}
            </div>
          )}
          {overviewMode === "timeline" && (
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
              {TIMELINE_DAYS.map((day, di) => { const dayItems = contentItems.filter(c => c.date === day.label); const isWeekend = day.day === "Sat" || day.day === "Sun"; return (
                <div key={di} style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, minHeight: dayItems.length > 0 ? 48 : 32, background: isWeekend ? COLORS.bg + "88" : "transparent" }}>
                  <div style={{ width: 72, padding: "6px 10px", borderRight: `1px solid ${COLORS.border}`, flexShrink: 0 }}><div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: dayItems.length > 0 ? COLORS.text : COLORS.textDim }}>{day.label.split(" ")[1]}</div><div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim }}>{day.day}</div></div>
                  <div style={{ flex: 1, padding: dayItems.length > 0 ? "4px 10px" : 0, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    {dayItems.map(item => { const plat = getPlatform(item.platform); return (<div key={item.id} style={{ padding: "3px 8px", borderRadius: 6, background: (plat?.color || COLORS.accent) + "12", border: `1px solid ${(plat?.color || COLORS.accent)}33`, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 9 }}>{plat?.icon}</span><span style={{ fontSize: 10, fontWeight: 600, color: plat?.color }}>{item.title.length > 30 ? item.title.slice(0, 30) + "..." : item.title}</span></div>); })}
                  </div>
                </div>); })}
            </div>
          )}
        </div>
      )}

      {PLATFORMS.map(p => p.key).includes(activePlatform) && (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <div style={{ flex: 1, overflow: "auto", padding: "20px 24px", borderRight: `1px solid ${COLORS.border}` }}>
            <div style={{ padding: "20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><span style={{ fontSize: 16 }}>{getPlatform(activePlatform)?.icon}</span><span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: platColor }}>Create {getPlatform(activePlatform)?.label} Content</span></div>
              <div style={{ marginBottom: 14 }}><label style={csLabelStyle}>TOPIC / IDEA</label><textarea value={topic} onChange={e => setTopic(e.target.value)} placeholder="Pick an idea from the right panel, or write your own..." rows={3} style={{ ...csInputStyle, resize: "vertical", lineHeight: 1.6 }} /></div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}><label style={csLabelStyle}>FORMAT</label><div style={{ display: "flex", gap: 6 }}>{formatOptions.map(f => (<button key={f.key} onClick={() => setFormat(f.key)} style={{ flex: 1, padding: "8px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, background: format === f.key ? platColor + "15" : "transparent", border: `1px solid ${format === f.key ? platColor + "44" : COLORS.border}`, color: format === f.key ? platColor : COLORS.textMuted, cursor: "pointer" }}>{f.label}</button>))}</div></div>
                {!isVideoPlatform && (<button onClick={async () => { setIsGenerating(true); setGenerateError(null); try { const res = await api.contentPosts.generate({ topic: topic.trim(), format }); const posts = Array.isArray(res?.posts) ? res.posts : []; setGeneratedPosts(posts); if (!posts.length) setGenerateError('No content generated. Try a different topic.'); } catch (err) { setGenerateError(err?.message || 'Failed to generate. Check your Anthropic API key in Settings.'); } setIsGenerating(false); }} disabled={isGenerating || !topic.trim()} style={{ padding: "12px 24px", background: topic.trim() ? platColor : COLORS.border, color: topic.trim() ? "#fff" : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: topic.trim() && !isGenerating ? "pointer" : "default", whiteSpace: "nowrap" }}>{isGenerating ? "Generating..." : "✍️ Generate"}</button>)}
              </div>
              {isVideoPlatform && (<div style={{ marginTop: 14 }}><button onClick={async () => { setIsGenerating(true); setGenerateError(null); try { const res = await api.contentPosts.generate({ topic: topic.trim(), format: 'text', context: `Platform: ${activePlatform}. Create a short-form video script with hook, body, and CTA sections.` }); const posts = Array.isArray(res?.posts) ? res.posts : []; setGeneratedPosts(posts.map(p => ({ id: p.id, hook: (p.content || '').split('\n\n')[0] || '', body: (p.content || '').split('\n\n').slice(1, -1).join('\n\n') || p.content || '', cta: (p.content || '').split('\n\n').slice(-1)[0] || '', caption: p.imageDescription || '', duration: '30-60s' }))); } catch (err) { setGenerateError(err?.message || 'Failed to generate. Check your API key.'); } setIsGenerating(false); }} disabled={isGenerating || !topic.trim()} style={{ padding: "12px 24px", background: topic.trim() ? platColor : COLORS.border, color: topic.trim() ? "#fff" : COLORS.textDim, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, width: "100%", cursor: topic.trim() && !isGenerating ? "pointer" : "default" }}>{isGenerating ? "Generating Script..." : "🎬 Generate Script"}</button></div>)}
            </div>
            {isGenerating && (<div style={{ padding: "40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center", marginBottom: 20 }}><div style={{ fontSize: 32, marginBottom: 8 }}>{isVideoPlatform ? "🎬" : "✍️"}</div><div style={{ fontFamily: FONT, fontSize: 12, color: platColor }}>Writing your {getPlatform(activePlatform)?.label} content...</div></div>)}
            {generateError && !isGenerating && (<div style={{ padding: "14px 18px", background: "#dc262615", border: "1px solid #dc262633", borderRadius: 10, marginBottom: 16 }}><div style={{ fontSize: 12, color: "#dc2626", lineHeight: 1.5 }}>{generateError}</div></div>)}
            {generatedPosts.length > 0 && !isGenerating && !isVideoPlatform && (<div style={{ marginBottom: 20 }}>{generatedPosts.map((post, i) => (<div key={post.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 10 }}><div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}` }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontFamily: FONT, fontSize: 11, color: platColor, fontWeight: 600 }}>OPTION {i + 1}</span><AutoSaveIndicator status={contentAutoSave.saveStatus} /></div><div style={{ display: "flex", gap: 6 }}><button onClick={() => navigator.clipboard?.writeText(post.content || "")} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Copy</button><button onClick={() => setSchedulePost(post)} style={{ padding: "4px 10px", background: platColor, color: "#fff", border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Schedule →</button></div></div><div style={{ padding: "14px 18px" }}><textarea value={post.content || ''} onChange={e => updateGeneratedPost(i, 'content', e.target.value)} style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line", width: "100%", background: "transparent", border: `1px solid transparent`, borderRadius: 6, fontFamily: FONT_BODY, outline: "none", resize: "vertical", minHeight: 100, padding: "8px 10px", boxSizing: "border-box" }} onFocus={e => { e.target.style.borderColor = COLORS.border; e.target.style.background = COLORS.bg; }} onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }} /></div></div>))}</div>)}
            {generatedPosts.length > 0 && !isGenerating && isVideoPlatform && (<div style={{ marginBottom: 20 }}>{generatedPosts.map((script, i) => (<div key={script.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 12 }}><div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}` }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontFamily: FONT, fontSize: 11, color: platColor, fontWeight: 600 }}>SCRIPT {i + 1}</span>{script.duration && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: COLORS.bg, color: COLORS.textDim, fontFamily: FONT }}>~{script.duration}</span>}</div><div style={{ display: "flex", gap: 6 }}><button onClick={() => navigator.clipboard?.writeText(`${script.hook}\n\n${script.body}\n\n${script.cta}`)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Copy Script</button></div></div><div style={{ padding: "16px 18px" }}><div style={{ marginBottom: 14 }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ fontSize: 11 }}>🪝</span><span style={{ fontFamily: FONT, fontSize: 9, fontWeight: 600, color: platColor, letterSpacing: "0.06em" }}>HOOK</span></div><div style={{ padding: "10px 14px", background: platColor + "08", border: `1px solid ${platColor}22`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: COLORS.text, lineHeight: 1.5 }}>{script.hook}</div></div><div style={{ marginBottom: 14 }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ fontSize: 11 }}>📝</span><span style={{ fontFamily: FONT, fontSize: 9, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em" }}>SCRIPT</span></div><div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{script.body}</div></div><div><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ fontSize: 11 }}>👋</span><span style={{ fontFamily: FONT, fontSize: 9, fontWeight: 600, color: COLORS.textDim, letterSpacing: "0.06em" }}>CTA</span></div><div style={{ fontSize: 12, color: COLORS.accent, fontWeight: 600, fontStyle: "italic" }}>{script.cta}</div></div></div></div>))}</div>)}
            <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 14 }}>{[{ key: "scheduled", label: "📅 Scheduled" }, { key: "published", label: "📊 Published" }].map(tab => (<button key={tab.key} onClick={() => setPlatformSection(tab.key)} style={{ padding: "8px 16px", background: "transparent", border: "none", borderBottom: platformSection === tab.key ? `2px solid ${platColor}` : "2px solid transparent", color: platformSection === tab.key ? platColor : COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{tab.label} <span style={{ fontSize: 10, color: COLORS.textDim }}>({contentItems.filter(c => c.platform === activePlatform && c.status === (tab.key === "scheduled" ? "Scheduled" : "Published")).length})</span></button>))}</div>
            {platformSection === "scheduled" && (<div>{contentItems.filter(c => c.platform === activePlatform && c.status === "Scheduled").map(item => (<div key={item.id} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 12, fontWeight: 600 }}>{item.title}</div><div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{item.date} · {item.format}</div></div><span style={{ padding: "3px 10px", borderRadius: 20, fontFamily: FONT, fontSize: 10, fontWeight: 500, background: COLORS.accent + "10", color: COLORS.accent }}>scheduled</span></div>))}{contentItems.filter(c => c.platform === activePlatform && c.status === "Scheduled").length === 0 && (<div style={{ padding: "30px", textAlign: "center", color: COLORS.textDim, fontSize: 12 }}>No scheduled posts. Generate content above and schedule it.</div>)}</div>)}
            {platformSection === "published" && (<div>{contentItems.filter(c => c.platform === activePlatform && c.engagement).map(item => (<div key={item.id} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}><div><div style={{ fontSize: 12, fontWeight: 600 }}>{item.title}</div><div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{item.date} · {item.format}</div></div><div style={{ display: "flex", gap: 12 }}>{[{ label: "Views", value: item.engagement?.impressions?.toLocaleString(), color: COLORS.text }, { label: "Likes", value: item.engagement?.likes, color: platColor }, { label: "Comments", value: item.engagement?.comments, color: COLORS.warn }].map(stat => (<div key={stat.label} style={{ textAlign: "center" }}><div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: stat.color }}>{stat.value}</div><div style={{ fontSize: 8, color: COLORS.textDim, fontFamily: FONT }}>{stat.label.toUpperCase()}</div></div>))}</div></div></div>))}{contentItems.filter(c => c.platform === activePlatform && c.engagement).length === 0 && (<div style={{ padding: "30px", textAlign: "center", color: COLORS.textDim, fontSize: 12 }}>No published posts yet for {getPlatform(activePlatform)?.label}.</div>)}</div>)}
          </div>
          <div style={{ width: 320, flexShrink: 0, overflow: "auto", background: COLORS.surface }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}` }}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{[{ key: "ideas", label: "💡 Ideas" }, { key: "calls", label: "🎙️ Calls" }, { key: "trending", label: "🔥 Trending" }, { key: "competitors", label: "🏢 Rivals" }].map(tab => (<button key={tab.key} onClick={() => setInspirationTab(tab.key)} style={{ padding: "5px 8px", borderRadius: 5, fontSize: 10, fontFamily: FONT, fontWeight: 600, cursor: "pointer", background: inspirationTab === tab.key ? platColor + "12" : "transparent", border: `1px solid ${inspirationTab === tab.key ? platColor + "44" : "transparent"}`, color: inspirationTab === tab.key ? platColor : COLORS.textDim }}>{tab.label}</button>))}</div></div>
            <div style={{ padding: "12px 16px" }}>
              {inspirationTab === "ideas" && IDEAS.ideas.map(idea => (<div key={idea.id} onClick={() => setTopic(idea.title)} style={{ padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", marginBottom: 6 }} onMouseEnter={e => e.currentTarget.style.borderColor = platColor + "55"} onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, lineHeight: 1.4 }}>{idea.title}</div><span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 8, background: idea.tagColor + "15", color: idea.tagColor, fontFamily: FONT, fontWeight: 600 }}>{idea.tag}</span></div>))}
              {inspirationTab === "calls" && IDEAS.calls.map(idea => (<div key={idea.id} onClick={() => setTopic(idea.title.replace(/"/g, ""))} style={{ padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", marginBottom: 6 }}><div style={{ fontSize: 12, fontWeight: 600, color: COLORS.warn, marginBottom: 3 }}>{idea.title}</div><div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.4, marginBottom: 4 }}>{idea.quote}</div><span style={{ fontSize: 8, color: COLORS.textDim, fontFamily: FONT }}>{idea.source}</span></div>))}
              {inspirationTab === "trending" && currentTrending.map((trend, i) => (<div key={i} onClick={() => setTopic(trend.topic)} style={{ padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", marginBottom: 6 }}><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{trend.topic}</div><div style={{ display: "flex", gap: 6, alignItems: "center" }}><span style={{ fontSize: 9, fontFamily: FONT, fontWeight: 600, color: COLORS.green }}>{trend.growth}</span><span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, fontFamily: FONT, fontWeight: 600, background: trend.level === "Very High" ? COLORS.accent + "15" : COLORS.green + "15", color: trend.level === "Very High" ? COLORS.accent : COLORS.green }}>{trend.level}</span></div></div>))}
              {inspirationTab === "competitors" && currentCompetitors.map(comp => { const compPlat = getPlatform(comp.platform); return (<div key={comp.id} onClick={() => setTopic(`Response to: ${comp.post}`)} style={{ padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", marginBottom: 6 }}><div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: (compPlat?.color || COLORS.accent) + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: compPlat?.color }}>{comp.name.charAt(0)}</div><div><div style={{ fontSize: 11, fontWeight: 600 }}>{comp.name}</div><div style={{ fontSize: 8, color: COLORS.textDim }}>{comp.followers}</div></div></div><div style={{ fontSize: 11, color: COLORS.text, marginBottom: 3, lineHeight: 1.4 }}>{comp.post}</div><span style={{ fontSize: 8, color: compPlat?.color }}>{comp.engagement}</span></div>); })}
            </div>
          </div>
        </div>
      )}

      {schedulePost && (<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSchedulePost(null)}><div onClick={e => e.stopPropagation()} style={{ width: 440, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}><div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}` }}><div style={{ fontWeight: 600, fontSize: 15 }}>Schedule Post</div><div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Publishing to {getPlatform(activePlatform)?.label}</div></div><div style={{ padding: "20px 24px" }}><div style={{ display: "flex", gap: 14 }}><div style={{ flex: 1 }}><label style={csLabelStyle}>DATE</label><input value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} type="date" style={csInputStyle} /></div><div style={{ flex: 1 }}><label style={csLabelStyle}>TIME</label><input value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} type="time" style={csInputStyle} /></div></div></div><div style={{ padding: "12px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 8 }}><button onClick={() => setSchedulePost(null)} style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button><button onClick={() => setSchedulePost(null)} style={{ padding: "8px 20px", background: platColor, color: "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Schedule →</button></div></div></div>)}
    </div>
  );
}

function LinkedInContentView() {
  const [activeTab, setActiveTab] = useState("create");
  const [topic, setTopic] = useState("");
  const [format, setFormat] = useState("text");
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [schedulePost, setSchedulePost] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");

  const CONTENT_IDEAS = [
    { id: "i1", title: "Why AI won't replace consultants (but will replace their methods)", source: "trending", tag: "Trending in your industry", tagColor: COLORS.warn },
    { id: "i2", title: "The real cost of bad lead data — and how to fix it", source: "competitor", tag: "Your competitor posted about this", tagColor: COLORS.blue },
    { id: "i3", title: "How I went from 0 to 100 client testimonials in 12 months", source: "profile", tag: "Follow-up to your Feb 8 post", tagColor: COLORS.accent },
    { id: "i4", title: "The one-person agency model is the future of consulting", source: "pillar", tag: "Core content pillar", tagColor: "#7B61FF" },
    { id: "i5", title: "3 AI tools most people don't know exist (but should)", source: "engagement", tag: "High engagement topic", tagColor: COLORS.accent },
  ];

  const [callInsights, setCallInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  useEffect(() => {
    setInsightsLoading(true);
    api.contentPosts.extractInsights().then(data => {
      const ins = Array.isArray(data?.insights) ? data.insights : [];
      setCallInsights(ins.map((item, i) => ({ id: `ci_${i}`, title: item.title || '', quote: item.quote || '', source: item.source || '', callDate: item.callDate || '' })));
    }).catch(err => {
      setInsightsError(err?.message || 'Could not extract insights');
    }).finally(() => setInsightsLoading(false));
  }, []);

  const [MOCK_COMPETITORS, setCompetitors] = useState([]);

  useEffect(() => {
    api.trackedCompetitors.list().then(data => {
      const comps = Array.isArray(data) ? data : data.tracked_competitors || [];
      setCompetitors(comps.map(c => ({ ...c, handle: c.handle || '', followers: c.followers || '', recentPost: c.recent_post || c.recentPost || '', engagement: c.engagement || '', tracked: true })));
    }).catch(() => {});
  }, []);

  const [MOCK_SCHEDULED, setScheduledPosts] = useState([]);
  const [MOCK_PUBLISHED, setPublishedPosts] = useState([]);

  useEffect(() => {
    api.contentPosts.list({ status: 'scheduled', platform: 'linkedin' }).then(data => {
      const posts = Array.isArray(data) ? data : data.content_posts || [];
      setScheduledPosts(posts.map(p => ({ ...p, date: p.scheduled_date || p.date || '', time: p.scheduled_time || p.time || '' })));
    }).catch(() => {});
    api.contentPosts.list({ status: 'published', platform: 'linkedin' }).then(data => {
      const posts = Array.isArray(data) ? data : data.content_posts || [];
      setPublishedPosts(posts.map(p => ({ ...p, date: p.published_date || p.date || '', impressions: p.impressions || 0, likes: p.likes || 0, comments: p.comments || 0, reposts: p.reposts || 0 })));
    }).catch(() => {});
  }, []);

  const [generateError, setGenerateError] = useState(null);

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };

  const selectIdea = (title) => { setTopic(title); };

  const generate = async () => {
    if (!topic.trim() || isGenerating) return;
    setIsGenerating(true); setGenerateError(null); setGeneratedPosts([]);
    try {
      const res = await api.contentPosts.generate({ topic: topic.trim(), format });
      const posts = Array.isArray(res?.posts) ? res.posts : [];
      setGeneratedPosts(posts);
      if (posts.length === 0) setGenerateError('No content was generated. Try a different topic.');
    } catch (err) {
      setGenerateError(err?.message || 'Failed to generate content. Check your Anthropic API key.');
    }
    setIsGenerating(false);
  };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          LinkedIn <span style={{ color: COLORS.blue }}>Content</span>
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Create, schedule, and publish LinkedIn posts</p>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[
          { key: "create", label: "Create" },
          { key: "competitors", label: "Competitors", count: MOCK_COMPETITORS.length },
          { key: "calendar", label: "Scheduled", count: MOCK_SCHEDULED.length },
          { key: "published", label: "Published", count: MOCK_PUBLISHED.length },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.key ? `2px solid ${COLORS.blue}` : "2px solid transparent",
            color: activeTab === tab.key ? COLORS.blue : COLORS.textMuted,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}>
            {tab.label}
            {tab.count !== undefined && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: activeTab === tab.key ? COLORS.blueBg : COLORS.surface, color: activeTab === tab.key ? COLORS.blue : COLORS.textDim }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Create Tab */}
      {activeTab === "create" && (
        <div>
          {/* Idea Sections */}
          <div style={{ display: "flex", gap: 20, marginBottom: 24 }}>
            {/* Content Ideas */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 16 }}>💡</span>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>CONTENT IDEAS</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {CONTENT_IDEAS.map(idea => (
                  <div key={idea.id} onClick={() => selectIdea(idea.title)} style={{
                    padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.blue + "66"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 6, lineHeight: 1.4 }}>{idea.title}</div>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: idea.tagColor + "15", color: idea.tagColor, fontFamily: FONT, fontWeight: 500 }}>{idea.tag}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* From Calls */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 16 }}>🎙️</span>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600 }}>FROM YOUR CALLS</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {insightsLoading ? (
                  <div style={{ padding: "30px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "center" }}>
                    <ProgressDots active={true} />
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 8 }}>Extracting insights from your calls...</div>
                  </div>
                ) : callInsights.length === 0 ? (
                  <div style={{ padding: "30px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "center" }}>
                    <div style={{ fontSize: 24, marginBottom: 6, opacity: 0.2 }}>🎙️</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim }}>{insightsError || 'No call transcripts yet. Add transcripts in Strategy → Transcripts to see insights here.'}</div>
                  </div>
                ) : callInsights.map(idea => (
                  <div key={idea.id} onClick={() => selectIdea(idea.title.replace(/[""]/g, ""))} style={{
                    padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.warn + "66"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.warn, marginBottom: 4, lineHeight: 1.4 }}>{idea.title}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5, marginBottom: 6 }}>{idea.quote}</div>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textDim, fontFamily: FONT }}>{idea.source}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Generator */}
          <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>TOPIC / IDEA</label>
              <textarea value={topic} onChange={e => setTopic(e.target.value)} placeholder="Pick an idea above or write your own — what do you want to talk about?" rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>FORMAT</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { key: "text", label: "✍️ Text Post" },
                    { key: "image", label: "🖼️ Image Post" },
                    { key: "carousel", label: "📑 Carousel" },
                  ].map(f => (
                    <button key={f.key} onClick={() => setFormat(f.key)} style={{
                      flex: 1, padding: "10px", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
                      background: format === f.key ? COLORS.blue + "15" : "transparent",
                      border: `1px solid ${format === f.key ? COLORS.blue + "44" : COLORS.border}`,
                      color: format === f.key ? COLORS.blue : COLORS.textMuted, cursor: "pointer",
                    }}>{f.label}</button>
                  ))}
                </div>
              </div>
              <button onClick={generate} disabled={isGenerating || !topic.trim()} style={{
                padding: "14px 28px", background: topic.trim() ? COLORS.blue : COLORS.border,
                color: topic.trim() ? "#fff" : COLORS.textDim,
                border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 13, fontWeight: 600,
                cursor: topic.trim() && !isGenerating ? "pointer" : "default", whiteSpace: "nowrap", height: 46,
              }}>
                {isGenerating ? "Generating..." : "✍️ Generate"}
              </button>
            </div>
          </div>

          {/* Generated Results */}
          {generateError && !isGenerating && (
            <div style={{ padding: "16px 20px", background: COLORS.danger + "10", border: `1px solid ${COLORS.danger}33`, borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: COLORS.danger }}>{generateError}</div>
            </div>
          )}
          {isGenerating && (
            <div style={{ padding: "50px 40px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✍️</div>
              <div style={{ fontFamily: FONT, fontSize: 13, color: COLORS.blue }}>Writing your content...</div>
              <ProgressDots active={true} />
            </div>
          )}

          {generatedPosts.length > 0 && !isGenerating && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {generatedPosts.map((post, i) => (
                <div key={post.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.blue, fontWeight: 600 }}>OPTION {i + 1}</span>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT }}>{post.format === "carousel" ? "carousel" : post.format === "image" ? "image post" : "text post"}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => navigator.clipboard?.writeText(post.content || "")} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Copy</button>
                      <button onClick={() => setSchedulePost(post)} style={{ padding: "5px 12px", background: COLORS.blue, color: "#fff", border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Schedule →</button>
                    </div>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    {post.format === "carousel" ? (
                      <div>
                        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
                          {post.slides.map(slide => (
                            <div key={slide.slide} style={{ width: 160, height: 200, flexShrink: 0, borderRadius: 10, background: slide.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, textAlign: "center" }}>
                              <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: "#fff", lineHeight: 1.5, whiteSpace: "pre-line" }}>{slide.text}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 8 }}>{post.slides.length} slides</div>
                      </div>
                    ) : (
                      <div>
                        {post.format === "image" && (
                          <div style={{ width: "100%", height: 200, borderRadius: 10, marginBottom: 12, background: `linear-gradient(135deg, ${COLORS.blue}33, ${COLORS.accent}33)`, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${COLORS.border}` }}>
                            <div style={{ textAlign: "center" }}><div style={{ fontSize: 32, marginBottom: 6 }}>🖼️</div><div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>AI-generated image</div></div>
                          </div>
                        )}
                        <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{post.content}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Schedule Modal */}
      {schedulePost && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSchedulePost(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 440, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Schedule Post</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Will publish directly to LinkedIn via API</div>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ padding: "12px 16px", background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, maxHeight: 100, overflow: "auto" }}>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>{schedulePost.content ? schedulePost.content.substring(0, 150) + "..." : `${schedulePost.slides?.length || 0} slide carousel`}</div>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>DATE</label><input value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} type="date" style={inputStyle} /></div>
                <div style={{ flex: 1 }}><label style={labelStyle}>TIME</label><input value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} type="time" style={inputStyle} /></div>
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setSchedulePost(null)} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => setSchedulePost(null)} style={{ padding: "10px 24px", background: COLORS.blue, color: "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Schedule →</button>
            </div>
          </div>
        </div>
      )}

      {/* Competitors Tab */}
      {activeTab === "competitors" && (
        <div>
          <p style={{ color: COLORS.textMuted, marginBottom: 20, fontSize: 13 }}>Track LinkedIn creators and competitors. Their content signals feed into your topic ideas.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button style={{ padding: "10px 20px", background: COLORS.blue, color: "#fff", border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ ADD PROFILE</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {MOCK_COMPETITORS.map(comp => (
              <div key={comp.id} style={{ padding: "18px 22px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: COLORS.blue + "25", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 16, color: COLORS.blue }}>{comp.name.charAt(0)}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{comp.name}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>{comp.handle} · {comp.followers} followers</div>
                    </div>
                  </div>
                  <span style={{ padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, background: COLORS.blueBg, color: COLORS.blue, border: `1px solid ${COLORS.blue}33` }}>tracking</span>
                </div>
                <div style={{ padding: "10px 14px", background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>LATEST POST</div>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4 }}>{comp.recentPost}</div>
                  <div style={{ fontSize: 11, color: COLORS.blue }}>{comp.engagement}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === "calendar" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {MOCK_SCHEDULED.map(post => (
            <div key={post.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 48, textAlign: "center" }}>
                  <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: COLORS.blue }}>{post.date.split(" ")[1]}</div>
                  <div style={{ fontFamily: FONT, fontSize: 9, color: COLORS.textDim }}>{post.date.split(" ")[0]}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4, maxWidth: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{post.content}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT }}>{post.format}</span>
                    <span style={{ fontSize: 11, color: COLORS.textDim }}>{post.time}</span>
                  </div>
                </div>
              </div>
              <span style={{ padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, background: COLORS.blueBg, color: COLORS.blue, border: `1px solid ${COLORS.blue}33` }}>{post.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Published Tab */}
      {activeTab === "published" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {MOCK_PUBLISHED.map(post => (
            <div key={post.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, marginRight: 20 }}>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 6, lineHeight: 1.5 }}>{post.content.substring(0, 150)}...</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT }}>{post.format}</span>
                    <span style={{ fontSize: 11, color: COLORS.textDim }}>{post.date}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16 }}>
                  {[
                    { label: "Impressions", value: post.impressions.toLocaleString(), color: COLORS.text },
                    { label: "Likes", value: post.likes, color: COLORS.blue },
                    { label: "Comments", value: post.comments, color: COLORS.accent },
                    { label: "Reposts", value: post.reposts, color: COLORS.warn },
                  ].map(stat => (
                    <div key={stat.label} style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                      <div style={{ fontFamily: FONT, fontSize: 8, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>{stat.label.toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CommunityView() {
  const [activeTab, setActiveTab] = useState("feed");
  const [mode, setMode] = useState("review");
  const [accounts, setAccounts] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountPlatform, setNewAccountPlatform] = useState("skool");
  const [newAccountName, setNewAccountName] = useState("");
  const [voiceSamples, setVoiceSamples] = useState([]);

  useEffect(() => {
    api.community.accounts.list().then(data => setAccounts(Array.isArray(data) ? data : data.accounts || [])).catch(() => {});
    api.community.keywords.list().then(data => {
      const kws = Array.isArray(data) ? data : data.keywords || [];
      setKeywords(kws.map(k => typeof k === 'string' ? k : k.keyword || k.text || ''));
    }).catch(() => {});
    api.community.voiceSamples.list().then(data => setVoiceSamples(Array.isArray(data) ? data : data.voice_samples || [])).catch(() => {});
  }, []);
  const [activityItems, setActivityItems] = useState([]);
  useEffect(() => {
    api.community.feed.list().then(data => {
      const items = Array.isArray(data) ? data : data.items || [];
      setActivityItems(items.slice(0, 10).map(item => ({
        platform: item.platform || 'linkedin',
        text: item.title || item.text || 'Activity',
        time: item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Recently',
      })));
    }).catch(() => {});
  }, []);
  const [newSampleText, setNewSampleText] = useState("");
  const [newSampleType, setNewSampleType] = useState("linkedin_post");
  const [showAgentWarning, setShowAgentWarning] = useState(false);

  const PLATFORMS = [
    { key: "skool", label: "Skool", icon: "🎓", color: "#4CAF50" },
    { key: "linkedin", label: "LinkedIn", icon: "💼", color: COLORS.blue },
    { key: "facebook", label: "Facebook", icon: "👥", color: "#1877F2" },
    { key: "instagram", label: "Instagram", icon: "📸", color: "#E1306C" },
    { key: "discord", label: "Discord", icon: "🎮", color: "#5865F2" },
    { key: "reddit", label: "Reddit", icon: "🔴", color: "#FF4500" },
    { key: "circle", label: "Circle", icon: "⭕", color: "#7B61FF" },
  ];

  const SAMPLE_TYPES = [
    { key: "linkedin_post", label: "LinkedIn Post" },
    { key: "blog", label: "Blog / Article" },
    { key: "comment", label: "Comment / Reply" },
    { key: "email", label: "Email" },
    { key: "tweet", label: "Tweet / X Post" },
    { key: "video_script", label: "Video Script" },
  ];

  const [MOCK_FEED, setFeedPosts] = useState([]);

  useEffect(() => {
    api.community.feed.list().then(data => {
      const posts = Array.isArray(data) ? data : data.feed || [];
      setFeedPosts(posts.map(p => ({ ...p, matchedKeywords: p.matched_keywords || p.matchedKeywords || [], draftReply: p.draft_reply || p.draftReply || '' })));
    }).catch(() => {});
  }, []);

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 };
  const getPlatform = (key) => PLATFORMS.find(p => p.key === key) || PLATFORMS[0];

  const handleModeSwitch = (newMode) => {
    if (newMode === "agent" && mode !== "agent") {
      setShowAgentWarning(true);
    } else {
      setMode(newMode);
    }
  };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      {/* Agent Warning Modal */}
      {showAgentWarning && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowAgentWarning(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 480, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>🤖</span>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Enable Agent Mode</div>
              </div>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ padding: "16px", background: COLORS.warn + "10", border: `1px solid ${COLORS.warn}33`, borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.warn, marginBottom: 6 }}>⚠️ Important</div>
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
                  When Agent Mode is enabled, the AI agent will automatically post responses to community posts on your behalf. The agent will use your connected account credentials to log in and comment directly.
                </div>
              </div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 6 }}>
                The agent will:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                {[
                  "Monitor your connected communities for keyword matches",
                  "Draft responses using your trained voice profile",
                  "Automatically post comments using your credentials",
                  "Log all actions in the agent activity feed",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: COLORS.text }}>
                    <span style={{ color: COLORS.accent, flexShrink: 0, marginTop: 2 }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>
                You can switch back to Review Mode at any time to pause automatic posting.
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setShowAgentWarning(false)} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => { setMode("agent"); setShowAgentWarning(false); }} style={{ padding: "10px 24px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Enable Agent Mode</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Community <span style={{ color: COLORS.accent }}>Monitor</span>
          </h2>
          <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>Track keywords across communities and engage with AI-drafted responses</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: FONT, fontSize: 11, color: mode === "review" ? COLORS.text : COLORS.textDim }}>Review</span>
          <div onClick={() => handleModeSwitch(mode === "review" ? "agent" : "review")} style={{
            width: 44, height: 24, borderRadius: 12, cursor: "pointer",
            background: mode === "agent" ? COLORS.accent : COLORS.borderActive,
            position: "relative", transition: "background 0.2s",
          }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: mode === "agent" ? 22 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
          <span style={{ fontFamily: FONT, fontSize: 11, color: mode === "agent" ? COLORS.accent : COLORS.textDim }}>Agent</span>
        </div>
      </div>

      {/* Agent Mode Banner */}
      {mode === "agent" && (
        <div style={{ padding: "12px 18px", background: COLORS.accent + "10", border: `1px solid ${COLORS.accent}33`, borderRadius: 8, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🤖</span>
          <div style={{ fontSize: 12, color: COLORS.accent, fontWeight: 500 }}>Agent Mode is active — the AI will automatically respond to matching posts on your behalf</div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[
          { key: "feed", label: "Feed", count: MOCK_FEED.length },
          { key: "activity", label: "Activity" },
          { key: "accounts", label: "Connected Accounts", count: accounts.length },
          { key: "keywords", label: "Keywords", count: keywords.length },
          { key: "voice", label: "Voice Training", count: voiceSamples.length },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {tab.label}
            <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: activeTab === tab.key ? COLORS.accentBg : COLORS.surface, color: activeTab === tab.key ? COLORS.accent : COLORS.textDim }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Accounts Tab */}
      {activeTab === "accounts" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setShowAddAccount(!showAddAccount)} style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ CONNECT ACCOUNT</button>
          </div>

          {showAddAccount && (
            <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.accent}33`, borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Connect New Account</div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>PLATFORM</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {PLATFORMS.map(p => (
                    <div key={p.key} onClick={() => setNewAccountPlatform(p.key)} style={{
                      padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                      background: newAccountPlatform === p.key ? p.color + "20" : "transparent",
                      border: `1px solid ${newAccountPlatform === p.key ? p.color + "55" : COLORS.border}`,
                      display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
                    }}>
                      <span>{p.icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: newAccountPlatform === p.key ? p.color : COLORS.textMuted }}>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>COMMUNITY / GROUP NAME</label>
                <input value={newAccountName} onChange={e => setNewAccountName(e.target.value)} placeholder="e.g. AI Automation Community" style={inputStyle} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={() => setShowAddAccount(false)} style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button onClick={() => { if (newAccountName.trim()) { setAccounts([...accounts, { id: Date.now(), platform: newAccountPlatform, name: newAccountName.trim(), connected: true }]); setNewAccountName(""); setShowAddAccount(false); } }} style={{ padding: "8px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Connect</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {accounts.map(acc => {
              const plat = getPlatform(acc.platform);
              return (
                <div key={acc.id} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: plat.color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{plat.icon}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{acc.name}</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>{plat.label}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ padding: "4px 12px", borderRadius: 20, fontFamily: FONT, fontSize: 11, fontWeight: 500, background: acc.connected ? COLORS.accentBg : COLORS.dangerBg, color: acc.connected ? COLORS.accent : COLORS.danger, border: `1px solid ${acc.connected ? COLORS.accent + "33" : COLORS.danger + "33"}` }}>
                      {acc.connected ? "connected" : "disconnected"}
                    </span>
                    <button onClick={() => setAccounts(accounts.filter(a => a.id !== acc.id))} style={{ padding: "4px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer" }}>×</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Keywords Tab */}
      {activeTab === "keywords" && (
        <div>
          <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 16 }}>
            <label style={labelStyle}>ADD KEYWORD TO TRACK</label>
            <div style={{ display: "flex", gap: 10 }}>
              <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newKeyword.trim()) { setKeywords([...keywords, newKeyword.trim()]); setNewKeyword(""); } }} placeholder="Type a keyword and press Enter..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => { if (newKeyword.trim()) { setKeywords([...keywords, newKeyword.trim()]); setNewKeyword(""); } }} style={{ padding: "10px 20px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>+ Add</button>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {keywords.map((kw, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: COLORS.accentBg, border: `1px solid ${COLORS.accent}33`, borderRadius: 20, fontSize: 13, color: COLORS.accent }}>
                {kw}
                <span onClick={() => setKeywords(keywords.filter((_, j) => j !== i))} style={{ cursor: "pointer", fontSize: 14, color: COLORS.textDim, marginLeft: 2 }}>×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice Training Tab */}
      {activeTab === "voice" && (
        <div>
          <div style={{ padding: "16px 20px", background: COLORS.blue + "10", border: `1px solid ${COLORS.blue}22`, borderRadius: 10, marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ fontSize: 20, marginTop: 2 }}>🧠</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.blue, marginBottom: 4 }}>Voice Training</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6 }}>
                Add examples of your writing — LinkedIn posts, blog articles, comments, emails — so the AI learns your tone, vocabulary, and style. The more samples you provide, the more accurately it mirrors your voice in drafted responses.
              </div>
            </div>
          </div>

          {/* Add Sample */}
          <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Add Writing Sample</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>CONTENT TYPE</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {SAMPLE_TYPES.map(t => (
                  <button key={t.key} onClick={() => setNewSampleType(t.key)} style={{
                    padding: "6px 12px", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600,
                    border: `1px solid ${newSampleType === t.key ? COLORS.accent + "55" : COLORS.border}`,
                    background: newSampleType === t.key ? COLORS.accentBg : "transparent",
                    color: newSampleType === t.key ? COLORS.accent : COLORS.textDim, cursor: "pointer",
                  }}>{t.label}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>PASTE YOUR CONTENT</label>
              <textarea value={newSampleText} onChange={e => setNewSampleText(e.target.value)}
                placeholder="Paste a LinkedIn post, blog excerpt, email, comment, or any writing that represents your voice..."
                rows={6} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => { if (newSampleText.trim()) { setVoiceSamples([{ id: Date.now(), type: newSampleType, title: SAMPLE_TYPES.find(t => t.key === newSampleType)?.label + " — " + new Date().toLocaleDateString(), preview: newSampleText.trim().substring(0, 150) + "...", date: new Date().toLocaleDateString() }, ...voiceSamples]); setNewSampleText(""); } }} disabled={!newSampleText.trim()} style={{
                padding: "10px 24px", background: newSampleText.trim() ? COLORS.accent : COLORS.border,
                color: newSampleText.trim() ? COLORS.bg : COLORS.textDim,
                border: "none", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600,
                cursor: newSampleText.trim() ? "pointer" : "default",
              }}>+ Add Sample</button>
            </div>
          </div>

          {/* Voice Profile Stats */}
          <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
            <div style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
              <div style={{ fontFamily: FONT, fontSize: 24, fontWeight: 600, color: COLORS.accent }}>{voiceSamples.length}</div>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>TOTAL SAMPLES</div>
            </div>
            <div style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
              <div style={{ fontFamily: FONT, fontSize: 24, fontWeight: 600, color: voiceSamples.length >= 10 ? COLORS.accent : voiceSamples.length >= 5 ? COLORS.warn : COLORS.danger }}>
                {voiceSamples.length >= 10 ? "Strong" : voiceSamples.length >= 5 ? "Good" : "Building"}
              </div>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>VOICE ACCURACY</div>
            </div>
            <div style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
              <div style={{ fontFamily: FONT, fontSize: 24, fontWeight: 600, color: COLORS.blue }}>
                {[...new Set(voiceSamples.map(s => s.type))].length}
              </div>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 2 }}>CONTENT TYPES</div>
            </div>
          </div>

          {/* Samples List */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {voiceSamples.map(sample => (
              <div key={sample.id} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, marginRight: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT, fontWeight: 500 }}>{SAMPLE_TYPES.find(t => t.key === sample.type)?.label || sample.type}</span>
                    <span style={{ fontSize: 11, color: COLORS.textDim }}>{sample.date}</span>
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5 }}>{sample.preview}</div>
                </div>
                <button onClick={() => setVoiceSamples(voiceSamples.filter(s => s.id !== sample.id))} style={{ padding: "4px 8px", background: "transparent", border: "none", color: COLORS.textDim, fontSize: 14, cursor: "pointer", flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === "activity" && (() => {
        const MOCK_ACTIVITY = activityItems.length > 0 ? activityItems : [
          { id: "a1", type: "agent", platform: "skool", community: "AI Automation Community", author: "Jake Morrison", postPreview: "Has anyone successfully set up an AI agent for lead generation?", reply: "Great question Jake! I've been running AI-powered lead gen for a while now. The key is layering tools...", timestamp: "2 hours ago", date: "Feb 11" },
          { id: "a2", type: "manual", platform: "linkedin", community: "SaaS Growth Leaders", author: "Sarah Chen", postPreview: "Hot take: Most companies are using AI wrong...", reply: "Couldn't agree more Sarah. The companies I work with that see the biggest results are the ones using AI for the high-leverage stuff...", timestamp: "4 hours ago", date: "Feb 11" },
          { id: "a3", type: "agent", platform: "skool", community: "AI Automation Community", author: "Marcus Williams", postPreview: "Just discovered vibe coding with Claude and it's completely changed how I build...", reply: "Love hearing this Marcus! You've nailed the key insight — the conversation-first approach is everything...", timestamp: "6 hours ago", date: "Feb 11" },
          { id: "a4", type: "manual", platform: "facebook", community: "Entrepreneurs Hub", author: "Lisa Park", postPreview: "Looking for recommendations on AI tools for lead generation...", reply: "Hey Lisa! I was in the exact same boat with Apollo. Switched to a stacked approach — AI Ark for smarter company discovery...", timestamp: "8 hours ago", date: "Feb 11" },
          { id: "a5", type: "agent", platform: "skool", community: "AI Automation Community", author: "Tom Richards", postPreview: "What's the best cold outreach tool in 2026?", reply: "Depends on your stack Tom, but I'd say Instantly for email and HeyReach for LinkedIn. The key is the data layer underneath...", timestamp: "Yesterday", date: "Feb 10" },
          { id: "a6", type: "manual", platform: "linkedin", community: "B2B Growth Hacks", author: "Nina Patel", postPreview: "Unpopular opinion: personalization at scale is a myth...", reply: "I'd push back on this Nina — it's not a myth, it's just that most tools do it poorly. When you feed AI real context about the prospect...", timestamp: "Yesterday", date: "Feb 10" },
          { id: "a7", type: "agent", platform: "facebook", community: "Entrepreneurs Hub", author: "David Kim", postPreview: "Anyone tried building an AI agent for customer onboarding?", reply: "This is a brilliant use case David. We've built exactly this for a client — cut onboarding time by 60%. The trick is...", timestamp: "2 days ago", date: "Feb 9" },
          { id: "a8", type: "manual", platform: "skool", community: "AI Automation Community", author: "Emma Lawson", postPreview: "Struggling to get my AI-generated emails past spam filters...", reply: "Emma, this is almost always a domain warm-up issue, not an AI issue. Here's what I'd check first...", timestamp: "2 days ago", date: "Feb 9" },
        ];

        const totalResponses = MOCK_ACTIVITY.length;
        const agentResponses = MOCK_ACTIVITY.filter(a => a.type === "agent").length;
        const manualResponses = MOCK_ACTIVITY.filter(a => a.type === "manual").length;
        const platforms = [...new Set(MOCK_ACTIVITY.map(a => a.platform))].length;

        return (
          <div>
            {/* Stats */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
              <StatCard label="Total Responses" value={totalResponses} accent={COLORS.accent} />
              <StatCard label="Agent Responses" value={agentResponses} accent={COLORS.blue} />
              <StatCard label="Manual Responses" value={manualResponses} accent={COLORS.warn} />
              <StatCard label="Platforms Active" value={platforms} accent="#7B61FF" />
            </div>

            {/* Timeline */}
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>RESPONSE TIMELINE</div>
            <div style={{ position: "relative" }}>
              {/* Vertical line */}
              <div style={{ position: "absolute", left: 15, top: 0, bottom: 0, width: 2, background: COLORS.border }} />

              {MOCK_ACTIVITY.map((item, i) => {
                const plat = getPlatform(item.platform);
                const showDateHeader = i === 0 || MOCK_ACTIVITY[i - 1].date !== item.date;
                return (
                  <div key={item.id}>
                    {showDateHeader && (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, marginTop: i > 0 ? 16 : 0 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: COLORS.surface, border: `2px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                          <span style={{ fontSize: 12 }}>📅</span>
                        </div>
                        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: COLORS.text }}>{item.date}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 12, marginBottom: 10, paddingLeft: 2 }}>
                      {/* Dot */}
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: item.type === "agent" ? COLORS.blue + "25" : COLORS.warn + "25", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1, marginTop: 4 }}>
                        <span style={{ fontSize: 12 }}>{item.type === "agent" ? "🤖" : "✍️"}</span>
                      </div>
                      {/* Card */}
                      <div style={{ flex: 1, padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12 }}>{plat.icon}</span>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{item.author}</span>
                            <span style={{ fontSize: 11, color: COLORS.textDim }}>{item.community}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: item.type === "agent" ? COLORS.blue + "15" : COLORS.warn + "15", color: item.type === "agent" ? COLORS.blue : COLORS.warn, fontFamily: FONT, fontWeight: 500 }}>
                              {item.type === "agent" ? "agent" : "manual"}
                            </span>
                            <span style={{ fontSize: 10, color: COLORS.textDim }}>{item.timestamp}</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 6, fontStyle: "italic" }}>"{item.postPreview}"</div>
                        <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5, padding: "8px 12px", background: COLORS.bg, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
                          {item.reply}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Feed Tab */}
      {activeTab === "feed" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MOCK_FEED.map(post => {
            const plat = getPlatform(post.platform);
            return (
              <div key={post.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: plat.color + "25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{plat.icon}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{post.author}</div>
                        <div style={{ fontSize: 11, color: COLORS.textDim }}>{post.community} · {post.timestamp}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {post.matchedKeywords.map(kw => (
                        <span key={kw} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.accent + "15", color: COLORS.accent, fontFamily: FONT, fontWeight: 500 }}>{kw}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>{post.content}</div>
                  <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11, color: COLORS.textDim }}>
                    <span>❤️ {post.likes}</span>
                    <span>💬 {post.comments}</span>
                  </div>
                </div>
                <div style={{ padding: "16px 20px", background: COLORS.bg }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.accent, letterSpacing: "0.06em", fontWeight: 600 }}>AI DRAFT REPLY</span>
                    {voiceSamples.length > 0 && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.blueBg, color: COLORS.blue, fontFamily: FONT }}>Voice-matched</span>}
                    {mode === "agent" && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: COLORS.accent + "15", color: COLORS.accent, fontFamily: FONT }}>Auto-posting</span>}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 12, padding: "12px 16px", background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                    {post.draftReply}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <a href={post.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: COLORS.blue, textDecoration: "none", fontFamily: FONT }}>↗ View original post</a>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => navigator.clipboard?.writeText(post.draftReply)} style={{ padding: "7px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.textMuted; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}
                      >Copy Reply</button>
                      {mode === "agent" ? (
                        <button style={{ padding: "7px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>🤖 Send via Agent</button>
                      ) : (
                        <a href={post.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                          <button style={{ padding: "7px 14px", background: COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 6, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Open & Post →</button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function ContentPlaceholder({ title, accent, icon, description, features }) {
  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
          {title} <span style={{ color: accent }}>Content</span>
        </h2>
        <p style={{ color: COLORS.textMuted, margin: "6px 0 0" }}>{description}</p>
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Published", value: "—" },
          { label: "Scheduled", value: "—" },
          { label: "Drafts", value: "—" },
          { label: "Engagement", value: "—" },
        ].map(s => <StatCard key={s.label} label={s.label} value={s.value} accent={accent} />)}
      </div>

      <div style={{ padding: "32px 28px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{icon}</div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Coming Soon</div>
            <div style={{ fontSize: 12, color: COLORS.textDim }}>This module is under development</div>
          </div>
        </div>

        <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 12 }}>PLANNED FEATURES</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {features.map((feature, i) => (
            <div key={i} style={{
              padding: "12px 16px", background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 8, display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: accent, fontFamily: FONT, fontWeight: 600, flexShrink: 0 }}>{i + 1}</div>
              <span style={{ fontSize: 13, color: COLORS.text }}>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px 20px", background: accent + "08", border: `1px solid ${accent}22`, borderRadius: 10 }}>
        <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>
          Want to shape this feature? We're building based on user feedback. The {title.toLowerCase()} module will integrate directly with your lead gen pipeline for a unified workflow.
        </div>
      </div>
    </div>
  );
}

function SolutionAIAssistantView() {
  const [messages, setMessages] = useState([
    { role: "agent", text: "Hi! 👋 I'm your AI Assistant with full context on your Pipeline account.\n\nI can see your leads, CRM deals, campaigns, and more. Think of me as a team member who's read every document and knows every number.\n\nTry asking me things like:\n• \"How many leads did we discover today?\"\n• \"What's our email campaign performance?\"\n• \"Show me leads that need follow-up\"\n• \"Draft a follow-up email for a prospect\"\n\nWhat would you like to know?" }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);
  
  useEffect(() => { 
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); 
  }, [messages, isTyping]);

  const CONTEXT_SOURCES = [
    { label: "CRM", count: "23 deals", icon: "📊", color: COLORS.accent },
    { label: "Leads", count: "—", icon: "⚡", color: COLORS.blue },
    { label: "Campaigns", count: "—", icon: "📧", color: COLORS.warn },
    { label: "Tasks", count: "—", icon: "📋", color: COLORS.green },
  ];

  const SUGGESTED = [
    "What leads should I prioritise today?",
    "Show me campaign performance",
    "What tasks are pending?",
    "Draft a follow-up email",
  ];

  const sendMessage = async () => {
    if (!input.trim() || isTyping) return;
    const userMsg = input;
    const nm = [...messages, { role: "user", text: userMsg }];
    setMessages(nm); setInput(""); setIsTyping(true);
    try {
      const data = await api.assistant.chat({ message: userMsg });
      const reply = data.reply || data.message || data.text || "I couldn't generate a response. Please try again.";
      setMessages([...nm, { role: "agent", text: reply }]);
    } catch (err) {
      setMessages([...nm, { role: "agent", text: "Sorry, I encountered an error: " + (err.message || "Unknown error") + ". Please try again." }]);
    }
    setIsTyping(false);
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: 0 }}>
      <div style={{ padding: "16px 32px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 24 }}>🤖</span>
          <div>
            <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700 }}>AI Assistant</div>
            <div style={{ fontSize: 11, color: COLORS.textDim }}>Personal AI with full org context</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CONTEXT_SOURCES.map(src => (
            <span key={src.label} style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 4, fontSize: 8, background: src.color + "10", border: `1px solid ${src.color}22`, color: src.color, fontFamily: FONT, fontWeight: 600 }}>{src.icon} {src.label}: {src.count}</span>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "20px 32px", display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "70%", padding: "14px 18px", borderRadius: 14, background: msg.role === "user" ? COLORS.accent + "18" : COLORS.surface, border: `1px solid ${msg.role === "user" ? COLORS.accent + "33" : COLORS.border}`, borderBottomRightRadius: msg.role === "user" ? 4 : 14, borderBottomLeftRadius: msg.role === "agent" ? 4 : 14 }}>
              {msg.role === "agent" && <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>AI ASSISTANT</div>}
              <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{msg.text}</div>
            </div>
          </div>
        ))}
        {isTyping && <div style={{ display: "flex" }}><div style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, borderBottomLeftRadius: 4 }}><div style={{ fontSize: 11, color: COLORS.textMuted }}>AI is thinking...</div></div></div>}
        <div ref={chatEndRef} />
      </div>

      {messages.length < 3 && (
        <div style={{ padding: "0 32px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SUGGESTED.map((s, i) => (
            <button key={i} onClick={() => { setInput(s); }} style={{ padding: "6px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 11, color: COLORS.textMuted, fontFamily: FONT_BODY, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent + "55"; e.currentTarget.style.color = COLORS.accent; }} onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMuted; }}>{s}</button>
          ))}
        </div>
      )}

      <div style={{ padding: "14px 32px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 10 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !isTyping) sendMessage(); }} placeholder="Ask me anything about your business..." style={{ flex: 1, padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none" }} disabled={isTyping} />
        <button onClick={sendMessage} disabled={isTyping || !input.trim()} style={{ padding: "12px 24px", background: input.trim() && !isTyping ? COLORS.accent : COLORS.border, color: input.trim() && !isTyping ? COLORS.bg : COLORS.textDim, border: "none", borderRadius: 10, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: input.trim() && !isTyping ? "pointer" : "default" }}>Send</button>
      </div>
    </div>
  );
}

function SendingAccountsView() {
  const [activeSubTab, setActiveSubTab] = useState("email");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [linkedinProfiles, setLinkedinProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAccount, setNewAccount] = useState({ email: "", domain: "" });

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const all = await api.sendingAccounts.list();
        const rows = Array.isArray(all) ? all : (all.accounts || []);
        setEmailAccounts(rows.filter(a => a.type === "email").map(a => ({
          id: a.id, email: a.email || "", domain: a.domain || "",
          health: a.health_score || 0, warmup: a.warmup_status || "pending",
          dailyLimit: a.daily_limit || 30, sent: a.sent_today || 0, status: a.status || "active",
        })));
        setLinkedinProfiles(rows.filter(a => a.type === "linkedin").map(a => ({
          id: a.id, name: a.profile_name || "", headline: (a.config_json || {}).headline || "",
          connected: a.status === "active", dailyLimit: a.daily_limit || 60,
          sent: a.sent_today || 0, responseRate: (a.config_json || {}).response_rate || 0,
        })));
      } catch (err) { console.error("Failed to load sending accounts:", err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  async function handleCreateAccount(mode) {
    if (mode === "existing" && newAccount.email) {
      try {
        await api.sendingAccounts.create({
          type: "email", email: newAccount.email, domain: newAccount.email.split("@")[1] || "",
          status: "active", warmup_status: "pending", health_score: 50, daily_limit: 30,
          account_type: "existing",
        });
        setShowAddModal(false); setAddMode(null); setNewAccount({ email: "", domain: "" });
        const all = await api.sendingAccounts.list();
        const rows = Array.isArray(all) ? all : (all.accounts || []);
        setEmailAccounts(rows.filter(a => a.type === "email").map(a => ({
          id: a.id, email: a.email || "", domain: a.domain || "",
          health: a.health_score || 0, warmup: a.warmup_status || "pending",
          dailyLimit: a.daily_limit || 30, sent: a.sent_today || 0, status: a.status || "active",
        })));
      } catch (err) { console.error("Failed to create sending account:", err); }
    } else {
      setShowAddModal(false); setAddMode(null);
    }
  }
  const totalDaily = emailAccounts.reduce((s, a) => s + a.dailyLimit, 0);
  const avgHealth = Math.round(emailAccounts.reduce((s, a) => s + a.health, 0) / emailAccounts.length);
  const warmReady = emailAccounts.filter(a => a.warmup === "complete").length;
  const domains = [...new Set(emailAccounts.map(a => a.domain))];
  const healthColor = h => h >= 80 ? "#67c23a" : h >= 60 ? "#e6a23c" : "#ff6b6b";
  const warmBadge = w => ({ complete: { bg: "rgba(100,200,100,0.12)", color: "#67c23a", label: "Warm & Ready" }, in_progress: { bg: "rgba(100,180,255,0.12)", color: "#64b5f6", label: "Warming Up" }, failed: { bg: "rgba(255,100,100,0.12)", color: "#ff6b6b", label: "Failed" } })[w] || { bg: COLORS.surface, color: COLORS.textDim, label: w };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading sending accounts...</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["email", "linkedin"].map(t => (
          <button key={t} onClick={() => setActiveSubTab(t)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${activeSubTab === t ? COLORS.accent : COLORS.border}`, background: activeSubTab === t ? COLORS.accentBg : "transparent", color: activeSubTab === t ? COLORS.accent : COLORS.textMuted, fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            {t === "email" ? "📧 Email" : "💼 LinkedIn"}
          </button>
        ))}
        <button onClick={() => setShowAddModal(true)} style={{ marginLeft: "auto", padding: "8px 18px", borderRadius: 8, background: COLORS.accent, color: COLORS.bg, border: "none", fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>+ Add Account</button>
      </div>

      {activeSubTab === "email" && emailAccounts.length === 0 && (
        <div style={{ padding: 24, background: COLORS.surface, border: `1px dashed ${COLORS.border}`, borderRadius: 12, color: COLORS.textDim, fontSize: 13, textAlign: "center" }}>No email sending accounts yet. Click "+ Add Account" to get started.</div>
      )}

      {activeSubTab === "email" && emailAccounts.length > 0 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 20 }}>
            {[["Total Accounts", emailAccounts.length], ["Daily Capacity", totalDaily], ["Avg Health", avgHealth + "%"], ["Warm & Ready", warmReady], ["Monthly Cost", "$" + (emailAccounts.length * 3.5).toFixed(0)]].map(([l, v]) => (
              <div key={l} style={{ padding: "14px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{v}</div>
                <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
          {domains.map(domain => (
            <div key={domain} style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>{domain.toUpperCase()}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {emailAccounts.filter(a => a.domain === domain).map(a => {
                  const wb = warmBadge(a.warmup);
                  return (
                    <div key={a.email} style={{ padding: "12px 16px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: COLORS.text }}>{a.email}</div>
                        <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                          <span style={{ padding: "3px 8px", borderRadius: 6, background: wb.bg, color: wb.color, fontSize: 10, fontFamily: FONT, fontWeight: 600 }}>{wb.label}</span>
                          <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>Sent {a.sent}/{a.dailyLimit} today</span>
                        </div>
                      </div>
                      <div style={{ width: 80, textAlign: "right" }}>
                        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: healthColor(a.health) }}>{a.health}%</div>
                        <div style={{ height: 4, borderRadius: 2, background: COLORS.border, marginTop: 4 }}>
                          <div style={{ width: a.health + "%", height: "100%", borderRadius: 2, background: healthColor(a.health) }} />
                        </div>
                      </div>
                      <div style={{ width: 70, textAlign: "right" }}>
                        <div style={{ height: 4, borderRadius: 2, background: COLORS.border }}>
                          <div style={{ width: (a.dailyLimit ? (a.sent / a.dailyLimit) * 100 : 0) + "%", height: "100%", borderRadius: 2, background: COLORS.accent }} />
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT, marginTop: 3 }}>{a.dailyLimit ? Math.round((a.sent / a.dailyLimit) * 100) : 0}% used</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeSubTab === "linkedin" && linkedinProfiles.length === 0 && (
        <div style={{ padding: 24, background: COLORS.surface, border: `1px dashed ${COLORS.border}`, borderRadius: 12, color: COLORS.textDim, fontSize: 13, textAlign: "center" }}>No LinkedIn profiles connected yet. Click "+ Add Account" to connect one.</div>
      )}

      {activeSubTab === "linkedin" && linkedinProfiles.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {linkedinProfiles.map(p => (
            <div key={p.name} style={{ padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: COLORS.accentBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>💼</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: COLORS.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{p.headline}</div>
              </div>
              <div style={{ textAlign: "center", padding: "0 12px" }}>
                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: COLORS.text }}>{p.sent}/{p.dailyLimit}</div>
                <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT }}>daily</div>
              </div>
              <div style={{ textAlign: "center", padding: "0 12px" }}>
                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: COLORS.accent }}>{p.responseRate}%</div>
                <div style={{ fontSize: 9, color: COLORS.textDim, fontFamily: FONT }}>response</div>
              </div>
              <span style={{ padding: "4px 10px", borderRadius: 6, background: p.connected ? "rgba(100,200,100,0.12)" : "rgba(255,100,100,0.12)", color: p.connected ? "#67c23a" : "#ff6b6b", fontFamily: FONT, fontSize: 10, fontWeight: 600 }}>{p.connected ? "Connected" : "Disconnected"}</span>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => { setShowAddModal(false); setAddMode(null); }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 28, width: 420, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 16 }}>Add Sending Account</div>
            {!addMode ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[["dfy", "🚀 Done-For-You", "We buy & warm domains for you"], ["prewarmed", "🔥 Pre-Warmed Inbox", "Ready-to-send accounts"], ["existing", "📎 Connect Existing", "Link your own mailbox"]].map(([k, t, d]) => (
                  <button key={k} onClick={() => setAddMode(k)} style={{ padding: "14px 18px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, textAlign: "left", cursor: "pointer", color: COLORS.text }}>
                    <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600 }}>{t}</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{d}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div>
                <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 16 }}>
                  {addMode === "dfy" && "We'll purchase and warm up new domains on your behalf. Typical setup takes 14 days."}
                  {addMode === "prewarmed" && "Select from our inventory of pre-warmed inboxes, ready to send within 24 hours."}
                  {addMode === "existing" && "Connect your Google Workspace, Outlook, or SMTP mailbox."}
                </p>
                {addMode === "existing" && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 10, fontFamily: FONT, fontWeight: 600, color: COLORS.textDim, display: "block", marginBottom: 4 }}>Email Address</label>
                    <input value={newAccount.email} onChange={e => setNewAccount({ ...newAccount, email: e.target.value })} placeholder="you@yourdomain.com" style={{ width: "100%", padding: "8px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setAddMode(null)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textMuted, fontFamily: FONT, fontSize: 12, cursor: "pointer" }}>Back</button>
                  <button onClick={() => handleCreateAccount(addMode)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: COLORS.accent, color: COLORS.bg, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Continue Setup</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PricingPlansSection({ plans, currentPlan, creditBalance, creditPackInfo, handleBuyCredits, purchaseLoading, purchaseMessage, setPurchaseMessage, annualEnabled = true, hasSubscription = false, subscriptionStatus = null, trialEndsAt = null }) {
  const [interval, setInterval] = useState('monthly');
  const [subscribing, setSubscribing] = useState(null);

  const displayPlans = (plans || []).filter(p => p.is_active !== false);
  if (displayPlans.length === 0) return null;

  const handleSubscribe = async (plan) => {
    if (plan.tier === 'free' || plan.tier === 'enterprise') return;
    setSubscribing(plan.id);
    try {
      const result = await api.billing.subscribe({ plan_id: plan.id, interval });
      if (result?.url) window.location.href = result.url;
      else setPurchaseMessage?.('Failed to start checkout.');
    } catch (err) {
      setPurchaseMessage?.(err?.message || 'Checkout failed.');
    } finally {
      setSubscribing(null);
    }
  };

  const handlePortal = async () => {
    try {
      const result = await api.billing.portal();
      if (result?.url) window.location.href = result.url;
    } catch (err) {
      setPurchaseMessage?.(err?.message || 'Could not open billing portal.');
    }
  };

  const isYearly = interval === 'yearly';

  // Trial state — derived once for use by every plan card.
  const trialDate = trialEndsAt ? new Date(trialEndsAt) : null;
  const trialValid = trialDate && !Number.isNaN(trialDate.getTime());
  const trialDaysLeft = trialValid ? Math.ceil((trialDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : 0;
  const isTrialing = subscriptionStatus === 'trialing' && trialValid && trialDaysLeft > 0;
  const isTrialExpired = subscriptionStatus === 'trial_expired' || (trialValid && trialDaysLeft <= 0 && !hasSubscription);
  // Trial is "already used" once trial_ends_at has been set OR they previously subscribed.
  const trialAlreadyConsumed = !!trialEndsAt || hasSubscription;

  // Returns the button label for a given plan card based on user/trial state.
  const subscribeButtonText = (plan) => {
    if (hasSubscription && currentPlan === plan.tier) return 'Current Plan';
    if (plan.tier === 'enterprise') return 'Contact Sales';
    if (hasSubscription) return `Switch to ${plan.name}`;
    if (trialAlreadyConsumed) return `Subscribe to ${plan.name}`;
    return plan.button_text || 'Start Free Trial';
  };

  // Sub-line under the price.
  const priceSubline = (plan) => {
    if (isTrialing) return `Trial active — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`;
    if (isTrialExpired) return 'Trial ended';
    return plan.trial_days > 0 ? `${plan.trial_days}-day free trial` : '';
  };

  const singlePlan = displayPlans.length === 1 ? displayPlans[0] : null;
  const heading = singlePlan ? `Subscribe to ${singlePlan.name}` : 'Choose your plan';
  const tagline = singlePlan
    ? 'Start your subscription. Cancel anytime.'
    : 'Select a plan that fits your needs. Upgrade or downgrade anytime.';

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>{heading}</div>
        <div style={{ fontSize: 12, color: COLORS.textDim }}>{tagline}</div>
      </div>
      {/* Monthly / Annual Toggle */}
      {annualEnabled && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <span style={{ fontSize: 12, fontFamily: FONT, fontWeight: 600, color: !isYearly ? COLORS.text : COLORS.textDim, cursor: "pointer" }} onClick={() => setInterval('monthly')}>Monthly</span>
          <div onClick={() => setInterval(isYearly ? 'monthly' : 'yearly')} style={{
            width: 44, height: 24, borderRadius: 12, cursor: "pointer",
            background: isYearly ? COLORS.accent : COLORS.borderActive,
            position: "relative", transition: "background 0.25s",
          }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: isYearly ? 23 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
          <span style={{ fontSize: 12, fontFamily: FONT, fontWeight: 600, color: isYearly ? COLORS.text : COLORS.textDim, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} onClick={() => setInterval('yearly')}>
            Annual
            <span style={{ fontSize: 9, fontFamily: FONT, fontWeight: 700, padding: "2px 6px", borderRadius: 8, background: COLORS.green + "20", color: COLORS.green }}>Save ~17%</span>
          </span>
        </div>
      )}
      {/* Plan Cards */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(displayPlans.length, 4)}, 1fr)`, gap: 12 }}>
        {displayPlans.map((plan, idx) => {
          const isCurrent = currentPlan === plan.tier && hasSubscription;
          const isFree = plan.tier === 'free';
          const isEnterprise = plan.tier === 'enterprise';
          const price = isYearly ? Number(plan.price_yearly || 0) : Number(plan.price_monthly || 0);
          const monthlyEquiv = isYearly && price > 0 ? (price / 12).toFixed(0) : null;
          const bullets = Array.isArray(plan.feature_bullets) ? plan.feature_bullets : [];
          const isLoading = subscribing === plan.id;
          return (
            <div key={plan.id} style={{
              padding: "20px 18px", background: COLORS.surface,
              border: `1px solid ${plan.highlight ? COLORS.accent + "55" : COLORS.border}`,
              borderRadius: 12, display: "flex", flexDirection: "column", position: "relative",
              boxShadow: plan.highlight ? `0 0 20px ${COLORS.accent}15` : "none",
            }}>
              {plan.highlight && (
                <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", fontSize: 9, fontFamily: FONT, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: COLORS.accent, color: COLORS.bg, letterSpacing: "0.04em" }}>MOST POPULAR</div>
              )}
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, marginBottom: 4 }}>0{idx + 1}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 16, minHeight: 30 }}>{plan.tagline || plan.description || ''}</div>
              {isFree ? (
                <div style={{ fontSize: 28, fontWeight: 700, fontFamily: FONT, color: COLORS.text, marginBottom: 4 }}>$0</div>
              ) : isEnterprise ? (
                <div style={{ fontSize: 28, fontWeight: 700, fontFamily: FONT, color: COLORS.text, marginBottom: 4 }}>Custom</div>
              ) : (
                <div>
                  {isYearly && plan.annual_discount_pct > 0 && (
                    <span style={{ fontSize: 9, fontFamily: FONT, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: COLORS.warn + "20", color: COLORS.warn, marginBottom: 4, display: "inline-block" }}>Annual promo</span>
                  )}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                    <span style={{ fontSize: 28, fontWeight: 700, fontFamily: FONT, color: COLORS.text }}>${monthlyEquiv || price}</span>
                    <span style={{ fontSize: 12, color: COLORS.textDim }}>/mo</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                    {priceSubline(plan)}
                    {isYearly && price > 0 && <span style={{ marginLeft: 6, textDecoration: "line-through" }}>${Number(plan.price_monthly || 0) * 12}/yr</span>}
                  </div>
                </div>
              )}
              <button
                onClick={() => isCurrent ? handlePortal() : isEnterprise ? null : handleSubscribe(plan)}
                disabled={isLoading || (isFree && isCurrent)}
                style={{
                  marginTop: 16, padding: "12px 0", width: "100%", borderRadius: 8, border: "none",
                  fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: isLoading || (isFree && isCurrent) ? "default" : "pointer",
                  background: isCurrent ? "transparent" : plan.highlight ? COLORS.accent : COLORS.text,
                  color: isCurrent ? COLORS.accent : plan.highlight ? COLORS.bg : COLORS.bg,
                  border: isCurrent ? `1px solid ${COLORS.accent}44` : "1px solid transparent",
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                {isLoading ? "Redirecting..." : subscribeButtonText(plan)}
              </button>
              <div style={{ marginTop: 16, fontSize: 10, color: COLORS.textDim }}>Includes:</div>
              <div style={{ marginTop: 8, flex: 1 }}>
                {bullets.map((b, bi) => (
                  <div key={bi} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <span style={{ fontSize: 9, color: COLORS.textMuted }}>✓</span>
                    </div>
                    <span style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.4 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {purchaseMessage && <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: COLORS.warn }}>{purchaseMessage}</div>}
    </div>
  );
}

function AccountView({ onCreditsChange }) {
  const [activeTab, setActiveTab] = useState("billing");
  const [profile, setProfile] = useState({ firstName: "", lastName: "", email: "", company: "", timezone: "Europe/London", photoUrl: "" });
  const [originalProfile, setOriginalProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [currentPlan, setCurrentPlan] = useState("growth");
  const [currentPlanName, setCurrentPlanName] = useState("");
  const [hasSubscription, setHasSubscription] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsTotal, setCreditsTotal] = useState(2000);
  const [creditBalance, setCreditBalance] = useState(0);
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageBreakdown, setUsageBreakdown] = useState(null);
  const [creditCostsData, setCreditCostsData] = useState([]);
  const [plansData, setPlansData] = useState([]);
  const [annualEnabled, setAnnualEnabled] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);
  const [creditPackInfo, setCreditPackInfo] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [purchaseMessage, setPurchaseMessage] = useState('');
  const [notifications, setNotifications] = useState({ weeklyDigest: true, creditAlert80: true, creditAlert100: true, campaignComplete: true, surveyResponses: false });
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function loadProfile() {
      try {
        const result = await api.me();
        if (result && result.user) {
          const user = result.user;
          const nameParts = (user.name || "").split(" ");
          const profileData = {
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
            email: user.email || "",
            company: user.company || "",
            timezone: user.timezone || "Europe/London",
            photoUrl: user.profile_photo_url || "",
          };
          setProfile(profileData);
          setOriginalProfile(profileData);
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();

    async function loadBilling() {
      setBillingLoading(true);
      try {
        const [planData, creditsData, historyData, usageByAction, costsRes, plansRes, packInfo] = await Promise.all([
          api.billing.plan().catch(() => null),
          api.billing.credits().catch(() => null),
          api.billing.creditHistory().catch(() => null),
          api.billing.usageByAction?.().catch(() => []),
          api.billing.creditCosts?.().catch(() => []),
          api.billing.plans?.().catch(() => []),
          api.billing.creditPackInfo().catch(() => null),
        ]);
        if (planData) {
          setCurrentPlan(planData.plan || "growth");
          setCurrentPlanName(planData.name || "");
          setHasSubscription(!!planData.has_subscription);
          setTrialEndsAt(planData.trial_ends_at || null);
          setSubscriptionStatus(planData.subscription_status || null);
          setCreditsTotal(planData.credits || 2000);
        }
        if (creditsData) {
          const allocated = creditsData.allocated ?? planData?.credits ?? 2000;
          setCreditsTotal(allocated);
          const bal = creditsData.balance ?? 0;
          setCreditBalance(bal);
          if (onCreditsChange) onCreditsChange(bal);
          setCreditsUsed(creditsData.usedThisMonth ?? Math.max(0, allocated - bal));
        }
        if (historyData && Array.isArray(historyData)) {
          setUsageHistory(historyData.slice(0, 20).map(h => ({
            date: new Date(h.created_at).toLocaleDateString("en-GB", { month: "short", day: "numeric" }),
            action: h.description || h.action || "Usage",
            credits: Math.abs(h.amount || 0),
            balance: h.running_balance || 0,
          })));
        }
        if (Array.isArray(usageByAction) && usageByAction.length > 0) {
          const byCat = {};
          for (const r of usageByAction) {
            const cat = r.category || "Other";
            byCat[cat] = (byCat[cat] || 0) + (r.credits || 0);
          }
          setUsageBreakdown([
            { category: "Lead Discovery", credits: byCat["Lead Discovery"] || 0, icon: "🔍", color: COLORS.accent },
            { category: "Enrichment", credits: byCat["Enrichment"] || 0, icon: "✉️", color: COLORS.blue },
            { category: "AI Analysis", credits: byCat["AI Analysis"] || 0, icon: "✨", color: "#7B61FF" },
            { category: "Content & Scripts", credits: byCat["Content & Scripts"] || 0, icon: "📝", color: COLORS.warn },
          ]);
        } else {
          setUsageBreakdown(null);
        }
        if (Array.isArray(costsRes) && costsRes.length > 0) {
          setCreditCostsData(costsRes);
        } else {
          setCreditCostsData([]);
        }
        if (plansRes) {
          const rows = Array.isArray(plansRes) ? plansRes : (plansRes.plans || []);
          setPlansData(rows);
          if (plansRes.annual_enabled !== undefined) setAnnualEnabled(plansRes.annual_enabled);
        } else {
          setPlansData([]);
        }
        if (packInfo) {
          setCreditPacks(Array.isArray(packInfo.packs) ? packInfo.packs.filter(p => p.is_active !== false) : []);
          if (packInfo.available) setCreditPackInfo(packInfo);
        }
      } catch (err) { console.error("Failed to load billing:", err); }
      finally { setBillingLoading(false); }
    }
    loadBilling();

    // Check for successful purchase redirect
    const hash = window.location.hash || '';
    if (hash.includes('purchased=true')) {
      // Clean URL and switch to billing tab
      window.location.hash = hash.replace(/[?&]purchased=true/g, '');
      setActiveTab('billing');
      // Poll for credit balance update — compare against initial balance
      let polls = 0;
      let initialBalance = null;
      setPurchaseMessage('Processing payment...');
      const pollInterval = setInterval(async () => {
        polls++;
        try {
          const freshCredits = await api.billing.credits();
          if (freshCredits && freshCredits.balance !== undefined) {
            if (initialBalance === null) initialBalance = freshCredits.balance;
            const newAllocated = freshCredits.allocated ?? creditsTotal;
            setCreditsUsed(freshCredits.usedThisMonth ?? 0);
            setCreditsTotal(newAllocated);
            setCreditBalance(freshCredits.balance);
            if (onCreditsChange) onCreditsChange(freshCredits.balance);
            // Check if balance actually increased since we started polling
            if (initialBalance !== null && freshCredits.balance > initialBalance) {
              const gained = freshCredits.balance - initialBalance;
              setPurchaseMessage(`Credits purchased successfully! +${gained} credits added.`);
              clearInterval(pollInterval);
              setTimeout(() => setPurchaseMessage(''), 8000);
            }
          }
        } catch (e) { /* ignore polling errors */ }
        if (polls >= 8) { // 8 * 2s = 16s
          clearInterval(pollInterval);
          setPurchaseMessage('Payment received — credits may take a moment to appear. Please refresh.');
          setTimeout(() => setPurchaseMessage(''), 8000);
        }
      }, 2000);
    }
  }, []);

  async function handleBuyCredits(packId = null) {
    setPurchaseLoading(packId || true);
    setPurchaseMessage('');
    try {
      const result = await api.billing.checkout(packId ? { pack_id: packId } : undefined);
      if (result && result.url) {
        window.location.href = result.url;
      } else {
        setPurchaseMessage('Failed to start checkout. Please try again.');
        setTimeout(() => setPurchaseMessage(''), 5000);
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setPurchaseMessage(err?.message || 'Failed to start checkout. Please try again.');
      setTimeout(() => setPurchaseMessage(''), 5000);
    } finally {
      setPurchaseLoading(null);
    }
  }

  async function handleSaveProfile() {
    setSaving(true);
    setSaveMessage("");
    try {
      const name = `${profile.firstName} ${profile.lastName}`.trim();
      await api.updateProfile({
        name,
        company: profile.company,
        timezone: profile.timezone,
      });
      setOriginalProfile(profile);
      setSaveMessage("Profile updated successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setSaveMessage("Failed to save profile. Please try again.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSaveMessage("Please select an image file");
      setTimeout(() => setSaveMessage(""), 3000);
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveMessage("Image size must be less than 5MB");
      setTimeout(() => setSaveMessage(""), 3000);
      return;
    }
    
    setUploadingPhoto(true);
    setSaveMessage("");
    try {
      const result = await api.uploadProfilePhoto(file);
      setProfile({ ...profile, photoUrl: result.photoUrl });
      setSaveMessage("Profile photo uploaded successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) {
      console.error("Failed to upload photo:", err);
      setSaveMessage("Failed to upload photo. Please try again.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const inputStyle = { width: "100%", padding: "10px 14px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: FONT_BODY, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 };

  const USAGE_BREAKDOWN_FALLBACK = [
    { category: "Lead Discovery", credits: 0, icon: "🔍", color: COLORS.accent },
    { category: "Enrichment", credits: 0, icon: "✉️", color: COLORS.blue },
    { category: "AI Analysis", credits: 0, icon: "✨", color: "#7B61FF" },
    { category: "Content & Scripts", credits: 0, icon: "📝", color: COLORS.warn },
  ];
  const USAGE_BREAKDOWN = usageBreakdown && usageBreakdown.length > 0 ? usageBreakdown : USAGE_BREAKDOWN_FALLBACK;

  const USAGE_HISTORY = usageHistory.length > 0 ? usageHistory : [];
  const normalizedCurrentPlan = String(currentPlan || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const currentPlanMeta = plansData.find((plan) => {
    const candidates = [plan?.tier, plan?.tier_key, plan?.name]
      .filter(Boolean)
      .map(value => String(value).trim().toLowerCase().replace(/[\s-]+/g, "_"));
    return candidates.includes(normalizedCurrentPlan);
  });
  const currentPlanLabel = currentPlanName
    || currentPlanMeta?.name
    || (currentPlan
      ? String(currentPlan).replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
      : "Growth");

  // Profile chip label: gate on subscription state so trial_expired/canceled/no-sub
  // users don't see a misleading "ConsultiX Pro Plan" badge.
  const profilePlanChip = subscriptionStatus === 'active'
    ? `${currentPlanLabel} Plan`
    : subscriptionStatus === 'trialing'
      ? `${currentPlanLabel} · Trial`
      : 'Free Plan';

  const creditPct = Math.round((creditsUsed / creditsTotal) * 100);
  const creditColor = creditPct > 90 ? COLORS.danger : creditPct > 70 ? COLORS.warn : COLORS.accent;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }}>
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 600, margin: 0 }}>
          <span style={{ color: COLORS.accent }}>Account</span>
        </h2>
        <button
          onClick={() => { api.clearToken(); window.location.reload(); }}
          style={{
            padding: "8px 18px", background: "transparent",
            border: `1px solid ${COLORS.danger}33`, borderRadius: 8,
            color: COLORS.danger, fontFamily: FONT, fontSize: 12, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.dangerBg; e.currentTarget.style.borderColor = COLORS.danger + "55"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = COLORS.danger + "33"; }}
        >
          <span>🚪</span> Log Out
        </button>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {[{ key: "profile", label: "👤 Profile" }, { key: "billing", label: "💳 Billing" }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            color: activeTab === tab.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>{tab.label}</button>
        ))}
      </div>

      {/* PROFILE TAB */}
      {activeTab === "profile" && (
        <div style={{ maxWidth: 560 }}>
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", color: COLORS.textMuted }}>Loading profile...</div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28, padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
                <div style={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: 12, 
                  background: profile.photoUrl ? COLORS.accent + "15" : COLORS.accent + "15", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  fontSize: 28, 
                  flexShrink: 0,
                  position: "relative",
                  overflow: "hidden"
                }}>
                  {profile.photoUrl ? (
                    <img 
                      src={profile.photoUrl} 
                      alt="Profile" 
                      style={{ 
                        width: "100%", 
                        height: "100%", 
                        objectFit: "cover",
                        objectPosition: "center"
                      }} 
                    />
                  ) : (
                    "👤"
                  )}
                  {uploadingPhoto && (
                    <div style={{ 
                      position: "absolute", 
                      inset: 0, 
                      background: "rgba(0,0,0,0.6)", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      color: "white",
                      fontSize: 12
                    }}>...</div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{profile.firstName || "Your Name"}</div>
                  <div style={{ fontSize: 12, color: COLORS.textDim }}>{profile.email}</div>
                  <div style={{ fontSize: 11, color: COLORS.accent, marginTop: 2 }}>{profilePlanChip}</div>
                </div>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoUpload}
                  style={{ display: "none" }}
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  style={{ 
                    padding: "6px 14px", 
                    background: "transparent", 
                    border: `1px solid ${COLORS.border}`, 
                    borderRadius: 6, 
                    color: COLORS.textMuted, 
                    fontFamily: FONT, 
                    fontSize: 10, 
                    fontWeight: 600, 
                    cursor: uploadingPhoto ? "not-allowed" : "pointer",
                    opacity: uploadingPhoto ? 0.6 : 1
                  }}
                >
                  {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div><label style={labelStyle}>FIRST NAME</label><input value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>LAST NAME</label><input value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })} placeholder="Enter last name" style={inputStyle} /></div>
                </div>
                <div><label style={labelStyle}>EMAIL ADDRESS</label><div style={{ display: "flex", gap: 8 }}><input value={profile.email} disabled style={{ ...inputStyle, flex: 1, opacity: 0.6 }} /><button disabled style={{ padding: "10px 14px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, fontFamily: FONT, fontSize: 10, fontWeight: 600, cursor: "not-allowed", whiteSpace: "nowrap", opacity: 0.5 }}>Change Email</button></div></div>
                <div><label style={labelStyle}>COMPANY NAME</label><input value={profile.company} onChange={e => setProfile({ ...profile, company: e.target.value })} placeholder="Enter company name" style={inputStyle} /></div>
                <div><label style={labelStyle}>TIMEZONE</label><select value={profile.timezone} onChange={e => setProfile({ ...profile, timezone: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}><option value="Europe/London">Europe/London (GMT)</option><option value="America/New_York">America/New_York (EST)</option><option value="America/Los_Angeles">America/Los_Angeles (PST)</option><option value="Asia/Dubai">Asia/Dubai (GST)</option></select></div>
              </div>
              
              {saveMessage && (
                <div style={{ 
                  marginTop: 16, 
                  padding: "12px 16px", 
                  background: saveMessage.includes("success") ? COLORS.accent + "15" : COLORS.danger + "15",
                  border: `1px solid ${saveMessage.includes("success") ? COLORS.accent + "33" : COLORS.danger + "33"}`,
                  borderRadius: 8,
                  color: saveMessage.includes("success") ? COLORS.accent : COLORS.danger,
                  fontSize: 13,
                  fontFamily: FONT_BODY
                }}>
                  {saveMessage}
                </div>
              )}
              
              <button 
                onClick={handleSaveProfile} 
                disabled={saving}
                style={{ 
                  marginTop: 24, 
                  padding: "12px 28px", 
                  background: saving ? COLORS.textMuted : COLORS.accent, 
                  color: COLORS.bg, 
                  border: "none", 
                  borderRadius: 8, 
                  fontFamily: FONT, 
                  fontSize: 12, 
                  fontWeight: 600, 
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      )}

      {/* BILLING TAB */}
      {activeTab === "billing" && billingLoading && (
        <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>Loading billing data...</div>
      )}

      {activeTab === "billing" && !billingLoading && (
        <div>
          {/* ── Trial banner ── */}
          {(() => {
            if (hasSubscription) return null;
            const trialDate = trialEndsAt ? new Date(trialEndsAt) : null;
            const trialValid = trialDate && !Number.isNaN(trialDate.getTime());
            if (!trialValid && subscriptionStatus !== 'trial_expired') return null;
            const daysLeft = trialValid ? Math.ceil((trialDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : 0;
            const expired = subscriptionStatus === 'trial_expired' || (trialValid && daysLeft <= 0);
            const fmt = trialValid ? trialDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '';
            return (
              <div style={{
                padding: "12px 16px", marginBottom: 16, borderRadius: 8,
                background: expired ? COLORS.danger + "12" : COLORS.warn + "12",
                border: `1px solid ${expired ? COLORS.danger + "33" : COLORS.warn + "33"}`,
                color: expired ? COLORS.danger : COLORS.warn,
                fontFamily: FONT, fontSize: 12, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 14 }}>{expired ? '⚠️' : '⏳'}</span>
                <span style={{ flex: 1 }}>
                  {expired
                    ? (trialValid ? `Your trial ended on ${fmt}. Subscribe to keep using premium features.` : 'Your trial has ended. Subscribe to keep using premium features.')
                    : `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining (ends ${fmt}).`}
                </span>
                <a
                  href="#plans"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById('plans');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{ fontSize: 11, fontWeight: 700, color: expired ? COLORS.danger : COLORS.warn, textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Subscribe now →
                </a>
              </div>
            );
          })()}
          {/* ── Pricing Plans ── */}
          <div id="plans" />
          {(() => {
            const [billingInterval, setBillingInterval] = [
              window.__consultixBillingInterval || 'yearly',
              (v) => { window.__consultixBillingInterval = v; /* force re-render trick */ },
            ];
            // Use React state via a mini-component pattern
            return <PricingPlansSection
              plans={plansData}
              currentPlan={currentPlan}
              hasSubscription={hasSubscription}
              subscriptionStatus={subscriptionStatus}
              trialEndsAt={trialEndsAt}
              creditBalance={creditBalance}
              onCreditsChange={onCreditsChange}
              purchaseMessage={purchaseMessage}
              setPurchaseMessage={setPurchaseMessage}
              creditPackInfo={creditPackInfo}
              handleBuyCredits={handleBuyCredits}
              annualEnabled={annualEnabled}
              purchaseLoading={purchaseLoading}
            />;
          })()}

          {/* Credit Balance */}
          <div style={{ padding: "24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 24, textAlign: "center" }}>
            <div style={{ fontSize: 12, color: COLORS.textDim, fontFamily: FONT, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 8 }}>YOUR CREDIT BALANCE</div>
            <div style={{ fontSize: 48, fontWeight: 700, fontFamily: FONT, color: COLORS.accent, lineHeight: 1 }}>{creditBalance.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 8 }}>Buy credits to use features. Credits are deducted as you use them.</div>
          </div>

          {/* Buy Credits */}
          {creditPacks.length === 0 ? (
            <div style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 24, textAlign: "center", color: COLORS.textDim, fontSize: 12 }}>
              Credits are not currently available for purchase.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {purchaseMessage && (
                <div style={{ padding: "8px 14px", borderRadius: 6, fontSize: 11, color: purchaseMessage.includes('success') ? COLORS.accent : COLORS.danger, background: (purchaseMessage.includes('success') ? COLORS.accent : COLORS.danger) + "12", border: `1px solid ${(purchaseMessage.includes('success') ? COLORS.accent : COLORS.danger)}33` }}>
                  {purchaseMessage}
                </div>
              )}
              {creditPacks.map(pack => {
                const isLoadingThis = purchaseLoading === pack.id;
                const anyLoading = !!purchaseLoading;
                return (
                  <div key={pack.id} style={{ padding: "20px 24px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{pack.label}</div>
                        <div style={{ fontSize: 12, color: COLORS.textDim }}>{Number(pack.credits || 0).toLocaleString()} credits · {pack.price_display}</div>
                      </div>
                      <button
                        onClick={() => handleBuyCredits(pack.id)}
                        disabled={anyLoading}
                        style={{
                          padding: "10px 24px",
                          background: anyLoading ? COLORS.border : COLORS.accent,
                          color: anyLoading ? COLORS.textDim : '#000',
                          border: "none",
                          borderRadius: 8,
                          fontFamily: FONT,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: anyLoading ? "not-allowed" : "pointer",
                        }}
                      >
                        {isLoadingThis ? 'Redirecting...' : 'Buy Now'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Credit Cost Reference */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Credit Costs</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {(creditCostsData.length > 0
                ? creditCostsData.map(c => ({ action: c.action_label || c.action_key, cost: `${c.credits ?? 1} credit${(c.credits ?? 1) !== 1 ? "s" : ""}/${c.unit || "unit"}` }))
                : [
                    { action: "Lead discovery", cost: "1 credit/lead" },
                    { action: "Email verification", cost: "1 credit/lead" },
                    { action: "Company enrichment", cost: "2 credits/company" },
                    { action: "AI personalisation", cost: "2 credits/lead" },
                    { action: "AI audit analysis", cost: "25 credits/audit" },
                    { action: "Deck generation", cost: "15 credits/deck" },
                    { action: "Niche research", cost: "10 credits/research" },
                  ]
              ).map((item, i) => (
                <div key={i} style={{ padding: "8px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: COLORS.text }}>{item.action}</span>
                  <span style={{ fontSize: 10, color: COLORS.accent, fontFamily: FONT, fontWeight: 600 }}>{item.cost}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Usage History */}
          <div>
            <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Recent Usage</div>
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
              {USAGE_HISTORY.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim, fontSize: 12 }}>No usage history yet</div>
              )}
              {USAGE_HISTORY.map((entry, i) => (
                <div key={i} style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: i < USAGE_HISTORY.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                  <div>
                    <div style={{ fontSize: 12, color: COLORS.text }}>{entry.action}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{entry.date}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.warn }}>-{entry.credits}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{entry.balance} remaining</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {activeTab === "notifications" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { key: "weeklyDigest", label: "Weekly Digest", desc: "Summary of pipeline activity, credits used, and campaign performance" },
              { key: "creditAlert80", label: "Credit Alert — 80% Used", desc: "Get notified when you've used 80% of your monthly credits" },
              { key: "creditAlert100", label: "Credit Alert — 100% Used", desc: "Get notified when your credits run out" },
              { key: "campaignComplete", label: "Campaign Completion", desc: "Notification when enrichment or outreach campaigns finish processing" },
              { key: "surveyResponses", label: "New Survey Responses", desc: "Alert when stakeholders submit audit survey responses" },
            ].map(notif => (
              <div key={notif.key} style={{ padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{notif.label}</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim }}>{notif.desc}</div>
                </div>
                <div onClick={() => setNotifications({ ...notifications, [notif.key]: !notifications[notif.key] })} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: notifications[notif.key] ? COLORS.accent : COLORS.borderActive, position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: 16 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: notifications[notif.key] ? 23 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ flex: 1, padding: "16px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
      <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 600, color: accent }}>{value}</div>
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: "0.06em", marginTop: 4 }}>{label.toUpperCase()}</div>
    </div>
  );
}
